/*!
 * Vue.js v2.7.16
 * (c) 2014-2023 Evan You
 * Released under the MIT License.
 */
(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
  typeof define === 'function' && define.amd ? define(factory) :
  (global = typeof globalThis !== 'undefined' ? globalThis : global || self, global.Vue = factory());
})(this, (function () { 'use strict';

  var emptyObject = Object.freeze({});
  var isArray = Array.isArray;
  // These helpers produce better VM code in JS engines due to their
  // explicitness and function inlining.
  function isUndef(v) {
      return v === undefined || v === null;
  }
  function isDef(v) {
      return v !== undefined && v !== null;
  }
  function isTrue(v) {
      return v === true;
  }
  function isFalse(v) {
      return v === false;
  }
  /**
   * Check if value is primitive.
   */
  function isPrimitive(value) {
      return (typeof value === 'string' ||
          typeof value === 'number' ||
          // $flow-disable-line
          typeof value === 'symbol' ||
          typeof value === 'boolean');
  }
  function isFunction(value) {
      return typeof value === 'function';
  }
  /**
   * Quick object check - this is primarily used to tell
   * objects from primitive values when we know the value
   * is a JSON-compliant type.
   */
  function isObject(obj) {
      return obj !== null && typeof obj === 'object';
  }
  /**
   * Get the raw type string of a value, e.g., [object Object].
   */
  var _toString = Object.prototype.toString;
  function toRawType(value) {
      return _toString.call(value).slice(8, -1);
  }
  /**
   * Strict object type check. Only returns true
   * for plain JavaScript objects.
   */
  function isPlainObject(obj) {
      return _toString.call(obj) === '[object Object]';
  }
  function isRegExp(v) {
      return _toString.call(v) === '[object RegExp]';
  }
  /**
   * Check if val is a valid array index.
   */
  function isValidArrayIndex(val) {
      var n = parseFloat(String(val));
      return n >= 0 && Math.floor(n) === n && isFinite(val);
  }
  function isPromise(val) {
      return (isDef(val) &&
          typeof val.then === 'function' &&
          typeof val.catch === 'function');
  }
  /**
   * Convert a value to a string that is actually rendered.
   */
  function toString(val) {
      return val == null
          ? ''
          : Array.isArray(val) || (isPlainObject(val) && val.toString === _toString)
              ? JSON.stringify(val, replacer, 2)
              : String(val);
  }
  function replacer(_key, val) {
      // avoid circular deps from v3
      if (val && val.__v_isRef) {
          return val.value;
      }
      return val;
  }
  /**
   * Convert an input value to a number for persistence.
   * If the conversion fails, return original string.
   */
  function toNumber(val) {
      var n = parseFloat(val);
      return isNaN(n) ? val : n;
  }
  /**
   * Make a map and return a function for checking if a key
   * is in that map.
   */
  function makeMap(str, expectsLowerCase) {
      var map = Object.create(null);
      var list = str.split(',');
      for (var i = 0; i < list.length; i++) {
          map[list[i]] = true;
      }
      return expectsLowerCase ? function (val) { return map[val.toLowerCase()]; } : function (val) { return map[val]; };
  }
  /**
   * Check if a tag is a built-in tag.
   */
  var isBuiltInTag = makeMap('slot,component', true);
  /**
   * Check if an attribute is a reserved attribute.
   */
  var isReservedAttribute = makeMap('key,ref,slot,slot-scope,is');
  /**
   * Remove an item from an array.
   */
  function remove$2(arr, item) {
      var len = arr.length;
      if (len) {
          // fast path for the only / last item
          if (item === arr[len - 1]) {
              arr.length = len - 1;
              return;
          }
          var index = arr.indexOf(item);
          if (index > -1) {
              return arr.splice(index, 1);
          }
      }
  }
  /**
   * Check whether an object has the property.
   */
  var hasOwnProperty = Object.prototype.hasOwnProperty;
  function hasOwn(obj, key) {
      return hasOwnProperty.call(obj, key);
  }
  /**
   * Create a cached version of a pure function.
   */
  function cached(fn) {
      var cache = Object.create(null);
      return function cachedFn(str) {
          var hit = cache[str];
          return hit || (cache[str] = fn(str));
      };
  }
  /**
   * Camelize a hyphen-delimited string.
   */
  var camelizeRE = /-(\w)/g;
  var camelize = cached(function (str) {
      return str.replace(camelizeRE, function (_, c) { return (c ? c.toUpperCase() : ''); });
  });
  /**
   * Capitalize a string.
   */
  var capitalize = cached(function (str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
  });
  /**
   * Hyphenate a camelCase string.
   */
  var hyphenateRE = /\B([A-Z])/g;
  var hyphenate = cached(function (str) {
      return str.replace(hyphenateRE, '-$1').toLowerCase();
  });
  /**
   * Simple bind polyfill for environments that do not support it,
   * e.g., PhantomJS 1.x. Technically, we don't need this anymore
   * since native bind is now performant enough in most browsers.
   * But removing it would mean breaking code that was able to run in
   * PhantomJS 1.x, so this must be kept for backward compatibility.
   */
  /* istanbul ignore next */
  function polyfillBind(fn, ctx) {
      function boundFn(a) {
          var l = arguments.length;
          return l
              ? l > 1
                  ? fn.apply(ctx, arguments)
                  : fn.call(ctx, a)
              : fn.call(ctx);
      }
      boundFn._length = fn.length;
      return boundFn;
  }
  function nativeBind(fn, ctx) {
      return fn.bind(ctx);
  }
  // @ts-expect-error bind cannot be `undefined`
  var bind$1 = Function.prototype.bind ? nativeBind : polyfillBind;
  /**
   * Convert an Array-like object to a real Array.
   */
  function toArray(list, start) {
      start = start || 0;
      var i = list.length - start;
      var ret = new Array(i);
      while (i--) {
          ret[i] = list[i + start];
      }
      return ret;
  }
  /**
   * Mix properties into target object.
   */
  function extend(to, _from) {
      for (var key in _from) {
          to[key] = _from[key];
      }
      return to;
  }
  /**
   * Merge an Array of Objects into a single Object.
   */
  function toObject(arr) {
      var res = {};
      for (var i = 0; i < arr.length; i++) {
          if (arr[i]) {
              extend(res, arr[i]);
          }
      }
      return res;
  }
  /* eslint-disable no-unused-vars */
  /**
   * Perform no operation.
   * Stubbing args to make Flow happy without leaving useless transpiled code
   * with ...rest (https://flow.org/blog/2017/05/07/Strict-Function-Call-Arity/).
   */
  function noop(a, b, c) { }
  /**
   * Always return false.
   */
  var no = function (a, b, c) { return false; };
  /* eslint-enable no-unused-vars */
  /**
   * Return the same value.
   */
  var identity = function (_) { return _; };
  /**
   * Generate a string containing static keys from compiler modules.
   */
  function genStaticKeys$1(modules) {
      return modules
          .reduce(function (keys, m) { return keys.concat(m.staticKeys || []); }, [])
          .join(',');
  }
  /**
   * Check if two values are loosely equal - that is,
   * if they are plain objects, do they have the same shape?
   */
  function looseEqual(a, b) {
      if (a === b)
          return true;
      var isObjectA = isObject(a);
      var isObjectB = isObject(b);
      if (isObjectA && isObjectB) {
          try {
              var isArrayA = Array.isArray(a);
              var isArrayB = Array.isArray(b);
              if (isArrayA && isArrayB) {
                  return (a.length === b.length &&
                      a.every(function (e, i) {
                          return looseEqual(e, b[i]);
                      }));
              }
              else if (a instanceof Date && b instanceof Date) {
                  return a.getTime() === b.getTime();
              }
              else if (!isArrayA && !isArrayB) {
                  var keysA = Object.keys(a);
                  var keysB = Object.keys(b);
                  return (keysA.length === keysB.length &&
                      keysA.every(function (key) {
                          return looseEqual(a[key], b[key]);
                      }));
              }
              else {
                  /* istanbul ignore next */
                  return false;
              }
          }
          catch (e) {
              /* istanbul ignore next */
              return false;
          }
      }
      else if (!isObjectA && !isObjectB) {
          return String(a) === String(b);
      }
      else {
          return false;
      }
  }
  /**
   * Return the first index at which a loosely equal value can be
   * found in the array (if value is a plain object, the array must
   * contain an object of the same shape), or -1 if it is not present.
   */
  function looseIndexOf(arr, val) {
      for (var i = 0; i < arr.length; i++) {
          if (looseEqual(arr[i], val))
              return i;
      }
      return -1;
  }
  /**
   * Ensure a function is called only once.
   */
  function once(fn) {
      var called = false;
      return function () {
          if (!called) {
              called = true;
              fn.apply(this, arguments);
          }
      };
  }
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is#polyfill
  function hasChanged(x, y) {
      if (x === y) {
          return x === 0 && 1 / x !== 1 / y;
      }
      else {
          return x === x || y === y;
      }
  }

  var SSR_ATTR = 'data-server-rendered';
  var ASSET_TYPES = ['component', 'directive', 'filter'];
  var LIFECYCLE_HOOKS = [
      'beforeCreate',
      'created',
      'beforeMount',
      'mounted',
      'beforeUpdate',
      'updated',
      'beforeDestroy',
      'destroyed',
      'activated',
      'deactivated',
      'errorCaptured',
      'serverPrefetch',
      'renderTracked',
      'renderTriggered'
  ];

  var config = {
      /**
       * Option merge strategies (used in core/util/options)
       */
      // $flow-disable-line
      optionMergeStrategies: Object.create(null),
      /**
       * Whether to suppress warnings.
       */
      silent: false,
      /**
       * Show production mode tip message on boot?
       */
      productionTip: true,
      /**
       * Whether to enable devtools
       */
      devtools: true,
      /**
       * Whether to record perf
       */
      performance: false,
      /**
       * Error handler for watcher errors
       */
      errorHandler: null,
      /**
       * Warn handler for watcher warns
       */
      warnHandler: null,
      /**
       * Ignore certain custom elements
       */
      ignoredElements: [],
      /**
       * Custom user key aliases for v-on
       */
      // $flow-disable-line
      keyCodes: Object.create(null),
      /**
       * Check if a tag is reserved so that it cannot be registered as a
       * component. This is platform-dependent and may be overwritten.
       */
      isReservedTag: no,
      /**
       * Check if an attribute is reserved so that it cannot be used as a component
       * prop. This is platform-dependent and may be overwritten.
       */
      isReservedAttr: no,
      /**
       * Check if a tag is an unknown element.
       * Platform-dependent.
       */
      isUnknownElement: no,
      /**
       * Get the namespace of an element
       */
      getTagNamespace: noop,
      /**
       * Parse the real tag name for the specific platform.
       */
      parsePlatformTagName: identity,
      /**
       * Check if an attribute must be bound using property, e.g. value
       * Platform-dependent.
       */
      mustUseProp: no,
      /**
       * Perform updates asynchronously. Intended to be used by Vue Test Utils
       * This will significantly reduce performance if set to false.
       */
      async: true,
      /**
       * Exposed for legacy reasons
       */
      _lifecycleHooks: LIFECYCLE_HOOKS
  };

  /**
   * unicode letters used for parsing html tags, component names and property paths.
   * using https://www.w3.org/TR/html53/semantics-scripting.html#potentialcustomelementname
   * skipping \u10000-\uEFFFF due to it freezing up PhantomJS
   */
  var unicodeRegExp = /a-zA-Z\u00B7\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u037D\u037F-\u1FFF\u200C-\u200D\u203F-\u2040\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD/;
  /**
   * Check if a string starts with $ or _
   */
  function isReserved(str) {
      var c = (str + '').charCodeAt(0);
      return c === 0x24 || c === 0x5f;
  }
  /**
   * Define a property.
   */
  function def(obj, key, val, enumerable) {
      Object.defineProperty(obj, key, {
          value: val,
          enumerable: !!enumerable,
          writable: true,
          configurable: true
      });
  }
  /**
   * Parse simple path.
   */
  var bailRE = new RegExp("[^".concat(unicodeRegExp.source, ".$_\\d]"));
  function parsePath(path) {
      if (bailRE.test(path)) {
          return;
      }
      var segments = path.split('.');
      return function (obj) {
          for (var i = 0; i < segments.length; i++) {
              if (!obj)
                  return;
              obj = obj[segments[i]];
          }
          return obj;
      };
  }

  // can we use __proto__?
  var hasProto = '__proto__' in {};
  // Browser environment sniffing
  var inBrowser = typeof window !== 'undefined';
  var UA = inBrowser && window.navigator.userAgent.toLowerCase();
  var isIE = UA && /msie|trident/.test(UA);
  var isIE9 = UA && UA.indexOf('msie 9.0') > 0;
  var isEdge = UA && UA.indexOf('edge/') > 0;
  UA && UA.indexOf('android') > 0;
  var isIOS = UA && /iphone|ipad|ipod|ios/.test(UA);
  UA && /chrome\/\d+/.test(UA) && !isEdge;
  UA && /phantomjs/.test(UA);
  var isFF = UA && UA.match(/firefox\/(\d+)/);
  // Firefox has a "watch" function on Object.prototype...
  // @ts-expect-error firebox support
  var nativeWatch = {}.watch;
  var supportsPassive = false;
  if (inBrowser) {
      try {
          var opts = {};
          Object.defineProperty(opts, 'passive', {
              get: function () {
                  /* istanbul ignore next */
                  supportsPassive = true;
              }
          }); // https://github.com/facebook/flow/issues/285
          window.addEventListener('test-passive', null, opts);
      }
      catch (e) { }
  }
  // this needs to be lazy-evaled because vue may be required before
  // vue-server-renderer can set VUE_ENV
  var _isServer;
  var isServerRendering = function () {
      if (_isServer === undefined) {
          /* istanbul ignore if */
          if (!inBrowser && typeof global !== 'undefined') {
              // detect presence of vue-server-renderer and avoid
              // Webpack shimming the process
              _isServer =
                  global['process'] && global['process'].env.VUE_ENV === 'server';
          }
          else {
              _isServer = false;
          }
      }
      return _isServer;
  };
  // detect devtools
  var devtools = inBrowser && window.__VUE_DEVTOOLS_GLOBAL_HOOK__;
  /* istanbul ignore next */
  function isNative(Ctor) {
      return typeof Ctor === 'function' && /native code/.test(Ctor.toString());
  }
  var hasSymbol = typeof Symbol !== 'undefined' &&
      isNative(Symbol) &&
      typeof Reflect !== 'undefined' &&
      isNative(Reflect.ownKeys);
  var _Set; // $flow-disable-line
  /* istanbul ignore if */ if (typeof Set !== 'undefined' && isNative(Set)) {
      // use native Set when available.
      _Set = Set;
  }
  else {
      // a non-standard Set polyfill that only works with primitive keys.
      _Set = /** @class */ (function () {
          function Set() {
              this.set = Object.create(null);
          }
          Set.prototype.has = function (key) {
              return this.set[key] === true;
          };
          Set.prototype.add = function (key) {
              this.set[key] = true;
          };
          Set.prototype.clear = function () {
              this.set = Object.create(null);
          };
          return Set;
      }());
  }

  var currentInstance = null;
  /**
   * This is exposed for compatibility with v3 (e.g. some functions in VueUse
   * relies on it). Do not use this internally, just use `currentInstance`.
   *
   * @internal this function needs manual type declaration because it relies
   * on previously manually authored types from Vue 2
   */
  function getCurrentInstance() {
      return currentInstance && { proxy: currentInstance };
  }
  /**
   * @internal
   */
  function setCurrentInstance(vm) {
      if (vm === void 0) { vm = null; }
      if (!vm)
          currentInstance && currentInstance._scope.off();
      currentInstance = vm;
      vm && vm._scope.on();
  }

  /**
   * @internal
   */
  var VNode = /** @class */ (function () {
      function VNode(tag, data, children, text, elm, context, componentOptions, asyncFactory) {
          this.tag = tag;
          this.data = data;
          this.children = children;
          this.text = text;
          this.elm = elm;
          this.ns = undefined;
          this.context = context;
          this.fnContext = undefined;
          this.fnOptions = undefined;
          this.fnScopeId = undefined;
          this.key = data && data.key;
          this.componentOptions = componentOptions;
          this.componentInstance = undefined;
          this.parent = undefined;
          this.raw = false;
          this.isStatic = false;
          this.isRootInsert = true;
          this.isComment = false;
          this.isCloned = false;
          this.isOnce = false;
          this.asyncFactory = asyncFactory;
          this.asyncMeta = undefined;
          this.isAsyncPlaceholder = false;
      }
      Object.defineProperty(VNode.prototype, "child", {
          // DEPRECATED: alias for componentInstance for backwards compat.
          /* istanbul ignore next */
          get: function () {
              return this.componentInstance;
          },
          enumerable: false,
          configurable: true
      });
      return VNode;
  }());
  var createEmptyVNode = function (text) {
      if (text === void 0) { text = ''; }
      var node = new VNode();
      node.text = text;
      node.isComment = true;
      return node;
  };
  function createTextVNode(val) {
      return new VNode(undefined, undefined, undefined, String(val));
  }
  // optimized shallow clone
  // used for static nodes and slot nodes because they may be reused across
  // multiple renders, cloning them avoids errors when DOM manipulations rely
  // on their elm reference.
  function cloneVNode(vnode) {
      var cloned = new VNode(vnode.tag, vnode.data, 
      // #7975
      // clone children array to avoid mutating original in case of cloning
      // a child.
      vnode.children && vnode.children.slice(), vnode.text, vnode.elm, vnode.context, vnode.componentOptions, vnode.asyncFactory);
      cloned.ns = vnode.ns;
      cloned.isStatic = vnode.isStatic;
      cloned.key = vnode.key;
      cloned.isComment = vnode.isComment;
      cloned.fnContext = vnode.fnContext;
      cloned.fnOptions = vnode.fnOptions;
      cloned.fnScopeId = vnode.fnScopeId;
      cloned.asyncMeta = vnode.asyncMeta;
      cloned.isCloned = true;
      return cloned;
  }

  /* not type checking this file because flow doesn't play well with Proxy */
  var initProxy;
  {
      var allowedGlobals_1 = makeMap('Infinity,undefined,NaN,isFinite,isNaN,' +
          'parseFloat,parseInt,decodeURI,decodeURIComponent,encodeURI,encodeURIComponent,' +
          'Math,Number,Date,Array,Object,Boolean,String,RegExp,Map,Set,JSON,Intl,BigInt,' +
          'require' // for Webpack/Browserify
      );
      var warnNonPresent_1 = function (target, key) {
          warn$2("Property or method \"".concat(key, "\" is not defined on the instance but ") +
              'referenced during render. Make sure that this property is reactive, ' +
              'either in the data option, or for class-based components, by ' +
              'initializing the property. ' +
              'See: https://v2.vuejs.org/v2/guide/reactivity.html#Declaring-Reactive-Properties.', target);
      };
      var warnReservedPrefix_1 = function (target, key) {
          warn$2("Property \"".concat(key, "\" must be accessed with \"$data.").concat(key, "\" because ") +
              'properties starting with "$" or "_" are not proxied in the Vue instance to ' +
              'prevent conflicts with Vue internals. ' +
              'See: https://v2.vuejs.org/v2/api/#data', target);
      };
      var hasProxy_1 = typeof Proxy !== 'undefined' && isNative(Proxy);
      if (hasProxy_1) {
          var isBuiltInModifier_1 = makeMap('stop,prevent,self,ctrl,shift,alt,meta,exact');
          config.keyCodes = new Proxy(config.keyCodes, {
              set: function (target, key, value) {
                  if (isBuiltInModifier_1(key)) {
                      warn$2("Avoid overwriting built-in modifier in config.keyCodes: .".concat(key));
                      return false;
                  }
                  else {
                      target[key] = value;
                      return true;
                  }
              }
          });
      }
      var hasHandler_1 = {
          has: function (target, key) {
              var has = key in target;
              var isAllowed = allowedGlobals_1(key) ||
                  (typeof key === 'string' &&
                      key.charAt(0) === '_' &&
                      !(key in target.$data));
              if (!has && !isAllowed) {
                  if (key in target.$data)
                      warnReservedPrefix_1(target, key);
                  else
                      warnNonPresent_1(target, key);
              }
              return has || !isAllowed;
          }
      };
      var getHandler_1 = {
          get: function (target, key) {
              if (typeof key === 'string' && !(key in target)) {
                  if (key in target.$data)
                      warnReservedPrefix_1(target, key);
                  else
                      warnNonPresent_1(target, key);
              }
              return target[key];
          }
      };
      initProxy = function initProxy(vm) {
          if (hasProxy_1) {
              // determine which proxy handler to use
              var options = vm.$options;
              var handlers = options.render && options.render._withStripped ? getHandler_1 : hasHandler_1;
              vm._renderProxy = new Proxy(vm, handlers);
          }
          else {
              vm._renderProxy = vm;
          }
      };
  }

  /******************************************************************************
  Copyright (c) Microsoft Corporation.

  Permission to use, copy, modify, and/or distribute this software for any
  purpose with or without fee is hereby granted.

  THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
  REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
  AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
  INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
  LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
  OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
  PERFORMANCE OF THIS SOFTWARE.
  ***************************************************************************** */

  var __assign = function() {
      __assign = Object.assign || function __assign(t) {
          for (var s, i = 1, n = arguments.length; i < n; i++) {
              s = arguments[i];
              for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
          }
          return t;
      };
      return __assign.apply(this, arguments);
  };

  typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
      var e = new Error(message);
      return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
  };

  var uid$2 = 0;
  var pendingCleanupDeps = [];
  var cleanupDeps = function () {
      for (var i = 0; i < pendingCleanupDeps.length; i++) {
          var dep = pendingCleanupDeps[i];
          dep.subs = dep.subs.filter(function (s) { return s; });
          dep._pending = false;
      }
      pendingCleanupDeps.length = 0;
  };
  /**
   * A dep is an observable that can have multiple
   * directives subscribing to it.
   * @internal
   */
  var Dep = /** @class */ (function () {
      function Dep() {
          // pending subs cleanup
          this._pending = false;
          this.id = uid$2++;
          this.subs = [];
      }
      Dep.prototype.addSub = function (sub) {
          this.subs.push(sub);
      };
      Dep.prototype.removeSub = function (sub) {
          // #12696 deps with massive amount of subscribers are extremely slow to
          // clean up in Chromium
          // to workaround this, we unset the sub for now, and clear them on
          // next scheduler flush.
          this.subs[this.subs.indexOf(sub)] = null;
          if (!this._pending) {
              this._pending = true;
              pendingCleanupDeps.push(this);
          }
      };
      Dep.prototype.depend = function (info) {
          if (Dep.target) {
              Dep.target.addDep(this);
              if (info && Dep.target.onTrack) {
                  Dep.target.onTrack(__assign({ effect: Dep.target }, info));
              }
          }
      };
      Dep.prototype.notify = function (info) {
          // stabilize the subscriber list first
          var subs = this.subs.filter(function (s) { return s; });
          if (!config.async) {
              // subs aren't sorted in scheduler if not running async
              // we need to sort them now to make sure they fire in correct
              // order
              subs.sort(function (a, b) { return a.id - b.id; });
          }
          for (var i = 0, l = subs.length; i < l; i++) {
              var sub = subs[i];
              if (info) {
                  sub.onTrigger &&
                      sub.onTrigger(__assign({ effect: subs[i] }, info));
              }
              sub.update();
          }
      };
      return Dep;
  }());
  // The current target watcher being evaluated.
  // This is globally unique because only one watcher
  // can be evaluated at a time.
  Dep.target = null;
  var targetStack = [];
  function pushTarget(target) {
      targetStack.push(target);
      Dep.target = target;
  }
  function popTarget() {
      targetStack.pop();
      Dep.target = targetStack[targetStack.length - 1];
  }

  /*
   * not type checking this file because flow doesn't play well with
   * dynamically accessing methods on Array prototype
   */
  var arrayProto = Array.prototype;
  var arrayMethods = Object.create(arrayProto);
  var methodsToPatch = [
      'push',
      'pop',
      'shift',
      'unshift',
      'splice',
      'sort',
      'reverse'
  ];
  /**
   * Intercept mutating methods and emit events
   */
  methodsToPatch.forEach(function (method) {
      // cache original method
      var original = arrayProto[method];
      def(arrayMethods, method, function mutator() {
          var args = [];
          for (var _i = 0; _i < arguments.length; _i++) {
              args[_i] = arguments[_i];
          }
          var result = original.apply(this, args);
          var ob = this.__ob__;
          var inserted;
          switch (method) {
              case 'push':
              case 'unshift':
                  inserted = args;
                  break;
              case 'splice':
                  inserted = args.slice(2);
                  break;
          }
          if (inserted)
              ob.observeArray(inserted);
          // notify change
          {
              ob.dep.notify({
                  type: "array mutation" /* TriggerOpTypes.ARRAY_MUTATION */,
                  target: this,
                  key: method
              });
          }
          return result;
      });
  });

  var arrayKeys = Object.getOwnPropertyNames(arrayMethods);
  var NO_INITIAL_VALUE = {};
  /**
   * In some cases we may want to disable observation inside a component's
   * update computation.
   */
  var shouldObserve = true;
  function toggleObserving(value) {
      shouldObserve = value;
  }
  // ssr mock dep
  var mockDep = {
      notify: noop,
      depend: noop,
      addSub: noop,
      removeSub: noop
  };
  /**
   * Observer class that is attached to each observed
   * object. Once attached, the observer converts the target
   * object's property keys into getter/setters that
   * collect dependencies and dispatch updates.
   */
  var Observer = /** @class */ (function () {
      function Observer(value, shallow, mock) {
          if (shallow === void 0) { shallow = false; }
          if (mock === void 0) { mock = false; }
          this.value = value;
          this.shallow = shallow;
          this.mock = mock;
          // this.value = value
          this.dep = mock ? mockDep : new Dep();
          this.vmCount = 0;
          def(value, '__ob__', this);
          if (isArray(value)) {
              if (!mock) {
                  if (hasProto) {
                      value.__proto__ = arrayMethods;
                      /* eslint-enable no-proto */
                  }
                  else {
                      for (var i = 0, l = arrayKeys.length; i < l; i++) {
                          var key = arrayKeys[i];
                          def(value, key, arrayMethods[key]);
                      }
                  }
              }
              if (!shallow) {
                  this.observeArray(value);
              }
          }
          else {
              /**
               * Walk through all properties and convert them into
               * getter/setters. This method should only be called when
               * value type is Object.
               */
              var keys = Object.keys(value);
              for (var i = 0; i < keys.length; i++) {
                  var key = keys[i];
                  defineReactive(value, key, NO_INITIAL_VALUE, undefined, shallow, mock);
              }
          }
      }
      /**
       * Observe a list of Array items.
       */
      Observer.prototype.observeArray = function (value) {
          for (var i = 0, l = value.length; i < l; i++) {
              observe(value[i], false, this.mock);
          }
      };
      return Observer;
  }());
  // helpers
  /**
   * Attempt to create an observer instance for a value,
   * returns the new observer if successfully observed,
   * or the existing observer if the value already has one.
   */
  function observe(value, shallow, ssrMockReactivity) {
      if (value && hasOwn(value, '__ob__') && value.__ob__ instanceof Observer) {
          return value.__ob__;
      }
      if (shouldObserve &&
          (ssrMockReactivity || !isServerRendering()) &&
          (isArray(value) || isPlainObject(value)) &&
          Object.isExtensible(value) &&
          !value.__v_skip /* ReactiveFlags.SKIP */ &&
          !isRef(value) &&
          !(value instanceof VNode)) {
          return new Observer(value, shallow, ssrMockReactivity);
      }
  }
  /**
   * Define a reactive property on an Object.
   */
  function defineReactive(obj, key, val, customSetter, shallow, mock, observeEvenIfShallow) {
      if (observeEvenIfShallow === void 0) { observeEvenIfShallow = false; }
      var dep = new Dep();
      var property = Object.getOwnPropertyDescriptor(obj, key);
      if (property && property.configurable === false) {
          return;
      }
      // cater for pre-defined getter/setters
      var getter = property && property.get;
      var setter = property && property.set;
      if ((!getter || setter) &&
          (val === NO_INITIAL_VALUE || arguments.length === 2)) {
          val = obj[key];
      }
      var childOb = shallow ? val && val.__ob__ : observe(val, false, mock);
      Object.defineProperty(obj, key, {
          enumerable: true,
          configurable: true,
          get: function reactiveGetter() {
              var value = getter ? getter.call(obj) : val;
              if (Dep.target) {
                  {
                      dep.depend({
                          target: obj,
                          type: "get" /* TrackOpTypes.GET */,
                          key: key
                      });
                  }
                  if (childOb) {
                      childOb.dep.depend();
                      if (isArray(value)) {
                          dependArray(value);
                      }
                  }
              }
              return isRef(value) && !shallow ? value.value : value;
          },
          set: function reactiveSetter(newVal) {
              var value = getter ? getter.call(obj) : val;
              if (!hasChanged(value, newVal)) {
                  return;
              }
              if (customSetter) {
                  customSetter();
              }
              if (setter) {
                  setter.call(obj, newVal);
              }
              else if (getter) {
                  // #7981: for accessor properties without setter
                  return;
              }
              else if (!shallow && isRef(value) && !isRef(newVal)) {
                  value.value = newVal;
                  return;
              }
              else {
                  val = newVal;
              }
              childOb = shallow ? newVal && newVal.__ob__ : observe(newVal, false, mock);
              {
                  dep.notify({
                      type: "set" /* TriggerOpTypes.SET */,
                      target: obj,
                      key: key,
                      newValue: newVal,
                      oldValue: value
                  });
              }
          }
      });
      return dep;
  }
  function set(target, key, val) {
      if ((isUndef(target) || isPrimitive(target))) {
          warn$2("Cannot set reactive property on undefined, null, or primitive value: ".concat(target));
      }
      if (isReadonly(target)) {
          warn$2("Set operation on key \"".concat(key, "\" failed: target is readonly."));
          return;
      }
      var ob = target.__ob__;
      if (isArray(target) && isValidArrayIndex(key)) {
          target.length = Math.max(target.length, key);
          target.splice(key, 1, val);
          // when mocking for SSR, array methods are not hijacked
          if (ob && !ob.shallow && ob.mock) {
              observe(val, false, true);
          }
          return val;
      }
      if (key in target && !(key in Object.prototype)) {
          target[key] = val;
          return val;
      }
      if (target._isVue || (ob && ob.vmCount)) {
          warn$2('Avoid adding reactive properties to a Vue instance or its root $data ' +
                  'at runtime - declare it upfront in the data option.');
          return val;
      }
      if (!ob) {
          target[key] = val;
          return val;
      }
      defineReactive(ob.value, key, val, undefined, ob.shallow, ob.mock);
      {
          ob.dep.notify({
              type: "add" /* TriggerOpTypes.ADD */,
              target: target,
              key: key,
              newValue: val,
              oldValue: undefined
          });
      }
      return val;
  }
  function del(target, key) {
      if ((isUndef(target) || isPrimitive(target))) {
          warn$2("Cannot delete reactive property on undefined, null, or primitive value: ".concat(target));
      }
      if (isArray(target) && isValidArrayIndex(key)) {
          target.splice(key, 1);
          return;
      }
      var ob = target.__ob__;
      if (target._isVue || (ob && ob.vmCount)) {
          warn$2('Avoid deleting properties on a Vue instance or its root $data ' +
                  '- just set it to null.');
          return;
      }
      if (isReadonly(target)) {
          warn$2("Delete operation on key \"".concat(key, "\" failed: target is readonly."));
          return;
      }
      if (!hasOwn(target, key)) {
          return;
      }
      delete target[key];
      if (!ob) {
          return;
      }
      {
          ob.dep.notify({
              type: "delete" /* TriggerOpTypes.DELETE */,
              target: target,
              key: key
          });
      }
  }
  /**
   * Collect dependencies on array elements when the array is touched, since
   * we cannot intercept array element access like property getters.
   */
  function dependArray(value) {
      for (var e = void 0, i = 0, l = value.length; i < l; i++) {
          e = value[i];
          if (e && e.__ob__) {
              e.__ob__.dep.depend();
          }
          if (isArray(e)) {
              dependArray(e);
          }
      }
  }

  function reactive(target) {
      makeReactive(target, false);
      return target;
  }
  /**
   * Return a shallowly-reactive copy of the original object, where only the root
   * level properties are reactive. It also does not auto-unwrap refs (even at the
   * root level).
   */
  function shallowReactive(target) {
      makeReactive(target, true);
      def(target, "__v_isShallow" /* ReactiveFlags.IS_SHALLOW */, true);
      return target;
  }
  function makeReactive(target, shallow) {
      // if trying to observe a readonly proxy, return the readonly version.
      if (!isReadonly(target)) {
          {
              if (isArray(target)) {
                  warn$2("Avoid using Array as root value for ".concat(shallow ? "shallowReactive()" : "reactive()", " as it cannot be tracked in watch() or watchEffect(). Use ").concat(shallow ? "shallowRef()" : "ref()", " instead. This is a Vue-2-only limitation."));
              }
              var existingOb = target && target.__ob__;
              if (existingOb && existingOb.shallow !== shallow) {
                  warn$2("Target is already a ".concat(existingOb.shallow ? "" : "non-", "shallow reactive object, and cannot be converted to ").concat(shallow ? "" : "non-", "shallow."));
              }
          }
          var ob = observe(target, shallow, isServerRendering() /* ssr mock reactivity */);
          if (!ob) {
              if (target == null || isPrimitive(target)) {
                  warn$2("value cannot be made reactive: ".concat(String(target)));
              }
              if (isCollectionType(target)) {
                  warn$2("Vue 2 does not support reactive collection types such as Map or Set.");
              }
          }
      }
  }
  function isReactive(value) {
      if (isReadonly(value)) {
          return isReactive(value["__v_raw" /* ReactiveFlags.RAW */]);
      }
      return !!(value && value.__ob__);
  }
  function isShallow(value) {
      return !!(value && value.__v_isShallow);
  }
  function isReadonly(value) {
      return !!(value && value.__v_isReadonly);
  }
  function isProxy(value) {
      return isReactive(value) || isReadonly(value);
  }
  function toRaw(observed) {
      var raw = observed && observed["__v_raw" /* ReactiveFlags.RAW */];
      return raw ? toRaw(raw) : observed;
  }
  function markRaw(value) {
      // non-extensible objects won't be observed anyway
      if (Object.isExtensible(value)) {
          def(value, "__v_skip" /* ReactiveFlags.SKIP */, true);
      }
      return value;
  }
  /**
   * @internal
   */
  function isCollectionType(value) {
      var type = toRawType(value);
      return (type === 'Map' || type === 'WeakMap' || type === 'Set' || type === 'WeakSet');
  }

  /**
   * @internal
   */
  var RefFlag = "__v_isRef";
  function isRef(r) {
      return !!(r && r.__v_isRef === true);
  }
  function ref$1(value) {
      return createRef(value, false);
  }
  function shallowRef(value) {
      return createRef(value, true);
  }
  function createRef(rawValue, shallow) {
      if (isRef(rawValue)) {
          return rawValue;
      }
      var ref = {};
      def(ref, RefFlag, true);
      def(ref, "__v_isShallow" /* ReactiveFlags.IS_SHALLOW */, shallow);
      def(ref, 'dep', defineReactive(ref, 'value', rawValue, null, shallow, isServerRendering()));
      return ref;
  }
  function triggerRef(ref) {
      if (!ref.dep) {
          warn$2("received object is not a triggerable ref.");
      }
      {
          ref.dep &&
              ref.dep.notify({
                  type: "set" /* TriggerOpTypes.SET */,
                  target: ref,
                  key: 'value'
              });
      }
  }
  function unref(ref) {
      return isRef(ref) ? ref.value : ref;
  }
  function proxyRefs(objectWithRefs) {
      if (isReactive(objectWithRefs)) {
          return objectWithRefs;
      }
      var proxy = {};
      var keys = Object.keys(objectWithRefs);
      for (var i = 0; i < keys.length; i++) {
          proxyWithRefUnwrap(proxy, objectWithRefs, keys[i]);
      }
      return proxy;
  }
  function proxyWithRefUnwrap(target, source, key) {
      Object.defineProperty(target, key, {
          enumerable: true,
          configurable: true,
          get: function () {
              var val = source[key];
              if (isRef(val)) {
                  return val.value;
              }
              else {
                  var ob = val && val.__ob__;
                  if (ob)
                      ob.dep.depend();
                  return val;
              }
          },
          set: function (value) {
              var oldValue = source[key];
              if (isRef(oldValue) && !isRef(value)) {
                  oldValue.value = value;
              }
              else {
                  source[key] = value;
              }
          }
      });
  }
  function customRef(factory) {
      var dep = new Dep();
      var _a = factory(function () {
          {
              dep.depend({
                  target: ref,
                  type: "get" /* TrackOpTypes.GET */,
                  key: 'value'
              });
          }
      }, function () {
          {
              dep.notify({
                  target: ref,
                  type: "set" /* TriggerOpTypes.SET */,
                  key: 'value'
              });
          }
      }), get = _a.get, set = _a.set;
      var ref = {
          get value() {
              return get();
          },
          set value(newVal) {
              set(newVal);
          }
      };
      def(ref, RefFlag, true);
      return ref;
  }
  function toRefs(object) {
      if (!isReactive(object)) {
          warn$2("toRefs() expects a reactive object but received a plain one.");
      }
      var ret = isArray(object) ? new Array(object.length) : {};
      for (var key in object) {
          ret[key] = toRef(object, key);
      }
      return ret;
  }
  function toRef(object, key, defaultValue) {
      var val = object[key];
      if (isRef(val)) {
          return val;
      }
      var ref = {
          get value() {
              var val = object[key];
              return val === undefined ? defaultValue : val;
          },
          set value(newVal) {
              object[key] = newVal;
          }
      };
      def(ref, RefFlag, true);
      return ref;
  }

  var rawToReadonlyFlag = "__v_rawToReadonly";
  var rawToShallowReadonlyFlag = "__v_rawToShallowReadonly";
  function readonly(target) {
      return createReadonly(target, false);
  }
  function createReadonly(target, shallow) {
      if (!isPlainObject(target)) {
          {
              if (isArray(target)) {
                  warn$2("Vue 2 does not support readonly arrays.");
              }
              else if (isCollectionType(target)) {
                  warn$2("Vue 2 does not support readonly collection types such as Map or Set.");
              }
              else {
                  warn$2("value cannot be made readonly: ".concat(typeof target));
              }
          }
          return target;
      }
      if (!Object.isExtensible(target)) {
          warn$2("Vue 2 does not support creating readonly proxy for non-extensible object.");
      }
      // already a readonly object
      if (isReadonly(target)) {
          return target;
      }
      // already has a readonly proxy
      var existingFlag = shallow ? rawToShallowReadonlyFlag : rawToReadonlyFlag;
      var existingProxy = target[existingFlag];
      if (existingProxy) {
          return existingProxy;
      }
      var proxy = Object.create(Object.getPrototypeOf(target));
      def(target, existingFlag, proxy);
      def(proxy, "__v_isReadonly" /* ReactiveFlags.IS_READONLY */, true);
      def(proxy, "__v_raw" /* ReactiveFlags.RAW */, target);
      if (isRef(target)) {
          def(proxy, RefFlag, true);
      }
      if (shallow || isShallow(target)) {
          def(proxy, "__v_isShallow" /* ReactiveFlags.IS_SHALLOW */, true);
      }
      var keys = Object.keys(target);
      for (var i = 0; i < keys.length; i++) {
          defineReadonlyProperty(proxy, target, keys[i], shallow);
      }
      return proxy;
  }
  function defineReadonlyProperty(proxy, target, key, shallow) {
      Object.defineProperty(proxy, key, {
          enumerable: true,
          configurable: true,
          get: function () {
              var val = target[key];
              return shallow || !isPlainObject(val) ? val : readonly(val);
          },
          set: function () {
              warn$2("Set operation on key \"".concat(key, "\" failed: target is readonly."));
          }
      });
  }
  /**
   * Returns a reactive-copy of the original object, where only the root level
   * properties are readonly, and does NOT unwrap refs nor recursively convert
   * returned properties.
   * This is used for creating the props proxy object for stateful components.
   */
  function shallowReadonly(target) {
      return createReadonly(target, true);
  }

  function computed(getterOrOptions, debugOptions) {
      var getter;
      var setter;
      var onlyGetter = isFunction(getterOrOptions);
      if (onlyGetter) {
          getter = getterOrOptions;
          setter = function () {
                  warn$2('Write operation failed: computed value is readonly');
              }
              ;
      }
      else {
          getter = getterOrOptions.get;
          setter = getterOrOptions.set;
      }
      var watcher = isServerRendering()
          ? null
          : new Watcher(currentInstance, getter, noop, { lazy: true });
      if (watcher && debugOptions) {
          watcher.onTrack = debugOptions.onTrack;
          watcher.onTrigger = debugOptions.onTrigger;
      }
      var ref = {
          // some libs rely on the presence effect for checking computed refs
          // from normal refs, but the implementation doesn't matter
          effect: watcher,
          get value() {
              if (watcher) {
                  if (watcher.dirty) {
                      watcher.evaluate();
                  }
                  if (Dep.target) {
                      if (Dep.target.onTrack) {
                          Dep.target.onTrack({
                              effect: Dep.target,
                              target: ref,
                              type: "get" /* TrackOpTypes.GET */,
                              key: 'value'
                          });
                      }
                      watcher.depend();
                  }
                  return watcher.value;
              }
              else {
                  return getter();
              }
          },
          set value(newVal) {
              setter(newVal);
          }
      };
      def(ref, RefFlag, true);
      def(ref, "__v_isReadonly" /* ReactiveFlags.IS_READONLY */, onlyGetter);
      return ref;
  }

  var mark;
  var measure;
  {
      var perf_1 = inBrowser && window.performance;
      /* istanbul ignore if */
      if (perf_1 &&
          // @ts-ignore
          perf_1.mark &&
          // @ts-ignore
          perf_1.measure &&
          // @ts-ignore
          perf_1.clearMarks &&
          // @ts-ignore
          perf_1.clearMeasures) {
          mark = function (tag) { return perf_1.mark(tag); };
          measure = function (name, startTag, endTag) {
              perf_1.measure(name, startTag, endTag);
              perf_1.clearMarks(startTag);
              perf_1.clearMarks(endTag);
              // perf.clearMeasures(name)
          };
      }
  }

  var normalizeEvent = cached(function (name) {
      var passive = name.charAt(0) === '&';
      name = passive ? name.slice(1) : name;
      var once = name.charAt(0) === '~'; // Prefixed last, checked first
      name = once ? name.slice(1) : name;
      var capture = name.charAt(0) === '!';
      name = capture ? name.slice(1) : name;
      return {
          name: name,
          once: once,
          capture: capture,
          passive: passive
      };
  });
  function createFnInvoker(fns, vm) {
      function invoker() {
          var fns = invoker.fns;
          if (isArray(fns)) {
              var cloned = fns.slice();
              for (var i = 0; i < cloned.length; i++) {
                  invokeWithErrorHandling(cloned[i], null, arguments, vm, "v-on handler");
              }
          }
          else {
              // return handler return value for single handlers
              return invokeWithErrorHandling(fns, null, arguments, vm, "v-on handler");
          }
      }
      invoker.fns = fns;
      return invoker;
  }
  function updateListeners(on, oldOn, add, remove, createOnceHandler, vm) {
      var name, cur, old, event;
      for (name in on) {
          cur = on[name];
          old = oldOn[name];
          event = normalizeEvent(name);
          if (isUndef(cur)) {
              warn$2("Invalid handler for event \"".concat(event.name, "\": got ") + String(cur), vm);
          }
          else if (isUndef(old)) {
              if (isUndef(cur.fns)) {
                  cur = on[name] = createFnInvoker(cur, vm);
              }
              if (isTrue(event.once)) {
                  cur = on[name] = createOnceHandler(event.name, cur, event.capture);
              }
              add(event.name, cur, event.capture, event.passive, event.params);
          }
          else if (cur !== old) {
              old.fns = cur;
              on[name] = old;
          }
      }
      for (name in oldOn) {
          if (isUndef(on[name])) {
              event = normalizeEvent(name);
              remove(event.name, oldOn[name], event.capture);
          }
      }
  }

  function mergeVNodeHook(def, hookKey, hook) {
      if (def instanceof VNode) {
          def = def.data.hook || (def.data.hook = {});
      }
      var invoker;
      var oldHook = def[hookKey];
      function wrappedHook() {
          hook.apply(this, arguments);
          // important: remove merged hook to ensure it's called only once
          // and prevent memory leak
          remove$2(invoker.fns, wrappedHook);
      }
      if (isUndef(oldHook)) {
          // no existing hook
          invoker = createFnInvoker([wrappedHook]);
      }
      else {
          /* istanbul ignore if */
          if (isDef(oldHook.fns) && isTrue(oldHook.merged)) {
              // already a merged invoker
              invoker = oldHook;
              invoker.fns.push(wrappedHook);
          }
          else {
              // existing plain hook
              invoker = createFnInvoker([oldHook, wrappedHook]);
          }
      }
      invoker.merged = true;
      def[hookKey] = invoker;
  }

  function extractPropsFromVNodeData(data, Ctor, tag) {
      // we are only extracting raw values here.
      // validation and default values are handled in the child
      // component itself.
      var propOptions = Ctor.options.props;
      if (isUndef(propOptions)) {
          return;
      }
      var res = {};
      var attrs = data.attrs, props = data.props;
      if (isDef(attrs) || isDef(props)) {
          for (var key in propOptions) {
              var altKey = hyphenate(key);
              {
                  var keyInLowerCase = key.toLowerCase();
                  if (key !== keyInLowerCase && attrs && hasOwn(attrs, keyInLowerCase)) {
                      tip("Prop \"".concat(keyInLowerCase, "\" is passed to component ") +
                          "".concat(formatComponentName(
                          // @ts-expect-error tag is string
                          tag || Ctor), ", but the declared prop name is") +
                          " \"".concat(key, "\". ") +
                          "Note that HTML attributes are case-insensitive and camelCased " +
                          "props need to use their kebab-case equivalents when using in-DOM " +
                          "templates. You should probably use \"".concat(altKey, "\" instead of \"").concat(key, "\"."));
                  }
              }
              checkProp(res, props, key, altKey, true) ||
                  checkProp(res, attrs, key, altKey, false);
          }
      }
      return res;
  }
  function checkProp(res, hash, key, altKey, preserve) {
      if (isDef(hash)) {
          if (hasOwn(hash, key)) {
              res[key] = hash[key];
              if (!preserve) {
                  delete hash[key];
              }
              return true;
          }
          else if (hasOwn(hash, altKey)) {
              res[key] = hash[altKey];
              if (!preserve) {
                  delete hash[altKey];
              }
              return true;
          }
      }
      return false;
  }

  // The template compiler attempts to minimize the need for normalization by
  // statically analyzing the template at compile time.
  //
  // For plain HTML markup, normalization can be completely skipped because the
  // generated render function is guaranteed to return Array<VNode>. There are
  // two cases where extra normalization is needed:
  // 1. When the children contains components - because a functional component
  // may return an Array instead of a single root. In this case, just a simple
  // normalization is needed - if any child is an Array, we flatten the whole
  // thing with Array.prototype.concat. It is guaranteed to be only 1-level deep
  // because functional components already normalize their own children.
  function simpleNormalizeChildren(children) {
      for (var i = 0; i < children.length; i++) {
          if (isArray(children[i])) {
              return Array.prototype.concat.apply([], children);
          }
      }
      return children;
  }
  // 2. When the children contains constructs that always generated nested Arrays,
  // e.g. <template>, <slot>, v-for, or when the children is provided by user
  // with hand-written render functions / JSX. In such cases a full normalization
  // is needed to cater to all possible types of children values.
  function normalizeChildren(children) {
      return isPrimitive(children)
          ? [createTextVNode(children)]
          : isArray(children)
              ? normalizeArrayChildren(children)
              : undefined;
  }
  function isTextNode(node) {
      return isDef(node) && isDef(node.text) && isFalse(node.isComment);
  }
  function normalizeArrayChildren(children, nestedIndex) {
      var res = [];
      var i, c, lastIndex, last;
      for (i = 0; i < children.length; i++) {
          c = children[i];
          if (isUndef(c) || typeof c === 'boolean')
              continue;
          lastIndex = res.length - 1;
          last = res[lastIndex];
          //  nested
          if (isArray(c)) {
              if (c.length > 0) {
                  c = normalizeArrayChildren(c, "".concat(nestedIndex || '', "_").concat(i));
                  // merge adjacent text nodes
                  if (isTextNode(c[0]) && isTextNode(last)) {
                      res[lastIndex] = createTextVNode(last.text + c[0].text);
                      c.shift();
                  }
                  res.push.apply(res, c);
              }
          }
          else if (isPrimitive(c)) {
              if (isTextNode(last)) {
                  // merge adjacent text nodes
                  // this is necessary for SSR hydration because text nodes are
                  // essentially merged when rendered to HTML strings
                  res[lastIndex] = createTextVNode(last.text + c);
              }
              else if (c !== '') {
                  // convert primitive to vnode
                  res.push(createTextVNode(c));
              }
          }
          else {
              if (isTextNode(c) && isTextNode(last)) {
                  // merge adjacent text nodes
                  res[lastIndex] = createTextVNode(last.text + c.text);
              }
              else {
                  // default key for nested array children (likely generated by v-for)
                  if (isTrue(children._isVList) &&
                      isDef(c.tag) &&
                      isUndef(c.key) &&
                      isDef(nestedIndex)) {
                      c.key = "__vlist".concat(nestedIndex, "_").concat(i, "__");
                  }
                  res.push(c);
              }
          }
      }
      return res;
  }

  var SIMPLE_NORMALIZE = 1;
  var ALWAYS_NORMALIZE = 2;
  // wrapper function for providing a more flexible interface
  // without getting yelled at by flow
  function createElement$1(context, tag, data, children, normalizationType, alwaysNormalize) {
      if (isArray(data) || isPrimitive(data)) {
          normalizationType = children;
          children = data;
          data = undefined;
      }
      if (isTrue(alwaysNormalize)) {
          normalizationType = ALWAYS_NORMALIZE;
      }
      return _createElement(context, tag, data, children, normalizationType);
  }
  function _createElement(context, tag, data, children, normalizationType) {
      if (isDef(data) && isDef(data.__ob__)) {
          warn$2("Avoid using observed data object as vnode data: ".concat(JSON.stringify(data), "\n") + 'Always create fresh vnode data objects in each render!', context);
          return createEmptyVNode();
      }
      // object syntax in v-bind
      if (isDef(data) && isDef(data.is)) {
          tag = data.is;
      }
      if (!tag) {
          // in case of component :is set to falsy value
          return createEmptyVNode();
      }
      // warn against non-primitive key
      if (isDef(data) && isDef(data.key) && !isPrimitive(data.key)) {
          warn$2('Avoid using non-primitive value as key, ' +
              'use string/number value instead.', context);
      }
      // support single function children as default scoped slot
      if (isArray(children) && isFunction(children[0])) {
          data = data || {};
          data.scopedSlots = { default: children[0] };
          children.length = 0;
      }
      if (normalizationType === ALWAYS_NORMALIZE) {
          children = normalizeChildren(children);
      }
      else if (normalizationType === SIMPLE_NORMALIZE) {
          children = simpleNormalizeChildren(children);
      }
      var vnode, ns;
      if (typeof tag === 'string') {
          var Ctor = void 0;
          ns = (context.$vnode && context.$vnode.ns) || config.getTagNamespace(tag);
          if (config.isReservedTag(tag)) {
              // platform built-in elements
              if (isDef(data) &&
                  isDef(data.nativeOn) &&
                  data.tag !== 'component') {
                  warn$2("The .native modifier for v-on is only valid on components but it was used on <".concat(tag, ">."), context);
              }
              vnode = new VNode(config.parsePlatformTagName(tag), data, children, undefined, undefined, context);
          }
          else if ((!data || !data.pre) &&
              isDef((Ctor = resolveAsset(context.$options, 'components', tag)))) {
              // component
              vnode = createComponent(Ctor, data, context, children, tag);
          }
          else {
              // unknown or unlisted namespaced elements
              // check at runtime because it may get assigned a namespace when its
              // parent normalizes children
              vnode = new VNode(tag, data, children, undefined, undefined, context);
          }
      }
      else {
          // direct component options / constructor
          vnode = createComponent(tag, data, context, children);
      }
      if (isArray(vnode)) {
          return vnode;
      }
      else if (isDef(vnode)) {
          if (isDef(ns))
              applyNS(vnode, ns);
          if (isDef(data))
              registerDeepBindings(data);
          return vnode;
      }
      else {
          return createEmptyVNode();
      }
  }
  function applyNS(vnode, ns, force) {
      vnode.ns = ns;
      if (vnode.tag === 'foreignObject') {
          // use default namespace inside foreignObject
          ns = undefined;
          force = true;
      }
      if (isDef(vnode.children)) {
          for (var i = 0, l = vnode.children.length; i < l; i++) {
              var child = vnode.children[i];
              if (isDef(child.tag) &&
                  (isUndef(child.ns) || (isTrue(force) && child.tag !== 'svg'))) {
                  applyNS(child, ns, force);
              }
          }
      }
  }
  // ref #5318
  // necessary to ensure parent re-render when deep bindings like :style and
  // :class are used on slot nodes
  function registerDeepBindings(data) {
      if (isObject(data.style)) {
          traverse(data.style);
      }
      if (isObject(data.class)) {
          traverse(data.class);
      }
  }

  /**
   * Runtime helper for rendering v-for lists.
   */
  function renderList(val, render) {
      var ret = null, i, l, keys, key;
      if (isArray(val) || typeof val === 'string') {
          ret = new Array(val.length);
          for (i = 0, l = val.length; i < l; i++) {
              ret[i] = render(val[i], i);
          }
      }
      else if (typeof val === 'number') {
          ret = new Array(val);
          for (i = 0; i < val; i++) {
              ret[i] = render(i + 1, i);
          }
      }
      else if (isObject(val)) {
          if (hasSymbol && val[Symbol.iterator]) {
              ret = [];
              var iterator = val[Symbol.iterator]();
              var result = iterator.next();
              while (!result.done) {
                  ret.push(render(result.value, ret.length));
                  result = iterator.next();
              }
          }
          else {
              keys = Object.keys(val);
              ret = new Array(keys.length);
              for (i = 0, l = keys.length; i < l; i++) {
                  key = keys[i];
                  ret[i] = render(val[key], key, i);
              }
          }
      }
      if (!isDef(ret)) {
          ret = [];
      }
      ret._isVList = true;
      return ret;
  }

  /**
   * Runtime helper for rendering <slot>
   */
  function renderSlot(name, fallbackRender, props, bindObject) {
      var scopedSlotFn = this.$scopedSlots[name];
      var nodes;
      if (scopedSlotFn) {
          // scoped slot
          props = props || {};
          if (bindObject) {
              if (!isObject(bindObject)) {
                  warn$2('slot v-bind without argument expects an Object', this);
              }
              props = extend(extend({}, bindObject), props);
          }
          nodes =
              scopedSlotFn(props) ||
                  (isFunction(fallbackRender) ? fallbackRender() : fallbackRender);
      }
      else {
          nodes =
              this.$slots[name] ||
                  (isFunction(fallbackRender) ? fallbackRender() : fallbackRender);
      }
      var target = props && props.slot;
      if (target) {
          return this.$createElement('template', { slot: target }, nodes);
      }
      else {
          return nodes;
      }
  }

  /**
   * Runtime helper for resolving filters
   */
  function resolveFilter(id) {
      return resolveAsset(this.$options, 'filters', id, true) || identity;
  }

  function isKeyNotMatch(expect, actual) {
      if (isArray(expect)) {
          return expect.indexOf(actual) === -1;
      }
      else {
          return expect !== actual;
      }
  }
  /**
   * Runtime helper for checking keyCodes from config.
   * exposed as Vue.prototype._k
   * passing in eventKeyName as last argument separately for backwards compat
   */
  function checkKeyCodes(eventKeyCode, key, builtInKeyCode, eventKeyName, builtInKeyName) {
      var mappedKeyCode = config.keyCodes[key] || builtInKeyCode;
      if (builtInKeyName && eventKeyName && !config.keyCodes[key]) {
          return isKeyNotMatch(builtInKeyName, eventKeyName);
      }
      else if (mappedKeyCode) {
          return isKeyNotMatch(mappedKeyCode, eventKeyCode);
      }
      else if (eventKeyName) {
          return hyphenate(eventKeyName) !== key;
      }
      return eventKeyCode === undefined;
  }

  /**
   * Runtime helper for merging v-bind="object" into a VNode's data.
   */
  function bindObjectProps(data, tag, value, asProp, isSync) {
      if (value) {
          if (!isObject(value)) {
              warn$2('v-bind without argument expects an Object or Array value', this);
          }
          else {
              if (isArray(value)) {
                  value = toObject(value);
              }
              var hash = void 0;
              var _loop_1 = function (key) {
                  if (key === 'class' || key === 'style' || isReservedAttribute(key)) {
                      hash = data;
                  }
                  else {
                      var type = data.attrs && data.attrs.type;
                      hash =
                          asProp || config.mustUseProp(tag, type, key)
                              ? data.domProps || (data.domProps = {})
                              : data.attrs || (data.attrs = {});
                  }
                  var camelizedKey = camelize(key);
                  var hyphenatedKey = hyphenate(key);
                  if (!(camelizedKey in hash) && !(hyphenatedKey in hash)) {
                      hash[key] = value[key];
                      if (isSync) {
                          var on = data.on || (data.on = {});
                          on["update:".concat(key)] = function ($event) {
                              value[key] = $event;
                          };
                      }
                  }
              };
              for (var key in value) {
                  _loop_1(key);
              }
          }
      }
      return data;
  }

  /**
   * Runtime helper for rendering static trees.
   */
  function renderStatic(index, isInFor) {
      var cached = this._staticTrees || (this._staticTrees = []);
      var tree = cached[index];
      // if has already-rendered static tree and not inside v-for,
      // we can reuse the same tree.
      if (tree && !isInFor) {
          return tree;
      }
      // otherwise, render a fresh tree.
      tree = cached[index] = this.$options.staticRenderFns[index].call(this._renderProxy, this._c, this // for render fns generated for functional component templates
      );
      markStatic$1(tree, "__static__".concat(index), false);
      return tree;
  }
  /**
   * Runtime helper for v-once.
   * Effectively it means marking the node as static with a unique key.
   */
  function markOnce(tree, index, key) {
      markStatic$1(tree, "__once__".concat(index).concat(key ? "_".concat(key) : ""), true);
      return tree;
  }
  function markStatic$1(tree, key, isOnce) {
      if (isArray(tree)) {
          for (var i = 0; i < tree.length; i++) {
              if (tree[i] && typeof tree[i] !== 'string') {
                  markStaticNode(tree[i], "".concat(key, "_").concat(i), isOnce);
              }
          }
      }
      else {
          markStaticNode(tree, key, isOnce);
      }
  }
  function markStaticNode(node, key, isOnce) {
      node.isStatic = true;
      node.key = key;
      node.isOnce = isOnce;
  }

  function bindObjectListeners(data, value) {
      if (value) {
          if (!isPlainObject(value)) {
              warn$2('v-on without argument expects an Object value', this);
          }
          else {
              var on = (data.on = data.on ? extend({}, data.on) : {});
              for (var key in value) {
                  var existing = on[key];
                  var ours = value[key];
                  on[key] = existing ? [].concat(existing, ours) : ours;
              }
          }
      }
      return data;
  }

  function resolveScopedSlots(fns, res, 
  // the following are added in 2.6
  hasDynamicKeys, contentHashKey) {
      res = res || { $stable: !hasDynamicKeys };
      for (var i = 0; i < fns.length; i++) {
          var slot = fns[i];
          if (isArray(slot)) {
              resolveScopedSlots(slot, res, hasDynamicKeys);
          }
          else if (slot) {
              // marker for reverse proxying v-slot without scope on this.$slots
              // @ts-expect-error
              if (slot.proxy) {
                  // @ts-expect-error
                  slot.fn.proxy = true;
              }
              res[slot.key] = slot.fn;
          }
      }
      if (contentHashKey) {
          res.$key = contentHashKey;
      }
      return res;
  }

  // helper to process dynamic keys for dynamic arguments in v-bind and v-on.
  function bindDynamicKeys(baseObj, values) {
      for (var i = 0; i < values.length; i += 2) {
          var key = values[i];
          if (typeof key === 'string' && key) {
              baseObj[values[i]] = values[i + 1];
          }
          else if (key !== '' && key !== null) {
              // null is a special value for explicitly removing a binding
              warn$2("Invalid value for dynamic directive argument (expected string or null): ".concat(key), this);
          }
      }
      return baseObj;
  }
  // helper to dynamically append modifier runtime markers to event names.
  // ensure only append when value is already string, otherwise it will be cast
  // to string and cause the type check to miss.
  function prependModifier(value, symbol) {
      return typeof value === 'string' ? symbol + value : value;
  }

  function installRenderHelpers(target) {
      target._o = markOnce;
      target._n = toNumber;
      target._s = toString;
      target._l = renderList;
      target._t = renderSlot;
      target._q = looseEqual;
      target._i = looseIndexOf;
      target._m = renderStatic;
      target._f = resolveFilter;
      target._k = checkKeyCodes;
      target._b = bindObjectProps;
      target._v = createTextVNode;
      target._e = createEmptyVNode;
      target._u = resolveScopedSlots;
      target._g = bindObjectListeners;
      target._d = bindDynamicKeys;
      target._p = prependModifier;
  }

  /**
   * Runtime helper for resolving raw children VNodes into a slot object.
   */
  function resolveSlots(children, context) {
      if (!children || !children.length) {
          return {};
      }
      var slots = {};
      for (var i = 0, l = children.length; i < l; i++) {
          var child = children[i];
          var data = child.data;
          // remove slot attribute if the node is resolved as a Vue slot node
          if (data && data.attrs && data.attrs.slot) {
              delete data.attrs.slot;
          }
          // named slots should only be respected if the vnode was rendered in the
          // same context.
          if ((child.context === context || child.fnContext === context) &&
              data &&
              data.slot != null) {
              var name_1 = data.slot;
              var slot = slots[name_1] || (slots[name_1] = []);
              if (child.tag === 'template') {
                  slot.push.apply(slot, child.children || []);
              }
              else {
                  slot.push(child);
              }
          }
          else {
              (slots.default || (slots.default = [])).push(child);
          }
      }
      // ignore slots that contains only whitespace
      for (var name_2 in slots) {
          if (slots[name_2].every(isWhitespace)) {
              delete slots[name_2];
          }
      }
      return slots;
  }
  function isWhitespace(node) {
      return (node.isComment && !node.asyncFactory) || node.text === ' ';
  }

  function isAsyncPlaceholder(node) {
      // @ts-expect-error not really boolean type
      return node.isComment && node.asyncFactory;
  }

  function normalizeScopedSlots(ownerVm, scopedSlots, normalSlots, prevScopedSlots) {
      var res;
      var hasNormalSlots = Object.keys(normalSlots).length > 0;
      var isStable = scopedSlots ? !!scopedSlots.$stable : !hasNormalSlots;
      var key = scopedSlots && scopedSlots.$key;
      if (!scopedSlots) {
          res = {};
      }
      else if (scopedSlots._normalized) {
          // fast path 1: child component re-render only, parent did not change
          return scopedSlots._normalized;
      }
      else if (isStable &&
          prevScopedSlots &&
          prevScopedSlots !== emptyObject &&
          key === prevScopedSlots.$key &&
          !hasNormalSlots &&
          !prevScopedSlots.$hasNormal) {
          // fast path 2: stable scoped slots w/ no normal slots to proxy,
          // only need to normalize once
          return prevScopedSlots;
      }
      else {
          res = {};
          for (var key_1 in scopedSlots) {
              if (scopedSlots[key_1] && key_1[0] !== '$') {
                  res[key_1] = normalizeScopedSlot(ownerVm, normalSlots, key_1, scopedSlots[key_1]);
              }
          }
      }
      // expose normal slots on scopedSlots
      for (var key_2 in normalSlots) {
          if (!(key_2 in res)) {
              res[key_2] = proxyNormalSlot(normalSlots, key_2);
          }
      }
      // avoriaz seems to mock a non-extensible $scopedSlots object
      // and when that is passed down this would cause an error
      if (scopedSlots && Object.isExtensible(scopedSlots)) {
          scopedSlots._normalized = res;
      }
      def(res, '$stable', isStable);
      def(res, '$key', key);
      def(res, '$hasNormal', hasNormalSlots);
      return res;
  }
  function normalizeScopedSlot(vm, normalSlots, key, fn) {
      var normalized = function () {
          var cur = currentInstance;
          setCurrentInstance(vm);
          var res = arguments.length ? fn.apply(null, arguments) : fn({});
          res =
              res && typeof res === 'object' && !isArray(res)
                  ? [res] // single vnode
                  : normalizeChildren(res);
          var vnode = res && res[0];
          setCurrentInstance(cur);
          return res &&
              (!vnode ||
                  (res.length === 1 && vnode.isComment && !isAsyncPlaceholder(vnode))) // #9658, #10391
              ? undefined
              : res;
      };
      // this is a slot using the new v-slot syntax without scope. although it is
      // compiled as a scoped slot, render fn users would expect it to be present
      // on this.$slots because the usage is semantically a normal slot.
      if (fn.proxy) {
          Object.defineProperty(normalSlots, key, {
              get: normalized,
              enumerable: true,
              configurable: true
          });
      }
      return normalized;
  }
  function proxyNormalSlot(slots, key) {
      return function () { return slots[key]; };
  }

  function initSetup(vm) {
      var options = vm.$options;
      var setup = options.setup;
      if (setup) {
          var ctx = (vm._setupContext = createSetupContext(vm));
          setCurrentInstance(vm);
          pushTarget();
          var setupResult = invokeWithErrorHandling(setup, null, [vm._props || shallowReactive({}), ctx], vm, "setup");
          popTarget();
          setCurrentInstance();
          if (isFunction(setupResult)) {
              // render function
              // @ts-ignore
              options.render = setupResult;
          }
          else if (isObject(setupResult)) {
              // bindings
              if (setupResult instanceof VNode) {
                  warn$2("setup() should not return VNodes directly - " +
                      "return a render function instead.");
              }
              vm._setupState = setupResult;
              // __sfc indicates compiled bindings from <script setup>
              if (!setupResult.__sfc) {
                  for (var key in setupResult) {
                      if (!isReserved(key)) {
                          proxyWithRefUnwrap(vm, setupResult, key);
                      }
                      else {
                          warn$2("Avoid using variables that start with _ or $ in setup().");
                      }
                  }
              }
              else {
                  // exposed for compiled render fn
                  var proxy = (vm._setupProxy = {});
                  for (var key in setupResult) {
                      if (key !== '__sfc') {
                          proxyWithRefUnwrap(proxy, setupResult, key);
                      }
                  }
              }
          }
          else if (setupResult !== undefined) {
              warn$2("setup() should return an object. Received: ".concat(setupResult === null ? 'null' : typeof setupResult));
          }
      }
  }
  function createSetupContext(vm) {
      var exposeCalled = false;
      return {
          get attrs() {
              if (!vm._attrsProxy) {
                  var proxy = (vm._attrsProxy = {});
                  def(proxy, '_v_attr_proxy', true);
                  syncSetupProxy(proxy, vm.$attrs, emptyObject, vm, '$attrs');
              }
              return vm._attrsProxy;
          },
          get listeners() {
              if (!vm._listenersProxy) {
                  var proxy = (vm._listenersProxy = {});
                  syncSetupProxy(proxy, vm.$listeners, emptyObject, vm, '$listeners');
              }
              return vm._listenersProxy;
          },
          get slots() {
              return initSlotsProxy(vm);
          },
          emit: bind$1(vm.$emit, vm),
          expose: function (exposed) {
              {
                  if (exposeCalled) {
                      warn$2("expose() should be called only once per setup().", vm);
                  }
                  exposeCalled = true;
              }
              if (exposed) {
                  Object.keys(exposed).forEach(function (key) {
                      return proxyWithRefUnwrap(vm, exposed, key);
                  });
              }
          }
      };
  }
  function syncSetupProxy(to, from, prev, instance, type) {
      var changed = false;
      for (var key in from) {
          if (!(key in to)) {
              changed = true;
              defineProxyAttr(to, key, instance, type);
          }
          else if (from[key] !== prev[key]) {
              changed = true;
          }
      }
      for (var key in to) {
          if (!(key in from)) {
              changed = true;
              delete to[key];
          }
      }
      return changed;
  }
  function defineProxyAttr(proxy, key, instance, type) {
      Object.defineProperty(proxy, key, {
          enumerable: true,
          configurable: true,
          get: function () {
              return instance[type][key];
          }
      });
  }
  function initSlotsProxy(vm) {
      if (!vm._slotsProxy) {
          syncSetupSlots((vm._slotsProxy = {}), vm.$scopedSlots);
      }
      return vm._slotsProxy;
  }
  function syncSetupSlots(to, from) {
      for (var key in from) {
          to[key] = from[key];
      }
      for (var key in to) {
          if (!(key in from)) {
              delete to[key];
          }
      }
  }
  /**
   * @internal use manual type def because public setup context type relies on
   * legacy VNode types
   */
  function useSlots() {
      return getContext().slots;
  }
  /**
   * @internal use manual type def because public setup context type relies on
   * legacy VNode types
   */
  function useAttrs() {
      return getContext().attrs;
  }
  /**
   * Vue 2 only
   * @internal use manual type def because public setup context type relies on
   * legacy VNode types
   */
  function useListeners() {
      return getContext().listeners;
  }
  function getContext() {
      if (!currentInstance) {
          warn$2("useContext() called without active instance.");
      }
      var vm = currentInstance;
      return vm._setupContext || (vm._setupContext = createSetupContext(vm));
  }
  /**
   * Runtime helper for merging default declarations. Imported by compiled code
   * only.
   * @internal
   */
  function mergeDefaults(raw, defaults) {
      var props = isArray(raw)
          ? raw.reduce(function (normalized, p) { return ((normalized[p] = {}), normalized); }, {})
          : raw;
      for (var key in defaults) {
          var opt = props[key];
          if (opt) {
              if (isArray(opt) || isFunction(opt)) {
                  props[key] = { type: opt, default: defaults[key] };
              }
              else {
                  opt.default = defaults[key];
              }
          }
          else if (opt === null) {
              props[key] = { default: defaults[key] };
          }
          else {
              warn$2("props default key \"".concat(key, "\" has no corresponding declaration."));
          }
      }
      return props;
  }

  function initRender(vm) {
      vm._vnode = null; // the root of the child tree
      vm._staticTrees = null; // v-once cached trees
      var options = vm.$options;
      var parentVnode = (vm.$vnode = options._parentVnode); // the placeholder node in parent tree
      var renderContext = parentVnode && parentVnode.context;
      vm.$slots = resolveSlots(options._renderChildren, renderContext);
      vm.$scopedSlots = parentVnode
          ? normalizeScopedSlots(vm.$parent, parentVnode.data.scopedSlots, vm.$slots)
          : emptyObject;
      // bind the createElement fn to this instance
      // so that we get proper render context inside it.
      // args order: tag, data, children, normalizationType, alwaysNormalize
      // internal version is used by render functions compiled from templates
      // @ts-expect-error
      vm._c = function (a, b, c, d) { return createElement$1(vm, a, b, c, d, false); };
      // normalization is always applied for the public version, used in
      // user-written render functions.
      // @ts-expect-error
      vm.$createElement = function (a, b, c, d) { return createElement$1(vm, a, b, c, d, true); };
      // $attrs & $listeners are exposed for easier HOC creation.
      // they need to be reactive so that HOCs using them are always updated
      var parentData = parentVnode && parentVnode.data;
      /* istanbul ignore else */
      {
          defineReactive(vm, '$attrs', (parentData && parentData.attrs) || emptyObject, function () {
              !isUpdatingChildComponent && warn$2("$attrs is readonly.", vm);
          }, true);
          defineReactive(vm, '$listeners', options._parentListeners || emptyObject, function () {
              !isUpdatingChildComponent && warn$2("$listeners is readonly.", vm);
          }, true);
      }
  }
  var currentRenderingInstance = null;
  function renderMixin(Vue) {
      // install runtime convenience helpers
      installRenderHelpers(Vue.prototype);
      Vue.prototype.$nextTick = function (fn) {
          return nextTick(fn, this);
      };
      Vue.prototype._render = function () {
          var vm = this;
          var _a = vm.$options, render = _a.render, _parentVnode = _a._parentVnode;
          if (_parentVnode && vm._isMounted) {
              vm.$scopedSlots = normalizeScopedSlots(vm.$parent, _parentVnode.data.scopedSlots, vm.$slots, vm.$scopedSlots);
              if (vm._slotsProxy) {
                  syncSetupSlots(vm._slotsProxy, vm.$scopedSlots);
              }
          }
          // set parent vnode. this allows render functions to have access
          // to the data on the placeholder node.
          vm.$vnode = _parentVnode;
          // render self
          var prevInst = currentInstance;
          var prevRenderInst = currentRenderingInstance;
          var vnode;
          try {
              setCurrentInstance(vm);
              currentRenderingInstance = vm;
              vnode = render.call(vm._renderProxy, vm.$createElement);
          }
          catch (e) {
              handleError(e, vm, "render");
              // return error render result,
              // or previous vnode to prevent render error causing blank component
              /* istanbul ignore else */
              if (vm.$options.renderError) {
                  try {
                      vnode = vm.$options.renderError.call(vm._renderProxy, vm.$createElement, e);
                  }
                  catch (e) {
                      handleError(e, vm, "renderError");
                      vnode = vm._vnode;
                  }
              }
              else {
                  vnode = vm._vnode;
              }
          }
          finally {
              currentRenderingInstance = prevRenderInst;
              setCurrentInstance(prevInst);
          }
          // if the returned array contains only a single node, allow it
          if (isArray(vnode) && vnode.length === 1) {
              vnode = vnode[0];
          }
          // return empty vnode in case the render function errored out
          if (!(vnode instanceof VNode)) {
              if (isArray(vnode)) {
                  warn$2('Multiple root nodes returned from render function. Render function ' +
                      'should return a single root node.', vm);
              }
              vnode = createEmptyVNode();
          }
          // set parent
          vnode.parent = _parentVnode;
          return vnode;
      };
  }

  function ensureCtor(comp, base) {
      if (comp.__esModule || (hasSymbol && comp[Symbol.toStringTag] === 'Module')) {
          comp = comp.default;
      }
      return isObject(comp) ? base.extend(comp) : comp;
  }
  function createAsyncPlaceholder(factory, data, context, children, tag) {
      var node = createEmptyVNode();
      node.asyncFactory = factory;
      node.asyncMeta = { data: data, context: context, children: children, tag: tag };
      return node;
  }
  function resolveAsyncComponent(factory, baseCtor) {
      if (isTrue(factory.error) && isDef(factory.errorComp)) {
          return factory.errorComp;
      }
      if (isDef(factory.resolved)) {
          return factory.resolved;
      }
      var owner = currentRenderingInstance;
      if (owner && isDef(factory.owners) && factory.owners.indexOf(owner) === -1) {
          // already pending
          factory.owners.push(owner);
      }
      if (isTrue(factory.loading) && isDef(factory.loadingComp)) {
          return factory.loadingComp;
      }
      if (owner && !isDef(factory.owners)) {
          var owners_1 = (factory.owners = [owner]);
          var sync_1 = true;
          var timerLoading_1 = null;
          var timerTimeout_1 = null;
          owner.$on('hook:destroyed', function () { return remove$2(owners_1, owner); });
          var forceRender_1 = function (renderCompleted) {
              for (var i = 0, l = owners_1.length; i < l; i++) {
                  owners_1[i].$forceUpdate();
              }
              if (renderCompleted) {
                  owners_1.length = 0;
                  if (timerLoading_1 !== null) {
                      clearTimeout(timerLoading_1);
                      timerLoading_1 = null;
                  }
                  if (timerTimeout_1 !== null) {
                      clearTimeout(timerTimeout_1);
                      timerTimeout_1 = null;
                  }
              }
          };
          var resolve = once(function (res) {
              // cache resolved
              factory.resolved = ensureCtor(res, baseCtor);
              // invoke callbacks only if this is not a synchronous resolve
              // (async resolves are shimmed as synchronous during SSR)
              if (!sync_1) {
                  forceRender_1(true);
              }
              else {
                  owners_1.length = 0;
              }
          });
          var reject_1 = once(function (reason) {
              warn$2("Failed to resolve async component: ".concat(String(factory)) +
                      (reason ? "\nReason: ".concat(reason) : ''));
              if (isDef(factory.errorComp)) {
                  factory.error = true;
                  forceRender_1(true);
              }
          });
          var res_1 = factory(resolve, reject_1);
          if (isObject(res_1)) {
              if (isPromise(res_1)) {
                  // () => Promise
                  if (isUndef(factory.resolved)) {
                      res_1.then(resolve, reject_1);
                  }
              }
              else if (isPromise(res_1.component)) {
                  res_1.component.then(resolve, reject_1);
                  if (isDef(res_1.error)) {
                      factory.errorComp = ensureCtor(res_1.error, baseCtor);
                  }
                  if (isDef(res_1.loading)) {
                      factory.loadingComp = ensureCtor(res_1.loading, baseCtor);
                      if (res_1.delay === 0) {
                          factory.loading = true;
                      }
                      else {
                          // @ts-expect-error NodeJS timeout type
                          timerLoading_1 = setTimeout(function () {
                              timerLoading_1 = null;
                              if (isUndef(factory.resolved) && isUndef(factory.error)) {
                                  factory.loading = true;
                                  forceRender_1(false);
                              }
                          }, res_1.delay || 200);
                      }
                  }
                  if (isDef(res_1.timeout)) {
                      // @ts-expect-error NodeJS timeout type
                      timerTimeout_1 = setTimeout(function () {
                          timerTimeout_1 = null;
                          if (isUndef(factory.resolved)) {
                              reject_1("timeout (".concat(res_1.timeout, "ms)") );
                          }
                      }, res_1.timeout);
                  }
              }
          }
          sync_1 = false;
          // return in case resolved synchronously
          return factory.loading ? factory.loadingComp : factory.resolved;
      }
  }

  function getFirstComponentChild(children) {
      if (isArray(children)) {
          for (var i = 0; i < children.length; i++) {
              var c = children[i];
              if (isDef(c) && (isDef(c.componentOptions) || isAsyncPlaceholder(c))) {
                  return c;
              }
          }
      }
  }

  function initEvents(vm) {
      vm._events = Object.create(null);
      vm._hasHookEvent = false;
      // init parent attached events
      var listeners = vm.$options._parentListeners;
      if (listeners) {
          updateComponentListeners(vm, listeners);
      }
  }
  var target$1;
  function add$1(event, fn) {
      target$1.$on(event, fn);
  }
  function remove$1(event, fn) {
      target$1.$off(event, fn);
  }
  function createOnceHandler$1(event, fn) {
      var _target = target$1;
      return function onceHandler() {
          var res = fn.apply(null, arguments);
          if (res !== null) {
              _target.$off(event, onceHandler);
          }
      };
  }
  function updateComponentListeners(vm, listeners, oldListeners) {
      target$1 = vm;
      updateListeners(listeners, oldListeners || {}, add$1, remove$1, createOnceHandler$1, vm);
      target$1 = undefined;
  }
  function eventsMixin(Vue) {
      var hookRE = /^hook:/;
      Vue.prototype.$on = function (event, fn) {
          var vm = this;
          if (isArray(event)) {
              for (var i = 0, l = event.length; i < l; i++) {
                  vm.$on(event[i], fn);
              }
          }
          else {
              (vm._events[event] || (vm._events[event] = [])).push(fn);
              // optimize hook:event cost by using a boolean flag marked at registration
              // instead of a hash lookup
              if (hookRE.test(event)) {
                  vm._hasHookEvent = true;
              }
          }
          return vm;
      };
      Vue.prototype.$once = function (event, fn) {
          var vm = this;
          function on() {
              vm.$off(event, on);
              fn.apply(vm, arguments);
          }
          on.fn = fn;
          vm.$on(event, on);
          return vm;
      };
      Vue.prototype.$off = function (event, fn) {
          var vm = this;
          // all
          if (!arguments.length) {
              vm._events = Object.create(null);
              return vm;
          }
          // array of events
          if (isArray(event)) {
              for (var i_1 = 0, l = event.length; i_1 < l; i_1++) {
                  vm.$off(event[i_1], fn);
              }
              return vm;
          }
          // specific event
          var cbs = vm._events[event];
          if (!cbs) {
              return vm;
          }
          if (!fn) {
              vm._events[event] = null;
              return vm;
          }
          // specific handler
          var cb;
          var i = cbs.length;
          while (i--) {
              cb = cbs[i];
              if (cb === fn || cb.fn === fn) {
                  cbs.splice(i, 1);
                  break;
              }
          }
          return vm;
      };
      Vue.prototype.$emit = function (event) {
          var vm = this;
          {
              var lowerCaseEvent = event.toLowerCase();
              if (lowerCaseEvent !== event && vm._events[lowerCaseEvent]) {
                  tip("Event \"".concat(lowerCaseEvent, "\" is emitted in component ") +
                      "".concat(formatComponentName(vm), " but the handler is registered for \"").concat(event, "\". ") +
                      "Note that HTML attributes are case-insensitive and you cannot use " +
                      "v-on to listen to camelCase events when using in-DOM templates. " +
                      "You should probably use \"".concat(hyphenate(event), "\" instead of \"").concat(event, "\"."));
              }
          }
          var cbs = vm._events[event];
          if (cbs) {
              cbs = cbs.length > 1 ? toArray(cbs) : cbs;
              var args = toArray(arguments, 1);
              var info = "event handler for \"".concat(event, "\"");
              for (var i = 0, l = cbs.length; i < l; i++) {
                  invokeWithErrorHandling(cbs[i], vm, args, vm, info);
              }
          }
          return vm;
      };
  }

  var activeEffectScope;
  var EffectScope = /** @class */ (function () {
      function EffectScope(detached) {
          if (detached === void 0) { detached = false; }
          this.detached = detached;
          /**
           * @internal
           */
          this.active = true;
          /**
           * @internal
           */
          this.effects = [];
          /**
           * @internal
           */
          this.cleanups = [];
          this.parent = activeEffectScope;
          if (!detached && activeEffectScope) {
              this.index =
                  (activeEffectScope.scopes || (activeEffectScope.scopes = [])).push(this) - 1;
          }
      }
      EffectScope.prototype.run = function (fn) {
          if (this.active) {
              var currentEffectScope = activeEffectScope;
              try {
                  activeEffectScope = this;
                  return fn();
              }
              finally {
                  activeEffectScope = currentEffectScope;
              }
          }
          else {
              warn$2("cannot run an inactive effect scope.");
          }
      };
      /**
       * This should only be called on non-detached scopes
       * @internal
       */
      EffectScope.prototype.on = function () {
          activeEffectScope = this;
      };
      /**
       * This should only be called on non-detached scopes
       * @internal
       */
      EffectScope.prototype.off = function () {
          activeEffectScope = this.parent;
      };
      EffectScope.prototype.stop = function (fromParent) {
          if (this.active) {
              var i = void 0, l = void 0;
              for (i = 0, l = this.effects.length; i < l; i++) {
                  this.effects[i].teardown();
              }
              for (i = 0, l = this.cleanups.length; i < l; i++) {
                  this.cleanups[i]();
              }
              if (this.scopes) {
                  for (i = 0, l = this.scopes.length; i < l; i++) {
                      this.scopes[i].stop(true);
                  }
              }
              // nested scope, dereference from parent to avoid memory leaks
              if (!this.detached && this.parent && !fromParent) {
                  // optimized O(1) removal
                  var last = this.parent.scopes.pop();
                  if (last && last !== this) {
                      this.parent.scopes[this.index] = last;
                      last.index = this.index;
                  }
              }
              this.parent = undefined;
              this.active = false;
          }
      };
      return EffectScope;
  }());
  function effectScope(detached) {
      return new EffectScope(detached);
  }
  /**
   * @internal
   */
  function recordEffectScope(effect, scope) {
      if (scope === void 0) { scope = activeEffectScope; }
      if (scope && scope.active) {
          scope.effects.push(effect);
      }
  }
  function getCurrentScope() {
      return activeEffectScope;
  }
  function onScopeDispose(fn) {
      if (activeEffectScope) {
          activeEffectScope.cleanups.push(fn);
      }
      else {
          warn$2("onScopeDispose() is called when there is no active effect scope" +
              " to be associated with.");
      }
  }

  var activeInstance = null;
  var isUpdatingChildComponent = false;
  function setActiveInstance(vm) {
      var prevActiveInstance = activeInstance;
      activeInstance = vm;
      return function () {
          activeInstance = prevActiveInstance;
      };
  }
  function initLifecycle(vm) {
      var options = vm.$options;
      // locate first non-abstract parent
      var parent = options.parent;
      if (parent && !options.abstract) {
          while (parent.$options.abstract && parent.$parent) {
              parent = parent.$parent;
          }
          parent.$children.push(vm);
      }
      vm.$parent = parent;
      vm.$root = parent ? parent.$root : vm;
      vm.$children = [];
      vm.$refs = {};
      vm._provided = parent ? parent._provided : Object.create(null);
      vm._watcher = null;
      vm._inactive = null;
      vm._directInactive = false;
      vm._isMounted = false;
      vm._isDestroyed = false;
      vm._isBeingDestroyed = false;
  }
  function lifecycleMixin(Vue) {
      Vue.prototype._update = function (vnode, hydrating) {
          var vm = this;
          var prevEl = vm.$el;
          var prevVnode = vm._vnode;
          var restoreActiveInstance = setActiveInstance(vm);
          vm._vnode = vnode;
          // Vue.prototype.__patch__ is injected in entry points
          // based on the rendering backend used.
          if (!prevVnode) {
              // initial render
              vm.$el = vm.__patch__(vm.$el, vnode, hydrating, false /* removeOnly */);
          }
          else {
              // updates
              vm.$el = vm.__patch__(prevVnode, vnode);
          }
          restoreActiveInstance();
          // update __vue__ reference
          if (prevEl) {
              prevEl.__vue__ = null;
          }
          if (vm.$el) {
              vm.$el.__vue__ = vm;
          }
          // if parent is an HOC, update its $el as well
          var wrapper = vm;
          while (wrapper &&
              wrapper.$vnode &&
              wrapper.$parent &&
              wrapper.$vnode === wrapper.$parent._vnode) {
              wrapper.$parent.$el = wrapper.$el;
              wrapper = wrapper.$parent;
          }
          // updated hook is called by the scheduler to ensure that children are
          // updated in a parent's updated hook.
      };
      Vue.prototype.$forceUpdate = function () {
          var vm = this;
          if (vm._watcher) {
              vm._watcher.update();
          }
      };
      Vue.prototype.$destroy = function () {
          var vm = this;
          if (vm._isBeingDestroyed) {
              return;
          }
          callHook$1(vm, 'beforeDestroy');
          vm._isBeingDestroyed = true;
          // remove self from parent
          var parent = vm.$parent;
          if (parent && !parent._isBeingDestroyed && !vm.$options.abstract) {
              remove$2(parent.$children, vm);
          }
          // teardown scope. this includes both the render watcher and other
          // watchers created
          vm._scope.stop();
          // remove reference from data ob
          // frozen object may not have observer.
          if (vm._data.__ob__) {
              vm._data.__ob__.vmCount--;
          }
          // call the last hook...
          vm._isDestroyed = true;
          // invoke destroy hooks on current rendered tree
          vm.__patch__(vm._vnode, null);
          // fire destroyed hook
          callHook$1(vm, 'destroyed');
          // turn off all instance listeners.
          vm.$off();
          // remove __vue__ reference
          if (vm.$el) {
              vm.$el.__vue__ = null;
          }
          // release circular reference (#6759)
          if (vm.$vnode) {
              vm.$vnode.parent = null;
          }
      };
  }
  function mountComponent(vm, el, hydrating) {
      vm.$el = el;
      if (!vm.$options.render) {
          // @ts-expect-error invalid type
          vm.$options.render = createEmptyVNode;
          {
              /* istanbul ignore if */
              if ((vm.$options.template && vm.$options.template.charAt(0) !== '#') ||
                  vm.$options.el ||
                  el) {
                  warn$2('You are using the runtime-only build of Vue where the template ' +
                      'compiler is not available. Either pre-compile the templates into ' +
                      'render functions, or use the compiler-included build.', vm);
              }
              else {
                  warn$2('Failed to mount component: template or render function not defined.', vm);
              }
          }
      }
      callHook$1(vm, 'beforeMount');
      var updateComponent;
      /* istanbul ignore if */
      if (config.performance && mark) {
          updateComponent = function () {
              var name = vm._name;
              var id = vm._uid;
              var startTag = "vue-perf-start:".concat(id);
              var endTag = "vue-perf-end:".concat(id);
              mark(startTag);
              var vnode = vm._render();
              mark(endTag);
              measure("vue ".concat(name, " render"), startTag, endTag);
              mark(startTag);
              vm._update(vnode, hydrating);
              mark(endTag);
              measure("vue ".concat(name, " patch"), startTag, endTag);
          };
      }
      else {
          updateComponent = function () {
              vm._update(vm._render(), hydrating);
          };
      }
      var watcherOptions = {
          before: function () {
              if (vm._isMounted && !vm._isDestroyed) {
                  callHook$1(vm, 'beforeUpdate');
              }
          }
      };
      {
          watcherOptions.onTrack = function (e) { return callHook$1(vm, 'renderTracked', [e]); };
          watcherOptions.onTrigger = function (e) { return callHook$1(vm, 'renderTriggered', [e]); };
      }
      // we set this to vm._watcher inside the watcher's constructor
      // since the watcher's initial patch may call $forceUpdate (e.g. inside child
      // component's mounted hook), which relies on vm._watcher being already defined
      new Watcher(vm, updateComponent, noop, watcherOptions, true /* isRenderWatcher */);
      hydrating = false;
      // flush buffer for flush: "pre" watchers queued in setup()
      var preWatchers = vm._preWatchers;
      if (preWatchers) {
          for (var i = 0; i < preWatchers.length; i++) {
              preWatchers[i].run();
          }
      }
      // manually mounted instance, call mounted on self
      // mounted is called for render-created child components in its inserted hook
      if (vm.$vnode == null) {
          vm._isMounted = true;
          callHook$1(vm, 'mounted');
      }
      return vm;
  }
  function updateChildComponent(vm, propsData, listeners, parentVnode, renderChildren) {
      {
          isUpdatingChildComponent = true;
      }
      // determine whether component has slot children
      // we need to do this before overwriting $options._renderChildren.
      // check if there are dynamic scopedSlots (hand-written or compiled but with
      // dynamic slot names). Static scoped slots compiled from template has the
      // "$stable" marker.
      var newScopedSlots = parentVnode.data.scopedSlots;
      var oldScopedSlots = vm.$scopedSlots;
      var hasDynamicScopedSlot = !!((newScopedSlots && !newScopedSlots.$stable) ||
          (oldScopedSlots !== emptyObject && !oldScopedSlots.$stable) ||
          (newScopedSlots && vm.$scopedSlots.$key !== newScopedSlots.$key) ||
          (!newScopedSlots && vm.$scopedSlots.$key));
      // Any static slot children from the parent may have changed during parent's
      // update. Dynamic scoped slots may also have changed. In such cases, a forced
      // update is necessary to ensure correctness.
      var needsForceUpdate = !!(renderChildren || // has new static slots
          vm.$options._renderChildren || // has old static slots
          hasDynamicScopedSlot);
      var prevVNode = vm.$vnode;
      vm.$options._parentVnode = parentVnode;
      vm.$vnode = parentVnode; // update vm's placeholder node without re-render
      if (vm._vnode) {
          // update child tree's parent
          vm._vnode.parent = parentVnode;
      }
      vm.$options._renderChildren = renderChildren;
      // update $attrs and $listeners hash
      // these are also reactive so they may trigger child update if the child
      // used them during render
      var attrs = parentVnode.data.attrs || emptyObject;
      if (vm._attrsProxy) {
          // force update if attrs are accessed and has changed since it may be
          // passed to a child component.
          if (syncSetupProxy(vm._attrsProxy, attrs, (prevVNode.data && prevVNode.data.attrs) || emptyObject, vm, '$attrs')) {
              needsForceUpdate = true;
          }
      }
      vm.$attrs = attrs;
      // update listeners
      listeners = listeners || emptyObject;
      var prevListeners = vm.$options._parentListeners;
      if (vm._listenersProxy) {
          syncSetupProxy(vm._listenersProxy, listeners, prevListeners || emptyObject, vm, '$listeners');
      }
      vm.$listeners = vm.$options._parentListeners = listeners;
      updateComponentListeners(vm, listeners, prevListeners);
      // update props
      if (propsData && vm.$options.props) {
          toggleObserving(false);
          var props = vm._props;
          var propKeys = vm.$options._propKeys || [];
          for (var i = 0; i < propKeys.length; i++) {
              var key = propKeys[i];
              var propOptions = vm.$options.props; // wtf flow?
              props[key] = validateProp(key, propOptions, propsData, vm);
          }
          toggleObserving(true);
          // keep a copy of raw propsData
          vm.$options.propsData = propsData;
      }
      // resolve slots + force update if has children
      if (needsForceUpdate) {
          vm.$slots = resolveSlots(renderChildren, parentVnode.context);
          vm.$forceUpdate();
      }
      {
          isUpdatingChildComponent = false;
      }
  }
  function isInInactiveTree(vm) {
      while (vm && (vm = vm.$parent)) {
          if (vm._inactive)
              return true;
      }
      return false;
  }
  function activateChildComponent(vm, direct) {
      if (direct) {
          vm._directInactive = false;
          if (isInInactiveTree(vm)) {
              return;
          }
      }
      else if (vm._directInactive) {
          return;
      }
      if (vm._inactive || vm._inactive === null) {
          vm._inactive = false;
          for (var i = 0; i < vm.$children.length; i++) {
              activateChildComponent(vm.$children[i]);
          }
          callHook$1(vm, 'activated');
      }
  }
  function deactivateChildComponent(vm, direct) {
      if (direct) {
          vm._directInactive = true;
          if (isInInactiveTree(vm)) {
              return;
          }
      }
      if (!vm._inactive) {
          vm._inactive = true;
          for (var i = 0; i < vm.$children.length; i++) {
              deactivateChildComponent(vm.$children[i]);
          }
          callHook$1(vm, 'deactivated');
      }
  }
  function callHook$1(vm, hook, args, setContext) {
      if (setContext === void 0) { setContext = true; }
      // #7573 disable dep collection when invoking lifecycle hooks
      pushTarget();
      var prevInst = currentInstance;
      var prevScope = getCurrentScope();
      setContext && setCurrentInstance(vm);
      var handlers = vm.$options[hook];
      var info = "".concat(hook, " hook");
      if (handlers) {
          for (var i = 0, j = handlers.length; i < j; i++) {
              invokeWithErrorHandling(handlers[i], vm, args || null, vm, info);
          }
      }
      if (vm._hasHookEvent) {
          vm.$emit('hook:' + hook);
      }
      if (setContext) {
          setCurrentInstance(prevInst);
          prevScope && prevScope.on();
      }
      popTarget();
  }

  var MAX_UPDATE_COUNT = 100;
  var queue = [];
  var activatedChildren = [];
  var has = {};
  var circular = {};
  var waiting = false;
  var flushing = false;
  var index$1 = 0;
  /**
   * Reset the scheduler's state.
   */
  function resetSchedulerState() {
      index$1 = queue.length = activatedChildren.length = 0;
      has = {};
      {
          circular = {};
      }
      waiting = flushing = false;
  }
  // Async edge case #6566 requires saving the timestamp when event listeners are
  // attached. However, calling performance.now() has a perf overhead especially
  // if the page has thousands of event listeners. Instead, we take a timestamp
  // every time the scheduler flushes and use that for all event listeners
  // attached during that flush.
  var currentFlushTimestamp = 0;
  // Async edge case fix requires storing an event listener's attach timestamp.
  var getNow = Date.now;
  // Determine what event timestamp the browser is using. Annoyingly, the
  // timestamp can either be hi-res (relative to page load) or low-res
  // (relative to UNIX epoch), so in order to compare time we have to use the
  // same timestamp type when saving the flush timestamp.
  // All IE versions use low-res event timestamps, and have problematic clock
  // implementations (#9632)
  if (inBrowser && !isIE) {
      var performance_1 = window.performance;
      if (performance_1 &&
          typeof performance_1.now === 'function' &&
          getNow() > document.createEvent('Event').timeStamp) {
          // if the event timestamp, although evaluated AFTER the Date.now(), is
          // smaller than it, it means the event is using a hi-res timestamp,
          // and we need to use the hi-res version for event listener timestamps as
          // well.
          getNow = function () { return performance_1.now(); };
      }
  }
  var sortCompareFn = function (a, b) {
      if (a.post) {
          if (!b.post)
              return 1;
      }
      else if (b.post) {
          return -1;
      }
      return a.id - b.id;
  };
  /**
   * Flush both queues and run the watchers.
   */
  function flushSchedulerQueue() {
      currentFlushTimestamp = getNow();
      flushing = true;
      var watcher, id;
      // Sort queue before flush.
      // This ensures that:
      // 1. Components are updated from parent to child. (because parent is always
      //    created before the child)
      // 2. A component's user watchers are run before its render watcher (because
      //    user watchers are created before the render watcher)
      // 3. If a component is destroyed during a parent component's watcher run,
      //    its watchers can be skipped.
      queue.sort(sortCompareFn);
      // do not cache length because more watchers might be pushed
      // as we run existing watchers
      for (index$1 = 0; index$1 < queue.length; index$1++) {
          watcher = queue[index$1];
          if (watcher.before) {
              watcher.before();
          }
          id = watcher.id;
          has[id] = null;
          watcher.run();
          // in dev build, check and stop circular updates.
          if (has[id] != null) {
              circular[id] = (circular[id] || 0) + 1;
              if (circular[id] > MAX_UPDATE_COUNT) {
                  warn$2('You may have an infinite update loop ' +
                      (watcher.user
                          ? "in watcher with expression \"".concat(watcher.expression, "\"")
                          : "in a component render function."), watcher.vm);
                  break;
              }
          }
      }
      // keep copies of post queues before resetting state
      var activatedQueue = activatedChildren.slice();
      var updatedQueue = queue.slice();
      resetSchedulerState();
      // call component updated and activated hooks
      callActivatedHooks(activatedQueue);
      callUpdatedHooks(updatedQueue);
      cleanupDeps();
      // devtool hook
      /* istanbul ignore if */
      if (devtools && config.devtools) {
          devtools.emit('flush');
      }
  }
  function callUpdatedHooks(queue) {
      var i = queue.length;
      while (i--) {
          var watcher = queue[i];
          var vm = watcher.vm;
          if (vm && vm._watcher === watcher && vm._isMounted && !vm._isDestroyed) {
              callHook$1(vm, 'updated');
          }
      }
  }
  /**
   * Queue a kept-alive component that was activated during patch.
   * The queue will be processed after the entire tree has been patched.
   */
  function queueActivatedComponent(vm) {
      // setting _inactive to false here so that a render function can
      // rely on checking whether it's in an inactive tree (e.g. router-view)
      vm._inactive = false;
      activatedChildren.push(vm);
  }
  function callActivatedHooks(queue) {
      for (var i = 0; i < queue.length; i++) {
          queue[i]._inactive = true;
          activateChildComponent(queue[i], true /* true */);
      }
  }
  /**
   * Push a watcher into the watcher queue.
   * Jobs with duplicate IDs will be skipped unless it's
   * pushed when the queue is being flushed.
   */
  function queueWatcher(watcher) {
      var id = watcher.id;
      if (has[id] != null) {
          return;
      }
      if (watcher === Dep.target && watcher.noRecurse) {
          return;
      }
      has[id] = true;
      if (!flushing) {
          queue.push(watcher);
      }
      else {
          // if already flushing, splice the watcher based on its id
          // if already past its id, it will be run next immediately.
          var i = queue.length - 1;
          while (i > index$1 && queue[i].id > watcher.id) {
              i--;
          }
          queue.splice(i + 1, 0, watcher);
      }
      // queue the flush
      if (!waiting) {
          waiting = true;
          if (!config.async) {
              flushSchedulerQueue();
              return;
          }
          nextTick(flushSchedulerQueue);
      }
  }

  var WATCHER = "watcher";
  var WATCHER_CB = "".concat(WATCHER, " callback");
  var WATCHER_GETTER = "".concat(WATCHER, " getter");
  var WATCHER_CLEANUP = "".concat(WATCHER, " cleanup");
  // Simple effect.
  function watchEffect(effect, options) {
      return doWatch(effect, null, options);
  }
  function watchPostEffect(effect, options) {
      return doWatch(effect, null, (__assign(__assign({}, options), { flush: 'post' }) ));
  }
  function watchSyncEffect(effect, options) {
      return doWatch(effect, null, (__assign(__assign({}, options), { flush: 'sync' }) ));
  }
  // initial value for watchers to trigger on undefined initial values
  var INITIAL_WATCHER_VALUE = {};
  // implementation
  function watch(source, cb, options) {
      if (typeof cb !== 'function') {
          warn$2("`watch(fn, options?)` signature has been moved to a separate API. " +
              "Use `watchEffect(fn, options?)` instead. `watch` now only " +
              "supports `watch(source, cb, options?) signature.");
      }
      return doWatch(source, cb, options);
  }
  function doWatch(source, cb, _a) {
      var _b = _a === void 0 ? emptyObject : _a, immediate = _b.immediate, deep = _b.deep, _c = _b.flush, flush = _c === void 0 ? 'pre' : _c, onTrack = _b.onTrack, onTrigger = _b.onTrigger;
      if (!cb) {
          if (immediate !== undefined) {
              warn$2("watch() \"immediate\" option is only respected when using the " +
                  "watch(source, callback, options?) signature.");
          }
          if (deep !== undefined) {
              warn$2("watch() \"deep\" option is only respected when using the " +
                  "watch(source, callback, options?) signature.");
          }
      }
      var warnInvalidSource = function (s) {
          warn$2("Invalid watch source: ".concat(s, ". A watch source can only be a getter/effect ") +
              "function, a ref, a reactive object, or an array of these types.");
      };
      var instance = currentInstance;
      var call = function (fn, type, args) {
          if (args === void 0) { args = null; }
          var res = invokeWithErrorHandling(fn, null, args, instance, type);
          if (deep && res && res.__ob__)
              res.__ob__.dep.depend();
          return res;
      };
      var getter;
      var forceTrigger = false;
      var isMultiSource = false;
      if (isRef(source)) {
          getter = function () { return source.value; };
          forceTrigger = isShallow(source);
      }
      else if (isReactive(source)) {
          getter = function () {
              source.__ob__.dep.depend();
              return source;
          };
          deep = true;
      }
      else if (isArray(source)) {
          isMultiSource = true;
          forceTrigger = source.some(function (s) { return isReactive(s) || isShallow(s); });
          getter = function () {
              return source.map(function (s) {
                  if (isRef(s)) {
                      return s.value;
                  }
                  else if (isReactive(s)) {
                      s.__ob__.dep.depend();
                      return traverse(s);
                  }
                  else if (isFunction(s)) {
                      return call(s, WATCHER_GETTER);
                  }
                  else {
                      warnInvalidSource(s);
                  }
              });
          };
      }
      else if (isFunction(source)) {
          if (cb) {
              // getter with cb
              getter = function () { return call(source, WATCHER_GETTER); };
          }
          else {
              // no cb -> simple effect
              getter = function () {
                  if (instance && instance._isDestroyed) {
                      return;
                  }
                  if (cleanup) {
                      cleanup();
                  }
                  return call(source, WATCHER, [onCleanup]);
              };
          }
      }
      else {
          getter = noop;
          warnInvalidSource(source);
      }
      if (cb && deep) {
          var baseGetter_1 = getter;
          getter = function () { return traverse(baseGetter_1()); };
      }
      var cleanup;
      var onCleanup = function (fn) {
          cleanup = watcher.onStop = function () {
              call(fn, WATCHER_CLEANUP);
          };
      };
      // in SSR there is no need to setup an actual effect, and it should be noop
      // unless it's eager
      if (isServerRendering()) {
          // we will also not call the invalidate callback (+ runner is not set up)
          onCleanup = noop;
          if (!cb) {
              getter();
          }
          else if (immediate) {
              call(cb, WATCHER_CB, [
                  getter(),
                  isMultiSource ? [] : undefined,
                  onCleanup
              ]);
          }
          return noop;
      }
      var watcher = new Watcher(currentInstance, getter, noop, {
          lazy: true
      });
      watcher.noRecurse = !cb;
      var oldValue = isMultiSource ? [] : INITIAL_WATCHER_VALUE;
      // overwrite default run
      watcher.run = function () {
          if (!watcher.active) {
              return;
          }
          if (cb) {
              // watch(source, cb)
              var newValue = watcher.get();
              if (deep ||
                  forceTrigger ||
                  (isMultiSource
                      ? newValue.some(function (v, i) {
                          return hasChanged(v, oldValue[i]);
                      })
                      : hasChanged(newValue, oldValue))) {
                  // cleanup before running cb again
                  if (cleanup) {
                      cleanup();
                  }
                  call(cb, WATCHER_CB, [
                      newValue,
                      // pass undefined as the old value when it's changed for the first time
                      oldValue === INITIAL_WATCHER_VALUE ? undefined : oldValue,
                      onCleanup
                  ]);
                  oldValue = newValue;
              }
          }
          else {
              // watchEffect
              watcher.get();
          }
      };
      if (flush === 'sync') {
          watcher.update = watcher.run;
      }
      else if (flush === 'post') {
          watcher.post = true;
          watcher.update = function () { return queueWatcher(watcher); };
      }
      else {
          // pre
          watcher.update = function () {
              if (instance && instance === currentInstance && !instance._isMounted) {
                  // pre-watcher triggered before
                  var buffer = instance._preWatchers || (instance._preWatchers = []);
                  if (buffer.indexOf(watcher) < 0)
                      buffer.push(watcher);
              }
              else {
                  queueWatcher(watcher);
              }
          };
      }
      {
          watcher.onTrack = onTrack;
          watcher.onTrigger = onTrigger;
      }
      // initial run
      if (cb) {
          if (immediate) {
              watcher.run();
          }
          else {
              oldValue = watcher.get();
          }
      }
      else if (flush === 'post' && instance) {
          instance.$once('hook:mounted', function () { return watcher.get(); });
      }
      else {
          watcher.get();
      }
      return function () {
          watcher.teardown();
      };
  }

  function provide(key, value) {
      if (!currentInstance) {
          {
              warn$2("provide() can only be used inside setup().");
          }
      }
      else {
          // TS doesn't allow symbol as index type
          resolveProvided(currentInstance)[key] = value;
      }
  }
  function resolveProvided(vm) {
      // by default an instance inherits its parent's provides object
      // but when it needs to provide values of its own, it creates its
      // own provides object using parent provides object as prototype.
      // this way in `inject` we can simply look up injections from direct
      // parent and let the prototype chain do the work.
      var existing = vm._provided;
      var parentProvides = vm.$parent && vm.$parent._provided;
      if (parentProvides === existing) {
          return (vm._provided = Object.create(parentProvides));
      }
      else {
          return existing;
      }
  }
  function inject(key, defaultValue, treatDefaultAsFactory) {
      if (treatDefaultAsFactory === void 0) { treatDefaultAsFactory = false; }
      // fallback to `currentRenderingInstance` so that this can be called in
      // a functional component
      var instance = currentInstance;
      if (instance) {
          // #2400
          // to support `app.use` plugins,
          // fallback to appContext's `provides` if the instance is at root
          var provides = instance.$parent && instance.$parent._provided;
          if (provides && key in provides) {
              // TS doesn't allow symbol as index type
              return provides[key];
          }
          else if (arguments.length > 1) {
              return treatDefaultAsFactory && isFunction(defaultValue)
                  ? defaultValue.call(instance)
                  : defaultValue;
          }
          else {
              warn$2("injection \"".concat(String(key), "\" not found."));
          }
      }
      else {
          warn$2("inject() can only be used inside setup() or functional components.");
      }
  }

  /**
   * @internal this function needs manual public type declaration because it relies
   * on previously manually authored types from Vue 2
   */
  function h(type, props, children) {
      if (!currentInstance) {
          warn$2("globally imported h() can only be invoked when there is an active " +
                  "component instance, e.g. synchronously in a component's render or setup function.");
      }
      return createElement$1(currentInstance, type, props, children, 2, true);
  }

  function handleError(err, vm, info) {
      // Deactivate deps tracking while processing error handler to avoid possible infinite rendering.
      // See: https://github.com/vuejs/vuex/issues/1505
      pushTarget();
      try {
          if (vm) {
              var cur = vm;
              while ((cur = cur.$parent)) {
                  var hooks = cur.$options.errorCaptured;
                  if (hooks) {
                      for (var i = 0; i < hooks.length; i++) {
                          try {
                              var capture = hooks[i].call(cur, err, vm, info) === false;
                              if (capture)
                                  return;
                          }
                          catch (e) {
                              globalHandleError(e, cur, 'errorCaptured hook');
                          }
                      }
                  }
              }
          }
          globalHandleError(err, vm, info);
      }
      finally {
          popTarget();
      }
  }
  function invokeWithErrorHandling(handler, context, args, vm, info) {
      var res;
      try {
          res = args ? handler.apply(context, args) : handler.call(context);
          if (res && !res._isVue && isPromise(res) && !res._handled) {
              res.catch(function (e) { return handleError(e, vm, info + " (Promise/async)"); });
              res._handled = true;
          }
      }
      catch (e) {
          handleError(e, vm, info);
      }
      return res;
  }
  function globalHandleError(err, vm, info) {
      if (config.errorHandler) {
          try {
              return config.errorHandler.call(null, err, vm, info);
          }
          catch (e) {
              // if the user intentionally throws the original error in the handler,
              // do not log it twice
              if (e !== err) {
                  logError(e, null, 'config.errorHandler');
              }
          }
      }
      logError(err, vm, info);
  }
  function logError(err, vm, info) {
      {
          warn$2("Error in ".concat(info, ": \"").concat(err.toString(), "\""), vm);
      }
      /* istanbul ignore else */
      if (inBrowser && typeof console !== 'undefined') {
          console.error(err);
      }
      else {
          throw err;
      }
  }

  /* globals MutationObserver */
  var isUsingMicroTask = false;
  var callbacks = [];
  var pending = false;
  function flushCallbacks() {
      pending = false;
      var copies = callbacks.slice(0);
      callbacks.length = 0;
      for (var i = 0; i < copies.length; i++) {
          copies[i]();
      }
  }
  // Here we have async deferring wrappers using microtasks.
  // In 2.5 we used (macro) tasks (in combination with microtasks).
  // However, it has subtle problems when state is changed right before repaint
  // (e.g. #6813, out-in transitions).
  // Also, using (macro) tasks in event handler would cause some weird behaviors
  // that cannot be circumvented (e.g. #7109, #7153, #7546, #7834, #8109).
  // So we now use microtasks everywhere, again.
  // A major drawback of this tradeoff is that there are some scenarios
  // where microtasks have too high a priority and fire in between supposedly
  // sequential events (e.g. #4521, #6690, which have workarounds)
  // or even between bubbling of the same event (#6566).
  var timerFunc;
  // The nextTick behavior leverages the microtask queue, which can be accessed
  // via either native Promise.then or MutationObserver.
  // MutationObserver has wider support, however it is seriously bugged in
  // UIWebView in iOS >= 9.3.3 when triggered in touch event handlers. It
  // completely stops working after triggering a few times... so, if native
  // Promise is available, we will use it:
  /* istanbul ignore next, $flow-disable-line */
  if (typeof Promise !== 'undefined' && isNative(Promise)) {
      var p_1 = Promise.resolve();
      timerFunc = function () {
          p_1.then(flushCallbacks);
          // In problematic UIWebViews, Promise.then doesn't completely break, but
          // it can get stuck in a weird state where callbacks are pushed into the
          // microtask queue but the queue isn't being flushed, until the browser
          // needs to do some other work, e.g. handle a timer. Therefore we can
          // "force" the microtask queue to be flushed by adding an empty timer.
          if (isIOS)
              setTimeout(noop);
      };
      isUsingMicroTask = true;
  }
  else if (!isIE &&
      typeof MutationObserver !== 'undefined' &&
      (isNative(MutationObserver) ||
          // PhantomJS and iOS 7.x
          MutationObserver.toString() === '[object MutationObserverConstructor]')) {
      // Use MutationObserver where native Promise is not available,
      // e.g. PhantomJS, iOS7, Android 4.4
      // (#6466 MutationObserver is unreliable in IE11)
      var counter_1 = 1;
      var observer = new MutationObserver(flushCallbacks);
      var textNode_1 = document.createTextNode(String(counter_1));
      observer.observe(textNode_1, {
          characterData: true
      });
      timerFunc = function () {
          counter_1 = (counter_1 + 1) % 2;
          textNode_1.data = String(counter_1);
      };
      isUsingMicroTask = true;
  }
  else if (typeof setImmediate !== 'undefined' && isNative(setImmediate)) {
      // Fallback to setImmediate.
      // Technically it leverages the (macro) task queue,
      // but it is still a better choice than setTimeout.
      timerFunc = function () {
          setImmediate(flushCallbacks);
      };
  }
  else {
      // Fallback to setTimeout.
      timerFunc = function () {
          setTimeout(flushCallbacks, 0);
      };
  }
  /**
   * @internal
   */
  function nextTick(cb, ctx) {
      var _resolve;
      callbacks.push(function () {
          if (cb) {
              try {
                  cb.call(ctx);
              }
              catch (e) {
                  handleError(e, ctx, 'nextTick');
              }
          }
          else if (_resolve) {
              _resolve(ctx);
          }
      });
      if (!pending) {
          pending = true;
          timerFunc();
      }
      // $flow-disable-line
      if (!cb && typeof Promise !== 'undefined') {
          return new Promise(function (resolve) {
              _resolve = resolve;
          });
      }
  }

  function useCssModule(name) {
      /* istanbul ignore else */
      {
          {
              warn$2("useCssModule() is not supported in the global build.");
          }
          return emptyObject;
      }
  }

  /**
   * Runtime helper for SFC's CSS variable injection feature.
   * @private
   */
  function useCssVars(getter) {
      if (!inBrowser && !false)
          return;
      var instance = currentInstance;
      if (!instance) {
          warn$2("useCssVars is called without current active component instance.");
          return;
      }
      watchPostEffect(function () {
          var el = instance.$el;
          var vars = getter(instance, instance._setupProxy);
          if (el && el.nodeType === 1) {
              var style = el.style;
              for (var key in vars) {
                  style.setProperty("--".concat(key), vars[key]);
              }
          }
      });
  }

  /**
   * v3-compatible async component API.
   * @internal the type is manually declared in <root>/types/v3-define-async-component.d.ts
   * because it relies on existing manual types
   */
  function defineAsyncComponent(source) {
      if (isFunction(source)) {
          source = { loader: source };
      }
      var loader = source.loader, loadingComponent = source.loadingComponent, errorComponent = source.errorComponent, _a = source.delay, delay = _a === void 0 ? 200 : _a, timeout = source.timeout, // undefined = never times out
      _b = source.suspensible, // undefined = never times out
      suspensible = _b === void 0 ? false : _b, // in Vue 3 default is true
      userOnError = source.onError;
      if (suspensible) {
          warn$2("The suspensible option for async components is not supported in Vue2. It is ignored.");
      }
      var pendingRequest = null;
      var retries = 0;
      var retry = function () {
          retries++;
          pendingRequest = null;
          return load();
      };
      var load = function () {
          var thisRequest;
          return (pendingRequest ||
              (thisRequest = pendingRequest =
                  loader()
                      .catch(function (err) {
                      err = err instanceof Error ? err : new Error(String(err));
                      if (userOnError) {
                          return new Promise(function (resolve, reject) {
                              var userRetry = function () { return resolve(retry()); };
                              var userFail = function () { return reject(err); };
                              userOnError(err, userRetry, userFail, retries + 1);
                          });
                      }
                      else {
                          throw err;
                      }
                  })
                      .then(function (comp) {
                      if (thisRequest !== pendingRequest && pendingRequest) {
                          return pendingRequest;
                      }
                      if (!comp) {
                          warn$2("Async component loader resolved to undefined. " +
                              "If you are using retry(), make sure to return its return value.");
                      }
                      // interop module default
                      if (comp &&
                          (comp.__esModule || comp[Symbol.toStringTag] === 'Module')) {
                          comp = comp.default;
                      }
                      if (comp && !isObject(comp) && !isFunction(comp)) {
                          throw new Error("Invalid async component load result: ".concat(comp));
                      }
                      return comp;
                  })));
      };
      return function () {
          var component = load();
          return {
              component: component,
              delay: delay,
              timeout: timeout,
              error: errorComponent,
              loading: loadingComponent
          };
      };
  }

  function createLifeCycle(hookName) {
      return function (fn, target) {
          if (target === void 0) { target = currentInstance; }
          if (!target) {
              warn$2("".concat(formatName(hookName), " is called when there is no active component instance to be ") +
                      "associated with. " +
                      "Lifecycle injection APIs can only be used during execution of setup().");
              return;
          }
          return injectHook(target, hookName, fn);
      };
  }
  function formatName(name) {
      if (name === 'beforeDestroy') {
          name = 'beforeUnmount';
      }
      else if (name === 'destroyed') {
          name = 'unmounted';
      }
      return "on".concat(name[0].toUpperCase() + name.slice(1));
  }
  function injectHook(instance, hookName, fn) {
      var options = instance.$options;
      options[hookName] = mergeLifecycleHook(options[hookName], fn);
  }
  var onBeforeMount = createLifeCycle('beforeMount');
  var onMounted = createLifeCycle('mounted');
  var onBeforeUpdate = createLifeCycle('beforeUpdate');
  var onUpdated = createLifeCycle('updated');
  var onBeforeUnmount = createLifeCycle('beforeDestroy');
  var onUnmounted = createLifeCycle('destroyed');
  var onActivated = createLifeCycle('activated');
  var onDeactivated = createLifeCycle('deactivated');
  var onServerPrefetch = createLifeCycle('serverPrefetch');
  var onRenderTracked = createLifeCycle('renderTracked');
  var onRenderTriggered = createLifeCycle('renderTriggered');
  var injectErrorCapturedHook = createLifeCycle('errorCaptured');
  function onErrorCaptured(hook, target) {
      if (target === void 0) { target = currentInstance; }
      injectErrorCapturedHook(hook, target);
  }

  /**
   * Note: also update dist/vue.runtime.mjs when adding new exports to this file.
   */
  var version = '2.7.16';
  /**
   * @internal type is manually declared in <root>/types/v3-define-component.d.ts
   */
  function defineComponent(options) {
      return options;
  }

  var vca = /*#__PURE__*/Object.freeze({
    __proto__: null,
    version: version,
    defineComponent: defineComponent,
    ref: ref$1,
    shallowRef: shallowRef,
    isRef: isRef,
    toRef: toRef,
    toRefs: toRefs,
    unref: unref,
    proxyRefs: proxyRefs,
    customRef: customRef,
    triggerRef: triggerRef,
    reactive: reactive,
    isReactive: isReactive,
    isReadonly: isReadonly,
    isShallow: isShallow,
    isProxy: isProxy,
    shallowReactive: shallowReactive,
    markRaw: markRaw,
    toRaw: toRaw,
    readonly: readonly,
    shallowReadonly: shallowReadonly,
    computed: computed,
    watch: watch,
    watchEffect: watchEffect,
    watchPostEffect: watchPostEffect,
    watchSyncEffect: watchSyncEffect,
    EffectScope: EffectScope,
    effectScope: effectScope,
    onScopeDispose: onScopeDispose,
    getCurrentScope: getCurrentScope,
    provide: provide,
    inject: inject,
    h: h,
    getCurrentInstance: getCurrentInstance,
    useSlots: useSlots,
    useAttrs: useAttrs,
    useListeners: useListeners,
    mergeDefaults: mergeDefaults,
    nextTick: nextTick,
    set: set,
    del: del,
    useCssModule: useCssModule,
    useCssVars: useCssVars,
    defineAsyncComponent: defineAsyncComponent,
    onBeforeMount: onBeforeMount,
    onMounted: onMounted,
    onBeforeUpdate: onBeforeUpdate,
    onUpdated: onUpdated,
    onBeforeUnmount: onBeforeUnmount,
    onUnmounted: onUnmounted,
    onActivated: onActivated,
    onDeactivated: onDeactivated,
    onServerPrefetch: onServerPrefetch,
    onRenderTracked: onRenderTracked,
    onRenderTriggered: onRenderTriggered,
    onErrorCaptured: onErrorCaptured
  });

  var seenObjects = new _Set();
  /**
   * Recursively traverse an object to evoke all converted
   * getters, so that every nested property inside the object
   * is collected as a "deep" dependency.
   */
  function traverse(val) {
      _traverse(val, seenObjects);
      seenObjects.clear();
      return val;
  }
  function _traverse(val, seen) {
      var i, keys;
      var isA = isArray(val);
      if ((!isA && !isObject(val)) ||
          val.__v_skip /* ReactiveFlags.SKIP */ ||
          Object.isFrozen(val) ||
          val instanceof VNode) {
          return;
      }
      if (val.__ob__) {
          var depId = val.__ob__.dep.id;
          if (seen.has(depId)) {
              return;
          }
          seen.add(depId);
      }
      if (isA) {
          i = val.length;
          while (i--)
              _traverse(val[i], seen);
      }
      else if (isRef(val)) {
          _traverse(val.value, seen);
      }
      else {
          keys = Object.keys(val);
          i = keys.length;
          while (i--)
              _traverse(val[keys[i]], seen);
      }
  }

  var uid$1 = 0;
  /**
   * A watcher parses an expression, collects dependencies,
   * and fires callback when the expression value changes.
   * This is used for both the $watch() api and directives.
   * @internal
   */
  var Watcher = /** @class */ (function () {
      function Watcher(vm, expOrFn, cb, options, isRenderWatcher) {
          recordEffectScope(this, 
          // if the active effect scope is manually created (not a component scope),
          // prioritize it
          activeEffectScope && !activeEffectScope._vm
              ? activeEffectScope
              : vm
                  ? vm._scope
                  : undefined);
          if ((this.vm = vm) && isRenderWatcher) {
              vm._watcher = this;
          }
          // options
          if (options) {
              this.deep = !!options.deep;
              this.user = !!options.user;
              this.lazy = !!options.lazy;
              this.sync = !!options.sync;
              this.before = options.before;
              {
                  this.onTrack = options.onTrack;
                  this.onTrigger = options.onTrigger;
              }
          }
          else {
              this.deep = this.user = this.lazy = this.sync = false;
          }
          this.cb = cb;
          this.id = ++uid$1; // uid for batching
          this.active = true;
          this.post = false;
          this.dirty = this.lazy; // for lazy watchers
          this.deps = [];
          this.newDeps = [];
          this.depIds = new _Set();
          this.newDepIds = new _Set();
          this.expression = expOrFn.toString() ;
          // parse expression for getter
          if (isFunction(expOrFn)) {
              this.getter = expOrFn;
          }
          else {
              this.getter = parsePath(expOrFn);
              if (!this.getter) {
                  this.getter = noop;
                  warn$2("Failed watching path: \"".concat(expOrFn, "\" ") +
                          'Watcher only accepts simple dot-delimited paths. ' +
                          'For full control, use a function instead.', vm);
              }
          }
          this.value = this.lazy ? undefined : this.get();
      }
      /**
       * Evaluate the getter, and re-collect dependencies.
       */
      Watcher.prototype.get = function () {
          pushTarget(this);
          var value;
          var vm = this.vm;
          try {
              value = this.getter.call(vm, vm);
          }
          catch (e) {
              if (this.user) {
                  handleError(e, vm, "getter for watcher \"".concat(this.expression, "\""));
              }
              else {
                  throw e;
              }
          }
          finally {
              // "touch" every property so they are all tracked as
              // dependencies for deep watching
              if (this.deep) {
                  traverse(value);
              }
              popTarget();
              this.cleanupDeps();
          }
          return value;
      };
      /**
       * Add a dependency to this directive.
       */
      Watcher.prototype.addDep = function (dep) {
          var id = dep.id;
          if (!this.newDepIds.has(id)) {
              this.newDepIds.add(id);
              this.newDeps.push(dep);
              if (!this.depIds.has(id)) {
                  dep.addSub(this);
              }
          }
      };
      /**
       * Clean up for dependency collection.
       */
      Watcher.prototype.cleanupDeps = function () {
          var i = this.deps.length;
          while (i--) {
              var dep = this.deps[i];
              if (!this.newDepIds.has(dep.id)) {
                  dep.removeSub(this);
              }
          }
          var tmp = this.depIds;
          this.depIds = this.newDepIds;
          this.newDepIds = tmp;
          this.newDepIds.clear();
          tmp = this.deps;
          this.deps = this.newDeps;
          this.newDeps = tmp;
          this.newDeps.length = 0;
      };
      /**
       * Subscriber interface.
       * Will be called when a dependency changes.
       */
      Watcher.prototype.update = function () {
          /* istanbul ignore else */
          if (this.lazy) {
              this.dirty = true;
          }
          else if (this.sync) {
              this.run();
          }
          else {
              queueWatcher(this);
          }
      };
      /**
       * Scheduler job interface.
       * Will be called by the scheduler.
       */
      Watcher.prototype.run = function () {
          if (this.active) {
              var value = this.get();
              if (value !== this.value ||
                  // Deep watchers and watchers on Object/Arrays should fire even
                  // when the value is the same, because the value may
                  // have mutated.
                  isObject(value) ||
                  this.deep) {
                  // set new value
                  var oldValue = this.value;
                  this.value = value;
                  if (this.user) {
                      var info = "callback for watcher \"".concat(this.expression, "\"");
                      invokeWithErrorHandling(this.cb, this.vm, [value, oldValue], this.vm, info);
                  }
                  else {
                      this.cb.call(this.vm, value, oldValue);
                  }
              }
          }
      };
      /**
       * Evaluate the value of the watcher.
       * This only gets called for lazy watchers.
       */
      Watcher.prototype.evaluate = function () {
          this.value = this.get();
          this.dirty = false;
      };
      /**
       * Depend on all deps collected by this watcher.
       */
      Watcher.prototype.depend = function () {
          var i = this.deps.length;
          while (i--) {
              this.deps[i].depend();
          }
      };
      /**
       * Remove self from all dependencies' subscriber list.
       */
      Watcher.prototype.teardown = function () {
          if (this.vm && !this.vm._isBeingDestroyed) {
              remove$2(this.vm._scope.effects, this);
          }
          if (this.active) {
              var i = this.deps.length;
              while (i--) {
                  this.deps[i].removeSub(this);
              }
              this.active = false;
              if (this.onStop) {
                  this.onStop();
              }
          }
      };
      return Watcher;
  }());

  var sharedPropertyDefinition = {
      enumerable: true,
      configurable: true,
      get: noop,
      set: noop
  };
  function proxy(target, sourceKey, key) {
      sharedPropertyDefinition.get = function proxyGetter() {
          return this[sourceKey][key];
      };
      sharedPropertyDefinition.set = function proxySetter(val) {
          this[sourceKey][key] = val;
      };
      Object.defineProperty(target, key, sharedPropertyDefinition);
  }
  function initState(vm) {
      var opts = vm.$options;
      if (opts.props)
          initProps$1(vm, opts.props);
      // Composition API
      initSetup(vm);
      if (opts.methods)
          initMethods(vm, opts.methods);
      if (opts.data) {
          initData(vm);
      }
      else {
          var ob = observe((vm._data = {}));
          ob && ob.vmCount++;
      }
      if (opts.computed)
          initComputed$1(vm, opts.computed);
      if (opts.watch && opts.watch !== nativeWatch) {
          initWatch(vm, opts.watch);
      }
  }
  function initProps$1(vm, propsOptions) {
      var propsData = vm.$options.propsData || {};
      var props = (vm._props = shallowReactive({}));
      // cache prop keys so that future props updates can iterate using Array
      // instead of dynamic object key enumeration.
      var keys = (vm.$options._propKeys = []);
      var isRoot = !vm.$parent;
      // root instance props should be converted
      if (!isRoot) {
          toggleObserving(false);
      }
      var _loop_1 = function (key) {
          keys.push(key);
          var value = validateProp(key, propsOptions, propsData, vm);
          /* istanbul ignore else */
          {
              var hyphenatedKey = hyphenate(key);
              if (isReservedAttribute(hyphenatedKey) ||
                  config.isReservedAttr(hyphenatedKey)) {
                  warn$2("\"".concat(hyphenatedKey, "\" is a reserved attribute and cannot be used as component prop."), vm);
              }
              defineReactive(props, key, value, function () {
                  if (!isRoot && !isUpdatingChildComponent) {
                      warn$2("Avoid mutating a prop directly since the value will be " +
                          "overwritten whenever the parent component re-renders. " +
                          "Instead, use a data or computed property based on the prop's " +
                          "value. Prop being mutated: \"".concat(key, "\""), vm);
                  }
              }, true /* shallow */);
          }
          // static props are already proxied on the component's prototype
          // during Vue.extend(). We only need to proxy props defined at
          // instantiation here.
          if (!(key in vm)) {
              proxy(vm, "_props", key);
          }
      };
      for (var key in propsOptions) {
          _loop_1(key);
      }
      toggleObserving(true);
  }
  function initData(vm) {
      var data = vm.$options.data;
      data = vm._data = isFunction(data) ? getData(data, vm) : data || {};
      if (!isPlainObject(data)) {
          data = {};
          warn$2('data functions should return an object:\n' +
                  'https://v2.vuejs.org/v2/guide/components.html#data-Must-Be-a-Function', vm);
      }
      // proxy data on instance
      var keys = Object.keys(data);
      var props = vm.$options.props;
      var methods = vm.$options.methods;
      var i = keys.length;
      while (i--) {
          var key = keys[i];
          {
              if (methods && hasOwn(methods, key)) {
                  warn$2("Method \"".concat(key, "\" has already been defined as a data property."), vm);
              }
          }
          if (props && hasOwn(props, key)) {
              warn$2("The data property \"".concat(key, "\" is already declared as a prop. ") +
                      "Use prop default value instead.", vm);
          }
          else if (!isReserved(key)) {
              proxy(vm, "_data", key);
          }
      }
      // observe data
      var ob = observe(data);
      ob && ob.vmCount++;
  }
  function getData(data, vm) {
      // #7573 disable dep collection when invoking data getters
      pushTarget();
      try {
          return data.call(vm, vm);
      }
      catch (e) {
          handleError(e, vm, "data()");
          return {};
      }
      finally {
          popTarget();
      }
  }
  var computedWatcherOptions = { lazy: true };
  function initComputed$1(vm, computed) {
      // $flow-disable-line
      var watchers = (vm._computedWatchers = Object.create(null));
      // computed properties are just getters during SSR
      var isSSR = isServerRendering();
      for (var key in computed) {
          var userDef = computed[key];
          var getter = isFunction(userDef) ? userDef : userDef.get;
          if (getter == null) {
              warn$2("Getter is missing for computed property \"".concat(key, "\"."), vm);
          }
          if (!isSSR) {
              // create internal watcher for the computed property.
              watchers[key] = new Watcher(vm, getter || noop, noop, computedWatcherOptions);
          }
          // component-defined computed properties are already defined on the
          // component prototype. We only need to define computed properties defined
          // at instantiation here.
          if (!(key in vm)) {
              defineComputed(vm, key, userDef);
          }
          else {
              if (key in vm.$data) {
                  warn$2("The computed property \"".concat(key, "\" is already defined in data."), vm);
              }
              else if (vm.$options.props && key in vm.$options.props) {
                  warn$2("The computed property \"".concat(key, "\" is already defined as a prop."), vm);
              }
              else if (vm.$options.methods && key in vm.$options.methods) {
                  warn$2("The computed property \"".concat(key, "\" is already defined as a method."), vm);
              }
          }
      }
  }
  function defineComputed(target, key, userDef) {
      var shouldCache = !isServerRendering();
      if (isFunction(userDef)) {
          sharedPropertyDefinition.get = shouldCache
              ? createComputedGetter(key)
              : createGetterInvoker(userDef);
          sharedPropertyDefinition.set = noop;
      }
      else {
          sharedPropertyDefinition.get = userDef.get
              ? shouldCache && userDef.cache !== false
                  ? createComputedGetter(key)
                  : createGetterInvoker(userDef.get)
              : noop;
          sharedPropertyDefinition.set = userDef.set || noop;
      }
      if (sharedPropertyDefinition.set === noop) {
          sharedPropertyDefinition.set = function () {
              warn$2("Computed property \"".concat(key, "\" was assigned to but it has no setter."), this);
          };
      }
      Object.defineProperty(target, key, sharedPropertyDefinition);
  }
  function createComputedGetter(key) {
      return function computedGetter() {
          var watcher = this._computedWatchers && this._computedWatchers[key];
          if (watcher) {
              if (watcher.dirty) {
                  watcher.evaluate();
              }
              if (Dep.target) {
                  if (Dep.target.onTrack) {
                      Dep.target.onTrack({
                          effect: Dep.target,
                          target: this,
                          type: "get" /* TrackOpTypes.GET */,
                          key: key
                      });
                  }
                  watcher.depend();
              }
              return watcher.value;
          }
      };
  }
  function createGetterInvoker(fn) {
      return function computedGetter() {
          return fn.call(this, this);
      };
  }
  function initMethods(vm, methods) {
      var props = vm.$options.props;
      for (var key in methods) {
          {
              if (typeof methods[key] !== 'function') {
                  warn$2("Method \"".concat(key, "\" has type \"").concat(typeof methods[key], "\" in the component definition. ") +
                      "Did you reference the function correctly?", vm);
              }
              if (props && hasOwn(props, key)) {
                  warn$2("Method \"".concat(key, "\" has already been defined as a prop."), vm);
              }
              if (key in vm && isReserved(key)) {
                  warn$2("Method \"".concat(key, "\" conflicts with an existing Vue instance method. ") +
                      "Avoid defining component methods that start with _ or $.");
              }
          }
          vm[key] = typeof methods[key] !== 'function' ? noop : bind$1(methods[key], vm);
      }
  }
  function initWatch(vm, watch) {
      for (var key in watch) {
          var handler = watch[key];
          if (isArray(handler)) {
              for (var i = 0; i < handler.length; i++) {
                  createWatcher(vm, key, handler[i]);
              }
          }
          else {
              createWatcher(vm, key, handler);
          }
      }
  }
  function createWatcher(vm, expOrFn, handler, options) {
      if (isPlainObject(handler)) {
          options = handler;
          handler = handler.handler;
      }
      if (typeof handler === 'string') {
          handler = vm[handler];
      }
      return vm.$watch(expOrFn, handler, options);
  }
  function stateMixin(Vue) {
      // flow somehow has problems with directly declared definition object
      // when using Object.defineProperty, so we have to procedurally build up
      // the object here.
      var dataDef = {};
      dataDef.get = function () {
          return this._data;
      };
      var propsDef = {};
      propsDef.get = function () {
          return this._props;
      };
      {
          dataDef.set = function () {
              warn$2('Avoid replacing instance root $data. ' +
                  'Use nested data properties instead.', this);
          };
          propsDef.set = function () {
              warn$2("$props is readonly.", this);
          };
      }
      Object.defineProperty(Vue.prototype, '$data', dataDef);
      Object.defineProperty(Vue.prototype, '$props', propsDef);
      Vue.prototype.$set = set;
      Vue.prototype.$delete = del;
      Vue.prototype.$watch = function (expOrFn, cb, options) {
          var vm = this;
          if (isPlainObject(cb)) {
              return createWatcher(vm, expOrFn, cb, options);
          }
          options = options || {};
          options.user = true;
          var watcher = new Watcher(vm, expOrFn, cb, options);
          if (options.immediate) {
              var info = "callback for immediate watcher \"".concat(watcher.expression, "\"");
              pushTarget();
              invokeWithErrorHandling(cb, vm, [watcher.value], vm, info);
              popTarget();
          }
          return function unwatchFn() {
              watcher.teardown();
          };
      };
  }

  function initProvide(vm) {
      var provideOption = vm.$options.provide;
      if (provideOption) {
          var provided = isFunction(provideOption)
              ? provideOption.call(vm)
              : provideOption;
          if (!isObject(provided)) {
              return;
          }
          var source = resolveProvided(vm);
          // IE9 doesn't support Object.getOwnPropertyDescriptors so we have to
          // iterate the keys ourselves.
          var keys = hasSymbol ? Reflect.ownKeys(provided) : Object.keys(provided);
          for (var i = 0; i < keys.length; i++) {
              var key = keys[i];
              Object.defineProperty(source, key, Object.getOwnPropertyDescriptor(provided, key));
          }
      }
  }
  function initInjections(vm) {
      var result = resolveInject(vm.$options.inject, vm);
      if (result) {
          toggleObserving(false);
          Object.keys(result).forEach(function (key) {
              /* istanbul ignore else */
              {
                  defineReactive(vm, key, result[key], function () {
                      warn$2("Avoid mutating an injected value directly since the changes will be " +
                          "overwritten whenever the provided component re-renders. " +
                          "injection being mutated: \"".concat(key, "\""), vm);
                  });
              }
          });
          toggleObserving(true);
      }
  }
  function resolveInject(inject, vm) {
      if (inject) {
          // inject is :any because flow is not smart enough to figure out cached
          var result = Object.create(null);
          var keys = hasSymbol ? Reflect.ownKeys(inject) : Object.keys(inject);
          for (var i = 0; i < keys.length; i++) {
              var key = keys[i];
              // #6574 in case the inject object is observed...
              if (key === '__ob__')
                  continue;
              var provideKey = inject[key].from;
              if (provideKey in vm._provided) {
                  result[key] = vm._provided[provideKey];
              }
              else if ('default' in inject[key]) {
                  var provideDefault = inject[key].default;
                  result[key] = isFunction(provideDefault)
                      ? provideDefault.call(vm)
                      : provideDefault;
              }
              else {
                  warn$2("Injection \"".concat(key, "\" not found"), vm);
              }
          }
          return result;
      }
  }

  var uid = 0;
  function initMixin$1(Vue) {
      Vue.prototype._init = function (options) {
          var vm = this;
          // a uid
          vm._uid = uid++;
          var startTag, endTag;
          /* istanbul ignore if */
          if (config.performance && mark) {
              startTag = "vue-perf-start:".concat(vm._uid);
              endTag = "vue-perf-end:".concat(vm._uid);
              mark(startTag);
          }
          // a flag to mark this as a Vue instance without having to do instanceof
          // check
          vm._isVue = true;
          // avoid instances from being observed
          vm.__v_skip = true;
          // effect scope
          vm._scope = new EffectScope(true /* detached */);
          // #13134 edge case where a child component is manually created during the
          // render of a parent component
          vm._scope.parent = undefined;
          vm._scope._vm = true;
          // merge options
          if (options && options._isComponent) {
              // optimize internal component instantiation
              // since dynamic options merging is pretty slow, and none of the
              // internal component options needs special treatment.
              initInternalComponent(vm, options);
          }
          else {
              vm.$options = mergeOptions(resolveConstructorOptions(vm.constructor), options || {}, vm);
          }
          /* istanbul ignore else */
          {
              initProxy(vm);
          }
          // expose real self
          vm._self = vm;
          initLifecycle(vm);
          initEvents(vm);
          initRender(vm);
          callHook$1(vm, 'beforeCreate', undefined, false /* setContext */);
          initInjections(vm); // resolve injections before data/props
          initState(vm);
          initProvide(vm); // resolve provide after data/props
          callHook$1(vm, 'created');
          /* istanbul ignore if */
          if (config.performance && mark) {
              vm._name = formatComponentName(vm, false);
              mark(endTag);
              measure("vue ".concat(vm._name, " init"), startTag, endTag);
          }
          if (vm.$options.el) {
              vm.$mount(vm.$options.el);
          }
      };
  }
  function initInternalComponent(vm, options) {
      var opts = (vm.$options = Object.create(vm.constructor.options));
      // doing this because it's faster than dynamic enumeration.
      var parentVnode = options._parentVnode;
      opts.parent = options.parent;
      opts._parentVnode = parentVnode;
      var vnodeComponentOptions = parentVnode.componentOptions;
      opts.propsData = vnodeComponentOptions.propsData;
      opts._parentListeners = vnodeComponentOptions.listeners;
      opts._renderChildren = vnodeComponentOptions.children;
      opts._componentTag = vnodeComponentOptions.tag;
      if (options.render) {
          opts.render = options.render;
          opts.staticRenderFns = options.staticRenderFns;
      }
  }
  function resolveConstructorOptions(Ctor) {
      var options = Ctor.options;
      if (Ctor.super) {
          var superOptions = resolveConstructorOptions(Ctor.super);
          var cachedSuperOptions = Ctor.superOptions;
          if (superOptions !== cachedSuperOptions) {
              // super option changed,
              // need to resolve new options.
              Ctor.superOptions = superOptions;
              // check if there are any late-modified/attached options (#4976)
              var modifiedOptions = resolveModifiedOptions(Ctor);
              // update base extend options
              if (modifiedOptions) {
                  extend(Ctor.extendOptions, modifiedOptions);
              }
              options = Ctor.options = mergeOptions(superOptions, Ctor.extendOptions);
              if (options.name) {
                  options.components[options.name] = Ctor;
              }
          }
      }
      return options;
  }
  function resolveModifiedOptions(Ctor) {
      var modified;
      var latest = Ctor.options;
      var sealed = Ctor.sealedOptions;
      for (var key in latest) {
          if (latest[key] !== sealed[key]) {
              if (!modified)
                  modified = {};
              modified[key] = latest[key];
          }
      }
      return modified;
  }

  function FunctionalRenderContext(data, props, children, parent, Ctor) {
      var _this = this;
      var options = Ctor.options;
      // ensure the createElement function in functional components
      // gets a unique context - this is necessary for correct named slot check
      var contextVm;
      if (hasOwn(parent, '_uid')) {
          contextVm = Object.create(parent);
          contextVm._original = parent;
      }
      else {
          // the context vm passed in is a functional context as well.
          // in this case we want to make sure we are able to get a hold to the
          // real context instance.
          contextVm = parent;
          // @ts-ignore
          parent = parent._original;
      }
      var isCompiled = isTrue(options._compiled);
      var needNormalization = !isCompiled;
      this.data = data;
      this.props = props;
      this.children = children;
      this.parent = parent;
      this.listeners = data.on || emptyObject;
      this.injections = resolveInject(options.inject, parent);
      this.slots = function () {
          if (!_this.$slots) {
              normalizeScopedSlots(parent, data.scopedSlots, (_this.$slots = resolveSlots(children, parent)));
          }
          return _this.$slots;
      };
      Object.defineProperty(this, 'scopedSlots', {
          enumerable: true,
          get: function () {
              return normalizeScopedSlots(parent, data.scopedSlots, this.slots());
          }
      });
      // support for compiled functional template
      if (isCompiled) {
          // exposing $options for renderStatic()
          this.$options = options;
          // pre-resolve slots for renderSlot()
          this.$slots = this.slots();
          this.$scopedSlots = normalizeScopedSlots(parent, data.scopedSlots, this.$slots);
      }
      if (options._scopeId) {
          this._c = function (a, b, c, d) {
              var vnode = createElement$1(contextVm, a, b, c, d, needNormalization);
              if (vnode && !isArray(vnode)) {
                  vnode.fnScopeId = options._scopeId;
                  vnode.fnContext = parent;
              }
              return vnode;
          };
      }
      else {
          this._c = function (a, b, c, d) {
              return createElement$1(contextVm, a, b, c, d, needNormalization);
          };
      }
  }
  installRenderHelpers(FunctionalRenderContext.prototype);
  function createFunctionalComponent(Ctor, propsData, data, contextVm, children) {
      var options = Ctor.options;
      var props = {};
      var propOptions = options.props;
      if (isDef(propOptions)) {
          for (var key in propOptions) {
              props[key] = validateProp(key, propOptions, propsData || emptyObject);
          }
      }
      else {
          if (isDef(data.attrs))
              mergeProps(props, data.attrs);
          if (isDef(data.props))
              mergeProps(props, data.props);
      }
      var renderContext = new FunctionalRenderContext(data, props, children, contextVm, Ctor);
      var vnode = options.render.call(null, renderContext._c, renderContext);
      if (vnode instanceof VNode) {
          return cloneAndMarkFunctionalResult(vnode, data, renderContext.parent, options, renderContext);
      }
      else if (isArray(vnode)) {
          var vnodes = normalizeChildren(vnode) || [];
          var res = new Array(vnodes.length);
          for (var i = 0; i < vnodes.length; i++) {
              res[i] = cloneAndMarkFunctionalResult(vnodes[i], data, renderContext.parent, options, renderContext);
          }
          return res;
      }
  }
  function cloneAndMarkFunctionalResult(vnode, data, contextVm, options, renderContext) {
      // #7817 clone node before setting fnContext, otherwise if the node is reused
      // (e.g. it was from a cached normal slot) the fnContext causes named slots
      // that should not be matched to match.
      var clone = cloneVNode(vnode);
      clone.fnContext = contextVm;
      clone.fnOptions = options;
      {
          (clone.devtoolsMeta = clone.devtoolsMeta || {}).renderContext =
              renderContext;
      }
      if (data.slot) {
          (clone.data || (clone.data = {})).slot = data.slot;
      }
      return clone;
  }
  function mergeProps(to, from) {
      for (var key in from) {
          to[camelize(key)] = from[key];
      }
  }

  function getComponentName(options) {
      return options.name || options.__name || options._componentTag;
  }
  // inline hooks to be invoked on component VNodes during patch
  var componentVNodeHooks = {
      init: function (vnode, hydrating) {
          if (vnode.componentInstance &&
              !vnode.componentInstance._isDestroyed &&
              vnode.data.keepAlive) {
              // kept-alive components, treat as a patch
              var mountedNode = vnode; // work around flow
              componentVNodeHooks.prepatch(mountedNode, mountedNode);
          }
          else {
              var child = (vnode.componentInstance = createComponentInstanceForVnode(vnode, activeInstance));
              child.$mount(hydrating ? vnode.elm : undefined, hydrating);
          }
      },
      prepatch: function (oldVnode, vnode) {
          var options = vnode.componentOptions;
          var child = (vnode.componentInstance = oldVnode.componentInstance);
          updateChildComponent(child, options.propsData, // updated props
          options.listeners, // updated listeners
          vnode, // new parent vnode
          options.children // new children
          );
      },
      insert: function (vnode) {
          var context = vnode.context, componentInstance = vnode.componentInstance;
          if (!componentInstance._isMounted) {
              componentInstance._isMounted = true;
              callHook$1(componentInstance, 'mounted');
          }
          if (vnode.data.keepAlive) {
              if (context._isMounted) {
                  // vue-router#1212
                  // During updates, a kept-alive component's child components may
                  // change, so directly walking the tree here may call activated hooks
                  // on incorrect children. Instead we push them into a queue which will
                  // be processed after the whole patch process ended.
                  queueActivatedComponent(componentInstance);
              }
              else {
                  activateChildComponent(componentInstance, true /* direct */);
              }
          }
      },
      destroy: function (vnode) {
          var componentInstance = vnode.componentInstance;
          if (!componentInstance._isDestroyed) {
              if (!vnode.data.keepAlive) {
                  componentInstance.$destroy();
              }
              else {
                  deactivateChildComponent(componentInstance, true /* direct */);
              }
          }
      }
  };
  var hooksToMerge = Object.keys(componentVNodeHooks);
  function createComponent(Ctor, data, context, children, tag) {
      if (isUndef(Ctor)) {
          return;
      }
      var baseCtor = context.$options._base;
      // plain options object: turn it into a constructor
      if (isObject(Ctor)) {
          Ctor = baseCtor.extend(Ctor);
      }
      // if at this stage it's not a constructor or an async component factory,
      // reject.
      if (typeof Ctor !== 'function') {
          {
              warn$2("Invalid Component definition: ".concat(String(Ctor)), context);
          }
          return;
      }
      // async component
      var asyncFactory;
      // @ts-expect-error
      if (isUndef(Ctor.cid)) {
          asyncFactory = Ctor;
          Ctor = resolveAsyncComponent(asyncFactory, baseCtor);
          if (Ctor === undefined) {
              // return a placeholder node for async component, which is rendered
              // as a comment node but preserves all the raw information for the node.
              // the information will be used for async server-rendering and hydration.
              return createAsyncPlaceholder(asyncFactory, data, context, children, tag);
          }
      }
      data = data || {};
      // resolve constructor options in case global mixins are applied after
      // component constructor creation
      resolveConstructorOptions(Ctor);
      // transform component v-model data into props & events
      if (isDef(data.model)) {
          // @ts-expect-error
          transformModel(Ctor.options, data);
      }
      // extract props
      // @ts-expect-error
      var propsData = extractPropsFromVNodeData(data, Ctor, tag);
      // functional component
      // @ts-expect-error
      if (isTrue(Ctor.options.functional)) {
          return createFunctionalComponent(Ctor, propsData, data, context, children);
      }
      // extract listeners, since these needs to be treated as
      // child component listeners instead of DOM listeners
      var listeners = data.on;
      // replace with listeners with .native modifier
      // so it gets processed during parent component patch.
      data.on = data.nativeOn;
      // @ts-expect-error
      if (isTrue(Ctor.options.abstract)) {
          // abstract components do not keep anything
          // other than props & listeners & slot
          // work around flow
          var slot = data.slot;
          data = {};
          if (slot) {
              data.slot = slot;
          }
      }
      // install component management hooks onto the placeholder node
      installComponentHooks(data);
      // return a placeholder vnode
      // @ts-expect-error
      var name = getComponentName(Ctor.options) || tag;
      var vnode = new VNode(
      // @ts-expect-error
      "vue-component-".concat(Ctor.cid).concat(name ? "-".concat(name) : ''), data, undefined, undefined, undefined, context, 
      // @ts-expect-error
      { Ctor: Ctor, propsData: propsData, listeners: listeners, tag: tag, children: children }, asyncFactory);
      return vnode;
  }
  function createComponentInstanceForVnode(
  // we know it's MountedComponentVNode but flow doesn't
  vnode, 
  // activeInstance in lifecycle state
  parent) {
      var options = {
          _isComponent: true,
          _parentVnode: vnode,
          parent: parent
      };
      // check inline-template render functions
      var inlineTemplate = vnode.data.inlineTemplate;
      if (isDef(inlineTemplate)) {
          options.render = inlineTemplate.render;
          options.staticRenderFns = inlineTemplate.staticRenderFns;
      }
      return new vnode.componentOptions.Ctor(options);
  }
  function installComponentHooks(data) {
      var hooks = data.hook || (data.hook = {});
      for (var i = 0; i < hooksToMerge.length; i++) {
          var key = hooksToMerge[i];
          var existing = hooks[key];
          var toMerge = componentVNodeHooks[key];
          // @ts-expect-error
          if (existing !== toMerge && !(existing && existing._merged)) {
              hooks[key] = existing ? mergeHook(toMerge, existing) : toMerge;
          }
      }
  }
  function mergeHook(f1, f2) {
      var merged = function (a, b) {
          // flow complains about extra args which is why we use any
          f1(a, b);
          f2(a, b);
      };
      merged._merged = true;
      return merged;
  }
  // transform component v-model info (value and callback) into
  // prop and event handler respectively.
  function transformModel(options, data) {
      var prop = (options.model && options.model.prop) || 'value';
      var event = (options.model && options.model.event) || 'input';
      (data.attrs || (data.attrs = {}))[prop] = data.model.value;
      var on = data.on || (data.on = {});
      var existing = on[event];
      var callback = data.model.callback;
      if (isDef(existing)) {
          if (isArray(existing)
              ? existing.indexOf(callback) === -1
              : existing !== callback) {
              on[event] = [callback].concat(existing);
          }
      }
      else {
          on[event] = callback;
      }
  }

  var warn$2 = noop;
  var tip = noop;
  var generateComponentTrace; // work around flow check
  var formatComponentName;
  {
      var hasConsole_1 = typeof console !== 'undefined';
      var classifyRE_1 = /(?:^|[-_])(\w)/g;
      var classify_1 = function (str) {
          return str.replace(classifyRE_1, function (c) { return c.toUpperCase(); }).replace(/[-_]/g, '');
      };
      warn$2 = function (msg, vm) {
          if (vm === void 0) { vm = currentInstance; }
          var trace = vm ? generateComponentTrace(vm) : '';
          if (config.warnHandler) {
              config.warnHandler.call(null, msg, vm, trace);
          }
          else if (hasConsole_1 && !config.silent) {
              console.error("[Vue warn]: ".concat(msg).concat(trace));
          }
      };
      tip = function (msg, vm) {
          if (hasConsole_1 && !config.silent) {
              console.warn("[Vue tip]: ".concat(msg) + (vm ? generateComponentTrace(vm) : ''));
          }
      };
      formatComponentName = function (vm, includeFile) {
          if (vm.$root === vm) {
              return '<Root>';
          }
          var options = isFunction(vm) && vm.cid != null
              ? vm.options
              : vm._isVue
                  ? vm.$options || vm.constructor.options
                  : vm;
          var name = getComponentName(options);
          var file = options.__file;
          if (!name && file) {
              var match = file.match(/([^/\\]+)\.vue$/);
              name = match && match[1];
          }
          return ((name ? "<".concat(classify_1(name), ">") : "<Anonymous>") +
              (file && includeFile !== false ? " at ".concat(file) : ''));
      };
      var repeat_1 = function (str, n) {
          var res = '';
          while (n) {
              if (n % 2 === 1)
                  res += str;
              if (n > 1)
                  str += str;
              n >>= 1;
          }
          return res;
      };
      generateComponentTrace = function (vm) {
          if (vm._isVue && vm.$parent) {
              var tree = [];
              var currentRecursiveSequence = 0;
              while (vm) {
                  if (tree.length > 0) {
                      var last = tree[tree.length - 1];
                      if (last.constructor === vm.constructor) {
                          currentRecursiveSequence++;
                          vm = vm.$parent;
                          continue;
                      }
                      else if (currentRecursiveSequence > 0) {
                          tree[tree.length - 1] = [last, currentRecursiveSequence];
                          currentRecursiveSequence = 0;
                      }
                  }
                  tree.push(vm);
                  vm = vm.$parent;
              }
              return ('\n\nfound in\n\n' +
                  tree
                      .map(function (vm, i) {
                      return "".concat(i === 0 ? '---> ' : repeat_1(' ', 5 + i * 2)).concat(isArray(vm)
                          ? "".concat(formatComponentName(vm[0]), "... (").concat(vm[1], " recursive calls)")
                          : formatComponentName(vm));
                  })
                      .join('\n'));
          }
          else {
              return "\n\n(found in ".concat(formatComponentName(vm), ")");
          }
      };
  }

  /**
   * Option overwriting strategies are functions that handle
   * how to merge a parent option value and a child option
   * value into the final value.
   */
  var strats = config.optionMergeStrategies;
  /**
   * Options with restrictions
   */
  {
      strats.el = strats.propsData = function (parent, child, vm, key) {
          if (!vm) {
              warn$2("option \"".concat(key, "\" can only be used during instance ") +
                  'creation with the `new` keyword.');
          }
          return defaultStrat(parent, child);
      };
  }
  /**
   * Helper that recursively merges two data objects together.
   */
  function mergeData(to, from, recursive) {
      if (recursive === void 0) { recursive = true; }
      if (!from)
          return to;
      var key, toVal, fromVal;
      var keys = hasSymbol
          ? Reflect.ownKeys(from)
          : Object.keys(from);
      for (var i = 0; i < keys.length; i++) {
          key = keys[i];
          // in case the object is already observed...
          if (key === '__ob__')
              continue;
          toVal = to[key];
          fromVal = from[key];
          if (!recursive || !hasOwn(to, key)) {
              set(to, key, fromVal);
          }
          else if (toVal !== fromVal &&
              isPlainObject(toVal) &&
              isPlainObject(fromVal)) {
              mergeData(toVal, fromVal);
          }
      }
      return to;
  }
  /**
   * Data
   */
  function mergeDataOrFn(parentVal, childVal, vm) {
      if (!vm) {
          // in a Vue.extend merge, both should be functions
          if (!childVal) {
              return parentVal;
          }
          if (!parentVal) {
              return childVal;
          }
          // when parentVal & childVal are both present,
          // we need to return a function that returns the
          // merged result of both functions... no need to
          // check if parentVal is a function here because
          // it has to be a function to pass previous merges.
          return function mergedDataFn() {
              return mergeData(isFunction(childVal) ? childVal.call(this, this) : childVal, isFunction(parentVal) ? parentVal.call(this, this) : parentVal);
          };
      }
      else {
          return function mergedInstanceDataFn() {
              // instance merge
              var instanceData = isFunction(childVal)
                  ? childVal.call(vm, vm)
                  : childVal;
              var defaultData = isFunction(parentVal)
                  ? parentVal.call(vm, vm)
                  : parentVal;
              if (instanceData) {
                  return mergeData(instanceData, defaultData);
              }
              else {
                  return defaultData;
              }
          };
      }
  }
  strats.data = function (parentVal, childVal, vm) {
      if (!vm) {
          if (childVal && typeof childVal !== 'function') {
              warn$2('The "data" option should be a function ' +
                      'that returns a per-instance value in component ' +
                      'definitions.', vm);
              return parentVal;
          }
          return mergeDataOrFn(parentVal, childVal);
      }
      return mergeDataOrFn(parentVal, childVal, vm);
  };
  /**
   * Hooks and props are merged as arrays.
   */
  function mergeLifecycleHook(parentVal, childVal) {
      var res = childVal
          ? parentVal
              ? parentVal.concat(childVal)
              : isArray(childVal)
                  ? childVal
                  : [childVal]
          : parentVal;
      return res ? dedupeHooks(res) : res;
  }
  function dedupeHooks(hooks) {
      var res = [];
      for (var i = 0; i < hooks.length; i++) {
          if (res.indexOf(hooks[i]) === -1) {
              res.push(hooks[i]);
          }
      }
      return res;
  }
  LIFECYCLE_HOOKS.forEach(function (hook) {
      strats[hook] = mergeLifecycleHook;
  });
  /**
   * Assets
   *
   * When a vm is present (instance creation), we need to do
   * a three-way merge between constructor options, instance
   * options and parent options.
   */
  function mergeAssets(parentVal, childVal, vm, key) {
      var res = Object.create(parentVal || null);
      if (childVal) {
          assertObjectType(key, childVal, vm);
          return extend(res, childVal);
      }
      else {
          return res;
      }
  }
  ASSET_TYPES.forEach(function (type) {
      strats[type + 's'] = mergeAssets;
  });
  /**
   * Watchers.
   *
   * Watchers hashes should not overwrite one
   * another, so we merge them as arrays.
   */
  strats.watch = function (parentVal, childVal, vm, key) {
      // work around Firefox's Object.prototype.watch...
      //@ts-expect-error work around
      if (parentVal === nativeWatch)
          parentVal = undefined;
      //@ts-expect-error work around
      if (childVal === nativeWatch)
          childVal = undefined;
      /* istanbul ignore if */
      if (!childVal)
          return Object.create(parentVal || null);
      {
          assertObjectType(key, childVal, vm);
      }
      if (!parentVal)
          return childVal;
      var ret = {};
      extend(ret, parentVal);
      for (var key_1 in childVal) {
          var parent_1 = ret[key_1];
          var child = childVal[key_1];
          if (parent_1 && !isArray(parent_1)) {
              parent_1 = [parent_1];
          }
          ret[key_1] = parent_1 ? parent_1.concat(child) : isArray(child) ? child : [child];
      }
      return ret;
  };
  /**
   * Other object hashes.
   */
  strats.props =
      strats.methods =
          strats.inject =
              strats.computed =
                  function (parentVal, childVal, vm, key) {
                      if (childVal && true) {
                          assertObjectType(key, childVal, vm);
                      }
                      if (!parentVal)
                          return childVal;
                      var ret = Object.create(null);
                      extend(ret, parentVal);
                      if (childVal)
                          extend(ret, childVal);
                      return ret;
                  };
  strats.provide = function (parentVal, childVal) {
      if (!parentVal)
          return childVal;
      return function () {
          var ret = Object.create(null);
          mergeData(ret, isFunction(parentVal) ? parentVal.call(this) : parentVal);
          if (childVal) {
              mergeData(ret, isFunction(childVal) ? childVal.call(this) : childVal, false // non-recursive
              );
          }
          return ret;
      };
  };
  /**
   * Default strategy.
   */
  var defaultStrat = function (parentVal, childVal) {
      return childVal === undefined ? parentVal : childVal;
  };
  /**
   * Validate component names
   */
  function checkComponents(options) {
      for (var key in options.components) {
          validateComponentName(key);
      }
  }
  function validateComponentName(name) {
      if (!new RegExp("^[a-zA-Z][\\-\\.0-9_".concat(unicodeRegExp.source, "]*$")).test(name)) {
          warn$2('Invalid component name: "' +
              name +
              '". Component names ' +
              'should conform to valid custom element name in html5 specification.');
      }
      if (isBuiltInTag(name) || config.isReservedTag(name)) {
          warn$2('Do not use built-in or reserved HTML elements as component ' +
              'id: ' +
              name);
      }
  }
  /**
   * Ensure all props option syntax are normalized into the
   * Object-based format.
   */
  function normalizeProps(options, vm) {
      var props = options.props;
      if (!props)
          return;
      var res = {};
      var i, val, name;
      if (isArray(props)) {
          i = props.length;
          while (i--) {
              val = props[i];
              if (typeof val === 'string') {
                  name = camelize(val);
                  res[name] = { type: null };
              }
              else {
                  warn$2('props must be strings when using array syntax.');
              }
          }
      }
      else if (isPlainObject(props)) {
          for (var key in props) {
              val = props[key];
              name = camelize(key);
              res[name] = isPlainObject(val) ? val : { type: val };
          }
      }
      else {
          warn$2("Invalid value for option \"props\": expected an Array or an Object, " +
              "but got ".concat(toRawType(props), "."), vm);
      }
      options.props = res;
  }
  /**
   * Normalize all injections into Object-based format
   */
  function normalizeInject(options, vm) {
      var inject = options.inject;
      if (!inject)
          return;
      var normalized = (options.inject = {});
      if (isArray(inject)) {
          for (var i = 0; i < inject.length; i++) {
              normalized[inject[i]] = { from: inject[i] };
          }
      }
      else if (isPlainObject(inject)) {
          for (var key in inject) {
              var val = inject[key];
              normalized[key] = isPlainObject(val)
                  ? extend({ from: key }, val)
                  : { from: val };
          }
      }
      else {
          warn$2("Invalid value for option \"inject\": expected an Array or an Object, " +
              "but got ".concat(toRawType(inject), "."), vm);
      }
  }
  /**
   * Normalize raw function directives into object format.
   */
  function normalizeDirectives$1(options) {
      var dirs = options.directives;
      if (dirs) {
          for (var key in dirs) {
              var def = dirs[key];
              if (isFunction(def)) {
                  dirs[key] = { bind: def, update: def };
              }
          }
      }
  }
  function assertObjectType(name, value, vm) {
      if (!isPlainObject(value)) {
          warn$2("Invalid value for option \"".concat(name, "\": expected an Object, ") +
              "but got ".concat(toRawType(value), "."), vm);
      }
  }
  /**
   * Merge two option objects into a new one.
   * Core utility used in both instantiation and inheritance.
   */
  function mergeOptions(parent, child, vm) {
      {
          checkComponents(child);
      }
      if (isFunction(child)) {
          // @ts-expect-error
          child = child.options;
      }
      normalizeProps(child, vm);
      normalizeInject(child, vm);
      normalizeDirectives$1(child);
      // Apply extends and mixins on the child options,
      // but only if it is a raw options object that isn't
      // the result of another mergeOptions call.
      // Only merged options has the _base property.
      if (!child._base) {
          if (child.extends) {
              parent = mergeOptions(parent, child.extends, vm);
          }
          if (child.mixins) {
              for (var i = 0, l = child.mixins.length; i < l; i++) {
                  parent = mergeOptions(parent, child.mixins[i], vm);
              }
          }
      }
      var options = {};
      var key;
      for (key in parent) {
          mergeField(key);
      }
      for (key in child) {
          if (!hasOwn(parent, key)) {
              mergeField(key);
          }
      }
      function mergeField(key) {
          var strat = strats[key] || defaultStrat;
          options[key] = strat(parent[key], child[key], vm, key);
      }
      return options;
  }
  /**
   * Resolve an asset.
   * This function is used because child instances need access
   * to assets defined in its ancestor chain.
   */
  function resolveAsset(options, type, id, warnMissing) {
      /* istanbul ignore if */
      if (typeof id !== 'string') {
          return;
      }
      var assets = options[type];
      // check local registration variations first
      if (hasOwn(assets, id))
          return assets[id];
      var camelizedId = camelize(id);
      if (hasOwn(assets, camelizedId))
          return assets[camelizedId];
      var PascalCaseId = capitalize(camelizedId);
      if (hasOwn(assets, PascalCaseId))
          return assets[PascalCaseId];
      // fallback to prototype chain
      var res = assets[id] || assets[camelizedId] || assets[PascalCaseId];
      if (warnMissing && !res) {
          warn$2('Failed to resolve ' + type.slice(0, -1) + ': ' + id);
      }
      return res;
  }

  function validateProp(key, propOptions, propsData, vm) {
      var prop = propOptions[key];
      var absent = !hasOwn(propsData, key);
      var value = propsData[key];
      // boolean casting
      var booleanIndex = getTypeIndex(Boolean, prop.type);
      if (booleanIndex > -1) {
          if (absent && !hasOwn(prop, 'default')) {
              value = false;
          }
          else if (value === '' || value === hyphenate(key)) {
              // only cast empty string / same name to boolean if
              // boolean has higher priority
              var stringIndex = getTypeIndex(String, prop.type);
              if (stringIndex < 0 || booleanIndex < stringIndex) {
                  value = true;
              }
          }
      }
      // check default value
      if (value === undefined) {
          value = getPropDefaultValue(vm, prop, key);
          // since the default value is a fresh copy,
          // make sure to observe it.
          var prevShouldObserve = shouldObserve;
          toggleObserving(true);
          observe(value);
          toggleObserving(prevShouldObserve);
      }
      {
          assertProp(prop, key, value, vm, absent);
      }
      return value;
  }
  /**
   * Get the default value of a prop.
   */
  function getPropDefaultValue(vm, prop, key) {
      // no default, return undefined
      if (!hasOwn(prop, 'default')) {
          return undefined;
      }
      var def = prop.default;
      // warn against non-factory defaults for Object & Array
      if (isObject(def)) {
          warn$2('Invalid default value for prop "' +
              key +
              '": ' +
              'Props with type Object/Array must use a factory function ' +
              'to return the default value.', vm);
      }
      // the raw prop value was also undefined from previous render,
      // return previous default value to avoid unnecessary watcher trigger
      if (vm &&
          vm.$options.propsData &&
          vm.$options.propsData[key] === undefined &&
          vm._props[key] !== undefined) {
          return vm._props[key];
      }
      // call factory function for non-Function types
      // a value is Function if its prototype is function even across different execution context
      return isFunction(def) && getType(prop.type) !== 'Function'
          ? def.call(vm)
          : def;
  }
  /**
   * Assert whether a prop is valid.
   */
  function assertProp(prop, name, value, vm, absent) {
      if (prop.required && absent) {
          warn$2('Missing required prop: "' + name + '"', vm);
          return;
      }
      if (value == null && !prop.required) {
          return;
      }
      var type = prop.type;
      var valid = !type || type === true;
      var expectedTypes = [];
      if (type) {
          if (!isArray(type)) {
              type = [type];
          }
          for (var i = 0; i < type.length && !valid; i++) {
              var assertedType = assertType(value, type[i], vm);
              expectedTypes.push(assertedType.expectedType || '');
              valid = assertedType.valid;
          }
      }
      var haveExpectedTypes = expectedTypes.some(function (t) { return t; });
      if (!valid && haveExpectedTypes) {
          warn$2(getInvalidTypeMessage(name, value, expectedTypes), vm);
          return;
      }
      var validator = prop.validator;
      if (validator) {
          if (!validator(value)) {
              warn$2('Invalid prop: custom validator check failed for prop "' + name + '".', vm);
          }
      }
  }
  var simpleCheckRE = /^(String|Number|Boolean|Function|Symbol|BigInt)$/;
  function assertType(value, type, vm) {
      var valid;
      var expectedType = getType(type);
      if (simpleCheckRE.test(expectedType)) {
          var t = typeof value;
          valid = t === expectedType.toLowerCase();
          // for primitive wrapper objects
          if (!valid && t === 'object') {
              valid = value instanceof type;
          }
      }
      else if (expectedType === 'Object') {
          valid = isPlainObject(value);
      }
      else if (expectedType === 'Array') {
          valid = isArray(value);
      }
      else {
          try {
              valid = value instanceof type;
          }
          catch (e) {
              warn$2('Invalid prop type: "' + String(type) + '" is not a constructor', vm);
              valid = false;
          }
      }
      return {
          valid: valid,
          expectedType: expectedType
      };
  }
  var functionTypeCheckRE = /^\s*function (\w+)/;
  /**
   * Use function string name to check built-in types,
   * because a simple equality check will fail when running
   * across different vms / iframes.
   */
  function getType(fn) {
      var match = fn && fn.toString().match(functionTypeCheckRE);
      return match ? match[1] : '';
  }
  function isSameType(a, b) {
      return getType(a) === getType(b);
  }
  function getTypeIndex(type, expectedTypes) {
      if (!isArray(expectedTypes)) {
          return isSameType(expectedTypes, type) ? 0 : -1;
      }
      for (var i = 0, len = expectedTypes.length; i < len; i++) {
          if (isSameType(expectedTypes[i], type)) {
              return i;
          }
      }
      return -1;
  }
  function getInvalidTypeMessage(name, value, expectedTypes) {
      var message = "Invalid prop: type check failed for prop \"".concat(name, "\".") +
          " Expected ".concat(expectedTypes.map(capitalize).join(', '));
      var expectedType = expectedTypes[0];
      var receivedType = toRawType(value);
      // check if we need to specify expected value
      if (expectedTypes.length === 1 &&
          isExplicable(expectedType) &&
          isExplicable(typeof value) &&
          !isBoolean(expectedType, receivedType)) {
          message += " with value ".concat(styleValue(value, expectedType));
      }
      message += ", got ".concat(receivedType, " ");
      // check if we need to specify received value
      if (isExplicable(receivedType)) {
          message += "with value ".concat(styleValue(value, receivedType), ".");
      }
      return message;
  }
  function styleValue(value, type) {
      if (type === 'String') {
          return "\"".concat(value, "\"");
      }
      else if (type === 'Number') {
          return "".concat(Number(value));
      }
      else {
          return "".concat(value);
      }
  }
  var EXPLICABLE_TYPES = ['string', 'number', 'boolean'];
  function isExplicable(value) {
      return EXPLICABLE_TYPES.some(function (elem) { return value.toLowerCase() === elem; });
  }
  function isBoolean() {
      var args = [];
      for (var _i = 0; _i < arguments.length; _i++) {
          args[_i] = arguments[_i];
      }
      return args.some(function (elem) { return elem.toLowerCase() === 'boolean'; });
  }

  function Vue(options) {
      if (!(this instanceof Vue)) {
          warn$2('Vue is a constructor and should be called with the `new` keyword');
      }
      this._init(options);
  }
  //@ts-expect-error Vue has function type
  initMixin$1(Vue);
  //@ts-expect-error Vue has function type
  stateMixin(Vue);
  //@ts-expect-error Vue has function type
  eventsMixin(Vue);
  //@ts-expect-error Vue has function type
  lifecycleMixin(Vue);
  //@ts-expect-error Vue has function type
  renderMixin(Vue);

  function initUse(Vue) {
      Vue.use = function (plugin) {
          var installedPlugins = this._installedPlugins || (this._installedPlugins = []);
          if (installedPlugins.indexOf(plugin) > -1) {
              return this;
          }
          // additional parameters
          var args = toArray(arguments, 1);
          args.unshift(this);
          if (isFunction(plugin.install)) {
              plugin.install.apply(plugin, args);
          }
          else if (isFunction(plugin)) {
              plugin.apply(null, args);
          }
          installedPlugins.push(plugin);
          return this;
      };
  }

  function initMixin(Vue) {
      Vue.mixin = function (mixin) {
          this.options = mergeOptions(this.options, mixin);
          return this;
      };
  }

  function initExtend(Vue) {
      /**
       * Each instance constructor, including Vue, has a unique
       * cid. This enables us to create wrapped "child
       * constructors" for prototypal inheritance and cache them.
       */
      Vue.cid = 0;
      var cid = 1;
      /**
       * Class inheritance
       */
      Vue.extend = function (extendOptions) {
          extendOptions = extendOptions || {};
          var Super = this;
          var SuperId = Super.cid;
          var cachedCtors = extendOptions._Ctor || (extendOptions._Ctor = {});
          if (cachedCtors[SuperId]) {
              return cachedCtors[SuperId];
          }
          var name = getComponentName(extendOptions) || getComponentName(Super.options);
          if (name) {
              validateComponentName(name);
          }
          var Sub = function VueComponent(options) {
              this._init(options);
          };
          Sub.prototype = Object.create(Super.prototype);
          Sub.prototype.constructor = Sub;
          Sub.cid = cid++;
          Sub.options = mergeOptions(Super.options, extendOptions);
          Sub['super'] = Super;
          // For props and computed properties, we define the proxy getters on
          // the Vue instances at extension time, on the extended prototype. This
          // avoids Object.defineProperty calls for each instance created.
          if (Sub.options.props) {
              initProps(Sub);
          }
          if (Sub.options.computed) {
              initComputed(Sub);
          }
          // allow further extension/mixin/plugin usage
          Sub.extend = Super.extend;
          Sub.mixin = Super.mixin;
          Sub.use = Super.use;
          // create asset registers, so extended classes
          // can have their private assets too.
          ASSET_TYPES.forEach(function (type) {
              Sub[type] = Super[type];
          });
          // enable recursive self-lookup
          if (name) {
              Sub.options.components[name] = Sub;
          }
          // keep a reference to the super options at extension time.
          // later at instantiation we can check if Super's options have
          // been updated.
          Sub.superOptions = Super.options;
          Sub.extendOptions = extendOptions;
          Sub.sealedOptions = extend({}, Sub.options);
          // cache constructor
          cachedCtors[SuperId] = Sub;
          return Sub;
      };
  }
  function initProps(Comp) {
      var props = Comp.options.props;
      for (var key in props) {
          proxy(Comp.prototype, "_props", key);
      }
  }
  function initComputed(Comp) {
      var computed = Comp.options.computed;
      for (var key in computed) {
          defineComputed(Comp.prototype, key, computed[key]);
      }
  }

  function initAssetRegisters(Vue) {
      /**
       * Create asset registration methods.
       */
      ASSET_TYPES.forEach(function (type) {
          // @ts-expect-error function is not exact same type
          Vue[type] = function (id, definition) {
              if (!definition) {
                  return this.options[type + 's'][id];
              }
              else {
                  /* istanbul ignore if */
                  if (type === 'component') {
                      validateComponentName(id);
                  }
                  if (type === 'component' && isPlainObject(definition)) {
                      // @ts-expect-error
                      definition.name = definition.name || id;
                      definition = this.options._base.extend(definition);
                  }
                  if (type === 'directive' && isFunction(definition)) {
                      definition = { bind: definition, update: definition };
                  }
                  this.options[type + 's'][id] = definition;
                  return definition;
              }
          };
      });
  }

  function _getComponentName(opts) {
      return opts && (getComponentName(opts.Ctor.options) || opts.tag);
  }
  function matches(pattern, name) {
      if (isArray(pattern)) {
          return pattern.indexOf(name) > -1;
      }
      else if (typeof pattern === 'string') {
          return pattern.split(',').indexOf(name) > -1;
      }
      else if (isRegExp(pattern)) {
          return pattern.test(name);
      }
      /* istanbul ignore next */
      return false;
  }
  function pruneCache(keepAliveInstance, filter) {
      var cache = keepAliveInstance.cache, keys = keepAliveInstance.keys, _vnode = keepAliveInstance._vnode, $vnode = keepAliveInstance.$vnode;
      for (var key in cache) {
          var entry = cache[key];
          if (entry) {
              var name_1 = entry.name;
              if (name_1 && !filter(name_1)) {
                  pruneCacheEntry(cache, key, keys, _vnode);
              }
          }
      }
      $vnode.componentOptions.children = undefined;
  }
  function pruneCacheEntry(cache, key, keys, current) {
      var entry = cache[key];
      if (entry && (!current || entry.tag !== current.tag)) {
          // @ts-expect-error can be undefined
          entry.componentInstance.$destroy();
      }
      cache[key] = null;
      remove$2(keys, key);
  }
  var patternTypes = [String, RegExp, Array];
  // TODO defineComponent
  var KeepAlive = {
      name: 'keep-alive',
      abstract: true,
      props: {
          include: patternTypes,
          exclude: patternTypes,
          max: [String, Number]
      },
      methods: {
          cacheVNode: function () {
              var _a = this, cache = _a.cache, keys = _a.keys, vnodeToCache = _a.vnodeToCache, keyToCache = _a.keyToCache;
              if (vnodeToCache) {
                  var tag = vnodeToCache.tag, componentInstance = vnodeToCache.componentInstance, componentOptions = vnodeToCache.componentOptions;
                  cache[keyToCache] = {
                      name: _getComponentName(componentOptions),
                      tag: tag,
                      componentInstance: componentInstance
                  };
                  keys.push(keyToCache);
                  // prune oldest entry
                  if (this.max && keys.length > parseInt(this.max)) {
                      pruneCacheEntry(cache, keys[0], keys, this._vnode);
                  }
                  this.vnodeToCache = null;
              }
          }
      },
      created: function () {
          this.cache = Object.create(null);
          this.keys = [];
      },
      destroyed: function () {
          for (var key in this.cache) {
              pruneCacheEntry(this.cache, key, this.keys);
          }
      },
      mounted: function () {
          var _this = this;
          this.cacheVNode();
          this.$watch('include', function (val) {
              pruneCache(_this, function (name) { return matches(val, name); });
          });
          this.$watch('exclude', function (val) {
              pruneCache(_this, function (name) { return !matches(val, name); });
          });
      },
      updated: function () {
          this.cacheVNode();
      },
      render: function () {
          var slot = this.$slots.default;
          var vnode = getFirstComponentChild(slot);
          var componentOptions = vnode && vnode.componentOptions;
          if (componentOptions) {
              // check pattern
              var name_2 = _getComponentName(componentOptions);
              var _a = this, include = _a.include, exclude = _a.exclude;
              if (
              // not included
              (include && (!name_2 || !matches(include, name_2))) ||
                  // excluded
                  (exclude && name_2 && matches(exclude, name_2))) {
                  return vnode;
              }
              var _b = this, cache = _b.cache, keys = _b.keys;
              var key = vnode.key == null
                  ? // same constructor may get registered as different local components
                      // so cid alone is not enough (#3269)
                      componentOptions.Ctor.cid +
                          (componentOptions.tag ? "::".concat(componentOptions.tag) : '')
                  : vnode.key;
              if (cache[key]) {
                  vnode.componentInstance = cache[key].componentInstance;
                  // make current key freshest
                  remove$2(keys, key);
                  keys.push(key);
              }
              else {
                  // delay setting the cache until update
                  this.vnodeToCache = vnode;
                  this.keyToCache = key;
              }
              // @ts-expect-error can vnode.data can be undefined
              vnode.data.keepAlive = true;
          }
          return vnode || (slot && slot[0]);
      }
  };

  var builtInComponents = {
      KeepAlive: KeepAlive
  };

  function initGlobalAPI(Vue) {
      // config
      var configDef = {};
      configDef.get = function () { return config; };
      {
          configDef.set = function () {
              warn$2('Do not replace the Vue.config object, set individual fields instead.');
          };
      }
      Object.defineProperty(Vue, 'config', configDef);
      // exposed util methods.
      // NOTE: these are not considered part of the public API - avoid relying on
      // them unless you are aware of the risk.
      Vue.util = {
          warn: warn$2,
          extend: extend,
          mergeOptions: mergeOptions,
          defineReactive: defineReactive
      };
      Vue.set = set;
      Vue.delete = del;
      Vue.nextTick = nextTick;
      // 2.6 explicit observable API
      Vue.observable = function (obj) {
          observe(obj);
          return obj;
      };
      Vue.options = Object.create(null);
      ASSET_TYPES.forEach(function (type) {
          Vue.options[type + 's'] = Object.create(null);
      });
      // this is used to identify the "base" constructor to extend all plain-object
      // components with in Weex's multi-instance scenarios.
      Vue.options._base = Vue;
      extend(Vue.options.components, builtInComponents);
      initUse(Vue);
      initMixin(Vue);
      initExtend(Vue);
      initAssetRegisters(Vue);
  }

  initGlobalAPI(Vue);
  Object.defineProperty(Vue.prototype, '$isServer', {
      get: isServerRendering
  });
  Object.defineProperty(Vue.prototype, '$ssrContext', {
      get: function () {
          /* istanbul ignore next */
          return this.$vnode && this.$vnode.ssrContext;
      }
  });
  // expose FunctionalRenderContext for ssr runtime helper installation
  Object.defineProperty(Vue, 'FunctionalRenderContext', {
      value: FunctionalRenderContext
  });
  Vue.version = version;

  // these are reserved for web because they are directly compiled away
  // during template compilation
  var isReservedAttr = makeMap('style,class');
  // attributes that should be using props for binding
  var acceptValue = makeMap('input,textarea,option,select,progress');
  var mustUseProp = function (tag, type, attr) {
      return ((attr === 'value' && acceptValue(tag) && type !== 'button') ||
          (attr === 'selected' && tag === 'option') ||
          (attr === 'checked' && tag === 'input') ||
          (attr === 'muted' && tag === 'video'));
  };
  var isEnumeratedAttr = makeMap('contenteditable,draggable,spellcheck');
  var isValidContentEditableValue = makeMap('events,caret,typing,plaintext-only');
  var convertEnumeratedValue = function (key, value) {
      return isFalsyAttrValue(value) || value === 'false'
          ? 'false'
          : // allow arbitrary string value for contenteditable
              key === 'contenteditable' && isValidContentEditableValue(value)
                  ? value
                  : 'true';
  };
  var isBooleanAttr = makeMap('allowfullscreen,async,autofocus,autoplay,checked,compact,controls,declare,' +
      'default,defaultchecked,defaultmuted,defaultselected,defer,disabled,' +
      'enabled,formnovalidate,hidden,indeterminate,inert,ismap,itemscope,loop,multiple,' +
      'muted,nohref,noresize,noshade,novalidate,nowrap,open,pauseonexit,readonly,' +
      'required,reversed,scoped,seamless,selected,sortable,' +
      'truespeed,typemustmatch,visible');
  var xlinkNS = 'http://www.w3.org/1999/xlink';
  var isXlink = function (name) {
      return name.charAt(5) === ':' && name.slice(0, 5) === 'xlink';
  };
  var getXlinkProp = function (name) {
      return isXlink(name) ? name.slice(6, name.length) : '';
  };
  var isFalsyAttrValue = function (val) {
      return val == null || val === false;
  };

  function genClassForVnode(vnode) {
      var data = vnode.data;
      var parentNode = vnode;
      var childNode = vnode;
      while (isDef(childNode.componentInstance)) {
          childNode = childNode.componentInstance._vnode;
          if (childNode && childNode.data) {
              data = mergeClassData(childNode.data, data);
          }
      }
      // @ts-expect-error parentNode.parent not VNodeWithData
      while (isDef((parentNode = parentNode.parent))) {
          if (parentNode && parentNode.data) {
              data = mergeClassData(data, parentNode.data);
          }
      }
      return renderClass(data.staticClass, data.class);
  }
  function mergeClassData(child, parent) {
      return {
          staticClass: concat(child.staticClass, parent.staticClass),
          class: isDef(child.class) ? [child.class, parent.class] : parent.class
      };
  }
  function renderClass(staticClass, dynamicClass) {
      if (isDef(staticClass) || isDef(dynamicClass)) {
          return concat(staticClass, stringifyClass(dynamicClass));
      }
      /* istanbul ignore next */
      return '';
  }
  function concat(a, b) {
      return a ? (b ? a + ' ' + b : a) : b || '';
  }
  function stringifyClass(value) {
      if (Array.isArray(value)) {
          return stringifyArray(value);
      }
      if (isObject(value)) {
          return stringifyObject(value);
      }
      if (typeof value === 'string') {
          return value;
      }
      /* istanbul ignore next */
      return '';
  }
  function stringifyArray(value) {
      var res = '';
      var stringified;
      for (var i = 0, l = value.length; i < l; i++) {
          if (isDef((stringified = stringifyClass(value[i]))) && stringified !== '') {
              if (res)
                  res += ' ';
              res += stringified;
          }
      }
      return res;
  }
  function stringifyObject(value) {
      var res = '';
      for (var key in value) {
          if (value[key]) {
              if (res)
                  res += ' ';
              res += key;
          }
      }
      return res;
  }

  var namespaceMap = {
      svg: 'http://www.w3.org/2000/svg',
      math: 'http://www.w3.org/1998/Math/MathML'
  };
  var isHTMLTag = makeMap('html,body,base,head,link,meta,style,title,' +
      'address,article,aside,footer,header,h1,h2,h3,h4,h5,h6,hgroup,nav,section,' +
      'div,dd,dl,dt,figcaption,figure,picture,hr,img,li,main,ol,p,pre,ul,' +
      'a,b,abbr,bdi,bdo,br,cite,code,data,dfn,em,i,kbd,mark,q,rp,rt,rtc,ruby,' +
      's,samp,small,span,strong,sub,sup,time,u,var,wbr,area,audio,map,track,video,' +
      'embed,object,param,source,canvas,script,noscript,del,ins,' +
      'caption,col,colgroup,table,thead,tbody,td,th,tr,' +
      'button,datalist,fieldset,form,input,label,legend,meter,optgroup,option,' +
      'output,progress,select,textarea,' +
      'details,dialog,menu,menuitem,summary,' +
      'content,element,shadow,template,blockquote,iframe,tfoot');
  // this map is intentionally selective, only covering SVG elements that may
  // contain child elements.
  var isSVG = makeMap('svg,animate,circle,clippath,cursor,defs,desc,ellipse,filter,font-face,' +
      'foreignobject,g,glyph,image,line,marker,mask,missing-glyph,path,pattern,' +
      'polygon,polyline,rect,switch,symbol,text,textpath,tspan,use,view', true);
  var isPreTag = function (tag) { return tag === 'pre'; };
  var isReservedTag = function (tag) {
      return isHTMLTag(tag) || isSVG(tag);
  };
  function getTagNamespace(tag) {
      if (isSVG(tag)) {
          return 'svg';
      }
      // basic support for MathML
      // note it doesn't support other MathML elements being component roots
      if (tag === 'math') {
          return 'math';
      }
  }
  var unknownElementCache = Object.create(null);
  function isUnknownElement(tag) {
      /* istanbul ignore if */
      if (!inBrowser) {
          return true;
      }
      if (isReservedTag(tag)) {
          return false;
      }
      tag = tag.toLowerCase();
      /* istanbul ignore if */
      if (unknownElementCache[tag] != null) {
          return unknownElementCache[tag];
      }
      var el = document.createElement(tag);
      if (tag.indexOf('-') > -1) {
          // https://stackoverflow.com/a/28210364/1070244
          return (unknownElementCache[tag] =
              el.constructor === window.HTMLUnknownElement ||
                  el.constructor === window.HTMLElement);
      }
      else {
          return (unknownElementCache[tag] = /HTMLUnknownElement/.test(el.toString()));
      }
  }
  var isTextInputType = makeMap('text,number,password,search,email,tel,url');

  /**
   * Query an element selector if it's not an element already.
   */
  function query(el) {
      if (typeof el === 'string') {
          var selected = document.querySelector(el);
          if (!selected) {
              warn$2('Cannot find element: ' + el);
              return document.createElement('div');
          }
          return selected;
      }
      else {
          return el;
      }
  }

  function createElement(tagName, vnode) {
      var elm = document.createElement(tagName);
      if (tagName !== 'select') {
          return elm;
      }
      // false or null will remove the attribute but undefined will not
      if (vnode.data &&
          vnode.data.attrs &&
          vnode.data.attrs.multiple !== undefined) {
          elm.setAttribute('multiple', 'multiple');
      }
      return elm;
  }
  function createElementNS(namespace, tagName) {
      return document.createElementNS(namespaceMap[namespace], tagName);
  }
  function createTextNode(text) {
      return document.createTextNode(text);
  }
  function createComment(text) {
      return document.createComment(text);
  }
  function insertBefore(parentNode, newNode, referenceNode) {
      parentNode.insertBefore(newNode, referenceNode);
  }
  function removeChild(node, child) {
      node.removeChild(child);
  }
  function appendChild(node, child) {
      node.appendChild(child);
  }
  function parentNode(node) {
      return node.parentNode;
  }
  function nextSibling(node) {
      return node.nextSibling;
  }
  function tagName(node) {
      return node.tagName;
  }
  function setTextContent(node, text) {
      node.textContent = text;
  }
  function setStyleScope(node, scopeId) {
      node.setAttribute(scopeId, '');
  }

  var nodeOps = /*#__PURE__*/Object.freeze({
    __proto__: null,
    createElement: createElement,
    createElementNS: createElementNS,
    createTextNode: createTextNode,
    createComment: createComment,
    insertBefore: insertBefore,
    removeChild: removeChild,
    appendChild: appendChild,
    parentNode: parentNode,
    nextSibling: nextSibling,
    tagName: tagName,
    setTextContent: setTextContent,
    setStyleScope: setStyleScope
  });

  var ref = {
      create: function (_, vnode) {
          registerRef(vnode);
      },
      update: function (oldVnode, vnode) {
          if (oldVnode.data.ref !== vnode.data.ref) {
              registerRef(oldVnode, true);
              registerRef(vnode);
          }
      },
      destroy: function (vnode) {
          registerRef(vnode, true);
      }
  };
  function registerRef(vnode, isRemoval) {
      var ref = vnode.data.ref;
      if (!isDef(ref))
          return;
      var vm = vnode.context;
      var refValue = vnode.componentInstance || vnode.elm;
      var value = isRemoval ? null : refValue;
      var $refsValue = isRemoval ? undefined : refValue;
      if (isFunction(ref)) {
          invokeWithErrorHandling(ref, vm, [value], vm, "template ref function");
          return;
      }
      var isFor = vnode.data.refInFor;
      var _isString = typeof ref === 'string' || typeof ref === 'number';
      var _isRef = isRef(ref);
      var refs = vm.$refs;
      if (_isString || _isRef) {
          if (isFor) {
              var existing = _isString ? refs[ref] : ref.value;
              if (isRemoval) {
                  isArray(existing) && remove$2(existing, refValue);
              }
              else {
                  if (!isArray(existing)) {
                      if (_isString) {
                          refs[ref] = [refValue];
                          setSetupRef(vm, ref, refs[ref]);
                      }
                      else {
                          ref.value = [refValue];
                      }
                  }
                  else if (!existing.includes(refValue)) {
                      existing.push(refValue);
                  }
              }
          }
          else if (_isString) {
              if (isRemoval && refs[ref] !== refValue) {
                  return;
              }
              refs[ref] = $refsValue;
              setSetupRef(vm, ref, value);
          }
          else if (_isRef) {
              if (isRemoval && ref.value !== refValue) {
                  return;
              }
              ref.value = value;
          }
          else {
              warn$2("Invalid template ref type: ".concat(typeof ref));
          }
      }
  }
  function setSetupRef(_a, key, val) {
      var _setupState = _a._setupState;
      if (_setupState && hasOwn(_setupState, key)) {
          if (isRef(_setupState[key])) {
              _setupState[key].value = val;
          }
          else {
              _setupState[key] = val;
          }
      }
  }

  /**
   * Virtual DOM patching algorithm based on Snabbdom by
   * Simon Friis Vindum (@paldepind)
   * Licensed under the MIT License
   * https://github.com/paldepind/snabbdom/blob/master/LICENSE
   *
   * modified by Evan You (@yyx990803)
   *
   * Not type-checking this because this file is perf-critical and the cost
   * of making flow understand it is not worth it.
   */
  var emptyNode = new VNode('', {}, []);
  var hooks = ['create', 'activate', 'update', 'remove', 'destroy'];
  function sameVnode(a, b) {
      return (a.key === b.key &&
          a.asyncFactory === b.asyncFactory &&
          ((a.tag === b.tag &&
              a.isComment === b.isComment &&
              isDef(a.data) === isDef(b.data) &&
              sameInputType(a, b)) ||
              (isTrue(a.isAsyncPlaceholder) && isUndef(b.asyncFactory.error))));
  }
  function sameInputType(a, b) {
      if (a.tag !== 'input')
          return true;
      var i;
      var typeA = isDef((i = a.data)) && isDef((i = i.attrs)) && i.type;
      var typeB = isDef((i = b.data)) && isDef((i = i.attrs)) && i.type;
      return typeA === typeB || (isTextInputType(typeA) && isTextInputType(typeB));
  }
  function createKeyToOldIdx(children, beginIdx, endIdx) {
      var i, key;
      var map = {};
      for (i = beginIdx; i <= endIdx; ++i) {
          key = children[i].key;
          if (isDef(key))
              map[key] = i;
      }
      return map;
  }
  function createPatchFunction(backend) {
      var i, j;
      var cbs = {};
      var modules = backend.modules, nodeOps = backend.nodeOps;
      for (i = 0; i < hooks.length; ++i) {
          cbs[hooks[i]] = [];
          for (j = 0; j < modules.length; ++j) {
              if (isDef(modules[j][hooks[i]])) {
                  cbs[hooks[i]].push(modules[j][hooks[i]]);
              }
          }
      }
      function emptyNodeAt(elm) {
          return new VNode(nodeOps.tagName(elm).toLowerCase(), {}, [], undefined, elm);
      }
      function createRmCb(childElm, listeners) {
          function remove() {
              if (--remove.listeners === 0) {
                  removeNode(childElm);
              }
          }
          remove.listeners = listeners;
          return remove;
      }
      function removeNode(el) {
          var parent = nodeOps.parentNode(el);
          // element may have already been removed due to v-html / v-text
          if (isDef(parent)) {
              nodeOps.removeChild(parent, el);
          }
      }
      function isUnknownElement(vnode, inVPre) {
          return (!inVPre &&
              !vnode.ns &&
              !(config.ignoredElements.length &&
                  config.ignoredElements.some(function (ignore) {
                      return isRegExp(ignore)
                          ? ignore.test(vnode.tag)
                          : ignore === vnode.tag;
                  })) &&
              config.isUnknownElement(vnode.tag));
      }
      var creatingElmInVPre = 0;
      function createElm(vnode, insertedVnodeQueue, parentElm, refElm, nested, ownerArray, index) {
          if (isDef(vnode.elm) && isDef(ownerArray)) {
              // This vnode was used in a previous render!
              // now it's used as a new node, overwriting its elm would cause
              // potential patch errors down the road when it's used as an insertion
              // reference node. Instead, we clone the node on-demand before creating
              // associated DOM element for it.
              vnode = ownerArray[index] = cloneVNode(vnode);
          }
          vnode.isRootInsert = !nested; // for transition enter check
          if (createComponent(vnode, insertedVnodeQueue, parentElm, refElm)) {
              return;
          }
          var data = vnode.data;
          var children = vnode.children;
          var tag = vnode.tag;
          if (isDef(tag)) {
              {
                  if (data && data.pre) {
                      creatingElmInVPre++;
                  }
                  if (isUnknownElement(vnode, creatingElmInVPre)) {
                      warn$2('Unknown custom element: <' +
                          tag +
                          '> - did you ' +
                          'register the component correctly? For recursive components, ' +
                          'make sure to provide the "name" option.', vnode.context);
                  }
              }
              vnode.elm = vnode.ns
                  ? nodeOps.createElementNS(vnode.ns, tag)
                  : nodeOps.createElement(tag, vnode);
              setScope(vnode);
              createChildren(vnode, children, insertedVnodeQueue);
              if (isDef(data)) {
                  invokeCreateHooks(vnode, insertedVnodeQueue);
              }
              insert(parentElm, vnode.elm, refElm);
              if (data && data.pre) {
                  creatingElmInVPre--;
              }
          }
          else if (isTrue(vnode.isComment)) {
              vnode.elm = nodeOps.createComment(vnode.text);
              insert(parentElm, vnode.elm, refElm);
          }
          else {
              vnode.elm = nodeOps.createTextNode(vnode.text);
              insert(parentElm, vnode.elm, refElm);
          }
      }
      function createComponent(vnode, insertedVnodeQueue, parentElm, refElm) {
          var i = vnode.data;
          if (isDef(i)) {
              var isReactivated = isDef(vnode.componentInstance) && i.keepAlive;
              if (isDef((i = i.hook)) && isDef((i = i.init))) {
                  i(vnode, false /* hydrating */);
              }
              // after calling the init hook, if the vnode is a child component
              // it should've created a child instance and mounted it. the child
              // component also has set the placeholder vnode's elm.
              // in that case we can just return the element and be done.
              if (isDef(vnode.componentInstance)) {
                  initComponent(vnode, insertedVnodeQueue);
                  insert(parentElm, vnode.elm, refElm);
                  if (isTrue(isReactivated)) {
                      reactivateComponent(vnode, insertedVnodeQueue, parentElm, refElm);
                  }
                  return true;
              }
          }
      }
      function initComponent(vnode, insertedVnodeQueue) {
          if (isDef(vnode.data.pendingInsert)) {
              insertedVnodeQueue.push.apply(insertedVnodeQueue, vnode.data.pendingInsert);
              vnode.data.pendingInsert = null;
          }
          vnode.elm = vnode.componentInstance.$el;
          if (isPatchable(vnode)) {
              invokeCreateHooks(vnode, insertedVnodeQueue);
              setScope(vnode);
          }
          else {
              // empty component root.
              // skip all element-related modules except for ref (#3455)
              registerRef(vnode);
              // make sure to invoke the insert hook
              insertedVnodeQueue.push(vnode);
          }
      }
      function reactivateComponent(vnode, insertedVnodeQueue, parentElm, refElm) {
          var i;
          // hack for #4339: a reactivated component with inner transition
          // does not trigger because the inner node's created hooks are not called
          // again. It's not ideal to involve module-specific logic in here but
          // there doesn't seem to be a better way to do it.
          var innerNode = vnode;
          while (innerNode.componentInstance) {
              innerNode = innerNode.componentInstance._vnode;
              if (isDef((i = innerNode.data)) && isDef((i = i.transition))) {
                  for (i = 0; i < cbs.activate.length; ++i) {
                      cbs.activate[i](emptyNode, innerNode);
                  }
                  insertedVnodeQueue.push(innerNode);
                  break;
              }
          }
          // unlike a newly created component,
          // a reactivated keep-alive component doesn't insert itself
          insert(parentElm, vnode.elm, refElm);
      }
      function insert(parent, elm, ref) {
          if (isDef(parent)) {
              if (isDef(ref)) {
                  if (nodeOps.parentNode(ref) === parent) {
                      nodeOps.insertBefore(parent, elm, ref);
                  }
              }
              else {
                  nodeOps.appendChild(parent, elm);
              }
          }
      }
      function createChildren(vnode, children, insertedVnodeQueue) {
          if (isArray(children)) {
              {
                  checkDuplicateKeys(children);
              }
              for (var i_1 = 0; i_1 < children.length; ++i_1) {
                  createElm(children[i_1], insertedVnodeQueue, vnode.elm, null, true, children, i_1);
              }
          }
          else if (isPrimitive(vnode.text)) {
              nodeOps.appendChild(vnode.elm, nodeOps.createTextNode(String(vnode.text)));
          }
      }
      function isPatchable(vnode) {
          while (vnode.componentInstance) {
              vnode = vnode.componentInstance._vnode;
          }
          return isDef(vnode.tag);
      }
      function invokeCreateHooks(vnode, insertedVnodeQueue) {
          for (var i_2 = 0; i_2 < cbs.create.length; ++i_2) {
              cbs.create[i_2](emptyNode, vnode);
          }
          i = vnode.data.hook; // Reuse variable
          if (isDef(i)) {
              if (isDef(i.create))
                  i.create(emptyNode, vnode);
              if (isDef(i.insert))
                  insertedVnodeQueue.push(vnode);
          }
      }
      // set scope id attribute for scoped CSS.
      // this is implemented as a special case to avoid the overhead
      // of going through the normal attribute patching process.
      function setScope(vnode) {
          var i;
          if (isDef((i = vnode.fnScopeId))) {
              nodeOps.setStyleScope(vnode.elm, i);
          }
          else {
              var ancestor = vnode;
              while (ancestor) {
                  if (isDef((i = ancestor.context)) && isDef((i = i.$options._scopeId))) {
                      nodeOps.setStyleScope(vnode.elm, i);
                  }
                  ancestor = ancestor.parent;
              }
          }
          // for slot content they should also get the scopeId from the host instance.
          if (isDef((i = activeInstance)) &&
              i !== vnode.context &&
              i !== vnode.fnContext &&
              isDef((i = i.$options._scopeId))) {
              nodeOps.setStyleScope(vnode.elm, i);
          }
      }
      function addVnodes(parentElm, refElm, vnodes, startIdx, endIdx, insertedVnodeQueue) {
          for (; startIdx <= endIdx; ++startIdx) {
              createElm(vnodes[startIdx], insertedVnodeQueue, parentElm, refElm, false, vnodes, startIdx);
          }
      }
      function invokeDestroyHook(vnode) {
          var i, j;
          var data = vnode.data;
          if (isDef(data)) {
              if (isDef((i = data.hook)) && isDef((i = i.destroy)))
                  i(vnode);
              for (i = 0; i < cbs.destroy.length; ++i)
                  cbs.destroy[i](vnode);
          }
          if (isDef((i = vnode.children))) {
              for (j = 0; j < vnode.children.length; ++j) {
                  invokeDestroyHook(vnode.children[j]);
              }
          }
      }
      function removeVnodes(vnodes, startIdx, endIdx) {
          for (; startIdx <= endIdx; ++startIdx) {
              var ch = vnodes[startIdx];
              if (isDef(ch)) {
                  if (isDef(ch.tag)) {
                      removeAndInvokeRemoveHook(ch);
                      invokeDestroyHook(ch);
                  }
                  else {
                      // Text node
                      removeNode(ch.elm);
                  }
              }
          }
      }
      function removeAndInvokeRemoveHook(vnode, rm) {
          if (isDef(rm) || isDef(vnode.data)) {
              var i_3;
              var listeners = cbs.remove.length + 1;
              if (isDef(rm)) {
                  // we have a recursively passed down rm callback
                  // increase the listeners count
                  rm.listeners += listeners;
              }
              else {
                  // directly removing
                  rm = createRmCb(vnode.elm, listeners);
              }
              // recursively invoke hooks on child component root node
              if (isDef((i_3 = vnode.componentInstance)) &&
                  isDef((i_3 = i_3._vnode)) &&
                  isDef(i_3.data)) {
                  removeAndInvokeRemoveHook(i_3, rm);
              }
              for (i_3 = 0; i_3 < cbs.remove.length; ++i_3) {
                  cbs.remove[i_3](vnode, rm);
              }
              if (isDef((i_3 = vnode.data.hook)) && isDef((i_3 = i_3.remove))) {
                  i_3(vnode, rm);
              }
              else {
                  rm();
              }
          }
          else {
              removeNode(vnode.elm);
          }
      }
      function updateChildren(parentElm, oldCh, newCh, insertedVnodeQueue, removeOnly) {
          var oldStartIdx = 0;
          var newStartIdx = 0;
          var oldEndIdx = oldCh.length - 1;
          var oldStartVnode = oldCh[0];
          var oldEndVnode = oldCh[oldEndIdx];
          var newEndIdx = newCh.length - 1;
          var newStartVnode = newCh[0];
          var newEndVnode = newCh[newEndIdx];
          var oldKeyToIdx, idxInOld, vnodeToMove, refElm;
          // removeOnly is a special flag used only by <transition-group>
          // to ensure removed elements stay in correct relative positions
          // during leaving transitions
          var canMove = !removeOnly;
          {
              checkDuplicateKeys(newCh);
          }
          while (oldStartIdx <= oldEndIdx && newStartIdx <= newEndIdx) {
              if (isUndef(oldStartVnode)) {
                  oldStartVnode = oldCh[++oldStartIdx]; // Vnode has been moved left
              }
              else if (isUndef(oldEndVnode)) {
                  oldEndVnode = oldCh[--oldEndIdx];
              }
              else if (sameVnode(oldStartVnode, newStartVnode)) {
                  patchVnode(oldStartVnode, newStartVnode, insertedVnodeQueue, newCh, newStartIdx);
                  oldStartVnode = oldCh[++oldStartIdx];
                  newStartVnode = newCh[++newStartIdx];
              }
              else if (sameVnode(oldEndVnode, newEndVnode)) {
                  patchVnode(oldEndVnode, newEndVnode, insertedVnodeQueue, newCh, newEndIdx);
                  oldEndVnode = oldCh[--oldEndIdx];
                  newEndVnode = newCh[--newEndIdx];
              }
              else if (sameVnode(oldStartVnode, newEndVnode)) {
                  // Vnode moved right
                  patchVnode(oldStartVnode, newEndVnode, insertedVnodeQueue, newCh, newEndIdx);
                  canMove &&
                      nodeOps.insertBefore(parentElm, oldStartVnode.elm, nodeOps.nextSibling(oldEndVnode.elm));
                  oldStartVnode = oldCh[++oldStartIdx];
                  newEndVnode = newCh[--newEndIdx];
              }
              else if (sameVnode(oldEndVnode, newStartVnode)) {
                  // Vnode moved left
                  patchVnode(oldEndVnode, newStartVnode, insertedVnodeQueue, newCh, newStartIdx);
                  canMove &&
                      nodeOps.insertBefore(parentElm, oldEndVnode.elm, oldStartVnode.elm);
                  oldEndVnode = oldCh[--oldEndIdx];
                  newStartVnode = newCh[++newStartIdx];
              }
              else {
                  if (isUndef(oldKeyToIdx))
                      oldKeyToIdx = createKeyToOldIdx(oldCh, oldStartIdx, oldEndIdx);
                  idxInOld = isDef(newStartVnode.key)
                      ? oldKeyToIdx[newStartVnode.key]
                      : findIdxInOld(newStartVnode, oldCh, oldStartIdx, oldEndIdx);
                  if (isUndef(idxInOld)) {
                      // New element
                      createElm(newStartVnode, insertedVnodeQueue, parentElm, oldStartVnode.elm, false, newCh, newStartIdx);
                  }
                  else {
                      vnodeToMove = oldCh[idxInOld];
                      if (sameVnode(vnodeToMove, newStartVnode)) {
                          patchVnode(vnodeToMove, newStartVnode, insertedVnodeQueue, newCh, newStartIdx);
                          oldCh[idxInOld] = undefined;
                          canMove &&
                              nodeOps.insertBefore(parentElm, vnodeToMove.elm, oldStartVnode.elm);
                      }
                      else {
                          // same key but different element. treat as new element
                          createElm(newStartVnode, insertedVnodeQueue, parentElm, oldStartVnode.elm, false, newCh, newStartIdx);
                      }
                  }
                  newStartVnode = newCh[++newStartIdx];
              }
          }
          if (oldStartIdx > oldEndIdx) {
              refElm = isUndef(newCh[newEndIdx + 1]) ? null : newCh[newEndIdx + 1].elm;
              addVnodes(parentElm, refElm, newCh, newStartIdx, newEndIdx, insertedVnodeQueue);
          }
          else if (newStartIdx > newEndIdx) {
              removeVnodes(oldCh, oldStartIdx, oldEndIdx);
          }
      }
      function checkDuplicateKeys(children) {
          var seenKeys = {};
          for (var i_4 = 0; i_4 < children.length; i_4++) {
              var vnode = children[i_4];
              var key = vnode.key;
              if (isDef(key)) {
                  if (seenKeys[key]) {
                      warn$2("Duplicate keys detected: '".concat(key, "'. This may cause an update error."), vnode.context);
                  }
                  else {
                      seenKeys[key] = true;
                  }
              }
          }
      }
      function findIdxInOld(node, oldCh, start, end) {
          for (var i_5 = start; i_5 < end; i_5++) {
              var c = oldCh[i_5];
              if (isDef(c) && sameVnode(node, c))
                  return i_5;
          }
      }
      function patchVnode(oldVnode, vnode, insertedVnodeQueue, ownerArray, index, removeOnly) {
          if (oldVnode === vnode) {
              return;
          }
          if (isDef(vnode.elm) && isDef(ownerArray)) {
              // clone reused vnode
              vnode = ownerArray[index] = cloneVNode(vnode);
          }
          var elm = (vnode.elm = oldVnode.elm);
          if (isTrue(oldVnode.isAsyncPlaceholder)) {
              if (isDef(vnode.asyncFactory.resolved)) {
                  hydrate(oldVnode.elm, vnode, insertedVnodeQueue);
              }
              else {
                  vnode.isAsyncPlaceholder = true;
              }
              return;
          }
          // reuse element for static trees.
          // note we only do this if the vnode is cloned -
          // if the new node is not cloned it means the render functions have been
          // reset by the hot-reload-api and we need to do a proper re-render.
          if (isTrue(vnode.isStatic) &&
              isTrue(oldVnode.isStatic) &&
              vnode.key === oldVnode.key &&
              (isTrue(vnode.isCloned) || isTrue(vnode.isOnce))) {
              vnode.componentInstance = oldVnode.componentInstance;
              return;
          }
          var i;
          var data = vnode.data;
          if (isDef(data) && isDef((i = data.hook)) && isDef((i = i.prepatch))) {
              i(oldVnode, vnode);
          }
          var oldCh = oldVnode.children;
          var ch = vnode.children;
          if (isDef(data) && isPatchable(vnode)) {
              for (i = 0; i < cbs.update.length; ++i)
                  cbs.update[i](oldVnode, vnode);
              if (isDef((i = data.hook)) && isDef((i = i.update)))
                  i(oldVnode, vnode);
          }
          if (isUndef(vnode.text)) {
              if (isDef(oldCh) && isDef(ch)) {
                  if (oldCh !== ch)
                      updateChildren(elm, oldCh, ch, insertedVnodeQueue, removeOnly);
              }
              else if (isDef(ch)) {
                  {
                      checkDuplicateKeys(ch);
                  }
                  if (isDef(oldVnode.text))
                      nodeOps.setTextContent(elm, '');
                  addVnodes(elm, null, ch, 0, ch.length - 1, insertedVnodeQueue);
              }
              else if (isDef(oldCh)) {
                  removeVnodes(oldCh, 0, oldCh.length - 1);
              }
              else if (isDef(oldVnode.text)) {
                  nodeOps.setTextContent(elm, '');
              }
          }
          else if (oldVnode.text !== vnode.text) {
              nodeOps.setTextContent(elm, vnode.text);
          }
          if (isDef(data)) {
              if (isDef((i = data.hook)) && isDef((i = i.postpatch)))
                  i(oldVnode, vnode);
          }
      }
      function invokeInsertHook(vnode, queue, initial) {
          // delay insert hooks for component root nodes, invoke them after the
          // element is really inserted
          if (isTrue(initial) && isDef(vnode.parent)) {
              vnode.parent.data.pendingInsert = queue;
          }
          else {
              for (var i_6 = 0; i_6 < queue.length; ++i_6) {
                  queue[i_6].data.hook.insert(queue[i_6]);
              }
          }
      }
      var hydrationBailed = false;
      // list of modules that can skip create hook during hydration because they
      // are already rendered on the client or has no need for initialization
      // Note: style is excluded because it relies on initial clone for future
      // deep updates (#7063).
      var isRenderedModule = makeMap('attrs,class,staticClass,staticStyle,key');
      // Note: this is a browser-only function so we can assume elms are DOM nodes.
      function hydrate(elm, vnode, insertedVnodeQueue, inVPre) {
          var i;
          var tag = vnode.tag, data = vnode.data, children = vnode.children;
          inVPre = inVPre || (data && data.pre);
          vnode.elm = elm;
          if (isTrue(vnode.isComment) && isDef(vnode.asyncFactory)) {
              vnode.isAsyncPlaceholder = true;
              return true;
          }
          // assert node match
          {
              if (!assertNodeMatch(elm, vnode, inVPre)) {
                  return false;
              }
          }
          if (isDef(data)) {
              if (isDef((i = data.hook)) && isDef((i = i.init)))
                  i(vnode, true /* hydrating */);
              if (isDef((i = vnode.componentInstance))) {
                  // child component. it should have hydrated its own tree.
                  initComponent(vnode, insertedVnodeQueue);
                  return true;
              }
          }
          if (isDef(tag)) {
              if (isDef(children)) {
                  // empty element, allow client to pick up and populate children
                  if (!elm.hasChildNodes()) {
                      createChildren(vnode, children, insertedVnodeQueue);
                  }
                  else {
                      // v-html and domProps: innerHTML
                      if (isDef((i = data)) &&
                          isDef((i = i.domProps)) &&
                          isDef((i = i.innerHTML))) {
                          if (i !== elm.innerHTML) {
                              /* istanbul ignore if */
                              if (typeof console !== 'undefined' &&
                                  !hydrationBailed) {
                                  hydrationBailed = true;
                                  console.warn('Parent: ', elm);
                                  console.warn('server innerHTML: ', i);
                                  console.warn('client innerHTML: ', elm.innerHTML);
                              }
                              return false;
                          }
                      }
                      else {
                          // iterate and compare children lists
                          var childrenMatch = true;
                          var childNode = elm.firstChild;
                          for (var i_7 = 0; i_7 < children.length; i_7++) {
                              if (!childNode ||
                                  !hydrate(childNode, children[i_7], insertedVnodeQueue, inVPre)) {
                                  childrenMatch = false;
                                  break;
                              }
                              childNode = childNode.nextSibling;
                          }
                          // if childNode is not null, it means the actual childNodes list is
                          // longer than the virtual children list.
                          if (!childrenMatch || childNode) {
                              /* istanbul ignore if */
                              if (typeof console !== 'undefined' &&
                                  !hydrationBailed) {
                                  hydrationBailed = true;
                                  console.warn('Parent: ', elm);
                                  console.warn('Mismatching childNodes vs. VNodes: ', elm.childNodes, children);
                              }
                              return false;
                          }
                      }
                  }
              }
              if (isDef(data)) {
                  var fullInvoke = false;
                  for (var key in data) {
                      if (!isRenderedModule(key)) {
                          fullInvoke = true;
                          invokeCreateHooks(vnode, insertedVnodeQueue);
                          break;
                      }
                  }
                  if (!fullInvoke && data['class']) {
                      // ensure collecting deps for deep class bindings for future updates
                      traverse(data['class']);
                  }
              }
          }
          else if (elm.data !== vnode.text) {
              elm.data = vnode.text;
          }
          return true;
      }
      function assertNodeMatch(node, vnode, inVPre) {
          if (isDef(vnode.tag)) {
              return (vnode.tag.indexOf('vue-component') === 0 ||
                  (!isUnknownElement(vnode, inVPre) &&
                      vnode.tag.toLowerCase() ===
                          (node.tagName && node.tagName.toLowerCase())));
          }
          else {
              return node.nodeType === (vnode.isComment ? 8 : 3);
          }
      }
      return function patch(oldVnode, vnode, hydrating, removeOnly) {
          if (isUndef(vnode)) {
              if (isDef(oldVnode))
                  invokeDestroyHook(oldVnode);
              return;
          }
          var isInitialPatch = false;
          var insertedVnodeQueue = [];
          if (isUndef(oldVnode)) {
              // empty mount (likely as component), create new root element
              isInitialPatch = true;
              createElm(vnode, insertedVnodeQueue);
          }
          else {
              var isRealElement = isDef(oldVnode.nodeType);
              if (!isRealElement && sameVnode(oldVnode, vnode)) {
                  // patch existing root node
                  patchVnode(oldVnode, vnode, insertedVnodeQueue, null, null, removeOnly);
              }
              else {
                  if (isRealElement) {
                      // mounting to a real element
                      // check if this is server-rendered content and if we can perform
                      // a successful hydration.
                      if (oldVnode.nodeType === 1 && oldVnode.hasAttribute(SSR_ATTR)) {
                          oldVnode.removeAttribute(SSR_ATTR);
                          hydrating = true;
                      }
                      if (isTrue(hydrating)) {
                          if (hydrate(oldVnode, vnode, insertedVnodeQueue)) {
                              invokeInsertHook(vnode, insertedVnodeQueue, true);
                              return oldVnode;
                          }
                          else {
                              warn$2('The client-side rendered virtual DOM tree is not matching ' +
                                  'server-rendered content. This is likely caused by incorrect ' +
                                  'HTML markup, for example nesting block-level elements inside ' +
                                  '<p>, or missing <tbody>. Bailing hydration and performing ' +
                                  'full client-side render.');
                          }
                      }
                      // either not server-rendered, or hydration failed.
                      // create an empty node and replace it
                      oldVnode = emptyNodeAt(oldVnode);
                  }
                  // replacing existing element
                  var oldElm = oldVnode.elm;
                  var parentElm = nodeOps.parentNode(oldElm);
                  // create new node
                  createElm(vnode, insertedVnodeQueue, 
                  // extremely rare edge case: do not insert if old element is in a
                  // leaving transition. Only happens when combining transition +
                  // keep-alive + HOCs. (#4590)
                  oldElm._leaveCb ? null : parentElm, nodeOps.nextSibling(oldElm));
                  // update parent placeholder node element, recursively
                  if (isDef(vnode.parent)) {
                      var ancestor = vnode.parent;
                      var patchable = isPatchable(vnode);
                      while (ancestor) {
                          for (var i_8 = 0; i_8 < cbs.destroy.length; ++i_8) {
                              cbs.destroy[i_8](ancestor);
                          }
                          ancestor.elm = vnode.elm;
                          if (patchable) {
                              for (var i_9 = 0; i_9 < cbs.create.length; ++i_9) {
                                  cbs.create[i_9](emptyNode, ancestor);
                              }
                              // #6513
                              // invoke insert hooks that may have been merged by create hooks.
                              // e.g. for directives that uses the "inserted" hook.
                              var insert_1 = ancestor.data.hook.insert;
                              if (insert_1.merged) {
                                  // start at index 1 to avoid re-invoking component mounted hook
                                  // clone insert hooks to avoid being mutated during iteration.
                                  // e.g. for customed directives under transition group.
                                  var cloned = insert_1.fns.slice(1);
                                  for (var i_10 = 0; i_10 < cloned.length; i_10++) {
                                      cloned[i_10]();
                                  }
                              }
                          }
                          else {
                              registerRef(ancestor);
                          }
                          ancestor = ancestor.parent;
                      }
                  }
                  // destroy old node
                  if (isDef(parentElm)) {
                      removeVnodes([oldVnode], 0, 0);
                  }
                  else if (isDef(oldVnode.tag)) {
                      invokeDestroyHook(oldVnode);
                  }
              }
          }
          invokeInsertHook(vnode, insertedVnodeQueue, isInitialPatch);
          return vnode.elm;
      };
  }

  var directives$1 = {
      create: updateDirectives,
      update: updateDirectives,
      destroy: function unbindDirectives(vnode) {
          // @ts-expect-error emptyNode is not VNodeWithData
          updateDirectives(vnode, emptyNode);
      }
  };
  function updateDirectives(oldVnode, vnode) {
      if (oldVnode.data.directives || vnode.data.directives) {
          _update(oldVnode, vnode);
      }
  }
  function _update(oldVnode, vnode) {
      var isCreate = oldVnode === emptyNode;
      var isDestroy = vnode === emptyNode;
      var oldDirs = normalizeDirectives(oldVnode.data.directives, oldVnode.context);
      var newDirs = normalizeDirectives(vnode.data.directives, vnode.context);
      var dirsWithInsert = [];
      var dirsWithPostpatch = [];
      var key, oldDir, dir;
      for (key in newDirs) {
          oldDir = oldDirs[key];
          dir = newDirs[key];
          if (!oldDir) {
              // new directive, bind
              callHook(dir, 'bind', vnode, oldVnode);
              if (dir.def && dir.def.inserted) {
                  dirsWithInsert.push(dir);
              }
          }
          else {
              // existing directive, update
              dir.oldValue = oldDir.value;
              dir.oldArg = oldDir.arg;
              callHook(dir, 'update', vnode, oldVnode);
              if (dir.def && dir.def.componentUpdated) {
                  dirsWithPostpatch.push(dir);
              }
          }
      }
      if (dirsWithInsert.length) {
          var callInsert = function () {
              for (var i = 0; i < dirsWithInsert.length; i++) {
                  callHook(dirsWithInsert[i], 'inserted', vnode, oldVnode);
              }
          };
          if (isCreate) {
              mergeVNodeHook(vnode, 'insert', callInsert);
          }
          else {
              callInsert();
          }
      }
      if (dirsWithPostpatch.length) {
          mergeVNodeHook(vnode, 'postpatch', function () {
              for (var i = 0; i < dirsWithPostpatch.length; i++) {
                  callHook(dirsWithPostpatch[i], 'componentUpdated', vnode, oldVnode);
              }
          });
      }
      if (!isCreate) {
          for (key in oldDirs) {
              if (!newDirs[key]) {
                  // no longer present, unbind
                  callHook(oldDirs[key], 'unbind', oldVnode, oldVnode, isDestroy);
              }
          }
      }
  }
  var emptyModifiers = Object.create(null);
  function normalizeDirectives(dirs, vm) {
      var res = Object.create(null);
      if (!dirs) {
          // $flow-disable-line
          return res;
      }
      var i, dir;
      for (i = 0; i < dirs.length; i++) {
          dir = dirs[i];
          if (!dir.modifiers) {
              // $flow-disable-line
              dir.modifiers = emptyModifiers;
          }
          res[getRawDirName(dir)] = dir;
          if (vm._setupState && vm._setupState.__sfc) {
              var setupDef = dir.def || resolveAsset(vm, '_setupState', 'v-' + dir.name);
              if (typeof setupDef === 'function') {
                  dir.def = {
                      bind: setupDef,
                      update: setupDef,
                  };
              }
              else {
                  dir.def = setupDef;
              }
          }
          dir.def = dir.def || resolveAsset(vm.$options, 'directives', dir.name, true);
      }
      // $flow-disable-line
      return res;
  }
  function getRawDirName(dir) {
      return (dir.rawName || "".concat(dir.name, ".").concat(Object.keys(dir.modifiers || {}).join('.')));
  }
  function callHook(dir, hook, vnode, oldVnode, isDestroy) {
      var fn = dir.def && dir.def[hook];
      if (fn) {
          try {
              fn(vnode.elm, dir, vnode, oldVnode, isDestroy);
          }
          catch (e) {
              handleError(e, vnode.context, "directive ".concat(dir.name, " ").concat(hook, " hook"));
          }
      }
  }

  var baseModules = [ref, directives$1];

  function updateAttrs(oldVnode, vnode) {
      var opts = vnode.componentOptions;
      if (isDef(opts) && opts.Ctor.options.inheritAttrs === false) {
          return;
      }
      if (isUndef(oldVnode.data.attrs) && isUndef(vnode.data.attrs)) {
          return;
      }
      var key, cur, old;
      var elm = vnode.elm;
      var oldAttrs = oldVnode.data.attrs || {};
      var attrs = vnode.data.attrs || {};
      // clone observed objects, as the user probably wants to mutate it
      if (isDef(attrs.__ob__) || isTrue(attrs._v_attr_proxy)) {
          attrs = vnode.data.attrs = extend({}, attrs);
      }
      for (key in attrs) {
          cur = attrs[key];
          old = oldAttrs[key];
          if (old !== cur) {
              setAttr(elm, key, cur, vnode.data.pre);
          }
      }
      // #4391: in IE9, setting type can reset value for input[type=radio]
      // #6666: IE/Edge forces progress value down to 1 before setting a max
      /* istanbul ignore if */
      if ((isIE || isEdge) && attrs.value !== oldAttrs.value) {
          setAttr(elm, 'value', attrs.value);
      }
      for (key in oldAttrs) {
          if (isUndef(attrs[key])) {
              if (isXlink(key)) {
                  elm.removeAttributeNS(xlinkNS, getXlinkProp(key));
              }
              else if (!isEnumeratedAttr(key)) {
                  elm.removeAttribute(key);
              }
          }
      }
  }
  function setAttr(el, key, value, isInPre) {
      if (isInPre || el.tagName.indexOf('-') > -1) {
          baseSetAttr(el, key, value);
      }
      else if (isBooleanAttr(key)) {
          // set attribute for blank value
          // e.g. <option disabled>Select one</option>
          if (isFalsyAttrValue(value)) {
              el.removeAttribute(key);
          }
          else {
              // technically allowfullscreen is a boolean attribute for <iframe>,
              // but Flash expects a value of "true" when used on <embed> tag
              value = key === 'allowfullscreen' && el.tagName === 'EMBED' ? 'true' : key;
              el.setAttribute(key, value);
          }
      }
      else if (isEnumeratedAttr(key)) {
          el.setAttribute(key, convertEnumeratedValue(key, value));
      }
      else if (isXlink(key)) {
          if (isFalsyAttrValue(value)) {
              el.removeAttributeNS(xlinkNS, getXlinkProp(key));
          }
          else {
              el.setAttributeNS(xlinkNS, key, value);
          }
      }
      else {
          baseSetAttr(el, key, value);
      }
  }
  function baseSetAttr(el, key, value) {
      if (isFalsyAttrValue(value)) {
          el.removeAttribute(key);
      }
      else {
          // #7138: IE10 & 11 fires input event when setting placeholder on
          // <textarea>... block the first input event and remove the blocker
          // immediately.
          /* istanbul ignore if */
          if (isIE &&
              !isIE9 &&
              el.tagName === 'TEXTAREA' &&
              key === 'placeholder' &&
              value !== '' &&
              !el.__ieph) {
              var blocker_1 = function (e) {
                  e.stopImmediatePropagation();
                  el.removeEventListener('input', blocker_1);
              };
              el.addEventListener('input', blocker_1);
              // $flow-disable-line
              el.__ieph = true; /* IE placeholder patched */
          }
          el.setAttribute(key, value);
      }
  }
  var attrs = {
      create: updateAttrs,
      update: updateAttrs
  };

  function updateClass(oldVnode, vnode) {
      var el = vnode.elm;
      var data = vnode.data;
      var oldData = oldVnode.data;
      if (isUndef(data.staticClass) &&
          isUndef(data.class) &&
          (isUndef(oldData) ||
              (isUndef(oldData.staticClass) && isUndef(oldData.class)))) {
          return;
      }
      var cls = genClassForVnode(vnode);
      // handle transition classes
      var transitionClass = el._transitionClasses;
      if (isDef(transitionClass)) {
          cls = concat(cls, stringifyClass(transitionClass));
      }
      // set the class
      if (cls !== el._prevClass) {
          el.setAttribute('class', cls);
          el._prevClass = cls;
      }
  }
  var klass$1 = {
      create: updateClass,
      update: updateClass
  };

  var validDivisionCharRE = /[\w).+\-_$\]]/;
  function parseFilters(exp) {
      var inSingle = false;
      var inDouble = false;
      var inTemplateString = false;
      var inRegex = false;
      var curly = 0;
      var square = 0;
      var paren = 0;
      var lastFilterIndex = 0;
      var c, prev, i, expression, filters;
      for (i = 0; i < exp.length; i++) {
          prev = c;
          c = exp.charCodeAt(i);
          if (inSingle) {
              if (c === 0x27 && prev !== 0x5c)
                  inSingle = false;
          }
          else if (inDouble) {
              if (c === 0x22 && prev !== 0x5c)
                  inDouble = false;
          }
          else if (inTemplateString) {
              if (c === 0x60 && prev !== 0x5c)
                  inTemplateString = false;
          }
          else if (inRegex) {
              if (c === 0x2f && prev !== 0x5c)
                  inRegex = false;
          }
          else if (c === 0x7c && // pipe
              exp.charCodeAt(i + 1) !== 0x7c &&
              exp.charCodeAt(i - 1) !== 0x7c &&
              !curly &&
              !square &&
              !paren) {
              if (expression === undefined) {
                  // first filter, end of expression
                  lastFilterIndex = i + 1;
                  expression = exp.slice(0, i).trim();
              }
              else {
                  pushFilter();
              }
          }
          else {
              switch (c) {
                  case 0x22:
                      inDouble = true;
                      break; // "
                  case 0x27:
                      inSingle = true;
                      break; // '
                  case 0x60:
                      inTemplateString = true;
                      break; // `
                  case 0x28:
                      paren++;
                      break; // (
                  case 0x29:
                      paren--;
                      break; // )
                  case 0x5b:
                      square++;
                      break; // [
                  case 0x5d:
                      square--;
                      break; // ]
                  case 0x7b:
                      curly++;
                      break; // {
                  case 0x7d:
                      curly--;
                      break; // }
              }
              if (c === 0x2f) {
                  // /
                  var j = i - 1;
                  var p 
                  // find first non-whitespace prev char
                  = void 0;
                  // find first non-whitespace prev char
                  for (; j >= 0; j--) {
                      p = exp.charAt(j);
                      if (p !== ' ')
                          break;
                  }
                  if (!p || !validDivisionCharRE.test(p)) {
                      inRegex = true;
                  }
              }
          }
      }
      if (expression === undefined) {
          expression = exp.slice(0, i).trim();
      }
      else if (lastFilterIndex !== 0) {
          pushFilter();
      }
      function pushFilter() {
          (filters || (filters = [])).push(exp.slice(lastFilterIndex, i).trim());
          lastFilterIndex = i + 1;
      }
      if (filters) {
          for (i = 0; i < filters.length; i++) {
              expression = wrapFilter(expression, filters[i]);
          }
      }
      return expression;
  }
  function wrapFilter(exp, filter) {
      var i = filter.indexOf('(');
      if (i < 0) {
          // _f: resolveFilter
          return "_f(\"".concat(filter, "\")(").concat(exp, ")");
      }
      else {
          var name_1 = filter.slice(0, i);
          var args = filter.slice(i + 1);
          return "_f(\"".concat(name_1, "\")(").concat(exp).concat(args !== ')' ? ',' + args : args);
      }
  }

  /* eslint-disable no-unused-vars */
  function baseWarn(msg, range) {
      console.error("[Vue compiler]: ".concat(msg));
  }
  /* eslint-enable no-unused-vars */
  function pluckModuleFunction(modules, key) {
      return modules ? modules.map(function (m) { return m[key]; }).filter(function (_) { return _; }) : [];
  }
  function addProp(el, name, value, range, dynamic) {
      (el.props || (el.props = [])).push(rangeSetItem({ name: name, value: value, dynamic: dynamic }, range));
      el.plain = false;
  }
  function addAttr(el, name, value, range, dynamic) {
      var attrs = dynamic
          ? el.dynamicAttrs || (el.dynamicAttrs = [])
          : el.attrs || (el.attrs = []);
      attrs.push(rangeSetItem({ name: name, value: value, dynamic: dynamic }, range));
      el.plain = false;
  }
  // add a raw attr (use this in preTransforms)
  function addRawAttr(el, name, value, range) {
      el.attrsMap[name] = value;
      el.attrsList.push(rangeSetItem({ name: name, value: value }, range));
  }
  function addDirective(el, name, rawName, value, arg, isDynamicArg, modifiers, range) {
      (el.directives || (el.directives = [])).push(rangeSetItem({
          name: name,
          rawName: rawName,
          value: value,
          arg: arg,
          isDynamicArg: isDynamicArg,
          modifiers: modifiers
      }, range));
      el.plain = false;
  }
  function prependModifierMarker(symbol, name, dynamic) {
      return dynamic ? "_p(".concat(name, ",\"").concat(symbol, "\")") : symbol + name; // mark the event as captured
  }
  function addHandler(el, name, value, modifiers, important, warn, range, dynamic) {
      modifiers = modifiers || emptyObject;
      // warn prevent and passive modifier
      /* istanbul ignore if */
      if (warn && modifiers.prevent && modifiers.passive) {
          warn("passive and prevent can't be used together. " +
              "Passive handler can't prevent default event.", range);
      }
      // normalize click.right and click.middle since they don't actually fire
      // this is technically browser-specific, but at least for now browsers are
      // the only target envs that have right/middle clicks.
      if (modifiers.right) {
          if (dynamic) {
              name = "(".concat(name, ")==='click'?'contextmenu':(").concat(name, ")");
          }
          else if (name === 'click') {
              name = 'contextmenu';
              delete modifiers.right;
          }
      }
      else if (modifiers.middle) {
          if (dynamic) {
              name = "(".concat(name, ")==='click'?'mouseup':(").concat(name, ")");
          }
          else if (name === 'click') {
              name = 'mouseup';
          }
      }
      // check capture modifier
      if (modifiers.capture) {
          delete modifiers.capture;
          name = prependModifierMarker('!', name, dynamic);
      }
      if (modifiers.once) {
          delete modifiers.once;
          name = prependModifierMarker('~', name, dynamic);
      }
      /* istanbul ignore if */
      if (modifiers.passive) {
          delete modifiers.passive;
          name = prependModifierMarker('&', name, dynamic);
      }
      var events;
      if (modifiers.native) {
          delete modifiers.native;
          events = el.nativeEvents || (el.nativeEvents = {});
      }
      else {
          events = el.events || (el.events = {});
      }
      var newHandler = rangeSetItem({ value: value.trim(), dynamic: dynamic }, range);
      if (modifiers !== emptyObject) {
          newHandler.modifiers = modifiers;
      }
      var handlers = events[name];
      /* istanbul ignore if */
      if (Array.isArray(handlers)) {
          important ? handlers.unshift(newHandler) : handlers.push(newHandler);
      }
      else if (handlers) {
          events[name] = important ? [newHandler, handlers] : [handlers, newHandler];
      }
      else {
          events[name] = newHandler;
      }
      el.plain = false;
  }
  function getRawBindingAttr(el, name) {
      return (el.rawAttrsMap[':' + name] ||
          el.rawAttrsMap['v-bind:' + name] ||
          el.rawAttrsMap[name]);
  }
  function getBindingAttr(el, name, getStatic) {
      var dynamicValue = getAndRemoveAttr(el, ':' + name) || getAndRemoveAttr(el, 'v-bind:' + name);
      if (dynamicValue != null) {
          return parseFilters(dynamicValue);
      }
      else if (getStatic !== false) {
          var staticValue = getAndRemoveAttr(el, name);
          if (staticValue != null) {
              return JSON.stringify(staticValue);
          }
      }
  }
  // note: this only removes the attr from the Array (attrsList) so that it
  // doesn't get processed by processAttrs.
  // By default it does NOT remove it from the map (attrsMap) because the map is
  // needed during codegen.
  function getAndRemoveAttr(el, name, removeFromMap) {
      var val;
      if ((val = el.attrsMap[name]) != null) {
          var list = el.attrsList;
          for (var i = 0, l = list.length; i < l; i++) {
              if (list[i].name === name) {
                  list.splice(i, 1);
                  break;
              }
          }
      }
      if (removeFromMap) {
          delete el.attrsMap[name];
      }
      return val;
  }
  function getAndRemoveAttrByRegex(el, name) {
      var list = el.attrsList;
      for (var i = 0, l = list.length; i < l; i++) {
          var attr = list[i];
          if (name.test(attr.name)) {
              list.splice(i, 1);
              return attr;
          }
      }
  }
  function rangeSetItem(item, range) {
      if (range) {
          if (range.start != null) {
              item.start = range.start;
          }
          if (range.end != null) {
              item.end = range.end;
          }
      }
      return item;
  }

  /**
   * Cross-platform code generation for component v-model
   */
  function genComponentModel(el, value, modifiers) {
      var _a = modifiers || {}, number = _a.number, trim = _a.trim;
      var baseValueExpression = '$$v';
      var valueExpression = baseValueExpression;
      if (trim) {
          valueExpression =
              "(typeof ".concat(baseValueExpression, " === 'string'") +
                  "? ".concat(baseValueExpression, ".trim()") +
                  ": ".concat(baseValueExpression, ")");
      }
      if (number) {
          valueExpression = "_n(".concat(valueExpression, ")");
      }
      var assignment = genAssignmentCode(value, valueExpression);
      el.model = {
          value: "(".concat(value, ")"),
          expression: JSON.stringify(value),
          callback: "function (".concat(baseValueExpression, ") {").concat(assignment, "}")
      };
  }
  /**
   * Cross-platform codegen helper for generating v-model value assignment code.
   */
  function genAssignmentCode(value, assignment) {
      var res = parseModel(value);
      if (res.key === null) {
          return "".concat(value, "=").concat(assignment);
      }
      else {
          return "$set(".concat(res.exp, ", ").concat(res.key, ", ").concat(assignment, ")");
      }
  }
  /**
   * Parse a v-model expression into a base path and a final key segment.
   * Handles both dot-path and possible square brackets.
   *
   * Possible cases:
   *
   * - test
   * - test[key]
   * - test[test1[key]]
   * - test["a"][key]
   * - xxx.test[a[a].test1[key]]
   * - test.xxx.a["asa"][test1[key]]
   *
   */
  var len, str, chr, index, expressionPos, expressionEndPos;
  function parseModel(val) {
      // Fix https://github.com/vuejs/vue/pull/7730
      // allow v-model="obj.val " (trailing whitespace)
      val = val.trim();
      len = val.length;
      if (val.indexOf('[') < 0 || val.lastIndexOf(']') < len - 1) {
          index = val.lastIndexOf('.');
          if (index > -1) {
              return {
                  exp: val.slice(0, index),
                  key: '"' + val.slice(index + 1) + '"'
              };
          }
          else {
              return {
                  exp: val,
                  key: null
              };
          }
      }
      str = val;
      index = expressionPos = expressionEndPos = 0;
      while (!eof()) {
          chr = next();
          /* istanbul ignore if */
          if (isStringStart(chr)) {
              parseString(chr);
          }
          else if (chr === 0x5b) {
              parseBracket(chr);
          }
      }
      return {
          exp: val.slice(0, expressionPos),
          key: val.slice(expressionPos + 1, expressionEndPos)
      };
  }
  function next() {
      return str.charCodeAt(++index);
  }
  function eof() {
      return index >= len;
  }
  function isStringStart(chr) {
      return chr === 0x22 || chr === 0x27;
  }
  function parseBracket(chr) {
      var inBracket = 1;
      expressionPos = index;
      while (!eof()) {
          chr = next();
          if (isStringStart(chr)) {
              parseString(chr);
              continue;
          }
          if (chr === 0x5b)
              inBracket++;
          if (chr === 0x5d)
              inBracket--;
          if (inBracket === 0) {
              expressionEndPos = index;
              break;
          }
      }
  }
  function parseString(chr) {
      var stringQuote = chr;
      while (!eof()) {
          chr = next();
          if (chr === stringQuote) {
              break;
          }
      }
  }

  var warn$1;
  // in some cases, the event used has to be determined at runtime
  // so we used some reserved tokens during compile.
  var RANGE_TOKEN = '__r';
  var CHECKBOX_RADIO_TOKEN = '__c';
  function model$1(el, dir, _warn) {
      warn$1 = _warn;
      var value = dir.value;
      var modifiers = dir.modifiers;
      var tag = el.tag;
      var type = el.attrsMap.type;
      {
          // inputs with type="file" are read only and setting the input's
          // value will throw an error.
          if (tag === 'input' && type === 'file') {
              warn$1("<".concat(el.tag, " v-model=\"").concat(value, "\" type=\"file\">:\n") +
                  "File inputs are read only. Use a v-on:change listener instead.", el.rawAttrsMap['v-model']);
          }
      }
      if (el.component) {
          genComponentModel(el, value, modifiers);
          // component v-model doesn't need extra runtime
          return false;
      }
      else if (tag === 'select') {
          genSelect(el, value, modifiers);
      }
      else if (tag === 'input' && type === 'checkbox') {
          genCheckboxModel(el, value, modifiers);
      }
      else if (tag === 'input' && type === 'radio') {
          genRadioModel(el, value, modifiers);
      }
      else if (tag === 'input' || tag === 'textarea') {
          genDefaultModel(el, value, modifiers);
      }
      else if (!config.isReservedTag(tag)) {
          genComponentModel(el, value, modifiers);
          // component v-model doesn't need extra runtime
          return false;
      }
      else {
          warn$1("<".concat(el.tag, " v-model=\"").concat(value, "\">: ") +
              "v-model is not supported on this element type. " +
              "If you are working with contenteditable, it's recommended to " +
              'wrap a library dedicated for that purpose inside a custom component.', el.rawAttrsMap['v-model']);
      }
      // ensure runtime directive metadata
      return true;
  }
  function genCheckboxModel(el, value, modifiers) {
      var number = modifiers && modifiers.number;
      var valueBinding = getBindingAttr(el, 'value') || 'null';
      var trueValueBinding = getBindingAttr(el, 'true-value') || 'true';
      var falseValueBinding = getBindingAttr(el, 'false-value') || 'false';
      addProp(el, 'checked', "Array.isArray(".concat(value, ")") +
          "?_i(".concat(value, ",").concat(valueBinding, ")>-1") +
          (trueValueBinding === 'true'
              ? ":(".concat(value, ")")
              : ":_q(".concat(value, ",").concat(trueValueBinding, ")")));
      addHandler(el, 'change', "var $$a=".concat(value, ",") +
          '$$el=$event.target,' +
          "$$c=$$el.checked?(".concat(trueValueBinding, "):(").concat(falseValueBinding, ");") +
          'if(Array.isArray($$a)){' +
          "var $$v=".concat(number ? '_n(' + valueBinding + ')' : valueBinding, ",") +
          '$$i=_i($$a,$$v);' +
          "if($$el.checked){$$i<0&&(".concat(genAssignmentCode(value, '$$a.concat([$$v])'), ")}") +
          "else{$$i>-1&&(".concat(genAssignmentCode(value, '$$a.slice(0,$$i).concat($$a.slice($$i+1))'), ")}") +
          "}else{".concat(genAssignmentCode(value, '$$c'), "}"), null, true);
  }
  function genRadioModel(el, value, modifiers) {
      var number = modifiers && modifiers.number;
      var valueBinding = getBindingAttr(el, 'value') || 'null';
      valueBinding = number ? "_n(".concat(valueBinding, ")") : valueBinding;
      addProp(el, 'checked', "_q(".concat(value, ",").concat(valueBinding, ")"));
      addHandler(el, 'change', genAssignmentCode(value, valueBinding), null, true);
  }
  function genSelect(el, value, modifiers) {
      var number = modifiers && modifiers.number;
      var selectedVal = "Array.prototype.filter" +
          ".call($event.target.options,function(o){return o.selected})" +
          ".map(function(o){var val = \"_value\" in o ? o._value : o.value;" +
          "return ".concat(number ? '_n(val)' : 'val', "})");
      var assignment = '$event.target.multiple ? $$selectedVal : $$selectedVal[0]';
      var code = "var $$selectedVal = ".concat(selectedVal, ";");
      code = "".concat(code, " ").concat(genAssignmentCode(value, assignment));
      addHandler(el, 'change', code, null, true);
  }
  function genDefaultModel(el, value, modifiers) {
      var type = el.attrsMap.type;
      // warn if v-bind:value conflicts with v-model
      // except for inputs with v-bind:type
      {
          var value_1 = el.attrsMap['v-bind:value'] || el.attrsMap[':value'];
          var typeBinding = el.attrsMap['v-bind:type'] || el.attrsMap[':type'];
          if (value_1 && !typeBinding) {
              var binding = el.attrsMap['v-bind:value'] ? 'v-bind:value' : ':value';
              warn$1("".concat(binding, "=\"").concat(value_1, "\" conflicts with v-model on the same element ") +
                  'because the latter already expands to a value binding internally', el.rawAttrsMap[binding]);
          }
      }
      var _a = modifiers || {}, lazy = _a.lazy, number = _a.number, trim = _a.trim;
      var needCompositionGuard = !lazy && type !== 'range';
      var event = lazy ? 'change' : type === 'range' ? RANGE_TOKEN : 'input';
      var valueExpression = '$event.target.value';
      if (trim) {
          valueExpression = "$event.target.value.trim()";
      }
      if (number) {
          valueExpression = "_n(".concat(valueExpression, ")");
      }
      var code = genAssignmentCode(value, valueExpression);
      if (needCompositionGuard) {
          code = "if($event.target.composing)return;".concat(code);
      }
      addProp(el, 'value', "(".concat(value, ")"));
      addHandler(el, event, code, null, true);
      if (trim || number) {
          addHandler(el, 'blur', '$forceUpdate()');
      }
  }

  // normalize v-model event tokens that can only be determined at runtime.
  // it's important to place the event as the first in the array because
  // the whole point is ensuring the v-model callback gets called before
  // user-attached handlers.
  function normalizeEvents(on) {
      /* istanbul ignore if */
      if (isDef(on[RANGE_TOKEN])) {
          // IE input[type=range] only supports `change` event
          var event_1 = isIE ? 'change' : 'input';
          on[event_1] = [].concat(on[RANGE_TOKEN], on[event_1] || []);
          delete on[RANGE_TOKEN];
      }
      // This was originally intended to fix #4521 but no longer necessary
      // after 2.5. Keeping it for backwards compat with generated code from < 2.4
      /* istanbul ignore if */
      if (isDef(on[CHECKBOX_RADIO_TOKEN])) {
          on.change = [].concat(on[CHECKBOX_RADIO_TOKEN], on.change || []);
          delete on[CHECKBOX_RADIO_TOKEN];
      }
  }
  var target;
  function createOnceHandler(event, handler, capture) {
      var _target = target; // save current target element in closure
      return function onceHandler() {
          var res = handler.apply(null, arguments);
          if (res !== null) {
              remove(event, onceHandler, capture, _target);
          }
      };
  }
  // #9446: Firefox <= 53 (in particular, ESR 52) has incorrect Event.timeStamp
  // implementation and does not fire microtasks in between event propagation, so
  // safe to exclude.
  var useMicrotaskFix = isUsingMicroTask && !(isFF && Number(isFF[1]) <= 53);
  function add(name, handler, capture, passive) {
      // async edge case #6566: inner click event triggers patch, event handler
      // attached to outer element during patch, and triggered again. This
      // happens because browsers fire microtask ticks between event propagation.
      // the solution is simple: we save the timestamp when a handler is attached,
      // and the handler would only fire if the event passed to it was fired
      // AFTER it was attached.
      if (useMicrotaskFix) {
          var attachedTimestamp_1 = currentFlushTimestamp;
          var original_1 = handler;
          //@ts-expect-error
          handler = original_1._wrapper = function (e) {
              if (
              // no bubbling, should always fire.
              // this is just a safety net in case event.timeStamp is unreliable in
              // certain weird environments...
              e.target === e.currentTarget ||
                  // event is fired after handler attachment
                  e.timeStamp >= attachedTimestamp_1 ||
                  // bail for environments that have buggy event.timeStamp implementations
                  // #9462 iOS 9 bug: event.timeStamp is 0 after history.pushState
                  // #9681 QtWebEngine event.timeStamp is negative value
                  e.timeStamp <= 0 ||
                  // #9448 bail if event is fired in another document in a multi-page
                  // electron/nw.js app, since event.timeStamp will be using a different
                  // starting reference
                  e.target.ownerDocument !== document) {
                  return original_1.apply(this, arguments);
              }
          };
      }
      target.addEventListener(name, handler, supportsPassive ? { capture: capture, passive: passive } : capture);
  }
  function remove(name, handler, capture, _target) {
      (_target || target).removeEventListener(name, 
      //@ts-expect-error
      handler._wrapper || handler, capture);
  }
  function updateDOMListeners(oldVnode, vnode) {
      if (isUndef(oldVnode.data.on) && isUndef(vnode.data.on)) {
          return;
      }
      var on = vnode.data.on || {};
      var oldOn = oldVnode.data.on || {};
      // vnode is empty when removing all listeners,
      // and use old vnode dom element
      target = vnode.elm || oldVnode.elm;
      normalizeEvents(on);
      updateListeners(on, oldOn, add, remove, createOnceHandler, vnode.context);
      target = undefined;
  }
  var events = {
      create: updateDOMListeners,
      update: updateDOMListeners,
      // @ts-expect-error emptyNode has actually data
      destroy: function (vnode) { return updateDOMListeners(vnode, emptyNode); }
  };

  var svgContainer;
  function updateDOMProps(oldVnode, vnode) {
      if (isUndef(oldVnode.data.domProps) && isUndef(vnode.data.domProps)) {
          return;
      }
      var key, cur;
      var elm = vnode.elm;
      var oldProps = oldVnode.data.domProps || {};
      var props = vnode.data.domProps || {};
      // clone observed objects, as the user probably wants to mutate it
      if (isDef(props.__ob__) || isTrue(props._v_attr_proxy)) {
          props = vnode.data.domProps = extend({}, props);
      }
      for (key in oldProps) {
          if (!(key in props)) {
              elm[key] = '';
          }
      }
      for (key in props) {
          cur = props[key];
          // ignore children if the node has textContent or innerHTML,
          // as these will throw away existing DOM nodes and cause removal errors
          // on subsequent patches (#3360)
          if (key === 'textContent' || key === 'innerHTML') {
              if (vnode.children)
                  vnode.children.length = 0;
              if (cur === oldProps[key])
                  continue;
              // #6601 work around Chrome version <= 55 bug where single textNode
              // replaced by innerHTML/textContent retains its parentNode property
              if (elm.childNodes.length === 1) {
                  elm.removeChild(elm.childNodes[0]);
              }
          }
          if (key === 'value' && elm.tagName !== 'PROGRESS') {
              // store value as _value as well since
              // non-string values will be stringified
              elm._value = cur;
              // avoid resetting cursor position when value is the same
              var strCur = isUndef(cur) ? '' : String(cur);
              if (shouldUpdateValue(elm, strCur)) {
                  elm.value = strCur;
              }
          }
          else if (key === 'innerHTML' &&
              isSVG(elm.tagName) &&
              isUndef(elm.innerHTML)) {
              // IE doesn't support innerHTML for SVG elements
              svgContainer = svgContainer || document.createElement('div');
              svgContainer.innerHTML = "<svg>".concat(cur, "</svg>");
              var svg = svgContainer.firstChild;
              while (elm.firstChild) {
                  elm.removeChild(elm.firstChild);
              }
              while (svg.firstChild) {
                  elm.appendChild(svg.firstChild);
              }
          }
          else if (
          // skip the update if old and new VDOM state is the same.
          // `value` is handled separately because the DOM value may be temporarily
          // out of sync with VDOM state due to focus, composition and modifiers.
          // This  #4521 by skipping the unnecessary `checked` update.
          cur !== oldProps[key]) {
              // some property updates can throw
              // e.g. `value` on <progress> w/ non-finite value
              try {
                  elm[key] = cur;
              }
              catch (e) { }
          }
      }
  }
  function shouldUpdateValue(elm, checkVal) {
      return (
      //@ts-expect-error
      !elm.composing &&
          (elm.tagName === 'OPTION' ||
              isNotInFocusAndDirty(elm, checkVal) ||
              isDirtyWithModifiers(elm, checkVal)));
  }
  function isNotInFocusAndDirty(elm, checkVal) {
      // return true when textbox (.number and .trim) loses focus and its value is
      // not equal to the updated value
      var notInFocus = true;
      // #6157
      // work around IE bug when accessing document.activeElement in an iframe
      try {
          notInFocus = document.activeElement !== elm;
      }
      catch (e) { }
      return notInFocus && elm.value !== checkVal;
  }
  function isDirtyWithModifiers(elm, newVal) {
      var value = elm.value;
      var modifiers = elm._vModifiers; // injected by v-model runtime
      if (isDef(modifiers)) {
          if (modifiers.number) {
              return toNumber(value) !== toNumber(newVal);
          }
          if (modifiers.trim) {
              return value.trim() !== newVal.trim();
          }
      }
      return value !== newVal;
  }
  var domProps = {
      create: updateDOMProps,
      update: updateDOMProps
  };

  var parseStyleText = cached(function (cssText) {
      var res = {};
      var listDelimiter = /;(?![^(]*\))/g;
      var propertyDelimiter = /:(.+)/;
      cssText.split(listDelimiter).forEach(function (item) {
          if (item) {
              var tmp = item.split(propertyDelimiter);
              tmp.length > 1 && (res[tmp[0].trim()] = tmp[1].trim());
          }
      });
      return res;
  });
  // merge static and dynamic style data on the same vnode
  function normalizeStyleData(data) {
      var style = normalizeStyleBinding(data.style);
      // static style is pre-processed into an object during compilation
      // and is always a fresh object, so it's safe to merge into it
      return data.staticStyle ? extend(data.staticStyle, style) : style;
  }
  // normalize possible array / string values into Object
  function normalizeStyleBinding(bindingStyle) {
      if (Array.isArray(bindingStyle)) {
          return toObject(bindingStyle);
      }
      if (typeof bindingStyle === 'string') {
          return parseStyleText(bindingStyle);
      }
      return bindingStyle;
  }
  /**
   * parent component style should be after child's
   * so that parent component's style could override it
   */
  function getStyle(vnode, checkChild) {
      var res = {};
      var styleData;
      if (checkChild) {
          var childNode = vnode;
          while (childNode.componentInstance) {
              childNode = childNode.componentInstance._vnode;
              if (childNode &&
                  childNode.data &&
                  (styleData = normalizeStyleData(childNode.data))) {
                  extend(res, styleData);
              }
          }
      }
      if ((styleData = normalizeStyleData(vnode.data))) {
          extend(res, styleData);
      }
      var parentNode = vnode;
      // @ts-expect-error parentNode.parent not VNodeWithData
      while ((parentNode = parentNode.parent)) {
          if (parentNode.data && (styleData = normalizeStyleData(parentNode.data))) {
              extend(res, styleData);
          }
      }
      return res;
  }

  var cssVarRE = /^--/;
  var importantRE = /\s*!important$/;
  var setProp = function (el, name, val) {
      /* istanbul ignore if */
      if (cssVarRE.test(name)) {
          el.style.setProperty(name, val);
      }
      else if (importantRE.test(val)) {
          el.style.setProperty(hyphenate(name), val.replace(importantRE, ''), 'important');
      }
      else {
          var normalizedName = normalize(name);
          if (Array.isArray(val)) {
              // Support values array created by autoprefixer, e.g.
              // {display: ["-webkit-box", "-ms-flexbox", "flex"]}
              // Set them one by one, and the browser will only set those it can recognize
              for (var i = 0, len = val.length; i < len; i++) {
                  el.style[normalizedName] = val[i];
              }
          }
          else {
              el.style[normalizedName] = val;
          }
      }
  };
  var vendorNames = ['Webkit', 'Moz', 'ms'];
  var emptyStyle;
  var normalize = cached(function (prop) {
      emptyStyle = emptyStyle || document.createElement('div').style;
      prop = camelize(prop);
      if (prop !== 'filter' && prop in emptyStyle) {
          return prop;
      }
      var capName = prop.charAt(0).toUpperCase() + prop.slice(1);
      for (var i = 0; i < vendorNames.length; i++) {
          var name_1 = vendorNames[i] + capName;
          if (name_1 in emptyStyle) {
              return name_1;
          }
      }
  });
  function updateStyle(oldVnode, vnode) {
      var data = vnode.data;
      var oldData = oldVnode.data;
      if (isUndef(data.staticStyle) &&
          isUndef(data.style) &&
          isUndef(oldData.staticStyle) &&
          isUndef(oldData.style)) {
          return;
      }
      var cur, name;
      var el = vnode.elm;
      var oldStaticStyle = oldData.staticStyle;
      var oldStyleBinding = oldData.normalizedStyle || oldData.style || {};
      // if static style exists, stylebinding already merged into it when doing normalizeStyleData
      var oldStyle = oldStaticStyle || oldStyleBinding;
      var style = normalizeStyleBinding(vnode.data.style) || {};
      // store normalized style under a different key for next diff
      // make sure to clone it if it's reactive, since the user likely wants
      // to mutate it.
      vnode.data.normalizedStyle = isDef(style.__ob__) ? extend({}, style) : style;
      var newStyle = getStyle(vnode, true);
      for (name in oldStyle) {
          if (isUndef(newStyle[name])) {
              setProp(el, name, '');
          }
      }
      for (name in newStyle) {
          cur = newStyle[name];
          // ie9 setting to null has no effect, must use empty string
          setProp(el, name, cur == null ? '' : cur);
      }
  }
  var style$1 = {
      create: updateStyle,
      update: updateStyle
  };

  var whitespaceRE$1 = /\s+/;
  /**
   * Add class with compatibility for SVG since classList is not supported on
   * SVG elements in IE
   */
  function addClass(el, cls) {
      /* istanbul ignore if */
      if (!cls || !(cls = cls.trim())) {
          return;
      }
      /* istanbul ignore else */
      if (el.classList) {
          if (cls.indexOf(' ') > -1) {
              cls.split(whitespaceRE$1).forEach(function (c) { return el.classList.add(c); });
          }
          else {
              el.classList.add(cls);
          }
      }
      else {
          var cur = " ".concat(el.getAttribute('class') || '', " ");
          if (cur.indexOf(' ' + cls + ' ') < 0) {
              el.setAttribute('class', (cur + cls).trim());
          }
      }
  }
  /**
   * Remove class with compatibility for SVG since classList is not supported on
   * SVG elements in IE
   */
  function removeClass(el, cls) {
      /* istanbul ignore if */
      if (!cls || !(cls = cls.trim())) {
          return;
      }
      /* istanbul ignore else */
      if (el.classList) {
          if (cls.indexOf(' ') > -1) {
              cls.split(whitespaceRE$1).forEach(function (c) { return el.classList.remove(c); });
          }
          else {
              el.classList.remove(cls);
          }
          if (!el.classList.length) {
              el.removeAttribute('class');
          }
      }
      else {
          var cur = " ".concat(el.getAttribute('class') || '', " ");
          var tar = ' ' + cls + ' ';
          while (cur.indexOf(tar) >= 0) {
              cur = cur.replace(tar, ' ');
          }
          cur = cur.trim();
          if (cur) {
              el.setAttribute('class', cur);
          }
          else {
              el.removeAttribute('class');
          }
      }
  }

  function resolveTransition(def) {
      if (!def) {
          return;
      }
      /* istanbul ignore else */
      if (typeof def === 'object') {
          var res = {};
          if (def.css !== false) {
              extend(res, autoCssTransition(def.name || 'v'));
          }
          extend(res, def);
          return res;
      }
      else if (typeof def === 'string') {
          return autoCssTransition(def);
      }
  }
  var autoCssTransition = cached(function (name) {
      return {
          enterClass: "".concat(name, "-enter"),
          enterToClass: "".concat(name, "-enter-to"),
          enterActiveClass: "".concat(name, "-enter-active"),
          leaveClass: "".concat(name, "-leave"),
          leaveToClass: "".concat(name, "-leave-to"),
          leaveActiveClass: "".concat(name, "-leave-active")
      };
  });
  var hasTransition = inBrowser && !isIE9;
  var TRANSITION = 'transition';
  var ANIMATION = 'animation';
  // Transition property/event sniffing
  var transitionProp = 'transition';
  var transitionEndEvent = 'transitionend';
  var animationProp = 'animation';
  var animationEndEvent = 'animationend';
  if (hasTransition) {
      /* istanbul ignore if */
      if (window.ontransitionend === undefined &&
          window.onwebkittransitionend !== undefined) {
          transitionProp = 'WebkitTransition';
          transitionEndEvent = 'webkitTransitionEnd';
      }
      if (window.onanimationend === undefined &&
          window.onwebkitanimationend !== undefined) {
          animationProp = 'WebkitAnimation';
          animationEndEvent = 'webkitAnimationEnd';
      }
  }
  // binding to window is necessary to make hot reload work in IE in strict mode
  var raf = inBrowser
      ? window.requestAnimationFrame
          ? window.requestAnimationFrame.bind(window)
          : setTimeout
      : /* istanbul ignore next */ function (/* istanbul ignore next */ fn) { return fn(); };
  function nextFrame(fn) {
      raf(function () {
          // @ts-expect-error
          raf(fn);
      });
  }
  function addTransitionClass(el, cls) {
      var transitionClasses = el._transitionClasses || (el._transitionClasses = []);
      if (transitionClasses.indexOf(cls) < 0) {
          transitionClasses.push(cls);
          addClass(el, cls);
      }
  }
  function removeTransitionClass(el, cls) {
      if (el._transitionClasses) {
          remove$2(el._transitionClasses, cls);
      }
      removeClass(el, cls);
  }
  function whenTransitionEnds(el, expectedType, cb) {
      var _a = getTransitionInfo(el, expectedType), type = _a.type, timeout = _a.timeout, propCount = _a.propCount;
      if (!type)
          return cb();
      var event = type === TRANSITION ? transitionEndEvent : animationEndEvent;
      var ended = 0;
      var end = function () {
          el.removeEventListener(event, onEnd);
          cb();
      };
      var onEnd = function (e) {
          if (e.target === el) {
              if (++ended >= propCount) {
                  end();
              }
          }
      };
      setTimeout(function () {
          if (ended < propCount) {
              end();
          }
      }, timeout + 1);
      el.addEventListener(event, onEnd);
  }
  var transformRE = /\b(transform|all)(,|$)/;
  function getTransitionInfo(el, expectedType) {
      var styles = window.getComputedStyle(el);
      // JSDOM may return undefined for transition properties
      var transitionDelays = (styles[transitionProp + 'Delay'] || '').split(', ');
      var transitionDurations = (styles[transitionProp + 'Duration'] || '').split(', ');
      var transitionTimeout = getTimeout(transitionDelays, transitionDurations);
      var animationDelays = (styles[animationProp + 'Delay'] || '').split(', ');
      var animationDurations = (styles[animationProp + 'Duration'] || '').split(', ');
      var animationTimeout = getTimeout(animationDelays, animationDurations);
      var type;
      var timeout = 0;
      var propCount = 0;
      /* istanbul ignore if */
      if (expectedType === TRANSITION) {
          if (transitionTimeout > 0) {
              type = TRANSITION;
              timeout = transitionTimeout;
              propCount = transitionDurations.length;
          }
      }
      else if (expectedType === ANIMATION) {
          if (animationTimeout > 0) {
              type = ANIMATION;
              timeout = animationTimeout;
              propCount = animationDurations.length;
          }
      }
      else {
          timeout = Math.max(transitionTimeout, animationTimeout);
          type =
              timeout > 0
                  ? transitionTimeout > animationTimeout
                      ? TRANSITION
                      : ANIMATION
                  : null;
          propCount = type
              ? type === TRANSITION
                  ? transitionDurations.length
                  : animationDurations.length
              : 0;
      }
      var hasTransform = type === TRANSITION && transformRE.test(styles[transitionProp + 'Property']);
      return {
          type: type,
          timeout: timeout,
          propCount: propCount,
          hasTransform: hasTransform
      };
  }
  function getTimeout(delays, durations) {
      /* istanbul ignore next */
      while (delays.length < durations.length) {
          delays = delays.concat(delays);
      }
      return Math.max.apply(null, durations.map(function (d, i) {
          return toMs(d) + toMs(delays[i]);
      }));
  }
  // Old versions of Chromium (below 61.0.3163.100) formats floating pointer numbers
  // in a locale-dependent way, using a comma instead of a dot.
  // If comma is not replaced with a dot, the input will be rounded down (i.e. acting
  // as a floor function) causing unexpected behaviors
  function toMs(s) {
      return Number(s.slice(0, -1).replace(',', '.')) * 1000;
  }

  function enter(vnode, toggleDisplay) {
      var el = vnode.elm;
      // call leave callback now
      if (isDef(el._leaveCb)) {
          el._leaveCb.cancelled = true;
          el._leaveCb();
      }
      var data = resolveTransition(vnode.data.transition);
      if (isUndef(data)) {
          return;
      }
      /* istanbul ignore if */
      if (isDef(el._enterCb) || el.nodeType !== 1) {
          return;
      }
      var css = data.css, type = data.type, enterClass = data.enterClass, enterToClass = data.enterToClass, enterActiveClass = data.enterActiveClass, appearClass = data.appearClass, appearToClass = data.appearToClass, appearActiveClass = data.appearActiveClass, beforeEnter = data.beforeEnter, enter = data.enter, afterEnter = data.afterEnter, enterCancelled = data.enterCancelled, beforeAppear = data.beforeAppear, appear = data.appear, afterAppear = data.afterAppear, appearCancelled = data.appearCancelled, duration = data.duration;
      // activeInstance will always be the <transition> component managing this
      // transition. One edge case to check is when the <transition> is placed
      // as the root node of a child component. In that case we need to check
      // <transition>'s parent for appear check.
      var context = activeInstance;
      var transitionNode = activeInstance.$vnode;
      while (transitionNode && transitionNode.parent) {
          context = transitionNode.context;
          transitionNode = transitionNode.parent;
      }
      var isAppear = !context._isMounted || !vnode.isRootInsert;
      if (isAppear && !appear && appear !== '') {
          return;
      }
      var startClass = isAppear && appearClass ? appearClass : enterClass;
      var activeClass = isAppear && appearActiveClass ? appearActiveClass : enterActiveClass;
      var toClass = isAppear && appearToClass ? appearToClass : enterToClass;
      var beforeEnterHook = isAppear ? beforeAppear || beforeEnter : beforeEnter;
      var enterHook = isAppear ? (isFunction(appear) ? appear : enter) : enter;
      var afterEnterHook = isAppear ? afterAppear || afterEnter : afterEnter;
      var enterCancelledHook = isAppear
          ? appearCancelled || enterCancelled
          : enterCancelled;
      var explicitEnterDuration = toNumber(isObject(duration) ? duration.enter : duration);
      if (explicitEnterDuration != null) {
          checkDuration(explicitEnterDuration, 'enter', vnode);
      }
      var expectsCSS = css !== false && !isIE9;
      var userWantsControl = getHookArgumentsLength(enterHook);
      var cb = (el._enterCb = once(function () {
          if (expectsCSS) {
              removeTransitionClass(el, toClass);
              removeTransitionClass(el, activeClass);
          }
          // @ts-expect-error
          if (cb.cancelled) {
              if (expectsCSS) {
                  removeTransitionClass(el, startClass);
              }
              enterCancelledHook && enterCancelledHook(el);
          }
          else {
              afterEnterHook && afterEnterHook(el);
          }
          el._enterCb = null;
      }));
      if (!vnode.data.show) {
          // remove pending leave element on enter by injecting an insert hook
          mergeVNodeHook(vnode, 'insert', function () {
              var parent = el.parentNode;
              var pendingNode = parent && parent._pending && parent._pending[vnode.key];
              if (pendingNode &&
                  pendingNode.tag === vnode.tag &&
                  pendingNode.elm._leaveCb) {
                  pendingNode.elm._leaveCb();
              }
              enterHook && enterHook(el, cb);
          });
      }
      // start enter transition
      beforeEnterHook && beforeEnterHook(el);
      if (expectsCSS) {
          addTransitionClass(el, startClass);
          addTransitionClass(el, activeClass);
          nextFrame(function () {
              removeTransitionClass(el, startClass);
              // @ts-expect-error
              if (!cb.cancelled) {
                  addTransitionClass(el, toClass);
                  if (!userWantsControl) {
                      if (isValidDuration(explicitEnterDuration)) {
                          setTimeout(cb, explicitEnterDuration);
                      }
                      else {
                          whenTransitionEnds(el, type, cb);
                      }
                  }
              }
          });
      }
      if (vnode.data.show) {
          toggleDisplay && toggleDisplay();
          enterHook && enterHook(el, cb);
      }
      if (!expectsCSS && !userWantsControl) {
          cb();
      }
  }
  function leave(vnode, rm) {
      var el = vnode.elm;
      // call enter callback now
      if (isDef(el._enterCb)) {
          el._enterCb.cancelled = true;
          el._enterCb();
      }
      var data = resolveTransition(vnode.data.transition);
      if (isUndef(data) || el.nodeType !== 1) {
          return rm();
      }
      /* istanbul ignore if */
      if (isDef(el._leaveCb)) {
          return;
      }
      var css = data.css, type = data.type, leaveClass = data.leaveClass, leaveToClass = data.leaveToClass, leaveActiveClass = data.leaveActiveClass, beforeLeave = data.beforeLeave, leave = data.leave, afterLeave = data.afterLeave, leaveCancelled = data.leaveCancelled, delayLeave = data.delayLeave, duration = data.duration;
      var expectsCSS = css !== false && !isIE9;
      var userWantsControl = getHookArgumentsLength(leave);
      var explicitLeaveDuration = toNumber(isObject(duration) ? duration.leave : duration);
      if (isDef(explicitLeaveDuration)) {
          checkDuration(explicitLeaveDuration, 'leave', vnode);
      }
      var cb = (el._leaveCb = once(function () {
          if (el.parentNode && el.parentNode._pending) {
              el.parentNode._pending[vnode.key] = null;
          }
          if (expectsCSS) {
              removeTransitionClass(el, leaveToClass);
              removeTransitionClass(el, leaveActiveClass);
          }
          // @ts-expect-error
          if (cb.cancelled) {
              if (expectsCSS) {
                  removeTransitionClass(el, leaveClass);
              }
              leaveCancelled && leaveCancelled(el);
          }
          else {
              rm();
              afterLeave && afterLeave(el);
          }
          el._leaveCb = null;
      }));
      if (delayLeave) {
          delayLeave(performLeave);
      }
      else {
          performLeave();
      }
      function performLeave() {
          // the delayed leave may have already been cancelled
          // @ts-expect-error
          if (cb.cancelled) {
              return;
          }
          // record leaving element
          if (!vnode.data.show && el.parentNode) {
              (el.parentNode._pending || (el.parentNode._pending = {}))[vnode.key] =
                  vnode;
          }
          beforeLeave && beforeLeave(el);
          if (expectsCSS) {
              addTransitionClass(el, leaveClass);
              addTransitionClass(el, leaveActiveClass);
              nextFrame(function () {
                  removeTransitionClass(el, leaveClass);
                  // @ts-expect-error
                  if (!cb.cancelled) {
                      addTransitionClass(el, leaveToClass);
                      if (!userWantsControl) {
                          if (isValidDuration(explicitLeaveDuration)) {
                              setTimeout(cb, explicitLeaveDuration);
                          }
                          else {
                              whenTransitionEnds(el, type, cb);
                          }
                      }
                  }
              });
          }
          leave && leave(el, cb);
          if (!expectsCSS && !userWantsControl) {
              cb();
          }
      }
  }
  // only used in dev mode
  function checkDuration(val, name, vnode) {
      if (typeof val !== 'number') {
          warn$2("<transition> explicit ".concat(name, " duration is not a valid number - ") +
              "got ".concat(JSON.stringify(val), "."), vnode.context);
      }
      else if (isNaN(val)) {
          warn$2("<transition> explicit ".concat(name, " duration is NaN - ") +
              'the duration expression might be incorrect.', vnode.context);
      }
  }
  function isValidDuration(val) {
      return typeof val === 'number' && !isNaN(val);
  }
  /**
   * Normalize a transition hook's argument length. The hook may be:
   * - a merged hook (invoker) with the original in .fns
   * - a wrapped component method (check ._length)
   * - a plain function (.length)
   */
  function getHookArgumentsLength(fn) {
      if (isUndef(fn)) {
          return false;
      }
      // @ts-expect-error
      var invokerFns = fn.fns;
      if (isDef(invokerFns)) {
          // invoker
          return getHookArgumentsLength(Array.isArray(invokerFns) ? invokerFns[0] : invokerFns);
      }
      else {
          // @ts-expect-error
          return (fn._length || fn.length) > 1;
      }
  }
  function _enter(_, vnode) {
      if (vnode.data.show !== true) {
          enter(vnode);
      }
  }
  var transition = inBrowser
      ? {
          create: _enter,
          activate: _enter,
          remove: function (vnode, rm) {
              /* istanbul ignore else */
              if (vnode.data.show !== true) {
                  // @ts-expect-error
                  leave(vnode, rm);
              }
              else {
                  rm();
              }
          }
      }
      : {};

  var platformModules = [attrs, klass$1, events, domProps, style$1, transition];

  // the directive module should be applied last, after all
  // built-in modules have been applied.
  var modules$1 = platformModules.concat(baseModules);
  var patch = createPatchFunction({ nodeOps: nodeOps, modules: modules$1 });

  /**
   * Not type checking this file because flow doesn't like attaching
   * properties to Elements.
   */
  /* istanbul ignore if */
  if (isIE9) {
      // http://www.matts411.com/post/internet-explorer-9-oninput/
      document.addEventListener('selectionchange', function () {
          var el = document.activeElement;
          // @ts-expect-error
          if (el && el.vmodel) {
              trigger(el, 'input');
          }
      });
  }
  var directive = {
      inserted: function (el, binding, vnode, oldVnode) {
          if (vnode.tag === 'select') {
              // #6903
              if (oldVnode.elm && !oldVnode.elm._vOptions) {
                  mergeVNodeHook(vnode, 'postpatch', function () {
                      directive.componentUpdated(el, binding, vnode);
                  });
              }
              else {
                  setSelected(el, binding, vnode.context);
              }
              el._vOptions = [].map.call(el.options, getValue);
          }
          else if (vnode.tag === 'textarea' || isTextInputType(el.type)) {
              el._vModifiers = binding.modifiers;
              if (!binding.modifiers.lazy) {
                  el.addEventListener('compositionstart', onCompositionStart);
                  el.addEventListener('compositionend', onCompositionEnd);
                  // Safari < 10.2 & UIWebView doesn't fire compositionend when
                  // switching focus before confirming composition choice
                  // this also fixes the issue where some browsers e.g. iOS Chrome
                  // fires "change" instead of "input" on autocomplete.
                  el.addEventListener('change', onCompositionEnd);
                  /* istanbul ignore if */
                  if (isIE9) {
                      el.vmodel = true;
                  }
              }
          }
      },
      componentUpdated: function (el, binding, vnode) {
          if (vnode.tag === 'select') {
              setSelected(el, binding, vnode.context);
              // in case the options rendered by v-for have changed,
              // it's possible that the value is out-of-sync with the rendered options.
              // detect such cases and filter out values that no longer has a matching
              // option in the DOM.
              var prevOptions_1 = el._vOptions;
              var curOptions_1 = (el._vOptions = [].map.call(el.options, getValue));
              if (curOptions_1.some(function (o, i) { return !looseEqual(o, prevOptions_1[i]); })) {
                  // trigger change event if
                  // no matching option found for at least one value
                  var needReset = el.multiple
                      ? binding.value.some(function (v) { return hasNoMatchingOption(v, curOptions_1); })
                      : binding.value !== binding.oldValue &&
                          hasNoMatchingOption(binding.value, curOptions_1);
                  if (needReset) {
                      trigger(el, 'change');
                  }
              }
          }
      }
  };
  function setSelected(el, binding, vm) {
      actuallySetSelected(el, binding, vm);
      /* istanbul ignore if */
      if (isIE || isEdge) {
          setTimeout(function () {
              actuallySetSelected(el, binding, vm);
          }, 0);
      }
  }
  function actuallySetSelected(el, binding, vm) {
      var value = binding.value;
      var isMultiple = el.multiple;
      if (isMultiple && !Array.isArray(value)) {
          warn$2("<select multiple v-model=\"".concat(binding.expression, "\"> ") +
                  "expects an Array value for its binding, but got ".concat(Object.prototype.toString
                      .call(value)
                      .slice(8, -1)), vm);
          return;
      }
      var selected, option;
      for (var i = 0, l = el.options.length; i < l; i++) {
          option = el.options[i];
          if (isMultiple) {
              selected = looseIndexOf(value, getValue(option)) > -1;
              if (option.selected !== selected) {
                  option.selected = selected;
              }
          }
          else {
              if (looseEqual(getValue(option), value)) {
                  if (el.selectedIndex !== i) {
                      el.selectedIndex = i;
                  }
                  return;
              }
          }
      }
      if (!isMultiple) {
          el.selectedIndex = -1;
      }
  }
  function hasNoMatchingOption(value, options) {
      return options.every(function (o) { return !looseEqual(o, value); });
  }
  function getValue(option) {
      return '_value' in option ? option._value : option.value;
  }
  function onCompositionStart(e) {
      e.target.composing = true;
  }
  function onCompositionEnd(e) {
      // prevent triggering an input event for no reason
      if (!e.target.composing)
          return;
      e.target.composing = false;
      trigger(e.target, 'input');
  }
  function trigger(el, type) {
      var e = document.createEvent('HTMLEvents');
      e.initEvent(type, true, true);
      el.dispatchEvent(e);
  }

  // recursively search for possible transition defined inside the component root
  function locateNode(vnode) {
      // @ts-expect-error
      return vnode.componentInstance && (!vnode.data || !vnode.data.transition)
          ? locateNode(vnode.componentInstance._vnode)
          : vnode;
  }
  var show = {
      bind: function (el, _a, vnode) {
          var value = _a.value;
          vnode = locateNode(vnode);
          var transition = vnode.data && vnode.data.transition;
          var originalDisplay = (el.__vOriginalDisplay =
              el.style.display === 'none' ? '' : el.style.display);
          if (value && transition) {
              vnode.data.show = true;
              enter(vnode, function () {
                  el.style.display = originalDisplay;
              });
          }
          else {
              el.style.display = value ? originalDisplay : 'none';
          }
      },
      update: function (el, _a, vnode) {
          var value = _a.value, oldValue = _a.oldValue;
          /* istanbul ignore if */
          if (!value === !oldValue)
              return;
          vnode = locateNode(vnode);
          var transition = vnode.data && vnode.data.transition;
          if (transition) {
              vnode.data.show = true;
              if (value) {
                  enter(vnode, function () {
                      el.style.display = el.__vOriginalDisplay;
                  });
              }
              else {
                  leave(vnode, function () {
                      el.style.display = 'none';
                  });
              }
          }
          else {
              el.style.display = value ? el.__vOriginalDisplay : 'none';
          }
      },
      unbind: function (el, binding, vnode, oldVnode, isDestroy) {
          if (!isDestroy) {
              el.style.display = el.__vOriginalDisplay;
          }
      }
  };

  var platformDirectives = {
      model: directive,
      show: show
  };

  // Provides transition support for a single element/component.
  var transitionProps = {
      name: String,
      appear: Boolean,
      css: Boolean,
      mode: String,
      type: String,
      enterClass: String,
      leaveClass: String,
      enterToClass: String,
      leaveToClass: String,
      enterActiveClass: String,
      leaveActiveClass: String,
      appearClass: String,
      appearActiveClass: String,
      appearToClass: String,
      duration: [Number, String, Object]
  };
  // in case the child is also an abstract component, e.g. <keep-alive>
  // we want to recursively retrieve the real component to be rendered
  function getRealChild(vnode) {
      var compOptions = vnode && vnode.componentOptions;
      if (compOptions && compOptions.Ctor.options.abstract) {
          return getRealChild(getFirstComponentChild(compOptions.children));
      }
      else {
          return vnode;
      }
  }
  function extractTransitionData(comp) {
      var data = {};
      var options = comp.$options;
      // props
      for (var key in options.propsData) {
          data[key] = comp[key];
      }
      // events.
      // extract listeners and pass them directly to the transition methods
      var listeners = options._parentListeners;
      for (var key in listeners) {
          data[camelize(key)] = listeners[key];
      }
      return data;
  }
  function placeholder(h, rawChild) {
      // @ts-expect-error
      if (/\d-keep-alive$/.test(rawChild.tag)) {
          return h('keep-alive', {
              props: rawChild.componentOptions.propsData
          });
      }
  }
  function hasParentTransition(vnode) {
      while ((vnode = vnode.parent)) {
          if (vnode.data.transition) {
              return true;
          }
      }
  }
  function isSameChild(child, oldChild) {
      return oldChild.key === child.key && oldChild.tag === child.tag;
  }
  var isNotTextNode = function (c) { return c.tag || isAsyncPlaceholder(c); };
  var isVShowDirective = function (d) { return d.name === 'show'; };
  var Transition = {
      name: 'transition',
      props: transitionProps,
      abstract: true,
      render: function (h) {
          var _this = this;
          var children = this.$slots.default;
          if (!children) {
              return;
          }
          // filter out text nodes (possible whitespaces)
          children = children.filter(isNotTextNode);
          /* istanbul ignore if */
          if (!children.length) {
              return;
          }
          // warn multiple elements
          if (children.length > 1) {
              warn$2('<transition> can only be used on a single element. Use ' +
                  '<transition-group> for lists.', this.$parent);
          }
          var mode = this.mode;
          // warn invalid mode
          if (mode && mode !== 'in-out' && mode !== 'out-in') {
              warn$2('invalid <transition> mode: ' + mode, this.$parent);
          }
          var rawChild = children[0];
          // if this is a component root node and the component's
          // parent container node also has transition, skip.
          if (hasParentTransition(this.$vnode)) {
              return rawChild;
          }
          // apply transition data to child
          // use getRealChild() to ignore abstract components e.g. keep-alive
          var child = getRealChild(rawChild);
          /* istanbul ignore if */
          if (!child) {
              return rawChild;
          }
          if (this._leaving) {
              return placeholder(h, rawChild);
          }
          // ensure a key that is unique to the vnode type and to this transition
          // component instance. This key will be used to remove pending leaving nodes
          // during entering.
          var id = "__transition-".concat(this._uid, "-");
          child.key =
              child.key == null
                  ? child.isComment
                      ? id + 'comment'
                      : id + child.tag
                  : isPrimitive(child.key)
                      ? String(child.key).indexOf(id) === 0
                          ? child.key
                          : id + child.key
                      : child.key;
          var data = ((child.data || (child.data = {})).transition =
              extractTransitionData(this));
          var oldRawChild = this._vnode;
          var oldChild = getRealChild(oldRawChild);
          // mark v-show
          // so that the transition module can hand over the control to the directive
          if (child.data.directives && child.data.directives.some(isVShowDirective)) {
              child.data.show = true;
          }
          if (oldChild &&
              oldChild.data &&
              !isSameChild(child, oldChild) &&
              !isAsyncPlaceholder(oldChild) &&
              // #6687 component root is a comment node
              !(oldChild.componentInstance &&
                  oldChild.componentInstance._vnode.isComment)) {
              // replace old child transition data with fresh one
              // important for dynamic transitions!
              var oldData = (oldChild.data.transition = extend({}, data));
              // handle transition mode
              if (mode === 'out-in') {
                  // return placeholder node and queue update when leave finishes
                  this._leaving = true;
                  mergeVNodeHook(oldData, 'afterLeave', function () {
                      _this._leaving = false;
                      _this.$forceUpdate();
                  });
                  return placeholder(h, rawChild);
              }
              else if (mode === 'in-out') {
                  if (isAsyncPlaceholder(child)) {
                      return oldRawChild;
                  }
                  var delayedLeave_1;
                  var performLeave = function () {
                      delayedLeave_1();
                  };
                  mergeVNodeHook(data, 'afterEnter', performLeave);
                  mergeVNodeHook(data, 'enterCancelled', performLeave);
                  mergeVNodeHook(oldData, 'delayLeave', function (leave) {
                      delayedLeave_1 = leave;
                  });
              }
          }
          return rawChild;
      }
  };

  // Provides transition support for list items.
  var props = extend({
      tag: String,
      moveClass: String
  }, transitionProps);
  delete props.mode;
  var TransitionGroup = {
      props: props,
      beforeMount: function () {
          var _this = this;
          var update = this._update;
          this._update = function (vnode, hydrating) {
              var restoreActiveInstance = setActiveInstance(_this);
              // force removing pass
              _this.__patch__(_this._vnode, _this.kept, false, // hydrating
              true // removeOnly (!important, avoids unnecessary moves)
              );
              _this._vnode = _this.kept;
              restoreActiveInstance();
              update.call(_this, vnode, hydrating);
          };
      },
      render: function (h) {
          var tag = this.tag || this.$vnode.data.tag || 'span';
          var map = Object.create(null);
          var prevChildren = (this.prevChildren = this.children);
          var rawChildren = this.$slots.default || [];
          var children = (this.children = []);
          var transitionData = extractTransitionData(this);
          for (var i = 0; i < rawChildren.length; i++) {
              var c = rawChildren[i];
              if (c.tag) {
                  if (c.key != null && String(c.key).indexOf('__vlist') !== 0) {
                      children.push(c);
                      map[c.key] = c;
                      (c.data || (c.data = {})).transition = transitionData;
                  }
                  else {
                      var opts = c.componentOptions;
                      var name_1 = opts
                          ? getComponentName(opts.Ctor.options) || opts.tag || ''
                          : c.tag;
                      warn$2("<transition-group> children must be keyed: <".concat(name_1, ">"));
                  }
              }
          }
          if (prevChildren) {
              var kept = [];
              var removed = [];
              for (var i = 0; i < prevChildren.length; i++) {
                  var c = prevChildren[i];
                  c.data.transition = transitionData;
                  // @ts-expect-error .getBoundingClientRect is not typed in Node
                  c.data.pos = c.elm.getBoundingClientRect();
                  if (map[c.key]) {
                      kept.push(c);
                  }
                  else {
                      removed.push(c);
                  }
              }
              this.kept = h(tag, null, kept);
              this.removed = removed;
          }
          return h(tag, null, children);
      },
      updated: function () {
          var children = this.prevChildren;
          var moveClass = this.moveClass || (this.name || 'v') + '-move';
          if (!children.length || !this.hasMove(children[0].elm, moveClass)) {
              return;
          }
          // we divide the work into three loops to avoid mixing DOM reads and writes
          // in each iteration - which helps prevent layout thrashing.
          children.forEach(callPendingCbs);
          children.forEach(recordPosition);
          children.forEach(applyTranslation);
          // force reflow to put everything in position
          // assign to this to avoid being removed in tree-shaking
          // $flow-disable-line
          this._reflow = document.body.offsetHeight;
          children.forEach(function (c) {
              if (c.data.moved) {
                  var el_1 = c.elm;
                  var s = el_1.style;
                  addTransitionClass(el_1, moveClass);
                  s.transform = s.WebkitTransform = s.transitionDuration = '';
                  el_1.addEventListener(transitionEndEvent, (el_1._moveCb = function cb(e) {
                      if (e && e.target !== el_1) {
                          return;
                      }
                      if (!e || /transform$/.test(e.propertyName)) {
                          el_1.removeEventListener(transitionEndEvent, cb);
                          el_1._moveCb = null;
                          removeTransitionClass(el_1, moveClass);
                      }
                  }));
              }
          });
      },
      methods: {
          hasMove: function (el, moveClass) {
              /* istanbul ignore if */
              if (!hasTransition) {
                  return false;
              }
              /* istanbul ignore if */
              if (this._hasMove) {
                  return this._hasMove;
              }
              // Detect whether an element with the move class applied has
              // CSS transitions. Since the element may be inside an entering
              // transition at this very moment, we make a clone of it and remove
              // all other transition classes applied to ensure only the move class
              // is applied.
              var clone = el.cloneNode();
              if (el._transitionClasses) {
                  el._transitionClasses.forEach(function (cls) {
                      removeClass(clone, cls);
                  });
              }
              addClass(clone, moveClass);
              clone.style.display = 'none';
              this.$el.appendChild(clone);
              var info = getTransitionInfo(clone);
              this.$el.removeChild(clone);
              return (this._hasMove = info.hasTransform);
          }
      }
  };
  function callPendingCbs(c) {
      /* istanbul ignore if */
      if (c.elm._moveCb) {
          c.elm._moveCb();
      }
      /* istanbul ignore if */
      if (c.elm._enterCb) {
          c.elm._enterCb();
      }
  }
  function recordPosition(c) {
      c.data.newPos = c.elm.getBoundingClientRect();
  }
  function applyTranslation(c) {
      var oldPos = c.data.pos;
      var newPos = c.data.newPos;
      var dx = oldPos.left - newPos.left;
      var dy = oldPos.top - newPos.top;
      if (dx || dy) {
          c.data.moved = true;
          var s = c.elm.style;
          s.transform = s.WebkitTransform = "translate(".concat(dx, "px,").concat(dy, "px)");
          s.transitionDuration = '0s';
      }
  }

  var platformComponents = {
      Transition: Transition,
      TransitionGroup: TransitionGroup
  };

  // install platform specific utils
  Vue.config.mustUseProp = mustUseProp;
  Vue.config.isReservedTag = isReservedTag;
  Vue.config.isReservedAttr = isReservedAttr;
  Vue.config.getTagNamespace = getTagNamespace;
  Vue.config.isUnknownElement = isUnknownElement;
  // install platform runtime directives & components
  extend(Vue.options.directives, platformDirectives);
  extend(Vue.options.components, platformComponents);
  // install platform patch function
  Vue.prototype.__patch__ = inBrowser ? patch : noop;
  // public mount method
  Vue.prototype.$mount = function (el, hydrating) {
      el = el && inBrowser ? query(el) : undefined;
      return mountComponent(this, el, hydrating);
  };
  // devtools global hook
  /* istanbul ignore next */
  if (inBrowser) {
      setTimeout(function () {
          if (config.devtools) {
              if (devtools) {
                  devtools.emit('init', Vue);
              }
              else {
                  // @ts-expect-error
                  console[console.info ? 'info' : 'log']('Download the Vue Devtools extension for a better development experience:\n' +
                      'https://github.com/vuejs/vue-devtools');
              }
          }
          if (config.productionTip !== false &&
              typeof console !== 'undefined') {
              // @ts-expect-error
              console[console.info ? 'info' : 'log']("You are running Vue in development mode.\n" +
                  "Make sure to turn on production mode when deploying for production.\n" +
                  "See more tips at https://vuejs.org/guide/deployment.html");
          }
      }, 0);
  }

  var defaultTagRE = /\{\{((?:.|\r?\n)+?)\}\}/g;
  var regexEscapeRE = /[-.*+?^${}()|[\]\/\\]/g;
  var buildRegex = cached(function (delimiters) {
      var open = delimiters[0].replace(regexEscapeRE, '\\$&');
      var close = delimiters[1].replace(regexEscapeRE, '\\$&');
      return new RegExp(open + '((?:.|\\n)+?)' + close, 'g');
  });
  function parseText(text, delimiters) {
      //@ts-expect-error
      var tagRE = delimiters ? buildRegex(delimiters) : defaultTagRE;
      if (!tagRE.test(text)) {
          return;
      }
      var tokens = [];
      var rawTokens = [];
      var lastIndex = (tagRE.lastIndex = 0);
      var match, index, tokenValue;
      while ((match = tagRE.exec(text))) {
          index = match.index;
          // push text token
          if (index > lastIndex) {
              rawTokens.push((tokenValue = text.slice(lastIndex, index)));
              tokens.push(JSON.stringify(tokenValue));
          }
          // tag token
          var exp = parseFilters(match[1].trim());
          tokens.push("_s(".concat(exp, ")"));
          rawTokens.push({ '@binding': exp });
          lastIndex = index + match[0].length;
      }
      if (lastIndex < text.length) {
          rawTokens.push((tokenValue = text.slice(lastIndex)));
          tokens.push(JSON.stringify(tokenValue));
      }
      return {
          expression: tokens.join('+'),
          tokens: rawTokens
      };
  }

  function transformNode$1(el, options) {
      var warn = options.warn || baseWarn;
      var staticClass = getAndRemoveAttr(el, 'class');
      if (staticClass) {
          var res = parseText(staticClass, options.delimiters);
          if (res) {
              warn("class=\"".concat(staticClass, "\": ") +
                  'Interpolation inside attributes has been removed. ' +
                  'Use v-bind or the colon shorthand instead. For example, ' +
                  'instead of <div class="{{ val }}">, use <div :class="val">.', el.rawAttrsMap['class']);
          }
      }
      if (staticClass) {
          el.staticClass = JSON.stringify(staticClass.replace(/\s+/g, ' ').trim());
      }
      var classBinding = getBindingAttr(el, 'class', false /* getStatic */);
      if (classBinding) {
          el.classBinding = classBinding;
      }
  }
  function genData$2(el) {
      var data = '';
      if (el.staticClass) {
          data += "staticClass:".concat(el.staticClass, ",");
      }
      if (el.classBinding) {
          data += "class:".concat(el.classBinding, ",");
      }
      return data;
  }
  var klass = {
      staticKeys: ['staticClass'],
      transformNode: transformNode$1,
      genData: genData$2
  };

  function transformNode(el, options) {
      var warn = options.warn || baseWarn;
      var staticStyle = getAndRemoveAttr(el, 'style');
      if (staticStyle) {
          /* istanbul ignore if */
          {
              var res = parseText(staticStyle, options.delimiters);
              if (res) {
                  warn("style=\"".concat(staticStyle, "\": ") +
                      'Interpolation inside attributes has been removed. ' +
                      'Use v-bind or the colon shorthand instead. For example, ' +
                      'instead of <div style="{{ val }}">, use <div :style="val">.', el.rawAttrsMap['style']);
              }
          }
          el.staticStyle = JSON.stringify(parseStyleText(staticStyle));
      }
      var styleBinding = getBindingAttr(el, 'style', false /* getStatic */);
      if (styleBinding) {
          el.styleBinding = styleBinding;
      }
  }
  function genData$1(el) {
      var data = '';
      if (el.staticStyle) {
          data += "staticStyle:".concat(el.staticStyle, ",");
      }
      if (el.styleBinding) {
          data += "style:(".concat(el.styleBinding, "),");
      }
      return data;
  }
  var style = {
      staticKeys: ['staticStyle'],
      transformNode: transformNode,
      genData: genData$1
  };

  var decoder;
  var he = {
      decode: function (html) {
          decoder = decoder || document.createElement('div');
          decoder.innerHTML = html;
          return decoder.textContent;
      }
  };

  var isUnaryTag = makeMap('area,base,br,col,embed,frame,hr,img,input,isindex,keygen,' +
      'link,meta,param,source,track,wbr');
  // Elements that you can, intentionally, leave open
  // (and which close themselves)
  var canBeLeftOpenTag = makeMap('colgroup,dd,dt,li,options,p,td,tfoot,th,thead,tr,source');
  // HTML5 tags https://html.spec.whatwg.org/multipage/indices.html#elements-3
  // Phrasing Content https://html.spec.whatwg.org/multipage/dom.html#phrasing-content
  var isNonPhrasingTag = makeMap('address,article,aside,base,blockquote,body,caption,col,colgroup,dd,' +
      'details,dialog,div,dl,dt,fieldset,figcaption,figure,footer,form,' +
      'h1,h2,h3,h4,h5,h6,head,header,hgroup,hr,html,legend,li,menuitem,meta,' +
      'optgroup,option,param,rp,rt,source,style,summary,tbody,td,tfoot,th,thead,' +
      'title,tr,track');

  /**
   * Not type-checking this file because it's mostly vendor code.
   */
  // Regular Expressions for parsing tags and attributes
  var attribute = /^\s*([^\s"'<>\/=]+)(?:\s*(=)\s*(?:"([^"]*)"+|'([^']*)'+|([^\s"'=<>`]+)))?/;
  var dynamicArgAttribute = /^\s*((?:v-[\w-]+:|@|:|#)\[[^=]+?\][^\s"'<>\/=]*)(?:\s*(=)\s*(?:"([^"]*)"+|'([^']*)'+|([^\s"'=<>`]+)))?/;
  var ncname = "[a-zA-Z_][\\-\\.0-9_a-zA-Z".concat(unicodeRegExp.source, "]*");
  var qnameCapture = "((?:".concat(ncname, "\\:)?").concat(ncname, ")");
  var startTagOpen = new RegExp("^<".concat(qnameCapture));
  var startTagClose = /^\s*(\/?)>/;
  var endTag = new RegExp("^<\\/".concat(qnameCapture, "[^>]*>"));
  var doctype = /^<!DOCTYPE [^>]+>/i;
  // #7298: escape - to avoid being passed as HTML comment when inlined in page
  var comment = /^<!\--/;
  var conditionalComment = /^<!\[/;
  // Special Elements (can contain anything)
  var isPlainTextElement = makeMap('script,style,textarea', true);
  var reCache = {};
  var decodingMap = {
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&amp;': '&',
      '&#10;': '\n',
      '&#9;': '\t',
      '&#39;': "'"
  };
  var encodedAttr = /&(?:lt|gt|quot|amp|#39);/g;
  var encodedAttrWithNewLines = /&(?:lt|gt|quot|amp|#39|#10|#9);/g;
  // #5992
  var isIgnoreNewlineTag = makeMap('pre,textarea', true);
  var shouldIgnoreFirstNewline = function (tag, html) {
      return tag && isIgnoreNewlineTag(tag) && html[0] === '\n';
  };
  function decodeAttr(value, shouldDecodeNewlines) {
      var re = shouldDecodeNewlines ? encodedAttrWithNewLines : encodedAttr;
      return value.replace(re, function (match) { return decodingMap[match]; });
  }
  function parseHTML(html, options) {
      var stack = [];
      var expectHTML = options.expectHTML;
      var isUnaryTag = options.isUnaryTag || no;
      var canBeLeftOpenTag = options.canBeLeftOpenTag || no;
      var index = 0;
      var last, lastTag;
      var _loop_1 = function () {
          last = html;
          // Make sure we're not in a plaintext content element like script/style
          if (!lastTag || !isPlainTextElement(lastTag)) {
              var textEnd = html.indexOf('<');
              if (textEnd === 0) {
                  // Comment:
                  if (comment.test(html)) {
                      var commentEnd = html.indexOf('-->');
                      if (commentEnd >= 0) {
                          if (options.shouldKeepComment && options.comment) {
                              options.comment(html.substring(4, commentEnd), index, index + commentEnd + 3);
                          }
                          advance(commentEnd + 3);
                          return "continue";
                      }
                  }
                  // https://en.wikipedia.org/wiki/Conditional_comment#Downlevel-revealed_conditional_comment
                  if (conditionalComment.test(html)) {
                      var conditionalEnd = html.indexOf(']>');
                      if (conditionalEnd >= 0) {
                          advance(conditionalEnd + 2);
                          return "continue";
                      }
                  }
                  // Doctype:
                  var doctypeMatch = html.match(doctype);
                  if (doctypeMatch) {
                      advance(doctypeMatch[0].length);
                      return "continue";
                  }
                  // End tag:
                  var endTagMatch = html.match(endTag);
                  if (endTagMatch) {
                      var curIndex = index;
                      advance(endTagMatch[0].length);
                      parseEndTag(endTagMatch[1], curIndex, index);
                      return "continue";
                  }
                  // Start tag:
                  var startTagMatch = parseStartTag();
                  if (startTagMatch) {
                      handleStartTag(startTagMatch);
                      if (shouldIgnoreFirstNewline(startTagMatch.tagName, html)) {
                          advance(1);
                      }
                      return "continue";
                  }
              }
              var text = void 0, rest = void 0, next = void 0;
              if (textEnd >= 0) {
                  rest = html.slice(textEnd);
                  while (!endTag.test(rest) &&
                      !startTagOpen.test(rest) &&
                      !comment.test(rest) &&
                      !conditionalComment.test(rest)) {
                      // < in plain text, be forgiving and treat it as text
                      next = rest.indexOf('<', 1);
                      if (next < 0)
                          break;
                      textEnd += next;
                      rest = html.slice(textEnd);
                  }
                  text = html.substring(0, textEnd);
              }
              if (textEnd < 0) {
                  text = html;
              }
              if (text) {
                  advance(text.length);
              }
              if (options.chars && text) {
                  options.chars(text, index - text.length, index);
              }
          }
          else {
              var endTagLength_1 = 0;
              var stackedTag_1 = lastTag.toLowerCase();
              var reStackedTag = reCache[stackedTag_1] ||
                  (reCache[stackedTag_1] = new RegExp('([\\s\\S]*?)(</' + stackedTag_1 + '[^>]*>)', 'i'));
              var rest = html.replace(reStackedTag, function (all, text, endTag) {
                  endTagLength_1 = endTag.length;
                  if (!isPlainTextElement(stackedTag_1) && stackedTag_1 !== 'noscript') {
                      text = text
                          .replace(/<!\--([\s\S]*?)-->/g, '$1') // #7298
                          .replace(/<!\[CDATA\[([\s\S]*?)]]>/g, '$1');
                  }
                  if (shouldIgnoreFirstNewline(stackedTag_1, text)) {
                      text = text.slice(1);
                  }
                  if (options.chars) {
                      options.chars(text);
                  }
                  return '';
              });
              index += html.length - rest.length;
              html = rest;
              parseEndTag(stackedTag_1, index - endTagLength_1, index);
          }
          if (html === last) {
              options.chars && options.chars(html);
              if (!stack.length && options.warn) {
                  options.warn("Mal-formatted tag at end of template: \"".concat(html, "\""), {
                      start: index + html.length
                  });
              }
              return "break";
          }
      };
      while (html) {
          var state_1 = _loop_1();
          if (state_1 === "break")
              break;
      }
      // Clean up any remaining tags
      parseEndTag();
      function advance(n) {
          index += n;
          html = html.substring(n);
      }
      function parseStartTag() {
          var start = html.match(startTagOpen);
          if (start) {
              var match = {
                  tagName: start[1],
                  attrs: [],
                  start: index
              };
              advance(start[0].length);
              var end = void 0, attr = void 0;
              while (!(end = html.match(startTagClose)) &&
                  (attr = html.match(dynamicArgAttribute) || html.match(attribute))) {
                  attr.start = index;
                  advance(attr[0].length);
                  attr.end = index;
                  match.attrs.push(attr);
              }
              if (end) {
                  match.unarySlash = end[1];
                  advance(end[0].length);
                  match.end = index;
                  return match;
              }
          }
      }
      function handleStartTag(match) {
          var tagName = match.tagName;
          var unarySlash = match.unarySlash;
          if (expectHTML) {
              if (lastTag === 'p' && isNonPhrasingTag(tagName)) {
                  parseEndTag(lastTag);
              }
              if (canBeLeftOpenTag(tagName) && lastTag === tagName) {
                  parseEndTag(tagName);
              }
          }
          var unary = isUnaryTag(tagName) || !!unarySlash;
          var l = match.attrs.length;
          var attrs = new Array(l);
          for (var i = 0; i < l; i++) {
              var args = match.attrs[i];
              var value = args[3] || args[4] || args[5] || '';
              var shouldDecodeNewlines = tagName === 'a' && args[1] === 'href'
                  ? options.shouldDecodeNewlinesForHref
                  : options.shouldDecodeNewlines;
              attrs[i] = {
                  name: args[1],
                  value: decodeAttr(value, shouldDecodeNewlines)
              };
              if (options.outputSourceRange) {
                  attrs[i].start = args.start + args[0].match(/^\s*/).length;
                  attrs[i].end = args.end;
              }
          }
          if (!unary) {
              stack.push({
                  tag: tagName,
                  lowerCasedTag: tagName.toLowerCase(),
                  attrs: attrs,
                  start: match.start,
                  end: match.end
              });
              lastTag = tagName;
          }
          if (options.start) {
              options.start(tagName, attrs, unary, match.start, match.end);
          }
      }
      function parseEndTag(tagName, start, end) {
          var pos, lowerCasedTagName;
          if (start == null)
              start = index;
          if (end == null)
              end = index;
          // Find the closest opened tag of the same type
          if (tagName) {
              lowerCasedTagName = tagName.toLowerCase();
              for (pos = stack.length - 1; pos >= 0; pos--) {
                  if (stack[pos].lowerCasedTag === lowerCasedTagName) {
                      break;
                  }
              }
          }
          else {
              // If no tag name is provided, clean shop
              pos = 0;
          }
          if (pos >= 0) {
              // Close all the open elements, up the stack
              for (var i = stack.length - 1; i >= pos; i--) {
                  if ((i > pos || !tagName) && options.warn) {
                      options.warn("tag <".concat(stack[i].tag, "> has no matching end tag."), {
                          start: stack[i].start,
                          end: stack[i].end
                      });
                  }
                  if (options.end) {
                      options.end(stack[i].tag, start, end);
                  }
              }
              // Remove the open elements from the stack
              stack.length = pos;
              lastTag = pos && stack[pos - 1].tag;
          }
          else if (lowerCasedTagName === 'br') {
              if (options.start) {
                  options.start(tagName, [], true, start, end);
              }
          }
          else if (lowerCasedTagName === 'p') {
              if (options.start) {
                  options.start(tagName, [], false, start, end);
              }
              if (options.end) {
                  options.end(tagName, start, end);
              }
          }
      }
  }

  var onRE = /^@|^v-on:/;
  var dirRE = /^v-|^@|^:|^#/;
  var forAliasRE = /([\s\S]*?)\s+(?:in|of)\s+([\s\S]*)/;
  var forIteratorRE = /,([^,\}\]]*)(?:,([^,\}\]]*))?$/;
  var stripParensRE = /^\(|\)$/g;
  var dynamicArgRE = /^\[.*\]$/;
  var argRE = /:(.*)$/;
  var bindRE = /^:|^\.|^v-bind:/;
  var modifierRE = /\.[^.\]]+(?=[^\]]*$)/g;
  var slotRE = /^v-slot(:|$)|^#/;
  var lineBreakRE = /[\r\n]/;
  var whitespaceRE = /[ \f\t\r\n]+/g;
  var invalidAttributeRE = /[\s"'<>\/=]/;
  var decodeHTMLCached = cached(he.decode);
  var emptySlotScopeToken = "_empty_";
  // configurable state
  var warn;
  var delimiters;
  var transforms;
  var preTransforms;
  var postTransforms;
  var platformIsPreTag;
  var platformMustUseProp;
  var platformGetTagNamespace;
  var maybeComponent;
  function createASTElement(tag, attrs, parent) {
      return {
          type: 1,
          tag: tag,
          attrsList: attrs,
          attrsMap: makeAttrsMap(attrs),
          rawAttrsMap: {},
          parent: parent,
          children: []
      };
  }
  /**
   * Convert HTML string to AST.
   */
  function parse(template, options) {
      warn = options.warn || baseWarn;
      platformIsPreTag = options.isPreTag || no;
      platformMustUseProp = options.mustUseProp || no;
      platformGetTagNamespace = options.getTagNamespace || no;
      var isReservedTag = options.isReservedTag || no;
      maybeComponent = function (el) {
          return !!(el.component ||
              el.attrsMap[':is'] ||
              el.attrsMap['v-bind:is'] ||
              !(el.attrsMap.is ? isReservedTag(el.attrsMap.is) : isReservedTag(el.tag)));
      };
      transforms = pluckModuleFunction(options.modules, 'transformNode');
      preTransforms = pluckModuleFunction(options.modules, 'preTransformNode');
      postTransforms = pluckModuleFunction(options.modules, 'postTransformNode');
      delimiters = options.delimiters;
      var stack = [];
      var preserveWhitespace = options.preserveWhitespace !== false;
      var whitespaceOption = options.whitespace;
      var root;
      var currentParent;
      var inVPre = false;
      var inPre = false;
      var warned = false;
      function warnOnce(msg, range) {
          if (!warned) {
              warned = true;
              warn(msg, range);
          }
      }
      function closeElement(element) {
          trimEndingWhitespace(element);
          if (!inVPre && !element.processed) {
              element = processElement(element, options);
          }
          // tree management
          if (!stack.length && element !== root) {
              // allow root elements with v-if, v-else-if and v-else
              if (root.if && (element.elseif || element.else)) {
                  {
                      checkRootConstraints(element);
                  }
                  addIfCondition(root, {
                      exp: element.elseif,
                      block: element
                  });
              }
              else {
                  warnOnce("Component template should contain exactly one root element. " +
                      "If you are using v-if on multiple elements, " +
                      "use v-else-if to chain them instead.", { start: element.start });
              }
          }
          if (currentParent && !element.forbidden) {
              if (element.elseif || element.else) {
                  processIfConditions(element, currentParent);
              }
              else {
                  if (element.slotScope) {
                      // scoped slot
                      // keep it in the children list so that v-else(-if) conditions can
                      // find it as the prev node.
                      var name_1 = element.slotTarget || '"default"';
                      (currentParent.scopedSlots || (currentParent.scopedSlots = {}))[name_1] = element;
                  }
                  currentParent.children.push(element);
                  element.parent = currentParent;
              }
          }
          // final children cleanup
          // filter out scoped slots
          element.children = element.children.filter(function (c) { return !c.slotScope; });
          // remove trailing whitespace node again
          trimEndingWhitespace(element);
          // check pre state
          if (element.pre) {
              inVPre = false;
          }
          if (platformIsPreTag(element.tag)) {
              inPre = false;
          }
          // apply post-transforms
          for (var i = 0; i < postTransforms.length; i++) {
              postTransforms[i](element, options);
          }
      }
      function trimEndingWhitespace(el) {
          // remove trailing whitespace node
          if (!inPre) {
              var lastNode = void 0;
              while ((lastNode = el.children[el.children.length - 1]) &&
                  lastNode.type === 3 &&
                  lastNode.text === ' ') {
                  el.children.pop();
              }
          }
      }
      function checkRootConstraints(el) {
          if (el.tag === 'slot' || el.tag === 'template') {
              warnOnce("Cannot use <".concat(el.tag, "> as component root element because it may ") +
                  'contain multiple nodes.', { start: el.start });
          }
          if (el.attrsMap.hasOwnProperty('v-for')) {
              warnOnce('Cannot use v-for on stateful component root element because ' +
                  'it renders multiple elements.', el.rawAttrsMap['v-for']);
          }
      }
      parseHTML(template, {
          warn: warn,
          expectHTML: options.expectHTML,
          isUnaryTag: options.isUnaryTag,
          canBeLeftOpenTag: options.canBeLeftOpenTag,
          shouldDecodeNewlines: options.shouldDecodeNewlines,
          shouldDecodeNewlinesForHref: options.shouldDecodeNewlinesForHref,
          shouldKeepComment: options.comments,
          outputSourceRange: options.outputSourceRange,
          start: function (tag, attrs, unary, start, end) {
              // check namespace.
              // inherit parent ns if there is one
              var ns = (currentParent && currentParent.ns) || platformGetTagNamespace(tag);
              // handle IE svg bug
              /* istanbul ignore if */
              if (isIE && ns === 'svg') {
                  attrs = guardIESVGBug(attrs);
              }
              var element = createASTElement(tag, attrs, currentParent);
              if (ns) {
                  element.ns = ns;
              }
              {
                  if (options.outputSourceRange) {
                      element.start = start;
                      element.end = end;
                      element.rawAttrsMap = element.attrsList.reduce(function (cumulated, attr) {
                          cumulated[attr.name] = attr;
                          return cumulated;
                      }, {});
                  }
                  attrs.forEach(function (attr) {
                      if (invalidAttributeRE.test(attr.name)) {
                          warn("Invalid dynamic argument expression: attribute names cannot contain " +
                              "spaces, quotes, <, >, / or =.", options.outputSourceRange
                              ? {
                                  start: attr.start + attr.name.indexOf("["),
                                  end: attr.start + attr.name.length
                              }
                              : undefined);
                      }
                  });
              }
              if (isForbiddenTag(element) && !isServerRendering()) {
                  element.forbidden = true;
                  warn('Templates should only be responsible for mapping the state to the ' +
                          'UI. Avoid placing tags with side-effects in your templates, such as ' +
                          "<".concat(tag, ">") +
                          ', as they will not be parsed.', { start: element.start });
              }
              // apply pre-transforms
              for (var i = 0; i < preTransforms.length; i++) {
                  element = preTransforms[i](element, options) || element;
              }
              if (!inVPre) {
                  processPre(element);
                  if (element.pre) {
                      inVPre = true;
                  }
              }
              if (platformIsPreTag(element.tag)) {
                  inPre = true;
              }
              if (inVPre) {
                  processRawAttrs(element);
              }
              else if (!element.processed) {
                  // structural directives
                  processFor(element);
                  processIf(element);
                  processOnce(element);
              }
              if (!root) {
                  root = element;
                  {
                      checkRootConstraints(root);
                  }
              }
              if (!unary) {
                  currentParent = element;
                  stack.push(element);
              }
              else {
                  closeElement(element);
              }
          },
          end: function (tag, start, end) {
              var element = stack[stack.length - 1];
              // pop stack
              stack.length -= 1;
              currentParent = stack[stack.length - 1];
              if (options.outputSourceRange) {
                  element.end = end;
              }
              closeElement(element);
          },
          chars: function (text, start, end) {
              if (!currentParent) {
                  {
                      if (text === template) {
                          warnOnce('Component template requires a root element, rather than just text.', { start: start });
                      }
                      else if ((text = text.trim())) {
                          warnOnce("text \"".concat(text, "\" outside root element will be ignored."), {
                              start: start
                          });
                      }
                  }
                  return;
              }
              // IE textarea placeholder bug
              /* istanbul ignore if */
              if (isIE &&
                  currentParent.tag === 'textarea' &&
                  currentParent.attrsMap.placeholder === text) {
                  return;
              }
              var children = currentParent.children;
              if (inPre || text.trim()) {
                  text = isTextTag(currentParent)
                      ? text
                      : decodeHTMLCached(text);
              }
              else if (!children.length) {
                  // remove the whitespace-only node right after an opening tag
                  text = '';
              }
              else if (whitespaceOption) {
                  if (whitespaceOption === 'condense') {
                      // in condense mode, remove the whitespace node if it contains
                      // line break, otherwise condense to a single space
                      text = lineBreakRE.test(text) ? '' : ' ';
                  }
                  else {
                      text = ' ';
                  }
              }
              else {
                  text = preserveWhitespace ? ' ' : '';
              }
              if (text) {
                  if (!inPre && whitespaceOption === 'condense') {
                      // condense consecutive whitespaces into single space
                      text = text.replace(whitespaceRE, ' ');
                  }
                  var res = void 0;
                  var child = void 0;
                  if (!inVPre && text !== ' ' && (res = parseText(text, delimiters))) {
                      child = {
                          type: 2,
                          expression: res.expression,
                          tokens: res.tokens,
                          text: text
                      };
                  }
                  else if (text !== ' ' ||
                      !children.length ||
                      children[children.length - 1].text !== ' ') {
                      child = {
                          type: 3,
                          text: text
                      };
                  }
                  if (child) {
                      if (options.outputSourceRange) {
                          child.start = start;
                          child.end = end;
                      }
                      children.push(child);
                  }
              }
          },
          comment: function (text, start, end) {
              // adding anything as a sibling to the root node is forbidden
              // comments should still be allowed, but ignored
              if (currentParent) {
                  var child = {
                      type: 3,
                      text: text,
                      isComment: true
                  };
                  if (options.outputSourceRange) {
                      child.start = start;
                      child.end = end;
                  }
                  currentParent.children.push(child);
              }
          }
      });
      return root;
  }
  function processPre(el) {
      if (getAndRemoveAttr(el, 'v-pre') != null) {
          el.pre = true;
      }
  }
  function processRawAttrs(el) {
      var list = el.attrsList;
      var len = list.length;
      if (len) {
          var attrs = (el.attrs = new Array(len));
          for (var i = 0; i < len; i++) {
              attrs[i] = {
                  name: list[i].name,
                  value: JSON.stringify(list[i].value)
              };
              if (list[i].start != null) {
                  attrs[i].start = list[i].start;
                  attrs[i].end = list[i].end;
              }
          }
      }
      else if (!el.pre) {
          // non root node in pre blocks with no attributes
          el.plain = true;
      }
  }
  function processElement(element, options) {
      processKey(element);
      // determine whether this is a plain element after
      // removing structural attributes
      element.plain =
          !element.key && !element.scopedSlots && !element.attrsList.length;
      processRef(element);
      processSlotContent(element);
      processSlotOutlet(element);
      processComponent(element);
      for (var i = 0; i < transforms.length; i++) {
          element = transforms[i](element, options) || element;
      }
      processAttrs(element);
      return element;
  }
  function processKey(el) {
      var exp = getBindingAttr(el, 'key');
      if (exp) {
          {
              if (el.tag === 'template') {
                  warn("<template> cannot be keyed. Place the key on real elements instead.", getRawBindingAttr(el, 'key'));
              }
              if (el.for) {
                  var iterator = el.iterator2 || el.iterator1;
                  var parent_1 = el.parent;
                  if (iterator &&
                      iterator === exp &&
                      parent_1 &&
                      parent_1.tag === 'transition-group') {
                      warn("Do not use v-for index as key on <transition-group> children, " +
                          "this is the same as not using keys.", getRawBindingAttr(el, 'key'), true /* tip */);
                  }
              }
          }
          el.key = exp;
      }
  }
  function processRef(el) {
      var ref = getBindingAttr(el, 'ref');
      if (ref) {
          el.ref = ref;
          el.refInFor = checkInFor(el);
      }
  }
  function processFor(el) {
      var exp;
      if ((exp = getAndRemoveAttr(el, 'v-for'))) {
          var res = parseFor(exp);
          if (res) {
              extend(el, res);
          }
          else {
              warn("Invalid v-for expression: ".concat(exp), el.rawAttrsMap['v-for']);
          }
      }
  }
  function parseFor(exp) {
      var inMatch = exp.match(forAliasRE);
      if (!inMatch)
          return;
      var res = {};
      res.for = inMatch[2].trim();
      var alias = inMatch[1].trim().replace(stripParensRE, '');
      var iteratorMatch = alias.match(forIteratorRE);
      if (iteratorMatch) {
          res.alias = alias.replace(forIteratorRE, '').trim();
          res.iterator1 = iteratorMatch[1].trim();
          if (iteratorMatch[2]) {
              res.iterator2 = iteratorMatch[2].trim();
          }
      }
      else {
          res.alias = alias;
      }
      return res;
  }
  function processIf(el) {
      var exp = getAndRemoveAttr(el, 'v-if');
      if (exp) {
          el.if = exp;
          addIfCondition(el, {
              exp: exp,
              block: el
          });
      }
      else {
          if (getAndRemoveAttr(el, 'v-else') != null) {
              el.else = true;
          }
          var elseif = getAndRemoveAttr(el, 'v-else-if');
          if (elseif) {
              el.elseif = elseif;
          }
      }
  }
  function processIfConditions(el, parent) {
      var prev = findPrevElement(parent.children);
      if (prev && prev.if) {
          addIfCondition(prev, {
              exp: el.elseif,
              block: el
          });
      }
      else {
          warn("v-".concat(el.elseif ? 'else-if="' + el.elseif + '"' : 'else', " ") +
              "used on element <".concat(el.tag, "> without corresponding v-if."), el.rawAttrsMap[el.elseif ? 'v-else-if' : 'v-else']);
      }
  }
  function findPrevElement(children) {
      var i = children.length;
      while (i--) {
          if (children[i].type === 1) {
              return children[i];
          }
          else {
              if (children[i].text !== ' ') {
                  warn("text \"".concat(children[i].text.trim(), "\" between v-if and v-else(-if) ") +
                      "will be ignored.", children[i]);
              }
              children.pop();
          }
      }
  }
  function addIfCondition(el, condition) {
      if (!el.ifConditions) {
          el.ifConditions = [];
      }
      el.ifConditions.push(condition);
  }
  function processOnce(el) {
      var once = getAndRemoveAttr(el, 'v-once');
      if (once != null) {
          el.once = true;
      }
  }
  // handle content being passed to a component as slot,
  // e.g. <template slot="xxx">, <div slot-scope="xxx">
  function processSlotContent(el) {
      var slotScope;
      if (el.tag === 'template') {
          slotScope = getAndRemoveAttr(el, 'scope');
          /* istanbul ignore if */
          if (slotScope) {
              warn("the \"scope\" attribute for scoped slots have been deprecated and " +
                  "replaced by \"slot-scope\" since 2.5. The new \"slot-scope\" attribute " +
                  "can also be used on plain elements in addition to <template> to " +
                  "denote scoped slots.", el.rawAttrsMap['scope'], true);
          }
          el.slotScope = slotScope || getAndRemoveAttr(el, 'slot-scope');
      }
      else if ((slotScope = getAndRemoveAttr(el, 'slot-scope'))) {
          /* istanbul ignore if */
          if (el.attrsMap['v-for']) {
              warn("Ambiguous combined usage of slot-scope and v-for on <".concat(el.tag, "> ") +
                  "(v-for takes higher priority). Use a wrapper <template> for the " +
                  "scoped slot to make it clearer.", el.rawAttrsMap['slot-scope'], true);
          }
          el.slotScope = slotScope;
      }
      // slot="xxx"
      var slotTarget = getBindingAttr(el, 'slot');
      if (slotTarget) {
          el.slotTarget = slotTarget === '""' ? '"default"' : slotTarget;
          el.slotTargetDynamic = !!(el.attrsMap[':slot'] || el.attrsMap['v-bind:slot']);
          // preserve slot as an attribute for native shadow DOM compat
          // only for non-scoped slots.
          if (el.tag !== 'template' && !el.slotScope) {
              addAttr(el, 'slot', slotTarget, getRawBindingAttr(el, 'slot'));
          }
      }
      // 2.6 v-slot syntax
      {
          if (el.tag === 'template') {
              // v-slot on <template>
              var slotBinding = getAndRemoveAttrByRegex(el, slotRE);
              if (slotBinding) {
                  {
                      if (el.slotTarget || el.slotScope) {
                          warn("Unexpected mixed usage of different slot syntaxes.", el);
                      }
                      if (el.parent && !maybeComponent(el.parent)) {
                          warn("<template v-slot> can only appear at the root level inside " +
                              "the receiving component", el);
                      }
                  }
                  var _a = getSlotName(slotBinding), name_2 = _a.name, dynamic = _a.dynamic;
                  el.slotTarget = name_2;
                  el.slotTargetDynamic = dynamic;
                  el.slotScope = slotBinding.value || emptySlotScopeToken; // force it into a scoped slot for perf
              }
          }
          else {
              // v-slot on component, denotes default slot
              var slotBinding = getAndRemoveAttrByRegex(el, slotRE);
              if (slotBinding) {
                  {
                      if (!maybeComponent(el)) {
                          warn("v-slot can only be used on components or <template>.", slotBinding);
                      }
                      if (el.slotScope || el.slotTarget) {
                          warn("Unexpected mixed usage of different slot syntaxes.", el);
                      }
                      if (el.scopedSlots) {
                          warn("To avoid scope ambiguity, the default slot should also use " +
                              "<template> syntax when there are other named slots.", slotBinding);
                      }
                  }
                  // add the component's children to its default slot
                  var slots = el.scopedSlots || (el.scopedSlots = {});
                  var _b = getSlotName(slotBinding), name_3 = _b.name, dynamic = _b.dynamic;
                  var slotContainer_1 = (slots[name_3] = createASTElement('template', [], el));
                  slotContainer_1.slotTarget = name_3;
                  slotContainer_1.slotTargetDynamic = dynamic;
                  slotContainer_1.children = el.children.filter(function (c) {
                      if (!c.slotScope) {
                          c.parent = slotContainer_1;
                          return true;
                      }
                  });
                  slotContainer_1.slotScope = slotBinding.value || emptySlotScopeToken;
                  // remove children as they are returned from scopedSlots now
                  el.children = [];
                  // mark el non-plain so data gets generated
                  el.plain = false;
              }
          }
      }
  }
  function getSlotName(binding) {
      var name = binding.name.replace(slotRE, '');
      if (!name) {
          if (binding.name[0] !== '#') {
              name = 'default';
          }
          else {
              warn("v-slot shorthand syntax requires a slot name.", binding);
          }
      }
      return dynamicArgRE.test(name)
          ? // dynamic [name]
              { name: name.slice(1, -1), dynamic: true }
          : // static name
              { name: "\"".concat(name, "\""), dynamic: false };
  }
  // handle <slot/> outlets
  function processSlotOutlet(el) {
      if (el.tag === 'slot') {
          el.slotName = getBindingAttr(el, 'name');
          if (el.key) {
              warn("`key` does not work on <slot> because slots are abstract outlets " +
                  "and can possibly expand into multiple elements. " +
                  "Use the key on a wrapping element instead.", getRawBindingAttr(el, 'key'));
          }
      }
  }
  function processComponent(el) {
      var binding;
      if ((binding = getBindingAttr(el, 'is'))) {
          el.component = binding;
      }
      if (getAndRemoveAttr(el, 'inline-template') != null) {
          el.inlineTemplate = true;
      }
  }
  function processAttrs(el) {
      var list = el.attrsList;
      var i, l, name, rawName, value, modifiers, syncGen, isDynamic;
      for (i = 0, l = list.length; i < l; i++) {
          name = rawName = list[i].name;
          value = list[i].value;
          if (dirRE.test(name)) {
              // mark element as dynamic
              el.hasBindings = true;
              // modifiers
              modifiers = parseModifiers(name.replace(dirRE, ''));
              // support .foo shorthand syntax for the .prop modifier
              if (modifiers) {
                  name = name.replace(modifierRE, '');
              }
              if (bindRE.test(name)) {
                  // v-bind
                  name = name.replace(bindRE, '');
                  value = parseFilters(value);
                  isDynamic = dynamicArgRE.test(name);
                  if (isDynamic) {
                      name = name.slice(1, -1);
                  }
                  if (value.trim().length === 0) {
                      warn("The value for a v-bind expression cannot be empty. Found in \"v-bind:".concat(name, "\""));
                  }
                  if (modifiers) {
                      if (modifiers.prop && !isDynamic) {
                          name = camelize(name);
                          if (name === 'innerHtml')
                              name = 'innerHTML';
                      }
                      if (modifiers.camel && !isDynamic) {
                          name = camelize(name);
                      }
                      if (modifiers.sync) {
                          syncGen = genAssignmentCode(value, "$event");
                          if (!isDynamic) {
                              addHandler(el, "update:".concat(camelize(name)), syncGen, null, false, warn, list[i]);
                              if (hyphenate(name) !== camelize(name)) {
                                  addHandler(el, "update:".concat(hyphenate(name)), syncGen, null, false, warn, list[i]);
                              }
                          }
                          else {
                              // handler w/ dynamic event name
                              addHandler(el, "\"update:\"+(".concat(name, ")"), syncGen, null, false, warn, list[i], true // dynamic
                              );
                          }
                      }
                  }
                  if ((modifiers && modifiers.prop) ||
                      (!el.component && platformMustUseProp(el.tag, el.attrsMap.type, name))) {
                      addProp(el, name, value, list[i], isDynamic);
                  }
                  else {
                      addAttr(el, name, value, list[i], isDynamic);
                  }
              }
              else if (onRE.test(name)) {
                  // v-on
                  name = name.replace(onRE, '');
                  isDynamic = dynamicArgRE.test(name);
                  if (isDynamic) {
                      name = name.slice(1, -1);
                  }
                  addHandler(el, name, value, modifiers, false, warn, list[i], isDynamic);
              }
              else {
                  // normal directives
                  name = name.replace(dirRE, '');
                  // parse arg
                  var argMatch = name.match(argRE);
                  var arg = argMatch && argMatch[1];
                  isDynamic = false;
                  if (arg) {
                      name = name.slice(0, -(arg.length + 1));
                      if (dynamicArgRE.test(arg)) {
                          arg = arg.slice(1, -1);
                          isDynamic = true;
                      }
                  }
                  addDirective(el, name, rawName, value, arg, isDynamic, modifiers, list[i]);
                  if (name === 'model') {
                      checkForAliasModel(el, value);
                  }
              }
          }
          else {
              // literal attribute
              {
                  var res = parseText(value, delimiters);
                  if (res) {
                      warn("".concat(name, "=\"").concat(value, "\": ") +
                          'Interpolation inside attributes has been removed. ' +
                          'Use v-bind or the colon shorthand instead. For example, ' +
                          'instead of <div id="{{ val }}">, use <div :id="val">.', list[i]);
                  }
              }
              addAttr(el, name, JSON.stringify(value), list[i]);
              // #6887 firefox doesn't update muted state if set via attribute
              // even immediately after element creation
              if (!el.component &&
                  name === 'muted' &&
                  platformMustUseProp(el.tag, el.attrsMap.type, name)) {
                  addProp(el, name, 'true', list[i]);
              }
          }
      }
  }
  function checkInFor(el) {
      var parent = el;
      while (parent) {
          if (parent.for !== undefined) {
              return true;
          }
          parent = parent.parent;
      }
      return false;
  }
  function parseModifiers(name) {
      var match = name.match(modifierRE);
      if (match) {
          var ret_1 = {};
          match.forEach(function (m) {
              ret_1[m.slice(1)] = true;
          });
          return ret_1;
      }
  }
  function makeAttrsMap(attrs) {
      var map = {};
      for (var i = 0, l = attrs.length; i < l; i++) {
          if (map[attrs[i].name] && !isIE && !isEdge) {
              warn('duplicate attribute: ' + attrs[i].name, attrs[i]);
          }
          map[attrs[i].name] = attrs[i].value;
      }
      return map;
  }
  // for script (e.g. type="x/template") or style, do not decode content
  function isTextTag(el) {
      return el.tag === 'script' || el.tag === 'style';
  }
  function isForbiddenTag(el) {
      return (el.tag === 'style' ||
          (el.tag === 'script' &&
              (!el.attrsMap.type || el.attrsMap.type === 'text/javascript')));
  }
  var ieNSBug = /^xmlns:NS\d+/;
  var ieNSPrefix = /^NS\d+:/;
  /* istanbul ignore next */
  function guardIESVGBug(attrs) {
      var res = [];
      for (var i = 0; i < attrs.length; i++) {
          var attr = attrs[i];
          if (!ieNSBug.test(attr.name)) {
              attr.name = attr.name.replace(ieNSPrefix, '');
              res.push(attr);
          }
      }
      return res;
  }
  function checkForAliasModel(el, value) {
      var _el = el;
      while (_el) {
          if (_el.for && _el.alias === value) {
              warn("<".concat(el.tag, " v-model=\"").concat(value, "\">: ") +
                  "You are binding v-model directly to a v-for iteration alias. " +
                  "This will not be able to modify the v-for source array because " +
                  "writing to the alias is like modifying a function local variable. " +
                  "Consider using an array of objects and use v-model on an object property instead.", el.rawAttrsMap['v-model']);
          }
          _el = _el.parent;
      }
  }

  /**
   * Expand input[v-model] with dynamic type bindings into v-if-else chains
   * Turn this:
   *   <input v-model="data[type]" :type="type">
   * into this:
   *   <input v-if="type === 'checkbox'" type="checkbox" v-model="data[type]">
   *   <input v-else-if="type === 'radio'" type="radio" v-model="data[type]">
   *   <input v-else :type="type" v-model="data[type]">
   */
  function preTransformNode(el, options) {
      if (el.tag === 'input') {
          var map = el.attrsMap;
          if (!map['v-model']) {
              return;
          }
          var typeBinding = void 0;
          if (map[':type'] || map['v-bind:type']) {
              typeBinding = getBindingAttr(el, 'type');
          }
          if (!map.type && !typeBinding && map['v-bind']) {
              typeBinding = "(".concat(map['v-bind'], ").type");
          }
          if (typeBinding) {
              var ifCondition = getAndRemoveAttr(el, 'v-if', true);
              var ifConditionExtra = ifCondition ? "&&(".concat(ifCondition, ")") : "";
              var hasElse = getAndRemoveAttr(el, 'v-else', true) != null;
              var elseIfCondition = getAndRemoveAttr(el, 'v-else-if', true);
              // 1. checkbox
              var branch0 = cloneASTElement(el);
              // process for on the main node
              processFor(branch0);
              addRawAttr(branch0, 'type', 'checkbox');
              processElement(branch0, options);
              branch0.processed = true; // prevent it from double-processed
              branch0.if = "(".concat(typeBinding, ")==='checkbox'") + ifConditionExtra;
              addIfCondition(branch0, {
                  exp: branch0.if,
                  block: branch0
              });
              // 2. add radio else-if condition
              var branch1 = cloneASTElement(el);
              getAndRemoveAttr(branch1, 'v-for', true);
              addRawAttr(branch1, 'type', 'radio');
              processElement(branch1, options);
              addIfCondition(branch0, {
                  exp: "(".concat(typeBinding, ")==='radio'") + ifConditionExtra,
                  block: branch1
              });
              // 3. other
              var branch2 = cloneASTElement(el);
              getAndRemoveAttr(branch2, 'v-for', true);
              addRawAttr(branch2, ':type', typeBinding);
              processElement(branch2, options);
              addIfCondition(branch0, {
                  exp: ifCondition,
                  block: branch2
              });
              if (hasElse) {
                  branch0.else = true;
              }
              else if (elseIfCondition) {
                  branch0.elseif = elseIfCondition;
              }
              return branch0;
          }
      }
  }
  function cloneASTElement(el) {
      return createASTElement(el.tag, el.attrsList.slice(), el.parent);
  }
  var model = {
      preTransformNode: preTransformNode
  };

  var modules = [klass, style, model];

  function text(el, dir) {
      if (dir.value) {
          addProp(el, 'textContent', "_s(".concat(dir.value, ")"), dir);
      }
  }

  function html(el, dir) {
      if (dir.value) {
          addProp(el, 'innerHTML', "_s(".concat(dir.value, ")"), dir);
      }
  }

  var directives = {
      model: model$1,
      text: text,
      html: html
  };

  var baseOptions = {
      expectHTML: true,
      modules: modules,
      directives: directives,
      isPreTag: isPreTag,
      isUnaryTag: isUnaryTag,
      mustUseProp: mustUseProp,
      canBeLeftOpenTag: canBeLeftOpenTag,
      isReservedTag: isReservedTag,
      getTagNamespace: getTagNamespace,
      staticKeys: genStaticKeys$1(modules)
  };

  var isStaticKey;
  var isPlatformReservedTag;
  var genStaticKeysCached = cached(genStaticKeys);
  /**
   * Goal of the optimizer: walk the generated template AST tree
   * and detect sub-trees that are purely static, i.e. parts of
   * the DOM that never needs to change.
   *
   * Once we detect these sub-trees, we can:
   *
   * 1. Hoist them into constants, so that we no longer need to
   *    create fresh nodes for them on each re-render;
   * 2. Completely skip them in the patching process.
   */
  function optimize(root, options) {
      if (!root)
          return;
      isStaticKey = genStaticKeysCached(options.staticKeys || '');
      isPlatformReservedTag = options.isReservedTag || no;
      // first pass: mark all non-static nodes.
      markStatic(root);
      // second pass: mark static roots.
      markStaticRoots(root, false);
  }
  function genStaticKeys(keys) {
      return makeMap('type,tag,attrsList,attrsMap,plain,parent,children,attrs,start,end,rawAttrsMap' +
          (keys ? ',' + keys : ''));
  }
  function markStatic(node) {
      node.static = isStatic(node);
      if (node.type === 1) {
          // do not make component slot content static. this avoids
          // 1. components not able to mutate slot nodes
          // 2. static slot content fails for hot-reloading
          if (!isPlatformReservedTag(node.tag) &&
              node.tag !== 'slot' &&
              node.attrsMap['inline-template'] == null) {
              return;
          }
          for (var i = 0, l = node.children.length; i < l; i++) {
              var child = node.children[i];
              markStatic(child);
              if (!child.static) {
                  node.static = false;
              }
          }
          if (node.ifConditions) {
              for (var i = 1, l = node.ifConditions.length; i < l; i++) {
                  var block = node.ifConditions[i].block;
                  markStatic(block);
                  if (!block.static) {
                      node.static = false;
                  }
              }
          }
      }
  }
  function markStaticRoots(node, isInFor) {
      if (node.type === 1) {
          if (node.static || node.once) {
              node.staticInFor = isInFor;
          }
          // For a node to qualify as a static root, it should have children that
          // are not just static text. Otherwise the cost of hoisting out will
          // outweigh the benefits and it's better off to just always render it fresh.
          if (node.static &&
              node.children.length &&
              !(node.children.length === 1 && node.children[0].type === 3)) {
              node.staticRoot = true;
              return;
          }
          else {
              node.staticRoot = false;
          }
          if (node.children) {
              for (var i = 0, l = node.children.length; i < l; i++) {
                  markStaticRoots(node.children[i], isInFor || !!node.for);
              }
          }
          if (node.ifConditions) {
              for (var i = 1, l = node.ifConditions.length; i < l; i++) {
                  markStaticRoots(node.ifConditions[i].block, isInFor);
              }
          }
      }
  }
  function isStatic(node) {
      if (node.type === 2) {
          // expression
          return false;
      }
      if (node.type === 3) {
          // text
          return true;
      }
      return !!(node.pre ||
          (!node.hasBindings && // no dynamic bindings
              !node.if &&
              !node.for && // not v-if or v-for or v-else
              !isBuiltInTag(node.tag) && // not a built-in
              isPlatformReservedTag(node.tag) && // not a component
              !isDirectChildOfTemplateFor(node) &&
              Object.keys(node).every(isStaticKey)));
  }
  function isDirectChildOfTemplateFor(node) {
      while (node.parent) {
          node = node.parent;
          if (node.tag !== 'template') {
              return false;
          }
          if (node.for) {
              return true;
          }
      }
      return false;
  }

  var fnExpRE = /^([\w$_]+|\([^)]*?\))\s*=>|^function(?:\s+[\w$]+)?\s*\(/;
  var fnInvokeRE = /\([^)]*?\);*$/;
  var simplePathRE = /^[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*|\['[^']*?']|\["[^"]*?"]|\[\d+]|\[[A-Za-z_$][\w$]*])*$/;
  // KeyboardEvent.keyCode aliases
  var keyCodes = {
      esc: 27,
      tab: 9,
      enter: 13,
      space: 32,
      up: 38,
      left: 37,
      right: 39,
      down: 40,
      delete: [8, 46]
  };
  // KeyboardEvent.key aliases
  var keyNames = {
      // #7880: IE11 and Edge use `Esc` for Escape key name.
      esc: ['Esc', 'Escape'],
      tab: 'Tab',
      enter: 'Enter',
      // #9112: IE11 uses `Spacebar` for Space key name.
      space: [' ', 'Spacebar'],
      // #7806: IE11 uses key names without `Arrow` prefix for arrow keys.
      up: ['Up', 'ArrowUp'],
      left: ['Left', 'ArrowLeft'],
      right: ['Right', 'ArrowRight'],
      down: ['Down', 'ArrowDown'],
      // #9112: IE11 uses `Del` for Delete key name.
      delete: ['Backspace', 'Delete', 'Del']
  };
  // #4868: modifiers that prevent the execution of the listener
  // need to explicitly return null so that we can determine whether to remove
  // the listener for .once
  var genGuard = function (condition) { return "if(".concat(condition, ")return null;"); };
  var modifierCode = {
      stop: '$event.stopPropagation();',
      prevent: '$event.preventDefault();',
      self: genGuard("$event.target !== $event.currentTarget"),
      ctrl: genGuard("!$event.ctrlKey"),
      shift: genGuard("!$event.shiftKey"),
      alt: genGuard("!$event.altKey"),
      meta: genGuard("!$event.metaKey"),
      left: genGuard("'button' in $event && $event.button !== 0"),
      middle: genGuard("'button' in $event && $event.button !== 1"),
      right: genGuard("'button' in $event && $event.button !== 2")
  };
  function genHandlers(events, isNative) {
      var prefix = isNative ? 'nativeOn:' : 'on:';
      var staticHandlers = "";
      var dynamicHandlers = "";
      for (var name_1 in events) {
          var handlerCode = genHandler(events[name_1]);
          //@ts-expect-error
          if (events[name_1] && events[name_1].dynamic) {
              dynamicHandlers += "".concat(name_1, ",").concat(handlerCode, ",");
          }
          else {
              staticHandlers += "\"".concat(name_1, "\":").concat(handlerCode, ",");
          }
      }
      staticHandlers = "{".concat(staticHandlers.slice(0, -1), "}");
      if (dynamicHandlers) {
          return prefix + "_d(".concat(staticHandlers, ",[").concat(dynamicHandlers.slice(0, -1), "])");
      }
      else {
          return prefix + staticHandlers;
      }
  }
  function genHandler(handler) {
      if (!handler) {
          return 'function(){}';
      }
      if (Array.isArray(handler)) {
          return "[".concat(handler.map(function (handler) { return genHandler(handler); }).join(','), "]");
      }
      var isMethodPath = simplePathRE.test(handler.value);
      var isFunctionExpression = fnExpRE.test(handler.value);
      var isFunctionInvocation = simplePathRE.test(handler.value.replace(fnInvokeRE, ''));
      if (!handler.modifiers) {
          if (isMethodPath || isFunctionExpression) {
              return handler.value;
          }
          return "function($event){".concat(isFunctionInvocation ? "return ".concat(handler.value) : handler.value, "}"); // inline statement
      }
      else {
          var code = '';
          var genModifierCode = '';
          var keys = [];
          var _loop_1 = function (key) {
              if (modifierCode[key]) {
                  genModifierCode += modifierCode[key];
                  // left/right
                  if (keyCodes[key]) {
                      keys.push(key);
                  }
              }
              else if (key === 'exact') {
                  var modifiers_1 = handler.modifiers;
                  genModifierCode += genGuard(['ctrl', 'shift', 'alt', 'meta']
                      .filter(function (keyModifier) { return !modifiers_1[keyModifier]; })
                      .map(function (keyModifier) { return "$event.".concat(keyModifier, "Key"); })
                      .join('||'));
              }
              else {
                  keys.push(key);
              }
          };
          for (var key in handler.modifiers) {
              _loop_1(key);
          }
          if (keys.length) {
              code += genKeyFilter(keys);
          }
          // Make sure modifiers like prevent and stop get executed after key filtering
          if (genModifierCode) {
              code += genModifierCode;
          }
          var handlerCode = isMethodPath
              ? "return ".concat(handler.value, ".apply(null, arguments)")
              : isFunctionExpression
                  ? "return (".concat(handler.value, ").apply(null, arguments)")
                  : isFunctionInvocation
                      ? "return ".concat(handler.value)
                      : handler.value;
          return "function($event){".concat(code).concat(handlerCode, "}");
      }
  }
  function genKeyFilter(keys) {
      return (
      // make sure the key filters only apply to KeyboardEvents
      // #9441: can't use 'keyCode' in $event because Chrome autofill fires fake
      // key events that do not have keyCode property...
      "if(!$event.type.indexOf('key')&&" +
          "".concat(keys.map(genFilterCode).join('&&'), ")return null;"));
  }
  function genFilterCode(key) {
      var keyVal = parseInt(key, 10);
      if (keyVal) {
          return "$event.keyCode!==".concat(keyVal);
      }
      var keyCode = keyCodes[key];
      var keyName = keyNames[key];
      return ("_k($event.keyCode," +
          "".concat(JSON.stringify(key), ",") +
          "".concat(JSON.stringify(keyCode), ",") +
          "$event.key," +
          "".concat(JSON.stringify(keyName)) +
          ")");
  }

  function on(el, dir) {
      if (dir.modifiers) {
          warn$2("v-on without argument does not support modifiers.");
      }
      el.wrapListeners = function (code) { return "_g(".concat(code, ",").concat(dir.value, ")"); };
  }

  function bind(el, dir) {
      el.wrapData = function (code) {
          return "_b(".concat(code, ",'").concat(el.tag, "',").concat(dir.value, ",").concat(dir.modifiers && dir.modifiers.prop ? 'true' : 'false').concat(dir.modifiers && dir.modifiers.sync ? ',true' : '', ")");
      };
  }

  var baseDirectives = {
      on: on,
      bind: bind,
      cloak: noop
  };

  var CodegenState = /** @class */ (function () {
      function CodegenState(options) {
          this.options = options;
          this.warn = options.warn || baseWarn;
          this.transforms = pluckModuleFunction(options.modules, 'transformCode');
          this.dataGenFns = pluckModuleFunction(options.modules, 'genData');
          this.directives = extend(extend({}, baseDirectives), options.directives);
          var isReservedTag = options.isReservedTag || no;
          this.maybeComponent = function (el) {
              return !!el.component || !isReservedTag(el.tag);
          };
          this.onceId = 0;
          this.staticRenderFns = [];
          this.pre = false;
      }
      return CodegenState;
  }());
  function generate(ast, options) {
      var state = new CodegenState(options);
      // fix #11483, Root level <script> tags should not be rendered.
      var code = ast
          ? ast.tag === 'script'
              ? 'null'
              : genElement(ast, state)
          : '_c("div")';
      return {
          render: "with(this){return ".concat(code, "}"),
          staticRenderFns: state.staticRenderFns
      };
  }
  function genElement(el, state) {
      if (el.parent) {
          el.pre = el.pre || el.parent.pre;
      }
      if (el.staticRoot && !el.staticProcessed) {
          return genStatic(el, state);
      }
      else if (el.once && !el.onceProcessed) {
          return genOnce(el, state);
      }
      else if (el.for && !el.forProcessed) {
          return genFor(el, state);
      }
      else if (el.if && !el.ifProcessed) {
          return genIf(el, state);
      }
      else if (el.tag === 'template' && !el.slotTarget && !state.pre) {
          return genChildren(el, state) || 'void 0';
      }
      else if (el.tag === 'slot') {
          return genSlot(el, state);
      }
      else {
          // component or element
          var code = void 0;
          if (el.component) {
              code = genComponent(el.component, el, state);
          }
          else {
              var data = void 0;
              var maybeComponent = state.maybeComponent(el);
              if (!el.plain || (el.pre && maybeComponent)) {
                  data = genData(el, state);
              }
              var tag 
              // check if this is a component in <script setup>
              = void 0;
              // check if this is a component in <script setup>
              var bindings = state.options.bindings;
              if (maybeComponent && bindings && bindings.__isScriptSetup !== false) {
                  tag = checkBindingType(bindings, el.tag);
              }
              if (!tag)
                  tag = "'".concat(el.tag, "'");
              var children = el.inlineTemplate ? null : genChildren(el, state, true);
              code = "_c(".concat(tag).concat(data ? ",".concat(data) : '' // data
              ).concat(children ? ",".concat(children) : '' // children
              , ")");
          }
          // module transforms
          for (var i = 0; i < state.transforms.length; i++) {
              code = state.transforms[i](el, code);
          }
          return code;
      }
  }
  function checkBindingType(bindings, key) {
      var camelName = camelize(key);
      var PascalName = capitalize(camelName);
      var checkType = function (type) {
          if (bindings[key] === type) {
              return key;
          }
          if (bindings[camelName] === type) {
              return camelName;
          }
          if (bindings[PascalName] === type) {
              return PascalName;
          }
      };
      var fromConst = checkType("setup-const" /* BindingTypes.SETUP_CONST */) ||
          checkType("setup-reactive-const" /* BindingTypes.SETUP_REACTIVE_CONST */);
      if (fromConst) {
          return fromConst;
      }
      var fromMaybeRef = checkType("setup-let" /* BindingTypes.SETUP_LET */) ||
          checkType("setup-ref" /* BindingTypes.SETUP_REF */) ||
          checkType("setup-maybe-ref" /* BindingTypes.SETUP_MAYBE_REF */);
      if (fromMaybeRef) {
          return fromMaybeRef;
      }
  }
  // hoist static sub-trees out
  function genStatic(el, state) {
      el.staticProcessed = true;
      // Some elements (templates) need to behave differently inside of a v-pre
      // node.  All pre nodes are static roots, so we can use this as a location to
      // wrap a state change and reset it upon exiting the pre node.
      var originalPreState = state.pre;
      if (el.pre) {
          state.pre = el.pre;
      }
      state.staticRenderFns.push("with(this){return ".concat(genElement(el, state), "}"));
      state.pre = originalPreState;
      return "_m(".concat(state.staticRenderFns.length - 1).concat(el.staticInFor ? ',true' : '', ")");
  }
  // v-once
  function genOnce(el, state) {
      el.onceProcessed = true;
      if (el.if && !el.ifProcessed) {
          return genIf(el, state);
      }
      else if (el.staticInFor) {
          var key = '';
          var parent_1 = el.parent;
          while (parent_1) {
              if (parent_1.for) {
                  key = parent_1.key;
                  break;
              }
              parent_1 = parent_1.parent;
          }
          if (!key) {
              state.warn("v-once can only be used inside v-for that is keyed. ", el.rawAttrsMap['v-once']);
              return genElement(el, state);
          }
          return "_o(".concat(genElement(el, state), ",").concat(state.onceId++, ",").concat(key, ")");
      }
      else {
          return genStatic(el, state);
      }
  }
  function genIf(el, state, altGen, altEmpty) {
      el.ifProcessed = true; // avoid recursion
      return genIfConditions(el.ifConditions.slice(), state, altGen, altEmpty);
  }
  function genIfConditions(conditions, state, altGen, altEmpty) {
      if (!conditions.length) {
          return altEmpty || '_e()';
      }
      var condition = conditions.shift();
      if (condition.exp) {
          return "(".concat(condition.exp, ")?").concat(genTernaryExp(condition.block), ":").concat(genIfConditions(conditions, state, altGen, altEmpty));
      }
      else {
          return "".concat(genTernaryExp(condition.block));
      }
      // v-if with v-once should generate code like (a)?_m(0):_m(1)
      function genTernaryExp(el) {
          return altGen
              ? altGen(el, state)
              : el.once
                  ? genOnce(el, state)
                  : genElement(el, state);
      }
  }
  function genFor(el, state, altGen, altHelper) {
      var exp = el.for;
      var alias = el.alias;
      var iterator1 = el.iterator1 ? ",".concat(el.iterator1) : '';
      var iterator2 = el.iterator2 ? ",".concat(el.iterator2) : '';
      if (state.maybeComponent(el) &&
          el.tag !== 'slot' &&
          el.tag !== 'template' &&
          !el.key) {
          state.warn("<".concat(el.tag, " v-for=\"").concat(alias, " in ").concat(exp, "\">: component lists rendered with ") +
              "v-for should have explicit keys. " +
              "See https://v2.vuejs.org/v2/guide/list.html#key for more info.", el.rawAttrsMap['v-for'], true /* tip */);
      }
      el.forProcessed = true; // avoid recursion
      return ("".concat(altHelper || '_l', "((").concat(exp, "),") +
          "function(".concat(alias).concat(iterator1).concat(iterator2, "){") +
          "return ".concat((altGen || genElement)(el, state)) +
          '})');
  }
  function genData(el, state) {
      var data = '{';
      // directives first.
      // directives may mutate the el's other properties before they are generated.
      var dirs = genDirectives(el, state);
      if (dirs)
          data += dirs + ',';
      // key
      if (el.key) {
          data += "key:".concat(el.key, ",");
      }
      // ref
      if (el.ref) {
          data += "ref:".concat(el.ref, ",");
      }
      if (el.refInFor) {
          data += "refInFor:true,";
      }
      // pre
      if (el.pre) {
          data += "pre:true,";
      }
      // record original tag name for components using "is" attribute
      if (el.component) {
          data += "tag:\"".concat(el.tag, "\",");
      }
      // module data generation functions
      for (var i = 0; i < state.dataGenFns.length; i++) {
          data += state.dataGenFns[i](el);
      }
      // attributes
      if (el.attrs) {
          data += "attrs:".concat(genProps(el.attrs), ",");
      }
      // DOM props
      if (el.props) {
          data += "domProps:".concat(genProps(el.props), ",");
      }
      // event handlers
      if (el.events) {
          data += "".concat(genHandlers(el.events, false), ",");
      }
      if (el.nativeEvents) {
          data += "".concat(genHandlers(el.nativeEvents, true), ",");
      }
      // slot target
      // only for non-scoped slots
      if (el.slotTarget && !el.slotScope) {
          data += "slot:".concat(el.slotTarget, ",");
      }
      // scoped slots
      if (el.scopedSlots) {
          data += "".concat(genScopedSlots(el, el.scopedSlots, state), ",");
      }
      // component v-model
      if (el.model) {
          data += "model:{value:".concat(el.model.value, ",callback:").concat(el.model.callback, ",expression:").concat(el.model.expression, "},");
      }
      // inline-template
      if (el.inlineTemplate) {
          var inlineTemplate = genInlineTemplate(el, state);
          if (inlineTemplate) {
              data += "".concat(inlineTemplate, ",");
          }
      }
      data = data.replace(/,$/, '') + '}';
      // v-bind dynamic argument wrap
      // v-bind with dynamic arguments must be applied using the same v-bind object
      // merge helper so that class/style/mustUseProp attrs are handled correctly.
      if (el.dynamicAttrs) {
          data = "_b(".concat(data, ",\"").concat(el.tag, "\",").concat(genProps(el.dynamicAttrs), ")");
      }
      // v-bind data wrap
      if (el.wrapData) {
          data = el.wrapData(data);
      }
      // v-on data wrap
      if (el.wrapListeners) {
          data = el.wrapListeners(data);
      }
      return data;
  }
  function genDirectives(el, state) {
      var dirs = el.directives;
      if (!dirs)
          return;
      var res = 'directives:[';
      var hasRuntime = false;
      var i, l, dir, needRuntime;
      for (i = 0, l = dirs.length; i < l; i++) {
          dir = dirs[i];
          needRuntime = true;
          var gen = state.directives[dir.name];
          if (gen) {
              // compile-time directive that manipulates AST.
              // returns true if it also needs a runtime counterpart.
              needRuntime = !!gen(el, dir, state.warn);
          }
          if (needRuntime) {
              hasRuntime = true;
              res += "{name:\"".concat(dir.name, "\",rawName:\"").concat(dir.rawName, "\"").concat(dir.value
                  ? ",value:(".concat(dir.value, "),expression:").concat(JSON.stringify(dir.value))
                  : '').concat(dir.arg ? ",arg:".concat(dir.isDynamicArg ? dir.arg : "\"".concat(dir.arg, "\"")) : '').concat(dir.modifiers ? ",modifiers:".concat(JSON.stringify(dir.modifiers)) : '', "},");
          }
      }
      if (hasRuntime) {
          return res.slice(0, -1) + ']';
      }
  }
  function genInlineTemplate(el, state) {
      var ast = el.children[0];
      if ((el.children.length !== 1 || ast.type !== 1)) {
          state.warn('Inline-template components must have exactly one child element.', { start: el.start });
      }
      if (ast && ast.type === 1) {
          var inlineRenderFns = generate(ast, state.options);
          return "inlineTemplate:{render:function(){".concat(inlineRenderFns.render, "},staticRenderFns:[").concat(inlineRenderFns.staticRenderFns
              .map(function (code) { return "function(){".concat(code, "}"); })
              .join(','), "]}");
      }
  }
  function genScopedSlots(el, slots, state) {
      // by default scoped slots are considered "stable", this allows child
      // components with only scoped slots to skip forced updates from parent.
      // but in some cases we have to bail-out of this optimization
      // for example if the slot contains dynamic names, has v-if or v-for on them...
      var needsForceUpdate = el.for ||
          Object.keys(slots).some(function (key) {
              var slot = slots[key];
              return (slot.slotTargetDynamic || slot.if || slot.for || containsSlotChild(slot) // is passing down slot from parent which may be dynamic
              );
          });
      // #9534: if a component with scoped slots is inside a conditional branch,
      // it's possible for the same component to be reused but with different
      // compiled slot content. To avoid that, we generate a unique key based on
      // the generated code of all the slot contents.
      var needsKey = !!el.if;
      // OR when it is inside another scoped slot or v-for (the reactivity may be
      // disconnected due to the intermediate scope variable)
      // #9438, #9506
      // TODO: this can be further optimized by properly analyzing in-scope bindings
      // and skip force updating ones that do not actually use scope variables.
      if (!needsForceUpdate) {
          var parent_2 = el.parent;
          while (parent_2) {
              if ((parent_2.slotScope && parent_2.slotScope !== emptySlotScopeToken) ||
                  parent_2.for) {
                  needsForceUpdate = true;
                  break;
              }
              if (parent_2.if) {
                  needsKey = true;
              }
              parent_2 = parent_2.parent;
          }
      }
      var generatedSlots = Object.keys(slots)
          .map(function (key) { return genScopedSlot(slots[key], state); })
          .join(',');
      return "scopedSlots:_u([".concat(generatedSlots, "]").concat(needsForceUpdate ? ",null,true" : "").concat(!needsForceUpdate && needsKey ? ",null,false,".concat(hash(generatedSlots)) : "", ")");
  }
  function hash(str) {
      var hash = 5381;
      var i = str.length;
      while (i) {
          hash = (hash * 33) ^ str.charCodeAt(--i);
      }
      return hash >>> 0;
  }
  function containsSlotChild(el) {
      if (el.type === 1) {
          if (el.tag === 'slot') {
              return true;
          }
          return el.children.some(containsSlotChild);
      }
      return false;
  }
  function genScopedSlot(el, state) {
      var isLegacySyntax = el.attrsMap['slot-scope'];
      if (el.if && !el.ifProcessed && !isLegacySyntax) {
          return genIf(el, state, genScopedSlot, "null");
      }
      if (el.for && !el.forProcessed) {
          return genFor(el, state, genScopedSlot);
      }
      var slotScope = el.slotScope === emptySlotScopeToken ? "" : String(el.slotScope);
      var fn = "function(".concat(slotScope, "){") +
          "return ".concat(el.tag === 'template'
              ? el.if && isLegacySyntax
                  ? "(".concat(el.if, ")?").concat(genChildren(el, state) || 'undefined', ":undefined")
                  : genChildren(el, state) || 'undefined'
              : genElement(el, state), "}");
      // reverse proxy v-slot without scope on this.$slots
      var reverseProxy = slotScope ? "" : ",proxy:true";
      return "{key:".concat(el.slotTarget || "\"default\"", ",fn:").concat(fn).concat(reverseProxy, "}");
  }
  function genChildren(el, state, checkSkip, altGenElement, altGenNode) {
      var children = el.children;
      if (children.length) {
          var el_1 = children[0];
          // optimize single v-for
          if (children.length === 1 &&
              el_1.for &&
              el_1.tag !== 'template' &&
              el_1.tag !== 'slot') {
              var normalizationType_1 = checkSkip
                  ? state.maybeComponent(el_1)
                      ? ",1"
                      : ",0"
                  : "";
              return "".concat((altGenElement || genElement)(el_1, state)).concat(normalizationType_1);
          }
          var normalizationType = checkSkip
              ? getNormalizationType(children, state.maybeComponent)
              : 0;
          var gen_1 = altGenNode || genNode;
          return "[".concat(children.map(function (c) { return gen_1(c, state); }).join(','), "]").concat(normalizationType ? ",".concat(normalizationType) : '');
      }
  }
  // determine the normalization needed for the children array.
  // 0: no normalization needed
  // 1: simple normalization needed (possible 1-level deep nested array)
  // 2: full normalization needed
  function getNormalizationType(children, maybeComponent) {
      var res = 0;
      for (var i = 0; i < children.length; i++) {
          var el = children[i];
          if (el.type !== 1) {
              continue;
          }
          if (needsNormalization(el) ||
              (el.ifConditions &&
                  el.ifConditions.some(function (c) { return needsNormalization(c.block); }))) {
              res = 2;
              break;
          }
          if (maybeComponent(el) ||
              (el.ifConditions && el.ifConditions.some(function (c) { return maybeComponent(c.block); }))) {
              res = 1;
          }
      }
      return res;
  }
  function needsNormalization(el) {
      return el.for !== undefined || el.tag === 'template' || el.tag === 'slot';
  }
  function genNode(node, state) {
      if (node.type === 1) {
          return genElement(node, state);
      }
      else if (node.type === 3 && node.isComment) {
          return genComment(node);
      }
      else {
          return genText(node);
      }
  }
  function genText(text) {
      return "_v(".concat(text.type === 2
          ? text.expression // no need for () because already wrapped in _s()
          : transformSpecialNewlines(JSON.stringify(text.text)), ")");
  }
  function genComment(comment) {
      return "_e(".concat(JSON.stringify(comment.text), ")");
  }
  function genSlot(el, state) {
      var slotName = el.slotName || '"default"';
      var children = genChildren(el, state);
      var res = "_t(".concat(slotName).concat(children ? ",function(){return ".concat(children, "}") : '');
      var attrs = el.attrs || el.dynamicAttrs
          ? genProps((el.attrs || []).concat(el.dynamicAttrs || []).map(function (attr) { return ({
              // slot props are camelized
              name: camelize(attr.name),
              value: attr.value,
              dynamic: attr.dynamic
          }); }))
          : null;
      var bind = el.attrsMap['v-bind'];
      if ((attrs || bind) && !children) {
          res += ",null";
      }
      if (attrs) {
          res += ",".concat(attrs);
      }
      if (bind) {
          res += "".concat(attrs ? '' : ',null', ",").concat(bind);
      }
      return res + ')';
  }
  // componentName is el.component, take it as argument to shun flow's pessimistic refinement
  function genComponent(componentName, el, state) {
      var children = el.inlineTemplate ? null : genChildren(el, state, true);
      return "_c(".concat(componentName, ",").concat(genData(el, state)).concat(children ? ",".concat(children) : '', ")");
  }
  function genProps(props) {
      var staticProps = "";
      var dynamicProps = "";
      for (var i = 0; i < props.length; i++) {
          var prop = props[i];
          var value = transformSpecialNewlines(prop.value);
          if (prop.dynamic) {
              dynamicProps += "".concat(prop.name, ",").concat(value, ",");
          }
          else {
              staticProps += "\"".concat(prop.name, "\":").concat(value, ",");
          }
      }
      staticProps = "{".concat(staticProps.slice(0, -1), "}");
      if (dynamicProps) {
          return "_d(".concat(staticProps, ",[").concat(dynamicProps.slice(0, -1), "])");
      }
      else {
          return staticProps;
      }
  }
  // #3895, #4268
  function transformSpecialNewlines(text) {
      return text.replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
  }

  // these keywords should not appear inside expressions, but operators like
  // typeof, instanceof and in are allowed
  var prohibitedKeywordRE = new RegExp('\\b' +
      ('do,if,for,let,new,try,var,case,else,with,await,break,catch,class,const,' +
          'super,throw,while,yield,delete,export,import,return,switch,default,' +
          'extends,finally,continue,debugger,function,arguments')
          .split(',')
          .join('\\b|\\b') +
      '\\b');
  // these unary operators should not be used as property/method names
  var unaryOperatorsRE = new RegExp('\\b' +
      'delete,typeof,void'.split(',').join('\\s*\\([^\\)]*\\)|\\b') +
      '\\s*\\([^\\)]*\\)');
  // strip strings in expressions
  var stripStringRE = /'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"|`(?:[^`\\]|\\.)*\$\{|\}(?:[^`\\]|\\.)*`|`(?:[^`\\]|\\.)*`/g;
  // detect problematic expressions in a template
  function detectErrors(ast, warn) {
      if (ast) {
          checkNode(ast, warn);
      }
  }
  function checkNode(node, warn) {
      if (node.type === 1) {
          for (var name_1 in node.attrsMap) {
              if (dirRE.test(name_1)) {
                  var value = node.attrsMap[name_1];
                  if (value) {
                      var range = node.rawAttrsMap[name_1];
                      if (name_1 === 'v-for') {
                          checkFor(node, "v-for=\"".concat(value, "\""), warn, range);
                      }
                      else if (name_1 === 'v-slot' || name_1[0] === '#') {
                          checkFunctionParameterExpression(value, "".concat(name_1, "=\"").concat(value, "\""), warn, range);
                      }
                      else if (onRE.test(name_1)) {
                          checkEvent(value, "".concat(name_1, "=\"").concat(value, "\""), warn, range);
                      }
                      else {
                          checkExpression(value, "".concat(name_1, "=\"").concat(value, "\""), warn, range);
                      }
                  }
              }
          }
          if (node.children) {
              for (var i = 0; i < node.children.length; i++) {
                  checkNode(node.children[i], warn);
              }
          }
      }
      else if (node.type === 2) {
          checkExpression(node.expression, node.text, warn, node);
      }
  }
  function checkEvent(exp, text, warn, range) {
      var stripped = exp.replace(stripStringRE, '');
      var keywordMatch = stripped.match(unaryOperatorsRE);
      if (keywordMatch && stripped.charAt(keywordMatch.index - 1) !== '$') {
          warn("avoid using JavaScript unary operator as property name: " +
              "\"".concat(keywordMatch[0], "\" in expression ").concat(text.trim()), range);
      }
      checkExpression(exp, text, warn, range);
  }
  function checkFor(node, text, warn, range) {
      checkExpression(node.for || '', text, warn, range);
      checkIdentifier(node.alias, 'v-for alias', text, warn, range);
      checkIdentifier(node.iterator1, 'v-for iterator', text, warn, range);
      checkIdentifier(node.iterator2, 'v-for iterator', text, warn, range);
  }
  function checkIdentifier(ident, type, text, warn, range) {
      if (typeof ident === 'string') {
          try {
              new Function("var ".concat(ident, "=_"));
          }
          catch (e) {
              warn("invalid ".concat(type, " \"").concat(ident, "\" in expression: ").concat(text.trim()), range);
          }
      }
  }
  function checkExpression(exp, text, warn, range) {
      try {
          new Function("return ".concat(exp));
      }
      catch (e) {
          var keywordMatch = exp
              .replace(stripStringRE, '')
              .match(prohibitedKeywordRE);
          if (keywordMatch) {
              warn("avoid using JavaScript keyword as property name: " +
                  "\"".concat(keywordMatch[0], "\"\n  Raw expression: ").concat(text.trim()), range);
          }
          else {
              warn("invalid expression: ".concat(e.message, " in\n\n") +
                  "    ".concat(exp, "\n\n") +
                  "  Raw expression: ".concat(text.trim(), "\n"), range);
          }
      }
  }
  function checkFunctionParameterExpression(exp, text, warn, range) {
      try {
          new Function(exp, '');
      }
      catch (e) {
          warn("invalid function parameter expression: ".concat(e.message, " in\n\n") +
              "    ".concat(exp, "\n\n") +
              "  Raw expression: ".concat(text.trim(), "\n"), range);
      }
  }

  var range = 2;
  function generateCodeFrame(source, start, end) {
      if (start === void 0) { start = 0; }
      if (end === void 0) { end = source.length; }
      var lines = source.split(/\r?\n/);
      var count = 0;
      var res = [];
      for (var i = 0; i < lines.length; i++) {
          count += lines[i].length + 1;
          if (count >= start) {
              for (var j = i - range; j <= i + range || end > count; j++) {
                  if (j < 0 || j >= lines.length)
                      continue;
                  res.push("".concat(j + 1).concat(repeat(" ", 3 - String(j + 1).length), "|  ").concat(lines[j]));
                  var lineLength = lines[j].length;
                  if (j === i) {
                      // push underline
                      var pad = start - (count - lineLength) + 1;
                      var length_1 = end > count ? lineLength - pad : end - start;
                      res.push("   |  " + repeat(" ", pad) + repeat("^", length_1));
                  }
                  else if (j > i) {
                      if (end > count) {
                          var length_2 = Math.min(end - count, lineLength);
                          res.push("   |  " + repeat("^", length_2));
                      }
                      count += lineLength + 1;
                  }
              }
              break;
          }
      }
      return res.join('\n');
  }
  function repeat(str, n) {
      var result = '';
      if (n > 0) {
          // eslint-disable-next-line no-constant-condition
          while (true) {
              // eslint-disable-line
              if (n & 1)
                  result += str;
              n >>>= 1;
              if (n <= 0)
                  break;
              str += str;
          }
      }
      return result;
  }

  function createFunction(code, errors) {
      try {
          return new Function(code);
      }
      catch (err) {
          errors.push({ err: err, code: code });
          return noop;
      }
  }
  function createCompileToFunctionFn(compile) {
      var cache = Object.create(null);
      return function compileToFunctions(template, options, vm) {
          options = extend({}, options);
          var warn = options.warn || warn$2;
          delete options.warn;
          /* istanbul ignore if */
          {
              // detect possible CSP restriction
              try {
                  new Function('return 1');
              }
              catch (e) {
                  if (e.toString().match(/unsafe-eval|CSP/)) {
                      warn('It seems you are using the standalone build of Vue.js in an ' +
                          'environment with Content Security Policy that prohibits unsafe-eval. ' +
                          'The template compiler cannot work in this environment. Consider ' +
                          'relaxing the policy to allow unsafe-eval or pre-compiling your ' +
                          'templates into render functions.');
                  }
              }
          }
          // check cache
          var key = options.delimiters
              ? String(options.delimiters) + template
              : template;
          if (cache[key]) {
              return cache[key];
          }
          // compile
          var compiled = compile(template, options);
          // check compilation errors/tips
          {
              if (compiled.errors && compiled.errors.length) {
                  if (options.outputSourceRange) {
                      compiled.errors.forEach(function (e) {
                          warn("Error compiling template:\n\n".concat(e.msg, "\n\n") +
                              generateCodeFrame(template, e.start, e.end), vm);
                      });
                  }
                  else {
                      warn("Error compiling template:\n\n".concat(template, "\n\n") +
                          compiled.errors.map(function (e) { return "- ".concat(e); }).join('\n') +
                          '\n', vm);
                  }
              }
              if (compiled.tips && compiled.tips.length) {
                  if (options.outputSourceRange) {
                      compiled.tips.forEach(function (e) { return tip(e.msg, vm); });
                  }
                  else {
                      compiled.tips.forEach(function (msg) { return tip(msg, vm); });
                  }
              }
          }
          // turn code into functions
          var res = {};
          var fnGenErrors = [];
          res.render = createFunction(compiled.render, fnGenErrors);
          res.staticRenderFns = compiled.staticRenderFns.map(function (code) {
              return createFunction(code, fnGenErrors);
          });
          // check function generation errors.
          // this should only happen if there is a bug in the compiler itself.
          // mostly for codegen development use
          /* istanbul ignore if */
          {
              if ((!compiled.errors || !compiled.errors.length) && fnGenErrors.length) {
                  warn("Failed to generate render function:\n\n" +
                      fnGenErrors
                          .map(function (_a) {
                          var err = _a.err, code = _a.code;
                          return "".concat(err.toString(), " in\n\n").concat(code, "\n");
                      })
                          .join('\n'), vm);
              }
          }
          return (cache[key] = res);
      };
  }

  function createCompilerCreator(baseCompile) {
      return function createCompiler(baseOptions) {
          function compile(template, options) {
              var finalOptions = Object.create(baseOptions);
              var errors = [];
              var tips = [];
              var warn = function (msg, range, tip) {
                  (tip ? tips : errors).push(msg);
              };
              if (options) {
                  if (options.outputSourceRange) {
                      // $flow-disable-line
                      var leadingSpaceLength_1 = template.match(/^\s*/)[0].length;
                      warn = function (msg, range, tip) {
                          var data = typeof msg === 'string' ? { msg: msg } : msg;
                          if (range) {
                              if (range.start != null) {
                                  data.start = range.start + leadingSpaceLength_1;
                              }
                              if (range.end != null) {
                                  data.end = range.end + leadingSpaceLength_1;
                              }
                          }
                          (tip ? tips : errors).push(data);
                      };
                  }
                  // merge custom modules
                  if (options.modules) {
                      finalOptions.modules = (baseOptions.modules || []).concat(options.modules);
                  }
                  // merge custom directives
                  if (options.directives) {
                      finalOptions.directives = extend(Object.create(baseOptions.directives || null), options.directives);
                  }
                  // copy other options
                  for (var key in options) {
                      if (key !== 'modules' && key !== 'directives') {
                          finalOptions[key] = options[key];
                      }
                  }
              }
              finalOptions.warn = warn;
              var compiled = baseCompile(template.trim(), finalOptions);
              {
                  detectErrors(compiled.ast, warn);
              }
              compiled.errors = errors;
              compiled.tips = tips;
              return compiled;
          }
          return {
              compile: compile,
              compileToFunctions: createCompileToFunctionFn(compile)
          };
      };
  }

  // `createCompilerCreator` allows creating compilers that use alternative
  // parser/optimizer/codegen, e.g the SSR optimizing compiler.
  // Here we just export a default compiler using the default parts.
  var createCompiler = createCompilerCreator(function baseCompile(template, options) {
      var ast = parse(template.trim(), options);
      if (options.optimize !== false) {
          optimize(ast, options);
      }
      var code = generate(ast, options);
      return {
          ast: ast,
          render: code.render,
          staticRenderFns: code.staticRenderFns
      };
  });

  var _a = createCompiler(baseOptions), compileToFunctions = _a.compileToFunctions;

  // check whether current browser encodes a char inside attribute values
  var div;
  function getShouldDecode(href) {
      div = div || document.createElement('div');
      div.innerHTML = href ? "<a href=\"\n\"/>" : "<div a=\"\n\"/>";
      return div.innerHTML.indexOf('&#10;') > 0;
  }
  // #3663: IE encodes newlines inside attribute values while other browsers don't
  var shouldDecodeNewlines = inBrowser ? getShouldDecode(false) : false;
  // #6828: chrome encodes content in a[href]
  var shouldDecodeNewlinesForHref = inBrowser
      ? getShouldDecode(true)
      : false;

  var idToTemplate = cached(function (id) {
      var el = query(id);
      return el && el.innerHTML;
  });
  var mount = Vue.prototype.$mount;
  Vue.prototype.$mount = function (el, hydrating) {
      el = el && query(el);
      /* istanbul ignore if */
      if (el === document.body || el === document.documentElement) {
          warn$2("Do not mount Vue to <html> or <body> - mount to normal elements instead.");
          return this;
      }
      var options = this.$options;
      // resolve template/el and convert to render function
      if (!options.render) {
          var template = options.template;
          if (template) {
              if (typeof template === 'string') {
                  if (template.charAt(0) === '#') {
                      template = idToTemplate(template);
                      /* istanbul ignore if */
                      if (!template) {
                          warn$2("Template element not found or is empty: ".concat(options.template), this);
                      }
                  }
              }
              else if (template.nodeType) {
                  template = template.innerHTML;
              }
              else {
                  {
                      warn$2('invalid template option:' + template, this);
                  }
                  return this;
              }
          }
          else if (el) {
              // @ts-expect-error
              template = getOuterHTML(el);
          }
          if (template) {
              /* istanbul ignore if */
              if (config.performance && mark) {
                  mark('compile');
              }
              var _a = compileToFunctions(template, {
                  outputSourceRange: true,
                  shouldDecodeNewlines: shouldDecodeNewlines,
                  shouldDecodeNewlinesForHref: shouldDecodeNewlinesForHref,
                  delimiters: options.delimiters,
                  comments: options.comments
              }, this), render = _a.render, staticRenderFns = _a.staticRenderFns;
              options.render = render;
              options.staticRenderFns = staticRenderFns;
              /* istanbul ignore if */
              if (config.performance && mark) {
                  mark('compile end');
                  measure("vue ".concat(this._name, " compile"), 'compile', 'compile end');
              }
          }
      }
      return mount.call(this, el, hydrating);
  };
  /**
   * Get outerHTML of elements, taking care
   * of SVG elements in IE as well.
   */
  function getOuterHTML(el) {
      if (el.outerHTML) {
          return el.outerHTML;
      }
      else {
          var container = document.createElement('div');
          container.appendChild(el.cloneNode(true));
          return container.innerHTML;
      }
  }
  Vue.compile = compileToFunctions;

  // export type EffectScheduler = (...args: any[]) => any
  /**
   * @internal since we are not exposing this in Vue 2, it's used only for
   * internal testing.
   */
  function effect(fn, scheduler) {
      var watcher = new Watcher(currentInstance, fn, noop, {
          sync: true
      });
      if (scheduler) {
          watcher.update = function () {
              scheduler(function () { return watcher.run(); });
          };
      }
  }

  extend(Vue, vca);
  Vue.effect = effect;

  return Vue;

}));

/**
  * vue-router v2.8.1
  * (c) 2017 Evan You
  * @license MIT
  */
(function (global, factory) {
	typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
	typeof define === 'function' && define.amd ? define(factory) :
	(global.VueRouter = factory());
}(this, (function () { 'use strict';

/*  */

function assert (condition, message) {
  if (!condition) {
    throw new Error(("[vue-router] " + message))
  }
}

function warn (condition, message) {
  if ("development" !== 'production' && !condition) {
    typeof console !== 'undefined' && console.warn(("[vue-router] " + message));
  }
}

function isError (err) {
  return Object.prototype.toString.call(err).indexOf('Error') > -1
}

var View = {
  name: 'router-view',
  functional: true,
  props: {
    name: {
      type: String,
      default: 'default'
    }
  },
  render: function render (_, ref) {
    var props = ref.props;
    var children = ref.children;
    var parent = ref.parent;
    var data = ref.data;

    data.routerView = true;

    // directly use parent context's createElement() function
    // so that components rendered by router-view can resolve named slots
    var h = parent.$createElement;
    var name = props.name;
    var route = parent.$route;
    var cache = parent._routerViewCache || (parent._routerViewCache = {});

    // determine current view depth, also check to see if the tree
    // has been toggled inactive but kept-alive.
    var depth = 0;
    var inactive = false;
    while (parent && parent._routerRoot !== parent) {
      if (parent.$vnode && parent.$vnode.data.routerView) {
        depth++;
      }
      if (parent._inactive) {
        inactive = true;
      }
      parent = parent.$parent;
    }
    data.routerViewDepth = depth;

    // render previous view if the tree is inactive and kept-alive
    if (inactive) {
      return h(cache[name], data, children)
    }

    var matched = route.matched[depth];
    // render empty node if no matched route
    if (!matched) {
      cache[name] = null;
      return h()
    }

    var component = cache[name] = matched.components[name];

    // attach instance registration hook
    // this will be called in the instance's injected lifecycle hooks
    data.registerRouteInstance = function (vm, val) {
      // val could be undefined for unregistration
      var current = matched.instances[name];
      if (
        (val && current !== vm) ||
        (!val && current === vm)
      ) {
        matched.instances[name] = val;
      }
    }

    // also register instance in prepatch hook
    // in case the same component instance is reused across different routes
    ;(data.hook || (data.hook = {})).prepatch = function (_, vnode) {
      matched.instances[name] = vnode.componentInstance;
    };

    // resolve props
    var propsToPass = data.props = resolveProps(route, matched.props && matched.props[name]);
    if (propsToPass) {
      // clone to prevent mutation
      propsToPass = data.props = extend({}, propsToPass);
      // pass non-declared props as attrs
      var attrs = data.attrs = data.attrs || {};
      for (var key in propsToPass) {
        if (!component.props || !(key in component.props)) {
          attrs[key] = propsToPass[key];
          delete propsToPass[key];
        }
      }
    }

    return h(component, data, children)
  }
};

function resolveProps (route, config) {
  switch (typeof config) {
    case 'undefined':
      return
    case 'object':
      return config
    case 'function':
      return config(route)
    case 'boolean':
      return config ? route.params : undefined
    default:
      {
        warn(
          false,
          "props in \"" + (route.path) + "\" is a " + (typeof config) + ", " +
          "expecting an object, function or boolean."
        );
      }
  }
}

function extend (to, from) {
  for (var key in from) {
    to[key] = from[key];
  }
  return to
}

/*  */

var encodeReserveRE = /[!'()*]/g;
var encodeReserveReplacer = function (c) { return '%' + c.charCodeAt(0).toString(16); };
var commaRE = /%2C/g;

// fixed encodeURIComponent which is more conformant to RFC3986:
// - escapes [!'()*]
// - preserve commas
var encode = function (str) { return encodeURIComponent(str)
  .replace(encodeReserveRE, encodeReserveReplacer)
  .replace(commaRE, ','); };

var decode = decodeURIComponent;

function resolveQuery (
  query,
  extraQuery,
  _parseQuery
) {
  if ( extraQuery === void 0 ) extraQuery = {};

  var parse = _parseQuery || parseQuery;
  var parsedQuery;
  try {
    parsedQuery = parse(query || '');
  } catch (e) {
    "development" !== 'production' && warn(false, e.message);
    parsedQuery = {};
  }
  for (var key in extraQuery) {
    parsedQuery[key] = extraQuery[key];
  }
  return parsedQuery
}

function parseQuery (query) {
  var res = {};

  query = query.trim().replace(/^(\?|#|&)/, '');

  if (!query) {
    return res
  }

  query.split('&').forEach(function (param) {
    var parts = param.replace(/\+/g, ' ').split('=');
    var key = decode(parts.shift());
    var val = parts.length > 0
      ? decode(parts.join('='))
      : null;

    if (res[key] === undefined) {
      res[key] = val;
    } else if (Array.isArray(res[key])) {
      res[key].push(val);
    } else {
      res[key] = [res[key], val];
    }
  });

  return res
}

function stringifyQuery (obj) {
  var res = obj ? Object.keys(obj).map(function (key) {
    var val = obj[key];

    if (val === undefined) {
      return ''
    }

    if (val === null) {
      return encode(key)
    }

    if (Array.isArray(val)) {
      var result = [];
      val.forEach(function (val2) {
        if (val2 === undefined) {
          return
        }
        if (val2 === null) {
          result.push(encode(key));
        } else {
          result.push(encode(key) + '=' + encode(val2));
        }
      });
      return result.join('&')
    }

    return encode(key) + '=' + encode(val)
  }).filter(function (x) { return x.length > 0; }).join('&') : null;
  return res ? ("?" + res) : ''
}

/*  */


var trailingSlashRE = /\/?$/;

function createRoute (
  record,
  location,
  redirectedFrom,
  router
) {
  var stringifyQuery$$1 = router && router.options.stringifyQuery;

  var query = location.query || {};
  try {
    query = clone(query);
  } catch (e) {}

  var route = {
    name: location.name || (record && record.name),
    meta: (record && record.meta) || {},
    path: location.path || '/',
    hash: location.hash || '',
    query: query,
    params: location.params || {},
    fullPath: getFullPath(location, stringifyQuery$$1),
    matched: record ? formatMatch(record) : []
  };
  if (redirectedFrom) {
    route.redirectedFrom = getFullPath(redirectedFrom, stringifyQuery$$1);
  }
  return Object.freeze(route)
}

function clone (value) {
  if (Array.isArray(value)) {
    return value.map(clone)
  } else if (value && typeof value === 'object') {
    var res = {};
    for (var key in value) {
      res[key] = clone(value[key]);
    }
    return res
  } else {
    return value
  }
}

// the starting route that represents the initial state
var START = createRoute(null, {
  path: '/'
});

function formatMatch (record) {
  var res = [];
  while (record) {
    res.unshift(record);
    record = record.parent;
  }
  return res
}

function getFullPath (
  ref,
  _stringifyQuery
) {
  var path = ref.path;
  var query = ref.query; if ( query === void 0 ) query = {};
  var hash = ref.hash; if ( hash === void 0 ) hash = '';

  var stringify = _stringifyQuery || stringifyQuery;
  return (path || '/') + stringify(query) + hash
}

function isSameRoute (a, b) {
  if (b === START) {
    return a === b
  } else if (!b) {
    return false
  } else if (a.path && b.path) {
    return (
      a.path.replace(trailingSlashRE, '') === b.path.replace(trailingSlashRE, '') &&
      a.hash === b.hash &&
      isObjectEqual(a.query, b.query)
    )
  } else if (a.name && b.name) {
    return (
      a.name === b.name &&
      a.hash === b.hash &&
      isObjectEqual(a.query, b.query) &&
      isObjectEqual(a.params, b.params)
    )
  } else {
    return false
  }
}

function isObjectEqual (a, b) {
  if ( a === void 0 ) a = {};
  if ( b === void 0 ) b = {};

  // handle null value #1566
  if (!a || !b) { return a === b }
  var aKeys = Object.keys(a);
  var bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false
  }
  return aKeys.every(function (key) {
    var aVal = a[key];
    var bVal = b[key];
    // check nested equality
    if (typeof aVal === 'object' && typeof bVal === 'object') {
      return isObjectEqual(aVal, bVal)
    }
    return String(aVal) === String(bVal)
  })
}

function isIncludedRoute (current, target) {
  return (
    current.path.replace(trailingSlashRE, '/').indexOf(
      target.path.replace(trailingSlashRE, '/')
    ) === 0 &&
    (!target.hash || current.hash === target.hash) &&
    queryIncludes(current.query, target.query)
  )
}

function queryIncludes (current, target) {
  for (var key in target) {
    if (!(key in current)) {
      return false
    }
  }
  return true
}

/*  */

// work around weird flow bug
var toTypes = [String, Object];
var eventTypes = [String, Array];

var Link = {
  name: 'router-link',
  props: {
    to: {
      type: toTypes,
      required: true
    },
    tag: {
      type: String,
      default: 'a'
    },
    exact: Boolean,
    append: Boolean,
    replace: Boolean,
    activeClass: String,
    exactActiveClass: String,
    event: {
      type: eventTypes,
      default: 'click'
    }
  },
  render: function render (h) {
    var this$1 = this;

    var router = this.$router;
    var current = this.$route;
    var ref = router.resolve(this.to, current, this.append);
    var location = ref.location;
    var route = ref.route;
    var href = ref.href;

    var classes = {};
    var globalActiveClass = router.options.linkActiveClass;
    var globalExactActiveClass = router.options.linkExactActiveClass;
    // Support global empty active class
    var activeClassFallback = globalActiveClass == null
            ? 'router-link-active'
            : globalActiveClass;
    var exactActiveClassFallback = globalExactActiveClass == null
            ? 'router-link-exact-active'
            : globalExactActiveClass;
    var activeClass = this.activeClass == null
            ? activeClassFallback
            : this.activeClass;
    var exactActiveClass = this.exactActiveClass == null
            ? exactActiveClassFallback
            : this.exactActiveClass;
    var compareTarget = location.path
      ? createRoute(null, location, null, router)
      : route;

    classes[exactActiveClass] = isSameRoute(current, compareTarget);
    classes[activeClass] = this.exact
      ? classes[exactActiveClass]
      : isIncludedRoute(current, compareTarget);

    var handler = function (e) {
      if (guardEvent(e)) {
        if (this$1.replace) {
          router.replace(location);
        } else {
          router.push(location);
        }
      }
    };

    var on = { click: guardEvent };
    if (Array.isArray(this.event)) {
      this.event.forEach(function (e) { on[e] = handler; });
    } else {
      on[this.event] = handler;
    }

    var data = {
      class: classes
    };

    if (this.tag === 'a') {
      data.on = on;
      data.attrs = { href: href };
    } else {
      // find the first <a> child and apply listener and href
      var a = findAnchor(this.$slots.default);
      if (a) {
        // in case the <a> is a static node
        a.isStatic = false;
        var extend = _Vue.util.extend;
        var aData = a.data = extend({}, a.data);
        aData.on = on;
        var aAttrs = a.data.attrs = extend({}, a.data.attrs);
        aAttrs.href = href;
      } else {
        // doesn't have <a> child, apply listener to self
        data.on = on;
      }
    }

    return h(this.tag, data, this.$slots.default)
  }
};

function guardEvent (e) {
  // don't redirect with control keys
  if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) { return }
  // don't redirect when preventDefault called
  if (e.defaultPrevented) { return }
  // don't redirect on right click
  if (e.button !== undefined && e.button !== 0) { return }
  // don't redirect if `target="_blank"`
  if (e.currentTarget && e.currentTarget.getAttribute) {
    var target = e.currentTarget.getAttribute('target');
    if (/\b_blank\b/i.test(target)) { return }
  }
  // this may be a Weex event which doesn't have this method
  if (e.preventDefault) {
    e.preventDefault();
  }
  return true
}

function findAnchor (children) {
  if (children) {
    var child;
    for (var i = 0; i < children.length; i++) {
      child = children[i];
      if (child.tag === 'a') {
        return child
      }
      if (child.children && (child = findAnchor(child.children))) {
        return child
      }
    }
  }
}

var _Vue;

function install (Vue) {
  if (install.installed && _Vue === Vue) { return }
  install.installed = true;

  _Vue = Vue;

  var isDef = function (v) { return v !== undefined; };

  var registerInstance = function (vm, callVal) {
    var i = vm.$options._parentVnode;
    if (isDef(i) && isDef(i = i.data) && isDef(i = i.registerRouteInstance)) {
      i(vm, callVal);
    }
  };

  Vue.mixin({
    beforeCreate: function beforeCreate () {
      if (isDef(this.$options.router)) {
        this._routerRoot = this;
        this._router = this.$options.router;
        this._router.init(this);
        Vue.util.defineReactive(this, '_route', this._router.history.current);
      } else {
        this._routerRoot = (this.$parent && this.$parent._routerRoot) || this;
      }
      registerInstance(this, this);
    },
    destroyed: function destroyed () {
      registerInstance(this);
    }
  });

  Object.defineProperty(Vue.prototype, '$router', {
    get: function get () { return this._routerRoot._router }
  });

  Object.defineProperty(Vue.prototype, '$route', {
    get: function get () { return this._routerRoot._route }
  });

  Vue.component('router-view', View);
  Vue.component('router-link', Link);

  var strats = Vue.config.optionMergeStrategies;
  // use the same hook merging strategy for route hooks
  strats.beforeRouteEnter = strats.beforeRouteLeave = strats.beforeRouteUpdate = strats.created;
}

/*  */

var inBrowser = typeof window !== 'undefined';

/*  */

function resolvePath (
  relative,
  base,
  append
) {
  var firstChar = relative.charAt(0);
  if (firstChar === '/') {
    return relative
  }

  if (firstChar === '?' || firstChar === '#') {
    return base + relative
  }

  var stack = base.split('/');

  // remove trailing segment if:
  // - not appending
  // - appending to trailing slash (last segment is empty)
  if (!append || !stack[stack.length - 1]) {
    stack.pop();
  }

  // resolve relative path
  var segments = relative.replace(/^\//, '').split('/');
  for (var i = 0; i < segments.length; i++) {
    var segment = segments[i];
    if (segment === '..') {
      stack.pop();
    } else if (segment !== '.') {
      stack.push(segment);
    }
  }

  // ensure leading slash
  if (stack[0] !== '') {
    stack.unshift('');
  }

  return stack.join('/')
}

function parsePath (path) {
  var hash = '';
  var query = '';

  var hashIndex = path.indexOf('#');
  if (hashIndex >= 0) {
    hash = path.slice(hashIndex);
    path = path.slice(0, hashIndex);
  }

  var queryIndex = path.indexOf('?');
  if (queryIndex >= 0) {
    query = path.slice(queryIndex + 1);
    path = path.slice(0, queryIndex);
  }

  return {
    path: path,
    query: query,
    hash: hash
  }
}

function cleanPath (path) {
  return path.replace(/\/\//g, '/')
}

var isarray = Array.isArray || function (arr) {
  return Object.prototype.toString.call(arr) == '[object Array]';
};

/**
 * Expose `pathToRegexp`.
 */
var pathToRegexp_1 = pathToRegexp;
var parse_1 = parse;
var compile_1 = compile;
var tokensToFunction_1 = tokensToFunction;
var tokensToRegExp_1 = tokensToRegExp;

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
var PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g');

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  var tokens = [];
  var key = 0;
  var index = 0;
  var path = '';
  var defaultDelimiter = options && options.delimiter || '/';
  var res;

  while ((res = PATH_REGEXP.exec(str)) != null) {
    var m = res[0];
    var escaped = res[1];
    var offset = res.index;
    path += str.slice(index, offset);
    index = offset + m.length;

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1];
      continue
    }

    var next = str[index];
    var prefix = res[2];
    var name = res[3];
    var capture = res[4];
    var group = res[5];
    var modifier = res[6];
    var asterisk = res[7];

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path);
      path = '';
    }

    var partial = prefix != null && next != null && next !== prefix;
    var repeat = modifier === '+' || modifier === '*';
    var optional = modifier === '?' || modifier === '*';
    var delimiter = res[2] || defaultDelimiter;
    var pattern = capture || group;

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter: delimiter,
      optional: optional,
      repeat: repeat,
      partial: partial,
      asterisk: !!asterisk,
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    });
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index);
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path);
  }

  return tokens
}

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
function compile (str, options) {
  return tokensToFunction(parse(str, options))
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str) {
  return encodeURI(str).replace(/[\/?#]/g, function (c) {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURI(str).replace(/[?#]/g, function (c) {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens) {
  // Compile all the tokens into regexps.
  var matches = new Array(tokens.length);

  // Compile all the patterns before compilation.
  for (var i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$');
    }
  }

  return function (obj, opts) {
    var path = '';
    var data = obj || {};
    var options = opts || {};
    var encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent;

    for (var i = 0; i < tokens.length; i++) {
      var token = tokens[i];

      if (typeof token === 'string') {
        path += token;

        continue
      }

      var value = data[token.name];
      var segment;

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix;
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (isarray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (var j = 0; j < value.length; j++) {
          segment = encode(value[j]);

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment;
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value);

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment;
    }

    return path
  }
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|\/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$\/()])/g, '\\$1')
}

/**
 * Attach the keys as a property of the regexp.
 *
 * @param  {!RegExp} re
 * @param  {Array}   keys
 * @return {!RegExp}
 */
function attachKeys (re, keys) {
  re.keys = keys;
  return re
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options.sensitive ? '' : 'i'
}

/**
 * Pull out keys from a regexp.
 *
 * @param  {!RegExp} path
 * @param  {!Array}  keys
 * @return {!RegExp}
 */
function regexpToRegexp (path, keys) {
  // Use a negative lookahead to match only capturing groups.
  var groups = path.source.match(/\((?!\?)/g);

  if (groups) {
    for (var i = 0; i < groups.length; i++) {
      keys.push({
        name: i,
        prefix: null,
        delimiter: null,
        optional: false,
        repeat: false,
        partial: false,
        asterisk: false,
        pattern: null
      });
    }
  }

  return attachKeys(path, keys)
}

/**
 * Transform an array into a regexp.
 *
 * @param  {!Array}  path
 * @param  {Array}   keys
 * @param  {!Object} options
 * @return {!RegExp}
 */
function arrayToRegexp (path, keys, options) {
  var parts = [];

  for (var i = 0; i < path.length; i++) {
    parts.push(pathToRegexp(path[i], keys, options).source);
  }

  var regexp = new RegExp('(?:' + parts.join('|') + ')', flags(options));

  return attachKeys(regexp, keys)
}

/**
 * Create a path regexp from string input.
 *
 * @param  {string}  path
 * @param  {!Array}  keys
 * @param  {!Object} options
 * @return {!RegExp}
 */
function stringToRegexp (path, keys, options) {
  return tokensToRegExp(parse(path, options), keys, options)
}

/**
 * Expose a function for taking tokens and returning a RegExp.
 *
 * @param  {!Array}          tokens
 * @param  {(Array|Object)=} keys
 * @param  {Object=}         options
 * @return {!RegExp}
 */
function tokensToRegExp (tokens, keys, options) {
  if (!isarray(keys)) {
    options = /** @type {!Object} */ (keys || options);
    keys = [];
  }

  options = options || {};

  var strict = options.strict;
  var end = options.end !== false;
  var route = '';

  // Iterate over the tokens and create our regexp string.
  for (var i = 0; i < tokens.length; i++) {
    var token = tokens[i];

    if (typeof token === 'string') {
      route += escapeString(token);
    } else {
      var prefix = escapeString(token.prefix);
      var capture = '(?:' + token.pattern + ')';

      keys.push(token);

      if (token.repeat) {
        capture += '(?:' + prefix + capture + ')*';
      }

      if (token.optional) {
        if (!token.partial) {
          capture = '(?:' + prefix + '(' + capture + '))?';
        } else {
          capture = prefix + '(' + capture + ')?';
        }
      } else {
        capture = prefix + '(' + capture + ')';
      }

      route += capture;
    }
  }

  var delimiter = escapeString(options.delimiter || '/');
  var endsWithDelimiter = route.slice(-delimiter.length) === delimiter;

  // In non-strict mode we allow a slash at the end of match. If the path to
  // match already ends with a slash, we remove it for consistency. The slash
  // is valid at the end of a path match, not in the middle. This is important
  // in non-ending mode, where "/test/" shouldn't match "/test//route".
  if (!strict) {
    route = (endsWithDelimiter ? route.slice(0, -delimiter.length) : route) + '(?:' + delimiter + '(?=$))?';
  }

  if (end) {
    route += '$';
  } else {
    // In non-ending mode, we need the capturing groups to match as much as
    // possible by using a positive lookahead to the end or next path segment.
    route += strict && endsWithDelimiter ? '' : '(?=' + delimiter + '|$)';
  }

  return attachKeys(new RegExp('^' + route, flags(options)), keys)
}

/**
 * Normalize the given path string, returning a regular expression.
 *
 * An empty array can be passed in for the keys, which will hold the
 * placeholder key descriptions. For example, using `/user/:id`, `keys` will
 * contain `[{ name: 'id', delimiter: '/', optional: false, repeat: false }]`.
 *
 * @param  {(string|RegExp|Array)} path
 * @param  {(Array|Object)=}       keys
 * @param  {Object=}               options
 * @return {!RegExp}
 */
function pathToRegexp (path, keys, options) {
  if (!isarray(keys)) {
    options = /** @type {!Object} */ (keys || options);
    keys = [];
  }

  options = options || {};

  if (path instanceof RegExp) {
    return regexpToRegexp(path, /** @type {!Array} */ (keys))
  }

  if (isarray(path)) {
    return arrayToRegexp(/** @type {!Array} */ (path), /** @type {!Array} */ (keys), options)
  }

  return stringToRegexp(/** @type {string} */ (path), /** @type {!Array} */ (keys), options)
}

pathToRegexp_1.parse = parse_1;
pathToRegexp_1.compile = compile_1;
pathToRegexp_1.tokensToFunction = tokensToFunction_1;
pathToRegexp_1.tokensToRegExp = tokensToRegExp_1;

/*  */

// $flow-disable-line
var regexpCompileCache = Object.create(null);

function fillParams (
  path,
  params,
  routeMsg
) {
  try {
    var filler =
      regexpCompileCache[path] ||
      (regexpCompileCache[path] = pathToRegexp_1.compile(path));
    return filler(params || {}, { pretty: true })
  } catch (e) {
    {
      warn(false, ("missing param for " + routeMsg + ": " + (e.message)));
    }
    return ''
  }
}

/*  */

function createRouteMap (
  routes,
  oldPathList,
  oldPathMap,
  oldNameMap
) {
  // the path list is used to control path matching priority
  var pathList = oldPathList || [];
  // $flow-disable-line
  var pathMap = oldPathMap || Object.create(null);
  // $flow-disable-line
  var nameMap = oldNameMap || Object.create(null);

  routes.forEach(function (route) {
    addRouteRecord(pathList, pathMap, nameMap, route);
  });

  // ensure wildcard routes are always at the end
  for (var i = 0, l = pathList.length; i < l; i++) {
    if (pathList[i] === '*') {
      pathList.push(pathList.splice(i, 1)[0]);
      l--;
      i--;
    }
  }

  return {
    pathList: pathList,
    pathMap: pathMap,
    nameMap: nameMap
  }
}

function addRouteRecord (
  pathList,
  pathMap,
  nameMap,
  route,
  parent,
  matchAs
) {
  var path = route.path;
  var name = route.name;
  {
    assert(path != null, "\"path\" is required in a route configuration.");
    assert(
      typeof route.component !== 'string',
      "route config \"component\" for path: " + (String(path || name)) + " cannot be a " +
      "string id. Use an actual component instead."
    );
  }

  var pathToRegexpOptions = route.pathToRegexpOptions || {};
  var normalizedPath = normalizePath(
    path,
    parent,
    pathToRegexpOptions.strict
  );

  if (typeof route.caseSensitive === 'boolean') {
    pathToRegexpOptions.sensitive = route.caseSensitive;
  }

  var record = {
    path: normalizedPath,
    regex: compileRouteRegex(normalizedPath, pathToRegexpOptions),
    components: route.components || { default: route.component },
    instances: {},
    name: name,
    parent: parent,
    matchAs: matchAs,
    redirect: route.redirect,
    beforeEnter: route.beforeEnter,
    meta: route.meta || {},
    props: route.props == null
      ? {}
      : route.components
        ? route.props
        : { default: route.props }
  };

  if (route.children) {
    // Warn if route is named, does not redirect and has a default child route.
    // If users navigate to this route by name, the default child will
    // not be rendered (GH Issue #629)
    {
      if (route.name && !route.redirect && route.children.some(function (child) { return /^\/?$/.test(child.path); })) {
        warn(
          false,
          "Named Route '" + (route.name) + "' has a default child route. " +
          "When navigating to this named route (:to=\"{name: '" + (route.name) + "'\"), " +
          "the default child route will not be rendered. Remove the name from " +
          "this route and use the name of the default child route for named " +
          "links instead."
        );
      }
    }
    route.children.forEach(function (child) {
      var childMatchAs = matchAs
        ? cleanPath((matchAs + "/" + (child.path)))
        : undefined;
      addRouteRecord(pathList, pathMap, nameMap, child, record, childMatchAs);
    });
  }

  if (route.alias !== undefined) {
    var aliases = Array.isArray(route.alias)
      ? route.alias
      : [route.alias];

    aliases.forEach(function (alias) {
      var aliasRoute = {
        path: alias,
        children: route.children
      };
      addRouteRecord(
        pathList,
        pathMap,
        nameMap,
        aliasRoute,
        parent,
        record.path || '/' // matchAs
      );
    });
  }

  if (!pathMap[record.path]) {
    pathList.push(record.path);
    pathMap[record.path] = record;
  }

  if (name) {
    if (!nameMap[name]) {
      nameMap[name] = record;
    } else if ("development" !== 'production' && !matchAs) {
      warn(
        false,
        "Duplicate named routes definition: " +
        "{ name: \"" + name + "\", path: \"" + (record.path) + "\" }"
      );
    }
  }
}

function compileRouteRegex (path, pathToRegexpOptions) {
  var regex = pathToRegexp_1(path, [], pathToRegexpOptions);
  {
    var keys = Object.create(null);
    regex.keys.forEach(function (key) {
      warn(!keys[key.name], ("Duplicate param keys in route with path: \"" + path + "\""));
      keys[key.name] = true;
    });
  }
  return regex
}

function normalizePath (path, parent, strict) {
  if (!strict) { path = path.replace(/\/$/, ''); }
  if (path[0] === '/') { return path }
  if (parent == null) { return path }
  return cleanPath(((parent.path) + "/" + path))
}

/*  */


function normalizeLocation (
  raw,
  current,
  append,
  router
) {
  var next = typeof raw === 'string' ? { path: raw } : raw;
  // named target
  if (next.name || next._normalized) {
    return next
  }

  // relative params
  if (!next.path && next.params && current) {
    next = assign({}, next);
    next._normalized = true;
    var params = assign(assign({}, current.params), next.params);
    if (current.name) {
      next.name = current.name;
      next.params = params;
    } else if (current.matched.length) {
      var rawPath = current.matched[current.matched.length - 1].path;
      next.path = fillParams(rawPath, params, ("path " + (current.path)));
    } else {
      warn(false, "relative params navigation requires a current route.");
    }
    return next
  }

  var parsedPath = parsePath(next.path || '');
  var basePath = (current && current.path) || '/';
  var path = parsedPath.path
    ? resolvePath(parsedPath.path, basePath, append || next.append)
    : basePath;

  var query = resolveQuery(
    parsedPath.query,
    next.query,
    router && router.options.parseQuery
  );

  var hash = next.hash || parsedPath.hash;
  if (hash && hash.charAt(0) !== '#') {
    hash = "#" + hash;
  }

  return {
    _normalized: true,
    path: path,
    query: query,
    hash: hash
  }
}

function assign (a, b) {
  for (var key in b) {
    a[key] = b[key];
  }
  return a
}

/*  */


function createMatcher (
  routes,
  router
) {
  var ref = createRouteMap(routes);
  var pathList = ref.pathList;
  var pathMap = ref.pathMap;
  var nameMap = ref.nameMap;

  function addRoutes (routes) {
    createRouteMap(routes, pathList, pathMap, nameMap);
  }

  function match (
    raw,
    currentRoute,
    redirectedFrom
  ) {
    var location = normalizeLocation(raw, currentRoute, false, router);
    var name = location.name;

    if (name) {
      var record = nameMap[name];
      {
        warn(record, ("Route with name '" + name + "' does not exist"));
      }
      if (!record) { return _createRoute(null, location) }
      var paramNames = record.regex.keys
        .filter(function (key) { return !key.optional; })
        .map(function (key) { return key.name; });

      if (typeof location.params !== 'object') {
        location.params = {};
      }

      if (currentRoute && typeof currentRoute.params === 'object') {
        for (var key in currentRoute.params) {
          if (!(key in location.params) && paramNames.indexOf(key) > -1) {
            location.params[key] = currentRoute.params[key];
          }
        }
      }

      if (record) {
        location.path = fillParams(record.path, location.params, ("named route \"" + name + "\""));
        return _createRoute(record, location, redirectedFrom)
      }
    } else if (location.path) {
      location.params = {};
      for (var i = 0; i < pathList.length; i++) {
        var path = pathList[i];
        var record$1 = pathMap[path];
        if (matchRoute(record$1.regex, location.path, location.params)) {
          return _createRoute(record$1, location, redirectedFrom)
        }
      }
    }
    // no match
    return _createRoute(null, location)
  }

  function redirect (
    record,
    location
  ) {
    var originalRedirect = record.redirect;
    var redirect = typeof originalRedirect === 'function'
        ? originalRedirect(createRoute(record, location, null, router))
        : originalRedirect;

    if (typeof redirect === 'string') {
      redirect = { path: redirect };
    }

    if (!redirect || typeof redirect !== 'object') {
      {
        warn(
          false, ("invalid redirect option: " + (JSON.stringify(redirect)))
        );
      }
      return _createRoute(null, location)
    }

    var re = redirect;
    var name = re.name;
    var path = re.path;
    var query = location.query;
    var hash = location.hash;
    var params = location.params;
    query = re.hasOwnProperty('query') ? re.query : query;
    hash = re.hasOwnProperty('hash') ? re.hash : hash;
    params = re.hasOwnProperty('params') ? re.params : params;

    if (name) {
      // resolved named direct
      var targetRecord = nameMap[name];
      {
        assert(targetRecord, ("redirect failed: named route \"" + name + "\" not found."));
      }
      return match({
        _normalized: true,
        name: name,
        query: query,
        hash: hash,
        params: params
      }, undefined, location)
    } else if (path) {
      // 1. resolve relative redirect
      var rawPath = resolveRecordPath(path, record);
      // 2. resolve params
      var resolvedPath = fillParams(rawPath, params, ("redirect route with path \"" + rawPath + "\""));
      // 3. rematch with existing query and hash
      return match({
        _normalized: true,
        path: resolvedPath,
        query: query,
        hash: hash
      }, undefined, location)
    } else {
      {
        warn(false, ("invalid redirect option: " + (JSON.stringify(redirect))));
      }
      return _createRoute(null, location)
    }
  }

  function alias (
    record,
    location,
    matchAs
  ) {
    var aliasedPath = fillParams(matchAs, location.params, ("aliased route with path \"" + matchAs + "\""));
    var aliasedMatch = match({
      _normalized: true,
      path: aliasedPath
    });
    if (aliasedMatch) {
      var matched = aliasedMatch.matched;
      var aliasedRecord = matched[matched.length - 1];
      location.params = aliasedMatch.params;
      return _createRoute(aliasedRecord, location)
    }
    return _createRoute(null, location)
  }

  function _createRoute (
    record,
    location,
    redirectedFrom
  ) {
    if (record && record.redirect) {
      return redirect(record, redirectedFrom || location)
    }
    if (record && record.matchAs) {
      return alias(record, location, record.matchAs)
    }
    return createRoute(record, location, redirectedFrom, router)
  }

  return {
    match: match,
    addRoutes: addRoutes
  }
}

function matchRoute (
  regex,
  path,
  params
) {
  var m = path.match(regex);

  if (!m) {
    return false
  } else if (!params) {
    return true
  }

  for (var i = 1, len = m.length; i < len; ++i) {
    var key = regex.keys[i - 1];
    var val = typeof m[i] === 'string' ? decodeURIComponent(m[i]) : m[i];
    if (key) {
      params[key.name] = val;
    }
  }

  return true
}

function resolveRecordPath (path, record) {
  return resolvePath(path, record.parent ? record.parent.path : '/', true)
}

/*  */


var positionStore = Object.create(null);

function setupScroll () {
  // Fix for #1585 for Firefox
  window.history.replaceState({ key: getStateKey() }, '');
  window.addEventListener('popstate', function (e) {
    saveScrollPosition();
    if (e.state && e.state.key) {
      setStateKey(e.state.key);
    }
  });
}

function handleScroll (
  router,
  to,
  from,
  isPop
) {
  if (!router.app) {
    return
  }

  var behavior = router.options.scrollBehavior;
  if (!behavior) {
    return
  }

  {
    assert(typeof behavior === 'function', "scrollBehavior must be a function");
  }

  // wait until re-render finishes before scrolling
  router.app.$nextTick(function () {
    var position = getScrollPosition();
    var shouldScroll = behavior(to, from, isPop ? position : null);

    if (!shouldScroll) {
      return
    }

    if (typeof shouldScroll.then === 'function') {
      shouldScroll.then(function (shouldScroll) {
        scrollToPosition((shouldScroll), position);
      }).catch(function (err) {
        {
          assert(false, err.toString());
        }
      });
    } else {
      scrollToPosition(shouldScroll, position);
    }
  });
}

function saveScrollPosition () {
  var key = getStateKey();
  if (key) {
    positionStore[key] = {
      x: window.pageXOffset,
      y: window.pageYOffset
    };
  }
}

function getScrollPosition () {
  var key = getStateKey();
  if (key) {
    return positionStore[key]
  }
}

function getElementPosition (el, offset) {
  var docEl = document.documentElement;
  var docRect = docEl.getBoundingClientRect();
  var elRect = el.getBoundingClientRect();
  return {
    x: elRect.left - docRect.left - offset.x,
    y: elRect.top - docRect.top - offset.y
  }
}

function isValidPosition (obj) {
  return isNumber(obj.x) || isNumber(obj.y)
}

function normalizePosition (obj) {
  return {
    x: isNumber(obj.x) ? obj.x : window.pageXOffset,
    y: isNumber(obj.y) ? obj.y : window.pageYOffset
  }
}

function normalizeOffset (obj) {
  return {
    x: isNumber(obj.x) ? obj.x : 0,
    y: isNumber(obj.y) ? obj.y : 0
  }
}

function isNumber (v) {
  return typeof v === 'number'
}

function scrollToPosition (shouldScroll, position) {
  var isObject = typeof shouldScroll === 'object';
  if (isObject && typeof shouldScroll.selector === 'string') {
    var el = document.querySelector(shouldScroll.selector);
    if (el) {
      var offset = shouldScroll.offset && typeof shouldScroll.offset === 'object' ? shouldScroll.offset : {};
      offset = normalizeOffset(offset);
      position = getElementPosition(el, offset);
    } else if (isValidPosition(shouldScroll)) {
      position = normalizePosition(shouldScroll);
    }
  } else if (isObject && isValidPosition(shouldScroll)) {
    position = normalizePosition(shouldScroll);
  }

  if (position) {
    window.scrollTo(position.x, position.y);
  }
}

/*  */

var supportsPushState = inBrowser && (function () {
  var ua = window.navigator.userAgent;

  if (
    (ua.indexOf('Android 2.') !== -1 || ua.indexOf('Android 4.0') !== -1) &&
    ua.indexOf('Mobile Safari') !== -1 &&
    ua.indexOf('Chrome') === -1 &&
    ua.indexOf('Windows Phone') === -1
  ) {
    return false
  }

  return window.history && 'pushState' in window.history
})();

// use User Timing api (if present) for more accurate key precision
var Time = inBrowser && window.performance && window.performance.now
  ? window.performance
  : Date;

var _key = genKey();

function genKey () {
  return Time.now().toFixed(3)
}

function getStateKey () {
  return _key
}

function setStateKey (key) {
  _key = key;
}

function pushState (url, replace) {
  saveScrollPosition();
  // try...catch the pushState call to get around Safari
  // DOM Exception 18 where it limits to 100 pushState calls
  var history = window.history;
  try {
    if (replace) {
      history.replaceState({ key: _key }, '', url);
    } else {
      _key = genKey();
      history.pushState({ key: _key }, '', url);
    }
  } catch (e) {
    window.location[replace ? 'replace' : 'assign'](url);
  }
}

function replaceState (url) {
  pushState(url, true);
}

/*  */

function runQueue (queue, fn, cb) {
  var step = function (index) {
    if (index >= queue.length) {
      cb();
    } else {
      if (queue[index]) {
        fn(queue[index], function () {
          step(index + 1);
        });
      } else {
        step(index + 1);
      }
    }
  };
  step(0);
}

/*  */

function resolveAsyncComponents (matched) {
  return function (to, from, next) {
    var hasAsync = false;
    var pending = 0;
    var error = null;

    flatMapComponents(matched, function (def, _, match, key) {
      // if it's a function and doesn't have cid attached,
      // assume it's an async component resolve function.
      // we are not using Vue's default async resolving mechanism because
      // we want to halt the navigation until the incoming component has been
      // resolved.
      if (typeof def === 'function' && def.cid === undefined) {
        hasAsync = true;
        pending++;

        var resolve = once(function (resolvedDef) {
          if (isESModule(resolvedDef)) {
            resolvedDef = resolvedDef.default;
          }
          // save resolved on async factory in case it's used elsewhere
          def.resolved = typeof resolvedDef === 'function'
            ? resolvedDef
            : _Vue.extend(resolvedDef);
          match.components[key] = resolvedDef;
          pending--;
          if (pending <= 0) {
            next();
          }
        });

        var reject = once(function (reason) {
          var msg = "Failed to resolve async component " + key + ": " + reason;
          "development" !== 'production' && warn(false, msg);
          if (!error) {
            error = isError(reason)
              ? reason
              : new Error(msg);
            next(error);
          }
        });

        var res;
        try {
          res = def(resolve, reject);
        } catch (e) {
          reject(e);
        }
        if (res) {
          if (typeof res.then === 'function') {
            res.then(resolve, reject);
          } else {
            // new syntax in Vue 2.3
            var comp = res.component;
            if (comp && typeof comp.then === 'function') {
              comp.then(resolve, reject);
            }
          }
        }
      }
    });

    if (!hasAsync) { next(); }
  }
}

function flatMapComponents (
  matched,
  fn
) {
  return flatten(matched.map(function (m) {
    return Object.keys(m.components).map(function (key) { return fn(
      m.components[key],
      m.instances[key],
      m, key
    ); })
  }))
}

function flatten (arr) {
  return Array.prototype.concat.apply([], arr)
}

var hasSymbol =
  typeof Symbol === 'function' &&
  typeof Symbol.toStringTag === 'symbol';

function isESModule (obj) {
  return obj.__esModule || (hasSymbol && obj[Symbol.toStringTag] === 'Module')
}

// in Webpack 2, require.ensure now also returns a Promise
// so the resolve/reject functions may get called an extra time
// if the user uses an arrow function shorthand that happens to
// return that Promise.
function once (fn) {
  var called = false;
  return function () {
    var args = [], len = arguments.length;
    while ( len-- ) args[ len ] = arguments[ len ];

    if (called) { return }
    called = true;
    return fn.apply(this, args)
  }
}

/*  */

var History = function History (router, base) {
  this.router = router;
  this.base = normalizeBase(base);
  // start with a route object that stands for "nowhere"
  this.current = START;
  this.pending = null;
  this.ready = false;
  this.readyCbs = [];
  this.readyErrorCbs = [];
  this.errorCbs = [];
};

History.prototype.listen = function listen (cb) {
  this.cb = cb;
};

History.prototype.onReady = function onReady (cb, errorCb) {
  if (this.ready) {
    cb();
  } else {
    this.readyCbs.push(cb);
    if (errorCb) {
      this.readyErrorCbs.push(errorCb);
    }
  }
};

History.prototype.onError = function onError (errorCb) {
  this.errorCbs.push(errorCb);
};

History.prototype.transitionTo = function transitionTo (location, onComplete, onAbort) {
    var this$1 = this;

  var route = this.router.match(location, this.current);
  this.confirmTransition(route, function () {
    this$1.updateRoute(route);
    onComplete && onComplete(route);
    this$1.ensureURL();

    // fire ready cbs once
    if (!this$1.ready) {
      this$1.ready = true;
      this$1.readyCbs.forEach(function (cb) { cb(route); });
    }
  }, function (err) {
    if (onAbort) {
      onAbort(err);
    }
    if (err && !this$1.ready) {
      this$1.ready = true;
      this$1.readyErrorCbs.forEach(function (cb) { cb(err); });
    }
  });
};

History.prototype.confirmTransition = function confirmTransition (route, onComplete, onAbort) {
    var this$1 = this;

  var current = this.current;
  var abort = function (err) {
    if (isError(err)) {
      if (this$1.errorCbs.length) {
        this$1.errorCbs.forEach(function (cb) { cb(err); });
      } else {
        warn(false, 'uncaught error during route navigation:');
        console.error(err);
      }
    }
    onAbort && onAbort(err);
  };
  if (
    isSameRoute(route, current) &&
    // in the case the route map has been dynamically appended to
    route.matched.length === current.matched.length
  ) {
    this.ensureURL();
    return abort()
  }

  var ref = resolveQueue(this.current.matched, route.matched);
    var updated = ref.updated;
    var deactivated = ref.deactivated;
    var activated = ref.activated;

  var queue = [].concat(
    // in-component leave guards
    extractLeaveGuards(deactivated),
    // global before hooks
    this.router.beforeHooks,
    // in-component update hooks
    extractUpdateHooks(updated),
    // in-config enter guards
    activated.map(function (m) { return m.beforeEnter; }),
    // async components
    resolveAsyncComponents(activated)
  );

  this.pending = route;
  var iterator = function (hook, next) {
    if (this$1.pending !== route) {
      return abort()
    }
    try {
      hook(route, current, function (to) {
        if (to === false || isError(to)) {
          // next(false) -> abort navigation, ensure current URL
          this$1.ensureURL(true);
          abort(to);
        } else if (
          typeof to === 'string' ||
          (typeof to === 'object' && (
            typeof to.path === 'string' ||
            typeof to.name === 'string'
          ))
        ) {
          // next('/') or next({ path: '/' }) -> redirect
          abort();
          if (typeof to === 'object' && to.replace) {
            this$1.replace(to);
          } else {
            this$1.push(to);
          }
        } else {
          // confirm transition and pass on the value
          next(to);
        }
      });
    } catch (e) {
      abort(e);
    }
  };

  runQueue(queue, iterator, function () {
    var postEnterCbs = [];
    var isValid = function () { return this$1.current === route; };
    // wait until async components are resolved before
    // extracting in-component enter guards
    var enterGuards = extractEnterGuards(activated, postEnterCbs, isValid);
    var queue = enterGuards.concat(this$1.router.resolveHooks);
    runQueue(queue, iterator, function () {
      if (this$1.pending !== route) {
        return abort()
      }
      this$1.pending = null;
      onComplete(route);
      if (this$1.router.app) {
        this$1.router.app.$nextTick(function () {
          postEnterCbs.forEach(function (cb) { cb(); });
        });
      }
    });
  });
};

History.prototype.updateRoute = function updateRoute (route) {
  var prev = this.current;
  this.current = route;
  this.cb && this.cb(route);
  this.router.afterHooks.forEach(function (hook) {
    hook && hook(route, prev);
  });
};

function normalizeBase (base) {
  if (!base) {
    if (inBrowser) {
      // respect <base> tag
      var baseEl = document.querySelector('base');
      base = (baseEl && baseEl.getAttribute('href')) || '/';
      // strip full URL origin
      base = base.replace(/^https?:\/\/[^\/]+/, '');
    } else {
      base = '/';
    }
  }
  // make sure there's the starting slash
  if (base.charAt(0) !== '/') {
    base = '/' + base;
  }
  // remove trailing slash
  return base.replace(/\/$/, '')
}

function resolveQueue (
  current,
  next
) {
  var i;
  var max = Math.max(current.length, next.length);
  for (i = 0; i < max; i++) {
    if (current[i] !== next[i]) {
      break
    }
  }
  return {
    updated: next.slice(0, i),
    activated: next.slice(i),
    deactivated: current.slice(i)
  }
}

function extractGuards (
  records,
  name,
  bind,
  reverse
) {
  var guards = flatMapComponents(records, function (def, instance, match, key) {
    var guard = extractGuard(def, name);
    if (guard) {
      return Array.isArray(guard)
        ? guard.map(function (guard) { return bind(guard, instance, match, key); })
        : bind(guard, instance, match, key)
    }
  });
  return flatten(reverse ? guards.reverse() : guards)
}

function extractGuard (
  def,
  key
) {
  if (typeof def !== 'function') {
    // extend now so that global mixins are applied.
    def = _Vue.extend(def);
  }
  return def.options[key]
}

function extractLeaveGuards (deactivated) {
  return extractGuards(deactivated, 'beforeRouteLeave', bindGuard, true)
}

function extractUpdateHooks (updated) {
  return extractGuards(updated, 'beforeRouteUpdate', bindGuard)
}

function bindGuard (guard, instance) {
  if (instance) {
    return function boundRouteGuard () {
      return guard.apply(instance, arguments)
    }
  }
}

function extractEnterGuards (
  activated,
  cbs,
  isValid
) {
  return extractGuards(activated, 'beforeRouteEnter', function (guard, _, match, key) {
    return bindEnterGuard(guard, match, key, cbs, isValid)
  })
}

function bindEnterGuard (
  guard,
  match,
  key,
  cbs,
  isValid
) {
  return function routeEnterGuard (to, from, next) {
    return guard(to, from, function (cb) {
      next(cb);
      if (typeof cb === 'function') {
        cbs.push(function () {
          // #750
          // if a router-view is wrapped with an out-in transition,
          // the instance may not have been registered at this time.
          // we will need to poll for registration until current route
          // is no longer valid.
          poll(cb, match.instances, key, isValid);
        });
      }
    })
  }
}

function poll (
  cb, // somehow flow cannot infer this is a function
  instances,
  key,
  isValid
) {
  if (instances[key]) {
    cb(instances[key]);
  } else if (isValid()) {
    setTimeout(function () {
      poll(cb, instances, key, isValid);
    }, 16);
  }
}

/*  */


var HTML5History = (function (History$$1) {
  function HTML5History (router, base) {
    var this$1 = this;

    History$$1.call(this, router, base);

    var expectScroll = router.options.scrollBehavior;

    if (expectScroll) {
      setupScroll();
    }

    var initLocation = getLocation(this.base);
    window.addEventListener('popstate', function (e) {
      var current = this$1.current;

      // Avoiding first `popstate` event dispatched in some browsers but first
      // history route not updated since async guard at the same time.
      var location = getLocation(this$1.base);
      if (this$1.current === START && location === initLocation) {
        return
      }

      this$1.transitionTo(location, function (route) {
        if (expectScroll) {
          handleScroll(router, route, current, true);
        }
      });
    });
  }

  if ( History$$1 ) HTML5History.__proto__ = History$$1;
  HTML5History.prototype = Object.create( History$$1 && History$$1.prototype );
  HTML5History.prototype.constructor = HTML5History;

  HTML5History.prototype.go = function go (n) {
    window.history.go(n);
  };

  HTML5History.prototype.push = function push (location, onComplete, onAbort) {
    var this$1 = this;

    var ref = this;
    var fromRoute = ref.current;
    this.transitionTo(location, function (route) {
      pushState(cleanPath(this$1.base + route.fullPath));
      handleScroll(this$1.router, route, fromRoute, false);
      onComplete && onComplete(route);
    }, onAbort);
  };

  HTML5History.prototype.replace = function replace (location, onComplete, onAbort) {
    var this$1 = this;

    var ref = this;
    var fromRoute = ref.current;
    this.transitionTo(location, function (route) {
      replaceState(cleanPath(this$1.base + route.fullPath));
      handleScroll(this$1.router, route, fromRoute, false);
      onComplete && onComplete(route);
    }, onAbort);
  };

  HTML5History.prototype.ensureURL = function ensureURL (push) {
    if (getLocation(this.base) !== this.current.fullPath) {
      var current = cleanPath(this.base + this.current.fullPath);
      push ? pushState(current) : replaceState(current);
    }
  };

  HTML5History.prototype.getCurrentLocation = function getCurrentLocation () {
    return getLocation(this.base)
  };

  return HTML5History;
}(History));

function getLocation (base) {
  var path = window.location.pathname;
  if (base && path.indexOf(base) === 0) {
    path = path.slice(base.length);
  }
  return (path || '/') + window.location.search + window.location.hash
}

/*  */


var HashHistory = (function (History$$1) {
  function HashHistory (router, base, fallback) {
    History$$1.call(this, router, base);
    // check history fallback deeplinking
    if (fallback && checkFallback(this.base)) {
      return
    }
    ensureSlash();
  }

  if ( History$$1 ) HashHistory.__proto__ = History$$1;
  HashHistory.prototype = Object.create( History$$1 && History$$1.prototype );
  HashHistory.prototype.constructor = HashHistory;

  // this is delayed until the app mounts
  // to avoid the hashchange listener being fired too early
  HashHistory.prototype.setupListeners = function setupListeners () {
    var this$1 = this;

    var router = this.router;
    var expectScroll = router.options.scrollBehavior;
    var supportsScroll = supportsPushState && expectScroll;

    if (supportsScroll) {
      setupScroll();
    }

    window.addEventListener(supportsPushState ? 'popstate' : 'hashchange', function () {
      var current = this$1.current;
      if (!ensureSlash()) {
        return
      }
      this$1.transitionTo(getHash(), function (route) {
        if (supportsScroll) {
          handleScroll(this$1.router, route, current, true);
        }
        if (!supportsPushState) {
          replaceHash(route.fullPath);
        }
      });
    });
  };

  HashHistory.prototype.push = function push (location, onComplete, onAbort) {
    var this$1 = this;

    var ref = this;
    var fromRoute = ref.current;
    this.transitionTo(location, function (route) {
      pushHash(route.fullPath);
      handleScroll(this$1.router, route, fromRoute, false);
      onComplete && onComplete(route);
    }, onAbort);
  };

  HashHistory.prototype.replace = function replace (location, onComplete, onAbort) {
    var this$1 = this;

    var ref = this;
    var fromRoute = ref.current;
    this.transitionTo(location, function (route) {
      replaceHash(route.fullPath);
      handleScroll(this$1.router, route, fromRoute, false);
      onComplete && onComplete(route);
    }, onAbort);
  };

  HashHistory.prototype.go = function go (n) {
    window.history.go(n);
  };

  HashHistory.prototype.ensureURL = function ensureURL (push) {
    var current = this.current.fullPath;
    if (getHash() !== current) {
      push ? pushHash(current) : replaceHash(current);
    }
  };

  HashHistory.prototype.getCurrentLocation = function getCurrentLocation () {
    return getHash()
  };

  return HashHistory;
}(History));

function checkFallback (base) {
  var location = getLocation(base);
  if (!/^\/#/.test(location)) {
    window.location.replace(
      cleanPath(base + '/#' + location)
    );
    return true
  }
}

function ensureSlash () {
  var path = getHash();
  if (path.charAt(0) === '/') {
    return true
  }
  replaceHash('/' + path);
  return false
}

function getHash () {
  // We can't use window.location.hash here because it's not
  // consistent across browsers - Firefox will pre-decode it!
  var href = window.location.href;
  var index = href.indexOf('#');
  return index === -1 ? '' : href.slice(index + 1)
}

function getUrl (path) {
  var href = window.location.href;
  var i = href.indexOf('#');
  var base = i >= 0 ? href.slice(0, i) : href;
  return (base + "#" + path)
}

function pushHash (path) {
  if (supportsPushState) {
    pushState(getUrl(path));
  } else {
    window.location.hash = path;
  }
}

function replaceHash (path) {
  if (supportsPushState) {
    replaceState(getUrl(path));
  } else {
    window.location.replace(getUrl(path));
  }
}

/*  */


var AbstractHistory = (function (History$$1) {
  function AbstractHistory (router, base) {
    History$$1.call(this, router, base);
    this.stack = [];
    this.index = -1;
  }

  if ( History$$1 ) AbstractHistory.__proto__ = History$$1;
  AbstractHistory.prototype = Object.create( History$$1 && History$$1.prototype );
  AbstractHistory.prototype.constructor = AbstractHistory;

  AbstractHistory.prototype.push = function push (location, onComplete, onAbort) {
    var this$1 = this;

    this.transitionTo(location, function (route) {
      this$1.stack = this$1.stack.slice(0, this$1.index + 1).concat(route);
      this$1.index++;
      onComplete && onComplete(route);
    }, onAbort);
  };

  AbstractHistory.prototype.replace = function replace (location, onComplete, onAbort) {
    var this$1 = this;

    this.transitionTo(location, function (route) {
      this$1.stack = this$1.stack.slice(0, this$1.index).concat(route);
      onComplete && onComplete(route);
    }, onAbort);
  };

  AbstractHistory.prototype.go = function go (n) {
    var this$1 = this;

    var targetIndex = this.index + n;
    if (targetIndex < 0 || targetIndex >= this.stack.length) {
      return
    }
    var route = this.stack[targetIndex];
    this.confirmTransition(route, function () {
      this$1.index = targetIndex;
      this$1.updateRoute(route);
    });
  };

  AbstractHistory.prototype.getCurrentLocation = function getCurrentLocation () {
    var current = this.stack[this.stack.length - 1];
    return current ? current.fullPath : '/'
  };

  AbstractHistory.prototype.ensureURL = function ensureURL () {
    // noop
  };

  return AbstractHistory;
}(History));

/*  */

var VueRouter = function VueRouter (options) {
  if ( options === void 0 ) options = {};

  this.app = null;
  this.apps = [];
  this.options = options;
  this.beforeHooks = [];
  this.resolveHooks = [];
  this.afterHooks = [];
  this.matcher = createMatcher(options.routes || [], this);

  var mode = options.mode || 'hash';
  this.fallback = mode === 'history' && !supportsPushState && options.fallback !== false;
  if (this.fallback) {
    mode = 'hash';
  }
  if (!inBrowser) {
    mode = 'abstract';
  }
  this.mode = mode;

  switch (mode) {
    case 'history':
      this.history = new HTML5History(this, options.base);
      break
    case 'hash':
      this.history = new HashHistory(this, options.base, this.fallback);
      break
    case 'abstract':
      this.history = new AbstractHistory(this, options.base);
      break
    default:
      {
        assert(false, ("invalid mode: " + mode));
      }
  }
};

var prototypeAccessors = { currentRoute: { configurable: true } };

VueRouter.prototype.match = function match (
  raw,
  current,
  redirectedFrom
) {
  return this.matcher.match(raw, current, redirectedFrom)
};

prototypeAccessors.currentRoute.get = function () {
  return this.history && this.history.current
};

VueRouter.prototype.init = function init (app /* Vue component instance */) {
    var this$1 = this;

  "development" !== 'production' && assert(
    install.installed,
    "not installed. Make sure to call `Vue.use(VueRouter)` " +
    "before creating root instance."
  );

  this.apps.push(app);

  // main app already initialized.
  if (this.app) {
    return
  }

  this.app = app;

  var history = this.history;

  if (history instanceof HTML5History) {
    history.transitionTo(history.getCurrentLocation());
  } else if (history instanceof HashHistory) {
    var setupHashListener = function () {
      history.setupListeners();
    };
    history.transitionTo(
      history.getCurrentLocation(),
      setupHashListener,
      setupHashListener
    );
  }

  history.listen(function (route) {
    this$1.apps.forEach(function (app) {
      app._route = route;
    });
  });
};

VueRouter.prototype.beforeEach = function beforeEach (fn) {
  return registerHook(this.beforeHooks, fn)
};

VueRouter.prototype.beforeResolve = function beforeResolve (fn) {
  return registerHook(this.resolveHooks, fn)
};

VueRouter.prototype.afterEach = function afterEach (fn) {
  return registerHook(this.afterHooks, fn)
};

VueRouter.prototype.onReady = function onReady (cb, errorCb) {
  this.history.onReady(cb, errorCb);
};

VueRouter.prototype.onError = function onError (errorCb) {
  this.history.onError(errorCb);
};

VueRouter.prototype.push = function push (location, onComplete, onAbort) {
  this.history.push(location, onComplete, onAbort);
};

VueRouter.prototype.replace = function replace (location, onComplete, onAbort) {
  this.history.replace(location, onComplete, onAbort);
};

VueRouter.prototype.go = function go (n) {
  this.history.go(n);
};

VueRouter.prototype.back = function back () {
  this.go(-1);
};

VueRouter.prototype.forward = function forward () {
  this.go(1);
};

VueRouter.prototype.getMatchedComponents = function getMatchedComponents (to) {
  var route = to
    ? to.matched
      ? to
      : this.resolve(to).route
    : this.currentRoute;
  if (!route) {
    return []
  }
  return [].concat.apply([], route.matched.map(function (m) {
    return Object.keys(m.components).map(function (key) {
      return m.components[key]
    })
  }))
};

VueRouter.prototype.resolve = function resolve (
  to,
  current,
  append
) {
  var location = normalizeLocation(
    to,
    current || this.history.current,
    append,
    this
  );
  var route = this.match(location, current);
  var fullPath = route.redirectedFrom || route.fullPath;
  var base = this.history.base;
  var href = createHref(base, fullPath, this.mode);
  return {
    location: location,
    route: route,
    href: href,
    // for backwards compat
    normalizedTo: location,
    resolved: route
  }
};

VueRouter.prototype.addRoutes = function addRoutes (routes) {
  this.matcher.addRoutes(routes);
  if (this.history.current !== START) {
    this.history.transitionTo(this.history.getCurrentLocation());
  }
};

Object.defineProperties( VueRouter.prototype, prototypeAccessors );

function registerHook (list, fn) {
  list.push(fn);
  return function () {
    var i = list.indexOf(fn);
    if (i > -1) { list.splice(i, 1); }
  }
}

function createHref (base, fullPath, mode) {
  var path = mode === 'hash' ? '#' + fullPath : fullPath;
  return base ? cleanPath(base + '/' + path) : path
}

VueRouter.install = install;
VueRouter.version = '2.8.1';

if (inBrowser && window.Vue) {
  window.Vue.use(VueRouter);
}

return VueRouter;

})));

/*!
    localForage -- Offline Storage, Improved
    Version 1.10.0
    https://localforage.github.io/localForage
    (c) 2013-2017 Mozilla, Apache License 2.0
*/
(function(f){if(typeof exports==="object"&&typeof module!=="undefined"){module.exports=f()}else if(typeof define==="function"&&define.amd){define([],f)}else{var g;if(typeof window!=="undefined"){g=window}else if(typeof global!=="undefined"){g=global}else if(typeof self!=="undefined"){g=self}else{g=this}g.localforage = f()}})(function(){var define,module,exports;return (function e(t,n,r){function s(o,u){if(!n[o]){if(!t[o]){var a=typeof require=="function"&&require;if(!u&&a)return a(o,!0);if(i)return i(o,!0);var f=new Error("Cannot find module '"+o+"'");throw (f.code="MODULE_NOT_FOUND", f)}var l=n[o]={exports:{}};t[o][0].call(l.exports,function(e){var n=t[o][1][e];return s(n?n:e)},l,l.exports,e,t,n,r)}return n[o].exports}var i=typeof require=="function"&&require;for(var o=0;o<r.length;o++)s(r[o]);return s})({1:[function(_dereq_,module,exports){
(function (global){
'use strict';
var Mutation = global.MutationObserver || global.WebKitMutationObserver;

var scheduleDrain;

{
  if (Mutation) {
    var called = 0;
    var observer = new Mutation(nextTick);
    var element = global.document.createTextNode('');
    observer.observe(element, {
      characterData: true
    });
    scheduleDrain = function () {
      element.data = (called = ++called % 2);
    };
  } else if (!global.setImmediate && typeof global.MessageChannel !== 'undefined') {
    var channel = new global.MessageChannel();
    channel.port1.onmessage = nextTick;
    scheduleDrain = function () {
      channel.port2.postMessage(0);
    };
  } else if ('document' in global && 'onreadystatechange' in global.document.createElement('script')) {
    scheduleDrain = function () {

      // Create a <script> element; its readystatechange event will be fired asynchronously once it is inserted
      // into the document. Do so, thus queuing up the task. Remember to clean up once it's been called.
      var scriptEl = global.document.createElement('script');
      scriptEl.onreadystatechange = function () {
        nextTick();

        scriptEl.onreadystatechange = null;
        scriptEl.parentNode.removeChild(scriptEl);
        scriptEl = null;
      };
      global.document.documentElement.appendChild(scriptEl);
    };
  } else {
    scheduleDrain = function () {
      setTimeout(nextTick, 0);
    };
  }
}

var draining;
var queue = [];
//named nextTick for less confusing stack traces
function nextTick() {
  draining = true;
  var i, oldQueue;
  var len = queue.length;
  while (len) {
    oldQueue = queue;
    queue = [];
    i = -1;
    while (++i < len) {
      oldQueue[i]();
    }
    len = queue.length;
  }
  draining = false;
}

module.exports = immediate;
function immediate(task) {
  if (queue.push(task) === 1 && !draining) {
    scheduleDrain();
  }
}

}).call(this,typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : typeof window !== "undefined" ? window : {})
},{}],2:[function(_dereq_,module,exports){
'use strict';
var immediate = _dereq_(1);

/* istanbul ignore next */
function INTERNAL() {}

var handlers = {};

var REJECTED = ['REJECTED'];
var FULFILLED = ['FULFILLED'];
var PENDING = ['PENDING'];

module.exports = Promise;

function Promise(resolver) {
  if (typeof resolver !== 'function') {
    throw new TypeError('resolver must be a function');
  }
  this.state = PENDING;
  this.queue = [];
  this.outcome = void 0;
  if (resolver !== INTERNAL) {
    safelyResolveThenable(this, resolver);
  }
}

Promise.prototype["catch"] = function (onRejected) {
  return this.then(null, onRejected);
};
Promise.prototype.then = function (onFulfilled, onRejected) {
  if (typeof onFulfilled !== 'function' && this.state === FULFILLED ||
    typeof onRejected !== 'function' && this.state === REJECTED) {
    return this;
  }
  var promise = new this.constructor(INTERNAL);
  if (this.state !== PENDING) {
    var resolver = this.state === FULFILLED ? onFulfilled : onRejected;
    unwrap(promise, resolver, this.outcome);
  } else {
    this.queue.push(new QueueItem(promise, onFulfilled, onRejected));
  }

  return promise;
};
function QueueItem(promise, onFulfilled, onRejected) {
  this.promise = promise;
  if (typeof onFulfilled === 'function') {
    this.onFulfilled = onFulfilled;
    this.callFulfilled = this.otherCallFulfilled;
  }
  if (typeof onRejected === 'function') {
    this.onRejected = onRejected;
    this.callRejected = this.otherCallRejected;
  }
}
QueueItem.prototype.callFulfilled = function (value) {
  handlers.resolve(this.promise, value);
};
QueueItem.prototype.otherCallFulfilled = function (value) {
  unwrap(this.promise, this.onFulfilled, value);
};
QueueItem.prototype.callRejected = function (value) {
  handlers.reject(this.promise, value);
};
QueueItem.prototype.otherCallRejected = function (value) {
  unwrap(this.promise, this.onRejected, value);
};

function unwrap(promise, func, value) {
  immediate(function () {
    var returnValue;
    try {
      returnValue = func(value);
    } catch (e) {
      return handlers.reject(promise, e);
    }
    if (returnValue === promise) {
      handlers.reject(promise, new TypeError('Cannot resolve promise with itself'));
    } else {
      handlers.resolve(promise, returnValue);
    }
  });
}

handlers.resolve = function (self, value) {
  var result = tryCatch(getThen, value);
  if (result.status === 'error') {
    return handlers.reject(self, result.value);
  }
  var thenable = result.value;

  if (thenable) {
    safelyResolveThenable(self, thenable);
  } else {
    self.state = FULFILLED;
    self.outcome = value;
    var i = -1;
    var len = self.queue.length;
    while (++i < len) {
      self.queue[i].callFulfilled(value);
    }
  }
  return self;
};
handlers.reject = function (self, error) {
  self.state = REJECTED;
  self.outcome = error;
  var i = -1;
  var len = self.queue.length;
  while (++i < len) {
    self.queue[i].callRejected(error);
  }
  return self;
};

function getThen(obj) {
  // Make sure we only access the accessor once as required by the spec
  var then = obj && obj.then;
  if (obj && (typeof obj === 'object' || typeof obj === 'function') && typeof then === 'function') {
    return function appyThen() {
      then.apply(obj, arguments);
    };
  }
}

function safelyResolveThenable(self, thenable) {
  // Either fulfill, reject or reject with error
  var called = false;
  function onError(value) {
    if (called) {
      return;
    }
    called = true;
    handlers.reject(self, value);
  }

  function onSuccess(value) {
    if (called) {
      return;
    }
    called = true;
    handlers.resolve(self, value);
  }

  function tryToUnwrap() {
    thenable(onSuccess, onError);
  }

  var result = tryCatch(tryToUnwrap);
  if (result.status === 'error') {
    onError(result.value);
  }
}

function tryCatch(func, value) {
  var out = {};
  try {
    out.value = func(value);
    out.status = 'success';
  } catch (e) {
    out.status = 'error';
    out.value = e;
  }
  return out;
}

Promise.resolve = resolve;
function resolve(value) {
  if (value instanceof this) {
    return value;
  }
  return handlers.resolve(new this(INTERNAL), value);
}

Promise.reject = reject;
function reject(reason) {
  var promise = new this(INTERNAL);
  return handlers.reject(promise, reason);
}

Promise.all = all;
function all(iterable) {
  var self = this;
  if (Object.prototype.toString.call(iterable) !== '[object Array]') {
    return this.reject(new TypeError('must be an array'));
  }

  var len = iterable.length;
  var called = false;
  if (!len) {
    return this.resolve([]);
  }

  var values = new Array(len);
  var resolved = 0;
  var i = -1;
  var promise = new this(INTERNAL);

  while (++i < len) {
    allResolver(iterable[i], i);
  }
  return promise;
  function allResolver(value, i) {
    self.resolve(value).then(resolveFromAll, function (error) {
      if (!called) {
        called = true;
        handlers.reject(promise, error);
      }
    });
    function resolveFromAll(outValue) {
      values[i] = outValue;
      if (++resolved === len && !called) {
        called = true;
        handlers.resolve(promise, values);
      }
    }
  }
}

Promise.race = race;
function race(iterable) {
  var self = this;
  if (Object.prototype.toString.call(iterable) !== '[object Array]') {
    return this.reject(new TypeError('must be an array'));
  }

  var len = iterable.length;
  var called = false;
  if (!len) {
    return this.resolve([]);
  }

  var i = -1;
  var promise = new this(INTERNAL);

  while (++i < len) {
    resolver(iterable[i]);
  }
  return promise;
  function resolver(value) {
    self.resolve(value).then(function (response) {
      if (!called) {
        called = true;
        handlers.resolve(promise, response);
      }
    }, function (error) {
      if (!called) {
        called = true;
        handlers.reject(promise, error);
      }
    });
  }
}

},{"1":1}],3:[function(_dereq_,module,exports){
(function (global){
'use strict';
if (typeof global.Promise !== 'function') {
  global.Promise = _dereq_(2);
}

}).call(this,typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : typeof window !== "undefined" ? window : {})
},{"2":2}],4:[function(_dereq_,module,exports){
'use strict';

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function getIDB() {
    /* global indexedDB,webkitIndexedDB,mozIndexedDB,OIndexedDB,msIndexedDB */
    try {
        if (typeof indexedDB !== 'undefined') {
            return indexedDB;
        }
        if (typeof webkitIndexedDB !== 'undefined') {
            return webkitIndexedDB;
        }
        if (typeof mozIndexedDB !== 'undefined') {
            return mozIndexedDB;
        }
        if (typeof OIndexedDB !== 'undefined') {
            return OIndexedDB;
        }
        if (typeof msIndexedDB !== 'undefined') {
            return msIndexedDB;
        }
    } catch (e) {
        return;
    }
}

var idb = getIDB();

function isIndexedDBValid() {
    try {
        // Initialize IndexedDB; fall back to vendor-prefixed versions
        // if needed.
        if (!idb || !idb.open) {
            return false;
        }
        // We mimic PouchDB here;
        //
        // We test for openDatabase because IE Mobile identifies itself
        // as Safari. Oh the lulz...
        var isSafari = typeof openDatabase !== 'undefined' && /(Safari|iPhone|iPad|iPod)/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent) && !/BlackBerry/.test(navigator.platform);

        var hasFetch = typeof fetch === 'function' && fetch.toString().indexOf('[native code') !== -1;

        // Safari <10.1 does not meet our requirements for IDB support
        // (see: https://github.com/pouchdb/pouchdb/issues/5572).
        // Safari 10.1 shipped with fetch, we can use that to detect it.
        // Note: this creates issues with `window.fetch` polyfills and
        // overrides; see:
        // https://github.com/localForage/localForage/issues/856
        return (!isSafari || hasFetch) && typeof indexedDB !== 'undefined' &&
        // some outdated implementations of IDB that appear on Samsung
        // and HTC Android devices <4.4 are missing IDBKeyRange
        // See: https://github.com/mozilla/localForage/issues/128
        // See: https://github.com/mozilla/localForage/issues/272
        typeof IDBKeyRange !== 'undefined';
    } catch (e) {
        return false;
    }
}

// Abstracts constructing a Blob object, so it also works in older
// browsers that don't support the native Blob constructor. (i.e.
// old QtWebKit versions, at least).
// Abstracts constructing a Blob object, so it also works in older
// browsers that don't support the native Blob constructor. (i.e.
// old QtWebKit versions, at least).
function createBlob(parts, properties) {
    /* global BlobBuilder,MSBlobBuilder,MozBlobBuilder,WebKitBlobBuilder */
    parts = parts || [];
    properties = properties || {};
    try {
        return new Blob(parts, properties);
    } catch (e) {
        if (e.name !== 'TypeError') {
            throw e;
        }
        var Builder = typeof BlobBuilder !== 'undefined' ? BlobBuilder : typeof MSBlobBuilder !== 'undefined' ? MSBlobBuilder : typeof MozBlobBuilder !== 'undefined' ? MozBlobBuilder : WebKitBlobBuilder;
        var builder = new Builder();
        for (var i = 0; i < parts.length; i += 1) {
            builder.append(parts[i]);
        }
        return builder.getBlob(properties.type);
    }
}

// This is CommonJS because lie is an external dependency, so Rollup
// can just ignore it.
if (typeof Promise === 'undefined') {
    // In the "nopromises" build this will just throw if you don't have
    // a global promise object, but it would throw anyway later.
    _dereq_(3);
}
var Promise$1 = Promise;

function executeCallback(promise, callback) {
    if (callback) {
        promise.then(function (result) {
            callback(null, result);
        }, function (error) {
            callback(error);
        });
    }
}

function executeTwoCallbacks(promise, callback, errorCallback) {
    if (typeof callback === 'function') {
        promise.then(callback);
    }

    if (typeof errorCallback === 'function') {
        promise["catch"](errorCallback);
    }
}

function normalizeKey(key) {
    // Cast the key to a string, as that's all we can set as a key.
    if (typeof key !== 'string') {
        console.warn(key + ' used as a key, but it is not a string.');
        key = String(key);
    }

    return key;
}

function getCallback() {
    if (arguments.length && typeof arguments[arguments.length - 1] === 'function') {
        return arguments[arguments.length - 1];
    }
}

// Some code originally from async_storage.js in
// [Gaia](https://github.com/mozilla-b2g/gaia).

var DETECT_BLOB_SUPPORT_STORE = 'local-forage-detect-blob-support';
var supportsBlobs = void 0;
var dbContexts = {};
var toString = Object.prototype.toString;

// Transaction Modes
var READ_ONLY = 'readonly';
var READ_WRITE = 'readwrite';

// Transform a binary string to an array buffer, because otherwise
// weird stuff happens when you try to work with the binary string directly.
// It is known.
// From http://stackoverflow.com/questions/14967647/ (continues on next line)
// encode-decode-image-with-base64-breaks-image (2013-04-21)
function _binStringToArrayBuffer(bin) {
    var length = bin.length;
    var buf = new ArrayBuffer(length);
    var arr = new Uint8Array(buf);
    for (var i = 0; i < length; i++) {
        arr[i] = bin.charCodeAt(i);
    }
    return buf;
}

//
// Blobs are not supported in all versions of IndexedDB, notably
// Chrome <37 and Android <5. In those versions, storing a blob will throw.
//
// Various other blob bugs exist in Chrome v37-42 (inclusive).
// Detecting them is expensive and confusing to users, and Chrome 37-42
// is at very low usage worldwide, so we do a hacky userAgent check instead.
//
// content-type bug: https://code.google.com/p/chromium/issues/detail?id=408120
// 404 bug: https://code.google.com/p/chromium/issues/detail?id=447916
// FileReader bug: https://code.google.com/p/chromium/issues/detail?id=447836
//
// Code borrowed from PouchDB. See:
// https://github.com/pouchdb/pouchdb/blob/master/packages/node_modules/pouchdb-adapter-idb/src/blobSupport.js
//
function _checkBlobSupportWithoutCaching(idb) {
    return new Promise$1(function (resolve) {
        var txn = idb.transaction(DETECT_BLOB_SUPPORT_STORE, READ_WRITE);
        var blob = createBlob(['']);
        txn.objectStore(DETECT_BLOB_SUPPORT_STORE).put(blob, 'key');

        txn.onabort = function (e) {
            // If the transaction aborts now its due to not being able to
            // write to the database, likely due to the disk being full
            e.preventDefault();
            e.stopPropagation();
            resolve(false);
        };

        txn.oncomplete = function () {
            var matchedChrome = navigator.userAgent.match(/Chrome\/(\d+)/);
            var matchedEdge = navigator.userAgent.match(/Edge\//);
            // MS Edge pretends to be Chrome 42:
            // https://msdn.microsoft.com/en-us/library/hh869301%28v=vs.85%29.aspx
            resolve(matchedEdge || !matchedChrome || parseInt(matchedChrome[1], 10) >= 43);
        };
    })["catch"](function () {
        return false; // error, so assume unsupported
    });
}

function _checkBlobSupport(idb) {
    if (typeof supportsBlobs === 'boolean') {
        return Promise$1.resolve(supportsBlobs);
    }
    return _checkBlobSupportWithoutCaching(idb).then(function (value) {
        supportsBlobs = value;
        return supportsBlobs;
    });
}

function _deferReadiness(dbInfo) {
    var dbContext = dbContexts[dbInfo.name];

    // Create a deferred object representing the current database operation.
    var deferredOperation = {};

    deferredOperation.promise = new Promise$1(function (resolve, reject) {
        deferredOperation.resolve = resolve;
        deferredOperation.reject = reject;
    });

    // Enqueue the deferred operation.
    dbContext.deferredOperations.push(deferredOperation);

    // Chain its promise to the database readiness.
    if (!dbContext.dbReady) {
        dbContext.dbReady = deferredOperation.promise;
    } else {
        dbContext.dbReady = dbContext.dbReady.then(function () {
            return deferredOperation.promise;
        });
    }
}

function _advanceReadiness(dbInfo) {
    var dbContext = dbContexts[dbInfo.name];

    // Dequeue a deferred operation.
    var deferredOperation = dbContext.deferredOperations.pop();

    // Resolve its promise (which is part of the database readiness
    // chain of promises).
    if (deferredOperation) {
        deferredOperation.resolve();
        return deferredOperation.promise;
    }
}

function _rejectReadiness(dbInfo, err) {
    var dbContext = dbContexts[dbInfo.name];

    // Dequeue a deferred operation.
    var deferredOperation = dbContext.deferredOperations.pop();

    // Reject its promise (which is part of the database readiness
    // chain of promises).
    if (deferredOperation) {
        deferredOperation.reject(err);
        return deferredOperation.promise;
    }
}

function _getConnection(dbInfo, upgradeNeeded) {
    return new Promise$1(function (resolve, reject) {
        dbContexts[dbInfo.name] = dbContexts[dbInfo.name] || createDbContext();

        if (dbInfo.db) {
            if (upgradeNeeded) {
                _deferReadiness(dbInfo);
                dbInfo.db.close();
            } else {
                return resolve(dbInfo.db);
            }
        }

        var dbArgs = [dbInfo.name];

        if (upgradeNeeded) {
            dbArgs.push(dbInfo.version);
        }

        var openreq = idb.open.apply(idb, dbArgs);

        if (upgradeNeeded) {
            openreq.onupgradeneeded = function (e) {
                var db = openreq.result;
                try {
                    db.createObjectStore(dbInfo.storeName);
                    if (e.oldVersion <= 1) {
                        // Added when support for blob shims was added
                        db.createObjectStore(DETECT_BLOB_SUPPORT_STORE);
                    }
                } catch (ex) {
                    if (ex.name === 'ConstraintError') {
                        console.warn('The database "' + dbInfo.name + '"' + ' has been upgraded from version ' + e.oldVersion + ' to version ' + e.newVersion + ', but the storage "' + dbInfo.storeName + '" already exists.');
                    } else {
                        throw ex;
                    }
                }
            };
        }

        openreq.onerror = function (e) {
            e.preventDefault();
            reject(openreq.error);
        };

        openreq.onsuccess = function () {
            var db = openreq.result;
            db.onversionchange = function (e) {
                // Triggered when the database is modified (e.g. adding an objectStore) or
                // deleted (even when initiated by other sessions in different tabs).
                // Closing the connection here prevents those operations from being blocked.
                // If the database is accessed again later by this instance, the connection
                // will be reopened or the database recreated as needed.
                e.target.close();
            };
            resolve(db);
            _advanceReadiness(dbInfo);
        };
    });
}

function _getOriginalConnection(dbInfo) {
    return _getConnection(dbInfo, false);
}

function _getUpgradedConnection(dbInfo) {
    return _getConnection(dbInfo, true);
}

function _isUpgradeNeeded(dbInfo, defaultVersion) {
    if (!dbInfo.db) {
        return true;
    }

    var isNewStore = !dbInfo.db.objectStoreNames.contains(dbInfo.storeName);
    var isDowngrade = dbInfo.version < dbInfo.db.version;
    var isUpgrade = dbInfo.version > dbInfo.db.version;

    if (isDowngrade) {
        // If the version is not the default one
        // then warn for impossible downgrade.
        if (dbInfo.version !== defaultVersion) {
            console.warn('The database "' + dbInfo.name + '"' + " can't be downgraded from version " + dbInfo.db.version + ' to version ' + dbInfo.version + '.');
        }
        // Align the versions to prevent errors.
        dbInfo.version = dbInfo.db.version;
    }

    if (isUpgrade || isNewStore) {
        // If the store is new then increment the version (if needed).
        // This will trigger an "upgradeneeded" event which is required
        // for creating a store.
        if (isNewStore) {
            var incVersion = dbInfo.db.version + 1;
            if (incVersion > dbInfo.version) {
                dbInfo.version = incVersion;
            }
        }

        return true;
    }

    return false;
}

// encode a blob for indexeddb engines that don't support blobs
function _encodeBlob(blob) {
    return new Promise$1(function (resolve, reject) {
        var reader = new FileReader();
        reader.onerror = reject;
        reader.onloadend = function (e) {
            var base64 = btoa(e.target.result || '');
            resolve({
                __local_forage_encoded_blob: true,
                data: base64,
                type: blob.type
            });
        };
        reader.readAsBinaryString(blob);
    });
}

// decode an encoded blob
function _decodeBlob(encodedBlob) {
    var arrayBuff = _binStringToArrayBuffer(atob(encodedBlob.data));
    return createBlob([arrayBuff], { type: encodedBlob.type });
}

// is this one of our fancy encoded blobs?
function _isEncodedBlob(value) {
    return value && value.__local_forage_encoded_blob;
}

// Specialize the default `ready()` function by making it dependent
// on the current database operations. Thus, the driver will be actually
// ready when it's been initialized (default) *and* there are no pending
// operations on the database (initiated by some other instances).
function _fullyReady(callback) {
    var self = this;

    var promise = self._initReady().then(function () {
        var dbContext = dbContexts[self._dbInfo.name];

        if (dbContext && dbContext.dbReady) {
            return dbContext.dbReady;
        }
    });

    executeTwoCallbacks(promise, callback, callback);
    return promise;
}

// Try to establish a new db connection to replace the
// current one which is broken (i.e. experiencing
// InvalidStateError while creating a transaction).
function _tryReconnect(dbInfo) {
    _deferReadiness(dbInfo);

    var dbContext = dbContexts[dbInfo.name];
    var forages = dbContext.forages;

    for (var i = 0; i < forages.length; i++) {
        var forage = forages[i];
        if (forage._dbInfo.db) {
            forage._dbInfo.db.close();
            forage._dbInfo.db = null;
        }
    }
    dbInfo.db = null;

    return _getOriginalConnection(dbInfo).then(function (db) {
        dbInfo.db = db;
        if (_isUpgradeNeeded(dbInfo)) {
            // Reopen the database for upgrading.
            return _getUpgradedConnection(dbInfo);
        }
        return db;
    }).then(function (db) {
        // store the latest db reference
        // in case the db was upgraded
        dbInfo.db = dbContext.db = db;
        for (var i = 0; i < forages.length; i++) {
            forages[i]._dbInfo.db = db;
        }
    })["catch"](function (err) {
        _rejectReadiness(dbInfo, err);
        throw err;
    });
}

// FF doesn't like Promises (micro-tasks) and IDDB store operations,
// so we have to do it with callbacks
function createTransaction(dbInfo, mode, callback, retries) {
    if (retries === undefined) {
        retries = 1;
    }

    try {
        var tx = dbInfo.db.transaction(dbInfo.storeName, mode);
        callback(null, tx);
    } catch (err) {
        if (retries > 0 && (!dbInfo.db || err.name === 'InvalidStateError' || err.name === 'NotFoundError')) {
            return Promise$1.resolve().then(function () {
                if (!dbInfo.db || err.name === 'NotFoundError' && !dbInfo.db.objectStoreNames.contains(dbInfo.storeName) && dbInfo.version <= dbInfo.db.version) {
                    // increase the db version, to create the new ObjectStore
                    if (dbInfo.db) {
                        dbInfo.version = dbInfo.db.version + 1;
                    }
                    // Reopen the database for upgrading.
                    return _getUpgradedConnection(dbInfo);
                }
            }).then(function () {
                return _tryReconnect(dbInfo).then(function () {
                    createTransaction(dbInfo, mode, callback, retries - 1);
                });
            })["catch"](callback);
        }

        callback(err);
    }
}

function createDbContext() {
    return {
        // Running localForages sharing a database.
        forages: [],
        // Shared database.
        db: null,
        // Database readiness (promise).
        dbReady: null,
        // Deferred operations on the database.
        deferredOperations: []
    };
}

// Open the IndexedDB database (automatically creates one if one didn't
// previously exist), using any options set in the config.
function _initStorage(options) {
    var self = this;
    var dbInfo = {
        db: null
    };

    if (options) {
        for (var i in options) {
            dbInfo[i] = options[i];
        }
    }

    // Get the current context of the database;
    var dbContext = dbContexts[dbInfo.name];

    // ...or create a new context.
    if (!dbContext) {
        dbContext = createDbContext();
        // Register the new context in the global container.
        dbContexts[dbInfo.name] = dbContext;
    }

    // Register itself as a running localForage in the current context.
    dbContext.forages.push(self);

    // Replace the default `ready()` function with the specialized one.
    if (!self._initReady) {
        self._initReady = self.ready;
        self.ready = _fullyReady;
    }

    // Create an array of initialization states of the related localForages.
    var initPromises = [];

    function ignoreErrors() {
        // Don't handle errors here,
        // just makes sure related localForages aren't pending.
        return Promise$1.resolve();
    }

    for (var j = 0; j < dbContext.forages.length; j++) {
        var forage = dbContext.forages[j];
        if (forage !== self) {
            // Don't wait for itself...
            initPromises.push(forage._initReady()["catch"](ignoreErrors));
        }
    }

    // Take a snapshot of the related localForages.
    var forages = dbContext.forages.slice(0);

    // Initialize the connection process only when
    // all the related localForages aren't pending.
    return Promise$1.all(initPromises).then(function () {
        dbInfo.db = dbContext.db;
        // Get the connection or open a new one without upgrade.
        return _getOriginalConnection(dbInfo);
    }).then(function (db) {
        dbInfo.db = db;
        if (_isUpgradeNeeded(dbInfo, self._defaultConfig.version)) {
            // Reopen the database for upgrading.
            return _getUpgradedConnection(dbInfo);
        }
        return db;
    }).then(function (db) {
        dbInfo.db = dbContext.db = db;
        self._dbInfo = dbInfo;
        // Share the final connection amongst related localForages.
        for (var k = 0; k < forages.length; k++) {
            var forage = forages[k];
            if (forage !== self) {
                // Self is already up-to-date.
                forage._dbInfo.db = dbInfo.db;
                forage._dbInfo.version = dbInfo.version;
            }
        }
    });
}

function getItem(key, callback) {
    var self = this;

    key = normalizeKey(key);

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            createTransaction(self._dbInfo, READ_ONLY, function (err, transaction) {
                if (err) {
                    return reject(err);
                }

                try {
                    var store = transaction.objectStore(self._dbInfo.storeName);
                    var req = store.get(key);

                    req.onsuccess = function () {
                        var value = req.result;
                        if (value === undefined) {
                            value = null;
                        }
                        if (_isEncodedBlob(value)) {
                            value = _decodeBlob(value);
                        }
                        resolve(value);
                    };

                    req.onerror = function () {
                        reject(req.error);
                    };
                } catch (e) {
                    reject(e);
                }
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

// Iterate over all items stored in database.
function iterate(iterator, callback) {
    var self = this;

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            createTransaction(self._dbInfo, READ_ONLY, function (err, transaction) {
                if (err) {
                    return reject(err);
                }

                try {
                    var store = transaction.objectStore(self._dbInfo.storeName);
                    var req = store.openCursor();
                    var iterationNumber = 1;

                    req.onsuccess = function () {
                        var cursor = req.result;

                        if (cursor) {
                            var value = cursor.value;
                            if (_isEncodedBlob(value)) {
                                value = _decodeBlob(value);
                            }
                            var result = iterator(value, cursor.key, iterationNumber++);

                            // when the iterator callback returns any
                            // (non-`undefined`) value, then we stop
                            // the iteration immediately
                            if (result !== void 0) {
                                resolve(result);
                            } else {
                                cursor["continue"]();
                            }
                        } else {
                            resolve();
                        }
                    };

                    req.onerror = function () {
                        reject(req.error);
                    };
                } catch (e) {
                    reject(e);
                }
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);

    return promise;
}

function setItem(key, value, callback) {
    var self = this;

    key = normalizeKey(key);

    var promise = new Promise$1(function (resolve, reject) {
        var dbInfo;
        self.ready().then(function () {
            dbInfo = self._dbInfo;
            if (toString.call(value) === '[object Blob]') {
                return _checkBlobSupport(dbInfo.db).then(function (blobSupport) {
                    if (blobSupport) {
                        return value;
                    }
                    return _encodeBlob(value);
                });
            }
            return value;
        }).then(function (value) {
            createTransaction(self._dbInfo, READ_WRITE, function (err, transaction) {
                if (err) {
                    return reject(err);
                }

                try {
                    var store = transaction.objectStore(self._dbInfo.storeName);

                    // The reason we don't _save_ null is because IE 10 does
                    // not support saving the `null` type in IndexedDB. How
                    // ironic, given the bug below!
                    // See: https://github.com/mozilla/localForage/issues/161
                    if (value === null) {
                        value = undefined;
                    }

                    var req = store.put(value, key);

                    transaction.oncomplete = function () {
                        // Cast to undefined so the value passed to
                        // callback/promise is the same as what one would get out
                        // of `getItem()` later. This leads to some weirdness
                        // (setItem('foo', undefined) will return `null`), but
                        // it's not my fault localStorage is our baseline and that
                        // it's weird.
                        if (value === undefined) {
                            value = null;
                        }

                        resolve(value);
                    };
                    transaction.onabort = transaction.onerror = function () {
                        var err = req.error ? req.error : req.transaction.error;
                        reject(err);
                    };
                } catch (e) {
                    reject(e);
                }
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

function removeItem(key, callback) {
    var self = this;

    key = normalizeKey(key);

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            createTransaction(self._dbInfo, READ_WRITE, function (err, transaction) {
                if (err) {
                    return reject(err);
                }

                try {
                    var store = transaction.objectStore(self._dbInfo.storeName);
                    // We use a Grunt task to make this safe for IE and some
                    // versions of Android (including those used by Cordova).
                    // Normally IE won't like `.delete()` and will insist on
                    // using `['delete']()`, but we have a build step that
                    // fixes this for us now.
                    var req = store["delete"](key);
                    transaction.oncomplete = function () {
                        resolve();
                    };

                    transaction.onerror = function () {
                        reject(req.error);
                    };

                    // The request will be also be aborted if we've exceeded our storage
                    // space.
                    transaction.onabort = function () {
                        var err = req.error ? req.error : req.transaction.error;
                        reject(err);
                    };
                } catch (e) {
                    reject(e);
                }
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

function clear(callback) {
    var self = this;

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            createTransaction(self._dbInfo, READ_WRITE, function (err, transaction) {
                if (err) {
                    return reject(err);
                }

                try {
                    var store = transaction.objectStore(self._dbInfo.storeName);
                    var req = store.clear();

                    transaction.oncomplete = function () {
                        resolve();
                    };

                    transaction.onabort = transaction.onerror = function () {
                        var err = req.error ? req.error : req.transaction.error;
                        reject(err);
                    };
                } catch (e) {
                    reject(e);
                }
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

function length(callback) {
    var self = this;

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            createTransaction(self._dbInfo, READ_ONLY, function (err, transaction) {
                if (err) {
                    return reject(err);
                }

                try {
                    var store = transaction.objectStore(self._dbInfo.storeName);
                    var req = store.count();

                    req.onsuccess = function () {
                        resolve(req.result);
                    };

                    req.onerror = function () {
                        reject(req.error);
                    };
                } catch (e) {
                    reject(e);
                }
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

function key(n, callback) {
    var self = this;

    var promise = new Promise$1(function (resolve, reject) {
        if (n < 0) {
            resolve(null);

            return;
        }

        self.ready().then(function () {
            createTransaction(self._dbInfo, READ_ONLY, function (err, transaction) {
                if (err) {
                    return reject(err);
                }

                try {
                    var store = transaction.objectStore(self._dbInfo.storeName);
                    var advanced = false;
                    var req = store.openKeyCursor();

                    req.onsuccess = function () {
                        var cursor = req.result;
                        if (!cursor) {
                            // this means there weren't enough keys
                            resolve(null);

                            return;
                        }

                        if (n === 0) {
                            // We have the first key, return it if that's what they
                            // wanted.
                            resolve(cursor.key);
                        } else {
                            if (!advanced) {
                                // Otherwise, ask the cursor to skip ahead n
                                // records.
                                advanced = true;
                                cursor.advance(n);
                            } else {
                                // When we get here, we've got the nth key.
                                resolve(cursor.key);
                            }
                        }
                    };

                    req.onerror = function () {
                        reject(req.error);
                    };
                } catch (e) {
                    reject(e);
                }
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

function keys(callback) {
    var self = this;

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            createTransaction(self._dbInfo, READ_ONLY, function (err, transaction) {
                if (err) {
                    return reject(err);
                }

                try {
                    var store = transaction.objectStore(self._dbInfo.storeName);
                    var req = store.openKeyCursor();
                    var keys = [];

                    req.onsuccess = function () {
                        var cursor = req.result;

                        if (!cursor) {
                            resolve(keys);
                            return;
                        }

                        keys.push(cursor.key);
                        cursor["continue"]();
                    };

                    req.onerror = function () {
                        reject(req.error);
                    };
                } catch (e) {
                    reject(e);
                }
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

function dropInstance(options, callback) {
    callback = getCallback.apply(this, arguments);

    var currentConfig = this.config();
    options = typeof options !== 'function' && options || {};
    if (!options.name) {
        options.name = options.name || currentConfig.name;
        options.storeName = options.storeName || currentConfig.storeName;
    }

    var self = this;
    var promise;
    if (!options.name) {
        promise = Promise$1.reject('Invalid arguments');
    } else {
        var isCurrentDb = options.name === currentConfig.name && self._dbInfo.db;

        var dbPromise = isCurrentDb ? Promise$1.resolve(self._dbInfo.db) : _getOriginalConnection(options).then(function (db) {
            var dbContext = dbContexts[options.name];
            var forages = dbContext.forages;
            dbContext.db = db;
            for (var i = 0; i < forages.length; i++) {
                forages[i]._dbInfo.db = db;
            }
            return db;
        });

        if (!options.storeName) {
            promise = dbPromise.then(function (db) {
                _deferReadiness(options);

                var dbContext = dbContexts[options.name];
                var forages = dbContext.forages;

                db.close();
                for (var i = 0; i < forages.length; i++) {
                    var forage = forages[i];
                    forage._dbInfo.db = null;
                }

                var dropDBPromise = new Promise$1(function (resolve, reject) {
                    var req = idb.deleteDatabase(options.name);

                    req.onerror = function () {
                        var db = req.result;
                        if (db) {
                            db.close();
                        }
                        reject(req.error);
                    };

                    req.onblocked = function () {
                        // Closing all open connections in onversionchange handler should prevent this situation, but if
                        // we do get here, it just means the request remains pending - eventually it will succeed or error
                        console.warn('dropInstance blocked for database "' + options.name + '" until all open connections are closed');
                    };

                    req.onsuccess = function () {
                        var db = req.result;
                        if (db) {
                            db.close();
                        }
                        resolve(db);
                    };
                });

                return dropDBPromise.then(function (db) {
                    dbContext.db = db;
                    for (var i = 0; i < forages.length; i++) {
                        var _forage = forages[i];
                        _advanceReadiness(_forage._dbInfo);
                    }
                })["catch"](function (err) {
                    (_rejectReadiness(options, err) || Promise$1.resolve())["catch"](function () {});
                    throw err;
                });
            });
        } else {
            promise = dbPromise.then(function (db) {
                if (!db.objectStoreNames.contains(options.storeName)) {
                    return;
                }

                var newVersion = db.version + 1;

                _deferReadiness(options);

                var dbContext = dbContexts[options.name];
                var forages = dbContext.forages;

                db.close();
                for (var i = 0; i < forages.length; i++) {
                    var forage = forages[i];
                    forage._dbInfo.db = null;
                    forage._dbInfo.version = newVersion;
                }

                var dropObjectPromise = new Promise$1(function (resolve, reject) {
                    var req = idb.open(options.name, newVersion);

                    req.onerror = function (err) {
                        var db = req.result;
                        db.close();
                        reject(err);
                    };

                    req.onupgradeneeded = function () {
                        var db = req.result;
                        db.deleteObjectStore(options.storeName);
                    };

                    req.onsuccess = function () {
                        var db = req.result;
                        db.close();
                        resolve(db);
                    };
                });

                return dropObjectPromise.then(function (db) {
                    dbContext.db = db;
                    for (var j = 0; j < forages.length; j++) {
                        var _forage2 = forages[j];
                        _forage2._dbInfo.db = db;
                        _advanceReadiness(_forage2._dbInfo);
                    }
                })["catch"](function (err) {
                    (_rejectReadiness(options, err) || Promise$1.resolve())["catch"](function () {});
                    throw err;
                });
            });
        }
    }

    executeCallback(promise, callback);
    return promise;
}

var asyncStorage = {
    _driver: 'asyncStorage',
    _initStorage: _initStorage,
    _support: isIndexedDBValid(),
    iterate: iterate,
    getItem: getItem,
    setItem: setItem,
    removeItem: removeItem,
    clear: clear,
    length: length,
    key: key,
    keys: keys,
    dropInstance: dropInstance
};

function isWebSQLValid() {
    return typeof openDatabase === 'function';
}

// Sadly, the best way to save binary data in WebSQL/localStorage is serializing
// it to Base64, so this is how we store it to prevent very strange errors with less
// verbose ways of binary <-> string data storage.
var BASE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

var BLOB_TYPE_PREFIX = '~~local_forage_type~';
var BLOB_TYPE_PREFIX_REGEX = /^~~local_forage_type~([^~]+)~/;

var SERIALIZED_MARKER = '__lfsc__:';
var SERIALIZED_MARKER_LENGTH = SERIALIZED_MARKER.length;

// OMG the serializations!
var TYPE_ARRAYBUFFER = 'arbf';
var TYPE_BLOB = 'blob';
var TYPE_INT8ARRAY = 'si08';
var TYPE_UINT8ARRAY = 'ui08';
var TYPE_UINT8CLAMPEDARRAY = 'uic8';
var TYPE_INT16ARRAY = 'si16';
var TYPE_INT32ARRAY = 'si32';
var TYPE_UINT16ARRAY = 'ur16';
var TYPE_UINT32ARRAY = 'ui32';
var TYPE_FLOAT32ARRAY = 'fl32';
var TYPE_FLOAT64ARRAY = 'fl64';
var TYPE_SERIALIZED_MARKER_LENGTH = SERIALIZED_MARKER_LENGTH + TYPE_ARRAYBUFFER.length;

var toString$1 = Object.prototype.toString;

function stringToBuffer(serializedString) {
    // Fill the string into a ArrayBuffer.
    var bufferLength = serializedString.length * 0.75;
    var len = serializedString.length;
    var i;
    var p = 0;
    var encoded1, encoded2, encoded3, encoded4;

    if (serializedString[serializedString.length - 1] === '=') {
        bufferLength--;
        if (serializedString[serializedString.length - 2] === '=') {
            bufferLength--;
        }
    }

    var buffer = new ArrayBuffer(bufferLength);
    var bytes = new Uint8Array(buffer);

    for (i = 0; i < len; i += 4) {
        encoded1 = BASE_CHARS.indexOf(serializedString[i]);
        encoded2 = BASE_CHARS.indexOf(serializedString[i + 1]);
        encoded3 = BASE_CHARS.indexOf(serializedString[i + 2]);
        encoded4 = BASE_CHARS.indexOf(serializedString[i + 3]);

        /*jslint bitwise: true */
        bytes[p++] = encoded1 << 2 | encoded2 >> 4;
        bytes[p++] = (encoded2 & 15) << 4 | encoded3 >> 2;
        bytes[p++] = (encoded3 & 3) << 6 | encoded4 & 63;
    }
    return buffer;
}

// Converts a buffer to a string to store, serialized, in the backend
// storage library.
function bufferToString(buffer) {
    // base64-arraybuffer
    var bytes = new Uint8Array(buffer);
    var base64String = '';
    var i;

    for (i = 0; i < bytes.length; i += 3) {
        /*jslint bitwise: true */
        base64String += BASE_CHARS[bytes[i] >> 2];
        base64String += BASE_CHARS[(bytes[i] & 3) << 4 | bytes[i + 1] >> 4];
        base64String += BASE_CHARS[(bytes[i + 1] & 15) << 2 | bytes[i + 2] >> 6];
        base64String += BASE_CHARS[bytes[i + 2] & 63];
    }

    if (bytes.length % 3 === 2) {
        base64String = base64String.substring(0, base64String.length - 1) + '=';
    } else if (bytes.length % 3 === 1) {
        base64String = base64String.substring(0, base64String.length - 2) + '==';
    }

    return base64String;
}

// Serialize a value, afterwards executing a callback (which usually
// instructs the `setItem()` callback/promise to be executed). This is how
// we store binary data with localStorage.
function serialize(value, callback) {
    var valueType = '';
    if (value) {
        valueType = toString$1.call(value);
    }

    // Cannot use `value instanceof ArrayBuffer` or such here, as these
    // checks fail when running the tests using casper.js...
    //
    // TODO: See why those tests fail and use a better solution.
    if (value && (valueType === '[object ArrayBuffer]' || value.buffer && toString$1.call(value.buffer) === '[object ArrayBuffer]')) {
        // Convert binary arrays to a string and prefix the string with
        // a special marker.
        var buffer;
        var marker = SERIALIZED_MARKER;

        if (value instanceof ArrayBuffer) {
            buffer = value;
            marker += TYPE_ARRAYBUFFER;
        } else {
            buffer = value.buffer;

            if (valueType === '[object Int8Array]') {
                marker += TYPE_INT8ARRAY;
            } else if (valueType === '[object Uint8Array]') {
                marker += TYPE_UINT8ARRAY;
            } else if (valueType === '[object Uint8ClampedArray]') {
                marker += TYPE_UINT8CLAMPEDARRAY;
            } else if (valueType === '[object Int16Array]') {
                marker += TYPE_INT16ARRAY;
            } else if (valueType === '[object Uint16Array]') {
                marker += TYPE_UINT16ARRAY;
            } else if (valueType === '[object Int32Array]') {
                marker += TYPE_INT32ARRAY;
            } else if (valueType === '[object Uint32Array]') {
                marker += TYPE_UINT32ARRAY;
            } else if (valueType === '[object Float32Array]') {
                marker += TYPE_FLOAT32ARRAY;
            } else if (valueType === '[object Float64Array]') {
                marker += TYPE_FLOAT64ARRAY;
            } else {
                callback(new Error('Failed to get type for BinaryArray'));
            }
        }

        callback(marker + bufferToString(buffer));
    } else if (valueType === '[object Blob]') {
        // Conver the blob to a binaryArray and then to a string.
        var fileReader = new FileReader();

        fileReader.onload = function () {
            // Backwards-compatible prefix for the blob type.
            var str = BLOB_TYPE_PREFIX + value.type + '~' + bufferToString(this.result);

            callback(SERIALIZED_MARKER + TYPE_BLOB + str);
        };

        fileReader.readAsArrayBuffer(value);
    } else {
        try {
            callback(JSON.stringify(value));
        } catch (e) {
            console.error("Couldn't convert value into a JSON string: ", value);

            callback(null, e);
        }
    }
}

// Deserialize data we've inserted into a value column/field. We place
// special markers into our strings to mark them as encoded; this isn't
// as nice as a meta field, but it's the only sane thing we can do whilst
// keeping localStorage support intact.
//
// Oftentimes this will just deserialize JSON content, but if we have a
// special marker (SERIALIZED_MARKER, defined above), we will extract
// some kind of arraybuffer/binary data/typed array out of the string.
function deserialize(value) {
    // If we haven't marked this string as being specially serialized (i.e.
    // something other than serialized JSON), we can just return it and be
    // done with it.
    if (value.substring(0, SERIALIZED_MARKER_LENGTH) !== SERIALIZED_MARKER) {
        return JSON.parse(value);
    }

    // The following code deals with deserializing some kind of Blob or
    // TypedArray. First we separate out the type of data we're dealing
    // with from the data itself.
    var serializedString = value.substring(TYPE_SERIALIZED_MARKER_LENGTH);
    var type = value.substring(SERIALIZED_MARKER_LENGTH, TYPE_SERIALIZED_MARKER_LENGTH);

    var blobType;
    // Backwards-compatible blob type serialization strategy.
    // DBs created with older versions of localForage will simply not have the blob type.
    if (type === TYPE_BLOB && BLOB_TYPE_PREFIX_REGEX.test(serializedString)) {
        var matcher = serializedString.match(BLOB_TYPE_PREFIX_REGEX);
        blobType = matcher[1];
        serializedString = serializedString.substring(matcher[0].length);
    }
    var buffer = stringToBuffer(serializedString);

    // Return the right type based on the code/type set during
    // serialization.
    switch (type) {
        case TYPE_ARRAYBUFFER:
            return buffer;
        case TYPE_BLOB:
            return createBlob([buffer], { type: blobType });
        case TYPE_INT8ARRAY:
            return new Int8Array(buffer);
        case TYPE_UINT8ARRAY:
            return new Uint8Array(buffer);
        case TYPE_UINT8CLAMPEDARRAY:
            return new Uint8ClampedArray(buffer);
        case TYPE_INT16ARRAY:
            return new Int16Array(buffer);
        case TYPE_UINT16ARRAY:
            return new Uint16Array(buffer);
        case TYPE_INT32ARRAY:
            return new Int32Array(buffer);
        case TYPE_UINT32ARRAY:
            return new Uint32Array(buffer);
        case TYPE_FLOAT32ARRAY:
            return new Float32Array(buffer);
        case TYPE_FLOAT64ARRAY:
            return new Float64Array(buffer);
        default:
            throw new Error('Unkown type: ' + type);
    }
}

var localforageSerializer = {
    serialize: serialize,
    deserialize: deserialize,
    stringToBuffer: stringToBuffer,
    bufferToString: bufferToString
};

/*
 * Includes code from:
 *
 * base64-arraybuffer
 * https://github.com/niklasvh/base64-arraybuffer
 *
 * Copyright (c) 2012 Niklas von Hertzen
 * Licensed under the MIT license.
 */

function createDbTable(t, dbInfo, callback, errorCallback) {
    t.executeSql('CREATE TABLE IF NOT EXISTS ' + dbInfo.storeName + ' ' + '(id INTEGER PRIMARY KEY, key unique, value)', [], callback, errorCallback);
}

// Open the WebSQL database (automatically creates one if one didn't
// previously exist), using any options set in the config.
function _initStorage$1(options) {
    var self = this;
    var dbInfo = {
        db: null
    };

    if (options) {
        for (var i in options) {
            dbInfo[i] = typeof options[i] !== 'string' ? options[i].toString() : options[i];
        }
    }

    var dbInfoPromise = new Promise$1(function (resolve, reject) {
        // Open the database; the openDatabase API will automatically
        // create it for us if it doesn't exist.
        try {
            dbInfo.db = openDatabase(dbInfo.name, String(dbInfo.version), dbInfo.description, dbInfo.size);
        } catch (e) {
            return reject(e);
        }

        // Create our key/value table if it doesn't exist.
        dbInfo.db.transaction(function (t) {
            createDbTable(t, dbInfo, function () {
                self._dbInfo = dbInfo;
                resolve();
            }, function (t, error) {
                reject(error);
            });
        }, reject);
    });

    dbInfo.serializer = localforageSerializer;
    return dbInfoPromise;
}

function tryExecuteSql(t, dbInfo, sqlStatement, args, callback, errorCallback) {
    t.executeSql(sqlStatement, args, callback, function (t, error) {
        if (error.code === error.SYNTAX_ERR) {
            t.executeSql('SELECT name FROM sqlite_master ' + "WHERE type='table' AND name = ?", [dbInfo.storeName], function (t, results) {
                if (!results.rows.length) {
                    // if the table is missing (was deleted)
                    // re-create it table and retry
                    createDbTable(t, dbInfo, function () {
                        t.executeSql(sqlStatement, args, callback, errorCallback);
                    }, errorCallback);
                } else {
                    errorCallback(t, error);
                }
            }, errorCallback);
        } else {
            errorCallback(t, error);
        }
    }, errorCallback);
}

function getItem$1(key, callback) {
    var self = this;

    key = normalizeKey(key);

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            var dbInfo = self._dbInfo;
            dbInfo.db.transaction(function (t) {
                tryExecuteSql(t, dbInfo, 'SELECT * FROM ' + dbInfo.storeName + ' WHERE key = ? LIMIT 1', [key], function (t, results) {
                    var result = results.rows.length ? results.rows.item(0).value : null;

                    // Check to see if this is serialized content we need to
                    // unpack.
                    if (result) {
                        result = dbInfo.serializer.deserialize(result);
                    }

                    resolve(result);
                }, function (t, error) {
                    reject(error);
                });
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

function iterate$1(iterator, callback) {
    var self = this;

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            var dbInfo = self._dbInfo;

            dbInfo.db.transaction(function (t) {
                tryExecuteSql(t, dbInfo, 'SELECT * FROM ' + dbInfo.storeName, [], function (t, results) {
                    var rows = results.rows;
                    var length = rows.length;

                    for (var i = 0; i < length; i++) {
                        var item = rows.item(i);
                        var result = item.value;

                        // Check to see if this is serialized content
                        // we need to unpack.
                        if (result) {
                            result = dbInfo.serializer.deserialize(result);
                        }

                        result = iterator(result, item.key, i + 1);

                        // void(0) prevents problems with redefinition
                        // of `undefined`.
                        if (result !== void 0) {
                            resolve(result);
                            return;
                        }
                    }

                    resolve();
                }, function (t, error) {
                    reject(error);
                });
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

function _setItem(key, value, callback, retriesLeft) {
    var self = this;

    key = normalizeKey(key);

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            // The localStorage API doesn't return undefined values in an
            // "expected" way, so undefined is always cast to null in all
            // drivers. See: https://github.com/mozilla/localForage/pull/42
            if (value === undefined) {
                value = null;
            }

            // Save the original value to pass to the callback.
            var originalValue = value;

            var dbInfo = self._dbInfo;
            dbInfo.serializer.serialize(value, function (value, error) {
                if (error) {
                    reject(error);
                } else {
                    dbInfo.db.transaction(function (t) {
                        tryExecuteSql(t, dbInfo, 'INSERT OR REPLACE INTO ' + dbInfo.storeName + ' ' + '(key, value) VALUES (?, ?)', [key, value], function () {
                            resolve(originalValue);
                        }, function (t, error) {
                            reject(error);
                        });
                    }, function (sqlError) {
                        // The transaction failed; check
                        // to see if it's a quota error.
                        if (sqlError.code === sqlError.QUOTA_ERR) {
                            // We reject the callback outright for now, but
                            // it's worth trying to re-run the transaction.
                            // Even if the user accepts the prompt to use
                            // more storage on Safari, this error will
                            // be called.
                            //
                            // Try to re-run the transaction.
                            if (retriesLeft > 0) {
                                resolve(_setItem.apply(self, [key, originalValue, callback, retriesLeft - 1]));
                                return;
                            }
                            reject(sqlError);
                        }
                    });
                }
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

function setItem$1(key, value, callback) {
    return _setItem.apply(this, [key, value, callback, 1]);
}

function removeItem$1(key, callback) {
    var self = this;

    key = normalizeKey(key);

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            var dbInfo = self._dbInfo;
            dbInfo.db.transaction(function (t) {
                tryExecuteSql(t, dbInfo, 'DELETE FROM ' + dbInfo.storeName + ' WHERE key = ?', [key], function () {
                    resolve();
                }, function (t, error) {
                    reject(error);
                });
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

// Deletes every item in the table.
// TODO: Find out if this resets the AUTO_INCREMENT number.
function clear$1(callback) {
    var self = this;

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            var dbInfo = self._dbInfo;
            dbInfo.db.transaction(function (t) {
                tryExecuteSql(t, dbInfo, 'DELETE FROM ' + dbInfo.storeName, [], function () {
                    resolve();
                }, function (t, error) {
                    reject(error);
                });
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

// Does a simple `COUNT(key)` to get the number of items stored in
// localForage.
function length$1(callback) {
    var self = this;

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            var dbInfo = self._dbInfo;
            dbInfo.db.transaction(function (t) {
                // Ahhh, SQL makes this one soooooo easy.
                tryExecuteSql(t, dbInfo, 'SELECT COUNT(key) as c FROM ' + dbInfo.storeName, [], function (t, results) {
                    var result = results.rows.item(0).c;
                    resolve(result);
                }, function (t, error) {
                    reject(error);
                });
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

// Return the key located at key index X; essentially gets the key from a
// `WHERE id = ?`. This is the most efficient way I can think to implement
// this rarely-used (in my experience) part of the API, but it can seem
// inconsistent, because we do `INSERT OR REPLACE INTO` on `setItem()`, so
// the ID of each key will change every time it's updated. Perhaps a stored
// procedure for the `setItem()` SQL would solve this problem?
// TODO: Don't change ID on `setItem()`.
function key$1(n, callback) {
    var self = this;

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            var dbInfo = self._dbInfo;
            dbInfo.db.transaction(function (t) {
                tryExecuteSql(t, dbInfo, 'SELECT key FROM ' + dbInfo.storeName + ' WHERE id = ? LIMIT 1', [n + 1], function (t, results) {
                    var result = results.rows.length ? results.rows.item(0).key : null;
                    resolve(result);
                }, function (t, error) {
                    reject(error);
                });
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

function keys$1(callback) {
    var self = this;

    var promise = new Promise$1(function (resolve, reject) {
        self.ready().then(function () {
            var dbInfo = self._dbInfo;
            dbInfo.db.transaction(function (t) {
                tryExecuteSql(t, dbInfo, 'SELECT key FROM ' + dbInfo.storeName, [], function (t, results) {
                    var keys = [];

                    for (var i = 0; i < results.rows.length; i++) {
                        keys.push(results.rows.item(i).key);
                    }

                    resolve(keys);
                }, function (t, error) {
                    reject(error);
                });
            });
        })["catch"](reject);
    });

    executeCallback(promise, callback);
    return promise;
}

// https://www.w3.org/TR/webdatabase/#databases
// > There is no way to enumerate or delete the databases available for an origin from this API.
function getAllStoreNames(db) {
    return new Promise$1(function (resolve, reject) {
        db.transaction(function (t) {
            t.executeSql('SELECT name FROM sqlite_master ' + "WHERE type='table' AND name <> '__WebKitDatabaseInfoTable__'", [], function (t, results) {
                var storeNames = [];

                for (var i = 0; i < results.rows.length; i++) {
                    storeNames.push(results.rows.item(i).name);
                }

                resolve({
                    db: db,
                    storeNames: storeNames
                });
            }, function (t, error) {
                reject(error);
            });
        }, function (sqlError) {
            reject(sqlError);
        });
    });
}

function dropInstance$1(options, callback) {
    callback = getCallback.apply(this, arguments);

    var currentConfig = this.config();
    options = typeof options !== 'function' && options || {};
    if (!options.name) {
        options.name = options.name || currentConfig.name;
        options.storeName = options.storeName || currentConfig.storeName;
    }

    var self = this;
    var promise;
    if (!options.name) {
        promise = Promise$1.reject('Invalid arguments');
    } else {
        promise = new Promise$1(function (resolve) {
            var db;
            if (options.name === currentConfig.name) {
                // use the db reference of the current instance
                db = self._dbInfo.db;
            } else {
                db = openDatabase(options.name, '', '', 0);
            }

            if (!options.storeName) {
                // drop all database tables
                resolve(getAllStoreNames(db));
            } else {
                resolve({
                    db: db,
                    storeNames: [options.storeName]
                });
            }
        }).then(function (operationInfo) {
            return new Promise$1(function (resolve, reject) {
                operationInfo.db.transaction(function (t) {
                    function dropTable(storeName) {
                        return new Promise$1(function (resolve, reject) {
                            t.executeSql('DROP TABLE IF EXISTS ' + storeName, [], function () {
                                resolve();
                            }, function (t, error) {
                                reject(error);
                            });
                        });
                    }

                    var operations = [];
                    for (var i = 0, len = operationInfo.storeNames.length; i < len; i++) {
                        operations.push(dropTable(operationInfo.storeNames[i]));
                    }

                    Promise$1.all(operations).then(function () {
                        resolve();
                    })["catch"](function (e) {
                        reject(e);
                    });
                }, function (sqlError) {
                    reject(sqlError);
                });
            });
        });
    }

    executeCallback(promise, callback);
    return promise;
}

var webSQLStorage = {
    _driver: 'webSQLStorage',
    _initStorage: _initStorage$1,
    _support: isWebSQLValid(),
    iterate: iterate$1,
    getItem: getItem$1,
    setItem: setItem$1,
    removeItem: removeItem$1,
    clear: clear$1,
    length: length$1,
    key: key$1,
    keys: keys$1,
    dropInstance: dropInstance$1
};

function isLocalStorageValid() {
    try {
        return typeof localStorage !== 'undefined' && 'setItem' in localStorage &&
        // in IE8 typeof localStorage.setItem === 'object'
        !!localStorage.setItem;
    } catch (e) {
        return false;
    }
}

function _getKeyPrefix(options, defaultConfig) {
    var keyPrefix = options.name + '/';

    if (options.storeName !== defaultConfig.storeName) {
        keyPrefix += options.storeName + '/';
    }
    return keyPrefix;
}

// Check if localStorage throws when saving an item
function checkIfLocalStorageThrows() {
    var localStorageTestKey = '_localforage_support_test';

    try {
        localStorage.setItem(localStorageTestKey, true);
        localStorage.removeItem(localStorageTestKey);

        return false;
    } catch (e) {
        return true;
    }
}

// Check if localStorage is usable and allows to save an item
// This method checks if localStorage is usable in Safari Private Browsing
// mode, or in any other case where the available quota for localStorage
// is 0 and there wasn't any saved items yet.
function _isLocalStorageUsable() {
    return !checkIfLocalStorageThrows() || localStorage.length > 0;
}

// Config the localStorage backend, using options set in the config.
function _initStorage$2(options) {
    var self = this;
    var dbInfo = {};
    if (options) {
        for (var i in options) {
            dbInfo[i] = options[i];
        }
    }

    dbInfo.keyPrefix = _getKeyPrefix(options, self._defaultConfig);

    if (!_isLocalStorageUsable()) {
        return Promise$1.reject();
    }

    self._dbInfo = dbInfo;
    dbInfo.serializer = localforageSerializer;

    return Promise$1.resolve();
}

// Remove all keys from the datastore, effectively destroying all data in
// the app's key/value store!
function clear$2(callback) {
    var self = this;
    var promise = self.ready().then(function () {
        var keyPrefix = self._dbInfo.keyPrefix;

        for (var i = localStorage.length - 1; i >= 0; i--) {
            var key = localStorage.key(i);

            if (key.indexOf(keyPrefix) === 0) {
                localStorage.removeItem(key);
            }
        }
    });

    executeCallback(promise, callback);
    return promise;
}

// Retrieve an item from the store. Unlike the original async_storage
// library in Gaia, we don't modify return values at all. If a key's value
// is `undefined`, we pass that value to the callback function.
function getItem$2(key, callback) {
    var self = this;

    key = normalizeKey(key);

    var promise = self.ready().then(function () {
        var dbInfo = self._dbInfo;
        var result = localStorage.getItem(dbInfo.keyPrefix + key);

        // If a result was found, parse it from the serialized
        // string into a JS object. If result isn't truthy, the key
        // is likely undefined and we'll pass it straight to the
        // callback.
        if (result) {
            result = dbInfo.serializer.deserialize(result);
        }

        return result;
    });

    executeCallback(promise, callback);
    return promise;
}

// Iterate over all items in the store.
function iterate$2(iterator, callback) {
    var self = this;

    var promise = self.ready().then(function () {
        var dbInfo = self._dbInfo;
        var keyPrefix = dbInfo.keyPrefix;
        var keyPrefixLength = keyPrefix.length;
        var length = localStorage.length;

        // We use a dedicated iterator instead of the `i` variable below
        // so other keys we fetch in localStorage aren't counted in
        // the `iterationNumber` argument passed to the `iterate()`
        // callback.
        //
        // See: github.com/mozilla/localForage/pull/435#discussion_r38061530
        var iterationNumber = 1;

        for (var i = 0; i < length; i++) {
            var key = localStorage.key(i);
            if (key.indexOf(keyPrefix) !== 0) {
                continue;
            }
            var value = localStorage.getItem(key);

            // If a result was found, parse it from the serialized
            // string into a JS object. If result isn't truthy, the
            // key is likely undefined and we'll pass it straight
            // to the iterator.
            if (value) {
                value = dbInfo.serializer.deserialize(value);
            }

            value = iterator(value, key.substring(keyPrefixLength), iterationNumber++);

            if (value !== void 0) {
                return value;
            }
        }
    });

    executeCallback(promise, callback);
    return promise;
}

// Same as localStorage's key() method, except takes a callback.
function key$2(n, callback) {
    var self = this;
    var promise = self.ready().then(function () {
        var dbInfo = self._dbInfo;
        var result;
        try {
            result = localStorage.key(n);
        } catch (error) {
            result = null;
        }

        // Remove the prefix from the key, if a key is found.
        if (result) {
            result = result.substring(dbInfo.keyPrefix.length);
        }

        return result;
    });

    executeCallback(promise, callback);
    return promise;
}

function keys$2(callback) {
    var self = this;
    var promise = self.ready().then(function () {
        var dbInfo = self._dbInfo;
        var length = localStorage.length;
        var keys = [];

        for (var i = 0; i < length; i++) {
            var itemKey = localStorage.key(i);
            if (itemKey.indexOf(dbInfo.keyPrefix) === 0) {
                keys.push(itemKey.substring(dbInfo.keyPrefix.length));
            }
        }

        return keys;
    });

    executeCallback(promise, callback);
    return promise;
}

// Supply the number of keys in the datastore to the callback function.
function length$2(callback) {
    var self = this;
    var promise = self.keys().then(function (keys) {
        return keys.length;
    });

    executeCallback(promise, callback);
    return promise;
}

// Remove an item from the store, nice and simple.
function removeItem$2(key, callback) {
    var self = this;

    key = normalizeKey(key);

    var promise = self.ready().then(function () {
        var dbInfo = self._dbInfo;
        localStorage.removeItem(dbInfo.keyPrefix + key);
    });

    executeCallback(promise, callback);
    return promise;
}

// Set a key's value and run an optional callback once the value is set.
// Unlike Gaia's implementation, the callback function is passed the value,
// in case you want to operate on that value only after you're sure it
// saved, or something like that.
function setItem$2(key, value, callback) {
    var self = this;

    key = normalizeKey(key);

    var promise = self.ready().then(function () {
        // Convert undefined values to null.
        // https://github.com/mozilla/localForage/pull/42
        if (value === undefined) {
            value = null;
        }

        // Save the original value to pass to the callback.
        var originalValue = value;

        return new Promise$1(function (resolve, reject) {
            var dbInfo = self._dbInfo;
            dbInfo.serializer.serialize(value, function (value, error) {
                if (error) {
                    reject(error);
                } else {
                    try {
                        localStorage.setItem(dbInfo.keyPrefix + key, value);
                        resolve(originalValue);
                    } catch (e) {
                        // localStorage capacity exceeded.
                        // TODO: Make this a specific error/event.
                        if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
                            reject(e);
                        }
                        reject(e);
                    }
                }
            });
        });
    });

    executeCallback(promise, callback);
    return promise;
}

function dropInstance$2(options, callback) {
    callback = getCallback.apply(this, arguments);

    options = typeof options !== 'function' && options || {};
    if (!options.name) {
        var currentConfig = this.config();
        options.name = options.name || currentConfig.name;
        options.storeName = options.storeName || currentConfig.storeName;
    }

    var self = this;
    var promise;
    if (!options.name) {
        promise = Promise$1.reject('Invalid arguments');
    } else {
        promise = new Promise$1(function (resolve) {
            if (!options.storeName) {
                resolve(options.name + '/');
            } else {
                resolve(_getKeyPrefix(options, self._defaultConfig));
            }
        }).then(function (keyPrefix) {
            for (var i = localStorage.length - 1; i >= 0; i--) {
                var key = localStorage.key(i);

                if (key.indexOf(keyPrefix) === 0) {
                    localStorage.removeItem(key);
                }
            }
        });
    }

    executeCallback(promise, callback);
    return promise;
}

var localStorageWrapper = {
    _driver: 'localStorageWrapper',
    _initStorage: _initStorage$2,
    _support: isLocalStorageValid(),
    iterate: iterate$2,
    getItem: getItem$2,
    setItem: setItem$2,
    removeItem: removeItem$2,
    clear: clear$2,
    length: length$2,
    key: key$2,
    keys: keys$2,
    dropInstance: dropInstance$2
};

var sameValue = function sameValue(x, y) {
    return x === y || typeof x === 'number' && typeof y === 'number' && isNaN(x) && isNaN(y);
};

var includes = function includes(array, searchElement) {
    var len = array.length;
    var i = 0;
    while (i < len) {
        if (sameValue(array[i], searchElement)) {
            return true;
        }
        i++;
    }

    return false;
};

var isArray = Array.isArray || function (arg) {
    return Object.prototype.toString.call(arg) === '[object Array]';
};

// Drivers are stored here when `defineDriver()` is called.
// They are shared across all instances of localForage.
var DefinedDrivers = {};

var DriverSupport = {};

var DefaultDrivers = {
    INDEXEDDB: asyncStorage,
    WEBSQL: webSQLStorage,
    LOCALSTORAGE: localStorageWrapper
};

var DefaultDriverOrder = [DefaultDrivers.INDEXEDDB._driver, DefaultDrivers.WEBSQL._driver, DefaultDrivers.LOCALSTORAGE._driver];

var OptionalDriverMethods = ['dropInstance'];

var LibraryMethods = ['clear', 'getItem', 'iterate', 'key', 'keys', 'length', 'removeItem', 'setItem'].concat(OptionalDriverMethods);

var DefaultConfig = {
    description: '',
    driver: DefaultDriverOrder.slice(),
    name: 'localforage',
    // Default DB size is _JUST UNDER_ 5MB, as it's the highest size
    // we can use without a prompt.
    size: 4980736,
    storeName: 'keyvaluepairs',
    version: 1.0
};

function callWhenReady(localForageInstance, libraryMethod) {
    localForageInstance[libraryMethod] = function () {
        var _args = arguments;
        return localForageInstance.ready().then(function () {
            return localForageInstance[libraryMethod].apply(localForageInstance, _args);
        });
    };
}

function extend() {
    for (var i = 1; i < arguments.length; i++) {
        var arg = arguments[i];

        if (arg) {
            for (var _key in arg) {
                if (arg.hasOwnProperty(_key)) {
                    if (isArray(arg[_key])) {
                        arguments[0][_key] = arg[_key].slice();
                    } else {
                        arguments[0][_key] = arg[_key];
                    }
                }
            }
        }
    }

    return arguments[0];
}

var LocalForage = function () {
    function LocalForage(options) {
        _classCallCheck(this, LocalForage);

        for (var driverTypeKey in DefaultDrivers) {
            if (DefaultDrivers.hasOwnProperty(driverTypeKey)) {
                var driver = DefaultDrivers[driverTypeKey];
                var driverName = driver._driver;
                this[driverTypeKey] = driverName;

                if (!DefinedDrivers[driverName]) {
                    // we don't need to wait for the promise,
                    // since the default drivers can be defined
                    // in a blocking manner
                    this.defineDriver(driver);
                }
            }
        }

        this._defaultConfig = extend({}, DefaultConfig);
        this._config = extend({}, this._defaultConfig, options);
        this._driverSet = null;
        this._initDriver = null;
        this._ready = false;
        this._dbInfo = null;

        this._wrapLibraryMethodsWithReady();
        this.setDriver(this._config.driver)["catch"](function () {});
    }

    // Set any config values for localForage; can be called anytime before
    // the first API call (e.g. `getItem`, `setItem`).
    // We loop through options so we don't overwrite existing config
    // values.


    LocalForage.prototype.config = function config(options) {
        // If the options argument is an object, we use it to set values.
        // Otherwise, we return either a specified config value or all
        // config values.
        if ((typeof options === 'undefined' ? 'undefined' : _typeof(options)) === 'object') {
            // If localforage is ready and fully initialized, we can't set
            // any new configuration values. Instead, we return an error.
            if (this._ready) {
                return new Error("Can't call config() after localforage " + 'has been used.');
            }

            for (var i in options) {
                if (i === 'storeName') {
                    options[i] = options[i].replace(/\W/g, '_');
                }

                if (i === 'version' && typeof options[i] !== 'number') {
                    return new Error('Database version must be a number.');
                }

                this._config[i] = options[i];
            }

            // after all config options are set and
            // the driver option is used, try setting it
            if ('driver' in options && options.driver) {
                return this.setDriver(this._config.driver);
            }

            return true;
        } else if (typeof options === 'string') {
            return this._config[options];
        } else {
            return this._config;
        }
    };

    // Used to define a custom driver, shared across all instances of
    // localForage.


    LocalForage.prototype.defineDriver = function defineDriver(driverObject, callback, errorCallback) {
        var promise = new Promise$1(function (resolve, reject) {
            try {
                var driverName = driverObject._driver;
                var complianceError = new Error('Custom driver not compliant; see ' + 'https://mozilla.github.io/localForage/#definedriver');

                // A driver name should be defined and not overlap with the
                // library-defined, default drivers.
                if (!driverObject._driver) {
                    reject(complianceError);
                    return;
                }

                var driverMethods = LibraryMethods.concat('_initStorage');
                for (var i = 0, len = driverMethods.length; i < len; i++) {
                    var driverMethodName = driverMethods[i];

                    // when the property is there,
                    // it should be a method even when optional
                    var isRequired = !includes(OptionalDriverMethods, driverMethodName);
                    if ((isRequired || driverObject[driverMethodName]) && typeof driverObject[driverMethodName] !== 'function') {
                        reject(complianceError);
                        return;
                    }
                }

                var configureMissingMethods = function configureMissingMethods() {
                    var methodNotImplementedFactory = function methodNotImplementedFactory(methodName) {
                        return function () {
                            var error = new Error('Method ' + methodName + ' is not implemented by the current driver');
                            var promise = Promise$1.reject(error);
                            executeCallback(promise, arguments[arguments.length - 1]);
                            return promise;
                        };
                    };

                    for (var _i = 0, _len = OptionalDriverMethods.length; _i < _len; _i++) {
                        var optionalDriverMethod = OptionalDriverMethods[_i];
                        if (!driverObject[optionalDriverMethod]) {
                            driverObject[optionalDriverMethod] = methodNotImplementedFactory(optionalDriverMethod);
                        }
                    }
                };

                configureMissingMethods();

                var setDriverSupport = function setDriverSupport(support) {
                    if (DefinedDrivers[driverName]) {
                        console.info('Redefining LocalForage driver: ' + driverName);
                    }
                    DefinedDrivers[driverName] = driverObject;
                    DriverSupport[driverName] = support;
                    // don't use a then, so that we can define
                    // drivers that have simple _support methods
                    // in a blocking manner
                    resolve();
                };

                if ('_support' in driverObject) {
                    if (driverObject._support && typeof driverObject._support === 'function') {
                        driverObject._support().then(setDriverSupport, reject);
                    } else {
                        setDriverSupport(!!driverObject._support);
                    }
                } else {
                    setDriverSupport(true);
                }
            } catch (e) {
                reject(e);
            }
        });

        executeTwoCallbacks(promise, callback, errorCallback);
        return promise;
    };

    LocalForage.prototype.driver = function driver() {
        return this._driver || null;
    };

    LocalForage.prototype.getDriver = function getDriver(driverName, callback, errorCallback) {
        var getDriverPromise = DefinedDrivers[driverName] ? Promise$1.resolve(DefinedDrivers[driverName]) : Promise$1.reject(new Error('Driver not found.'));

        executeTwoCallbacks(getDriverPromise, callback, errorCallback);
        return getDriverPromise;
    };

    LocalForage.prototype.getSerializer = function getSerializer(callback) {
        var serializerPromise = Promise$1.resolve(localforageSerializer);
        executeTwoCallbacks(serializerPromise, callback);
        return serializerPromise;
    };

    LocalForage.prototype.ready = function ready(callback) {
        var self = this;

        var promise = self._driverSet.then(function () {
            if (self._ready === null) {
                self._ready = self._initDriver();
            }

            return self._ready;
        });

        executeTwoCallbacks(promise, callback, callback);
        return promise;
    };

    LocalForage.prototype.setDriver = function setDriver(drivers, callback, errorCallback) {
        var self = this;

        if (!isArray(drivers)) {
            drivers = [drivers];
        }

        var supportedDrivers = this._getSupportedDrivers(drivers);

        function setDriverToConfig() {
            self._config.driver = self.driver();
        }

        function extendSelfWithDriver(driver) {
            self._extend(driver);
            setDriverToConfig();

            self._ready = self._initStorage(self._config);
            return self._ready;
        }

        function initDriver(supportedDrivers) {
            return function () {
                var currentDriverIndex = 0;

                function driverPromiseLoop() {
                    while (currentDriverIndex < supportedDrivers.length) {
                        var driverName = supportedDrivers[currentDriverIndex];
                        currentDriverIndex++;

                        self._dbInfo = null;
                        self._ready = null;

                        return self.getDriver(driverName).then(extendSelfWithDriver)["catch"](driverPromiseLoop);
                    }

                    setDriverToConfig();
                    var error = new Error('No available storage method found.');
                    self._driverSet = Promise$1.reject(error);
                    return self._driverSet;
                }

                return driverPromiseLoop();
            };
        }

        // There might be a driver initialization in progress
        // so wait for it to finish in order to avoid a possible
        // race condition to set _dbInfo
        var oldDriverSetDone = this._driverSet !== null ? this._driverSet["catch"](function () {
            return Promise$1.resolve();
        }) : Promise$1.resolve();

        this._driverSet = oldDriverSetDone.then(function () {
            var driverName = supportedDrivers[0];
            self._dbInfo = null;
            self._ready = null;

            return self.getDriver(driverName).then(function (driver) {
                self._driver = driver._driver;
                setDriverToConfig();
                self._wrapLibraryMethodsWithReady();
                self._initDriver = initDriver(supportedDrivers);
            });
        })["catch"](function () {
            setDriverToConfig();
            var error = new Error('No available storage method found.');
            self._driverSet = Promise$1.reject(error);
            return self._driverSet;
        });

        executeTwoCallbacks(this._driverSet, callback, errorCallback);
        return this._driverSet;
    };

    LocalForage.prototype.supports = function supports(driverName) {
        return !!DriverSupport[driverName];
    };

    LocalForage.prototype._extend = function _extend(libraryMethodsAndProperties) {
        extend(this, libraryMethodsAndProperties);
    };

    LocalForage.prototype._getSupportedDrivers = function _getSupportedDrivers(drivers) {
        var supportedDrivers = [];
        for (var i = 0, len = drivers.length; i < len; i++) {
            var driverName = drivers[i];
            if (this.supports(driverName)) {
                supportedDrivers.push(driverName);
            }
        }
        return supportedDrivers;
    };

    LocalForage.prototype._wrapLibraryMethodsWithReady = function _wrapLibraryMethodsWithReady() {
        // Add a stub for each driver API method that delays the call to the
        // corresponding driver method until localForage is ready. These stubs
        // will be replaced by the driver methods as soon as the driver is
        // loaded, so there is no performance impact.
        for (var i = 0, len = LibraryMethods.length; i < len; i++) {
            callWhenReady(this, LibraryMethods[i]);
        }
    };

    LocalForage.prototype.createInstance = function createInstance(options) {
        return new LocalForage(options);
    };

    return LocalForage;
}();

// The actual localForage object that we expose as a module or via a
// global. It's extended by pulling in one of our other libraries.


var localforage_js = new LocalForage();

module.exports = localforage_js;

},{"3":3}]},{},[4])(4)
});

/**
 * jsPanel - A JavaScript library to create highly configurable multifunctional floating panels that can also be used as modal, tooltip, hint or contextmenu
 * @version v4.16.1
 * @homepage https://jspanel.de/
 * @license MIT
 * @author Stefan Sträßer - info@jspanel.de
 * @author of dialog extension: Michael Daumling - michael@terrapinlogo.com
 * @github https://github.com/Flyer53/jsPanel4.git
 */

'use strict';
function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }
function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }
function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }
function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
// eslint-disable-next-line no-redeclare
// noinspection JSVoidFunctionReturnValueUsed
// eslint-disable-next-line no-redeclare
var jsPanel = {
  version: '4.16.1',
  date: '2022-11-03 09:18',
  ajaxAlwaysCallbacks: [],
  autopositionSpacing: 4,
  closeOnEscape: function () {
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.code === 'Escape' || e.key === 'Esc') {
        jsPanel.getPanels(function (panel) {
          return panel.classList.contains('jsPanel');
        }) // Array is sorted by z-index (the highest first)
        .some(function (item) {
          if (item.options.closeOnEscape) {
            if (typeof item.options.closeOnEscape === 'function') {
              return item.options.closeOnEscape.call(item, item);
              // if return value is falsy next panel in sequence will close, otherwise processing of Array.prototype.some() stops
            } else {
              item.close(null, true);
              return true;
            }
          }
          return false;
        });
      }
    }, false);
  }(),
  defaults: {
    boxShadow: 3,
    container: 'window',
    contentSize: {
      width: '400px',
      height: '200px'
    },
    // must be an object
    dragit: {
      cursor: 'move',
      handles: '.jsPanel-headerlogo, .jsPanel-titlebar, .jsPanel-ftr',
      // do not use .jsPanel-headerbar
      opacity: 0.8,
      disableOnMaximized: true
    },
    header: true,
    headerTitle: 'jsPanel',
    headerControls: {
      size: 'md'
    },
    // must be an object
    iconfont: undefined,
    maximizedMargin: 0,
    minimizeTo: 'default',
    paneltype: 'standard',
    position: {
      my: 'center',
      at: 'center'
    },
    // default position.of MUST NOT be set with new positioning engine
    resizeit: {
      handles: 'n, e, s, w, ne, se, sw, nw',
      minWidth: 128,
      minHeight: 38
    },
    theme: 'default'
  },
  defaultAutocloseConfig: {
    time: '8s',
    progressbar: true
  },
  defaultSnapConfig: {
    sensitivity: 70,
    trigger: 'panel',
    active: 'both'
  },
  extensions: {},
  globalCallbacks: false,
  icons: {
    close: "<svg focusable=\"false\" class=\"jsPanel-icon\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 22 22\"><path fill=\"currentColor\" d=\"M13.7,11l6.1-6.1c0.4-0.4,0.4-0.9,0-1.3l-1.4-1.4c-0.4-0.4-0.9-0.4-1.3,0L11,8.3L4.9,2.3C4.6,1.9,4,1.9,3.7,2.3L2.3,3.7 C1.9,4,1.9,4.6,2.3,4.9L8.3,11l-6.1,6.1c-0.4,0.4-0.4,0.9,0,1.3l1.4,1.4c0.4,0.4,0.9,0.4,1.3,0l6.1-6.1l6.1,6.1 c0.4,0.4,0.9,0.4,1.3,0l1.4-1.4c0.4-0.4,0.4-0.9,0-1.3L13.7,11z\"/></svg>",
    maximize: "<svg focusable=\"false\" class=\"jsPanel-icon\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 22 22\"><path fill=\"currentColor\" d=\"M18.3,2H3.7C2.8,2,2,2.9,2,3.9v14.1C2,19.1,2.8,20,3.7,20h14.6c0.9,0,1.7-0.9,1.7-1.9V3.9C20,2.9,19.2,2,18.3,2z M18.3,17.8 c0,0.1-0.1,0.2-0.2,0.2H3.9c-0.1,0-0.2-0.1-0.2-0.2V8.4h14.6V17.8z\"/></svg>",
    normalize: "<svg focusable=\"false\" class=\"jsPanel-icon\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 22 22\"><path fill=\"currentColor\" d=\"M18.3,2H7.1C6.1,2,5.4,2.8,5.4,3.7v1.7H3.7C2.8,5.4,2,6.1,2,7.1v11.3C2,19.2,2.8,20,3.7,20h11.3c0.9,0,1.7-0.8,1.7-1.7v-1.7 h1.7c0.9,0,1.7-0.8,1.7-1.7V3.7C20,2.8,19.2,2,18.3,2z M14.9,18.3H3.7V11h11.3V18.3z M18.3,14.9h-1.7V7.1c0-0.9-0.8-1.7-1.7-1.7H7.1 V3.7h11.3V14.9z\"/></svg>",
    minimize: "<svg focusable=\"false\" class=\"jsPanel-icon\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 22 22\"><path fill=\"currentColor\" d=\"M18.9,19.8H3.1c-0.6,0-1.1-0.5-1.1-1.1s0.5-1.1,1.1-1.1h15.8c0.6,0,1.1,0.5,1.1,1.1S19.5,19.8,18.9,19.8z\"/></svg>",
    smallify: "<svg focusable=\"false\" class=\"jsPanel-icon\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 22 22\"><path fill=\"currentColor\" d=\"M2.1,15.2L2.9,16c0.2,0.2,0.5,0.2,0.7,0L11,8.7l7.4,7.3c0.2,0.2,0.5,0.2,0.7,0l0.8-0.8c0.2-0.2,0.2-0.5,0-0.7L11.3,6 c-0.2-0.2-0.5-0.2-0.7,0l-8.5,8.5C2,14.7,2,15,2.1,15.2z\"/></svg>"
  },
  idCounter: 0,
  isIE: function () {
    return document.documentMode || false;
  }(),
  pointerdown: 'onpointerdown' in window ? ['pointerdown'] : 'ontouchend' in window ? ['touchstart', 'mousedown'] : ['mousedown'],
  pointermove: 'onpointermove' in window ? ['pointermove'] : 'ontouchend' in window ? ['touchmove', 'mousemove'] : ['mousemove'],
  pointerup: 'onpointerup' in window ? ['pointerup'] : 'ontouchend' in window ? ['touchend', 'mouseup'] : ['mouseup'],
  polyfills: function () {
    // Polyfills for IE11 only
    // Object.assign polyfill - https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Object/assign
    if (!Object.assign) {
      Object.defineProperty(Object, 'assign', {
        enumerable: false,
        configurable: true,
        writable: true,
        value: function value(target) {
          if (target === undefined || target === null) {
            throw new TypeError('Cannot convert first argument to object');
          }
          var to = Object(target);
          for (var i = 1; i < arguments.length; i++) {
            var nextSource = arguments[i];
            if (nextSource === undefined || nextSource === null) {
              continue;
            }
            nextSource = Object(nextSource);
            var keysArray = Object.keys(Object(nextSource));
            for (var nextIndex = 0, len = keysArray.length; nextIndex < len; nextIndex++) {
              var nextKey = keysArray[nextIndex];
              var desc = Object.getOwnPropertyDescriptor(nextSource, nextKey);
              if (desc !== undefined && desc.enumerable) {
                to[nextKey] = nextSource[nextKey];
              }
            }
          }
          return to;
        }
      });
    }
    // Object.entries() - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/entries
    if (!Object.entries) {
      Object.entries = function (obj) {
        // noinspection ES6ConvertVarToLetConst
        var ownProps = Object.keys(obj),
          i = ownProps.length,
          resArray = new Array(i); // preallocate the Array
        while (i--) {
          resArray[i] = [ownProps[i], obj[ownProps[i]]];
        }
        return resArray;
      };
    }
    // NodeList.prototype.forEach() polyfill - https://developer.mozilla.org/en-US/docs/Web/API/NodeList/forEach
    if (window.NodeList && !NodeList.prototype.forEach) {
      NodeList.prototype.forEach = function (callback, thisArg) {
        thisArg = thisArg || window;
        for (var i = 0; i < this.length; i++) {
          callback.call(thisArg, this[i], i, this);
        }
      };
    }
    // .append() polyfill - https://developer.mozilla.org/en-US/docs/Web/API/ParentNode/append
    (function (arr) {
      arr.forEach(function (item) {
        item.append = item.append || function () {
          var argArr = Array.prototype.slice.call(arguments),
            docFrag = document.createDocumentFragment();
          argArr.forEach(function (argItem) {
            var isNode = argItem instanceof Node;
            docFrag.appendChild(isNode ? argItem : document.createTextNode(String(argItem)));
          });
          this.appendChild(docFrag);
        };
      });
    })([Element.prototype, Document.prototype, DocumentFragment.prototype]);
    // Element.closest() polyfill - https://developer.mozilla.org/en-US/docs/Web/API/Element/closest
    if (window.Element && !Element.prototype.closest) {
      // noinspection JSValidateTypes
      Element.prototype.closest = function (s) {
        // noinspection JSUnresolvedVariable
        var matches = (this.document || this.ownerDocument).querySelectorAll(s),
          i,
          el = this;
        do {
          i = matches.length;
          // eslint-disable-next-line no-empty
          while (--i >= 0 && matches.item(i) !== el) {}
        } while (i < 0 && (el = el.parentElement));
        return el;
      };
    }
    // CustomEvent - https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent
    (function () {
      if (typeof window.CustomEvent === 'function') return false;
      function CustomEvent(event, params) {
        params = params || {
          bubbles: false,
          cancelable: false,
          detail: undefined
        };
        var evt = document.createEvent('CustomEvent');
        // noinspection JSDeprecatedSymbols
        evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
        return evt;
      }
      // noinspection JSValidateTypes
      CustomEvent.prototype = window.Event.prototype;
      // noinspection JSValidateTypes
      window.CustomEvent = CustomEvent;
    })();
    // String.prototype.endsWith() - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/endsWith
    if (!String.prototype.endsWith) {
      String.prototype.endsWith = function (search, this_len) {
        if (this_len === undefined || this_len > this.length) {
          this_len = this.length;
        }
        return this.substring(this_len - search.length, this_len) === search;
      };
    }
    // String.prototype.startsWith() - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/startsWith
    if (!String.prototype.startsWith) {
      Object.defineProperty(String.prototype, 'startsWith', {
        value: function value(search, rawPos) {
          // noinspection ES6ConvertVarToLetConst
          var pos = rawPos > 0 ? rawPos | 0 : 0;
          return this.substring(pos, pos + search.length) === search;
        }
      });
    }
    // String.prototype.includes() - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/includes
    if (!String.prototype.includes) {
      String.prototype.includes = function (search, start) {
        'use strict';

        if (search instanceof RegExp) {
          throw TypeError('first argument must not be a RegExp');
        }
        if (start === undefined) {
          start = 0;
        }
        return this.indexOf(search, start) !== -1;
      };
    }
    // String.prototype repeat() - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/repeat
    if (!String.prototype.repeat) {
      String.prototype.repeat = function (count) {
        'use strict';

        if (this == null) throw new TypeError('can\'t convert ' + this + ' to object');
        // noinspection ES6ConvertVarToLetConst
        var str = '' + this;
        count = +count;
        if (count != count) count = 0;
        if (count < 0) throw new RangeError('repeat count must be non-negative');
        if (count == Infinity) throw new RangeError('repeat count must be less than infinity');
        count = Math.floor(count);
        if (str.length == 0 || count == 0) return '';
        if (str.length * count >= 1 << 28) throw new RangeError('repeat count must not overflow maximum string size');
        // noinspection ES6ConvertVarToLetConst
        var maxCount = str.length * count;
        count = Math.floor(Math.log(count) / Math.log(2));
        while (count) {
          str += str;
          count--;
        }
        str += str.substring(0, maxCount - str.length);
        return str;
      };
    }
    // Number.isInteger() -
    Number.isInteger = Number.isInteger || function (value) {
      return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
    };
    // Array.prototype.includes() - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/includes
    if (!Array.prototype.includes) {
      Object.defineProperty(Array.prototype, 'includes', {
        value: function value(searchElement, fromIndex) {
          if (this == null) {
            throw new TypeError('"this" is null or not defined');
          }
          // 1. Let O be ? ToObject(this value).
          var o = Object(this);
          // 2. Let len be ? ToLength(? Get(O, "length")).
          var len = o.length >>> 0;
          // 3. If len is 0, return false.
          if (len === 0) {
            return false;
          }
          // 4. Let n be ? ToInteger(fromIndex).
          //    (If fromIndex is undefined, this step produces the value 0.)
          var n = fromIndex | 0;
          // 5. If n ≥ 0, then
          //  a. Let k be n.
          // 6. Else n < 0,
          //  a. Let k be len + n.
          //  b. If k < 0, let k be 0.
          var k = Math.max(n >= 0 ? n : len - Math.abs(n), 0);
          function sameValueZero(x, y) {
            return x === y || typeof x === 'number' && typeof y === 'number' && isNaN(x) && isNaN(y);
          }
          // 7. Repeat, while k < len
          while (k < len) {
            // a. Let elementK be the result of ? Get(O, ! ToString(k)).
            // b. If SameValueZero(searchElement, elementK) is true, return true.
            if (sameValueZero(o[k], searchElement)) {
              return true;
            }
            // c. Increase k by 1.
            k++;
          }
          // 8. Return false
          return false;
        }
      });
    }
  }(),
  ziBase: 100,
  colorFilledLight: 0.81,
  colorFilledDark: 0.08,
  colorFilled: 0,
  colorBrightnessThreshold: 0.55,
  colorNames: {
    "default": 'b0bec5',
    // Material Design bluegray200
    secondary: 'b0bec5',
    primary: '01579b',
    // Material Design lightblue900
    info: '039be5',
    // Material Design lightblue600
    success: '2e7d32',
    // Material Design green800
    warning: 'f57f17',
    // Material Design yellow900
    danger: 'dd2c00',
    // Material Design deeporangeA700
    light: 'e0e0e0',
    // Material Design gray300
    dark: '263238',
    // Material Design bluegray900
    // https://developer.mozilla.org/en-US/docs/Web/CSS/color_value#Color_keywords
    aliceblue: 'f0f8ff',
    antiquewhite: 'faebd7',
    aqua: '00ffff',
    aquamarine: '7fffd4',
    azure: 'f0ffff',
    beige: 'f5f5dc',
    bisque: 'ffe4c4',
    black: '000000',
    blanchedalmond: 'ffebcd',
    blue: '0000ff',
    blueviolet: '8a2be2',
    brown: 'a52a2a',
    burlywood: 'deb887',
    cadetblue: '5f9ea0',
    chartreuse: '7fff00',
    chocolate: 'd2691e',
    coral: 'ff7f50',
    cornflowerblue: '6495ed',
    cornsilk: 'fff8dc',
    crimson: 'dc143c',
    cyan: '00ffff',
    darkblue: '00008b',
    darkcyan: '008b8b',
    darkgoldenrod: 'b8860b',
    darkgray: 'a9a9a9',
    darkgrey: 'a9a9a9',
    darkgreen: '006400',
    darkkhaki: 'bdb76b',
    darkmagenta: '8b008b',
    darkolivegreen: '556b2f',
    darkorange: 'ff8c00',
    darkorchid: '9932cc',
    darkred: '8b0000',
    darksalmon: 'e9967a',
    darkseagreen: '8fbc8f',
    darkslateblue: '483d8b',
    darkslategray: '2f4f4f',
    darkslategrey: '2f4f4f',
    darkturquoise: '00ced1',
    darkviolet: '9400d3',
    deeppink: 'ff1493',
    deepskyblue: '00bfff',
    dimgray: '696969',
    dimgrey: '696969',
    dodgerblue: '1e90ff',
    firebrick: 'b22222',
    floralwhite: 'fffaf0',
    forestgreen: '228b22',
    fuchsia: 'ff00ff',
    gainsboro: 'dcdcdc',
    ghostwhite: 'f8f8ff',
    gold: 'ffd700',
    goldenrod: 'daa520',
    gray: '808080',
    grey: '808080',
    green: '008000',
    greenyellow: 'adff2f',
    honeydew: 'f0fff0',
    hotpink: 'ff69b4',
    indianred: 'cd5c5c',
    indigo: '4b0082',
    ivory: 'fffff0',
    khaki: 'f0e68c',
    lavender: 'e6e6fa',
    lavenderblush: 'fff0f5',
    lawngreen: '7cfc00',
    lemonchiffon: 'fffacd',
    lightblue: 'add8e6',
    lightcoral: 'f08080',
    lightcyan: 'e0ffff',
    lightgoldenrodyellow: 'fafad2',
    lightgray: 'd3d3d3',
    lightgrey: 'd3d3d3',
    lightgreen: '90ee90',
    lightpink: 'ffb6c1',
    lightsalmon: 'ffa07a',
    lightseagreen: '20b2aa',
    lightskyblue: '87cefa',
    lightslategray: '778899',
    lightslategrey: '778899',
    lightsteelblue: 'b0c4de',
    lightyellow: 'ffffe0',
    lime: '00ff00',
    limegreen: '32cd32',
    linen: 'faf0e6',
    magenta: 'ff00ff',
    maroon: '800000',
    mediumaquamarine: '66cdaa',
    mediumblue: '0000cd',
    mediumorchid: 'ba55d3',
    mediumpurple: '9370d8',
    mediumseagreen: '3cb371',
    mediumslateblue: '7b68ee',
    mediumspringgreen: '00fa9a',
    mediumturquoise: '48d1cc',
    mediumvioletred: 'c71585',
    midnightblue: '191970',
    mintcream: 'f5fffa',
    mistyrose: 'ffe4e1',
    moccasin: 'ffe4b5',
    navajowhite: 'ffdead',
    navy: '000080',
    oldlace: 'fdf5e6',
    olive: '808000',
    olivedrab: '6b8e23',
    orange: 'ffa500',
    orangered: 'ff4500',
    orchid: 'da70d6',
    palegoldenrod: 'eee8aa',
    palegreen: '98fb98',
    paleturquoise: 'afeeee',
    palevioletred: 'd87093',
    papayawhip: 'ffefd5',
    peachpuff: 'ffdab9',
    peru: 'cd853f',
    pink: 'ffc0cb',
    plum: 'dda0dd',
    powderblue: 'b0e0e6',
    purple: '800080',
    rebeccapurple: '663399',
    red: 'ff0000',
    rosybrown: 'bc8f8f',
    royalblue: '4169e1',
    saddlebrown: '8b4513',
    salmon: 'fa8072',
    sandybrown: 'f4a460',
    seagreen: '2e8b57',
    seashell: 'fff5ee',
    sienna: 'a0522d',
    silver: 'c0c0c0',
    skyblue: '87ceeb',
    slateblue: '6a5acd',
    slategray: '708090',
    slategrey: '708090',
    snow: 'fffafa',
    springgreen: '00ff7f',
    steelblue: '4682b4',
    tan: 'd2b48c',
    teal: '008080',
    thistle: 'd8bfd8',
    tomato: 'ff6347',
    turquoise: '40e0d0',
    violet: 'ee82ee',
    wheat: 'f5deb3',
    white: 'ffffff',
    whitesmoke: 'f5f5f5',
    yellow: 'ffff00',
    yellowgreen: '9acd32',
    /* Material Design Colors https://material.io/design/color/the-color-system.html#tools-for-picking-colors */
    grey50: 'fafafa',
    grey100: 'f5f5f5',
    grey200: 'eeeeee',
    grey300: 'e0e0e0',
    grey400: 'bdbdbd',
    grey500: '9e9e9e',
    grey600: '757575',
    grey700: '616161',
    grey800: '424242',
    grey900: '212121',
    gray50: 'fafafa',
    gray100: 'f5f5f5',
    gray200: 'eeeeee',
    gray300: 'e0e0e0',
    gray400: 'bdbdbd',
    gray500: '9e9e9e',
    gray600: '757575',
    gray700: '616161',
    gray800: '424242',
    gray900: '212121',
    bluegrey50: 'eceff1',
    bluegrey100: 'CFD8DC',
    bluegrey200: 'B0BEC5',
    bluegrey300: '90A4AE',
    bluegrey400: '78909C',
    bluegrey500: '607D8B',
    bluegrey600: '546E7A',
    bluegrey700: '455A64',
    bluegrey800: '37474F',
    bluegrey900: '263238',
    bluegray50: 'eceff1',
    bluegray100: 'CFD8DC',
    bluegray200: 'B0BEC5',
    bluegray300: '90A4AE',
    bluegray400: '78909C',
    bluegray500: '607D8B',
    bluegray600: '546E7A',
    bluegray700: '455A64',
    bluegray800: '37474F',
    bluegray900: '263238',
    red50: 'FFEBEE',
    red100: 'FFCDD2',
    red200: 'EF9A9A',
    red300: 'E57373',
    red400: 'EF5350',
    red500: 'F44336',
    red600: 'E53935',
    red700: 'D32F2F',
    red800: 'C62828',
    red900: 'B71C1C',
    reda100: 'FF8A80',
    reda200: 'FF5252',
    reda400: 'FF1744',
    reda700: 'D50000',
    pink50: 'FCE4EC',
    pink100: 'F8BBD0',
    pink200: 'F48FB1',
    pink300: 'F06292',
    pink400: 'EC407A',
    pink500: 'E91E63',
    pink600: 'D81B60',
    pink700: 'C2185B',
    pink800: 'AD1457',
    pink900: '880E4F',
    pinka100: 'FF80AB',
    pinka200: 'FF4081',
    pinka400: 'F50057',
    pinka700: 'C51162',
    purple50: 'F3E5F5',
    purple100: 'E1BEE7',
    purple200: 'CE93D8',
    purple300: 'BA68C8',
    purple400: 'AB47BC',
    purple500: '9C27B0',
    purple600: '8E24AA',
    purple700: '7B1FA2',
    purple800: '6A1B9A',
    purple900: '4A148C',
    purplea100: 'EA80FC',
    purplea200: 'E040FB',
    purplea400: 'D500F9',
    purplea700: 'AA00FF',
    deeppurple50: 'EDE7F6',
    deeppurple100: 'D1C4E9',
    deeppurple200: 'B39DDB',
    deeppurple300: '9575CD',
    deeppurple400: '7E57C2',
    deeppurple500: '673AB7',
    deeppurple600: '5E35B1',
    deeppurple700: '512DA8',
    deeppurple800: '4527A0',
    deeppurple900: '311B92',
    deeppurplea100: 'B388FF',
    deeppurplea200: '7C4DFF',
    deeppurplea400: '651FFF',
    deeppurplea700: '6200EA',
    indigo50: 'E8EAF6',
    indigo100: 'C5CAE9',
    indigo200: '9FA8DA',
    indigo300: '7986CB',
    indigo400: '5C6BC0',
    indigo500: '3F51B5',
    indigo600: '3949AB',
    indigo700: '303F9F',
    indigo800: '283593',
    indigo900: '1A237E',
    indigoa100: '8C9EFF',
    indigoa200: '536DFE',
    indigoa400: '3D5AFE',
    indigoa700: '304FFE',
    blue50: 'E3F2FD',
    blue100: 'BBDEFB',
    blue200: '90CAF9',
    blue300: '64B5F6',
    blue400: '42A5F5',
    blue500: '2196F3',
    blue600: '1E88E5',
    blue700: '1976D2',
    blue800: '1565C0',
    blue900: '0D47A1',
    bluea100: '82B1FF',
    bluea200: '448AFF',
    bluea400: '2979FF',
    bluea700: '2962FF',
    lightblue50: 'E1F5FE',
    lightblue100: 'B3E5FC',
    lightblue200: '81D4FA',
    lightblue300: '4FC3F7',
    lightblue400: '29B6F6',
    lightblue500: '03A9F4',
    lightblue600: '039BE5',
    lightblue700: '0288D1',
    lightblue800: '0277BD',
    lightblue900: '01579B',
    lightbluea100: '80D8FF',
    lightbluea200: '40C4FF',
    lightbluea400: '00B0FF',
    lightbluea700: '0091EA',
    cyan50: 'E0F7FA',
    cyan100: 'B2EBF2',
    cyan200: '80DEEA',
    cyan300: '4DD0E1',
    cyan400: '26C6DA',
    cyan500: '00BCD4',
    cyan600: '00ACC1',
    cyan700: '0097A7',
    cyan800: '00838F',
    cyan900: '006064',
    cyana100: '84FFFF',
    cyana200: '18FFFF',
    cyana400: '00E5FF',
    cyana700: '00B8D4',
    teal50: 'E0F2F1',
    teal100: 'B2DFDB',
    teal200: '80CBC4',
    teal300: '4DB6AC',
    teal400: '26A69A',
    teal500: '009688',
    teal600: '00897B',
    teal700: '00796B',
    teal800: '00695C',
    teal900: '004D40',
    teala100: 'A7FFEB',
    teala200: '64FFDA',
    teala400: '1DE9B6',
    teala700: '00BFA5',
    green50: 'E8F5E9',
    green100: 'C8E6C9',
    green200: 'A5D6A7',
    green300: '81C784',
    green400: '66BB6A',
    green500: '4CAF50',
    green600: '43A047',
    green700: '388E3C',
    green800: '2E7D32',
    green900: '1B5E20',
    greena100: 'B9F6CA',
    greena200: '69F0AE',
    greena400: '00E676',
    greena700: '00C853',
    lightgreen50: 'F1F8E9',
    lightgreen100: 'DCEDC8',
    lightgreen200: 'C5E1A5',
    lightgreen300: 'AED581',
    lightgreen400: '9CCC65',
    lightgreen500: '8BC34A',
    lightgreen600: '7CB342',
    lightgreen700: '689F38',
    lightgreen800: '558B2F',
    lightgreen900: '33691E',
    lightgreena100: 'CCFF90',
    lightgreena200: 'B2FF59',
    lightgreena400: '76FF03',
    lightgreena700: '64DD17',
    lime50: 'F9FBE7',
    lime100: 'F0F4C3',
    lime200: 'E6EE9C',
    lime300: 'DCE775',
    lime400: 'D4E157',
    lime500: 'CDDC39',
    lime600: 'C0CA33',
    lime700: 'AFB42B',
    lime800: '9E9D24',
    lime900: '827717',
    limea100: 'F4FF81',
    limea200: 'EEFF41',
    limea400: 'C6FF00',
    limea700: 'AEEA00',
    yellow50: 'FFFDE7',
    yellow100: 'FFF9C4',
    yellow200: 'FFF59D',
    yellow300: 'FFF176',
    yellow400: 'FFEE58',
    yellow500: 'FFEB3B',
    yellow600: 'FDD835',
    yellow700: 'FBC02D',
    yellow800: 'F9A825',
    yellow900: 'F57F17',
    yellowa100: 'FFFF8D',
    yellowa200: 'FFFF00',
    yellowa400: 'FFEA00',
    yellowa700: 'FFD600',
    amber50: 'FFF8E1',
    amber100: 'FFECB3',
    amber200: 'FFE082',
    amber300: 'FFD54F',
    amber400: 'FFCA28',
    amber500: 'FFC107',
    amber600: 'FFB300',
    amber700: 'FFA000',
    amber800: 'FF8F00',
    amber900: 'FF6F00',
    ambera100: 'FFE57F',
    ambera200: 'FFD740',
    ambera400: 'FFC400',
    ambera700: 'FFAB00',
    orange50: 'FFF3E0',
    orange100: 'FFE0B2',
    orange200: 'FFCC80',
    orange300: 'FFB74D',
    orange400: 'FFA726',
    orange500: 'FF9800',
    orange600: 'FB8C00',
    orange700: 'F57C00',
    orange800: 'EF6C00',
    orange900: 'E65100',
    orangea100: 'FFD180',
    orangea200: 'FFAB40',
    orangea400: 'FF9100',
    orangea700: 'FF6D00',
    deeporange50: 'FBE9E7',
    deeporange100: 'FFCCBC',
    deeporange200: 'FFAB91',
    deeporange300: 'FF8A65',
    deeporange400: 'FF7043',
    deeporange500: 'FF5722',
    deeporange600: 'F4511E',
    deeporange700: 'E64A19',
    deeporange800: 'D84315',
    deeporange900: 'BF360C',
    deeporangea100: 'FF9E80',
    deeporangea200: 'FF6E40',
    deeporangea400: 'FF3D00',
    deeporangea700: 'DD2C00',
    brown50: 'EFEBE9',
    brown100: 'D7CCC8',
    brown200: 'BCAAA4',
    brown300: 'A1887F',
    brown400: '8D6E63',
    brown500: '795548',
    brown600: '6D4C41',
    brown700: '5D4037',
    brown800: '4E342E',
    brown900: '3E2723',
    /* Material Design for Bootstrap v4 themes https://mdbootstrap.com/docs/b4/jquery/css/colors/#mdb-colors */
    'mdb-default': '2BBBAD',
    'mdb-default-dark': '00695c',
    'mdb-primary': '4285F4',
    'mdb-primary-dark': '0d47a1',
    'mdb-secondary': 'aa66cc',
    'mdb-secondary-dark': '9933CC',
    'mdb-danger': 'ff4444',
    'mdb-danger-dark': 'CC0000',
    'mdb-warning': 'ffbb33',
    'mdb-warning-dark': 'FF8800',
    'mdb-success': '00C851',
    'mdb-success-dark': '007E33',
    'mdb-info': '33b5e5',
    'mdb-info-dark': '0099CC',
    'mdb-elegant': '2E2E2E',
    'mdb-elegant-dark': '212121',
    'mdb-stylish': '4B515D',
    'mdb-stylish-dark': '3E4551',
    'mdb-unique': '3F729B',
    'mdb-unique-dark': '1C2331',
    'mdb-special': '37474F',
    'mdb-special-dark': '263238'
  },
  errorReporting: 1,
  modifier: false,
  helper: function () {
    document.addEventListener('keydown', function (e) {
      return jsPanel.modifier = e;
    });
    document.addEventListener('keyup', function () {
      return jsPanel.modifier = false;
    });
  }(),
  usePointerEvents: function usePointerEvents() {
    var use = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    if (!use) {
      this.pointerdown = 'ontouchend' in window ? ['touchstart', 'mousedown'] : ['mousedown'];
      this.pointermove = 'ontouchend' in window ? ['touchmove', 'mousemove'] : ['mousemove'];
      this.pointerup = 'ontouchend' in window ? ['touchend', 'mouseup'] : ['mouseup'];
    } else {
      this.pointerdown = 'onpointerdown' in window ? ['pointerdown'] : 'ontouchend' in window ? ['touchstart', 'mousedown'] : ['mousedown'];
      this.pointermove = 'onpointermove' in window ? ['pointermove'] : 'ontouchend' in window ? ['touchmove', 'mousemove'] : ['mousemove'];
      this.pointerup = 'onpointerup' in window ? ['pointerup'] : 'ontouchend' in window ? ['touchend', 'mouseup'] : ['mouseup'];
    }
  },
  // normalize some values passed via panel's options object
  pOcontainer: function pOcontainer(container) {
    if (container === 'window') {
      return document.body;
    } else if (typeof container === 'string') {
      var list = document.querySelectorAll(container);
      // a returned list is a NodeList
      return list.length && list.length > 0 ? list : false;
    } else if (container.nodeType === 1) {
      return container;
    } else if (container.length) {
      return container[0];
    }
    return false;
  },
  pOcontainment: function pOcontainment(arg) {
    var value = arg;
    if (typeof arg === 'function') {
      value = arg();
    }
    if (typeof value === 'number') {
      // value: 20 => value: [20, 20, 20, 20]
      return [value, value, value, value];
    } else if (Array.isArray(value)) {
      if (value.length === 1) {
        // value: [20] => value: [20, 20, 20, 20]
        return [value[0], value[0], value[0], value[0]];
      } else if (value.length === 2) {
        // value: [20, 40] => value: [20, 40, 20, 40]
        return value.concat(value);
      } else if (value.length === 3) {
        value[3] = value[1];
      }
    }
    return value; // assumed to be an array with 4 values
  },
  pOsize: function pOsize(panel, size) {
    var values = size || this.defaults.contentSize;
    var parent = panel.parentElement;
    if (typeof values === 'string') {
      var nums = values.trim().split(' ');
      values = {};
      values.width = nums[0];
      nums.length === 2 ? values.height = nums[1] : values.height = nums[0];
    } else {
      if (values.width && !values.height) {
        // noinspection JSSuspiciousNameCombination
        values.height = values.width;
      } else if (values.height && !values.width) {
        // noinspection JSSuspiciousNameCombination
        values.width = values.height;
      }
    }
    if (String(values.width).match(/^[\d.]+$/gi)) {
      // if number only
      values.width += 'px';
    } else if (typeof values.width === 'string' && values.width.endsWith('%')) {
      if (parent === document.body) {
        values.width = window.innerWidth * (parseFloat(values.width) / 100) + 'px';
      } else {
        var prtStyles = window.getComputedStyle(parent),
          border = parseFloat(prtStyles.borderLeftWidth) + parseFloat(prtStyles.borderRightWidth);
        values.width = (parseFloat(prtStyles.width) - border) * (parseFloat(values.width) / 100) + 'px';
      }
    } else if (typeof values.width === 'function') {
      values.width = values.width.call(panel, panel);
      if (typeof values.width === 'number') {
        values.width += 'px';
      } else if (typeof values.width === 'string' && values.width.match(/^[\d.]+$/gi)) {
        values.width += 'px';
      }
    }
    if (String(values.height).match(/^[\d.]+$/gi)) {
      // if number only
      values.height += 'px';
    } else if (typeof values.height === 'string' && values.height.endsWith('%')) {
      if (parent === document.body) {
        values.height = window.innerHeight * (parseFloat(values.height) / 100) + 'px';
      } else {
        var _prtStyles = window.getComputedStyle(parent),
          _border = parseFloat(_prtStyles.borderTopWidth) + parseFloat(_prtStyles.borderBottomWidth);
        values.height = (parseFloat(_prtStyles.height) - _border) * (parseFloat(values.height) / 100) + 'px';
      }
    } else if (typeof values.height === 'function') {
      values.height = values.height.call(panel, panel);
      if (typeof values.height === 'number') {
        values.height += 'px';
      } else if (typeof values.height === 'string' && values.height.match(/^[\d.]+$/gi)) {
        values.height += 'px';
      }
    }
    return values; // return value must be an object {width: xxx, height: xxx}
  },
  pOborder: function pOborder(str) {
    var result = [];
    // remove all unnecessary whitepsace
    var border = str.trim().replace(/\s*\(\s*/g, '(') // remove whitespace around opening brackets
    .replace(/\s*\)/g, ')') // remove whitespace around closing brackets
    .replace(/\s+/g, ' ') // replace all other whitespace(s) with a single whitespace
    .split(' ');
    // replace css custom props/variables with values
    border.forEach(function (val, index) {
      if (val.startsWith('--') || val.startsWith('var')) {
        border[index] = jsPanel.getCssVariableValue(val);
      }
    }); // border is now an array like ['5px', 'solid', 'red']
    // check values for type (border width, style or color) and add to result array
    border.forEach(function (val) {
      if (jsPanel.colorNames[val]) {
        result[2] = '#' + jsPanel.colorNames[val];
      } else if (val.match(/(none|hidden|dotted|dashed|solid|double|groove|ridge|inset|outset)/)) {
        result[1] = val;
      } else if (val.match(/(thin|medium|thick)|(\d*\.?\d*(cap|ch|em|ex|ic|lh|rem|rlh|vh|vw|vmax|vmin|vb|vi|px|cm|mm|Q|in|pc|pt))/)) {
        result[0] = val;
      } else {
        result[2] = val;
      }
    });
    // set default values if needed
    if (!result[0]) {
      result[0] = 'medium';
    }
    if (!result[1]) {
      result[1] = 'solid';
    }
    if (!result[2]) {
      result[2] = '';
    }
    return result;
  },
  pOheaderControls: function pOheaderControls(oHdrCtrls) {
    if (typeof oHdrCtrls === 'string') {
      var setting = {},
        str = oHdrCtrls.toLowerCase(),
        sizeMatch = str.match(/xl|lg|md|sm|xs/),
        ctrlMatch = str.match(/closeonly|none/);
      if (sizeMatch) {
        setting.size = sizeMatch[0];
      }
      if (ctrlMatch) {
        setting = Object.assign({}, setting, {
          maximize: 'remove',
          normalize: 'remove',
          minimize: 'remove',
          smallify: 'remove'
        });
        if (ctrlMatch[0] === 'none') {
          setting.close = 'remove';
        }
      }
      return Object.assign({}, this.defaults.headerControls, setting);
    } else {
      return Object.assign({}, this.defaults.headerControls, oHdrCtrls);
    }
  },
  pOtheme: function pOtheme(optionTheme) {
    optionTheme = optionTheme.trim();
    var color,
      filling = '';
    if (optionTheme.match(/^(rgb|hsl|var)/)) {
      // for themes starting with rgb , hsl or var
      var devide = optionTheme.indexOf(')');
      color = optionTheme.slice(0, devide + 1).replace(/\s+/g, '');
      if (color.startsWith('var')) {
        color = jsPanel.getCssVariableValue(color);
      }
      filling = optionTheme.slice(devide + 1, optionTheme.length).trim();
    } else if (optionTheme.match(/^(#|\w|--)/)) {
      // for themes starting with #, [A-Za-z0-9_] or --
      var _devide = optionTheme.indexOf(' ');
      if (_devide > 0) {
        color = optionTheme.slice(0, _devide + 1).replace(/\s+/g, '');
        filling = optionTheme.slice(_devide + 1, optionTheme.length).trim();
      } else {
        color = optionTheme;
      }
      if (color.startsWith('--')) {
        color = jsPanel.getCssVariableValue(color);
      }
    }
    if (color.match(/^([\da-f]{3}|[\da-f]{6})$/gi)) {
      color = '#' + color;
    }
    if (filling.startsWith('fillcolor')) {
      var _devide2 = filling.indexOf(' ');
      filling = filling.slice(_devide2 + 1, filling.length).trim().replace(/\s+/g, '');
      if (filling.match(/^([\da-f]{3}|[\da-f]{6})$/gi)) {
        filling = '#' + filling;
      } else if (jsPanel.colorNames[filling]) {
        filling = '#' + jsPanel.colorNames[filling];
      } else if (filling.match(/^(--|var)/)) {
        filling = jsPanel.getCssVariableValue(filling);
      } else {
        filling = '#fff';
      }
    }
    return {
      color: color,
      colors: false,
      filling: filling
    };
  },
  // color methods ---------------
  color: function color(val) {
    var color = val.toLowerCase(),
      r,
      g,
      b,
      h,
      s,
      l,
      match,
      channels,
      hsl,
      result = {};
    var hexPattern = /^#?([\da-f]{3}|[\da-f]{6})$/gi,
      // matches "#123" or "#f05a78" with or without "#"
      RGBAPattern = /^rgba?\((\d{1,3}),(\d{1,3}),(\d{1,3}),?(0|1|0\.\d{1,2}|\.\d{1,2})?\)$/gi,
      // matches rgb/rgba color values, whitespace allowed
      HSLAPattern = /^hsla?\((\d{1,3}),(\d{1,3}%),(\d{1,3}%),?(0|1|0\.\d{1,2}|\.\d{1,2})?\)$/gi,
      namedColors = this.colorNames;

    // change named color to corresponding hex value
    if (namedColors[color]) {
      color = namedColors[color];
    }

    // check val for hex color
    if (color.match(hexPattern) !== null) {
      // '#' remove
      color = color.replace('#', '');
      // color has either 3 or 6 characters
      if (color.length % 2 === 1) {
        // color has 3 char -> convert to 6 char
        r = color.slice(0, 1).repeat(2);
        g = color.slice(1, 2).repeat(2);
        b = color.slice(2, 3).repeat(2);
        result.rgb = {
          r: parseInt(r, 16),
          g: parseInt(g, 16),
          b: parseInt(b, 16)
        };
        result.hex = "#".concat(r).concat(g).concat(b);
      } else {
        // color has 6 char
        result.rgb = {
          r: parseInt(color.slice(0, 2), 16),
          g: parseInt(color.slice(2, 4), 16),
          b: parseInt(color.slice(4, 6), 16)
        };
        result.hex = "#".concat(color);
      }
      hsl = this.rgbToHsl(result.rgb.r, result.rgb.g, result.rgb.b);
      result.hsl = hsl;
      result.rgb.css = "rgb(".concat(result.rgb.r, ",").concat(result.rgb.g, ",").concat(result.rgb.b, ")");
    }
    // check val for rgb/rgba color
    else if (color.match(RGBAPattern)) {
      match = RGBAPattern.exec(color);
      result.rgb = {
        css: color,
        r: match[1],
        g: match[2],
        b: match[3]
      };
      result.hex = this.rgbToHex(match[1], match[2], match[3]);
      hsl = this.rgbToHsl(match[1], match[2], match[3]);
      result.hsl = hsl;
    }
    // check val for hsl/hsla color
    else if (color.match(HSLAPattern)) {
      match = HSLAPattern.exec(color);
      h = match[1] / 360;
      s = match[2].slice(0, match[2].length - 1) / 100;
      l = match[3].slice(0, match[3].length - 1) / 100;
      channels = this.hslToRgb(h, s, l);
      result.rgb = {
        css: "rgb(".concat(channels[0], ",").concat(channels[1], ",").concat(channels[2], ")"),
        r: channels[0],
        g: channels[1],
        b: channels[2]
      };
      result.hex = this.rgbToHex(result.rgb.r, result.rgb.g, result.rgb.b);
      result.hsl = {
        css: "hsl(".concat(match[1], ",").concat(match[2], ",").concat(match[3], ")"),
        h: match[1],
        s: match[2],
        l: match[3]
      };
    }

    // or return #f5f5f5
    else {
      result.hex = '#f5f5f5';
      result.rgb = {
        css: 'rgb(245,245,245)',
        r: 245,
        g: 245,
        b: 245
      };
      result.hsl = {
        css: 'hsl(0,0%,96%)',
        h: 0,
        s: '0%',
        l: '96%'
      };
    }
    return result;
  },
  calcColors: function calcColors(primaryColor) {
    var threshold = this.colorBrightnessThreshold,
      primeColor = this.color(primaryColor),
      filledlightColor = this.lighten(primaryColor, this.colorFilledLight),
      filledColor = this.darken(primaryColor, this.colorFilled),
      fontColorForPrimary = this.perceivedBrightness(primaryColor) <= threshold ? '#ffffff' : '#000000',
      fontColorFilledlight = this.perceivedBrightness(filledlightColor) <= threshold ? '#ffffff' : '#000000',
      fontColorFilled = this.perceivedBrightness(filledColor) <= threshold ? '#ffffff' : '#000000',
      filleddarkColor = this.lighten(primaryColor, this.colorFilledDark),
      fontColorFilleddark = this.perceivedBrightness(filleddarkColor) <= threshold ? '#ffffff' : '#000000';
    return [primeColor.hsl.css, filledlightColor, filledColor, fontColorForPrimary, fontColorFilledlight, fontColorFilled, filleddarkColor, fontColorFilleddark];
  },
  darken: function darken(val, amount) {
    // amount is value between 0 and 1
    var hsl = this.color(val).hsl,
      l = parseFloat(hsl.l),
      lnew = Math.round(l - l * amount) + '%';
    return "hsl(".concat(hsl.h, ",").concat(hsl.s, ",").concat(lnew, ")");
  },
  lighten: function lighten(val, amount) {
    // amount is value between 0 and 1
    var hsl = this.color(val).hsl,
      l = parseFloat(hsl.l),
      lnew = Math.round(l + (100 - l) * amount) + '%';
    return "hsl(".concat(hsl.h, ",").concat(hsl.s, ",").concat(lnew, ")");
  },
  hslToRgb: function hslToRgb(h, s, l) {
    // h, s and l must be values between 0 and 1
    var r, g, b;
    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      var hue2rgb = function hue2rgb(p, q, t) {
        if (t < 0) {
          t += 1;
        }
        if (t > 1) {
          t -= 1;
        }
        if (t < 1 / 6) {
          return p + (q - p) * 6 * t;
        }
        if (t < 1 / 2) {
          return q;
        }
        if (t < 2 / 3) {
          return p + (q - p) * (2 / 3 - t) * 6;
        }
        return p;
      };
      var q = l < 0.5 ? l * (1 + s) : l + s - l * s,
        p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }
    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  },
  rgbToHsl: function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    var max = Math.max(r, g, b),
      min = Math.min(r, g, b),
      h,
      s,
      l = (max + min) / 2;
    if (max === min) {
      h = s = 0; // achromatic
    } else {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }
    //return [ h, s, l ];
    h = Math.round(h * 360);
    s = Math.round(s * 100) + '%';
    l = Math.round(l * 100) + '%';
    return {
      css: 'hsl(' + h + ',' + s + ',' + l + ')',
      h: h,
      s: s,
      l: l
    };
  },
  rgbToHex: function rgbToHex(r, g, b) {
    var red = Number(r).toString(16),
      green = Number(g).toString(16),
      blue = Number(b).toString(16);
    if (red.length === 1) {
      red = "0".concat(red);
    }
    if (green.length === 1) {
      green = "0".concat(green);
    }
    if (blue.length === 1) {
      blue = "0".concat(blue);
    }
    return "#".concat(red).concat(green).concat(blue);
  },
  perceivedBrightness: function perceivedBrightness(val) {
    var rgb = this.color(val).rgb;
    // return value is in the range 0 - 1 and input rgb values must also be in the range 0 - 1
    // https://www.w3.org/TR/WCAG20-TECHS/G18.html
    return rgb.r / 255 * 0.2126 + rgb.g / 255 * 0.7152 + rgb.b / 255 * 0.0722;
  },
  // positioning methods ---------------
  pOposition: function pOposition(positionshorthand) {
    var result = {};
    // remove leading and trailing whitespace and split position shorthand string into array
    var pos = positionshorthand.trim().split(/\s+/);

    // find autoposition value and assign to result, must be the first item to find and remove
    var auto = pos.filter(function (item) {
      return item.match(/^(down|right|up|left)$/i);
    });
    if (auto.length) {
      result.autoposition = auto[0];
      pos.splice(pos.indexOf(auto[0]), 1);
    }

    // find my and at values and assign to result
    var my_at = pos.filter(function (item) {
      return item.match(/^(left-|right-)(top|center|bottom)$|(^center-)(top|bottom)$|(^center$)/i);
    });
    if (my_at.length) {
      result.my = my_at[0];
      result.at = my_at[1] || my_at[0];
      pos.splice(pos.indexOf(my_at[0]), 1);
      if (my_at[1]) {
        pos.splice(pos.indexOf(my_at[1]), 1);
      }
    } else {
      result.my = 'center';
      result.at = 'center';
    }

    // find offset and assign to result
    var offsets = pos.filter(function (item) {
      return item.match(/^[+-]?\d*\.?\d+[a-z%]*$/i);
    });
    if (offsets.length) {
      result.offsetX = offsets[0].match(/^[+-]?\d*\.?\d+$/i) ? "".concat(offsets[0], "px") : offsets[0];
      if (offsets[1]) {
        result.offsetY = offsets[1].match(/^[+-]?\d*\.?\d+$/i) ? "".concat(offsets[1], "px") : offsets[1];
      } else {
        // noinspection JSSuspiciousNameCombination
        result.offsetY = result.offsetX;
      }
      pos.splice(pos.indexOf(offsets[0]), 1);
      if (offsets[1]) {
        pos.splice(pos.indexOf(offsets[1]), 1);
      }
    }

    // last to find and assign is of value and must be all the rest (if there is a rest)
    if (pos.length) {
      result.of = pos.join(' ');
    }
    return result;
  },
  position: function position(panel, _position) {
    // @panel:     selector string | Element | jQuery object
    //                - usually the jsPanel to position
    // @position:  object
    //                - positioning configuration
    //                - if panel config uses a position shorthand string it must be converted to object before it's passed to this function

    // if @position is not set return panel
    if (!_position) {
      panel.style.opacity = 1;
      return panel;
    }

    // merge position defaults with @position
    if (typeof _position === 'string') {
      _position = Object.assign({}, this.defaults.position, this.pOposition(_position));
    } else {
      _position = Object.assign({}, this.defaults.position, _position);
    }

    // process parameter functions for 'my', 'at', 'of'
    // 'offsetX', 'offsetY', 'minLeft', 'maxLeft', 'minTop', 'maxTop' are processed when params are applied
    ['my', 'at', 'of'].forEach(function (item) {
      if (typeof _position[item] === 'function') {
        _position[item] = _position[item].call(panel, panel);
      }
    });

    // panel uses option.container: 'window' position is always fixed
    if (panel.options.container === 'window') {
      panel.style.position = 'fixed';
    }

    // normalize param @panel to ensure it's an Element object
    if (typeof panel === 'string') {
      panel = document.querySelector(panel);
    } else if (Object.getPrototypeOf(panel).jquery) {
      panel = panel[0];
    } // else panel is assumed to be element object

    // container is either 'window' or the panel's parent element
    var container = panel.options.container === 'window' ? 'window' : panel.parentElement;

    // get base values in order to calculate position deltas
    // since getBoundingClientRect() calculates values relative to the viewport the parentElement of panel/elmtToPositionAgainst is irrelevant
    var panelRect = panel.getBoundingClientRect(),
      containerDomRect = panel.parentElement.getBoundingClientRect(),
      containerRect = container === 'window' ? {
        left: 0,
        top: 0,
        width: document.documentElement.clientWidth,
        height: window.innerHeight
      } // fake window.getBoundingClientRect() return value
      :
      //: panel.parentElement.getBoundingClientRect(); // using 'container' instead of 'panel.parentElement' produces an error
      // https://developer.mozilla.org/en-US/docs/Web/API/Element/getBoundingClientRect#Notes
      // due to the infos from above link IE and EDGE (old version not based on Chromium) report an error in strict mode -> line of code above replaced with line below
      {
        width: containerDomRect.width,
        height: containerDomRect.height,
        left: containerDomRect.left,
        top: containerDomRect.top
      };

    // calculate scale factors, needed for correct positioning if container is scaled - transform: scale()
    // window is never scaled --> scale factors default to 1
    var scaleFactor = container === 'window' ? {
      x: 1,
      y: 1
    } : {
      x: containerRect.width / container.offsetWidth,
      y: containerRect.height / container.offsetHeight
    };

    // get and apply border width values of container - needed for positioning corrections due to positioning with %-values
    var containerStyle = container === 'window' ? {
      borderTopWidth: '0px',
      borderRightWidth: '0px',
      borderBottomWidth: '0px',
      borderLeftWidth: '0px'
    } // fake getComputedStyle(window) return value
    : window.getComputedStyle(container);
    containerRect.width -= (parseFloat(containerStyle.borderLeftWidth) + parseFloat(containerStyle.borderRightWidth)) * scaleFactor.x;
    containerRect.height -= (parseFloat(containerStyle.borderTopWidth) + parseFloat(containerStyle.borderBottomWidth)) * scaleFactor.y;

    // calculate @position.of rect if @position.of is set
    var positionOfRect;
    if (!_position.of) {
      positionOfRect = containerRect;
    } else {
      if (typeof _position.of === 'string') {
        positionOfRect = _position.of === 'window' ? {
          borderTopWidth: '0px',
          borderRightWidth: '0px',
          borderBottomWidth: '0px',
          borderLeftWidth: '0px'
        } // fake getComputedStyle(window) return value
        : document.querySelector(_position.of).getBoundingClientRect();
      } else if (Object.getPrototypeOf(_position.of).jquery) {
        positionOfRect = _position.of[0].getBoundingClientRect();
      } else {
        positionOfRect = _position.of.getBoundingClientRect();
      }
    }

    // check for scrollbar width values
    var scrollbarwidthsWindow = this.getScrollbarWidth(document.body),
      scrollbarwidthsContainer = this.getScrollbarWidth(panel.parentElement);

    // calc css left for @panel in regard of @position.my and @position.at
    var left = '0px';
    if (_position.my.startsWith('left-')) {
      if (_position.at.startsWith('left-')) {
        if (_position.of) {
          left = positionOfRect.left - containerRect.left - parseFloat(containerStyle.borderLeftWidth) + 'px';
        } else {
          left = '0px';
        }
      } else if (_position.at.startsWith('center')) {
        if (_position.of) {
          left = positionOfRect.left - containerRect.left - parseFloat(containerStyle.borderLeftWidth) + positionOfRect.width / 2 + 'px';
        } else {
          left = containerRect.width / 2 + 'px';
        }
      } else if (_position.at.startsWith('right-')) {
        if (_position.of) {
          left = positionOfRect.left - containerRect.left - parseFloat(containerStyle.borderLeftWidth) + positionOfRect.width + 'px';
        } else {
          left = containerRect.width + 'px';
        }
      }
    } else if (_position.my.startsWith('center')) {
      if (_position.at.startsWith('left-')) {
        if (_position.of) {
          left = positionOfRect.left - containerRect.left - parseFloat(containerStyle.borderLeftWidth) - panelRect.width / 2 + 'px';
        } else {
          left = -panelRect.width / 2 + 'px';
        }
      } else if (_position.at.startsWith('center')) {
        if (_position.of) {
          left = positionOfRect.left - containerRect.left - parseFloat(containerStyle.borderLeftWidth) - (panelRect.width - positionOfRect.width) / 2 + 'px';
        } else {
          left = containerRect.width / 2 - panelRect.width / 2 + 'px';
        }
      } else if (_position.at.startsWith('right-')) {
        if (_position.of) {
          left = positionOfRect.left - containerRect.left - parseFloat(containerStyle.borderLeftWidth) + (positionOfRect.width - panelRect.width / 2) + 'px';
        } else {
          left = containerRect.width - panelRect.width / 2 + 'px';
        }
      }
    } else if (_position.my.startsWith('right-')) {
      if (_position.at.startsWith('left-')) {
        if (_position.of) {
          left = positionOfRect.left - containerRect.left - parseFloat(containerStyle.borderLeftWidth) - panelRect.width + 'px';
        } else {
          left = -panelRect.width + 'px';
        }
      } else if (_position.at.startsWith('center')) {
        if (_position.of) {
          left = positionOfRect.left - containerRect.left - parseFloat(containerStyle.borderLeftWidth) - panelRect.width + positionOfRect.width / 2 + 'px';
        } else {
          left = containerRect.width / 2 - panelRect.width + 'px';
        }
      } else if (_position.at.startsWith('right-')) {
        if (_position.of) {
          left = positionOfRect.left - containerRect.left - parseFloat(containerStyle.borderLeftWidth) + positionOfRect.width - panelRect.width + 'px';
        } else {
          left = containerRect.width - panelRect.width + 'px';
        }
        // correction for vertical scrollbar only needed for panels using my: 'right-*' together with at: 'right-*'
        if (container !== 'window') {
          left = parseFloat(left) - scrollbarwidthsContainer.y + 'px';
        }
      }
    }

    // calc css top for @panel in regard of @position.my and @position.at
    var top = '0px';
    if (_position.my.endsWith('-top')) {
      if (_position.at.endsWith('-top')) {
        if (_position.of) {
          top = positionOfRect.top - containerRect.top - parseFloat(containerStyle.borderTopWidth) + 'px';
        } else {
          top = '0px';
        }
      } else if (_position.at.endsWith('center')) {
        if (_position.of) {
          top = positionOfRect.top - containerRect.top - parseFloat(containerStyle.borderTopWidth) + positionOfRect.height / 2 + 'px';
        } else {
          top = containerRect.height / 2 + 'px';
        }
      } else if (_position.at.endsWith('-bottom')) {
        if (_position.of) {
          top = positionOfRect.top - containerRect.top - parseFloat(containerStyle.borderTopWidth) + positionOfRect.height + 'px';
        } else {
          top = containerRect.height + 'px';
        }
      }
    } else if (_position.my.endsWith('center')) {
      if (_position.at.endsWith('-top')) {
        if (_position.of) {
          top = positionOfRect.top - containerRect.top - parseFloat(containerStyle.borderTopWidth) - panelRect.height / 2 + 'px';
        } else {
          top = -panelRect.height / 2 + 'px';
        }
      } else if (_position.at.endsWith('center')) {
        if (_position.of) {
          top = positionOfRect.top - containerRect.top - parseFloat(containerStyle.borderTopWidth) - panelRect.height / 2 + positionOfRect.height / 2 + 'px';
        } else {
          top = containerRect.height / 2 - panelRect.height / 2 + 'px';
        }
      } else if (_position.at.endsWith('-bottom')) {
        if (_position.of) {
          top = positionOfRect.top - containerRect.top - parseFloat(containerStyle.borderTopWidth) - panelRect.height / 2 + positionOfRect.height + 'px';
        } else {
          top = containerRect.height - panelRect.height / 2 + 'px';
        }
      }
    } else if (_position.my.endsWith('-bottom')) {
      if (_position.at.endsWith('-top')) {
        if (_position.of) {
          top = positionOfRect.top - containerRect.top - parseFloat(containerStyle.borderTopWidth) - panelRect.height + 'px';
        } else {
          top = -panelRect.height + 'px';
        }
      } else if (_position.at.endsWith('center')) {
        if (_position.of) {
          top = positionOfRect.top - containerRect.top - parseFloat(containerStyle.borderTopWidth) - panelRect.height + positionOfRect.height / 2 + 'px';
        } else {
          top = containerRect.height / 2 - panelRect.height + 'px';
        }
      } else if (_position.at.endsWith('-bottom')) {
        if (_position.of) {
          top = positionOfRect.top - containerRect.top - parseFloat(containerStyle.borderTopWidth) - panelRect.height + positionOfRect.height + 'px';
        } else {
          top = containerRect.height - panelRect.height + 'px';
        }
        // correction for horizontal scrollbar only needed for panels using my: '*-bottom' together with at: '*-bottom'
        if (container !== 'window') {
          top = parseFloat(top) - scrollbarwidthsContainer.x + 'px';
        } else {
          top = parseFloat(top) - scrollbarwidthsWindow.x + 'px';
        }
      }
    }
    panel.style.left = scaleFactor.x === 1 ? left : parseFloat(left) / scaleFactor.x + 'px';
    panel.style.top = scaleFactor.y === 1 ? top : parseFloat(top) / scaleFactor.y + 'px';
    // at this point panels are correctly positioned according to my/at values

    var panelStyle = getComputedStyle(panel);
    // eslint-disable-next-line no-unused-vars
    var pos = {
      left: panelStyle.left,
      top: panelStyle.top
    };
    //console.log('pos after applying my/at/of:', pos);

    // apply autoposition only if ...
    if (_position.autoposition && _position.my === _position.at && ['left-top', 'center-top', 'right-top', 'left-bottom', 'center-bottom', 'right-bottom'].indexOf(_position.my) >= 0) {
      pos = this.applyPositionAutopos(panel, pos, _position);
      //console.log('let pos after applying autoposition:', pos);
    }

    // apply position.offsetX and position.offsetY
    if (_position.offsetX || _position.offsetY) {
      pos = this.applyPositionOffset(panel, pos, _position);
      //console.log('pos after applying offsets:', pos);
    }

    // calculate and apply position.minLeft, position.minTop, position.maxLeft and position.maxTop
    if (_position.minLeft || _position.minTop || _position.maxLeft || _position.maxTop) {
      pos = this.applyPositionMinMax(panel, pos, _position);
      //console.log('pos after applying minLeft, maxLeft, maxTop, minTop:', pos);
    }

    // apply position.modify
    // must be function returning an object with keys left/top, each with valid css length value
    if (_position.modify) {
      // eslint-disable-next-line no-unused-vars
      pos = this.applyPositionModify(panel, pos, _position);
      //console.log('pos after applying modify():', pos);
    }

    typeof panel.options.opacity === 'number' ? panel.style.opacity = panel.options.opacity : panel.style.opacity = 1;
    return panel;
  },
  applyPositionAutopos: function applyPositionAutopos(panel, pos, position) {
    // add class with position and autoposition direction
    var newClass = "".concat(position.my, "-").concat(position.autoposition.toLowerCase());
    panel.classList.add(newClass);

    // get all panels with same class
    var newClassAll = Array.prototype.slice.call(document.querySelectorAll(".".concat(newClass))),
      ownIndex = newClassAll.indexOf(panel);

    // if more than 1 position new panel
    if (newClassAll.length > 1) {
      switch (position.autoposition) {
        case 'down':
          // collect heights of all elmts to calc new top position
          newClassAll.forEach(function (item, index) {
            if (index > 0 && index <= ownIndex) {
              pos.top = parseFloat(pos.top) + newClassAll[--index].getBoundingClientRect().height + jsPanel.autopositionSpacing + 'px';
            }
          });
          break;
        case 'up':
          newClassAll.forEach(function (item, index) {
            if (index > 0 && index <= ownIndex) {
              pos.top = parseFloat(pos.top) - newClassAll[--index].getBoundingClientRect().height - jsPanel.autopositionSpacing + 'px';
            }
          });
          break;
        case 'right':
          // collect widths of all elmts to calc new left position
          newClassAll.forEach(function (item, index) {
            if (index > 0 && index <= ownIndex) {
              pos.left = parseFloat(pos.left) + newClassAll[--index].getBoundingClientRect().width + jsPanel.autopositionSpacing + 'px';
            }
          });
          break;
        case 'left':
          newClassAll.forEach(function (item, index) {
            if (index > 0 && index <= ownIndex) {
              pos.left = parseFloat(pos.left) - newClassAll[--index].getBoundingClientRect().width - jsPanel.autopositionSpacing + 'px';
            }
          });
          break;
      }
      panel.style.left = pos.left;
      panel.style.top = pos.top;
    }
    return {
      left: pos.left,
      top: pos.top
    };
  },
  applyPositionOffset: function applyPositionOffset(panel, pos, position) {
    ['offsetX', 'offsetY'].forEach(function (offset) {
      if (position[offset]) {
        if (typeof position[offset] === 'function') {
          position[offset] = position[offset].call(pos, pos, position);
        }
        if (isNaN(position[offset]) === false) {
          // if an offset's value type is integer it's interpreted as pixel value
          position[offset] = "".concat(position[offset], "px");
        } // else it's assumed offsets are strings with valid css length values
      } else {
        position[offset] = '0px';
      }
    });
    panel.style.left = "calc(".concat(panel.style.left, " + ").concat(position.offsetX, ")"); // offsetX
    panel.style.top = "calc(".concat(panel.style.top, " + ").concat(position.offsetY, ")"); // offsetY
    var panelStyle = getComputedStyle(panel);
    return {
      left: panelStyle.left,
      top: panelStyle.top
    };
  },
  applyPositionMinMax: function applyPositionMinMax(panel, pos, position) {
    ['minLeft', 'minTop', 'maxLeft', 'maxTop'].forEach(function (val) {
      if (position[val]) {
        if (typeof position[val] === 'function') {
          position[val] = position[val].call(pos, pos, position);
        }
        if (Number.isInteger(position[val]) || position[val].match(/^\d+$/)) {
          // if  val type is integer it's interpreted as pixel value
          position[val] = "".concat(position[val], "px");
        } // else it's assumed val is string with valid css length value
      }
    });
    // process minLeft
    if (position.minLeft) {
      // apply minLeft value in order to compare with previous left (pos.left)
      panel.style.left = position.minLeft;
      // now get computed css left
      var left = getComputedStyle(panel).left; // returns string with pixel value
      // now compare current left (minLeft) with pos.left
      if (parseFloat(left) < parseFloat(pos.left)) {
        // if minLeft is less than pos.left return to pos.left
        panel.style.left = pos.left;
      } else {
        // if minLeft is greater than pos.left keep minLeft and reset pos.left to new value
        pos.left = left;
      }
    }
    // process minTop
    if (position.minTop) {
      panel.style.top = position.minTop;
      var top = getComputedStyle(panel).top;
      if (parseFloat(top) < parseFloat(pos.top)) {
        panel.style.top = pos.top;
      } else {
        pos.top = top;
      }
    }
    // process maxLeft
    if (position.maxLeft) {
      panel.style.left = position.maxLeft;
      var _left = getComputedStyle(panel).left;
      if (parseFloat(_left) > parseFloat(pos.left)) {
        panel.style.left = pos.left;
      } else {
        pos.left = _left;
      }
    }
    // process maxTop
    if (position.maxTop) {
      panel.style.top = position.maxTop;
      var _top = getComputedStyle(panel).top;
      if (parseFloat(_top) > parseFloat(pos.top)) {
        panel.style.top = pos.top;
      } else {
        pos.top = _top;
      }
    }
    var panelStyle = getComputedStyle(panel);
    return {
      left: panelStyle.left,
      top: panelStyle.top
    };
  },
  applyPositionModify: function applyPositionModify(panel, pos, position) {
    if (position.modify && typeof position.modify === 'function') {
      var modifiedPosition = position.modify.call(pos, pos, position);
      panel.style.left = Number.isInteger(modifiedPosition.left) || modifiedPosition.left.match(/^\d+$/) ? "".concat(modifiedPosition.left, "px") : modifiedPosition.left;
      panel.style.top = Number.isInteger(modifiedPosition.top) || modifiedPosition.top.match(/^\d+$/) ? "".concat(modifiedPosition.top, "px") : modifiedPosition.top;
    }
    var panelStyle = getComputedStyle(panel);
    return {
      left: panelStyle.left,
      top: panelStyle.top
    };
  },
  autopositionRemaining: function autopositionRemaining(panel) {
    var autoPos,
      parent = panel.options.container;
    ['left-top-down', 'left-top-right', 'center-top-down', 'right-top-down', 'right-top-left', 'left-bottom-up', 'left-bottom-right', 'center-bottom-up', 'right-bottom-up', 'right-bottom-left'].forEach(function (item) {
      if (panel.classList.contains(item)) {
        autoPos = item;
      }
    });
    if (autoPos) {
      var box = parent === 'window' ? document.body : typeof parent === 'string' ? document.querySelector(parent) : parent;
      box.querySelectorAll(".".concat(autoPos)).forEach(function (item) {
        return item.reposition();
      });
    }
  },
  // theming methods -------------------------
  getThemeDetails: function getThemeDetails(th) {
    var theme = this.pOtheme(th);
    if (theme.color.startsWith('bootstrap-')) {
      // works with bootstrap 3 and 4
      var index = theme.color.indexOf('-'),
        btn = document.createElement('button');
      btn.className = 'btn btn' + theme.color.slice(index);
      document.body.appendChild(btn);
      theme.color = getComputedStyle(btn).backgroundColor.replace(/\s+/gi, '');
      document.body.removeChild(btn);
      // noinspection JSUnusedAssignment
      btn = undefined;
    }
    theme.colors = this.calcColors(theme.color);
    return theme;
  },
  clearTheme: function clearTheme(panel, cb) {
    panel.content.classList.remove('jsPanel-content-filled', 'jsPanel-content-filledlight');
    panel.header.classList.remove('jsPanel-hdr-light');
    panel.header.classList.remove('jsPanel-hdr-dark');
    panel.style.backgroundColor = '';
    this.setStyles(panel.headertoolbar, {
      boxShadow: '',
      width: '',
      marginLeft: '',
      borderTopColor: 'transparent'
    });
    this.setStyles(panel.content, {
      background: '',
      borderTopColor: 'transparent'
    });
    panel.header.style.background = '';
    Array.prototype.slice.call(panel.controlbar.querySelectorAll('.jsPanel-icon')).concat([panel.headerlogo, panel.headertitle, panel.headertoolbar, panel.content]).forEach(function (item) {
      return item.style.color = '';
    });
    if (cb) {
      cb.call(panel, panel);
    }
    return panel;
  },
  applyColorTheme: function applyColorTheme(panel, themeDetails) {
    panel.style.backgroundColor = themeDetails.colors[0];
    panel.header.style.backgroundColor = themeDetails.colors[0];
    panel.header.style.color = themeDetails.colors[3];
    ['.jsPanel-headerlogo', '.jsPanel-title', '.jsPanel-hdr-toolbar'].forEach(function (item) {
      return panel.querySelector(item).style.color = themeDetails.colors[3];
    });
    panel.querySelectorAll('.jsPanel-controlbar .jsPanel-btn').forEach(function (item) {
      return item.style.color = themeDetails.colors[3];
    });
    // apply border to content only themes 'filled'
    if (typeof panel.options.theme === 'string' && themeDetails.filling === 'filled') {
      panel.content.style.borderTop = themeDetails.colors[3] === '#000000' ? '1px solid rgba(0,0,0,0.15)' : '1px solid rgba(255,255,255,0.15)';
    }
    if (themeDetails.colors[3] === '#000000') {
      panel.header.classList.add('jsPanel-hdr-light');
    } else {
      panel.header.classList.add('jsPanel-hdr-dark');
    }
    if (themeDetails.filling) {
      switch (themeDetails.filling) {
        case 'filled':
          this.setStyles(panel.content, {
            backgroundColor: themeDetails.colors[2],
            color: themeDetails.colors[3]
          });
          break;
        case 'filledlight':
          panel.content.style.backgroundColor = themeDetails.colors[1];
          break;
        case 'filleddark':
          this.setStyles(panel.content, {
            backgroundColor: themeDetails.colors[6],
            color: themeDetails.colors[7]
          });
          break;
        default:
          panel.content.style.backgroundColor = themeDetails.filling;
          panel.content.style.color = this.perceivedBrightness(themeDetails.filling) <= this.colorBrightnessThreshold ? '#fff' : '#000';
      }
    }
    return panel;
  },
  applyCustomTheme: function applyCustomTheme(panel, theme) {
    var _this = this;
    var defaults = {
        bgPanel: '#ffffff',
        bgContent: '#ffffff',
        bgFooter: '#f5f5f5',
        colorHeader: '#000000',
        colorContent: '#000000',
        colorFooter: '#000000',
        border: undefined,
        borderRadius: undefined
      },
      passedTheme = _typeof(theme) === 'object' ? Object.assign(defaults, theme) : defaults;
    var bgPanel = passedTheme.bgPanel,
      bgContent = passedTheme.bgContent,
      colorHeader = passedTheme.colorHeader,
      colorContent = passedTheme.colorContent,
      bgFooter = passedTheme.bgFooter,
      colorFooter = passedTheme.colorFooter;
    // set background panel/header
    if (this.colorNames[bgPanel]) {
      panel.style.background = '#' + this.colorNames[bgPanel];
    } else {
      panel.style.background = this.getCssVariableValue(bgPanel);
      // getCssVariableValue(bgPanel) returns bgPanel unchanged if value does not start with either '--' or 'var'
    }
    // set font color header
    if (this.colorNames[colorHeader]) {
      colorHeader = '#' + this.colorNames[colorHeader];
    }
    ['.jsPanel-headerlogo', '.jsPanel-title', '.jsPanel-hdr-toolbar'].forEach(function (item) {
      return panel.querySelector(item).style.color = _this.getCssVariableValue(colorHeader);
    });
    panel.querySelectorAll('.jsPanel-controlbar .jsPanel-btn').forEach(function (item) {
      return item.style.color = _this.getCssVariableValue(colorHeader);
    });
    // set content background
    if (this.colorNames[bgContent]) {
      panel.content.style.background = '#' + this.colorNames[bgContent];
    } else {
      panel.content.style.background = this.getCssVariableValue(bgContent);
      // getCssVariableValue(bgContent) returns bgContent unchanged if value does not start with either '--' or 'var'
    }
    // set content font color
    this.colorNames[colorContent] ? panel.content.style.color = '#' + this.colorNames[colorContent] : panel.content.style.color = this.getCssVariableValue(colorContent); // getCssVariableValue(val) returns val unchanged if val does not start with either '--' or 'var'
    // set border-top for header toolbar and add header class
    var pbPanel = this.perceivedBrightness(colorHeader);
    if (pbPanel > this.colorBrightnessThreshold) {
      panel.header.classList.add('jsPanel-hdr-dark');
    } else {
      panel.header.classList.add('jsPanel-hdr-light');
    }
    // set border-top for content
    var pbContent = this.perceivedBrightness(colorContent);
    pbContent > this.colorBrightnessThreshold ? panel.content.style.borderTop = '1px solid rgba(255,255,255,0.15)' : panel.content.style.borderTop = '1px solid rgba(0,0,0,0.15)';
    // set footer background
    if (this.colorNames[bgFooter]) {
      panel.footer.style.background = '#' + this.colorNames[bgFooter];
    } else {
      panel.footer.style.background = this.getCssVariableValue(bgFooter);
      // getCssVariableValue(bgFooter) returns bgFooter unchanged if value does not start with either '--' or 'var'
    }
    // set footer font color
    this.colorNames[colorFooter] ? panel.footer.style.color = '#' + this.colorNames[colorFooter] : panel.footer.style.color = this.getCssVariableValue(colorFooter);
    // set panel border (option.border does not work for themes using an object)
    if (passedTheme.border) {
      panel.setBorder(passedTheme.border);
    }
    if (passedTheme.borderRadius) {
      panel.options.borderRadius = undefined;
      panel.setBorderRadius(passedTheme.borderRadius);
    }
    return panel;
  },
  // methods dealing with css and html -------
  getCssVariableValue: function getCssVariableValue(str) {
    // str may be something like '--my-color' or 'var(--my-color)'
    // if str does not start with '--' or 'var' str is returned unchanged
    // str = str.replace(/\s+/g, ''); do not uncomment this line !!
    if (str.startsWith('--')) {
      return getComputedStyle(document.documentElement).getPropertyValue(str).replace(/\s+/g, '');
    } else if (str.startsWith('var')) {
      var v = str.slice(str.indexOf('(') + 1, str.indexOf(')'));
      return getComputedStyle(document.documentElement).getPropertyValue(v).replace(/\s+/g, '');
    }
    return str;
  },
  getScrollbarWidth: function getScrollbarWidth() {
    var elmt = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document.body;
    if (elmt === document.body) {
      return {
        y: window.innerWidth - document.documentElement.clientWidth,
        x: window.innerHeight - document.documentElement.clientHeight
      };
    } else {
      var styles = getComputedStyle(elmt);
      return {
        y: elmt.offsetWidth - elmt.clientWidth - parseFloat(styles.borderRightWidth) - parseFloat(styles.borderLeftWidth),
        x: elmt.offsetHeight - elmt.clientHeight - parseFloat(styles.borderBottomWidth) - parseFloat(styles.borderTopWidth)
      };
    }
  },
  remClass: function remClass(elmt, classnames) {
    classnames.trim().split(/\s+/).forEach(function (item) {
      return elmt.classList.remove(item);
    });
    return elmt;
  },
  setClass: function setClass(elmt, classnames) {
    classnames.trim().split(/\s+/).forEach(function (item) {
      return elmt.classList.add(item);
    });
    return elmt;
  },
  setStyles: function setStyles(elmt, stylesobject) {
    for (var _i = 0, _Object$entries = Object.entries(stylesobject); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
        prop = _Object$entries$_i[0],
        value = _Object$entries$_i[1];
      elmt.style[prop] = typeof value === 'string' ? jsPanel.getCssVariableValue(value) : value;
    }
    return elmt;
  },
  setStyle: function setStyle(elmt, stylesobject) {
    return this.setStyles.call(elmt, elmt, stylesobject);
  },
  // alias for setStyles()
  strToHtml: function strToHtml(str) {
    // TODO: add param to strip script tags from returned DocumentFragment
    /* str has to be an HTMLString
     * returns a DocumentFragment - https://developer.mozilla.org/en-US/docs/Web/API/DocumentFragment
     * after inserting executes inline script and script tags */
    return document.createRange().createContextualFragment(str);
  },
  toggleClass: function toggleClass(elmt, classnames) {
    // IE11 doesn't support https://developer.mozilla.org/en-US/docs/Web/API/DOMTokenList/toggle
    classnames.trim().split(/\s+/).forEach(function (classname) {
      return elmt.classList.contains(classname) ? elmt.classList.remove(classname) : elmt.classList.add(classname);
    });
    return elmt;
  },
  emptyNode: function emptyNode(node) {
    while (node.firstChild) {
      node.removeChild(node.firstChild);
    }
    return node;
  },
  addScript: function addScript(path) {
    var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'application/javascript';
    var cb = arguments.length > 2 ? arguments[2] : undefined;
    if (!document.querySelector("script[src=\"".concat(path, "\"]"))) {
      var script = document.createElement('script');
      script.src = path;
      script.type = type;
      document.head.appendChild(script);
      if (cb) {
        script.onload = cb;
      }
    }
  },
  ajax: function ajax(ajaxConfig, panel) {
    var config,
      urlParts,
      xhr = new XMLHttpRequest();
    var configDefaults = {
      method: 'GET',
      async: true,
      user: '',
      pwd: '',
      done: function done() {
        if (panel) {
          var res = jsPanel.strToHtml(this.responseText);
          if (config.urlSelector) {
            res = res.querySelector(config.urlSelector);
          }
          panel.contentRemove();
          panel.content.append(res);
        }
      },
      autoresize: true,
      autoreposition: true
    };
    if (panel && typeof ajaxConfig === 'string') {
      config = Object.assign({}, configDefaults, {
        url: ajaxConfig
      });
    } else if (_typeof(ajaxConfig) === 'object' && ajaxConfig.url) {
      config = Object.assign({}, configDefaults, ajaxConfig);
      config.url = ajaxConfig.url;
      // reset timeout to 0, withCredentials & responseType to false if request is synchronous
      if (config.async === false) {
        config.timeout = 0;
        if (config.withCredentials) {
          config.withCredentials = undefined;
        }
        if (config.responseType) {
          config.responseType = undefined;
        }
      }
    } else {
      if (this.errorReporting) {
        var err = 'XMLHttpRequest seems to miss the <mark>url</mark> parameter!';
        jsPanel.errorpanel(err);
      }
      return;
    }
    // check url for added selector
    urlParts = config.url.trim().split(/\s+/);
    config.url = encodeURI(urlParts[0]);
    if (urlParts.length > 1) {
      urlParts.shift();
      config.urlSelector = urlParts.join(' ');
    }
    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          panel ? config.done.call(xhr, xhr, panel) : config.done.call(xhr, xhr);
        } else {
          if (config.fail) {
            panel ? config.fail.call(xhr, xhr, panel) : config.fail.call(xhr, xhr);
          }
        }
        if (config.always) {
          panel ? config.always.call(xhr, xhr, panel) : config.always.call(xhr, xhr);
        }

        // resize and/or reposition panel if either width or height is set to 'auto'
        if (panel) {
          if (config.autoresize || config.autoreposition) {
            jsPanel.ajaxAutoresizeAutoreposition(panel, config);
          }
        }

        // allows plugins to add callback functions to the ajax always callback
        if (jsPanel.ajaxAlwaysCallbacks.length) {
          jsPanel.ajaxAlwaysCallbacks.forEach(function (item) {
            panel ? item.call(xhr, xhr, panel) : item.call(xhr, xhr);
          });
        }
      }
    };
    xhr.open(config.method, config.url, config.async, config.user, config.pwd);
    xhr.timeout = config.timeout || 0;
    if (config.withCredentials) {
      xhr.withCredentials = config.withCredentials;
    }
    if (config.responseType) {
      xhr.responseType = config.responseType;
    }
    if (config.beforeSend) {
      panel ? config.beforeSend.call(xhr, xhr, panel) : config.beforeSend.call(xhr, xhr);
    }
    config.data ? xhr.send(config.data) : xhr.send(null);
  },
  fetch: function (_fetch) {
    function fetch(_x, _x2) {
      return _fetch.apply(this, arguments);
    }
    fetch.toString = function () {
      return _fetch.toString();
    };
    return fetch;
  }(function (fetchConfig, panel) {
    var config;
    var configDefaults = {
      bodyMethod: 'text',
      autoresize: true,
      autoreposition: true,
      done: function done(response, panel) {
        if (panel) {
          var res = jsPanel.strToHtml(response);
          panel.contentRemove();
          panel.content.append(res);
        }
      }
    };
    if (panel && typeof fetchConfig === 'string') {
      config = Object.assign({}, configDefaults, {
        resource: encodeURI(fetchConfig)
      });
    } else if (_typeof(fetchConfig) === 'object' && fetchConfig.resource) {
      config = Object.assign({}, configDefaults, fetchConfig);
      config.resource = encodeURI(fetchConfig.resource);
    } else {
      if (this.errorReporting) {
        var err = 'Fetch Request seems to miss the <mark>resource</mark> parameter!';
        jsPanel.errorpanel(err);
      }
      return;
    }
    var fetchInit = config.fetchInit || {};
    if (config.beforeSend) {
      panel ? config.beforeSend.call(fetchConfig, fetchConfig, panel) : config.beforeSend.call(fetchConfig, fetchConfig);
    }
    fetch(config.resource, fetchInit).then(function (response) {
      if (response.ok) {
        return response[config.bodyMethod]();
      }
    }).then(function (response) {
      panel ? config.done.call(response, response, panel) : config.done.call(response, response);
      if (panel) {
        // resize and/or reposition panel if either width or height is set to 'auto'
        if (config.autoresize || config.autoreposition) {
          jsPanel.ajaxAutoresizeAutoreposition(panel, config);
        }
      }
    });
  }),
  ajaxAutoresizeAutoreposition: function ajaxAutoresizeAutoreposition(panel, ajaxOrFetchConfig) {
    var oContentSize = panel.options.contentSize;
    if (typeof oContentSize === 'string' && oContentSize.match(/auto/i)) {
      var parts = oContentSize.split(' '),
        sizes = Object.assign({}, {
          width: parts[0],
          height: parts[1]
        });
      if (ajaxOrFetchConfig.autoresize) {
        panel.resize(sizes);
      }
      if (!panel.classList.contains('jsPanel-contextmenu')) {
        if (ajaxOrFetchConfig.autoreposition) {
          panel.reposition();
        }
      }
    } else if (_typeof(oContentSize) === 'object' && (oContentSize.width === 'auto' || oContentSize.height === 'auto')) {
      var _sizes = Object.assign({}, oContentSize);
      if (ajaxOrFetchConfig.autoresize) {
        panel.resize(_sizes);
      }
      if (!panel.classList.contains('jsPanel-contextmenu')) {
        if (ajaxOrFetchConfig.autoreposition) {
          panel.reposition();
        }
      }
    }
  },
  // templates and other elements ----------
  createPanelTemplate: function createPanelTemplate() {
    var dataAttr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    var panel = document.createElement('div');
    panel.className = 'jsPanel';
    panel.style.left = '0';
    panel.style.top = '0';
    if (dataAttr) {
      ['close', 'maximize', 'normalize', 'minimize', 'smallify'].forEach(function (item) {
        panel.setAttribute("data-btn".concat(item), 'enabled');
      });
    }
    panel.innerHTML = "<div class=\"jsPanel-hdr\">\n                                <div class=\"jsPanel-headerbar\">\n                                    <div class=\"jsPanel-headerlogo\"></div>\n                                    <div class=\"jsPanel-titlebar\">\n                                        <div class=\"jsPanel-title\"></div>\n                                    </div>\n                                    <div class=\"jsPanel-controlbar\">\n                                        <button type=\"button\" class=\"jsPanel-btn jsPanel-btn-smallify\" aria-label=\"Smallify\">".concat(this.icons.smallify, "</button>\n                                        <button type=\"button\" class=\"jsPanel-btn jsPanel-btn-minimize\" aria-label=\"Minimize\">").concat(this.icons.minimize, "</button>\n                                        <button type=\"button\" class=\"jsPanel-btn jsPanel-btn-normalize\" aria-label=\"Normalize\">").concat(this.icons.normalize, "</button>\n                                        <button type=\"button\" class=\"jsPanel-btn jsPanel-btn-maximize\" aria-label=\"Maximize\">").concat(this.icons.maximize, "</button>\n                                        <button type=\"button\" class=\"jsPanel-btn jsPanel-btn-close\" aria-label=\"Close\">").concat(this.icons.close, "</button>\n                                    </div>\n                                </div>\n                                <div class=\"jsPanel-hdr-toolbar\"></div>\n                            </div>\n                            <div class=\"jsPanel-progressbar\">\n                                <div class=\"jsPanel-progressbar-slider\"></div>\n                            </div>\n                            <div class=\"jsPanel-content\"></div>\n                            <div class=\"jsPanel-minimized-box\"></div>\n                            <div class=\"jsPanel-ftr\"></div>");
    return panel;
  },
  createMinimizedTemplate: function createMinimizedTemplate() {
    var panel = document.createElement('div');
    panel.className = 'jsPanel-replacement';
    panel.innerHTML = "<div class=\"jsPanel-hdr\">\n                                <div class=\"jsPanel-headerbar\">\n                                    <div class=\"jsPanel-headerlogo\"></div>\n                                    <div class=\"jsPanel-titlebar\">\n                                        <div class=\"jsPanel-title\"></div>\n                                    </div>\n                                    <div class=\"jsPanel-controlbar\">\n                                        <button type=\"button\" class=\"jsPanel-btn jsPanel-btn-sm jsPanel-btn-normalize\" aria-label=\"Normalize\">".concat(this.icons.normalize, "</button>\n                                        <button type=\"button\" class=\"jsPanel-btn jsPanel-btn-sm jsPanel-btn-maximize\" aria-label=\"Maximize\">").concat(this.icons.maximize, "</button>\n                                        <button type=\"button\" class=\"jsPanel-btn jsPanel-btn-sm jsPanel-btn-close\" aria-label=\"Close\">").concat(this.icons.close, "</button>\n                                    </div>\n                                </div>\n                            </div>");
    return panel;
  },
  createSnapArea: function createSnapArea(panel, pos, snapsens) {
    var el = document.createElement('div'),
      parent = panel.parentElement;
    el.className = "jsPanel-snap-area jsPanel-snap-area-".concat(pos);
    if (pos === 'lt' || pos === 'rt' || pos === 'rb' || pos === 'lb') {
      el.style.width = snapsens + 'px';
      el.style.height = snapsens + 'px';
    } else if (pos === 'ct' || pos === 'cb') {
      el.style.height = snapsens + 'px';
    } else if (pos === 'lc' || pos === 'rc') {
      el.style.width = snapsens + 'px';
    }
    if (parent !== document.body) {
      el.style.position = 'absolute';
    }
    if (!document.querySelector(".jsPanel-snap-area.jsPanel-snap-area-".concat(pos))) {
      panel.parentElement.appendChild(el);
    }
  },
  removeSnapAreas: function removeSnapAreas() {
    document.querySelectorAll('.jsPanel-snap-area').forEach(function (el) {
      return el.parentElement.removeChild(el);
    });
  },
  // various helpers -------
  extend: function extend(obj) {
    // obj needs to be a plain object (to extend the individual panel, not the global object)
    if (Object.prototype.toString.call(obj) === '[object Object]') {
      for (var ext in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, ext)) {
          this.extensions[ext] = obj[ext];
        }
      }
    }
  },
  getPanels: function getPanels() {
    var condition = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : function () {
      return this.classList.contains('jsPanel-standard');
    };
    return Array.prototype.slice.call(document.querySelectorAll('.jsPanel')).filter(function (value) {
      return condition.call(value, value);
    }).sort(function (a, b) {
      return b.style.zIndex - a.style.zIndex;
    });
  },
  processCallbacks: function processCallbacks(panel, arg) {
    var someOrEvery = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'some';
    var param = arguments.length > 3 ? arguments[3] : undefined;
    var param2 = arguments.length > 4 ? arguments[4] : undefined;
    // if arg != array make it one
    if (typeof arg === 'function') {
      arg = [arg];
    }
    // some():  execute callbacks until one is found returning a truthy value
    // every(): execute callbacks until one is found returning a falsy value
    // truthy values are: '0' (string with single zero), 'false' (string with text false), [] (empty array), {} (empty object), function(){} ("empty" function)
    // falsy values are: false, 0, '', "", null, undefined, NaN
    if (someOrEvery) {
      return arg[someOrEvery](function (cb) {
        return cb.call(panel, panel, param, param2);
      });
    } else {
      arg.forEach(function (cb) {
        return cb.call(panel, panel, param, param2);
      });
    }
  },
  resetZi: function resetZi() {
    this.zi = function () {
      var startValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : jsPanel.ziBase;
      var val = startValue;
      return {
        next: function next() {
          return val++;
        }
      };
    }();
    Array.prototype.slice.call(document.querySelectorAll('.jsPanel-standard')).sort(function (a, b) {
      return a.style.zIndex - b.style.zIndex;
    }).forEach(function (panel) {
      return panel.style.zIndex = jsPanel.zi.next();
    });
  },
  errorpanel: function errorpanel(e) {
    this.create({
      paneltype: 'error',
      dragit: false,
      resizeit: false,
      theme: {
        bgPanel: 'white',
        bgContent: 'white',
        colorHeader: 'rebeccapurple',
        colorContent: '#333333',
        border: '2px solid rebeccapurple'
      },
      borderRadius: '.33rem',
      headerControls: 'closeonly xs',
      headerTitle: '&#9888; jsPanel Error',
      contentSize: {
        width: '50%',
        height: 'auto'
      },
      position: 'center-top 0 5 down',
      animateIn: 'jsPanelFadeIn',
      content: "<div class=\"jsPanel-error-content-separator\"></div><p>".concat(e, "</p>")
    });
  },
  // METHOD CREATING THE PANEL ---------------------------------------------
  create: function create() {
    var _this2 = this;
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var cb = arguments.length > 1 ? arguments[1] : undefined;
    // initialize z-index generator
    if (!jsPanel.zi) {
      jsPanel.zi = function () {
        var startValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : jsPanel.ziBase;
        var val = startValue;
        return {
          next: function next() {
            return val++;
          }
        };
      }();
    }
    if (options.config) {
      options = Object.assign({}, this.defaults, options.config, options);
      delete options.config;
    } else {
      options = Object.assign({}, this.defaults, options);
    }
    if (!options.id) {
      options.id = "jsPanel-".concat(jsPanel.idCounter += 1);
    } else if (typeof options.id === 'function') {
      options.id = options.id();
    }
    var p = document.getElementById(options.id);
    if (p !== null) {
      // if a panel with passed id already exists, front it and return false
      if (p.classList.contains('jsPanel')) {
        p.front();
      }
      if (this.errorReporting) {
        var err = "&#9664; COULD NOT CREATE NEW JSPANEL &#9658;<br>An element with the ID <mark>".concat(options.id, "</mark> already exists in the document.");
        jsPanel.errorpanel(err);
      }
      return false;
    }

    // check whether container is valid -> if not return and throw error
    var panelContainer = this.pOcontainer(options.container);
    // panelContainer might be a NodeList, so use only first node in list
    if (_typeof(panelContainer) === 'object' && panelContainer.length && panelContainer.length > 0) {
      panelContainer = panelContainer[0];
    }
    if (!panelContainer) {
      if (this.errorReporting) {
        var _err = '&#9664; COULD NOT CREATE NEW JSPANEL &#9658;<br>The container to append the panel to does not exist';
        jsPanel.errorpanel(_err);
      }
      return false;
    }

    // normalize on... callbacks
    // these callbacks must be an array of function(s) in order to be able to dynamically add/remove callbacks (for example in extensions)
    ['onbeforeclose', 'onbeforemaximize', 'onbeforeminimize', 'onbeforenormalize', 'onbeforesmallify', 'onbeforeunsmallify', 'onclosed', 'onfronted', 'onmaximized', 'onminimized', 'onnormalized', 'onsmallified', 'onstatuschange', 'onunsmallified'].forEach(function (item) {
      if (options[item]) {
        if (typeof options[item] === 'function') {
          options[item] = [options[item]];
        }
      } else {
        options[item] = [];
      }
    });
    var self = options.template || this.createPanelTemplate();

    // Properties
    self.options = options;
    self.closetimer = undefined;
    self.status = 'initialized';
    self.currentData = {};
    self.header = self.querySelector('.jsPanel-hdr'); // complete header section
    self.headerbar = self.header.querySelector('.jsPanel-headerbar'); // log, title and controls
    self.titlebar = self.header.querySelector('.jsPanel-titlebar'); // div surrounding title div
    self.headerlogo = self.headerbar.querySelector('.jsPanel-headerlogo'); // logo only
    self.headertitle = self.headerbar.querySelector('.jsPanel-title'); // title div
    self.controlbar = self.headerbar.querySelector('.jsPanel-controlbar'); // div surrounding all controls
    self.headertoolbar = self.header.querySelector('.jsPanel-hdr-toolbar');
    self.content = self.querySelector('.jsPanel-content');
    self.footer = self.querySelector('.jsPanel-ftr');
    self.snappableTo = false;
    self.snapped = false;
    self.droppableTo = false;
    self.progressbar = self.autocloseProgressbar = self.querySelector('.jsPanel-progressbar'); // self.autocloseProgressbar kept for compatibility

    // Events
    var jspanelloaded = new CustomEvent('jspanelloaded', {
        detail: options.id,
        cancelable: true
      }),
      jspanelstatuschange = new CustomEvent('jspanelstatuschange', {
        detail: options.id,
        cancelable: true
      }),
      jspanelbeforenormalize = new CustomEvent('jspanelbeforenormalize', {
        detail: options.id,
        cancelable: true
      }),
      jspanelnormalized = new CustomEvent('jspanelnormalized', {
        detail: options.id,
        cancelable: true
      }),
      jspanelbeforemaximize = new CustomEvent('jspanelbeforemaximize', {
        detail: options.id,
        cancelable: true
      }),
      jspanelmaximized = new CustomEvent('jspanelmaximized', {
        detail: options.id,
        cancelable: true
      }),
      jspanelbeforeminimize = new CustomEvent('jspanelbeforeminimize', {
        detail: options.id,
        cancelable: true
      }),
      jspanelminimized = new CustomEvent('jspanelminimized', {
        detail: options.id,
        cancelable: true
      }),
      jspanelbeforesmallify = new CustomEvent('jspanelbeforesmallify', {
        detail: options.id,
        cancelable: true
      }),
      jspanelsmallified = new CustomEvent('jspanelsmallified', {
        detail: options.id,
        cancelable: true
      }),
      jspanelsmallifiedmax = new CustomEvent('jspanelsmallifiedmax', {
        detail: options.id,
        cancelable: true
      }),
      jspanelbeforeunsmallify = new CustomEvent('jspanelbeforeunsmallify', {
        detail: options.id,
        cancelable: true
      }),
      jspanelfronted = new CustomEvent('jspanelfronted', {
        detail: options.id,
        cancelable: true
      }),
      jspanelbeforeclose = new CustomEvent('jspanelbeforeclose', {
        detail: options.id,
        cancelable: true
      }),
      jspanelclosed = new CustomEvent('jspanelclosed', {
        detail: options.id,
        cancelable: true
      }),
      jspanelcloseduser = new CustomEvent('jspanelcloseduser', {
        detail: options.id,
        cancelable: true
      });
    // make panel available as event object property 'panel'
    [jspanelloaded, jspanelstatuschange, jspanelbeforenormalize, jspanelnormalized, jspanelbeforemaximize, jspanelmaximized, jspanelbeforeminimize, jspanelminimized, jspanelbeforesmallify, jspanelsmallified, jspanelsmallifiedmax, jspanelbeforeunsmallify, jspanelfronted, jspanelbeforeclose].forEach(function (evt) {
      return evt.panel = self;
    });

    // controls buttons
    var closeBtn = self.querySelector('.jsPanel-btn-close'),
      maxBtn = self.querySelector('.jsPanel-btn-maximize'),
      normBtn = self.querySelector('.jsPanel-btn-normalize'),
      smallBtn = self.querySelector('.jsPanel-btn-smallify'),
      minBtn = self.querySelector('.jsPanel-btn-minimize');
    if (closeBtn) {
      jsPanel.pointerup.forEach(function (item) {
        closeBtn.addEventListener(item, function (e) {
          e.preventDefault();
          // disable close for all mouse buttons but left
          if (e.button && e.button > 0) {
            return false;
          }
          self.close(null, true); // true indicates panel closed by using the close control
        });
      });
    }

    if (maxBtn) {
      jsPanel.pointerup.forEach(function (item) {
        maxBtn.addEventListener(item, function (e) {
          e.preventDefault();
          if (e.button && e.button > 0) {
            return false;
          }
          self.maximize();
        });
      });
    }
    if (normBtn) {
      jsPanel.pointerup.forEach(function (item) {
        normBtn.addEventListener(item, function (e) {
          e.preventDefault();
          if (e.button && e.button > 0) {
            return false;
          }
          self.normalize();
        });
      });
    }
    if (smallBtn) {
      jsPanel.pointerup.forEach(function (item) {
        smallBtn.addEventListener(item, function (e) {
          e.preventDefault();
          if (e.button && e.button > 0) {
            return false;
          }
          if (self.status === 'normalized' || self.status === 'maximized') {
            self.smallify();
          } else if (self.status === 'smallified' || self.status === 'smallifiedmax') {
            self.unsmallify();
          }
        });
      });
    }
    if (minBtn) {
      jsPanel.pointerup.forEach(function (item) {
        minBtn.addEventListener(item, function (e) {
          e.preventDefault();
          if (e.button && e.button > 0) {
            return false;
          }
          self.minimize();
        });
      });
    }

    // import extensions (extensions of the individual panel, not the global object jsPanel)
    var extensions = jsPanel.extensions;
    for (var ext in extensions) {
      if (Object.prototype.hasOwnProperty.call(extensions, ext)) {
        self[ext] = extensions[ext];
      }
    }

    // panel methods
    self.setBorder = function (val) {
      var border = jsPanel.pOborder(val);
      if (!border[2].length) {
        border[2] = self.style.backgroundColor;
      }
      border = border.join(' ');
      self.style.border = border;
      self.options.border = border;
      return self;
    };
    self.setBorderRadius = function (rad) {
      if (typeof rad === 'string') {
        if (rad.startsWith('--') || rad.startsWith('var')) {
          rad = rad.replace(/\s*\(\s*/g, '(') // remove whitespace around opening brackets
          .replace(/\s*\)/g, ')') // remove whitespace around closing brackets
          .replace(/\s+/g, ' '); // replace all other whitespace(s) with a single whitespace
          rad = jsPanel.getCssVariableValue(rad);
        }
      }
      if (typeof rad === 'number') {
        rad += 'px';
      }
      self.style.borderRadius = rad;
      var br = getComputedStyle(self);
      // set border-radius of either header or content section depending on presence of header
      if (self.options.header) {
        self.header.style.borderTopLeftRadius = br.borderTopLeftRadius;
        self.header.style.borderTopRightRadius = br.borderTopRightRadius;
      } else {
        self.content.style.borderTopLeftRadius = br.borderTopLeftRadius;
        self.content.style.borderTopRightRadius = br.borderTopRightRadius;
      }
      // set border-radius of either footer or content section depending on presence of footer
      if (self.options.footerToolbar) {
        self.footer.style.borderBottomRightRadius = br.borderBottomRightRadius;
        self.footer.style.borderBottomLeftRadius = br.borderBottomLeftRadius;
      } else {
        self.content.style.borderBottomRightRadius = br.borderBottomRightRadius;
        self.content.style.borderBottomLeftRadius = br.borderBottomLeftRadius;
      }
      return self;
    };
    self.setTheme = function () {
      var theme = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : options.theme;
      var cb = arguments.length > 1 ? arguments[1] : undefined;
      // if panel is minimized normalize it for theme change
      var minimized;
      if (self.status === 'minimized') {
        minimized = true;
        self.normalize();
      }
      // first remove all theme related styles
      jsPanel.clearTheme(self);
      if (_typeof(theme) === 'object') {
        options.border = undefined;
        jsPanel.applyCustomTheme(self, theme);
      } else if (typeof theme === 'string') {
        if (theme === 'none') {
          theme = 'white';
        }
        var themeDetails = jsPanel.getThemeDetails(theme);
        jsPanel.applyColorTheme(self, themeDetails);
      }
      // minimize again if panel was minimized prior theme change
      if (minimized) {
        self.minimize();
      }
      if (cb) {
        cb.call(self, self);
      }
      return self;
    };
    self.remove = function (id, closedBy, cb) {
      // self.remove() is just a helper func used in self.close()
      self.parentElement.removeChild(self);
      if (!document.getElementById(id)) {
        self.removeMinimizedReplacement();
        self.status = 'closed';
        if (closedBy) {
          document.dispatchEvent(jspanelcloseduser);
        }
        document.dispatchEvent(jspanelclosed);
        if (self.options.onclosed) {
          jsPanel.processCallbacks(self, self.options.onclosed, 'every', closedBy);
        }
        jsPanel.autopositionRemaining(self);
        if (cb) {
          cb.call(id, id);
        }
      } else {
        if (cb) {
          cb.call(self, id, self);
        }
      }
      window.removeEventListener('resize', self.windowResizeHandler);
      document.removeEventListener('jspanelresize', self.parentResizeHandler);
    };
    self.close = function (cb, closedByUser) {
      // if panel does not exist return
      if (!self.parentElement) {
        return;
      }
      if (self.closetimer) {
        window.clearInterval(self.closetimer);
      }
      document.dispatchEvent(jspanelbeforeclose);
      self.statusBefore = self.status;
      if (self.options.onbeforeclose && self.options.onbeforeclose.length > 0 && !jsPanel.processCallbacks(self, self.options.onbeforeclose, 'some', self.status, closedByUser)) {
        return self;
      }
      if (self.options.animateOut) {
        if (self.options.animateIn) {
          jsPanel.remClass(self, self.options.animateIn);
        }
        jsPanel.setClass(self, self.options.animateOut);
        self.addEventListener('animationend', function (e) {
          e.stopPropagation();
          self.remove(self.id, closedByUser, cb);
        });
      } else {
        self.remove(self.id, closedByUser, cb);
      }
    };
    self.maximize = function (cb, donotfront) {
      // Note: do not disable maximize method for already maximized panels -> onContainerResize wouldn't work
      self.statusBefore = self.status;
      if (options.onbeforemaximize && options.onbeforemaximize.length > 0 && !jsPanel.processCallbacks(self, options.onbeforemaximize, 'some', self.statusBefore)) {
        return self;
      }
      document.dispatchEvent(jspanelbeforemaximize);
      var parent = self.parentElement,
        margins = jsPanel.pOcontainment(options.maximizedMargin); // normalize maximizedMargin

      if (parent === document.body) {
        // maximize within window
        /*
           When clientHeight is used on the root element (the <html> element), (or on <body> if the document is in quirks mode),
           the viewport's height (excluding any scrollbar) is returned. This is a special case of clientHeight.
           See https://developer.mozilla.org/en-US/docs/Web/API/Element/clientHeight
           document.documentElement in the code below returns the <html> element
        */
        self.style.width = document.documentElement.clientWidth - margins[1] - margins[3] + 'px';
        self.style.height = document.documentElement.clientHeight - margins[0] - margins[2] + 'px';
        self.style.left = margins[3] + 'px';
        self.style.top = margins[0] + 'px';
      } else {
        // maximize within parentElement
        self.style.width = parent.clientWidth - margins[1] - margins[3] + 'px';
        self.style.height = parent.clientHeight - margins[0] - margins[2] + 'px';
        self.style.left = margins[3] + 'px';
        self.style.top = margins[0] + 'px';
      }
      smallBtn.style.transform = 'unset';
      self.removeMinimizedReplacement();
      self.status = 'maximized';
      self.setControls(['.jsPanel-btn-maximize']);
      if (!donotfront) {
        self.front();
      }
      document.dispatchEvent(jspanelmaximized);
      document.dispatchEvent(jspanelstatuschange);
      if (options.onstatuschange) {
        jsPanel.processCallbacks(self, options.onstatuschange, 'every', self.statusBefore);
      }
      if (cb) {
        cb.call(self, self, self.statusBefore);
      }
      if (options.onmaximized) {
        jsPanel.processCallbacks(self, options.onmaximized, 'every', self.statusBefore);
      }
      return self;
    };
    self.minimize = function (cb) {
      if (self.status === 'minimized') {
        return self;
      }
      self.statusBefore = self.status;
      if (options.onbeforeminimize && options.onbeforeminimize.length > 0 && !jsPanel.processCallbacks(self, options.onbeforeminimize, 'some', self.statusBefore)) {
        return self;
      }
      document.dispatchEvent(jspanelbeforeminimize);

      // create container for minimized replacements if not already there
      if (!document.getElementById('jsPanel-replacement-container')) {
        var replacementContainer = document.createElement('div');
        replacementContainer.id = 'jsPanel-replacement-container';
        document.body.append(replacementContainer);
      }
      self.style.left = '-9999px';
      self.status = 'minimized';
      document.dispatchEvent(jspanelminimized);
      document.dispatchEvent(jspanelstatuschange);
      if (options.onstatuschange) {
        jsPanel.processCallbacks(self, options.onstatuschange, 'every', self.statusBefore);
      }
      if (options.minimizeTo) {
        var replacement = self.createMinimizedReplacement(),
          container,
          parent,
          list;
        switch (options.minimizeTo) {
          case 'default':
            document.getElementById('jsPanel-replacement-container').append(replacement);
            break;
          case 'parentpanel':
            parent = self.closest('.jsPanel-content').parentElement;
            list = parent.querySelectorAll('.jsPanel-minimized-box');
            container = list[list.length - 1];
            container.append(replacement);
            break;
          case 'parent':
            parent = self.parentElement;
            container = parent.querySelector('.jsPanel-minimized-container');
            if (!container) {
              container = document.createElement('div');
              container.className = 'jsPanel-minimized-container';
              parent.append(container);
            }
            container.append(replacement);
            break;
          default:
            // all other strings are assumed to be selector strings returning a single element to append the min replacement to
            document.querySelector(options.minimizeTo).append(replacement);
        }
      }
      if (cb) {
        cb.call(self, self, self.statusBefore);
      }
      if (options.onminimized) {
        jsPanel.processCallbacks(self, options.onminimized, 'every', self.statusBefore);
      }
      return self;
    };
    self.normalize = function (cb) {
      if (self.status === 'normalized') {
        return self;
      }
      self.statusBefore = self.status;
      // ensure smallify/unsmallify transition is turned off when resizing begins
      //self.style.transition = 'unset';

      if (options.onbeforenormalize && options.onbeforenormalize.length > 0 && !jsPanel.processCallbacks(self, options.onbeforenormalize, 'some', self.statusBefore)) {
        return self;
      }
      document.dispatchEvent(jspanelbeforenormalize);
      self.style.width = self.currentData.width;
      self.style.height = self.currentData.height;
      if (self.snapped) {
        // if panel is snapped before minimizing restore snapped position
        self.snap(self.snapped, true);
      } else {
        self.style.left = self.currentData.left;
        self.style.top = self.currentData.top;
      }
      smallBtn.style.transform = 'unset';
      self.removeMinimizedReplacement();
      self.status = 'normalized';
      self.setControls(['.jsPanel-btn-normalize']);
      self.front();
      document.dispatchEvent(jspanelnormalized);
      document.dispatchEvent(jspanelstatuschange);
      if (options.onstatuschange) {
        jsPanel.processCallbacks(self, options.onstatuschange, 'every', self.statusBefore);
      }
      if (cb) {
        cb.call(self, self, self.statusBefore);
      }
      if (options.onnormalized) {
        jsPanel.processCallbacks(self, options.onnormalized, 'every', self.statusBefore);
      }
      return self;
    };
    self.smallify = function (cb) {
      if (self.status === 'smallified' || self.status === 'smallifiedmax') {
        return self;
      }
      self.statusBefore = self.status;
      if (options.onbeforesmallify && options.onbeforesmallify.length > 0 && !jsPanel.processCallbacks(self, options.onbeforesmallify, 'some', self.statusBefore)) {
        return self;
      }
      document.dispatchEvent(jspanelbeforesmallify);
      self.style.overflow = 'hidden';
      var selfStyles = window.getComputedStyle(self),
        selfHeaderHeight = parseFloat(window.getComputedStyle(self.headerbar).height);
      self.style.height = parseFloat(selfStyles.borderTopWidth) + parseFloat(selfStyles.borderBottomWidth) + selfHeaderHeight + 'px';
      smallBtn.style.transform = 'rotate(180deg)';
      if (self.status === 'normalized') {
        self.setControls(['.jsPanel-btn-normalize']);
        self.status = 'smallified';
        document.dispatchEvent(jspanelsmallified);
        document.dispatchEvent(jspanelstatuschange);
        if (options.onstatuschange) {
          jsPanel.processCallbacks(self, options.onstatuschange, 'every', self.statusBefore);
        }
      } else if (self.status === 'maximized') {
        self.setControls(['.jsPanel-btn-maximize']);
        self.status = 'smallifiedmax';
        document.dispatchEvent(jspanelsmallifiedmax);
        document.dispatchEvent(jspanelstatuschange);
        if (options.onstatuschange) {
          jsPanel.processCallbacks(self, options.onstatuschange, 'every', self.statusBefore);
        }
      }
      var minBoxes = self.querySelectorAll('.jsPanel-minimized-box');
      minBoxes[minBoxes.length - 1].style.display = 'none';
      if (cb) {
        cb.call(self, self, self.statusBefore);
      }
      if (options.onsmallified) {
        jsPanel.processCallbacks(self, options.onsmallified, 'every', self.statusBefore);
      }
      return self;
    };
    self.unsmallify = function (cb) {
      self.statusBefore = self.status;
      if (self.status === 'smallified' || self.status === 'smallifiedmax') {
        if (options.onbeforeunsmallify && options.onbeforeunsmallify.length > 0 && !jsPanel.processCallbacks(self, options.onbeforeunsmallify, 'some', self.statusBefore)) {
          return self;
        }
        document.dispatchEvent(jspanelbeforeunsmallify);
        self.style.overflow = 'visible';
        self.front();
        if (self.status === 'smallified') {
          self.style.height = self.currentData.height;
          self.setControls(['.jsPanel-btn-normalize']);
          self.status = 'normalized';
          document.dispatchEvent(jspanelnormalized);
          document.dispatchEvent(jspanelstatuschange);
          if (options.onstatuschange) {
            jsPanel.processCallbacks(self, options.onstatuschange, 'every', self.statusBefore);
          }
        } else if (self.status === 'smallifiedmax') {
          self.maximize();
        } else if (self.status === 'minimized') {
          self.normalize();
        }
        smallBtn.style.transform = 'rotate(0deg)';
        var minBoxes = self.querySelectorAll('.jsPanel-minimized-box');
        minBoxes[minBoxes.length - 1].style.display = 'flex';
        if (cb) {
          cb.call(self, self, self.statusBefore);
        }
        if (options.onunsmallified) {
          jsPanel.processCallbacks(self, options.onunsmallified, 'every', self.statusBefore);
        }
      }
      return self;
    };
    self.front = function (callback) {
      var execOnFrontedCallbacks = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      if (self.status === 'minimized') {
        self.statusBefore === 'maximized' ? self.maximize() : self.normalize();
      } else {
        var newArr = Array.prototype.slice.call(document.querySelectorAll('.jsPanel-standard')).map(function (panel) {
          return panel.style.zIndex;
        });
        if (Math.max.apply(Math, _toConsumableArray(newArr)) > self.style.zIndex) {
          self.style.zIndex = jsPanel.zi.next();
        }
        jsPanel.resetZi();
      }
      document.dispatchEvent(jspanelfronted);
      if (callback) {
        callback.call(self, self);
      }
      if (options.onfronted && execOnFrontedCallbacks) {
        jsPanel.processCallbacks(self, options.onfronted, 'every', self.status);
      }
      return self;
    };
    self.snap = function (pos) {
      var alreadySnapped = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      // store panel size before it snaps, only if not snapped already
      if (!alreadySnapped) {
        self.currentData.beforeSnap = {
          width: self.currentData.width,
          height: self.currentData.height
        };
      }
      // snap panel
      if (pos && typeof pos === 'function' && !alreadySnapped) {
        pos.call(self, self, self.snappableTo);
      } else if (pos !== false) {
        var offsets = [0, 0];
        if (self.options.dragit.snap.containment) {
          if (self.options.dragit.containment) {
            var containment = jsPanel.pOcontainment(self.options.dragit.containment),
              position = self.snappableTo;
            if (position.startsWith('left')) {
              offsets[0] = containment[3];
            } else if (position.startsWith('right')) {
              offsets[0] = -containment[1];
            }
            if (position.endsWith('top')) {
              offsets[1] = containment[0];
            } else if (position.endsWith('bottom')) {
              offsets[1] = -containment[2];
            }
          }
        }
        self.reposition("".concat(self.snappableTo, " ").concat(offsets[0], " ").concat(offsets[1]));
      }
      if (!alreadySnapped) {
        self.snapped = self.snappableTo;
      }
    };
    self.move = function (target, cb) {
      var overlaps = self.overlaps(target, 'paddingbox'),
        source = self.parentElement;
      target.appendChild(self);
      self.options.container = target;
      self.style.left = overlaps.left + 'px';
      self.style.top = overlaps.top + 'px';
      self.saveCurrentDimensions();
      self.saveCurrentPosition();
      self.calcSizeFactors(); // important for option.onContainerResize
      if (cb) {
        cb.call(self, self, target, source);
      }
      return self;
    };
    self.closeChildpanels = function (cb) {
      self.getChildpanels().forEach(function (item) {
        return item.close();
      });
      if (cb) {
        cb.call(self, self);
      }
      return self;
    };
    self.getChildpanels = function (cb) {
      var childpanels = self.content.querySelectorAll('.jsPanel');
      if (cb) {
        childpanels.forEach(function (panel, index, list) {
          cb.call(panel, panel, index, list);
        });
      }
      return childpanels;
    };
    self.isChildpanel = function (cb) {
      var pp = self.closest('.jsPanel-content'),
        parentpanel = pp ? pp.parentElement : null;
      if (cb) {
        cb.call(self, self, parentpanel);
      }
      // if panel is childpanel of another panel returns parentpanel, otherwise false
      return pp ? parentpanel : false;
    };
    self.contentRemove = function (cb) {
      jsPanel.emptyNode(self.content);
      if (cb) {
        cb.call(self, self);
      }
      return self;
    };
    self.createMinimizedReplacement = function () {
      var tpl = jsPanel.createMinimizedTemplate(),
        color = window.getComputedStyle(self.headertitle).color,
        selfStyles = window.getComputedStyle(self),
        font = options.iconfont,
        controlbar = tpl.querySelector('.jsPanel-controlbar');

      // if panel background is an image (that includes gradients) instead of a color value
      if (self.options.header !== 'auto-show-hide') {
        jsPanel.setStyles(tpl, {
          backgroundColor: selfStyles.backgroundColor,
          backgroundPositionX: selfStyles.backgroundPositionX,
          backgroundPositionY: selfStyles.backgroundPositionY,
          backgroundRepeat: selfStyles.backgroundRepeat,
          backgroundAttachment: selfStyles.backgroundAttachment,
          backgroundImage: selfStyles.backgroundImage,
          backgroundSize: selfStyles.backgroundSize,
          backgroundOrigin: selfStyles.backgroundOrigin,
          backgroundClip: selfStyles.backgroundClip
        });
      } else {
        tpl.style.backgroundColor = window.getComputedStyle(self.header).backgroundColor;
      }
      tpl.id = self.id + '-min';
      tpl.querySelector('.jsPanel-headerbar').replaceChild(self.headerlogo.cloneNode(true), tpl.querySelector('.jsPanel-headerlogo'));
      tpl.querySelector('.jsPanel-titlebar').replaceChild(self.headertitle.cloneNode(true), tpl.querySelector('.jsPanel-title'));
      tpl.querySelector('.jsPanel-titlebar').setAttribute('title', self.headertitle.textContent);
      tpl.querySelector('.jsPanel-title').style.color = color;
      controlbar.style.color = color;
      controlbar.querySelectorAll('button').forEach(function (btn) {
        return btn.style.color = color;
      });
      ['jsPanel-hdr-dark', 'jsPanel-hdr-light'].forEach(function (item) {
        if (self.header.classList.contains(item)) {
          tpl.querySelector('.jsPanel-hdr').classList.add(item);
        }
      });

      // set iconfont
      self.setIconfont(font, tpl);
      if (self.dataset.btnnormalize === 'enabled') {
        jsPanel.pointerup.forEach(function (evt) {
          tpl.querySelector('.jsPanel-btn-normalize').addEventListener(evt, function (e) {
            e.preventDefault();
            if (e.button && e.button > 0) {
              return false;
            }
            self.normalize();
          });
        });
      } else {
        controlbar.querySelector('.jsPanel-btn-normalize').style.display = 'none';
      }
      if (self.dataset.btnmaximize === 'enabled') {
        jsPanel.pointerup.forEach(function (evt) {
          tpl.querySelector('.jsPanel-btn-maximize').addEventListener(evt, function (e) {
            e.preventDefault();
            if (e.button && e.button > 0) {
              return false;
            }
            self.maximize();
          });
        });
      } else {
        controlbar.querySelector('.jsPanel-btn-maximize').style.display = 'none';
      }
      if (self.dataset.btnclose === 'enabled') {
        jsPanel.pointerup.forEach(function (evt) {
          tpl.querySelector('.jsPanel-btn-close').addEventListener(evt, function (e) {
            e.preventDefault();
            if (e.button && e.button > 0) {
              return false;
            }
            self.close(null, true);
          });
        });
      } else {
        controlbar.querySelector('.jsPanel-btn-close').style.display = 'none';
      }
      return tpl;
    };
    self.removeMinimizedReplacement = function () {
      var elmt = document.getElementById("".concat(self.id, "-min"));
      if (elmt) {
        elmt.parentElement.removeChild(elmt);
      }
    };
    self.drag = function () {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var dragstarted, dragElmt, opts;
      var jspaneldragstart = new CustomEvent('jspaneldragstart', {
          detail: self.id
        }),
        jspaneldrag = new CustomEvent('jspaneldrag', {
          detail: self.id
        }),
        jspaneldragstop = new CustomEvent('jspaneldragstop', {
          detail: self.id
        });

      // make panel available as event object property 'panel'
      [jspaneldragstart, jspaneldrag, jspaneldragstop].forEach(function (evt) {
        return evt.panel = self;
      });
      var camelcase = function camelcase(string) {
        // 'left-top' converted to 'snapLeftTop'
        var str = string.split('-');
        str.forEach(function (word, index) {
          str[index] = word.charAt(0).toUpperCase() + word.slice(1);
        });
        return 'snap' + str.join('');
      };
      function windowListener(e) {
        if (e.relatedTarget === null) {
          jsPanel.pointermove.forEach(function (evt) {
            document.removeEventListener(evt, dragElmt, false);
            self.style.opacity = 1;
          });
        }
      }
      // attach handler to each drag handle
      var handles = options.handles || jsPanel.defaults.dragit.handles;
      var cursor = options.cursor || jsPanel.defaults.dragit.cursor;
      function pointerUpHandlerDragit(e) {
        jsPanel.pointermove.forEach(function (e) {
          return document.removeEventListener(e, dragElmt);
        });
        jsPanel.removeSnapAreas();
        if (dragstarted) {
          self.style.opacity = 1;
          dragstarted = undefined;
          if (opts.snap) {
            switch (self.snappableTo) {
              case 'left-top':
                self.snap(opts.snap.snapLeftTop);
                break;
              case 'center-top':
                self.snap(opts.snap.snapCenterTop);
                break;
              case 'right-top':
                self.snap(opts.snap.snapRightTop);
                break;
              case 'right-center':
                self.snap(opts.snap.snapRightCenter);
                break;
              case 'right-bottom':
                self.snap(opts.snap.snapRightBottom);
                break;
              case 'center-bottom':
                self.snap(opts.snap.snapCenterBottom);
                break;
              case 'left-bottom':
                self.snap(opts.snap.snapLeftBottom);
                break;
              case 'left-center':
                self.snap(opts.snap.snapLeftCenter);
                break;
            }
            if (opts.snap.callback && self.snappableTo && typeof opts.snap.callback === 'function') {
              opts.snap.callback.call(self, self);
              if (opts.snap.repositionOnSnap && opts.snap[camelcase(self.snappableTo)] !== false) {
                self.repositionOnSnap(self.snappableTo);
              }
            }
            if (self.snappableTo && opts.snap.repositionOnSnap && opts.snap[camelcase(self.snappableTo)]) {
              self.repositionOnSnap(self.snappableTo);
            }
          }
          // opts.drop
          if (self.droppableTo && self.droppableTo) {
            var sourceContainer = self.parentElement;
            self.move(self.droppableTo);
            if (opts.drop.callback) {
              opts.drop.callback.call(self, self, self.droppableTo, sourceContainer);
            }
          }
          document.dispatchEvent(jspaneldragstop);
          if (opts.stop.length) {
            var stopStyles = window.getComputedStyle(self),
              paneldata = {
                left: parseFloat(stopStyles.left),
                top: parseFloat(stopStyles.top),
                width: parseFloat(stopStyles.width),
                height: parseFloat(stopStyles.height)
              };
            jsPanel.processCallbacks(self, opts.stop, false, paneldata, e);
          }
          self.saveCurrentPosition();
          self.calcSizeFactors(); // important for options onwindowresize/onparentresize
        }

        self.controlbar.style.pointerEvents = 'inherit';
        self.content.style.pointerEvents = 'inherit';
        // restore other panel's css pointer-events
        document.querySelectorAll('iframe').forEach(function (frame) {
          return frame.style.pointerEvents = 'auto';
        });
        document.removeEventListener(e, pointerUpHandlerDragit);
      }
      self.querySelectorAll(handles).forEach(function (handle) {
        handle.style.touchAction = 'none';
        handle.style.cursor = cursor;
        jsPanel.pointerdown.forEach(function (evt) {
          handle.addEventListener(evt, function (e) {
            // disable dragging for all mouse buttons but left
            if (e.button && e.button > 0) {
              return false;
            }

            // setup and normalize dragit options
            opts = Object.assign({}, jsPanel.defaults.dragit, options);
            if (opts.disableOnMaximized && self.status === 'maximized') {
              return false;
            }
            if (opts.containment || opts.containment === 0) {
              opts.containment = jsPanel.pOcontainment(opts.containment);
            }
            if (opts.grid) {
              if (Array.isArray(opts.grid)) {
                if (opts.grid.length === 1) {
                  opts.grid[1] = opts.grid[0];
                }
              }
            }
            if (opts.snap) {
              if (_typeof(opts.snap) === 'object') {
                opts.snap = Object.assign({}, jsPanel.defaultSnapConfig, opts.snap);
              } else {
                opts.snap = jsPanel.defaultSnapConfig;
              }
            }

            // footer elmts with the class "jsPanel-ftr-btn" don't drag a panel
            // do not compare e.target with e.currentTarget because there might be footer elmts supposed to drag the panel
            // noinspection JSUnresolvedFunction
            if (e.target.closest('.jsPanel-ftr-btn')) {
              return;
            }
            self.controlbar.style.pointerEvents = 'none';
            self.content.style.pointerEvents = 'none'; // without this code handler might not be unbound when content has iframe or object tag
            // prevents iframes in other panel from interfering with drag action of dragged panel
            document.querySelectorAll('iframe').forEach(function (frame) {
              return frame.style.pointerEvents = 'none';
            });
            var startStyles = window.getComputedStyle(self),
              startLeft = parseFloat(startStyles.left),
              startTop = parseFloat(startStyles.top),
              startWidth = parseFloat(startStyles.width),
              startHeight = parseFloat(startStyles.height),
              psx = e.touches ? e.touches[0].clientX : e.clientX,
              // pointer x on mousedown (don't use pageX, doesn't work on FF for Android)
              psy = e.touches ? e.touches[0].clientY : e.clientY,
              // same as above
              parent = self.parentElement,
              parentRect = parent.getBoundingClientRect(),
              parentStyles = window.getComputedStyle(parent),
              scaleFactor = self.getScaleFactor(),
              startLeftCorrection = 0,
              scrollbarwidths = jsPanel.getScrollbarWidth(parent);

            // function actually dragging the elmt
            dragElmt = function dragElmt(e) {
              e.preventDefault();
              if (!dragstarted) {
                document.dispatchEvent(jspaneldragstart);
                self.style.opacity = opts.opacity;
                // if configured restore panel size to a value before snap and reposition reasonable before drag actually starts
                if (self.snapped && opts.snap.resizeToPreSnap && self.currentData.beforeSnap) {
                  self.resize(self.currentData.beforeSnap.width + ' ' + self.currentData.beforeSnap.height);
                  self.setControls(['.jsPanel-btn-normalize']);
                  var intermediateStyles = self.getBoundingClientRect(),
                    delta = psx - (intermediateStyles.left + intermediateStyles.width),
                    wHalf = intermediateStyles.width / 2;
                  if (delta > -wHalf) {
                    startLeftCorrection = delta + wHalf;
                  }
                }
                self.front();
                self.snapped = false;

                // panel is maximized on dragstart
                if (self.status === 'maximized') {
                  self.setControls(['.jsPanel-btn-normalize']);
                  self.status = 'normalized';
                }

                // opts.drop
                if (opts.drop && opts.drop.dropZones) {
                  //opts.drop.dropZones = opts.drop.dropZones.map(zone => jsPanel.pOcontainer(zone));
                  var dropzones = opts.drop.dropZones.map(function (zone) {
                    return jsPanel.pOcontainer(zone);
                  }); // -> array where each item is a NodeList
                  var dropzonelist = [];
                  dropzones.forEach(function (nodelist) {
                    if (nodelist.length) {
                      // an element node does not have a length property
                      nodelist.forEach(function (node) {
                        dropzonelist.push(node);
                      });
                    } else {
                      dropzonelist.push(nodelist);
                    }
                  });
                  // filter list to have only unique values
                  dropzonelist = dropzonelist.filter(function (value, index, self) {
                    return self.indexOf(value) === index;
                  });
                  opts.drop.dropZones = dropzonelist;
                }

                // dragstart callback
                if (opts.start.length) {
                  jsPanel.processCallbacks(self, opts.start, false, {
                    left: startLeft,
                    top: startTop,
                    width: startWidth,
                    height: startHeight
                  }, e);
                }
              }
              dragstarted = 1;
              var elmtL, elmtL2, elmtT, elmtT2, elmtR, elmtR2, elmtB, elmtB2, right, bottom;
              var pmx = e.touches ? e.touches[0].clientX : e.clientX,
                // current pointer x while pointer moves (don't use pageX, doesn't work on FF for Android)
                pmy = e.touches ? e.touches[0].clientY : e.clientY,
                // current pointer y while pointer moves (don't use pageY, doesn't work on FF for Android)
                dragStyles = window.getComputedStyle(self),
                // get current styles while dragging
                overlaps;

              // EDGE reports "auto" instead of pixel value using getComputedStyle(), so some values need to be calculated different
              // this whole block of code could be removed if EDGE not based on Chromium doesn't need to be supported
              if (parent === document.body) {
                var elmtRect = self.getBoundingClientRect();
                right = window.innerWidth - parseInt(parentStyles.borderLeftWidth, 10) - parseInt(parentStyles.borderRightWidth, 10) - (elmtRect.left + elmtRect.width);
                bottom = window.innerHeight - parseInt(parentStyles.borderTopWidth, 10) - parseInt(parentStyles.borderBottomWidth, 10) - (elmtRect.top + elmtRect.height);
              } else {
                right = parseInt(parentStyles.width, 10) - parseInt(parentStyles.borderLeftWidth, 10) - parseInt(parentStyles.borderRightWidth, 10) - (parseInt(dragStyles.left, 10) + parseInt(dragStyles.width, 10));
                bottom = parseInt(parentStyles.height, 10) - parseInt(parentStyles.borderTopWidth, 10) - parseInt(parentStyles.borderBottomWidth, 10) - (parseInt(dragStyles.top, 10) + parseInt(dragStyles.height, 10));
              }
              // -- -- --

              elmtL = parseFloat(dragStyles.left);
              elmtT = parseFloat(dragStyles.top);
              elmtR = right; // replace line with parseFloat(dragStyles.right); if EDGE code block above is removed
              elmtB = bottom; // replace line with parseFloat(dragStyles.bottom); if EDGE code block above is removed

              if (opts.snap) {
                if (opts.snap.trigger === 'panel') {
                  elmtL2 = Math.pow(elmtL, 2);
                  elmtT2 = Math.pow(elmtT, 2);
                  elmtR2 = Math.pow(elmtR, 2);
                  elmtB2 = Math.pow(elmtB, 2);
                } else if (opts.snap.trigger === 'pointer') {
                  if (self.options.container === 'window') {
                    elmtL = pmx;
                    elmtT = pmy;
                    elmtR = window.innerWidth - pmx;
                    elmtB = window.innerHeight - pmy;
                    elmtL2 = Math.pow(pmx, 2);
                    elmtT2 = Math.pow(elmtT, 2);
                    elmtR2 = Math.pow(elmtR, 2);
                    elmtB2 = Math.pow(elmtB, 2);
                  } else {
                    overlaps = self.overlaps(parent, 'paddingbox', e);
                    elmtL = overlaps.pointer.left;
                    elmtT = overlaps.pointer.top;
                    elmtR = overlaps.pointer.right;
                    elmtB = overlaps.pointer.bottom;
                    elmtL2 = Math.pow(overlaps.pointer.left, 2);
                    elmtT2 = Math.pow(overlaps.pointer.top, 2);
                    elmtR2 = Math.pow(overlaps.pointer.right, 2);
                    elmtB2 = Math.pow(overlaps.pointer.bottom, 2);
                  }
                }
              }
              var lefttopVectorDrag = Math.sqrt(elmtL2 + elmtT2),
                leftbottomVectorDrag = Math.sqrt(elmtL2 + elmtB2),
                righttopVectorDrag = Math.sqrt(elmtR2 + elmtT2),
                rightbottomVectorDrag = Math.sqrt(elmtR2 + elmtB2),
                horizontalDeltaDrag = Math.abs(elmtL - elmtR) / 2,
                verticalDeltaDrag = Math.abs(elmtT - elmtB) / 2,
                leftVectorDrag = Math.sqrt(elmtL2 + Math.pow(verticalDeltaDrag, 2)),
                topVectorDrag = Math.sqrt(elmtT2 + Math.pow(horizontalDeltaDrag, 2)),
                rightVectorDrag = Math.sqrt(elmtR2 + Math.pow(verticalDeltaDrag, 2)),
                bottomVectorDrag = Math.sqrt(elmtB2 + Math.pow(horizontalDeltaDrag, 2));

              // prevent selections while dragging
              window.getSelection().removeAllRanges();

              // trigger drag permanently while dragging
              document.dispatchEvent(jspaneldrag);

              // move elmt and apply axis option
              if (!opts.axis || opts.axis === 'x') {
                self.style.left = startLeft + (pmx - psx) / scaleFactor.x + startLeftCorrection + 'px'; // set new css left of elmt depending on opts.axis
              }

              if (!opts.axis || opts.axis === 'y') {
                self.style.top = startTop + (pmy - psy) / scaleFactor.y + 'px'; // set new css top of elmt depending on opts.axis
              }

              // apply grid option
              if (opts.grid) {
                var grid = opts.grid,
                  axis = opts.axis;
                // formula rounds to the nearest multiple of grid
                // https://www.webveteran.com/blog/web-coding/javascript-round-to-any-multiple-of-a-specific-number/
                var x = grid[0] * Math.round((startLeft + (pmx - psx)) / grid[0]),
                  y = grid[1] * Math.round((startTop + (pmy - psy)) / grid[1]);
                if (!axis || axis === 'x') {
                  self.style.left = "".concat(x, "px");
                }
                if (!axis || axis === 'y') {
                  self.style.top = "".concat(y, "px");
                }
              }

              // apply containment option
              if (opts.containment || opts.containment === 0) {
                var containment = opts.containment;
                var maxLeft, maxTop;
                // calc maxLeft and maxTop (minLeft and MinTop is equal to containment setting)
                if (self.options.container === 'window') {
                  maxLeft = window.innerWidth - parseFloat(dragStyles.width) - containment[1] - scrollbarwidths.y;
                  maxTop = window.innerHeight - parseFloat(dragStyles.height) - containment[2] - scrollbarwidths.x;
                } else {
                  var xCorr = parseFloat(parentStyles.borderLeftWidth) + parseFloat(parentStyles.borderRightWidth),
                    yCorr = parseFloat(parentStyles.borderTopWidth) + parseFloat(parentStyles.borderBottomWidth);
                  maxLeft = parentRect.width / scaleFactor.x - parseFloat(dragStyles.width) - containment[1] - xCorr - scrollbarwidths.y;
                  maxTop = parentRect.height / scaleFactor.y - parseFloat(dragStyles.height) - containment[2] - yCorr - scrollbarwidths.x;
                }
                if (parseFloat(self.style.left) <= containment[3]) {
                  self.style.left = containment[3] + 'px';
                }
                if (parseFloat(self.style.top) <= containment[0]) {
                  self.style.top = containment[0] + 'px';
                }
                if (parseFloat(self.style.left) >= maxLeft) {
                  self.style.left = maxLeft + 'px';
                }
                if (parseFloat(self.style.top) >= maxTop) {
                  self.style.top = maxTop + 'px';
                }
              }

              // callback while dragging
              if (opts.drag.length) {
                var paneldata = {
                  left: elmtL,
                  top: elmtT,
                  right: elmtR,
                  bottom: elmtB,
                  width: parseFloat(dragStyles.width),
                  height: parseFloat(dragStyles.height)
                };
                jsPanel.processCallbacks(self, opts.drag, false, paneldata, e);
              }

              // apply snap options
              if (opts.snap) {
                var snapSens = opts.snap.sensitivity,
                  topSensAreaLength = parent === document.body ? window.innerWidth / 8 : parentRect.width / 8,
                  sideSensAreaLength = parent === document.body ? window.innerHeight / 8 : parentRect.height / 8;
                self.snappableTo = false;
                jsPanel.removeSnapAreas();
                if (lefttopVectorDrag < snapSens) {
                  if (opts.snap.snapLeftTop !== false) {
                    if (!opts.snap.active || opts.snap.active === 'both') {
                      self.snappableTo = 'left-top';
                      jsPanel.createSnapArea(self, 'lt', snapSens);
                    } else if (opts.snap.trigger === 'pointer' && opts.snap.active && opts.snap.active === 'inside') {
                      if (overlaps.pointer.left > 0 && overlaps.pointer.top > 0) {
                        self.snappableTo = 'left-top';
                        jsPanel.createSnapArea(self, 'lt', snapSens);
                      } else {
                        self.snappableTo = false;
                        jsPanel.removeSnapAreas();
                      }
                    }
                  }
                } else if (leftbottomVectorDrag < snapSens) {
                  if (opts.snap.snapLeftBottom !== false) {
                    if (!opts.snap.active || opts.snap.active === 'both') {
                      self.snappableTo = 'left-bottom';
                      jsPanel.createSnapArea(self, 'lb', snapSens);
                    } else if (opts.snap.trigger === 'pointer' && opts.snap.active && opts.snap.active === 'inside') {
                      if (overlaps.pointer.left > 0 && overlaps.pointer.bottom > 0) {
                        self.snappableTo = 'left-bottom';
                        jsPanel.createSnapArea(self, 'lb', snapSens);
                      } else {
                        self.snappableTo = false;
                        jsPanel.removeSnapAreas();
                      }
                    }
                  }
                } else if (righttopVectorDrag < snapSens) {
                  if (opts.snap.snapRightTop !== false) {
                    if (!opts.snap.active || opts.snap.active === 'both') {
                      self.snappableTo = 'right-top';
                      jsPanel.createSnapArea(self, 'rt', snapSens);
                    } else if (opts.snap.trigger === 'pointer' && opts.snap.active && opts.snap.active === 'inside') {
                      if (overlaps.pointer.right > 0 && overlaps.pointer.top > 0) {
                        self.snappableTo = 'right-top';
                        jsPanel.createSnapArea(self, 'rt', snapSens);
                      } else {
                        self.snappableTo = false;
                        jsPanel.removeSnapAreas();
                      }
                    }
                  }
                } else if (rightbottomVectorDrag < snapSens) {
                  if (opts.snap.snapRightBottom !== false) {
                    if (!opts.snap.active || opts.snap.active === 'both') {
                      self.snappableTo = 'right-bottom';
                      jsPanel.createSnapArea(self, 'rb', snapSens);
                    } else if (opts.snap.trigger === 'pointer' && opts.snap.active && opts.snap.active === 'inside') {
                      if (overlaps.pointer.right > 0 && overlaps.pointer.bottom > 0) {
                        self.snappableTo = 'right-bottom';
                        jsPanel.createSnapArea(self, 'rb', snapSens);
                      } else {
                        self.snappableTo = false;
                        jsPanel.removeSnapAreas();
                      }
                    }
                  }
                } else if (elmtT < snapSens && topVectorDrag < topSensAreaLength) {
                  if (opts.snap.snapCenterTop !== false) {
                    if (!opts.snap.active || opts.snap.active === 'both') {
                      self.snappableTo = 'center-top';
                      jsPanel.createSnapArea(self, 'ct', snapSens);
                    } else if (opts.snap.trigger === 'pointer' && opts.snap.active && opts.snap.active === 'inside') {
                      if (overlaps.pointer.top > 0) {
                        self.snappableTo = 'center-top';
                        jsPanel.createSnapArea(self, 'ct', snapSens);
                      } else {
                        self.snappableTo = false;
                        jsPanel.removeSnapAreas();
                      }
                    }
                  }
                } else if (elmtL < snapSens && leftVectorDrag < sideSensAreaLength) {
                  if (opts.snap.snapLeftCenter !== false) {
                    if (!opts.snap.active || opts.snap.active === 'both') {
                      self.snappableTo = 'left-center';
                      jsPanel.createSnapArea(self, 'lc', snapSens);
                    } else if (opts.snap.trigger === 'pointer' && opts.snap.active && opts.snap.active === 'inside') {
                      if (overlaps.pointer.left > 0) {
                        self.snappableTo = 'left-center';
                        jsPanel.createSnapArea(self, 'lc', snapSens);
                      } else {
                        self.snappableTo = false;
                        jsPanel.removeSnapAreas();
                      }
                    }
                  }
                } else if (elmtR < snapSens && rightVectorDrag < sideSensAreaLength) {
                  if (opts.snap.snapRightCenter !== false) {
                    if (!opts.snap.active || opts.snap.active === 'both') {
                      self.snappableTo = 'right-center';
                      jsPanel.createSnapArea(self, 'rc', snapSens);
                    } else if (opts.snap.trigger === 'pointer' && opts.snap.active && opts.snap.active === 'inside') {
                      if (overlaps.pointer.right > 0) {
                        self.snappableTo = 'right-center';
                        jsPanel.createSnapArea(self, 'rc', snapSens);
                      } else {
                        self.snappableTo = false;
                        jsPanel.removeSnapAreas();
                      }
                    }
                  }
                } else if (elmtB < snapSens && bottomVectorDrag < topSensAreaLength) {
                  if (opts.snap.snapCenterBottom !== false) {
                    if (!opts.snap.active || opts.snap.active === 'both') {
                      self.snappableTo = 'center-bottom';
                      jsPanel.createSnapArea(self, 'cb', snapSens);
                    } else if (opts.snap.trigger === 'pointer' && opts.snap.active && opts.snap.active === 'inside') {
                      if (overlaps.pointer.bottom > 0) {
                        self.snappableTo = 'center-bottom';
                        jsPanel.createSnapArea(self, 'cb', snapSens);
                      } else {
                        self.snappableTo = false;
                        jsPanel.removeSnapAreas();
                      }
                    }
                  }
                }
              }

              // opts.drop
              if (opts.drop && opts.drop.dropZones) {
                // IE doesn't offer document.elementsFromPoint() but document.msElementsFromPoint()
                var elementsFromPoint = jsPanel.isIE ? 'msElementsFromPoint' : 'elementsFromPoint';
                var elementsFrom = document[elementsFromPoint](e.clientX, e.clientY);
                // document.msElementsFromPoint() returns a nodeList -> convert to array
                if (!Array.isArray(elementsFrom)) {
                  elementsFrom = Array.prototype.slice.call(elementsFrom);
                }
                opts.drop.dropZones.forEach(function (zone) {
                  // Array.prototype.includes() needs polyfill in IE
                  if (elementsFrom.includes(zone)) {
                    self.droppableTo = zone;
                  }
                });
                // do not include following if statement in this.options.dragit.drop.dropZones.forEach !!!!
                if (!elementsFrom.includes(self.droppableTo)) {
                  self.droppableTo = false;
                }
              }
            };
            jsPanel.pointermove.forEach(function (e) {
              return document.addEventListener(e, dragElmt);
            });

            // remove drag handler when mouse leaves browser window (mouseleave doesn't work)
            window.addEventListener('mouseout', windowListener, false);
          });
        });
        jsPanel.pointerup.forEach(function (event) {
          document.addEventListener(event, pointerUpHandlerDragit);
          window.removeEventListener('mouseout', windowListener);
        });

        // dragit is initialized - now disable if set
        if (options.disable) {
          handle.style.pointerEvents = 'none';
        }
      });
      return self;
    };
    self.dragit = function (string) {
      var dragitOptions = Object.assign({}, jsPanel.defaults.dragit, options.dragit),
        handles = self.querySelectorAll(dragitOptions.handles);
      if (string === 'disable') {
        handles.forEach(function (handle) {
          return handle.style.pointerEvents = 'none';
        });
      } else {
        handles.forEach(function (handle) {
          return handle.style.pointerEvents = 'auto';
        });
      }
      return self;
    };
    self.sizeit = function () {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var jspanelresizestart = new CustomEvent('jspanelresizestart', {
          detail: self.id
        }),
        jspanelresize = new CustomEvent('jspanelresize', {
          detail: self.id
        }),
        jspanelresizestop = new CustomEvent('jspanelresizestop', {
          detail: self.id
        });
      // make panel available as event object property 'panel'
      [jspanelresizestart, jspanelresize, jspanelresizestop].forEach(function (evt) {
        return evt.panel = self;
      });
      var opts = {},
        resizePanel,
        resizestarted,
        w,
        h,
        startWidth,
        startHeight;
      opts.handles = options.handles || jsPanel.defaults.resizeit.handles;
      opts.handles.split(',').forEach(function (item) {
        var node = document.createElement('DIV');
        node.className = "jsPanel-resizeit-handle jsPanel-resizeit-".concat(item.trim());
        //node.style.zIndex = 90;
        self.append(node);
      });
      // cache option aspectRatio of original resizeit configuration (is restored on pointerup)
      var cachedOptionAspectRatio = options.aspectRatio ? options.aspectRatio : false;
      function windowListener(e) {
        if (e.relatedTarget === null) {
          jsPanel.pointermove.forEach(function (evt) {
            return document.removeEventListener(evt, resizePanel, false);
          });
        }
      }
      function pointerUpHandlerResizeit(e) {
        jsPanel.pointermove.forEach(function (evt) {
          return document.removeEventListener(evt, resizePanel, false);
        });
        if (e.target.classList && e.target.classList.contains('jsPanel-resizeit-handle')) {
          var isLeftChange,
            isTopChange,
            cl = e.target.className;
          if (cl.match(/jsPanel-resizeit-nw|jsPanel-resizeit-w|jsPanel-resizeit-sw/i)) {
            isLeftChange = true;
          }
          if (cl.match(/jsPanel-resizeit-nw|jsPanel-resizeit-n|jsPanel-resizeit-ne/i)) {
            isTopChange = true;
          }

          // snap panel to grid (doesn't work that well if inside function resizePanel)
          if (opts.grid && Array.isArray(opts.grid)) {
            if (opts.grid.length === 1) {
              opts.grid[1] = opts.grid[0];
            }
            var cw = parseFloat(self.style.width),
              ch = parseFloat(self.style.height),
              modW = cw % opts.grid[0],
              modH = ch % opts.grid[1],
              cx = parseFloat(self.style.left),
              cy = parseFloat(self.style.top),
              modX = cx % opts.grid[0],
              modY = cy % opts.grid[1];
            if (modW < opts.grid[0] / 2) {
              self.style.width = cw - modW + 'px';
            } else {
              self.style.width = cw + (opts.grid[0] - modW) + 'px';
            }
            if (modH < opts.grid[1] / 2) {
              self.style.height = ch - modH + 'px';
            } else {
              self.style.height = ch + (opts.grid[1] - modH) + 'px';
            }
            if (isLeftChange) {
              if (modX < opts.grid[0] / 2) {
                self.style.left = cx - modX + 'px';
              } else {
                self.style.left = cx + (opts.grid[0] - modX) + 'px';
              }
            }
            if (isTopChange) {
              if (modY < opts.grid[1] / 2) {
                self.style.top = cy - modY + 'px';
              } else {
                self.style.top = cy + (opts.grid[1] - modY) + 'px';
              }
            }
          }
        }
        if (resizestarted) {
          self.content.style.pointerEvents = 'inherit';
          resizestarted = undefined;
          self.saveCurrentDimensions();
          self.saveCurrentPosition();
          self.calcSizeFactors();
          var smallifyBtn = self.controlbar.querySelector('.jsPanel-btn-smallify');
          var elmtRect = self.getBoundingClientRect();
          if (smallifyBtn && elmtRect.height > startHeight + 5) {
            smallifyBtn.style.transform = 'rotate(0deg)';
          }
          document.dispatchEvent(jspanelresizestop);
          if (opts.stop.length) {
            var stopStyles = window.getComputedStyle(self),
              paneldata = {
                left: parseFloat(stopStyles.left),
                top: parseFloat(stopStyles.top),
                width: parseFloat(stopStyles.width),
                height: parseFloat(stopStyles.height)
              };
            jsPanel.processCallbacks(self, opts.stop, false, paneldata, e);
          }
        }
        self.content.style.pointerEvents = 'inherit';
        // restore other panel's css pointer-events
        document.querySelectorAll('iframe').forEach(function (frame) {
          return frame.style.pointerEvents = 'auto';
        });
        // restore option aspectRatio to original configuration
        opts.aspectRatio = cachedOptionAspectRatio;
        document.removeEventListener(e, pointerUpHandlerResizeit);
      }
      self.querySelectorAll('.jsPanel-resizeit-handle').forEach(function (handle) {
        handle.style.touchAction = 'none';
        jsPanel.pointerdown.forEach(function (event) {
          handle.addEventListener(event, function (e) {
            // prevent window scroll while resizing elmt
            e.preventDefault();
            e.stopPropagation();
            // disable resizing for all mouse buttons but left
            if (e.button && e.button > 0) {
              return false;
            }
            // factor is needed only for the modifier key Shift feature
            var factor = 1;
            // setup and normalize resizeit options
            opts = Object.assign({}, jsPanel.defaults.resizeit, options);
            if (opts.containment || opts.containment === 0) {
              opts.containment = jsPanel.pOcontainment(opts.containment);
            }
            // legacy line: aspectRatio should be either 'panel' or 'content', not just true
            if (opts.aspectRatio && opts.aspectRatio === true) {
              opts.aspectRatio = 'panel';
            }
            // set aspectRatio according to modifier key
            if (jsPanel.modifier) {
              var modifier = jsPanel.modifier;
              if (modifier.altKey) {
                opts.aspectRatio = 'content';
              } else if (modifier.ctrlKey) {
                opts.aspectRatio = 'panel';
              } else if (modifier.shiftKey) {
                opts.aspectRatio = false;
                factor = 2; // does work only with 2 as value
              }
            }

            // noinspection JSUnresolvedFunction
            var maxWidth = typeof opts.maxWidth === 'function' ? opts.maxWidth() : opts.maxWidth || 10000,
              maxHeight = typeof opts.maxHeight === 'function' ? opts.maxHeight() : opts.maxHeight || 10000,
              minWidth = typeof opts.minWidth === 'function' ? opts.minWidth() : opts.minWidth,
              minHeight = typeof opts.minHeight === 'function' ? opts.minHeight() : opts.minHeight;
            self.content.style.pointerEvents = 'none';
            // prevents iframes in other panel from interfering with resize action of dragged panel
            document.querySelectorAll('iframe').forEach(function (frame) {
              return frame.style.pointerEvents = 'none';
            });

            // noinspection JSUnresolvedVariable
            var elmtParent = self.parentElement,
              elmtParentTagName = elmtParent.tagName.toLowerCase(),
              elmtRect = self.getBoundingClientRect(),
              elmtParentRect = elmtParent.getBoundingClientRect(),
              elmtParentStyles = window.getComputedStyle(elmtParent, null),
              elmtParentBLW = parseInt(elmtParentStyles.borderLeftWidth, 10),
              elmtParentBTW = parseInt(elmtParentStyles.borderTopWidth, 10),
              elmtParentPosition = elmtParentStyles.getPropertyValue('position'),
              startX = e.clientX || e.clientX === 0 || e.touches[0].clientX,
              startY = e.clientY || e.clientY === 0 || e.touches[0].clientY,
              startRatio = startX / startY,
              resizeHandleClassList = e.target.classList,
              scaleFactor = self.getScaleFactor(),
              aspectRatio = elmtRect.width / elmtRect.height,
              elmtContentRect = self.content.getBoundingClientRect(),
              aspectRatioContent = elmtContentRect.width / elmtContentRect.height,
              hdrHeight = self.header.getBoundingClientRect().height,
              // needed in aspectRatio
              ftrHeight = self.footer.getBoundingClientRect().height || 0; // needed in aspectRatio
            var startLeft = elmtRect.left,
              startTop = elmtRect.top,
              maxWidthEast = 10000,
              maxWidthWest = 10000,
              maxHeightSouth = 10000,
              maxHeightNorth = 10000;
            startWidth = elmtRect.width;
            startHeight = elmtRect.height;
            if (elmtParentTagName !== 'body') {
              startLeft = elmtRect.left - elmtParentRect.left + elmtParent.scrollLeft;
              startTop = elmtRect.top - elmtParentRect.top + elmtParent.scrollTop;
            }

            // calc min/max left/top values if containment is set - code from jsDraggable
            if (elmtParentTagName === 'body' && opts.containment) {
              maxWidthEast = document.documentElement.clientWidth - elmtRect.left;
              maxHeightSouth = document.documentElement.clientHeight - elmtRect.top;
              maxWidthWest = elmtRect.width + elmtRect.left;
              maxHeightNorth = elmtRect.height + elmtRect.top;
            } else {
              // if panel is NOT in body
              if (opts.containment) {
                if (elmtParentPosition === 'static') {
                  maxWidthEast = elmtParentRect.width - elmtRect.left + elmtParentBLW;
                  maxHeightSouth = elmtParentRect.height + elmtParentRect.top - elmtRect.top + elmtParentBTW;
                  maxWidthWest = elmtRect.width + (elmtRect.left - elmtParentRect.left) - elmtParentBLW;
                  maxHeightNorth = elmtRect.height + (elmtRect.top - elmtParentRect.top) - elmtParentBTW;
                } else {
                  maxWidthEast = elmtParent.clientWidth - (elmtRect.left - elmtParentRect.left) / scaleFactor.x + elmtParentBLW;
                  maxHeightSouth = elmtParent.clientHeight - (elmtRect.top - elmtParentRect.top) / scaleFactor.y + elmtParentBTW;
                  maxWidthWest = (elmtRect.width + elmtRect.left - elmtParentRect.left) / scaleFactor.x - elmtParentBLW;
                  maxHeightNorth = self.clientHeight + (elmtRect.top - elmtParentRect.top) / scaleFactor.y - elmtParentBTW;
                }
              }
            }
            // if original opts.containment is an array
            if (opts.containment) {
              maxWidthWest -= opts.containment[3];
              maxHeightNorth -= opts.containment[0];
              maxWidthEast -= opts.containment[1];
              maxHeightSouth -= opts.containment[2];
            }

            // calculate corrections for rotated panels
            var computedStyle = window.getComputedStyle(self),
              wDif = parseFloat(computedStyle.width) - elmtRect.width,
              hDif = parseFloat(computedStyle.height) - elmtRect.height;
            var xDif = parseFloat(computedStyle.left) - elmtRect.left,
              yDif = parseFloat(computedStyle.top) - elmtRect.top;
            if (elmtParent !== document.body) {
              xDif += elmtParentRect.left;
              yDif += elmtParentRect.top;
            }
            // used in aspectRatio code
            var borderTopWidth = parseInt(computedStyle.borderTopWidth, 10),
              borderRightWidth = parseInt(computedStyle.borderRightWidth, 10),
              borderBottomWidth = parseInt(computedStyle.borderBottomWidth, 10),
              borderLeftWidth = parseInt(computedStyle.borderLeftWidth, 10);
            resizePanel = function resizePanel(evt) {
              evt.preventDefault();
              // trigger resizestarted only once per resize
              if (!resizestarted) {
                document.dispatchEvent(jspanelresizestart);
                if (opts.start.length) {
                  jsPanel.processCallbacks(self, opts.start, false, {
                    width: startWidth,
                    height: startHeight,
                    left: startLeft,
                    top: startTop
                  }, evt);
                }
                self.front();

                // if panel is maximized on resize start set status to normalized and swap maximize/normalize buttons
                if (self.status === 'maximized') {
                  self.status = 'normalized';
                  if (self.controlbar.querySelector('.jsPanel-btn-maximize')) {
                    self.setControlStatus('maximize', 'show');
                  }
                  if (self.controlbar.querySelector('.jsPanel-btn-normalize')) {
                    self.setControlStatus('normalize', 'hide');
                  }
                }
                if (elmtRect.height > startHeight + 5) {
                  self.status = 'normalized';
                  self.setControls(['.jsPanel-btn-normalize']);
                }
              }
              resizestarted = 1;
              // trigger resize permanently while resizing
              document.dispatchEvent(jspanelresize);

              // possibly updated while resizing
              var eventX = evt.touches ? evt.touches[0].clientX : evt.clientX,
                eventY = evt.touches ? evt.touches[0].clientY : evt.clientY,
                overlaps;
              if (resizeHandleClassList.contains('jsPanel-resizeit-e')) {
                //w = startWidth + (eventX - startX) / scaleFactor.x + wDif;
                w = startWidth + (eventX - startX) * factor / scaleFactor.x + wDif; // needs left adjust, for width and height adjust factor may be either 1 (no adjust) or 2
                if (w >= maxWidthEast) {
                  w = maxWidthEast;
                }
                if (w >= maxWidth) {
                  w = maxWidth;
                }
                if (w <= minWidth) {
                  w = minWidth;
                }
                self.style.width = w + 'px';
                if (factor === 2) {
                  // factor works only with value of 2 when adjusting left or top
                  self.style.left = startLeft - (eventX - startX) + 'px';
                }
                if (opts.aspectRatio === 'content') {
                  // if aspectRatio is true and set to 'content' the panels content section maintains its aspect ratio
                  self.style.height = (w - borderRightWidth - borderLeftWidth) / aspectRatioContent + hdrHeight + ftrHeight + borderTopWidth + borderBottomWidth + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.bottom <= opts.containment[2]) {
                      self.style.height = maxHeightSouth + 'px';
                      self.style.width = maxHeightSouth * aspectRatioContent + 'px';
                    }
                  }
                } else if (opts.aspectRatio === 'panel') {
                  // otherwise the complete panel maintains its aspect ratio
                  self.style.height = w / aspectRatio + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.bottom <= opts.containment[2]) {
                      self.style.height = maxHeightSouth + 'px';
                      self.style.width = maxHeightSouth * aspectRatio + 'px';
                    }
                  }
                }
              } else if (resizeHandleClassList.contains('jsPanel-resizeit-s')) {
                //h = startHeight + (eventY - startY) / scaleFactor.y + hDif;
                h = startHeight + (eventY - startY) * factor / scaleFactor.y + hDif; // needs top adjust
                if (h >= maxHeightSouth) {
                  h = maxHeightSouth;
                }
                if (h >= maxHeight) {
                  h = maxHeight;
                }
                if (h <= minHeight) {
                  h = minHeight;
                }
                self.style.height = h + 'px';
                if (factor === 2) {
                  self.style.top = startTop - (eventY - startY) + 'px';
                }
                if (opts.aspectRatio === 'content') {
                  // if aspectRatio is true and set to 'content' the panels content section maintains its aspect ratio
                  self.style.width = (h - hdrHeight - ftrHeight - borderTopWidth - borderBottomWidth) * aspectRatioContent + borderTopWidth + borderBottomWidth + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.right <= opts.containment[1]) {
                      self.style.width = maxWidthEast + 'px';
                      self.style.height = maxWidthEast / aspectRatioContent + 'px';
                    }
                  }
                } else if (opts.aspectRatio === 'panel') {
                  // otherwise the complete panel maintains its aspect ratio
                  self.style.width = h * aspectRatio + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.right <= opts.containment[1]) {
                      self.style.width = maxWidthEast + 'px';
                      self.style.height = maxWidthEast / aspectRatio + 'px';
                    }
                  }
                }
              } else if (resizeHandleClassList.contains('jsPanel-resizeit-w')) {
                //w = startWidth + (startX - eventX) / scaleFactor.x + wDif;
                w = startWidth + (startX - eventX) * factor / scaleFactor.x + wDif; // doesn't need left adjust
                if (w <= maxWidth && w >= minWidth && w <= maxWidthWest) {
                  self.style.left = startLeft + (eventX - startX) / scaleFactor.x + xDif + 'px';
                }
                if (w >= maxWidthWest) {
                  w = maxWidthWest;
                }
                if (w >= maxWidth) {
                  w = maxWidth;
                }
                if (w <= minWidth) {
                  w = minWidth;
                }
                self.style.width = w + 'px';
                if (opts.aspectRatio === 'content') {
                  // if aspectRatio is true and set to 'content' the panels content section maintains its aspect ratio
                  self.style.height = (w - borderRightWidth - borderLeftWidth) / aspectRatioContent + hdrHeight + ftrHeight + borderTopWidth + borderBottomWidth + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.bottom <= opts.containment[2]) {
                      self.style.height = maxHeightSouth + 'px';
                      self.style.width = maxHeightSouth * aspectRatioContent + 'px';
                    }
                  }
                } else if (opts.aspectRatio === 'panel') {
                  // otherwise the complete panel maintains its aspect ratio
                  self.style.height = w / aspectRatio + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.bottom <= opts.containment[2]) {
                      self.style.height = maxHeightSouth + 'px';
                      self.style.width = maxHeightSouth * aspectRatio + 'px';
                    }
                  }
                }
              } else if (resizeHandleClassList.contains('jsPanel-resizeit-n')) {
                //h = startHeight + (startY - eventY) / scaleFactor.y + hDif;
                h = startHeight + (startY - eventY) * factor / scaleFactor.y + hDif; // doesn't need top adjust
                if (h <= maxHeight && h >= minHeight && h <= maxHeightNorth) {
                  self.style.top = startTop + (eventY - startY) / scaleFactor.y + yDif + 'px';
                }
                if (h >= maxHeightNorth) {
                  h = maxHeightNorth;
                }
                if (h >= maxHeight) {
                  h = maxHeight;
                }
                if (h <= minHeight) {
                  h = minHeight;
                }
                self.style.height = h + 'px';
                if (opts.aspectRatio === 'content') {
                  // if aspectRatio is true and set to 'content' the panels content section maintains its aspect ratio
                  self.style.width = (h - hdrHeight - ftrHeight - borderTopWidth - borderBottomWidth) * aspectRatioContent + borderTopWidth + borderBottomWidth + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.right <= opts.containment[1]) {
                      self.style.width = maxWidthEast + 'px';
                      self.style.height = maxWidthEast / aspectRatioContent + 'px';
                    }
                  }
                } else if (opts.aspectRatio === 'panel') {
                  // otherwise the complete panel maintains its aspect ratio
                  self.style.width = h * aspectRatio + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.right <= opts.containment[1]) {
                      self.style.width = maxWidthEast + 'px';
                      self.style.height = maxWidthEast / aspectRatio + 'px';
                    }
                  }
                }
              } else if (resizeHandleClassList.contains('jsPanel-resizeit-se')) {
                //w = startWidth + (eventX - startX) / scaleFactor.x + wDif;
                w = startWidth + (eventX - startX) * factor / scaleFactor.x + wDif; // needs left adjust
                if (w >= maxWidthEast) {
                  w = maxWidthEast;
                }
                if (w >= maxWidth) {
                  w = maxWidth;
                }
                if (w <= minWidth) {
                  w = minWidth;
                }
                self.style.width = w + 'px';
                if (factor === 2) {
                  self.style.left = startLeft - (eventX - startX) + 'px';
                }
                if (opts.aspectRatio) {
                  self.style.height = w / aspectRatio + 'px';
                }

                //h = startHeight + (eventY - startY) / scaleFactor.y + hDif;
                h = startHeight + (eventY - startY) * factor / scaleFactor.y + hDif; // needs top adjust
                if (h >= maxHeightSouth) {
                  h = maxHeightSouth;
                }
                if (h >= maxHeight) {
                  h = maxHeight;
                }
                if (h <= minHeight) {
                  h = minHeight;
                }
                self.style.height = h + 'px';
                if (factor === 2) {
                  self.style.top = startTop - (eventY - startY) + 'px';
                }
                if (opts.aspectRatio === 'content') {
                  // if aspectRatio is true and set to 'content' the panels content section maintains its aspect ratio
                  self.style.width = (h - hdrHeight - ftrHeight - borderTopWidth - borderBottomWidth) * aspectRatioContent + borderTopWidth + borderBottomWidth + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.right <= opts.containment[1]) {
                      self.style.width = maxWidthEast + 'px';
                      self.style.height = maxWidthEast / aspectRatioContent + 'px';
                    }
                  }
                } else if (opts.aspectRatio === 'panel') {
                  // otherwise the complete panel maintains its aspect ratio
                  self.style.width = h * aspectRatio + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.right <= opts.containment[1]) {
                      self.style.width = maxWidthEast + 'px';
                      self.style.height = maxWidthEast / aspectRatio + 'px';
                    }
                  }
                }
              } else if (resizeHandleClassList.contains('jsPanel-resizeit-sw')) {
                //h = startHeight + (eventY - startY) / scaleFactor.y + hDif;
                h = startHeight + (eventY - startY) * factor / scaleFactor.y + hDif; // needs top adjust
                if (h >= maxHeightSouth) {
                  h = maxHeightSouth;
                }
                if (h >= maxHeight) {
                  h = maxHeight;
                }
                if (h <= minHeight) {
                  h = minHeight;
                }
                self.style.height = h + 'px';
                if (factor === 2) {
                  self.style.top = startTop - (eventY - startY) + 'px';
                }
                if (opts.aspectRatio) {
                  self.style.width = h * aspectRatio + 'px';
                }

                //w = startWidth + (startX - eventX) / scaleFactor.x + wDif;
                w = startWidth + (startX - eventX) * factor / scaleFactor.x + wDif; // doesn't need left adjust
                if (w <= maxWidth && w >= minWidth && w <= maxWidthWest) {
                  self.style.left = startLeft + (eventX - startX) / scaleFactor.x + xDif + 'px';
                }
                if (w >= maxWidthWest) {
                  w = maxWidthWest;
                }
                if (w >= maxWidth) {
                  w = maxWidth;
                }
                if (w <= minWidth) {
                  w = minWidth;
                }
                self.style.width = w + 'px';
                if (opts.aspectRatio === 'content') {
                  // if aspectRatio is true and set to 'content' the panels content section maintains its aspect ratio
                  self.style.height = (w - borderRightWidth - borderLeftWidth) / aspectRatioContent + hdrHeight + ftrHeight + borderTopWidth + borderBottomWidth + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.bottom <= opts.containment[2]) {
                      self.style.height = maxHeightSouth + 'px';
                      self.style.width = maxHeightSouth * aspectRatioContent + 'px';
                    }
                  }
                } else if (opts.aspectRatio === 'panel') {
                  // otherwise the complete panel maintains its aspect ratio
                  self.style.height = w / aspectRatio + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.bottom <= opts.containment[2]) {
                      self.style.height = maxHeightSouth + 'px';
                      self.style.width = maxHeightSouth * aspectRatio + 'px';
                    }
                  }
                }
              } else if (resizeHandleClassList.contains('jsPanel-resizeit-ne')) {
                //w = startWidth + (eventX - startX) / scaleFactor.x + wDif;
                w = startWidth + (eventX - startX) * factor / scaleFactor.x + wDif; // needs left adjust
                if (w >= maxWidthEast) {
                  w = maxWidthEast;
                }
                if (w >= maxWidth) {
                  w = maxWidth;
                }
                if (w <= minWidth) {
                  w = minWidth;
                }
                self.style.width = w + 'px';
                if (factor === 2) {
                  self.style.left = startLeft - (eventX - startX) + 'px';
                }
                if (opts.aspectRatio) {
                  self.style.height = w / aspectRatio + 'px';
                }
                h = startHeight + (startY - eventY) * factor / scaleFactor.y + hDif; // doesn't need top adjust
                if (h <= maxHeight && h >= minHeight && h <= maxHeightNorth) {
                  self.style.top = startTop + (eventY - startY) / scaleFactor.y + yDif + 'px';
                }
                if (h >= maxHeightNorth) {
                  h = maxHeightNorth;
                }
                if (h >= maxHeight) {
                  h = maxHeight;
                }
                if (h <= minHeight) {
                  h = minHeight;
                }
                self.style.height = h + 'px';
                if (opts.aspectRatio === 'content') {
                  // if aspectRatio is true and set to 'content' the panels content section maintains its aspect ratio
                  self.style.width = (h - hdrHeight - ftrHeight - borderTopWidth - borderBottomWidth) * aspectRatioContent + borderTopWidth + borderBottomWidth + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.right <= opts.containment[1]) {
                      self.style.width = maxWidthEast + 'px';
                      self.style.height = maxWidthEast / aspectRatioContent + 'px';
                    }
                  }
                } else if (opts.aspectRatio === 'panel') {
                  // otherwise the complete panel maintains its aspect ratio
                  self.style.width = h * aspectRatio + 'px';
                  if (opts.containment) {
                    overlaps = self.overlaps(elmtParent);
                    if (overlaps.right <= opts.containment[1]) {
                      self.style.width = maxWidthEast + 'px';
                      self.style.height = maxWidthEast / aspectRatio + 'px';
                    }
                  }
                }
              } else if (resizeHandleClassList.contains('jsPanel-resizeit-nw')) {
                if (opts.aspectRatio && resizeHandleClassList.contains('jsPanel-resizeit-nw')) {
                  eventX = eventY * startRatio;
                  eventY = eventX / startRatio;
                }
                //w = startWidth + (startX - eventX) / scaleFactor.x + wDif;
                w = startWidth + (startX - eventX) * factor / scaleFactor.x + wDif; // doesn't need left adjust
                if (w <= maxWidth && w >= minWidth && w <= maxWidthWest) {
                  self.style.left = startLeft + (eventX - startX) / scaleFactor.x + xDif + 'px';
                }
                if (w >= maxWidthWest) {
                  w = maxWidthWest;
                }
                if (w >= maxWidth) {
                  w = maxWidth;
                }
                if (w <= minWidth) {
                  w = minWidth;
                }
                self.style.width = w + 'px';
                if (opts.aspectRatio) {
                  self.style.height = w / aspectRatio + 'px';
                }
                h = startHeight + (startY - eventY) * factor / scaleFactor.y + hDif; // doesn't need top adjust
                if (h <= maxHeight && h >= minHeight && h <= maxHeightNorth) {
                  self.style.top = startTop + (eventY - startY) / scaleFactor.y + yDif + 'px';
                }
                if (h >= maxHeightNorth) {
                  h = maxHeightNorth;
                }
                if (h >= maxHeight) {
                  h = maxHeight;
                }
                if (h <= minHeight) {
                  h = minHeight;
                }
                self.style.height = h + 'px';
                if (opts.aspectRatio === 'content') {
                  // if aspectRatio is true and set to 'content' the panels content section maintains its aspect ratio
                  self.style.width = (h - hdrHeight - ftrHeight - borderTopWidth - borderBottomWidth) * aspectRatioContent + borderTopWidth + borderBottomWidth + 'px';
                } else if (opts.aspectRatio === 'panel') {
                  // otherwise the complete panel maintains its aspect ratio
                  self.style.width = h * aspectRatio + 'px';
                }
              }
              window.getSelection().removeAllRanges();

              // get current position and size values while resizing
              var styles = window.getComputedStyle(self),
                values = {
                  left: parseFloat(styles.left),
                  top: parseFloat(styles.top),
                  right: parseFloat(styles.right),
                  bottom: parseFloat(styles.bottom),
                  width: parseFloat(styles.width),
                  height: parseFloat(styles.height)
                };

              // callback while resizing
              if (opts.resize.length) {
                jsPanel.processCallbacks(self, opts.resize, false, values, evt);
              }
            };
            jsPanel.pointermove.forEach(function (event) {
              return document.addEventListener(event, resizePanel, false);
            });

            // remove resize handler when mouse leaves browser window (mouseleave doesn't work)
            window.addEventListener('mouseout', windowListener, false);
          });
        });
        jsPanel.pointerup.forEach(function (event) {
          document.addEventListener(event, pointerUpHandlerResizeit);
          window.removeEventListener('mouseout', windowListener);
        });

        // resizeit is initialized - now disable if set
        if (options.disable) {
          handle.style.pointerEvents = 'none';
        }
      });
      return self;
    };
    self.resizeit = function (string) {
      var handles = self.querySelectorAll('.jsPanel-resizeit-handle');
      if (string === 'disable') {
        handles.forEach(function (handle) {
          return handle.style.pointerEvents = 'none';
        });
      } else {
        handles.forEach(function (handle) {
          return handle.style.pointerEvents = 'auto';
        });
      }
      return self;
    };
    self.getScaleFactor = function () {
      var rect = self.getBoundingClientRect();
      return {
        x: rect.width / self.offsetWidth,
        y: rect.height / self.offsetHeight
      };
    };
    self.calcSizeFactors = function () {
      var styles = window.getComputedStyle(self);
      if (options.container === 'window') {
        self.hf = parseFloat(styles.left) / (window.innerWidth - parseFloat(styles.width));
        self.vf = parseFloat(styles.top) / (window.innerHeight - parseFloat(styles.height));
      } else {
        if (self.parentElement) {
          var parentStyles = self.parentElement.getBoundingClientRect();
          self.hf = parseFloat(styles.left) / (parentStyles.width - parseFloat(styles.width));
          self.vf = parseFloat(styles.top) / (parentStyles.height - parseFloat(styles.height));
        }
      }
    };
    self.saveCurrentDimensions = function () {
      var normData = window.getComputedStyle(self);
      self.currentData.width = normData.width;
      self.currentData.height = normData.height;
    };
    self.saveCurrentPosition = function () {
      var normData = window.getComputedStyle(self);
      self.currentData.left = normData.left;
      self.currentData.top = normData.top;
    };
    self.reposition = function () {
      var pos = options.position,
        updateCache = true,
        callback;
      for (var _len = arguments.length, params = new Array(_len), _key = 0; _key < _len; _key++) {
        params[_key] = arguments[_key];
      }
      params.forEach(function (value) {
        if (typeof value === 'string' || _typeof(value) === 'object') {
          pos = value;
        } else if (typeof value === 'boolean') {
          updateCache = value;
        } else if (typeof value === 'function') {
          callback = value;
        }
      });
      jsPanel.position(self, pos);

      // check whether self has docked panels -> reposition docked panels as well
      if (self.slaves && self.slaves.size > 0) {
        self.slaves.forEach(function (slave) {
          return slave.reposition();
        });
      }
      if (updateCache) {
        self.saveCurrentPosition();
      }
      if (callback) {
        callback.call(self, self);
      }
      return self;
    };
    self.repositionOnSnap = function (pos) {
      var offsetX = '0',
        offsetY = '0',
        margins = jsPanel.pOcontainment(options.dragit.containment);
      // calculate offsets
      if (options.dragit.snap.containment) {
        switch (pos) {
          case 'left-top':
            offsetX = margins[3];
            offsetY = margins[0];
            break;
          case 'right-top':
            offsetX = -margins[1];
            offsetY = margins[0];
            break;
          case 'right-bottom':
            offsetX = -margins[1];
            offsetY = -margins[2];
            break;
          case 'left-bottom':
            offsetX = margins[3];
            offsetY = -margins[2];
            break;
          case 'center-top':
            offsetX = margins[3] / 2 - margins[1] / 2;
            offsetY = margins[0];
            break;
          case 'center-bottom':
            offsetX = margins[3] / 2 - margins[1] / 2;
            offsetY = -margins[2];
            break;
          case 'left-center':
            offsetX = margins[3];
            offsetY = margins[0] / 2 - margins[2] / 2;
            break;
          case 'right-center':
            offsetX = -margins[1];
            offsetY = margins[0] / 2 - margins[2] / 2;
            break;
        }
      }
      /* jsPanel.position(self, `${pos} ${offsetX} ${offsetY}`);
         For some reason I could not find the line above does not work (pos and offsets in one string), but only when
         center-bottom is used with different settings for left/right margin.
      */
      jsPanel.position(self, pos);
      jsPanel.setStyles(self, {
        left: "calc(".concat(self.style.left, " + ").concat(offsetX, "px)"),
        top: "calc(".concat(self.style.top, " + ").concat(offsetY, "px)")
      });
    };
    self.overlaps = function (reference, elmtBox, event) {
      var panelRect = self.getBoundingClientRect(),
        parentStyle = getComputedStyle(self.parentElement),
        scaleFactor = self.getScaleFactor(),
        parentBorderWidth = {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        },
        referenceRect,
        evtX = 0,
        evtY = 0,
        evtXparent = 0,
        evtYparent = 0;
      if (self.options.container !== 'window' && elmtBox === 'paddingbox') {
        parentBorderWidth.top = parseInt(parentStyle.borderTopWidth, 10) * scaleFactor.y;
        parentBorderWidth.right = parseInt(parentStyle.borderRightWidth, 10) * scaleFactor.x;
        parentBorderWidth.bottom = parseInt(parentStyle.borderBottomWidth, 10) * scaleFactor.y;
        parentBorderWidth.left = parseInt(parentStyle.borderLeftWidth, 10) * scaleFactor.x;
      }
      if (typeof reference === 'string') {
        if (reference === 'window') {
          referenceRect = {
            left: 0,
            top: 0,
            right: window.innerWidth,
            bottom: window.innerHeight
          };
        } else if (reference === 'parent') {
          referenceRect = self.parentElement.getBoundingClientRect();
        } else {
          referenceRect = document.querySelector(reference).getBoundingClientRect();
        }
      } else {
        referenceRect = reference.getBoundingClientRect();
      }
      if (event) {
        evtX = event.touches ? event.touches[0].clientX : event.clientX;
        evtY = event.touches ? event.touches[0].clientY : event.clientY;
        evtXparent = evtX - referenceRect.left;
        evtYparent = evtY - referenceRect.top;
      }
      var overlapsX = panelRect.left < referenceRect.right && panelRect.right > referenceRect.left,
        overlapsY = panelRect.top < referenceRect.bottom && panelRect.bottom > referenceRect.top,
        overlaps = overlapsX && overlapsY;
      return {
        overlaps: overlaps,
        top: panelRect.top - referenceRect.top - parentBorderWidth.top,
        right: referenceRect.right - panelRect.right - parentBorderWidth.right,
        bottom: referenceRect.bottom - panelRect.bottom - parentBorderWidth.bottom,
        left: panelRect.left - referenceRect.left - parentBorderWidth.left,
        parentBorderWidth: parentBorderWidth,
        panelRect: panelRect,
        referenceRect: referenceRect,
        pointer: {
          // pointer coords relative to window and reference
          clientX: evtX,
          clientY: evtY,
          left: evtXparent - parentBorderWidth.left,
          top: evtYparent - parentBorderWidth.top,
          right: referenceRect.width - evtXparent - parentBorderWidth.right,
          bottom: referenceRect.height - evtYparent - parentBorderWidth.bottom
        }
      };
    };
    self.setSize = function () {
      if (options.panelSize) {
        var values = jsPanel.pOsize(self, options.panelSize);
        self.style.width = values.width;
        self.style.height = values.height;
      } else if (options.contentSize) {
        var _values = jsPanel.pOsize(self, options.contentSize);
        self.content.style.width = _values.width;
        self.content.style.height = _values.height;
        self.style.width = _values.width; // explicitly assign current width/height to panel
        self.content.style.width = '100%';
      }
      return self;
    };
    self.resize = function () {
      var dimensions = window.getComputedStyle(self),
        size = {
          width: dimensions.width,
          height: dimensions.height
        },
        updateCache = true,
        callback;
      for (var _len2 = arguments.length, params = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        params[_key2] = arguments[_key2];
      }
      params.forEach(function (value) {
        if (typeof value === 'string') {
          size = value;
        } else if (_typeof(value) === 'object') {
          size = Object.assign(size, value);
        } else if (typeof value === 'boolean') {
          updateCache = value;
        } else if (typeof value === 'function') {
          callback = value;
        }
      });
      var values = jsPanel.pOsize(self, size);
      self.style.width = values.width;
      self.style.height = values.height;

      // check whether self has docked panels -> reposition docked panels
      if (self.slaves && self.slaves.size > 0) {
        self.slaves.forEach(function (slave) {
          return slave.reposition();
        });
      }
      if (updateCache) {
        self.saveCurrentDimensions();
      }
      self.status = 'normalized';
      var smallifyBtn = self.controlbar.querySelector('.jsPanel-btn-smallify');
      if (smallifyBtn) {
        smallifyBtn.style.transform = 'rotate(0deg)';
      }
      if (callback) {
        callback.call(self, self);
      }
      self.calcSizeFactors();
      return self;
    };
    self.windowResizeHandler = function (e) {
      if (e.target === window) {
        // see https://bugs.jqueryui.com/ticket/7514
        var status = self.status,
          onWindowResize = options.onwindowresize,
          left,
          top;
        if (status === 'maximized' && onWindowResize) {
          self.maximize(false, true);
        } else if (self.snapped && status !== 'minimized') {
          self.snap(self.snapped, true);
        } else if (status === 'normalized' || status === 'smallified' || status === 'maximized') {
          var settingType = _typeof(onWindowResize);
          if (settingType === 'boolean') {
            left = (window.innerWidth - self.offsetWidth) * self.hf;
            self.style.left = left <= 0 ? 0 : left + 'px';
            top = (window.innerHeight - self.offsetHeight) * self.vf;
            self.style.top = top <= 0 ? 0 : top + 'px';
          } else if (settingType === 'function') {
            onWindowResize.call(self, e, self);
          } else if (settingType === 'object') {
            // { preset: boolean, callback: function(event, panel){} }
            if (onWindowResize.preset === true) {
              left = (window.innerWidth - self.offsetWidth) * self.hf;
              self.style.left = left <= 0 ? 0 : left + 'px';
              top = (window.innerHeight - self.offsetHeight) * self.vf;
              self.style.top = top <= 0 ? 0 : top + 'px';
              onWindowResize.callback.call(self, e, self);
            } else {
              onWindowResize.callback.call(self, e, self);
            }
          }
        } else if (status === 'smallifiedmax' && onWindowResize) {
          self.maximize(false, true).smallify();
        }
        // check whether self has docked panels -> reposition docked panels as well
        if (self.slaves && self.slaves.size > 0) {
          self.slaves.forEach(function (slave) {
            return slave.reposition();
          });
        }
      }
    };
    self.setControls = function (sel, cb) {
      self.header.querySelectorAll('.jsPanel-btn').forEach(function (item) {
        var btnClass = item.className.split('-'),
          btn = btnClass[btnClass.length - 1];
        if (self.getAttribute("data-btn".concat(btn)) !== 'hidden') {
          item.style.display = 'block';
        }
      });
      sel.forEach(function (item) {
        var btn = self.controlbar.querySelector(item);
        if (btn) {
          btn.style.display = 'none';
        }
      });
      if (cb) {
        cb.call(self, self);
      }
      return self;
    };
    self.setControlStatus = function (ctrl) {
      var action = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'enable';
      var cb = arguments.length > 2 ? arguments[2] : undefined;
      var btn = self.controlbar.querySelector(".jsPanel-btn-".concat(ctrl));
      switch (action) {
        case 'disable':
          if (self.getAttribute("data-btn".concat(ctrl)) !== 'removed') {
            self.setAttribute("data-btn".concat(ctrl), 'disabled');
            btn.style.pointerEvents = 'none';
            btn.style.opacity = 0.4;
            btn.style.cursor = 'default';
          }
          break;
        case 'hide':
          if (self.getAttribute("data-btn".concat(ctrl)) !== 'removed') {
            self.setAttribute("data-btn".concat(ctrl), 'hidden');
            btn.style.display = 'none';
          }
          break;
        case 'show':
          if (self.getAttribute("data-btn".concat(ctrl)) !== 'removed') {
            self.setAttribute("data-btn".concat(ctrl), 'enabled');
            btn.style.display = 'block';
            btn.style.pointerEvents = 'auto';
            btn.style.opacity = 1;
            btn.style.cursor = 'pointer';
          }
          break;
        case 'enable':
          if (self.getAttribute("data-btn".concat(ctrl)) !== 'removed') {
            if (self.getAttribute("data-btn".concat(ctrl)) === 'hidden') {
              btn.style.display = 'block';
            }
            self.setAttribute("data-btn".concat(ctrl), 'enabled');
            btn.style.pointerEvents = 'auto';
            btn.style.opacity = 1;
            btn.style.cursor = 'pointer';
          }
          break;
        case 'remove':
          self.controlbar.removeChild(btn);
          self.setAttribute("data-btn".concat(ctrl), 'removed');
          break;
      }
      if (cb) {
        cb.call(self, self);
      }
      return self;
    };
    self.setControlSize = function (ctrlSize) {
      // does not work with Font Awesome webfont (only svg/js)
      var size = ctrlSize.toLowerCase(),
        icons = self.controlbar.querySelectorAll('.jsPanel-btn');
      icons.forEach(function (icon) {
        ['jsPanel-btn-xl', 'jsPanel-btn-lg', 'jsPanel-btn-md', 'jsPanel-btn-sm', 'jsPanel-btn-xs'].forEach(function (item) {
          return icon.classList.remove(item);
        });
        icon.classList.add("jsPanel-btn-".concat(size));
      });
      // adjust font-size of title
      if (size === 'xl') {
        self.titlebar.style.fontSize = '1.5rem';
      } else if (size === 'lg') {
        self.titlebar.style.fontSize = '1.25rem';
      } else if (size === 'md') {
        self.titlebar.style.fontSize = '1.05rem';
      } else if (size === 'sm') {
        self.titlebar.style.fontSize = '.9rem';
      } else if (size === 'xs') {
        self.titlebar.style.fontSize = '.8rem';
      }
    };
    self.setHeaderControls = function (cb) {
      // add custom controls
      if (self.options.headerControls.add) {
        var customControls = self.options.headerControls.add;
        if (!Array.isArray(customControls)) {
          // if options.addControls is not an array make it one
          customControls = [customControls];
        }
        customControls.forEach(function (ctrl) {
          return self.addControl(ctrl);
        });
      }
      // get all control names including controls added with option.addControls
      var controls = [],
        ctrls = self.controlbar.querySelectorAll('.jsPanel-btn');
      ctrls.forEach(function (ctrl) {
        var match = ctrl.className.match(/jsPanel-btn-[a-z\d]{3,}/i),
          ctrlName = match[0].substring(12);
        controls.push(ctrlName);
      });
      // normalize option headerControls and reset it accordingly
      var option = jsPanel.pOheaderControls(options.headerControls);
      options.headerControls = option;
      // set status of controls
      controls.forEach(function (item) {
        if (option[item]) {
          self.setControlStatus(item, option[item]);
        }
      });
      // set size of controls
      self.setControlSize(option.size);
      if (cb) {
        cb.call(self, self);
      }
      return self;
    };
    self.setHeaderLogo = function (logo, cb) {
      var logos = [self.headerlogo],
        minPanel = document.querySelector('#' + self.id + '-min');
      if (minPanel) {
        logos.push(minPanel.querySelector('.jsPanel-headerlogo'));
      }
      if (typeof logo === 'string') {
        if (!logo.startsWith('<')) {
          // is assumed to be an img url
          logos.forEach(function (item) {
            jsPanel.emptyNode(item);
            var img = document.createElement('img');
            img.src = logo;
            item.append(img);
          });
        } else {
          logos.forEach(function (item) {
            return item.innerHTML = logo;
          });
        }
      } else {
        // assumed to be a node object
        logos.forEach(function (item) {
          jsPanel.emptyNode(item);
          item.append(logo);
        });
      }
      // images must not be draggable, otherwise dragit interactions locks
      self.headerlogo.childNodes.forEach(function (logo) {
        if (logo.nodeName && logo.nodeName === 'IMG') {
          logo.setAttribute('draggable', 'false');
        }
      });
      if (cb) {
        cb.call(self, self);
      }
      return self;
    };
    self.setHeaderRemove = function (cb) {
      self.removeChild(self.header);
      self.content.classList.add('jsPanel-content-noheader');
      ['close', 'maximize', 'normalize', 'minimize', 'smallify'].forEach(function (item) {
        return self.setAttribute("data-btn".concat(item), 'removed');
      });
      if (cb) {
        cb.call(self, self);
      }
      return self;
    };
    self.setHeaderTitle = function (hdrTitle, cb) {
      var titles = [self.headertitle],
        minPanel = document.querySelector('#' + self.id + '-min');
      if (minPanel) {
        titles.push(minPanel.querySelector('.jsPanel-title'));
      }
      if (typeof hdrTitle === 'string') {
        titles.forEach(function (item) {
          return item.innerHTML = hdrTitle;
        });
      } else if (typeof hdrTitle === 'function') {
        titles.forEach(function (item) {
          jsPanel.emptyNode(item);
          item.innerHTML = hdrTitle();
        });
      } else {
        // assumed to be a node object
        titles.forEach(function (item) {
          jsPanel.emptyNode(item);
          item.append(hdrTitle);
        });
      }
      if (cb) {
        cb.call(self, self);
      }
      return self;
    };
    self.setIconfont = function (font) {
      var panel = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : self;
      var cb = arguments.length > 2 ? arguments[2] : undefined;
      if (font) {
        var classArray, textArray;
        if (font === 'fa' || font === 'far' || font === 'fal' || font === 'fas' || font === 'fad') {
          classArray = ["".concat(font, " fa-window-close"), "".concat(font, " fa-window-maximize"), "".concat(font, " fa-window-restore"), "".concat(font, " fa-window-minimize"), "".concat(font, " fa-chevron-up")];
        } else if (font === 'material-icons') {
          classArray = [font, font, font, font, font, font];
          textArray = ['close', 'fullscreen', 'fullscreen_exit', 'call_received', 'expand_less'];
        } else if (Array.isArray(font)) {
          classArray = ["custom-control-icon ".concat(font[4]), "custom-control-icon ".concat(font[3]), "custom-control-icon ".concat(font[2]), "custom-control-icon ".concat(font[1]), "custom-control-icon ".concat(font[0])];
        } else if (font === 'bootstrap' || font === 'glyphicon') {
          classArray = ['glyphicon glyphicon-remove', 'glyphicon glyphicon-fullscreen', 'glyphicon glyphicon-resize-full', 'glyphicon glyphicon-minus', 'glyphicon glyphicon-chevron-up'];
        } else {
          return panel;
        }
        panel.querySelectorAll('.jsPanel-controlbar .jsPanel-btn').forEach(function (item) {
          return jsPanel.emptyNode(item).innerHTML = '<span></span>';
        });
        Array.prototype.slice.call(panel.querySelectorAll('.jsPanel-controlbar .jsPanel-btn > span')).reverse().forEach(function (item, i) {
          item.className = classArray[i];
          if (font === 'material-icons') {
            item.textContent = textArray[i];
          }
        });
      }
      if (cb) {
        cb.call(panel, panel);
      }
      return panel;
    };
    self.addToolbar = function (place, tb, cb) {
      if (place === 'header') {
        place = self.headertoolbar;
      } else if (place === 'footer') {
        place = self.footer;
      }
      if (typeof tb === 'string') {
        place.innerHTML = tb;
      } else if (Array.isArray(tb)) {
        tb.forEach(function (item) {
          if (typeof item === 'string') {
            place.innerHTML += item;
          } else {
            place.append(item);
          }
        });
      } else if (typeof tb === 'function') {
        var tool = tb.call(self, self);
        if (typeof tool === 'string') {
          place.innerHTML = tool;
        } else {
          place.append(tool);
        }
      } else {
        place.append(tb);
      }
      place.classList.add('active');
      if (cb) {
        cb.call(self, self);
      }
      return self;
    };
    self.addCloseControl = function () {
      var ctrl = document.createElement('button'),
        colorContent = self.content.style.color;
      ctrl.classList.add('jsPanel-addCloseCtrl');
      ctrl.innerHTML = jsPanel.icons.close;
      ctrl.style.color = colorContent;
      if (self.options.rtl) {
        ctrl.classList.add('rtl');
      }
      self.appendChild(ctrl);
      jsPanel.pointerup.forEach(function (evt) {
        ctrl.addEventListener(evt, function (e) {
          e.preventDefault();
          // disable close for all mouse buttons but left
          if (e.button && e.button > 0) {
            return false;
          }
          self.close(null, true);
        });
      });
      // pointerdown handler needed to prevent side effect with resize handles
      jsPanel.pointerdown.forEach(function (evt) {
        ctrl.addEventListener(evt, function (e) {
          return e.preventDefault();
        });
      });
      return self;
    };
    self.addControl = function (obj) {
      // obj.name - string, the name of the control, results for example in "jsPanel-btn jsPanel-btn-menu"
      // obj.html - string, the html of the control to add, for example "<span class='fa fa-menu'></span>"
      // obj.handler - function(panel, control), the event handler to assign to the new control
      // obj.position - number, the position within the controlbar where the control is to be added
      if (!obj.html) {
        return self;
      }
      if (!obj.position) {
        obj.position = 1;
      }
      var count = self.controlbar.querySelectorAll('.jsPanel-btn').length;
      var control = document.createElement('button');
      control.innerHTML = obj.html;
      control.className = "jsPanel-btn jsPanel-btn-".concat(obj.name, " jsPanel-btn-").concat(options.headerControls.size);
      control.style.color = self.header.style.color;
      if (obj.position > count) {
        // new control is the first from right (default ltr text-direction) or the first from left (if option rtl is used)
        self.controlbar.append(control);
      } else {
        self.controlbar.insertBefore(control, self.querySelector(".jsPanel-controlbar .jsPanel-btn:nth-child(".concat(obj.position, ")")));
      }
      var ariaLabel = obj.ariaLabel || obj.name;
      if (ariaLabel) {
        control.setAttribute('aria-label', ariaLabel);
      }
      jsPanel.pointerup.forEach(function (evt) {
        control.addEventListener(evt, function (e) {
          e.preventDefault();
          if (e.button && e.button > 0) {
            return false;
          }
          obj.handler.call(self, self, control);
        });
      });
      if (obj.afterInsert) {
        obj.afterInsert.call(control, control);
      }
      return self;
    };
    self.setRtl = function () {
      [self.header, self.content, self.footer].forEach(function (item) {
        item.dir = 'rtl';
        if (options.rtl.lang) {
          item.lang = options.rtl.lang;
        }
      });
    };

    // option.id
    self.id = options.id;
    // option.paneltype classname
    self.classList.add('jsPanel-' + options.paneltype);

    // set z-index and paneltype class
    if (options.paneltype === 'standard') {
      self.style.zIndex = this.zi.next();
    }

    // option.container
    panelContainer.append(self);
    self.front(false, false); // just to ensure iframe code in self.front() works for very first panel as well, second false prevents onfronted callbacks to be executed
    // option.theme
    self.setTheme(options.theme);

    // option.boxShadow
    if (options.boxShadow) {
      self.classList.add("jsPanel-depth-".concat(options.boxShadow));
    }

    /* option.header, option.iconfont, option.headerControls, option.headerLogo, option.headerTitle */
    if (options.header) {
      if (options.headerLogo) {
        self.setHeaderLogo(options.headerLogo);
      }
      self.setIconfont(options.iconfont);
      self.setHeaderTitle(options.headerTitle);
      self.setHeaderControls(); // now handles controls added with option addControls as well

      // compatibility code for IE11 due to flex bug - https://caniuse.com/#feat=flexbox
      if (jsPanel.isIE) {
        var bars = [self.headerbar, self.controlbar];
        switch (self.options.headerControls.size) {
          case 'md':
            bars.forEach(function (bar) {
              bar.style.height = '34px';
            });
            break;
          case 'xs':
            bars.forEach(function (bar) {
              bar.style.height = '26px';
            });
            break;
          case 'sm':
            bars.forEach(function (bar) {
              bar.style.height = '30px';
            });
            break;
          case 'lg':
            bars.forEach(function (bar) {
              bar.style.height = '38px';
            });
            break;
          case 'xl':
            bars.forEach(function (bar) {
              bar.style.height = '42px';
            });
            break;
        }
      }
      // end - - - - - - - - -

      if (options.header === 'auto-show-hide') {
        var boxShadow = 'jsPanel-depth-' + options.boxShadow;
        self.header.style.opacity = 0;
        self.style.backgroundColor = 'transparent';
        this.remClass(self, boxShadow);
        this.setClass(self.content, boxShadow);
        self.header.addEventListener('mouseenter', function () {
          self.header.style.opacity = 1;
          jsPanel.setClass(self, boxShadow);
          jsPanel.remClass(self.content, boxShadow);
        });
        self.header.addEventListener('mouseleave', function () {
          self.header.style.opacity = 0;
          jsPanel.remClass(self, boxShadow);
          jsPanel.setClass(self.content, boxShadow);
        });
      }
    } else {
      self.setHeaderRemove();
      if (options.addCloseControl) {
        self.addCloseControl();
      }
    }

    // option.headerToolbar
    if (options.headerToolbar) {
      self.addToolbar(self.headertoolbar, options.headerToolbar);
    }
    // option.footerToolbar
    if (options.footerToolbar) {
      self.addToolbar(self.footer, options.footerToolbar);
    }

    // option.border
    if (options.border) {
      self.setBorder(options.border);
    }
    // option.borderRadius
    if (options.borderRadius) {
      self.setBorderRadius(options.borderRadius);
    }

    // option.css - add custom css classes to the panel html
    if (options.css) {
      for (var _i2 = 0, _Object$entries2 = Object.entries(options.css); _i2 < _Object$entries2.length; _i2++) {
        var _Object$entries2$_i = _slicedToArray(_Object$entries2[_i2], 2),
          selector = _Object$entries2$_i[0],
          classname = _Object$entries2$_i[1];
        // option is a string used to build the desired class selector like `.jsPanel-${option}` except for the outermost DIV where option must be simply 'panel'
        // value is a string with either a single class name or a space separated list of class names like 'classA classB classC'
        if (selector === 'panel') {
          // handles the special case outermost DIV of the panel
          self.className += " ".concat(classname); // don't remove space at the beginning of template string
        } else {
          // handles all other elements within the panel template
          var elmt = self.querySelector(".jsPanel-".concat(selector));
          if (elmt) {
            elmt.className += " ".concat(classname); // don't remove space at the beginning of template string
          }
        }
      }
    }

    // option.content
    if (options.content) {
      if (typeof options.content === 'function') {
        options.content.call(self, self);
      } else if (typeof options.content === 'string') {
        self.content.innerHTML = options.content;
      } else {
        self.content.append(options.content);
      }
    }

    // option.contentAjax
    if (options.contentAjax) {
      this.ajax(options.contentAjax, self);
    }

    // option.contentFetch
    if (options.contentFetch) {
      this.fetch(options.contentFetch, self);
    }

    // option.contentOverflow
    if (options.contentOverflow) {
      var value = options.contentOverflow.split(' ');
      if (value.length === 1) {
        self.content.style.overflow = value[0];
      } else if (value.length === 2) {
        self.content.style.overflowX = value[0];
        self.content.style.overflowY = value[1];
      }
    }

    // option.autoclose -- should be before option.size
    if (options.autoclose) {
      if (typeof options.autoclose === 'number') {
        options.autoclose = {
          time: options.autoclose + 'ms'
        };
      } else if (typeof options.autoclose === 'string') {
        options.autoclose = {
          time: options.autoclose
        };
      }
      var autoclose = Object.assign({}, jsPanel.defaultAutocloseConfig, options.autoclose);
      if (autoclose.time && typeof autoclose.time === 'number') {
        autoclose.time += 'ms';
      }
      var slider = self.progressbar.querySelector('div');
      slider.addEventListener('animationend', function (e) {
        e.stopPropagation();
        self.progressbar.classList.remove('active');
        self.close();
      });
      if (autoclose.progressbar) {
        self.progressbar.classList.add('active');
        if (autoclose.background) {
          if (jsPanel.colorNames[autoclose.background]) {
            self.progressbar.style.background = '#' + jsPanel.colorNames[autoclose.background];
          } else {
            self.progressbar.style.background = autoclose.background;
          }
        } else {
          self.progressbar.classList.add('success-bg'); // default background for progressbar
        }
      }

      slider.style.animation = "".concat(autoclose.time, " progressbar");
    }

    // option.rtl
    if (options.rtl) {
      self.setRtl();
    }
    // option.size -- should be after option.theme
    self.setSize();

    // option.position
    self.status = 'normalized';
    // if option.position evaluates to false panel will not be positioned at all
    if (options.position) {
      this.position(self, options.position);
    } else {
      self.style.opacity = 1;
    }
    document.dispatchEvent(jspanelnormalized);
    self.calcSizeFactors();

    // option.animateIn
    if (options.animateIn) {
      // remove class again on animationend, otherwise opacity doesn't change when panel is dragged
      self.addEventListener('animationend', function () {
        _this2.remClass(self, options.animateIn);
      });
      this.setClass(self, options.animateIn);
    }

    // option.dragit AND option.resizeit AND option.syncMargins
    if (options.syncMargins) {
      var containment = this.pOcontainment(options.maximizedMargin);
      if (options.dragit) {
        options.dragit.containment = containment;
        if (options.dragit.snap === true) {
          // options.dragit.snap must be object in order to set options.dragit.snap.containment = true;
          options.dragit.snap = jsPanel.defaultSnapConfig;
          options.dragit.snap.containment = true;
        } else if (options.dragit.snap) {
          options.dragit.snap.containment = true;
        }
      }
      if (options.resizeit) {
        options.resizeit.containment = containment;
      }
    }
    if (options.dragit) {
      // callbacks must be an array of function(s) in order to be able to dynamically add/remove callbacks (for example in extensions)
      ['start', 'drag', 'stop'].forEach(function (item) {
        if (options.dragit[item]) {
          if (typeof options.dragit[item] === 'function') {
            options.dragit[item] = [options.dragit[item]];
          }
        } else {
          options.dragit[item] = [];
        }
      });
      self.drag(options.dragit);
      // do not use self.options.dragit.stop.push() !!!
      self.addEventListener('jspaneldragstop', function (e) {
        if (e.panel === self) {
          self.calcSizeFactors();
        }
      }, false);
    } else {
      self.titlebar.style.cursor = 'default';
    }
    if (options.resizeit) {
      // callbacks must be an array of function(s) in order to be able to dynamically add/remove callbacks (for example in extensions)
      ['start', 'resize', 'stop'].forEach(function (item) {
        if (options.resizeit[item]) {
          if (typeof options.resizeit[item] === 'function') {
            options.resizeit[item] = [options.resizeit[item]];
          }
        } else {
          options.resizeit[item] = [];
        }
      });
      self.sizeit(options.resizeit);
      var startstatus = void 0;
      // do not use self.options.resizeit.start.push() !!!
      self.addEventListener('jspanelresizestart', function (e) {
        if (e.panel === self) {
          startstatus = self.status;
        }
      }, false);
      // do not use self.options.resizeit.stop.push() !!!
      self.addEventListener('jspanelresizestop', function (e) {
        if (e.panel === self) {
          if ((startstatus === 'smallified' || startstatus === 'smallifiedmax' || startstatus === 'maximized') && parseFloat(self.style.height) > parseFloat(window.getComputedStyle(self.header).height)) {
            self.setControls(['.jsPanel-btn-normalize']);
            self.status = 'normalized';
            document.dispatchEvent(jspanelnormalized);
            document.dispatchEvent(jspanelstatuschange);
            if (options.onstatuschange) {
              jsPanel.processCallbacks(self, options.onstatuschange, 'every');
            }
            self.calcSizeFactors();
          }
        }
      }, false);
    }

    // initialize self.currentData - must be after options position & size
    //self.saveCurrentDimensions(true); // not clear why 'true' was added here or why this param is needed at all in the method
    self.saveCurrentDimensions();
    self.saveCurrentPosition();

    // option.setStatus
    if (options.setStatus) {
      if (options.setStatus === 'smallifiedmax') {
        self.maximize().smallify();
      } else if (options.setStatus === 'smallified') {
        self.smallify();
      } else {
        // remove last char ('d') from end of string to get function name to call
        self[options.setStatus.slice(0, -1)]();
      }
    }

    // front panel on mousedown
    this.pointerdown.forEach(function (item) {
      self.addEventListener(item, function (e) {
        if (!e.target.closest('.jsPanel-btn-close') && !e.target.closest('.jsPanel-btn-minimize') && options.paneltype === 'standard') {
          self.front();
        }
      }, false);
    });

    // option onwindowresize
    if (options.onwindowresize) {
      // if container is 'window'
      if (self.options.container === 'window') {
        window.addEventListener('resize', self.windowResizeHandler, false);
      }
    }

    // option onparentresize
    if (options.onparentresize) {
      var onResize = options.onparentresize,
        settingType = _typeof(onResize),
        parentPanel = self.isChildpanel();
      if (parentPanel) {
        var parentContainer = parentPanel.content;
        var parentContainerSize = [];
        self.parentResizeHandler = function (e) {
          // if resized panel is the parent panel of the one whose option onContentResize is set to true
          if (e.panel === parentPanel) {
            // get dimensions of parent panel's content section
            parentContainerSize[0] = parentContainer.offsetWidth;
            parentContainerSize[1] = parentContainer.offsetHeight;
            var status = self.status,
              left,
              top;
            if (status === 'maximized' && onResize) {
              self.maximize();
            } else if (self.snapped && status !== 'minimized') {
              self.snap(self.snapped, true);
            } else if (status === 'normalized' || status === 'smallified' || status === 'maximized') {
              if (settingType === 'function') {
                onResize.call(self, self, {
                  width: parentContainerSize[0],
                  height: parentContainerSize[1]
                });
              } else if (settingType === 'object' && onResize.preset === true) {
                left = (parentContainerSize[0] - self.offsetWidth) * self.hf;
                self.style.left = left <= 0 ? 0 : left + 'px';
                top = (parentContainerSize[1] - self.offsetHeight) * self.vf;
                self.style.top = top <= 0 ? 0 : top + 'px';
                onResize.callback.call(self, self, {
                  width: parentContainerSize[0],
                  height: parentContainerSize[1]
                });
              } else if (settingType === 'boolean') {
                left = (parentContainerSize[0] - self.offsetWidth) * self.hf;
                self.style.left = left <= 0 ? 0 : left + 'px';
                top = (parentContainerSize[1] - self.offsetHeight) * self.vf;
                self.style.top = top <= 0 ? 0 : top + 'px';
              }
            } else if (status === 'smallifiedmax' && onResize) {
              self.maximize().smallify();
            }
          }
        };
        document.addEventListener('jspanelresize', self.parentResizeHandler, false);
      }
    }

    // global callbacks
    if (this.globalCallbacks) {
      if (Array.isArray(this.globalCallbacks)) {
        this.globalCallbacks.forEach(function (item) {
          return item.call(self, self);
        });
      } else {
        this.globalCallbacks.call(self, self);
      }
    }

    // option.callback
    if (options.callback) {
      if (Array.isArray(options.callback)) {
        options.callback.forEach(function (item) {
          return item.call(self, self);
        });
      } else {
        options.callback.call(self, self);
      }
    }

    // constructor callback
    // if (cb) {cb.call(self, self);}
    if (cb) {
      if (Array.isArray(cb)) {
        cb.forEach(function (item) {
          return item.call(self, self);
        });
      } else {
        cb.call(self, self);
      }
    }
    document.dispatchEvent(jspanelloaded);
    return self;
  }
};

// Add CommonJS module exports, so it can be imported using require() in Node.js
// https://nodejs.org/docs/latest/api/modules.html
if (typeof module !== 'undefined') { module.exports = jsPanel; }

;(function(window,document,Math,undefined){
  var guid = function(){
    var s4 = function(){
      return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
    };

    return 'g' + s4() + s4() + '-' + s4() + '-' + s4() + '-' +
    s4() + '-' + s4() + s4() + s4();
  };
  var extend = (function(){
    function isMergeableObject(val) {
      var nonNullObject = val && typeof val === 'object';

      return nonNullObject && Object.prototype.toString.call(val) !== '[object RegExp]' && Object.prototype.toString.call(val) !== '[object Date]';
    }

    function emptyTarget(val) {
      return Array.isArray(val) ? [] : {};
    }

    function cloneIfNecessary(value, optionsArgument) {
      var clone = optionsArgument && optionsArgument.clone === true;
      return (clone && isMergeableObject(value)) ? deepmerge(emptyTarget(value), value, optionsArgument) : value;
    }

    function defaultArrayMerge(target, source, optionsArgument) {
      var destination = target.slice();
      source.forEach(function (e, i) {
        if (typeof destination[i] === 'undefined') {
          destination[i] = cloneIfNecessary(e, optionsArgument);
        } else if (isMergeableObject(e)) {
          destination[i] = deepmerge(target[i], e, optionsArgument);
        } else if (target.indexOf(e) === -1) {
          destination.push(cloneIfNecessary(e, optionsArgument));
        }
      });
      return destination;
    }

    function mergeObject(target, source, optionsArgument) {
      var destination = optionsArgument && optionsArgument.mutate? target:{};
      if (isMergeableObject(target)) {
        Object.keys(target).forEach(function (key) {
          destination[key] = cloneIfNecessary(target[key], optionsArgument);
        });
      }
      Object.keys(source).forEach(function (key) {
        if (!isMergeableObject(source[key]) || !target[key]) {
          destination[key] = cloneIfNecessary(source[key], optionsArgument);
        } else {
          destination[key] = deepmerge(target[key], source[key], optionsArgument);
        }
      });
      return destination;
    }

    function deepmerge(target, source, optionsArgument) {
      var array = Array.isArray(source);
      var options = optionsArgument || { arrayMerge: defaultArrayMerge };
      var arrayMerge = options.arrayMerge || defaultArrayMerge;

      if (array) {
        return Array.isArray(target) ? arrayMerge(target, source, optionsArgument) : cloneIfNecessary(source, optionsArgument);
      } else {
        return mergeObject(target, source, optionsArgument);
      }
    }

    deepmerge.all = function deepmergeAll(array, optionsArgument) {
      if (!Array.isArray(array) || array.length < 2) {
        throw new Error('first argument should be an array with at least two elements');
      }

      // we are sure there are at least 2 values, so it is safe to have no initial value
      return array.reduce(function (prev, next) {
        return deepmerge(prev, next, optionsArgument);
      });
    };

    return deepmerge;
  })();
  
  var Deferred = function(){
    var def = this;
    this.promise = new Promise(function(resolve,reject){
      def.resolve = resolve;
      def.reject = reject;
    });

    this.then = this.promise.then.bind(this.promise);
    this.catch = this.promise.catch.bind(this.promise);
  };

  var defaults = {
    acceptStr:'*',
    captureType: '',
    fileParamName:'file',
    formArgs:{},
    targetURL:'',
    uploadTimeout: 10500,
    multipleSelect: false,
    isResponseInQueryString: false
  };

  var addArgsToForm = function(form,argsObj){
    var keys = Object.keys(argsObj);

    for(var i = 0; i < keys.length; i++){
      var input = document.createElement('input');
      input.setAttribute('id',keys[i]);
      input.setAttribute('name',keys[i]);
      input.setAttribute('value',argsObj[keys[i]]);
      input.setAttribute('type','hidden');
      form.appendChild(input);
    }
  };

  var cleanupIframe = function($iframe){
    $iframe.remove();
  };

  var parseQueryString = function(qs) {
    var pairs = qs.slice(1).split('&');

    var result = {};
    pairs.forEach(function(pair) {
      pair = pair.split('=');
      result[pair[0]] = decodeURIComponent(pair[1] || '');
    });

    return JSON.parse(JSON.stringify(result));
  };

  var setupForm = function(url,fileParamName,args,acceptStr,multipleSelect,captureType){
    var def = new Deferred();
    var form = document.createElement('form');
    form.setAttribute('action',url);
    form.setAttribute('method','POST');
    form.setAttribute('enctype','multipart/form-data');

    addArgsToForm(form,args);

    var fileInput = document.createElement('input');
    fileInput.setAttribute('id',fileParamName);
    fileInput.setAttribute('name',fileParamName);
    fileInput.setAttribute('type','file');
    fileInput.setAttribute('value','');
    fileInput.setAttribute('accept',acceptStr);
    fileInput.setAttribute('multiple',multipleSelect);
    if(captureType){
      fileInput.setAttribute('capture',captureType);
    }
    fileInput.addEventListener('change',function(){
      if(fileInput.value.length){
        def.resolve(fileInput.value);
      } else{
        def.reject();
      }
    });

    form.appendChild(fileInput);

    return {
      el: form,
      fileChangePromise: def.promise
    };
  };

  var setupIframe = function(isResponseInQueryString){
    var def = new Deferred();
    var iframe = document.createElement('iframe');
    iframe.setAttribute('id',guid());
    iframe.setAttribute('src','about:blank');
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    iframe.style.position = 'absolute';
    
    document.body.appendChild(iframe);

    iframe.addEventListener('load',function(){
      var frameWindow = iframe.contentWindow || iframe.documentWindow;
      var frameSrc = frameWindow.location.href;
      if(frameSrc === 'about:blank'){
        return false;
      }

      var iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

      var responseText;
      if(isResponseInQueryString && frameWindow.location.search) {
        responseText = parseQueryString(frameWindow.location.search);
      } else {
        responseText = iframeDocument.children.length?iframeDocument.children[0]:"";
      }

      if(responseText){
        def.resolve(responseText);
      } else{
        def.reject(responseText);
      }
    });

    return {
      el: iframe,
      responsePromise: def.promise
    };
  };

  window.allUpload = function(opts){
    var def = new Deferred();
    var finished = false;
    var options = extend(extend({},defaults),opts);

    var formObj = setupForm(
      options.targetURL,
      options.fileParamName,
      options.formArgs,
      options.acceptStr,
      options.multipleSelect,
      options.captureType);
    var form = formObj.el;
    var fileChangePromise = formObj.fileChangePromise;

    var iframeObj = setupIframe(options.isResponseInQueryString);
    var iframe = iframeObj.el;
    var responsePromise = iframeObj.responsePromise;

    form.querySelector('input[type="file"]').click();

    fileChangePromise
      .then(function(value){
        if(options.onfilechange && typeof options.onfilechange === 'function'){
          options.onfilechange(value);
        }
        var iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
        iframeDocument.body.appendChild(form);
        iframeDocument.querySelector('form').submit();

        //hacky IE fix to compensate for the lack of
        //iframe load event;
        setTimeout(function(){
          if(!finished){
            def.resolve();
          }
        },options.uploadTimeout);
      }).catch(function(){
        if(options.onfilechange && typeof options.onfilechange === 'function'){
          options.onfilechange(false);
        }
        cleanupIframe(iframe);
        def.reject();
      });

    responsePromise
      .then(function(resp){
        def.resolve(resp);
      }).catch(function(resp){
        console.error('> file upload error',resp);
        def.reject();
      }).then(function(){
        finished = true;
        cleanupIframe(iframe);
      });

    return def.promise;
  };
})(window,document,Math);
/*!
 * vanilla-picker v2.12.3
 * https://vanilla-picker.js.org
 *
 * Copyright 2017-2024 Andreas Borgen (https://github.com/Sphinxxxx), Adam Brooks (https://github.com/dissimulate)
 * Released under the ISC license.
 */
(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
  typeof define === 'function' && define.amd ? define(factory) :
  (global = typeof globalThis !== 'undefined' ? globalThis : global || self, global.Picker = factory());
})(this, (function () { 'use strict';

  var classCallCheck = function (instance, Constructor) {
    if (!(instance instanceof Constructor)) {
      throw new TypeError("Cannot call a class as a function");
    }
  };

  var createClass = function () {
    function defineProperties(target, props) {
      for (var i = 0; i < props.length; i++) {
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
      }
    }

    return function (Constructor, protoProps, staticProps) {
      if (protoProps) defineProperties(Constructor.prototype, protoProps);
      if (staticProps) defineProperties(Constructor, staticProps);
      return Constructor;
    };
  }();

  var slicedToArray = function () {
    function sliceIterator(arr, i) {
      var _arr = [];
      var _n = true;
      var _d = false;
      var _e = undefined;

      try {
        for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
          _arr.push(_s.value);

          if (i && _arr.length === i) break;
        }
      } catch (err) {
        _d = true;
        _e = err;
      } finally {
        try {
          if (!_n && _i["return"]) _i["return"]();
        } finally {
          if (_d) throw _e;
        }
      }

      return _arr;
    }

    return function (arr, i) {
      if (Array.isArray(arr)) {
        return arr;
      } else if (Symbol.iterator in Object(arr)) {
        return sliceIterator(arr, i);
      } else {
        throw new TypeError("Invalid attempt to destructure non-iterable instance");
      }
    };
  }();

  String.prototype.startsWith = String.prototype.startsWith || function (needle) {
      return this.indexOf(needle) === 0;
  };
  String.prototype.padStart = String.prototype.padStart || function (len, pad) {
      var str = this;while (str.length < len) {
          str = pad + str;
      }return str;
  };

  var colorNames = { cb: '0f8ff', tqw: 'aebd7', q: '-ffff', qmrn: '7fffd4', zr: '0ffff', bg: '5f5dc', bsq: 'e4c4', bck: '---', nch: 'ebcd', b: '--ff', bvt: '8a2be2', brwn: 'a52a2a', brw: 'deb887', ctb: '5f9ea0', hrt: '7fff-', chcT: 'd2691e', cr: '7f50', rnw: '6495ed', crns: '8dc', crms: 'dc143c', cn: '-ffff', Db: '--8b', Dcn: '-8b8b', Dgnr: 'b8860b', Dgr: 'a9a9a9', Dgrn: '-64-', Dkhk: 'bdb76b', Dmgn: '8b-8b', Dvgr: '556b2f', Drng: '8c-', Drch: '9932cc', Dr: '8b--', Dsmn: 'e9967a', Dsgr: '8fbc8f', DsTb: '483d8b', DsTg: '2f4f4f', Dtrq: '-ced1', Dvt: '94-d3', ppnk: '1493', pskb: '-bfff', mgr: '696969', grb: '1e90ff', rbrc: 'b22222', rwht: 'af0', stg: '228b22', chs: '-ff', gnsb: 'dcdcdc', st: '8f8ff', g: 'd7-', gnr: 'daa520', gr: '808080', grn: '-8-0', grnw: 'adff2f', hnw: '0fff0', htpn: '69b4', nnr: 'cd5c5c', ng: '4b-82', vr: '0', khk: '0e68c', vnr: 'e6e6fa', nrb: '0f5', wngr: '7cfc-', mnch: 'acd', Lb: 'add8e6', Lcr: '08080', Lcn: 'e0ffff', Lgnr: 'afad2', Lgr: 'd3d3d3', Lgrn: '90ee90', Lpnk: 'b6c1', Lsmn: 'a07a', Lsgr: '20b2aa', Lskb: '87cefa', LsTg: '778899', Lstb: 'b0c4de', Lw: 'e0', m: '-ff-', mgrn: '32cd32', nn: 'af0e6', mgnt: '-ff', mrn: '8--0', mqm: '66cdaa', mmb: '--cd', mmrc: 'ba55d3', mmpr: '9370db', msg: '3cb371', mmsT: '7b68ee', '': '-fa9a', mtr: '48d1cc', mmvt: 'c71585', mnLb: '191970', ntc: '5fffa', mstr: 'e4e1', mccs: 'e4b5', vjw: 'dead', nv: '--80', c: 'df5e6', v: '808-0', vrb: '6b8e23', rng: 'a5-', rngr: '45-', rch: 'da70d6', pgnr: 'eee8aa', pgrn: '98fb98', ptrq: 'afeeee', pvtr: 'db7093', ppwh: 'efd5', pchp: 'dab9', pr: 'cd853f', pnk: 'c0cb', pm: 'dda0dd', pwrb: 'b0e0e6', prp: '8-080', cc: '663399', r: '--', sbr: 'bc8f8f', rb: '4169e1', sbrw: '8b4513', smn: 'a8072', nbr: '4a460', sgrn: '2e8b57', ssh: '5ee', snn: 'a0522d', svr: 'c0c0c0', skb: '87ceeb', sTb: '6a5acd', sTgr: '708090', snw: 'afa', n: '-ff7f', stb: '4682b4', tn: 'd2b48c', t: '-8080', thst: 'd8bfd8', tmT: '6347', trqs: '40e0d0', vt: 'ee82ee', whT: '5deb3', wht: '', hts: '5f5f5', w: '-', wgrn: '9acd32' };

  function printNum(num) {
      var decs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;

      var str = decs > 0 ? num.toFixed(decs).replace(/0+$/, '').replace(/\.$/, '') : num.toString();
      return str || '0';
  }

  var Color = function () {
      function Color(r, g, b, a) {
          classCallCheck(this, Color);


          var that = this;
          function parseString(input) {

              if (input.startsWith('hsl')) {
                  var _input$match$map = input.match(/([\-\d\.e]+)/g).map(Number),
                      _input$match$map2 = slicedToArray(_input$match$map, 4),
                      h = _input$match$map2[0],
                      s = _input$match$map2[1],
                      l = _input$match$map2[2],
                      _a = _input$match$map2[3];

                  if (_a === undefined) {
                      _a = 1;
                  }

                  h /= 360;
                  s /= 100;
                  l /= 100;
                  that.hsla = [h, s, l, _a];
              } else if (input.startsWith('rgb')) {
                  var _input$match$map3 = input.match(/([\-\d\.e]+)/g).map(Number),
                      _input$match$map4 = slicedToArray(_input$match$map3, 4),
                      _r = _input$match$map4[0],
                      _g = _input$match$map4[1],
                      _b = _input$match$map4[2],
                      _a2 = _input$match$map4[3];

                  if (_a2 === undefined) {
                      _a2 = 1;
                  }

                  that.rgba = [_r, _g, _b, _a2];
              } else {
                  if (input.startsWith('#')) {
                      that.rgba = Color.hexToRgb(input);
                  } else {
                      that.rgba = Color.nameToRgb(input) || Color.hexToRgb(input);
                  }
              }
          }

          if (r === undefined) ; else if (Array.isArray(r)) {
              this.rgba = r;
          } else if (b === undefined) {
              var color = r && '' + r;
              if (color) {
                  parseString(color.toLowerCase());
              }
          } else {
              this.rgba = [r, g, b, a === undefined ? 1 : a];
          }
      }

      createClass(Color, [{
          key: 'printRGB',
          value: function printRGB(alpha) {
              var rgb = alpha ? this.rgba : this.rgba.slice(0, 3),
                  vals = rgb.map(function (x, i) {
                  return printNum(x, i === 3 ? 3 : 0);
              });

              return alpha ? 'rgba(' + vals + ')' : 'rgb(' + vals + ')';
          }
      }, {
          key: 'printHSL',
          value: function printHSL(alpha) {
              var mults = [360, 100, 100, 1],
                  suff = ['', '%', '%', ''];

              var hsl = alpha ? this.hsla : this.hsla.slice(0, 3),
                  vals = hsl.map(function (x, i) {
                  return printNum(x * mults[i], i === 3 ? 3 : 1) + suff[i];
              });

              return alpha ? 'hsla(' + vals + ')' : 'hsl(' + vals + ')';
          }
      }, {
          key: 'printHex',
          value: function printHex(alpha) {
              var hex = this.hex;
              return alpha ? hex : hex.substring(0, 7);
          }
      }, {
          key: 'rgba',
          get: function get() {
              if (this._rgba) {
                  return this._rgba;
              }
              if (!this._hsla) {
                  throw new Error('No color is set');
              }

              return this._rgba = Color.hslToRgb(this._hsla);
          },
          set: function set(rgb) {
              if (rgb.length === 3) {
                  rgb[3] = 1;
              }

              this._rgba = rgb;
              this._hsla = null;
          }
      }, {
          key: 'rgbString',
          get: function get() {
              return this.printRGB();
          }
      }, {
          key: 'rgbaString',
          get: function get() {
              return this.printRGB(true);
          }
      }, {
          key: 'hsla',
          get: function get() {
              if (this._hsla) {
                  return this._hsla;
              }
              if (!this._rgba) {
                  throw new Error('No color is set');
              }

              return this._hsla = Color.rgbToHsl(this._rgba);
          },
          set: function set(hsl) {
              if (hsl.length === 3) {
                  hsl[3] = 1;
              }

              this._hsla = hsl;
              this._rgba = null;
          }
      }, {
          key: 'hslString',
          get: function get() {
              return this.printHSL();
          }
      }, {
          key: 'hslaString',
          get: function get() {
              return this.printHSL(true);
          }
      }, {
          key: 'hex',
          get: function get() {
              var rgb = this.rgba,
                  hex = rgb.map(function (x, i) {
                  return i < 3 ? x.toString(16) : Math.round(x * 255).toString(16);
              });

              return '#' + hex.map(function (x) {
                  return x.padStart(2, '0');
              }).join('');
          },
          set: function set(hex) {
              this.rgba = Color.hexToRgb(hex);
          }
      }], [{
          key: 'hexToRgb',
          value: function hexToRgb(input) {

              var hex = (input.startsWith('#') ? input.slice(1) : input).replace(/^(\w{3})$/, '$1F').replace(/^(\w)(\w)(\w)(\w)$/, '$1$1$2$2$3$3$4$4').replace(/^(\w{6})$/, '$1FF');

              if (!hex.match(/^([0-9a-fA-F]{8})$/)) {
                  throw new Error('Unknown hex color; ' + input);
              }

              var rgba = hex.match(/^(\w\w)(\w\w)(\w\w)(\w\w)$/).slice(1).map(function (x) {
                  return parseInt(x, 16);
              });

              rgba[3] = rgba[3] / 255;
              return rgba;
          }
      }, {
          key: 'nameToRgb',
          value: function nameToRgb(input) {

              var hash = input.toLowerCase().replace('at', 'T').replace(/[aeiouyldf]/g, '').replace('ght', 'L').replace('rk', 'D').slice(-5, 4),
                  hex = colorNames[hash];
              return hex === undefined ? hex : Color.hexToRgb(hex.replace(/\-/g, '00').padStart(6, 'f'));
          }
      }, {
          key: 'rgbToHsl',
          value: function rgbToHsl(_ref) {
              var _ref2 = slicedToArray(_ref, 4),
                  r = _ref2[0],
                  g = _ref2[1],
                  b = _ref2[2],
                  a = _ref2[3];

              r /= 255;
              g /= 255;
              b /= 255;

              var max = Math.max(r, g, b),
                  min = Math.min(r, g, b);
              var h = void 0,
                  s = void 0,
                  l = (max + min) / 2;

              if (max === min) {
                  h = s = 0;
              } else {
                  var d = max - min;
                  s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                  switch (max) {
                      case r:
                          h = (g - b) / d + (g < b ? 6 : 0);break;
                      case g:
                          h = (b - r) / d + 2;break;
                      case b:
                          h = (r - g) / d + 4;break;
                  }

                  h /= 6;
              }

              return [h, s, l, a];
          }
      }, {
          key: 'hslToRgb',
          value: function hslToRgb(_ref3) {
              var _ref4 = slicedToArray(_ref3, 4),
                  h = _ref4[0],
                  s = _ref4[1],
                  l = _ref4[2],
                  a = _ref4[3];

              var r = void 0,
                  g = void 0,
                  b = void 0;

              if (s === 0) {
                  r = g = b = l;
              } else {
                  var hue2rgb = function hue2rgb(p, q, t) {
                      if (t < 0) t += 1;
                      if (t > 1) t -= 1;
                      if (t < 1 / 6) return p + (q - p) * 6 * t;
                      if (t < 1 / 2) return q;
                      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                      return p;
                  };

                  var q = l < 0.5 ? l * (1 + s) : l + s - l * s,
                      p = 2 * l - q;

                  r = hue2rgb(p, q, h + 1 / 3);
                  g = hue2rgb(p, q, h);
                  b = hue2rgb(p, q, h - 1 / 3);
              }

              var rgba = [r * 255, g * 255, b * 255].map(Math.round);
              rgba[3] = a;

              return rgba;
          }
      }]);
      return Color;
  }();

  var EventBucket = function () {
      function EventBucket() {
          classCallCheck(this, EventBucket);

          this._events = [];
      }

      createClass(EventBucket, [{
          key: 'add',
          value: function add(target, type, handler) {
              target.addEventListener(type, handler, false);
              this._events.push({
                  target: target,
                  type: type,
                  handler: handler
              });
          }
      }, {
          key: 'remove',
          value: function remove(target, type, handler) {
              this._events = this._events.filter(function (e) {
                  var isMatch = true;
                  if (target && target !== e.target) {
                      isMatch = false;
                  }
                  if (type && type !== e.type) {
                      isMatch = false;
                  }
                  if (handler && handler !== e.handler) {
                      isMatch = false;
                  }

                  if (isMatch) {
                      EventBucket._doRemove(e.target, e.type, e.handler);
                  }
                  return !isMatch;
              });
          }
      }, {
          key: 'destroy',
          value: function destroy() {
              this._events.forEach(function (e) {
                  return EventBucket._doRemove(e.target, e.type, e.handler);
              });
              this._events = [];
          }
      }], [{
          key: '_doRemove',
          value: function _doRemove(target, type, handler) {
              target.removeEventListener(type, handler, false);
          }
      }]);
      return EventBucket;
  }();

  function parseHTML(htmlString) {

      var div = document.createElement('div');
      div.innerHTML = htmlString;
      return div.firstElementChild;
  }

  function dragTrack(eventBucket, area, callback) {
      var dragging = false;

      function clamp(val, min, max) {
          return Math.max(min, Math.min(val, max));
      }

      function onMove(e, info, starting) {
          if (starting) {
              dragging = true;
          }
          if (!dragging) {
              return;
          }

          e.preventDefault();

          var bounds = area.getBoundingClientRect(),
              w = bounds.width,
              h = bounds.height,
              x = info.clientX,
              y = info.clientY;

          var relX = clamp(x - bounds.left, 0, w),
              relY = clamp(y - bounds.top, 0, h);

          callback(relX / w, relY / h);
      }

      function onMouse(e, starting) {
          var button = e.buttons === undefined ? e.which : e.buttons;
          if (button === 1) {
              onMove(e, e, starting);
          } else {
              dragging = false;
          }
      }

      function onTouch(e, starting) {
          if (e.touches.length === 1) {
              onMove(e, e.touches[0], starting);
          } else {
              dragging = false;
          }
      }

      eventBucket.add(area, 'mousedown', function (e) {
          onMouse(e, true);
      });
      eventBucket.add(area, 'touchstart', function (e) {
          onTouch(e, true);
      });
      eventBucket.add(window, 'mousemove', onMouse);
      eventBucket.add(area, 'touchmove', onTouch);
      eventBucket.add(window, 'mouseup', function (e) {
          dragging = false;
      });
      eventBucket.add(area, 'touchend', function (e) {
          dragging = false;
      });
      eventBucket.add(area, 'touchcancel', function (e) {
          dragging = false;
      });
  }

  var BG_TRANSP = 'linear-gradient(45deg, lightgrey 25%, transparent 25%, transparent 75%, lightgrey 75%) 0 0 / 2em 2em,\n                   linear-gradient(45deg, lightgrey 25%,       white 25%,       white 75%, lightgrey 75%) 1em 1em / 2em 2em';
  var HUES = 360;

  var EVENT_KEY = 'keydown',
      EVENT_CLICK_OUTSIDE = 'mousedown',
      EVENT_TAB_MOVE = 'focusin';

  function $(selector, context) {
      return (context || document).querySelector(selector);
  }

  function stopEvent(e) {

      e.preventDefault();
      e.stopPropagation();
  }
  function onKey(bucket, target, keys, handler, stop) {
      bucket.add(target, EVENT_KEY, function (e) {
          if (keys.indexOf(e.key) >= 0) {
              if (stop) {
                  stopEvent(e);
              }
              handler(e);
          }
      });
  }

  var Picker = function () {
      function Picker(options) {
          classCallCheck(this, Picker);


          this.settings = {

              popup: 'right',
              layout: 'default',
              alpha: true,
              editor: true,
              editorFormat: 'hex',
              cancelButton: false,
              defaultColor: '#0cf'
          };

          this._events = new EventBucket();

          this.onChange = null;

          this.onDone = null;

          this.onOpen = null;

          this.onClose = null;

          this.setOptions(options);
      }

      createClass(Picker, [{
          key: 'setOptions',
          value: function setOptions(options) {
              var _this = this;

              if (!options) {
                  return;
              }
              var settings = this.settings;

              function transfer(source, target, skipKeys) {
                  for (var key in source) {
                      if (skipKeys && skipKeys.indexOf(key) >= 0) {
                          continue;
                      }

                      target[key] = source[key];
                  }
              }

              if (options instanceof HTMLElement) {
                  settings.parent = options;
              } else {

                  if (settings.parent && options.parent && settings.parent !== options.parent) {
                      this._events.remove(settings.parent);
                      this._popupInited = false;
                  }

                  transfer(options, settings);

                  if (options.onChange) {
                      this.onChange = options.onChange;
                  }
                  if (options.onDone) {
                      this.onDone = options.onDone;
                  }
                  if (options.onOpen) {
                      this.onOpen = options.onOpen;
                  }
                  if (options.onClose) {
                      this.onClose = options.onClose;
                  }

                  var col = options.color || options.colour;
                  if (col) {
                      this._setColor(col);
                  }
              }

              var parent = settings.parent;
              if (parent && settings.popup && !this._popupInited) {

                  var openProxy = function openProxy(e) {
                      return _this.openHandler(e);
                  };

                  this._events.add(parent, 'click', openProxy);

                  onKey(this._events, parent, [' ', 'Spacebar', 'Enter'], openProxy);

                  this._popupInited = true;
              } else if (options.parent && !settings.popup) {
                  this.show();
              }
          }
      }, {
          key: 'openHandler',
          value: function openHandler(e) {
              if (this.show()) {

                  e && e.preventDefault();

                  this.settings.parent.style.pointerEvents = 'none';

                  var toFocus = e && e.type === EVENT_KEY ? this._domEdit : this.domElement;
                  setTimeout(function () {
                      return toFocus.focus();
                  }, 100);

                  if (this.onOpen) {
                      this.onOpen(this.colour);
                  }
              }
          }
      }, {
          key: 'closeHandler',
          value: function closeHandler(e) {
              var event = e && e.type;
              var doHide = false;

              if (!e) {
                  doHide = true;
              } else if (event === EVENT_CLICK_OUTSIDE || event === EVENT_TAB_MOVE) {

                  var knownTime = (this.__containedEvent || 0) + 100;
                  if (e.timeStamp > knownTime) {
                      doHide = true;
                  }
              } else {

                  stopEvent(e);

                  doHide = true;
              }

              if (doHide && this.hide()) {
                  this.settings.parent.style.pointerEvents = '';

                  if (event !== EVENT_CLICK_OUTSIDE) {
                      this.settings.parent.focus();
                  }

                  if (this.onClose) {
                      this.onClose(this.colour);
                  }
              }
          }
      }, {
          key: 'movePopup',
          value: function movePopup(options, open) {

              this.closeHandler();

              this.setOptions(options);
              if (open) {
                  this.openHandler();
              }
          }
      }, {
          key: 'setColor',
          value: function setColor(color, silent) {
              this._setColor(color, { silent: silent });
          }
      }, {
          key: '_setColor',
          value: function _setColor(color, flags) {
              if (typeof color === 'string') {
                  color = color.trim();
              }
              if (!color) {
                  return;
              }

              flags = flags || {};
              var c = void 0;
              try {

                  c = new Color(color);
              } catch (ex) {
                  if (flags.failSilently) {
                      return;
                  }
                  throw ex;
              }

              if (!this.settings.alpha) {
                  var hsla = c.hsla;
                  hsla[3] = 1;
                  c.hsla = hsla;
              }
              this.colour = this.color = c;
              this._setHSLA(null, null, null, null, flags);
          }
      }, {
          key: 'setColour',
          value: function setColour(colour, silent) {
              this.setColor(colour, silent);
          }
      }, {
          key: 'show',
          value: function show() {
              var parent = this.settings.parent;
              if (!parent) {
                  return false;
              }

              if (this.domElement) {
                  var toggled = this._toggleDOM(true);

                  this._setPosition();

                  return toggled;
              }

              var html = this.settings.template || '<div class="picker_wrapper" tabindex="-1"><div class="picker_arrow"></div><div class="picker_hue picker_slider"><div class="picker_selector"></div></div><div class="picker_sl"><div class="picker_selector"></div></div><div class="picker_alpha picker_slider"><div class="picker_selector"></div></div><div class="picker_editor"><input aria-label="Type a color name or hex value"/></div><div class="picker_sample"></div><div class="picker_done"><button>Ok</button></div><div class="picker_cancel"><button>Cancel</button></div></div>';
              var wrapper = parseHTML(html);

              this.domElement = wrapper;
              this._domH = $('.picker_hue', wrapper);
              this._domSL = $('.picker_sl', wrapper);
              this._domA = $('.picker_alpha', wrapper);
              this._domEdit = $('.picker_editor input', wrapper);
              this._domSample = $('.picker_sample', wrapper);
              this._domOkay = $('.picker_done button', wrapper);
              this._domCancel = $('.picker_cancel button', wrapper);

              wrapper.classList.add('layout_' + this.settings.layout);
              if (!this.settings.alpha) {
                  wrapper.classList.add('no_alpha');
              }
              if (!this.settings.editor) {
                  wrapper.classList.add('no_editor');
              }
              if (!this.settings.cancelButton) {
                  wrapper.classList.add('no_cancel');
              }
              this._ifPopup(function () {
                  return wrapper.classList.add('popup');
              });

              this._setPosition();

              if (this.colour) {
                  this._updateUI();
              } else {
                  this._setColor(this.settings.defaultColor);
              }
              this._bindEvents();

              return true;
          }
      }, {
          key: 'hide',
          value: function hide() {
              return this._toggleDOM(false);
          }
      }, {
          key: 'destroy',
          value: function destroy() {
              this._events.destroy();
              if (this.domElement) {
                  this.settings.parent.removeChild(this.domElement);
              }
          }
      }, {
          key: '_bindEvents',
          value: function _bindEvents() {
              var _this2 = this;

              var that = this,
                  dom = this.domElement,
                  events = this._events;

              function addEvent(target, type, handler) {
                  events.add(target, type, handler);
              }

              addEvent(dom, 'click', function (e) {
                  return e.preventDefault();
              });

              dragTrack(events, this._domH, function (x, y) {
                  return that._setHSLA(x);
              });

              dragTrack(events, this._domSL, function (x, y) {
                  return that._setHSLA(null, x, 1 - y);
              });

              if (this.settings.alpha) {
                  dragTrack(events, this._domA, function (x, y) {
                      return that._setHSLA(null, null, null, 1 - y);
                  });
              }

              var editInput = this._domEdit;
              {
                  addEvent(editInput, 'input', function (e) {
                      that._setColor(this.value, { fromEditor: true, failSilently: true });
                  });

                  addEvent(editInput, 'focus', function (e) {
                      var input = this;

                      if (input.selectionStart === input.selectionEnd) {
                          input.select();
                      }
                  });
              }

              this._ifPopup(function () {

                  var popupCloseProxy = function popupCloseProxy(e) {
                      return _this2.closeHandler(e);
                  };

                  addEvent(window, EVENT_CLICK_OUTSIDE, popupCloseProxy);
                  addEvent(window, EVENT_TAB_MOVE, popupCloseProxy);
                  onKey(events, dom, ['Esc', 'Escape'], popupCloseProxy);

                  var timeKeeper = function timeKeeper(e) {
                      _this2.__containedEvent = e.timeStamp;
                  };
                  addEvent(dom, EVENT_CLICK_OUTSIDE, timeKeeper);

                  addEvent(dom, EVENT_TAB_MOVE, timeKeeper);

                  addEvent(_this2._domCancel, 'click', popupCloseProxy);
              });

              var onDoneProxy = function onDoneProxy(e) {
                  _this2._ifPopup(function () {
                      return _this2.closeHandler(e);
                  });
                  if (_this2.onDone) {
                      _this2.onDone(_this2.colour);
                  }
              };
              addEvent(this._domOkay, 'click', onDoneProxy);
              onKey(events, dom, ['Enter'], onDoneProxy);
          }
      }, {
          key: '_setPosition',
          value: function _setPosition() {
              var parent = this.settings.parent,
                  elm = this.domElement;

              if (parent !== elm.parentNode) {
                  parent.appendChild(elm);
              }

              this._ifPopup(function (popup) {

                  if (getComputedStyle(parent).position === 'static') {
                      parent.style.position = 'relative';
                  }

                  var cssClass = popup === true ? 'popup_right' : 'popup_' + popup;

                  ['popup_top', 'popup_bottom', 'popup_left', 'popup_right'].forEach(function (c) {

                      if (c === cssClass) {
                          elm.classList.add(c);
                      } else {
                          elm.classList.remove(c);
                      }
                  });

                  elm.classList.add(cssClass);
              });
          }
      }, {
          key: '_setHSLA',
          value: function _setHSLA(h, s, l, a, flags) {
              flags = flags || {};

              var col = this.colour,
                  hsla = col.hsla;

              [h, s, l, a].forEach(function (x, i) {
                  if (x || x === 0) {
                      hsla[i] = x;
                  }
              });
              col.hsla = hsla;

              this._updateUI(flags);

              if (this.onChange && !flags.silent) {
                  this.onChange(col);
              }
          }
      }, {
          key: '_updateUI',
          value: function _updateUI(flags) {
              if (!this.domElement) {
                  return;
              }
              flags = flags || {};

              var col = this.colour,
                  hsl = col.hsla,
                  cssHue = 'hsl(' + hsl[0] * HUES + ', 100%, 50%)',
                  cssHSL = col.hslString,
                  cssHSLA = col.hslaString;

              var uiH = this._domH,
                  uiSL = this._domSL,
                  uiA = this._domA,
                  thumbH = $('.picker_selector', uiH),
                  thumbSL = $('.picker_selector', uiSL),
                  thumbA = $('.picker_selector', uiA);

              function posX(parent, child, relX) {
                  child.style.left = relX * 100 + '%';
              }
              function posY(parent, child, relY) {
                  child.style.top = relY * 100 + '%';
              }

              posX(uiH, thumbH, hsl[0]);

              this._domSL.style.backgroundColor = this._domH.style.color = cssHue;

              posX(uiSL, thumbSL, hsl[1]);
              posY(uiSL, thumbSL, 1 - hsl[2]);

              uiSL.style.color = cssHSL;

              posY(uiA, thumbA, 1 - hsl[3]);

              var opaque = cssHSL,
                  transp = opaque.replace('hsl', 'hsla').replace(')', ', 0)'),
                  bg = 'linear-gradient(' + [opaque, transp] + ')';

              this._domA.style.background = bg + ', ' + BG_TRANSP;

              if (!flags.fromEditor) {
                  var format = this.settings.editorFormat,
                      alpha = this.settings.alpha;

                  var value = void 0;
                  switch (format) {
                      case 'rgb':
                          value = col.printRGB(alpha);break;
                      case 'hsl':
                          value = col.printHSL(alpha);break;
                      default:
                          value = col.printHex(alpha);
                  }
                  this._domEdit.value = value;
              }

              this._domSample.style.color = cssHSLA;
          }
      }, {
          key: '_ifPopup',
          value: function _ifPopup(actionIf, actionElse) {
              if (this.settings.parent && this.settings.popup) {
                  actionIf && actionIf(this.settings.popup);
              } else {
                  actionElse && actionElse();
              }
          }
      }, {
          key: '_toggleDOM',
          value: function _toggleDOM(toVisible) {
              var dom = this.domElement;
              if (!dom) {
                  return false;
              }

              var displayStyle = toVisible ? '' : 'none',
                  toggle = dom.style.display !== displayStyle;

              if (toggle) {
                  dom.style.display = displayStyle;
              }
              return toggle;
          }
      }]);
      return Picker;
  }();

  {
      var style = document.createElement('style');
      style.textContent = '.picker_wrapper.no_alpha .picker_alpha{display:none}.picker_wrapper.no_editor .picker_editor{position:absolute;z-index:-1;opacity:0}.picker_wrapper.no_cancel .picker_cancel{display:none}.layout_default.picker_wrapper{display:flex;flex-flow:row wrap;justify-content:space-between;align-items:stretch;font-size:10px;width:25em;padding:.5em}.layout_default.picker_wrapper input,.layout_default.picker_wrapper button{font-size:1rem}.layout_default.picker_wrapper>*{margin:.5em}.layout_default.picker_wrapper::before{content:"";display:block;width:100%;height:0;order:1}.layout_default .picker_slider,.layout_default .picker_selector{padding:1em}.layout_default .picker_hue{width:100%}.layout_default .picker_sl{flex:1 1 auto}.layout_default .picker_sl::before{content:"";display:block;padding-bottom:100%}.layout_default .picker_editor{order:1;width:6.5rem}.layout_default .picker_editor input{width:100%;height:100%}.layout_default .picker_sample{order:1;flex:1 1 auto}.layout_default .picker_done,.layout_default .picker_cancel{order:1}.picker_wrapper{box-sizing:border-box;background:#f2f2f2;box-shadow:0 0 0 1px silver;cursor:default;font-family:sans-serif;color:#444;pointer-events:auto}.picker_wrapper:focus{outline:none}.picker_wrapper button,.picker_wrapper input{box-sizing:border-box;border:none;box-shadow:0 0 0 1px silver;outline:none}.picker_wrapper button:focus,.picker_wrapper button:active,.picker_wrapper input:focus,.picker_wrapper input:active{box-shadow:0 0 2px 1px #1e90ff}.picker_wrapper button{padding:.4em .6em;cursor:pointer;background-color:#f5f5f5;background-image:linear-gradient(0deg, gainsboro, transparent)}.picker_wrapper button:active{background-image:linear-gradient(0deg, transparent, gainsboro)}.picker_wrapper button:hover{background-color:#fff}.picker_selector{position:absolute;z-index:1;display:block;-webkit-transform:translate(-50%, -50%);transform:translate(-50%, -50%);border:2px solid #fff;border-radius:100%;box-shadow:0 0 3px 1px #67b9ff;background:currentColor;cursor:pointer}.picker_slider .picker_selector{border-radius:2px}.picker_hue{position:relative;background-image:linear-gradient(90deg, red, yellow, lime, cyan, blue, magenta, red);box-shadow:0 0 0 1px silver}.picker_sl{position:relative;box-shadow:0 0 0 1px silver;background-image:linear-gradient(180deg, white, rgba(255, 255, 255, 0) 50%),linear-gradient(0deg, black, rgba(0, 0, 0, 0) 50%),linear-gradient(90deg, #808080, rgba(128, 128, 128, 0))}.picker_alpha,.picker_sample{position:relative;background:linear-gradient(45deg, lightgrey 25%, transparent 25%, transparent 75%, lightgrey 75%) 0 0/2em 2em,linear-gradient(45deg, lightgrey 25%, white 25%, white 75%, lightgrey 75%) 1em 1em/2em 2em;box-shadow:0 0 0 1px silver}.picker_alpha .picker_selector,.picker_sample .picker_selector{background:none}.picker_editor input{font-family:monospace;padding:.2em .4em}.picker_sample::before{content:"";position:absolute;display:block;width:100%;height:100%;background:currentColor}.picker_arrow{position:absolute;z-index:-1}.picker_wrapper.popup{position:absolute;z-index:2;margin:1.5em}.picker_wrapper.popup,.picker_wrapper.popup .picker_arrow::before,.picker_wrapper.popup .picker_arrow::after{background:#f2f2f2;box-shadow:0 0 10px 1px rgba(0,0,0,.4)}.picker_wrapper.popup .picker_arrow{width:3em;height:3em;margin:0}.picker_wrapper.popup .picker_arrow::before,.picker_wrapper.popup .picker_arrow::after{content:"";display:block;position:absolute;top:0;left:0;z-index:-99}.picker_wrapper.popup .picker_arrow::before{width:100%;height:100%;-webkit-transform:skew(45deg);transform:skew(45deg);-webkit-transform-origin:0 100%;transform-origin:0 100%}.picker_wrapper.popup .picker_arrow::after{width:150%;height:150%;box-shadow:none}.popup.popup_top{bottom:100%;left:0}.popup.popup_top .picker_arrow{bottom:0;left:0;-webkit-transform:rotate(-90deg);transform:rotate(-90deg)}.popup.popup_bottom{top:100%;left:0}.popup.popup_bottom .picker_arrow{top:0;left:0;-webkit-transform:rotate(90deg) scale(1, -1);transform:rotate(90deg) scale(1, -1)}.popup.popup_left{top:0;right:100%}.popup.popup_left .picker_arrow{top:0;right:0;-webkit-transform:scale(-1, 1);transform:scale(-1, 1)}.popup.popup_right{top:0;left:100%}.popup.popup_right .picker_arrow{top:0;left:0}';
      document.documentElement.firstElementChild.appendChild(style);

      Picker.StyleElement = style;
  }

  return Picker;

}));

/*!
 * Cropper.js v1.6.2
 * https://fengyuanchen.github.io/cropperjs
 *
 * Copyright 2015-present Chen Fengyuan
 * Released under the MIT license
 *
 * Date: 2024-04-21T07:43:05.335Z
 */

(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
  typeof define === 'function' && define.amd ? define(factory) :
  (global = typeof globalThis !== 'undefined' ? globalThis : global || self, global.Cropper = factory());
})(this, (function () { 'use strict';

  function ownKeys(e, r) {
    var t = Object.keys(e);
    if (Object.getOwnPropertySymbols) {
      var o = Object.getOwnPropertySymbols(e);
      r && (o = o.filter(function (r) {
        return Object.getOwnPropertyDescriptor(e, r).enumerable;
      })), t.push.apply(t, o);
    }
    return t;
  }
  function _objectSpread2(e) {
    for (var r = 1; r < arguments.length; r++) {
      var t = null != arguments[r] ? arguments[r] : {};
      r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
        _defineProperty(e, r, t[r]);
      }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
        Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
      });
    }
    return e;
  }
  function _toPrimitive(t, r) {
    if ("object" != typeof t || !t) return t;
    var e = t[Symbol.toPrimitive];
    if (void 0 !== e) {
      var i = e.call(t, r || "default");
      if ("object" != typeof i) return i;
      throw new TypeError("@@toPrimitive must return a primitive value.");
    }
    return ("string" === r ? String : Number)(t);
  }
  function _toPropertyKey(t) {
    var i = _toPrimitive(t, "string");
    return "symbol" == typeof i ? i : i + "";
  }
  function _typeof(o) {
    "@babel/helpers - typeof";

    return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
      return typeof o;
    } : function (o) {
      return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
    }, _typeof(o);
  }
  function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
      throw new TypeError("Cannot call a class as a function");
    }
  }
  function _defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor);
    }
  }
  function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    Object.defineProperty(Constructor, "prototype", {
      writable: false
    });
    return Constructor;
  }
  function _defineProperty(obj, key, value) {
    key = _toPropertyKey(key);
    if (key in obj) {
      Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
      });
    } else {
      obj[key] = value;
    }
    return obj;
  }
  function _toConsumableArray(arr) {
    return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
  }
  function _arrayWithoutHoles(arr) {
    if (Array.isArray(arr)) return _arrayLikeToArray(arr);
  }
  function _iterableToArray(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
  }
  function _unsupportedIterableToArray(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(o);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
  }
  function _arrayLikeToArray(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
    return arr2;
  }
  function _nonIterableSpread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  var IS_BROWSER = typeof window !== 'undefined' && typeof window.document !== 'undefined';
  var WINDOW = IS_BROWSER ? window : {};
  var IS_TOUCH_DEVICE = IS_BROWSER && WINDOW.document.documentElement ? 'ontouchstart' in WINDOW.document.documentElement : false;
  var HAS_POINTER_EVENT = IS_BROWSER ? 'PointerEvent' in WINDOW : false;
  var NAMESPACE = 'cropper';

  // Actions
  var ACTION_ALL = 'all';
  var ACTION_CROP = 'crop';
  var ACTION_MOVE = 'move';
  var ACTION_ZOOM = 'zoom';
  var ACTION_EAST = 'e';
  var ACTION_WEST = 'w';
  var ACTION_SOUTH = 's';
  var ACTION_NORTH = 'n';
  var ACTION_NORTH_EAST = 'ne';
  var ACTION_NORTH_WEST = 'nw';
  var ACTION_SOUTH_EAST = 'se';
  var ACTION_SOUTH_WEST = 'sw';

  // Classes
  var CLASS_CROP = "".concat(NAMESPACE, "-crop");
  var CLASS_DISABLED = "".concat(NAMESPACE, "-disabled");
  var CLASS_HIDDEN = "".concat(NAMESPACE, "-hidden");
  var CLASS_HIDE = "".concat(NAMESPACE, "-hide");
  var CLASS_INVISIBLE = "".concat(NAMESPACE, "-invisible");
  var CLASS_MODAL = "".concat(NAMESPACE, "-modal");
  var CLASS_MOVE = "".concat(NAMESPACE, "-move");

  // Data keys
  var DATA_ACTION = "".concat(NAMESPACE, "Action");
  var DATA_PREVIEW = "".concat(NAMESPACE, "Preview");

  // Drag modes
  var DRAG_MODE_CROP = 'crop';
  var DRAG_MODE_MOVE = 'move';
  var DRAG_MODE_NONE = 'none';

  // Events
  var EVENT_CROP = 'crop';
  var EVENT_CROP_END = 'cropend';
  var EVENT_CROP_MOVE = 'cropmove';
  var EVENT_CROP_START = 'cropstart';
  var EVENT_DBLCLICK = 'dblclick';
  var EVENT_TOUCH_START = IS_TOUCH_DEVICE ? 'touchstart' : 'mousedown';
  var EVENT_TOUCH_MOVE = IS_TOUCH_DEVICE ? 'touchmove' : 'mousemove';
  var EVENT_TOUCH_END = IS_TOUCH_DEVICE ? 'touchend touchcancel' : 'mouseup';
  var EVENT_POINTER_DOWN = HAS_POINTER_EVENT ? 'pointerdown' : EVENT_TOUCH_START;
  var EVENT_POINTER_MOVE = HAS_POINTER_EVENT ? 'pointermove' : EVENT_TOUCH_MOVE;
  var EVENT_POINTER_UP = HAS_POINTER_EVENT ? 'pointerup pointercancel' : EVENT_TOUCH_END;
  var EVENT_READY = 'ready';
  var EVENT_RESIZE = 'resize';
  var EVENT_WHEEL = 'wheel';
  var EVENT_ZOOM = 'zoom';

  // Mime types
  var MIME_TYPE_JPEG = 'image/jpeg';

  // RegExps
  var REGEXP_ACTIONS = /^e|w|s|n|se|sw|ne|nw|all|crop|move|zoom$/;
  var REGEXP_DATA_URL = /^data:/;
  var REGEXP_DATA_URL_JPEG = /^data:image\/jpeg;base64,/;
  var REGEXP_TAG_NAME = /^img|canvas$/i;

  // Misc
  // Inspired by the default width and height of a canvas element.
  var MIN_CONTAINER_WIDTH = 200;
  var MIN_CONTAINER_HEIGHT = 100;

  var DEFAULTS = {
    // Define the view mode of the cropper
    viewMode: 0,
    // 0, 1, 2, 3

    // Define the dragging mode of the cropper
    dragMode: DRAG_MODE_CROP,
    // 'crop', 'move' or 'none'

    // Define the initial aspect ratio of the crop box
    initialAspectRatio: NaN,
    // Define the aspect ratio of the crop box
    aspectRatio: NaN,
    // An object with the previous cropping result data
    data: null,
    // A selector for adding extra containers to preview
    preview: '',
    // Re-render the cropper when resize the window
    responsive: true,
    // Restore the cropped area after resize the window
    restore: true,
    // Check if the current image is a cross-origin image
    checkCrossOrigin: true,
    // Check the current image's Exif Orientation information
    checkOrientation: true,
    // Show the black modal
    modal: true,
    // Show the dashed lines for guiding
    guides: true,
    // Show the center indicator for guiding
    center: true,
    // Show the white modal to highlight the crop box
    highlight: true,
    // Show the grid background
    background: true,
    // Enable to crop the image automatically when initialize
    autoCrop: true,
    // Define the percentage of automatic cropping area when initializes
    autoCropArea: 0.8,
    // Enable to move the image
    movable: true,
    // Enable to rotate the image
    rotatable: true,
    // Enable to scale the image
    scalable: true,
    // Enable to zoom the image
    zoomable: true,
    // Enable to zoom the image by dragging touch
    zoomOnTouch: true,
    // Enable to zoom the image by wheeling mouse
    zoomOnWheel: true,
    // Define zoom ratio when zoom the image by wheeling mouse
    wheelZoomRatio: 0.1,
    // Enable to move the crop box
    cropBoxMovable: true,
    // Enable to resize the crop box
    cropBoxResizable: true,
    // Toggle drag mode between "crop" and "move" when click twice on the cropper
    toggleDragModeOnDblclick: true,
    // Size limitation
    minCanvasWidth: 0,
    minCanvasHeight: 0,
    minCropBoxWidth: 0,
    minCropBoxHeight: 0,
    minContainerWidth: MIN_CONTAINER_WIDTH,
    minContainerHeight: MIN_CONTAINER_HEIGHT,
    // Shortcuts of events
    ready: null,
    cropstart: null,
    cropmove: null,
    cropend: null,
    crop: null,
    zoom: null
  };

  var TEMPLATE = '<div class="cropper-container" touch-action="none">' + '<div class="cropper-wrap-box">' + '<div class="cropper-canvas"></div>' + '</div>' + '<div class="cropper-drag-box"></div>' + '<div class="cropper-crop-box">' + '<span class="cropper-view-box"></span>' + '<span class="cropper-dashed dashed-h"></span>' + '<span class="cropper-dashed dashed-v"></span>' + '<span class="cropper-center"></span>' + '<span class="cropper-face"></span>' + '<span class="cropper-line line-e" data-cropper-action="e"></span>' + '<span class="cropper-line line-n" data-cropper-action="n"></span>' + '<span class="cropper-line line-w" data-cropper-action="w"></span>' + '<span class="cropper-line line-s" data-cropper-action="s"></span>' + '<span class="cropper-point point-e" data-cropper-action="e"></span>' + '<span class="cropper-point point-n" data-cropper-action="n"></span>' + '<span class="cropper-point point-w" data-cropper-action="w"></span>' + '<span class="cropper-point point-s" data-cropper-action="s"></span>' + '<span class="cropper-point point-ne" data-cropper-action="ne"></span>' + '<span class="cropper-point point-nw" data-cropper-action="nw"></span>' + '<span class="cropper-point point-sw" data-cropper-action="sw"></span>' + '<span class="cropper-point point-se" data-cropper-action="se"></span>' + '</div>' + '</div>';

  /**
   * Check if the given value is not a number.
   */
  var isNaN = Number.isNaN || WINDOW.isNaN;

  /**
   * Check if the given value is a number.
   * @param {*} value - The value to check.
   * @returns {boolean} Returns `true` if the given value is a number, else `false`.
   */
  function isNumber(value) {
    return typeof value === 'number' && !isNaN(value);
  }

  /**
   * Check if the given value is a positive number.
   * @param {*} value - The value to check.
   * @returns {boolean} Returns `true` if the given value is a positive number, else `false`.
   */
  var isPositiveNumber = function isPositiveNumber(value) {
    return value > 0 && value < Infinity;
  };

  /**
   * Check if the given value is undefined.
   * @param {*} value - The value to check.
   * @returns {boolean} Returns `true` if the given value is undefined, else `false`.
   */
  function isUndefined(value) {
    return typeof value === 'undefined';
  }

  /**
   * Check if the given value is an object.
   * @param {*} value - The value to check.
   * @returns {boolean} Returns `true` if the given value is an object, else `false`.
   */
  function isObject(value) {
    return _typeof(value) === 'object' && value !== null;
  }
  var hasOwnProperty = Object.prototype.hasOwnProperty;

  /**
   * Check if the given value is a plain object.
   * @param {*} value - The value to check.
   * @returns {boolean} Returns `true` if the given value is a plain object, else `false`.
   */
  function isPlainObject(value) {
    if (!isObject(value)) {
      return false;
    }
    try {
      var _constructor = value.constructor;
      var prototype = _constructor.prototype;
      return _constructor && prototype && hasOwnProperty.call(prototype, 'isPrototypeOf');
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if the given value is a function.
   * @param {*} value - The value to check.
   * @returns {boolean} Returns `true` if the given value is a function, else `false`.
   */
  function isFunction(value) {
    return typeof value === 'function';
  }
  var slice = Array.prototype.slice;

  /**
   * Convert array-like or iterable object to an array.
   * @param {*} value - The value to convert.
   * @returns {Array} Returns a new array.
   */
  function toArray(value) {
    return Array.from ? Array.from(value) : slice.call(value);
  }

  /**
   * Iterate the given data.
   * @param {*} data - The data to iterate.
   * @param {Function} callback - The process function for each element.
   * @returns {*} The original data.
   */
  function forEach(data, callback) {
    if (data && isFunction(callback)) {
      if (Array.isArray(data) || isNumber(data.length) /* array-like */) {
        toArray(data).forEach(function (value, key) {
          callback.call(data, value, key, data);
        });
      } else if (isObject(data)) {
        Object.keys(data).forEach(function (key) {
          callback.call(data, data[key], key, data);
        });
      }
    }
    return data;
  }

  /**
   * Extend the given object.
   * @param {*} target - The target object to extend.
   * @param {*} args - The rest objects for merging to the target object.
   * @returns {Object} The extended object.
   */
  var assign = Object.assign || function assign(target) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    if (isObject(target) && args.length > 0) {
      args.forEach(function (arg) {
        if (isObject(arg)) {
          Object.keys(arg).forEach(function (key) {
            target[key] = arg[key];
          });
        }
      });
    }
    return target;
  };
  var REGEXP_DECIMALS = /\.\d*(?:0|9){12}\d*$/;

  /**
   * Normalize decimal number.
   * Check out {@link https://0.30000000000000004.com/}
   * @param {number} value - The value to normalize.
   * @param {number} [times=100000000000] - The times for normalizing.
   * @returns {number} Returns the normalized number.
   */
  function normalizeDecimalNumber(value) {
    var times = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 100000000000;
    return REGEXP_DECIMALS.test(value) ? Math.round(value * times) / times : value;
  }
  var REGEXP_SUFFIX = /^width|height|left|top|marginLeft|marginTop$/;

  /**
   * Apply styles to the given element.
   * @param {Element} element - The target element.
   * @param {Object} styles - The styles for applying.
   */
  function setStyle(element, styles) {
    var style = element.style;
    forEach(styles, function (value, property) {
      if (REGEXP_SUFFIX.test(property) && isNumber(value)) {
        value = "".concat(value, "px");
      }
      style[property] = value;
    });
  }

  /**
   * Check if the given element has a special class.
   * @param {Element} element - The element to check.
   * @param {string} value - The class to search.
   * @returns {boolean} Returns `true` if the special class was found.
   */
  function hasClass(element, value) {
    return element.classList ? element.classList.contains(value) : element.className.indexOf(value) > -1;
  }

  /**
   * Add classes to the given element.
   * @param {Element} element - The target element.
   * @param {string} value - The classes to be added.
   */
  function addClass(element, value) {
    if (!value) {
      return;
    }
    if (isNumber(element.length)) {
      forEach(element, function (elem) {
        addClass(elem, value);
      });
      return;
    }
    if (element.classList) {
      element.classList.add(value);
      return;
    }
    var className = element.className.trim();
    if (!className) {
      element.className = value;
    } else if (className.indexOf(value) < 0) {
      element.className = "".concat(className, " ").concat(value);
    }
  }

  /**
   * Remove classes from the given element.
   * @param {Element} element - The target element.
   * @param {string} value - The classes to be removed.
   */
  function removeClass(element, value) {
    if (!value) {
      return;
    }
    if (isNumber(element.length)) {
      forEach(element, function (elem) {
        removeClass(elem, value);
      });
      return;
    }
    if (element.classList) {
      element.classList.remove(value);
      return;
    }
    if (element.className.indexOf(value) >= 0) {
      element.className = element.className.replace(value, '');
    }
  }

  /**
   * Add or remove classes from the given element.
   * @param {Element} element - The target element.
   * @param {string} value - The classes to be toggled.
   * @param {boolean} added - Add only.
   */
  function toggleClass(element, value, added) {
    if (!value) {
      return;
    }
    if (isNumber(element.length)) {
      forEach(element, function (elem) {
        toggleClass(elem, value, added);
      });
      return;
    }

    // IE10-11 doesn't support the second parameter of `classList.toggle`
    if (added) {
      addClass(element, value);
    } else {
      removeClass(element, value);
    }
  }
  var REGEXP_CAMEL_CASE = /([a-z\d])([A-Z])/g;

  /**
   * Transform the given string from camelCase to kebab-case
   * @param {string} value - The value to transform.
   * @returns {string} The transformed value.
   */
  function toParamCase(value) {
    return value.replace(REGEXP_CAMEL_CASE, '$1-$2').toLowerCase();
  }

  /**
   * Get data from the given element.
   * @param {Element} element - The target element.
   * @param {string} name - The data key to get.
   * @returns {string} The data value.
   */
  function getData(element, name) {
    if (isObject(element[name])) {
      return element[name];
    }
    if (element.dataset) {
      return element.dataset[name];
    }
    return element.getAttribute("data-".concat(toParamCase(name)));
  }

  /**
   * Set data to the given element.
   * @param {Element} element - The target element.
   * @param {string} name - The data key to set.
   * @param {string} data - The data value.
   */
  function setData(element, name, data) {
    if (isObject(data)) {
      element[name] = data;
    } else if (element.dataset) {
      element.dataset[name] = data;
    } else {
      element.setAttribute("data-".concat(toParamCase(name)), data);
    }
  }

  /**
   * Remove data from the given element.
   * @param {Element} element - The target element.
   * @param {string} name - The data key to remove.
   */
  function removeData(element, name) {
    if (isObject(element[name])) {
      try {
        delete element[name];
      } catch (error) {
        element[name] = undefined;
      }
    } else if (element.dataset) {
      // #128 Safari not allows to delete dataset property
      try {
        delete element.dataset[name];
      } catch (error) {
        element.dataset[name] = undefined;
      }
    } else {
      element.removeAttribute("data-".concat(toParamCase(name)));
    }
  }
  var REGEXP_SPACES = /\s\s*/;
  var onceSupported = function () {
    var supported = false;
    if (IS_BROWSER) {
      var once = false;
      var listener = function listener() {};
      var options = Object.defineProperty({}, 'once', {
        get: function get() {
          supported = true;
          return once;
        },
        /**
         * This setter can fix a `TypeError` in strict mode
         * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Getter_only}
         * @param {boolean} value - The value to set
         */
        set: function set(value) {
          once = value;
        }
      });
      WINDOW.addEventListener('test', listener, options);
      WINDOW.removeEventListener('test', listener, options);
    }
    return supported;
  }();

  /**
   * Remove event listener from the target element.
   * @param {Element} element - The event target.
   * @param {string} type - The event type(s).
   * @param {Function} listener - The event listener.
   * @param {Object} options - The event options.
   */
  function removeListener(element, type, listener) {
    var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    var handler = listener;
    type.trim().split(REGEXP_SPACES).forEach(function (event) {
      if (!onceSupported) {
        var listeners = element.listeners;
        if (listeners && listeners[event] && listeners[event][listener]) {
          handler = listeners[event][listener];
          delete listeners[event][listener];
          if (Object.keys(listeners[event]).length === 0) {
            delete listeners[event];
          }
          if (Object.keys(listeners).length === 0) {
            delete element.listeners;
          }
        }
      }
      element.removeEventListener(event, handler, options);
    });
  }

  /**
   * Add event listener to the target element.
   * @param {Element} element - The event target.
   * @param {string} type - The event type(s).
   * @param {Function} listener - The event listener.
   * @param {Object} options - The event options.
   */
  function addListener(element, type, listener) {
    var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    var _handler = listener;
    type.trim().split(REGEXP_SPACES).forEach(function (event) {
      if (options.once && !onceSupported) {
        var _element$listeners = element.listeners,
          listeners = _element$listeners === void 0 ? {} : _element$listeners;
        _handler = function handler() {
          delete listeners[event][listener];
          element.removeEventListener(event, _handler, options);
          for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
            args[_key2] = arguments[_key2];
          }
          listener.apply(element, args);
        };
        if (!listeners[event]) {
          listeners[event] = {};
        }
        if (listeners[event][listener]) {
          element.removeEventListener(event, listeners[event][listener], options);
        }
        listeners[event][listener] = _handler;
        element.listeners = listeners;
      }
      element.addEventListener(event, _handler, options);
    });
  }

  /**
   * Dispatch event on the target element.
   * @param {Element} element - The event target.
   * @param {string} type - The event type(s).
   * @param {Object} data - The additional event data.
   * @returns {boolean} Indicate if the event is default prevented or not.
   */
  function dispatchEvent(element, type, data) {
    var event;

    // Event and CustomEvent on IE9-11 are global objects, not constructors
    if (isFunction(Event) && isFunction(CustomEvent)) {
      event = new CustomEvent(type, {
        detail: data,
        bubbles: true,
        cancelable: true
      });
    } else {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(type, true, true, data);
    }
    return element.dispatchEvent(event);
  }

  /**
   * Get the offset base on the document.
   * @param {Element} element - The target element.
   * @returns {Object} The offset data.
   */
  function getOffset(element) {
    var box = element.getBoundingClientRect();
    return {
      left: box.left + (window.pageXOffset - document.documentElement.clientLeft),
      top: box.top + (window.pageYOffset - document.documentElement.clientTop)
    };
  }
  var location = WINDOW.location;
  var REGEXP_ORIGINS = /^(\w+:)\/\/([^:/?#]*):?(\d*)/i;

  /**
   * Check if the given URL is a cross origin URL.
   * @param {string} url - The target URL.
   * @returns {boolean} Returns `true` if the given URL is a cross origin URL, else `false`.
   */
  function isCrossOriginURL(url) {
    var parts = url.match(REGEXP_ORIGINS);
    return parts !== null && (parts[1] !== location.protocol || parts[2] !== location.hostname || parts[3] !== location.port);
  }

  /**
   * Add timestamp to the given URL.
   * @param {string} url - The target URL.
   * @returns {string} The result URL.
   */
  function addTimestamp(url) {
    var timestamp = "timestamp=".concat(new Date().getTime());
    return url + (url.indexOf('?') === -1 ? '?' : '&') + timestamp;
  }

  /**
   * Get transforms base on the given object.
   * @param {Object} obj - The target object.
   * @returns {string} A string contains transform values.
   */
  function getTransforms(_ref) {
    var rotate = _ref.rotate,
      scaleX = _ref.scaleX,
      scaleY = _ref.scaleY,
      translateX = _ref.translateX,
      translateY = _ref.translateY;
    var values = [];
    if (isNumber(translateX) && translateX !== 0) {
      values.push("translateX(".concat(translateX, "px)"));
    }
    if (isNumber(translateY) && translateY !== 0) {
      values.push("translateY(".concat(translateY, "px)"));
    }

    // Rotate should come first before scale to match orientation transform
    if (isNumber(rotate) && rotate !== 0) {
      values.push("rotate(".concat(rotate, "deg)"));
    }
    if (isNumber(scaleX) && scaleX !== 1) {
      values.push("scaleX(".concat(scaleX, ")"));
    }
    if (isNumber(scaleY) && scaleY !== 1) {
      values.push("scaleY(".concat(scaleY, ")"));
    }
    var transform = values.length ? values.join(' ') : 'none';
    return {
      WebkitTransform: transform,
      msTransform: transform,
      transform: transform
    };
  }

  /**
   * Get the max ratio of a group of pointers.
   * @param {string} pointers - The target pointers.
   * @returns {number} The result ratio.
   */
  function getMaxZoomRatio(pointers) {
    var pointers2 = _objectSpread2({}, pointers);
    var maxRatio = 0;
    forEach(pointers, function (pointer, pointerId) {
      delete pointers2[pointerId];
      forEach(pointers2, function (pointer2) {
        var x1 = Math.abs(pointer.startX - pointer2.startX);
        var y1 = Math.abs(pointer.startY - pointer2.startY);
        var x2 = Math.abs(pointer.endX - pointer2.endX);
        var y2 = Math.abs(pointer.endY - pointer2.endY);
        var z1 = Math.sqrt(x1 * x1 + y1 * y1);
        var z2 = Math.sqrt(x2 * x2 + y2 * y2);
        var ratio = (z2 - z1) / z1;
        if (Math.abs(ratio) > Math.abs(maxRatio)) {
          maxRatio = ratio;
        }
      });
    });
    return maxRatio;
  }

  /**
   * Get a pointer from an event object.
   * @param {Object} event - The target event object.
   * @param {boolean} endOnly - Indicates if only returns the end point coordinate or not.
   * @returns {Object} The result pointer contains start and/or end point coordinates.
   */
  function getPointer(_ref2, endOnly) {
    var pageX = _ref2.pageX,
      pageY = _ref2.pageY;
    var end = {
      endX: pageX,
      endY: pageY
    };
    return endOnly ? end : _objectSpread2({
      startX: pageX,
      startY: pageY
    }, end);
  }

  /**
   * Get the center point coordinate of a group of pointers.
   * @param {Object} pointers - The target pointers.
   * @returns {Object} The center point coordinate.
   */
  function getPointersCenter(pointers) {
    var pageX = 0;
    var pageY = 0;
    var count = 0;
    forEach(pointers, function (_ref3) {
      var startX = _ref3.startX,
        startY = _ref3.startY;
      pageX += startX;
      pageY += startY;
      count += 1;
    });
    pageX /= count;
    pageY /= count;
    return {
      pageX: pageX,
      pageY: pageY
    };
  }

  /**
   * Get the max sizes in a rectangle under the given aspect ratio.
   * @param {Object} data - The original sizes.
   * @param {string} [type='contain'] - The adjust type.
   * @returns {Object} The result sizes.
   */
  function getAdjustedSizes(_ref4) {
    var aspectRatio = _ref4.aspectRatio,
      height = _ref4.height,
      width = _ref4.width;
    var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'contain';
    var isValidWidth = isPositiveNumber(width);
    var isValidHeight = isPositiveNumber(height);
    if (isValidWidth && isValidHeight) {
      var adjustedWidth = height * aspectRatio;
      if (type === 'contain' && adjustedWidth > width || type === 'cover' && adjustedWidth < width) {
        height = width / aspectRatio;
      } else {
        width = height * aspectRatio;
      }
    } else if (isValidWidth) {
      height = width / aspectRatio;
    } else if (isValidHeight) {
      width = height * aspectRatio;
    }
    return {
      width: width,
      height: height
    };
  }

  /**
   * Get the new sizes of a rectangle after rotated.
   * @param {Object} data - The original sizes.
   * @returns {Object} The result sizes.
   */
  function getRotatedSizes(_ref5) {
    var width = _ref5.width,
      height = _ref5.height,
      degree = _ref5.degree;
    degree = Math.abs(degree) % 180;
    if (degree === 90) {
      return {
        width: height,
        height: width
      };
    }
    var arc = degree % 90 * Math.PI / 180;
    var sinArc = Math.sin(arc);
    var cosArc = Math.cos(arc);
    var newWidth = width * cosArc + height * sinArc;
    var newHeight = width * sinArc + height * cosArc;
    return degree > 90 ? {
      width: newHeight,
      height: newWidth
    } : {
      width: newWidth,
      height: newHeight
    };
  }

  /**
   * Get a canvas which drew the given image.
   * @param {HTMLImageElement} image - The image for drawing.
   * @param {Object} imageData - The image data.
   * @param {Object} canvasData - The canvas data.
   * @param {Object} options - The options.
   * @returns {HTMLCanvasElement} The result canvas.
   */
  function getSourceCanvas(image, _ref6, _ref7, _ref8) {
    var imageAspectRatio = _ref6.aspectRatio,
      imageNaturalWidth = _ref6.naturalWidth,
      imageNaturalHeight = _ref6.naturalHeight,
      _ref6$rotate = _ref6.rotate,
      rotate = _ref6$rotate === void 0 ? 0 : _ref6$rotate,
      _ref6$scaleX = _ref6.scaleX,
      scaleX = _ref6$scaleX === void 0 ? 1 : _ref6$scaleX,
      _ref6$scaleY = _ref6.scaleY,
      scaleY = _ref6$scaleY === void 0 ? 1 : _ref6$scaleY;
    var aspectRatio = _ref7.aspectRatio,
      naturalWidth = _ref7.naturalWidth,
      naturalHeight = _ref7.naturalHeight;
    var _ref8$fillColor = _ref8.fillColor,
      fillColor = _ref8$fillColor === void 0 ? 'transparent' : _ref8$fillColor,
      _ref8$imageSmoothingE = _ref8.imageSmoothingEnabled,
      imageSmoothingEnabled = _ref8$imageSmoothingE === void 0 ? true : _ref8$imageSmoothingE,
      _ref8$imageSmoothingQ = _ref8.imageSmoothingQuality,
      imageSmoothingQuality = _ref8$imageSmoothingQ === void 0 ? 'low' : _ref8$imageSmoothingQ,
      _ref8$maxWidth = _ref8.maxWidth,
      maxWidth = _ref8$maxWidth === void 0 ? Infinity : _ref8$maxWidth,
      _ref8$maxHeight = _ref8.maxHeight,
      maxHeight = _ref8$maxHeight === void 0 ? Infinity : _ref8$maxHeight,
      _ref8$minWidth = _ref8.minWidth,
      minWidth = _ref8$minWidth === void 0 ? 0 : _ref8$minWidth,
      _ref8$minHeight = _ref8.minHeight,
      minHeight = _ref8$minHeight === void 0 ? 0 : _ref8$minHeight;
    var canvas = document.createElement('canvas');
    var context = canvas.getContext('2d');
    var maxSizes = getAdjustedSizes({
      aspectRatio: aspectRatio,
      width: maxWidth,
      height: maxHeight
    });
    var minSizes = getAdjustedSizes({
      aspectRatio: aspectRatio,
      width: minWidth,
      height: minHeight
    }, 'cover');
    var width = Math.min(maxSizes.width, Math.max(minSizes.width, naturalWidth));
    var height = Math.min(maxSizes.height, Math.max(minSizes.height, naturalHeight));

    // Note: should always use image's natural sizes for drawing as
    // imageData.naturalWidth === canvasData.naturalHeight when rotate % 180 === 90
    var destMaxSizes = getAdjustedSizes({
      aspectRatio: imageAspectRatio,
      width: maxWidth,
      height: maxHeight
    });
    var destMinSizes = getAdjustedSizes({
      aspectRatio: imageAspectRatio,
      width: minWidth,
      height: minHeight
    }, 'cover');
    var destWidth = Math.min(destMaxSizes.width, Math.max(destMinSizes.width, imageNaturalWidth));
    var destHeight = Math.min(destMaxSizes.height, Math.max(destMinSizes.height, imageNaturalHeight));
    var params = [-destWidth / 2, -destHeight / 2, destWidth, destHeight];
    canvas.width = normalizeDecimalNumber(width);
    canvas.height = normalizeDecimalNumber(height);
    context.fillStyle = fillColor;
    context.fillRect(0, 0, width, height);
    context.save();
    context.translate(width / 2, height / 2);
    context.rotate(rotate * Math.PI / 180);
    context.scale(scaleX, scaleY);
    context.imageSmoothingEnabled = imageSmoothingEnabled;
    context.imageSmoothingQuality = imageSmoothingQuality;
    context.drawImage.apply(context, [image].concat(_toConsumableArray(params.map(function (param) {
      return Math.floor(normalizeDecimalNumber(param));
    }))));
    context.restore();
    return canvas;
  }
  var fromCharCode = String.fromCharCode;

  /**
   * Get string from char code in data view.
   * @param {DataView} dataView - The data view for read.
   * @param {number} start - The start index.
   * @param {number} length - The read length.
   * @returns {string} The read result.
   */
  function getStringFromCharCode(dataView, start, length) {
    var str = '';
    length += start;
    for (var i = start; i < length; i += 1) {
      str += fromCharCode(dataView.getUint8(i));
    }
    return str;
  }
  var REGEXP_DATA_URL_HEAD = /^data:.*,/;

  /**
   * Transform Data URL to array buffer.
   * @param {string} dataURL - The Data URL to transform.
   * @returns {ArrayBuffer} The result array buffer.
   */
  function dataURLToArrayBuffer(dataURL) {
    var base64 = dataURL.replace(REGEXP_DATA_URL_HEAD, '');
    var binary = atob(base64);
    var arrayBuffer = new ArrayBuffer(binary.length);
    var uint8 = new Uint8Array(arrayBuffer);
    forEach(uint8, function (value, i) {
      uint8[i] = binary.charCodeAt(i);
    });
    return arrayBuffer;
  }

  /**
   * Transform array buffer to Data URL.
   * @param {ArrayBuffer} arrayBuffer - The array buffer to transform.
   * @param {string} mimeType - The mime type of the Data URL.
   * @returns {string} The result Data URL.
   */
  function arrayBufferToDataURL(arrayBuffer, mimeType) {
    var chunks = [];

    // Chunk Typed Array for better performance (#435)
    var chunkSize = 8192;
    var uint8 = new Uint8Array(arrayBuffer);
    while (uint8.length > 0) {
      // XXX: Babel's `toConsumableArray` helper will throw error in IE or Safari 9
      // eslint-disable-next-line prefer-spread
      chunks.push(fromCharCode.apply(null, toArray(uint8.subarray(0, chunkSize))));
      uint8 = uint8.subarray(chunkSize);
    }
    return "data:".concat(mimeType, ";base64,").concat(btoa(chunks.join('')));
  }

  /**
   * Get orientation value from given array buffer.
   * @param {ArrayBuffer} arrayBuffer - The array buffer to read.
   * @returns {number} The read orientation value.
   */
  function resetAndGetOrientation(arrayBuffer) {
    var dataView = new DataView(arrayBuffer);
    var orientation;

    // Ignores range error when the image does not have correct Exif information
    try {
      var littleEndian;
      var app1Start;
      var ifdStart;

      // Only handle JPEG image (start by 0xFFD8)
      if (dataView.getUint8(0) === 0xFF && dataView.getUint8(1) === 0xD8) {
        var length = dataView.byteLength;
        var offset = 2;
        while (offset + 1 < length) {
          if (dataView.getUint8(offset) === 0xFF && dataView.getUint8(offset + 1) === 0xE1) {
            app1Start = offset;
            break;
          }
          offset += 1;
        }
      }
      if (app1Start) {
        var exifIDCode = app1Start + 4;
        var tiffOffset = app1Start + 10;
        if (getStringFromCharCode(dataView, exifIDCode, 4) === 'Exif') {
          var endianness = dataView.getUint16(tiffOffset);
          littleEndian = endianness === 0x4949;
          if (littleEndian || endianness === 0x4D4D /* bigEndian */) {
            if (dataView.getUint16(tiffOffset + 2, littleEndian) === 0x002A) {
              var firstIFDOffset = dataView.getUint32(tiffOffset + 4, littleEndian);
              if (firstIFDOffset >= 0x00000008) {
                ifdStart = tiffOffset + firstIFDOffset;
              }
            }
          }
        }
      }
      if (ifdStart) {
        var _length = dataView.getUint16(ifdStart, littleEndian);
        var _offset;
        var i;
        for (i = 0; i < _length; i += 1) {
          _offset = ifdStart + i * 12 + 2;
          if (dataView.getUint16(_offset, littleEndian) === 0x0112 /* Orientation */) {
            // 8 is the offset of the current tag's value
            _offset += 8;

            // Get the original orientation value
            orientation = dataView.getUint16(_offset, littleEndian);

            // Override the orientation with its default value
            dataView.setUint16(_offset, 1, littleEndian);
            break;
          }
        }
      }
    } catch (error) {
      orientation = 1;
    }
    return orientation;
  }

  /**
   * Parse Exif Orientation value.
   * @param {number} orientation - The orientation to parse.
   * @returns {Object} The parsed result.
   */
  function parseOrientation(orientation) {
    var rotate = 0;
    var scaleX = 1;
    var scaleY = 1;
    switch (orientation) {
      // Flip horizontal
      case 2:
        scaleX = -1;
        break;

      // Rotate left 180°
      case 3:
        rotate = -180;
        break;

      // Flip vertical
      case 4:
        scaleY = -1;
        break;

      // Flip vertical and rotate right 90°
      case 5:
        rotate = 90;
        scaleY = -1;
        break;

      // Rotate right 90°
      case 6:
        rotate = 90;
        break;

      // Flip horizontal and rotate right 90°
      case 7:
        rotate = 90;
        scaleX = -1;
        break;

      // Rotate left 90°
      case 8:
        rotate = -90;
        break;
    }
    return {
      rotate: rotate,
      scaleX: scaleX,
      scaleY: scaleY
    };
  }

  var render = {
    render: function render() {
      this.initContainer();
      this.initCanvas();
      this.initCropBox();
      this.renderCanvas();
      if (this.cropped) {
        this.renderCropBox();
      }
    },
    initContainer: function initContainer() {
      var element = this.element,
        options = this.options,
        container = this.container,
        cropper = this.cropper;
      var minWidth = Number(options.minContainerWidth);
      var minHeight = Number(options.minContainerHeight);
      addClass(cropper, CLASS_HIDDEN);
      removeClass(element, CLASS_HIDDEN);
      var containerData = {
        width: Math.max(container.offsetWidth, minWidth >= 0 ? minWidth : MIN_CONTAINER_WIDTH),
        height: Math.max(container.offsetHeight, minHeight >= 0 ? minHeight : MIN_CONTAINER_HEIGHT)
      };
      this.containerData = containerData;
      setStyle(cropper, {
        width: containerData.width,
        height: containerData.height
      });
      addClass(element, CLASS_HIDDEN);
      removeClass(cropper, CLASS_HIDDEN);
    },
    // Canvas (image wrapper)
    initCanvas: function initCanvas() {
      var containerData = this.containerData,
        imageData = this.imageData;
      var viewMode = this.options.viewMode;
      var rotated = Math.abs(imageData.rotate) % 180 === 90;
      var naturalWidth = rotated ? imageData.naturalHeight : imageData.naturalWidth;
      var naturalHeight = rotated ? imageData.naturalWidth : imageData.naturalHeight;
      var aspectRatio = naturalWidth / naturalHeight;
      var canvasWidth = containerData.width;
      var canvasHeight = containerData.height;
      if (containerData.height * aspectRatio > containerData.width) {
        if (viewMode === 3) {
          canvasWidth = containerData.height * aspectRatio;
        } else {
          canvasHeight = containerData.width / aspectRatio;
        }
      } else if (viewMode === 3) {
        canvasHeight = containerData.width / aspectRatio;
      } else {
        canvasWidth = containerData.height * aspectRatio;
      }
      var canvasData = {
        aspectRatio: aspectRatio,
        naturalWidth: naturalWidth,
        naturalHeight: naturalHeight,
        width: canvasWidth,
        height: canvasHeight
      };
      this.canvasData = canvasData;
      this.limited = viewMode === 1 || viewMode === 2;
      this.limitCanvas(true, true);
      canvasData.width = Math.min(Math.max(canvasData.width, canvasData.minWidth), canvasData.maxWidth);
      canvasData.height = Math.min(Math.max(canvasData.height, canvasData.minHeight), canvasData.maxHeight);
      canvasData.left = (containerData.width - canvasData.width) / 2;
      canvasData.top = (containerData.height - canvasData.height) / 2;
      canvasData.oldLeft = canvasData.left;
      canvasData.oldTop = canvasData.top;
      this.initialCanvasData = assign({}, canvasData);
    },
    limitCanvas: function limitCanvas(sizeLimited, positionLimited) {
      var options = this.options,
        containerData = this.containerData,
        canvasData = this.canvasData,
        cropBoxData = this.cropBoxData;
      var viewMode = options.viewMode;
      var aspectRatio = canvasData.aspectRatio;
      var cropped = this.cropped && cropBoxData;
      if (sizeLimited) {
        var minCanvasWidth = Number(options.minCanvasWidth) || 0;
        var minCanvasHeight = Number(options.minCanvasHeight) || 0;
        if (viewMode > 1) {
          minCanvasWidth = Math.max(minCanvasWidth, containerData.width);
          minCanvasHeight = Math.max(minCanvasHeight, containerData.height);
          if (viewMode === 3) {
            if (minCanvasHeight * aspectRatio > minCanvasWidth) {
              minCanvasWidth = minCanvasHeight * aspectRatio;
            } else {
              minCanvasHeight = minCanvasWidth / aspectRatio;
            }
          }
        } else if (viewMode > 0) {
          if (minCanvasWidth) {
            minCanvasWidth = Math.max(minCanvasWidth, cropped ? cropBoxData.width : 0);
          } else if (minCanvasHeight) {
            minCanvasHeight = Math.max(minCanvasHeight, cropped ? cropBoxData.height : 0);
          } else if (cropped) {
            minCanvasWidth = cropBoxData.width;
            minCanvasHeight = cropBoxData.height;
            if (minCanvasHeight * aspectRatio > minCanvasWidth) {
              minCanvasWidth = minCanvasHeight * aspectRatio;
            } else {
              minCanvasHeight = minCanvasWidth / aspectRatio;
            }
          }
        }
        var _getAdjustedSizes = getAdjustedSizes({
          aspectRatio: aspectRatio,
          width: minCanvasWidth,
          height: minCanvasHeight
        });
        minCanvasWidth = _getAdjustedSizes.width;
        minCanvasHeight = _getAdjustedSizes.height;
        canvasData.minWidth = minCanvasWidth;
        canvasData.minHeight = minCanvasHeight;
        canvasData.maxWidth = Infinity;
        canvasData.maxHeight = Infinity;
      }
      if (positionLimited) {
        if (viewMode > (cropped ? 0 : 1)) {
          var newCanvasLeft = containerData.width - canvasData.width;
          var newCanvasTop = containerData.height - canvasData.height;
          canvasData.minLeft = Math.min(0, newCanvasLeft);
          canvasData.minTop = Math.min(0, newCanvasTop);
          canvasData.maxLeft = Math.max(0, newCanvasLeft);
          canvasData.maxTop = Math.max(0, newCanvasTop);
          if (cropped && this.limited) {
            canvasData.minLeft = Math.min(cropBoxData.left, cropBoxData.left + (cropBoxData.width - canvasData.width));
            canvasData.minTop = Math.min(cropBoxData.top, cropBoxData.top + (cropBoxData.height - canvasData.height));
            canvasData.maxLeft = cropBoxData.left;
            canvasData.maxTop = cropBoxData.top;
            if (viewMode === 2) {
              if (canvasData.width >= containerData.width) {
                canvasData.minLeft = Math.min(0, newCanvasLeft);
                canvasData.maxLeft = Math.max(0, newCanvasLeft);
              }
              if (canvasData.height >= containerData.height) {
                canvasData.minTop = Math.min(0, newCanvasTop);
                canvasData.maxTop = Math.max(0, newCanvasTop);
              }
            }
          }
        } else {
          canvasData.minLeft = -canvasData.width;
          canvasData.minTop = -canvasData.height;
          canvasData.maxLeft = containerData.width;
          canvasData.maxTop = containerData.height;
        }
      }
    },
    renderCanvas: function renderCanvas(changed, transformed) {
      var canvasData = this.canvasData,
        imageData = this.imageData;
      if (transformed) {
        var _getRotatedSizes = getRotatedSizes({
            width: imageData.naturalWidth * Math.abs(imageData.scaleX || 1),
            height: imageData.naturalHeight * Math.abs(imageData.scaleY || 1),
            degree: imageData.rotate || 0
          }),
          naturalWidth = _getRotatedSizes.width,
          naturalHeight = _getRotatedSizes.height;
        var width = canvasData.width * (naturalWidth / canvasData.naturalWidth);
        var height = canvasData.height * (naturalHeight / canvasData.naturalHeight);
        canvasData.left -= (width - canvasData.width) / 2;
        canvasData.top -= (height - canvasData.height) / 2;
        canvasData.width = width;
        canvasData.height = height;
        canvasData.aspectRatio = naturalWidth / naturalHeight;
        canvasData.naturalWidth = naturalWidth;
        canvasData.naturalHeight = naturalHeight;
        this.limitCanvas(true, false);
      }
      if (canvasData.width > canvasData.maxWidth || canvasData.width < canvasData.minWidth) {
        canvasData.left = canvasData.oldLeft;
      }
      if (canvasData.height > canvasData.maxHeight || canvasData.height < canvasData.minHeight) {
        canvasData.top = canvasData.oldTop;
      }
      canvasData.width = Math.min(Math.max(canvasData.width, canvasData.minWidth), canvasData.maxWidth);
      canvasData.height = Math.min(Math.max(canvasData.height, canvasData.minHeight), canvasData.maxHeight);
      this.limitCanvas(false, true);
      canvasData.left = Math.min(Math.max(canvasData.left, canvasData.minLeft), canvasData.maxLeft);
      canvasData.top = Math.min(Math.max(canvasData.top, canvasData.minTop), canvasData.maxTop);
      canvasData.oldLeft = canvasData.left;
      canvasData.oldTop = canvasData.top;
      setStyle(this.canvas, assign({
        width: canvasData.width,
        height: canvasData.height
      }, getTransforms({
        translateX: canvasData.left,
        translateY: canvasData.top
      })));
      this.renderImage(changed);
      if (this.cropped && this.limited) {
        this.limitCropBox(true, true);
      }
    },
    renderImage: function renderImage(changed) {
      var canvasData = this.canvasData,
        imageData = this.imageData;
      var width = imageData.naturalWidth * (canvasData.width / canvasData.naturalWidth);
      var height = imageData.naturalHeight * (canvasData.height / canvasData.naturalHeight);
      assign(imageData, {
        width: width,
        height: height,
        left: (canvasData.width - width) / 2,
        top: (canvasData.height - height) / 2
      });
      setStyle(this.image, assign({
        width: imageData.width,
        height: imageData.height
      }, getTransforms(assign({
        translateX: imageData.left,
        translateY: imageData.top
      }, imageData))));
      if (changed) {
        this.output();
      }
    },
    initCropBox: function initCropBox() {
      var options = this.options,
        canvasData = this.canvasData;
      var aspectRatio = options.aspectRatio || options.initialAspectRatio;
      var autoCropArea = Number(options.autoCropArea) || 0.8;
      var cropBoxData = {
        width: canvasData.width,
        height: canvasData.height
      };
      if (aspectRatio) {
        if (canvasData.height * aspectRatio > canvasData.width) {
          cropBoxData.height = cropBoxData.width / aspectRatio;
        } else {
          cropBoxData.width = cropBoxData.height * aspectRatio;
        }
      }
      this.cropBoxData = cropBoxData;
      this.limitCropBox(true, true);

      // Initialize auto crop area
      cropBoxData.width = Math.min(Math.max(cropBoxData.width, cropBoxData.minWidth), cropBoxData.maxWidth);
      cropBoxData.height = Math.min(Math.max(cropBoxData.height, cropBoxData.minHeight), cropBoxData.maxHeight);

      // The width/height of auto crop area must large than "minWidth/Height"
      cropBoxData.width = Math.max(cropBoxData.minWidth, cropBoxData.width * autoCropArea);
      cropBoxData.height = Math.max(cropBoxData.minHeight, cropBoxData.height * autoCropArea);
      cropBoxData.left = canvasData.left + (canvasData.width - cropBoxData.width) / 2;
      cropBoxData.top = canvasData.top + (canvasData.height - cropBoxData.height) / 2;
      cropBoxData.oldLeft = cropBoxData.left;
      cropBoxData.oldTop = cropBoxData.top;
      this.initialCropBoxData = assign({}, cropBoxData);
    },
    limitCropBox: function limitCropBox(sizeLimited, positionLimited) {
      var options = this.options,
        containerData = this.containerData,
        canvasData = this.canvasData,
        cropBoxData = this.cropBoxData,
        limited = this.limited;
      var aspectRatio = options.aspectRatio;
      if (sizeLimited) {
        var minCropBoxWidth = Number(options.minCropBoxWidth) || 0;
        var minCropBoxHeight = Number(options.minCropBoxHeight) || 0;
        var maxCropBoxWidth = limited ? Math.min(containerData.width, canvasData.width, canvasData.width + canvasData.left, containerData.width - canvasData.left) : containerData.width;
        var maxCropBoxHeight = limited ? Math.min(containerData.height, canvasData.height, canvasData.height + canvasData.top, containerData.height - canvasData.top) : containerData.height;

        // The min/maxCropBoxWidth/Height must be less than container's width/height
        minCropBoxWidth = Math.min(minCropBoxWidth, containerData.width);
        minCropBoxHeight = Math.min(minCropBoxHeight, containerData.height);
        if (aspectRatio) {
          if (minCropBoxWidth && minCropBoxHeight) {
            if (minCropBoxHeight * aspectRatio > minCropBoxWidth) {
              minCropBoxHeight = minCropBoxWidth / aspectRatio;
            } else {
              minCropBoxWidth = minCropBoxHeight * aspectRatio;
            }
          } else if (minCropBoxWidth) {
            minCropBoxHeight = minCropBoxWidth / aspectRatio;
          } else if (minCropBoxHeight) {
            minCropBoxWidth = minCropBoxHeight * aspectRatio;
          }
          if (maxCropBoxHeight * aspectRatio > maxCropBoxWidth) {
            maxCropBoxHeight = maxCropBoxWidth / aspectRatio;
          } else {
            maxCropBoxWidth = maxCropBoxHeight * aspectRatio;
          }
        }

        // The minWidth/Height must be less than maxWidth/Height
        cropBoxData.minWidth = Math.min(minCropBoxWidth, maxCropBoxWidth);
        cropBoxData.minHeight = Math.min(minCropBoxHeight, maxCropBoxHeight);
        cropBoxData.maxWidth = maxCropBoxWidth;
        cropBoxData.maxHeight = maxCropBoxHeight;
      }
      if (positionLimited) {
        if (limited) {
          cropBoxData.minLeft = Math.max(0, canvasData.left);
          cropBoxData.minTop = Math.max(0, canvasData.top);
          cropBoxData.maxLeft = Math.min(containerData.width, canvasData.left + canvasData.width) - cropBoxData.width;
          cropBoxData.maxTop = Math.min(containerData.height, canvasData.top + canvasData.height) - cropBoxData.height;
        } else {
          cropBoxData.minLeft = 0;
          cropBoxData.minTop = 0;
          cropBoxData.maxLeft = containerData.width - cropBoxData.width;
          cropBoxData.maxTop = containerData.height - cropBoxData.height;
        }
      }
    },
    renderCropBox: function renderCropBox() {
      var options = this.options,
        containerData = this.containerData,
        cropBoxData = this.cropBoxData;
      if (cropBoxData.width > cropBoxData.maxWidth || cropBoxData.width < cropBoxData.minWidth) {
        cropBoxData.left = cropBoxData.oldLeft;
      }
      if (cropBoxData.height > cropBoxData.maxHeight || cropBoxData.height < cropBoxData.minHeight) {
        cropBoxData.top = cropBoxData.oldTop;
      }
      cropBoxData.width = Math.min(Math.max(cropBoxData.width, cropBoxData.minWidth), cropBoxData.maxWidth);
      cropBoxData.height = Math.min(Math.max(cropBoxData.height, cropBoxData.minHeight), cropBoxData.maxHeight);
      this.limitCropBox(false, true);
      cropBoxData.left = Math.min(Math.max(cropBoxData.left, cropBoxData.minLeft), cropBoxData.maxLeft);
      cropBoxData.top = Math.min(Math.max(cropBoxData.top, cropBoxData.minTop), cropBoxData.maxTop);
      cropBoxData.oldLeft = cropBoxData.left;
      cropBoxData.oldTop = cropBoxData.top;
      if (options.movable && options.cropBoxMovable) {
        // Turn to move the canvas when the crop box is equal to the container
        setData(this.face, DATA_ACTION, cropBoxData.width >= containerData.width && cropBoxData.height >= containerData.height ? ACTION_MOVE : ACTION_ALL);
      }
      setStyle(this.cropBox, assign({
        width: cropBoxData.width,
        height: cropBoxData.height
      }, getTransforms({
        translateX: cropBoxData.left,
        translateY: cropBoxData.top
      })));
      if (this.cropped && this.limited) {
        this.limitCanvas(true, true);
      }
      if (!this.disabled) {
        this.output();
      }
    },
    output: function output() {
      this.preview();
      dispatchEvent(this.element, EVENT_CROP, this.getData());
    }
  };

  var preview = {
    initPreview: function initPreview() {
      var element = this.element,
        crossOrigin = this.crossOrigin;
      var preview = this.options.preview;
      var url = crossOrigin ? this.crossOriginUrl : this.url;
      var alt = element.alt || 'The image to preview';
      var image = document.createElement('img');
      if (crossOrigin) {
        image.crossOrigin = crossOrigin;
      }
      image.src = url;
      image.alt = alt;
      this.viewBox.appendChild(image);
      this.viewBoxImage = image;
      if (!preview) {
        return;
      }
      var previews = preview;
      if (typeof preview === 'string') {
        previews = element.ownerDocument.querySelectorAll(preview);
      } else if (preview.querySelector) {
        previews = [preview];
      }
      this.previews = previews;
      forEach(previews, function (el) {
        var img = document.createElement('img');

        // Save the original size for recover
        setData(el, DATA_PREVIEW, {
          width: el.offsetWidth,
          height: el.offsetHeight,
          html: el.innerHTML
        });
        if (crossOrigin) {
          img.crossOrigin = crossOrigin;
        }
        img.src = url;
        img.alt = alt;

        /**
         * Override img element styles
         * Add `display:block` to avoid margin top issue
         * Add `height:auto` to override `height` attribute on IE8
         * (Occur only when margin-top <= -height)
         */
        img.style.cssText = 'display:block;' + 'width:100%;' + 'height:auto;' + 'min-width:0!important;' + 'min-height:0!important;' + 'max-width:none!important;' + 'max-height:none!important;' + 'image-orientation:0deg!important;"';
        el.innerHTML = '';
        el.appendChild(img);
      });
    },
    resetPreview: function resetPreview() {
      forEach(this.previews, function (element) {
        var data = getData(element, DATA_PREVIEW);
        setStyle(element, {
          width: data.width,
          height: data.height
        });
        element.innerHTML = data.html;
        removeData(element, DATA_PREVIEW);
      });
    },
    preview: function preview() {
      var imageData = this.imageData,
        canvasData = this.canvasData,
        cropBoxData = this.cropBoxData;
      var cropBoxWidth = cropBoxData.width,
        cropBoxHeight = cropBoxData.height;
      var width = imageData.width,
        height = imageData.height;
      var left = cropBoxData.left - canvasData.left - imageData.left;
      var top = cropBoxData.top - canvasData.top - imageData.top;
      if (!this.cropped || this.disabled) {
        return;
      }
      setStyle(this.viewBoxImage, assign({
        width: width,
        height: height
      }, getTransforms(assign({
        translateX: -left,
        translateY: -top
      }, imageData))));
      forEach(this.previews, function (element) {
        var data = getData(element, DATA_PREVIEW);
        var originalWidth = data.width;
        var originalHeight = data.height;
        var newWidth = originalWidth;
        var newHeight = originalHeight;
        var ratio = 1;
        if (cropBoxWidth) {
          ratio = originalWidth / cropBoxWidth;
          newHeight = cropBoxHeight * ratio;
        }
        if (cropBoxHeight && newHeight > originalHeight) {
          ratio = originalHeight / cropBoxHeight;
          newWidth = cropBoxWidth * ratio;
          newHeight = originalHeight;
        }
        setStyle(element, {
          width: newWidth,
          height: newHeight
        });
        setStyle(element.getElementsByTagName('img')[0], assign({
          width: width * ratio,
          height: height * ratio
        }, getTransforms(assign({
          translateX: -left * ratio,
          translateY: -top * ratio
        }, imageData))));
      });
    }
  };

  var events = {
    bind: function bind() {
      var element = this.element,
        options = this.options,
        cropper = this.cropper;
      if (isFunction(options.cropstart)) {
        addListener(element, EVENT_CROP_START, options.cropstart);
      }
      if (isFunction(options.cropmove)) {
        addListener(element, EVENT_CROP_MOVE, options.cropmove);
      }
      if (isFunction(options.cropend)) {
        addListener(element, EVENT_CROP_END, options.cropend);
      }
      if (isFunction(options.crop)) {
        addListener(element, EVENT_CROP, options.crop);
      }
      if (isFunction(options.zoom)) {
        addListener(element, EVENT_ZOOM, options.zoom);
      }
      addListener(cropper, EVENT_POINTER_DOWN, this.onCropStart = this.cropStart.bind(this));
      if (options.zoomable && options.zoomOnWheel) {
        addListener(cropper, EVENT_WHEEL, this.onWheel = this.wheel.bind(this), {
          passive: false,
          capture: true
        });
      }
      if (options.toggleDragModeOnDblclick) {
        addListener(cropper, EVENT_DBLCLICK, this.onDblclick = this.dblclick.bind(this));
      }
      addListener(element.ownerDocument, EVENT_POINTER_MOVE, this.onCropMove = this.cropMove.bind(this));
      addListener(element.ownerDocument, EVENT_POINTER_UP, this.onCropEnd = this.cropEnd.bind(this));
      if (options.responsive) {
        addListener(window, EVENT_RESIZE, this.onResize = this.resize.bind(this));
      }
    },
    unbind: function unbind() {
      var element = this.element,
        options = this.options,
        cropper = this.cropper;
      if (isFunction(options.cropstart)) {
        removeListener(element, EVENT_CROP_START, options.cropstart);
      }
      if (isFunction(options.cropmove)) {
        removeListener(element, EVENT_CROP_MOVE, options.cropmove);
      }
      if (isFunction(options.cropend)) {
        removeListener(element, EVENT_CROP_END, options.cropend);
      }
      if (isFunction(options.crop)) {
        removeListener(element, EVENT_CROP, options.crop);
      }
      if (isFunction(options.zoom)) {
        removeListener(element, EVENT_ZOOM, options.zoom);
      }
      removeListener(cropper, EVENT_POINTER_DOWN, this.onCropStart);
      if (options.zoomable && options.zoomOnWheel) {
        removeListener(cropper, EVENT_WHEEL, this.onWheel, {
          passive: false,
          capture: true
        });
      }
      if (options.toggleDragModeOnDblclick) {
        removeListener(cropper, EVENT_DBLCLICK, this.onDblclick);
      }
      removeListener(element.ownerDocument, EVENT_POINTER_MOVE, this.onCropMove);
      removeListener(element.ownerDocument, EVENT_POINTER_UP, this.onCropEnd);
      if (options.responsive) {
        removeListener(window, EVENT_RESIZE, this.onResize);
      }
    }
  };

  var handlers = {
    resize: function resize() {
      if (this.disabled) {
        return;
      }
      var options = this.options,
        container = this.container,
        containerData = this.containerData;
      var ratioX = container.offsetWidth / containerData.width;
      var ratioY = container.offsetHeight / containerData.height;
      var ratio = Math.abs(ratioX - 1) > Math.abs(ratioY - 1) ? ratioX : ratioY;

      // Resize when width changed or height changed
      if (ratio !== 1) {
        var canvasData;
        var cropBoxData;
        if (options.restore) {
          canvasData = this.getCanvasData();
          cropBoxData = this.getCropBoxData();
        }
        this.render();
        if (options.restore) {
          this.setCanvasData(forEach(canvasData, function (n, i) {
            canvasData[i] = n * ratio;
          }));
          this.setCropBoxData(forEach(cropBoxData, function (n, i) {
            cropBoxData[i] = n * ratio;
          }));
        }
      }
    },
    dblclick: function dblclick() {
      if (this.disabled || this.options.dragMode === DRAG_MODE_NONE) {
        return;
      }
      this.setDragMode(hasClass(this.dragBox, CLASS_CROP) ? DRAG_MODE_MOVE : DRAG_MODE_CROP);
    },
    wheel: function wheel(event) {
      var _this = this;
      var ratio = Number(this.options.wheelZoomRatio) || 0.1;
      var delta = 1;
      if (this.disabled) {
        return;
      }
      event.preventDefault();

      // Limit wheel speed to prevent zoom too fast (#21)
      if (this.wheeling) {
        return;
      }
      this.wheeling = true;
      setTimeout(function () {
        _this.wheeling = false;
      }, 50);
      if (event.deltaY) {
        delta = event.deltaY > 0 ? 1 : -1;
      } else if (event.wheelDelta) {
        delta = -event.wheelDelta / 120;
      } else if (event.detail) {
        delta = event.detail > 0 ? 1 : -1;
      }
      this.zoom(-delta * ratio, event);
    },
    cropStart: function cropStart(event) {
      var buttons = event.buttons,
        button = event.button;
      if (this.disabled

      // Handle mouse event and pointer event and ignore touch event
      || (event.type === 'mousedown' || event.type === 'pointerdown' && event.pointerType === 'mouse') && (
      // No primary button (Usually the left button)
      isNumber(buttons) && buttons !== 1 || isNumber(button) && button !== 0

      // Open context menu
      || event.ctrlKey)) {
        return;
      }
      var options = this.options,
        pointers = this.pointers;
      var action;
      if (event.changedTouches) {
        // Handle touch event
        forEach(event.changedTouches, function (touch) {
          pointers[touch.identifier] = getPointer(touch);
        });
      } else {
        // Handle mouse event and pointer event
        pointers[event.pointerId || 0] = getPointer(event);
      }
      if (Object.keys(pointers).length > 1 && options.zoomable && options.zoomOnTouch) {
        action = ACTION_ZOOM;
      } else {
        action = getData(event.target, DATA_ACTION);
      }
      if (!REGEXP_ACTIONS.test(action)) {
        return;
      }
      if (dispatchEvent(this.element, EVENT_CROP_START, {
        originalEvent: event,
        action: action
      }) === false) {
        return;
      }

      // This line is required for preventing page zooming in iOS browsers
      event.preventDefault();
      this.action = action;
      this.cropping = false;
      if (action === ACTION_CROP) {
        this.cropping = true;
        addClass(this.dragBox, CLASS_MODAL);
      }
    },
    cropMove: function cropMove(event) {
      var action = this.action;
      if (this.disabled || !action) {
        return;
      }
      var pointers = this.pointers;
      event.preventDefault();
      if (dispatchEvent(this.element, EVENT_CROP_MOVE, {
        originalEvent: event,
        action: action
      }) === false) {
        return;
      }
      if (event.changedTouches) {
        forEach(event.changedTouches, function (touch) {
          // The first parameter should not be undefined (#432)
          assign(pointers[touch.identifier] || {}, getPointer(touch, true));
        });
      } else {
        assign(pointers[event.pointerId || 0] || {}, getPointer(event, true));
      }
      this.change(event);
    },
    cropEnd: function cropEnd(event) {
      if (this.disabled) {
        return;
      }
      var action = this.action,
        pointers = this.pointers;
      if (event.changedTouches) {
        forEach(event.changedTouches, function (touch) {
          delete pointers[touch.identifier];
        });
      } else {
        delete pointers[event.pointerId || 0];
      }
      if (!action) {
        return;
      }
      event.preventDefault();
      if (!Object.keys(pointers).length) {
        this.action = '';
      }
      if (this.cropping) {
        this.cropping = false;
        toggleClass(this.dragBox, CLASS_MODAL, this.cropped && this.options.modal);
      }
      dispatchEvent(this.element, EVENT_CROP_END, {
        originalEvent: event,
        action: action
      });
    }
  };

  var change = {
    change: function change(event) {
      var options = this.options,
        canvasData = this.canvasData,
        containerData = this.containerData,
        cropBoxData = this.cropBoxData,
        pointers = this.pointers;
      var action = this.action;
      var aspectRatio = options.aspectRatio;
      var left = cropBoxData.left,
        top = cropBoxData.top,
        width = cropBoxData.width,
        height = cropBoxData.height;
      var right = left + width;
      var bottom = top + height;
      var minLeft = 0;
      var minTop = 0;
      var maxWidth = containerData.width;
      var maxHeight = containerData.height;
      var renderable = true;
      var offset;

      // Locking aspect ratio in "free mode" by holding shift key
      if (!aspectRatio && event.shiftKey) {
        aspectRatio = width && height ? width / height : 1;
      }
      if (this.limited) {
        minLeft = cropBoxData.minLeft;
        minTop = cropBoxData.minTop;
        maxWidth = minLeft + Math.min(containerData.width, canvasData.width, canvasData.left + canvasData.width);
        maxHeight = minTop + Math.min(containerData.height, canvasData.height, canvasData.top + canvasData.height);
      }
      var pointer = pointers[Object.keys(pointers)[0]];
      var range = {
        x: pointer.endX - pointer.startX,
        y: pointer.endY - pointer.startY
      };
      var check = function check(side) {
        switch (side) {
          case ACTION_EAST:
            if (right + range.x > maxWidth) {
              range.x = maxWidth - right;
            }
            break;
          case ACTION_WEST:
            if (left + range.x < minLeft) {
              range.x = minLeft - left;
            }
            break;
          case ACTION_NORTH:
            if (top + range.y < minTop) {
              range.y = minTop - top;
            }
            break;
          case ACTION_SOUTH:
            if (bottom + range.y > maxHeight) {
              range.y = maxHeight - bottom;
            }
            break;
        }
      };
      switch (action) {
        // Move crop box
        case ACTION_ALL:
          left += range.x;
          top += range.y;
          break;

        // Resize crop box
        case ACTION_EAST:
          if (range.x >= 0 && (right >= maxWidth || aspectRatio && (top <= minTop || bottom >= maxHeight))) {
            renderable = false;
            break;
          }
          check(ACTION_EAST);
          width += range.x;
          if (width < 0) {
            action = ACTION_WEST;
            width = -width;
            left -= width;
          }
          if (aspectRatio) {
            height = width / aspectRatio;
            top += (cropBoxData.height - height) / 2;
          }
          break;
        case ACTION_NORTH:
          if (range.y <= 0 && (top <= minTop || aspectRatio && (left <= minLeft || right >= maxWidth))) {
            renderable = false;
            break;
          }
          check(ACTION_NORTH);
          height -= range.y;
          top += range.y;
          if (height < 0) {
            action = ACTION_SOUTH;
            height = -height;
            top -= height;
          }
          if (aspectRatio) {
            width = height * aspectRatio;
            left += (cropBoxData.width - width) / 2;
          }
          break;
        case ACTION_WEST:
          if (range.x <= 0 && (left <= minLeft || aspectRatio && (top <= minTop || bottom >= maxHeight))) {
            renderable = false;
            break;
          }
          check(ACTION_WEST);
          width -= range.x;
          left += range.x;
          if (width < 0) {
            action = ACTION_EAST;
            width = -width;
            left -= width;
          }
          if (aspectRatio) {
            height = width / aspectRatio;
            top += (cropBoxData.height - height) / 2;
          }
          break;
        case ACTION_SOUTH:
          if (range.y >= 0 && (bottom >= maxHeight || aspectRatio && (left <= minLeft || right >= maxWidth))) {
            renderable = false;
            break;
          }
          check(ACTION_SOUTH);
          height += range.y;
          if (height < 0) {
            action = ACTION_NORTH;
            height = -height;
            top -= height;
          }
          if (aspectRatio) {
            width = height * aspectRatio;
            left += (cropBoxData.width - width) / 2;
          }
          break;
        case ACTION_NORTH_EAST:
          if (aspectRatio) {
            if (range.y <= 0 && (top <= minTop || right >= maxWidth)) {
              renderable = false;
              break;
            }
            check(ACTION_NORTH);
            height -= range.y;
            top += range.y;
            width = height * aspectRatio;
          } else {
            check(ACTION_NORTH);
            check(ACTION_EAST);
            if (range.x >= 0) {
              if (right < maxWidth) {
                width += range.x;
              } else if (range.y <= 0 && top <= minTop) {
                renderable = false;
              }
            } else {
              width += range.x;
            }
            if (range.y <= 0) {
              if (top > minTop) {
                height -= range.y;
                top += range.y;
              }
            } else {
              height -= range.y;
              top += range.y;
            }
          }
          if (width < 0 && height < 0) {
            action = ACTION_SOUTH_WEST;
            height = -height;
            width = -width;
            top -= height;
            left -= width;
          } else if (width < 0) {
            action = ACTION_NORTH_WEST;
            width = -width;
            left -= width;
          } else if (height < 0) {
            action = ACTION_SOUTH_EAST;
            height = -height;
            top -= height;
          }
          break;
        case ACTION_NORTH_WEST:
          if (aspectRatio) {
            if (range.y <= 0 && (top <= minTop || left <= minLeft)) {
              renderable = false;
              break;
            }
            check(ACTION_NORTH);
            height -= range.y;
            top += range.y;
            width = height * aspectRatio;
            left += cropBoxData.width - width;
          } else {
            check(ACTION_NORTH);
            check(ACTION_WEST);
            if (range.x <= 0) {
              if (left > minLeft) {
                width -= range.x;
                left += range.x;
              } else if (range.y <= 0 && top <= minTop) {
                renderable = false;
              }
            } else {
              width -= range.x;
              left += range.x;
            }
            if (range.y <= 0) {
              if (top > minTop) {
                height -= range.y;
                top += range.y;
              }
            } else {
              height -= range.y;
              top += range.y;
            }
          }
          if (width < 0 && height < 0) {
            action = ACTION_SOUTH_EAST;
            height = -height;
            width = -width;
            top -= height;
            left -= width;
          } else if (width < 0) {
            action = ACTION_NORTH_EAST;
            width = -width;
            left -= width;
          } else if (height < 0) {
            action = ACTION_SOUTH_WEST;
            height = -height;
            top -= height;
          }
          break;
        case ACTION_SOUTH_WEST:
          if (aspectRatio) {
            if (range.x <= 0 && (left <= minLeft || bottom >= maxHeight)) {
              renderable = false;
              break;
            }
            check(ACTION_WEST);
            width -= range.x;
            left += range.x;
            height = width / aspectRatio;
          } else {
            check(ACTION_SOUTH);
            check(ACTION_WEST);
            if (range.x <= 0) {
              if (left > minLeft) {
                width -= range.x;
                left += range.x;
              } else if (range.y >= 0 && bottom >= maxHeight) {
                renderable = false;
              }
            } else {
              width -= range.x;
              left += range.x;
            }
            if (range.y >= 0) {
              if (bottom < maxHeight) {
                height += range.y;
              }
            } else {
              height += range.y;
            }
          }
          if (width < 0 && height < 0) {
            action = ACTION_NORTH_EAST;
            height = -height;
            width = -width;
            top -= height;
            left -= width;
          } else if (width < 0) {
            action = ACTION_SOUTH_EAST;
            width = -width;
            left -= width;
          } else if (height < 0) {
            action = ACTION_NORTH_WEST;
            height = -height;
            top -= height;
          }
          break;
        case ACTION_SOUTH_EAST:
          if (aspectRatio) {
            if (range.x >= 0 && (right >= maxWidth || bottom >= maxHeight)) {
              renderable = false;
              break;
            }
            check(ACTION_EAST);
            width += range.x;
            height = width / aspectRatio;
          } else {
            check(ACTION_SOUTH);
            check(ACTION_EAST);
            if (range.x >= 0) {
              if (right < maxWidth) {
                width += range.x;
              } else if (range.y >= 0 && bottom >= maxHeight) {
                renderable = false;
              }
            } else {
              width += range.x;
            }
            if (range.y >= 0) {
              if (bottom < maxHeight) {
                height += range.y;
              }
            } else {
              height += range.y;
            }
          }
          if (width < 0 && height < 0) {
            action = ACTION_NORTH_WEST;
            height = -height;
            width = -width;
            top -= height;
            left -= width;
          } else if (width < 0) {
            action = ACTION_SOUTH_WEST;
            width = -width;
            left -= width;
          } else if (height < 0) {
            action = ACTION_NORTH_EAST;
            height = -height;
            top -= height;
          }
          break;

        // Move canvas
        case ACTION_MOVE:
          this.move(range.x, range.y);
          renderable = false;
          break;

        // Zoom canvas
        case ACTION_ZOOM:
          this.zoom(getMaxZoomRatio(pointers), event);
          renderable = false;
          break;

        // Create crop box
        case ACTION_CROP:
          if (!range.x || !range.y) {
            renderable = false;
            break;
          }
          offset = getOffset(this.cropper);
          left = pointer.startX - offset.left;
          top = pointer.startY - offset.top;
          width = cropBoxData.minWidth;
          height = cropBoxData.minHeight;
          if (range.x > 0) {
            action = range.y > 0 ? ACTION_SOUTH_EAST : ACTION_NORTH_EAST;
          } else if (range.x < 0) {
            left -= width;
            action = range.y > 0 ? ACTION_SOUTH_WEST : ACTION_NORTH_WEST;
          }
          if (range.y < 0) {
            top -= height;
          }

          // Show the crop box if is hidden
          if (!this.cropped) {
            removeClass(this.cropBox, CLASS_HIDDEN);
            this.cropped = true;
            if (this.limited) {
              this.limitCropBox(true, true);
            }
          }
          break;
      }
      if (renderable) {
        cropBoxData.width = width;
        cropBoxData.height = height;
        cropBoxData.left = left;
        cropBoxData.top = top;
        this.action = action;
        this.renderCropBox();
      }

      // Override
      forEach(pointers, function (p) {
        p.startX = p.endX;
        p.startY = p.endY;
      });
    }
  };

  var methods = {
    // Show the crop box manually
    crop: function crop() {
      if (this.ready && !this.cropped && !this.disabled) {
        this.cropped = true;
        this.limitCropBox(true, true);
        if (this.options.modal) {
          addClass(this.dragBox, CLASS_MODAL);
        }
        removeClass(this.cropBox, CLASS_HIDDEN);
        this.setCropBoxData(this.initialCropBoxData);
      }
      return this;
    },
    // Reset the image and crop box to their initial states
    reset: function reset() {
      if (this.ready && !this.disabled) {
        this.imageData = assign({}, this.initialImageData);
        this.canvasData = assign({}, this.initialCanvasData);
        this.cropBoxData = assign({}, this.initialCropBoxData);
        this.renderCanvas();
        if (this.cropped) {
          this.renderCropBox();
        }
      }
      return this;
    },
    // Clear the crop box
    clear: function clear() {
      if (this.cropped && !this.disabled) {
        assign(this.cropBoxData, {
          left: 0,
          top: 0,
          width: 0,
          height: 0
        });
        this.cropped = false;
        this.renderCropBox();
        this.limitCanvas(true, true);

        // Render canvas after crop box rendered
        this.renderCanvas();
        removeClass(this.dragBox, CLASS_MODAL);
        addClass(this.cropBox, CLASS_HIDDEN);
      }
      return this;
    },
    /**
     * Replace the image's src and rebuild the cropper
     * @param {string} url - The new URL.
     * @param {boolean} [hasSameSize] - Indicate if the new image has the same size as the old one.
     * @returns {Cropper} this
     */
    replace: function replace(url) {
      var hasSameSize = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      if (!this.disabled && url) {
        if (this.isImg) {
          this.element.src = url;
        }
        if (hasSameSize) {
          this.url = url;
          this.image.src = url;
          if (this.ready) {
            this.viewBoxImage.src = url;
            forEach(this.previews, function (element) {
              element.getElementsByTagName('img')[0].src = url;
            });
          }
        } else {
          if (this.isImg) {
            this.replaced = true;
          }
          this.options.data = null;
          this.uncreate();
          this.load(url);
        }
      }
      return this;
    },
    // Enable (unfreeze) the cropper
    enable: function enable() {
      if (this.ready && this.disabled) {
        this.disabled = false;
        removeClass(this.cropper, CLASS_DISABLED);
      }
      return this;
    },
    // Disable (freeze) the cropper
    disable: function disable() {
      if (this.ready && !this.disabled) {
        this.disabled = true;
        addClass(this.cropper, CLASS_DISABLED);
      }
      return this;
    },
    /**
     * Destroy the cropper and remove the instance from the image
     * @returns {Cropper} this
     */
    destroy: function destroy() {
      var element = this.element;
      if (!element[NAMESPACE]) {
        return this;
      }
      element[NAMESPACE] = undefined;
      if (this.isImg && this.replaced) {
        element.src = this.originalUrl;
      }
      this.uncreate();
      return this;
    },
    /**
     * Move the canvas with relative offsets
     * @param {number} offsetX - The relative offset distance on the x-axis.
     * @param {number} [offsetY=offsetX] - The relative offset distance on the y-axis.
     * @returns {Cropper} this
     */
    move: function move(offsetX) {
      var offsetY = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : offsetX;
      var _this$canvasData = this.canvasData,
        left = _this$canvasData.left,
        top = _this$canvasData.top;
      return this.moveTo(isUndefined(offsetX) ? offsetX : left + Number(offsetX), isUndefined(offsetY) ? offsetY : top + Number(offsetY));
    },
    /**
     * Move the canvas to an absolute point
     * @param {number} x - The x-axis coordinate.
     * @param {number} [y=x] - The y-axis coordinate.
     * @returns {Cropper} this
     */
    moveTo: function moveTo(x) {
      var y = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : x;
      var canvasData = this.canvasData;
      var changed = false;
      x = Number(x);
      y = Number(y);
      if (this.ready && !this.disabled && this.options.movable) {
        if (isNumber(x)) {
          canvasData.left = x;
          changed = true;
        }
        if (isNumber(y)) {
          canvasData.top = y;
          changed = true;
        }
        if (changed) {
          this.renderCanvas(true);
        }
      }
      return this;
    },
    /**
     * Zoom the canvas with a relative ratio
     * @param {number} ratio - The target ratio.
     * @param {Event} _originalEvent - The original event if any.
     * @returns {Cropper} this
     */
    zoom: function zoom(ratio, _originalEvent) {
      var canvasData = this.canvasData;
      ratio = Number(ratio);
      if (ratio < 0) {
        ratio = 1 / (1 - ratio);
      } else {
        ratio = 1 + ratio;
      }
      return this.zoomTo(canvasData.width * ratio / canvasData.naturalWidth, null, _originalEvent);
    },
    /**
     * Zoom the canvas to an absolute ratio
     * @param {number} ratio - The target ratio.
     * @param {Object} pivot - The zoom pivot point coordinate.
     * @param {Event} _originalEvent - The original event if any.
     * @returns {Cropper} this
     */
    zoomTo: function zoomTo(ratio, pivot, _originalEvent) {
      var options = this.options,
        canvasData = this.canvasData;
      var width = canvasData.width,
        height = canvasData.height,
        naturalWidth = canvasData.naturalWidth,
        naturalHeight = canvasData.naturalHeight;
      ratio = Number(ratio);
      if (ratio >= 0 && this.ready && !this.disabled && options.zoomable) {
        var newWidth = naturalWidth * ratio;
        var newHeight = naturalHeight * ratio;
        if (dispatchEvent(this.element, EVENT_ZOOM, {
          ratio: ratio,
          oldRatio: width / naturalWidth,
          originalEvent: _originalEvent
        }) === false) {
          return this;
        }
        if (_originalEvent) {
          var pointers = this.pointers;
          var offset = getOffset(this.cropper);
          var center = pointers && Object.keys(pointers).length ? getPointersCenter(pointers) : {
            pageX: _originalEvent.pageX,
            pageY: _originalEvent.pageY
          };

          // Zoom from the triggering point of the event
          canvasData.left -= (newWidth - width) * ((center.pageX - offset.left - canvasData.left) / width);
          canvasData.top -= (newHeight - height) * ((center.pageY - offset.top - canvasData.top) / height);
        } else if (isPlainObject(pivot) && isNumber(pivot.x) && isNumber(pivot.y)) {
          canvasData.left -= (newWidth - width) * ((pivot.x - canvasData.left) / width);
          canvasData.top -= (newHeight - height) * ((pivot.y - canvasData.top) / height);
        } else {
          // Zoom from the center of the canvas
          canvasData.left -= (newWidth - width) / 2;
          canvasData.top -= (newHeight - height) / 2;
        }
        canvasData.width = newWidth;
        canvasData.height = newHeight;
        this.renderCanvas(true);
      }
      return this;
    },
    /**
     * Rotate the canvas with a relative degree
     * @param {number} degree - The rotate degree.
     * @returns {Cropper} this
     */
    rotate: function rotate(degree) {
      return this.rotateTo((this.imageData.rotate || 0) + Number(degree));
    },
    /**
     * Rotate the canvas to an absolute degree
     * @param {number} degree - The rotate degree.
     * @returns {Cropper} this
     */
    rotateTo: function rotateTo(degree) {
      degree = Number(degree);
      if (isNumber(degree) && this.ready && !this.disabled && this.options.rotatable) {
        this.imageData.rotate = degree % 360;
        this.renderCanvas(true, true);
      }
      return this;
    },
    /**
     * Scale the image on the x-axis.
     * @param {number} scaleX - The scale ratio on the x-axis.
     * @returns {Cropper} this
     */
    scaleX: function scaleX(_scaleX) {
      var scaleY = this.imageData.scaleY;
      return this.scale(_scaleX, isNumber(scaleY) ? scaleY : 1);
    },
    /**
     * Scale the image on the y-axis.
     * @param {number} scaleY - The scale ratio on the y-axis.
     * @returns {Cropper} this
     */
    scaleY: function scaleY(_scaleY) {
      var scaleX = this.imageData.scaleX;
      return this.scale(isNumber(scaleX) ? scaleX : 1, _scaleY);
    },
    /**
     * Scale the image
     * @param {number} scaleX - The scale ratio on the x-axis.
     * @param {number} [scaleY=scaleX] - The scale ratio on the y-axis.
     * @returns {Cropper} this
     */
    scale: function scale(scaleX) {
      var scaleY = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : scaleX;
      var imageData = this.imageData;
      var transformed = false;
      scaleX = Number(scaleX);
      scaleY = Number(scaleY);
      if (this.ready && !this.disabled && this.options.scalable) {
        if (isNumber(scaleX)) {
          imageData.scaleX = scaleX;
          transformed = true;
        }
        if (isNumber(scaleY)) {
          imageData.scaleY = scaleY;
          transformed = true;
        }
        if (transformed) {
          this.renderCanvas(true, true);
        }
      }
      return this;
    },
    /**
     * Get the cropped area position and size data (base on the original image)
     * @param {boolean} [rounded=false] - Indicate if round the data values or not.
     * @returns {Object} The result cropped data.
     */
    getData: function getData() {
      var rounded = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      var options = this.options,
        imageData = this.imageData,
        canvasData = this.canvasData,
        cropBoxData = this.cropBoxData;
      var data;
      if (this.ready && this.cropped) {
        data = {
          x: cropBoxData.left - canvasData.left,
          y: cropBoxData.top - canvasData.top,
          width: cropBoxData.width,
          height: cropBoxData.height
        };
        var ratio = imageData.width / imageData.naturalWidth;
        forEach(data, function (n, i) {
          data[i] = n / ratio;
        });
        if (rounded) {
          // In case rounding off leads to extra 1px in right or bottom border
          // we should round the top-left corner and the dimension (#343).
          var bottom = Math.round(data.y + data.height);
          var right = Math.round(data.x + data.width);
          data.x = Math.round(data.x);
          data.y = Math.round(data.y);
          data.width = right - data.x;
          data.height = bottom - data.y;
        }
      } else {
        data = {
          x: 0,
          y: 0,
          width: 0,
          height: 0
        };
      }
      if (options.rotatable) {
        data.rotate = imageData.rotate || 0;
      }
      if (options.scalable) {
        data.scaleX = imageData.scaleX || 1;
        data.scaleY = imageData.scaleY || 1;
      }
      return data;
    },
    /**
     * Set the cropped area position and size with new data
     * @param {Object} data - The new data.
     * @returns {Cropper} this
     */
    setData: function setData(data) {
      var options = this.options,
        imageData = this.imageData,
        canvasData = this.canvasData;
      var cropBoxData = {};
      if (this.ready && !this.disabled && isPlainObject(data)) {
        var transformed = false;
        if (options.rotatable) {
          if (isNumber(data.rotate) && data.rotate !== imageData.rotate) {
            imageData.rotate = data.rotate;
            transformed = true;
          }
        }
        if (options.scalable) {
          if (isNumber(data.scaleX) && data.scaleX !== imageData.scaleX) {
            imageData.scaleX = data.scaleX;
            transformed = true;
          }
          if (isNumber(data.scaleY) && data.scaleY !== imageData.scaleY) {
            imageData.scaleY = data.scaleY;
            transformed = true;
          }
        }
        if (transformed) {
          this.renderCanvas(true, true);
        }
        var ratio = imageData.width / imageData.naturalWidth;
        if (isNumber(data.x)) {
          cropBoxData.left = data.x * ratio + canvasData.left;
        }
        if (isNumber(data.y)) {
          cropBoxData.top = data.y * ratio + canvasData.top;
        }
        if (isNumber(data.width)) {
          cropBoxData.width = data.width * ratio;
        }
        if (isNumber(data.height)) {
          cropBoxData.height = data.height * ratio;
        }
        this.setCropBoxData(cropBoxData);
      }
      return this;
    },
    /**
     * Get the container size data.
     * @returns {Object} The result container data.
     */
    getContainerData: function getContainerData() {
      return this.ready ? assign({}, this.containerData) : {};
    },
    /**
     * Get the image position and size data.
     * @returns {Object} The result image data.
     */
    getImageData: function getImageData() {
      return this.sized ? assign({}, this.imageData) : {};
    },
    /**
     * Get the canvas position and size data.
     * @returns {Object} The result canvas data.
     */
    getCanvasData: function getCanvasData() {
      var canvasData = this.canvasData;
      var data = {};
      if (this.ready) {
        forEach(['left', 'top', 'width', 'height', 'naturalWidth', 'naturalHeight'], function (n) {
          data[n] = canvasData[n];
        });
      }
      return data;
    },
    /**
     * Set the canvas position and size with new data.
     * @param {Object} data - The new canvas data.
     * @returns {Cropper} this
     */
    setCanvasData: function setCanvasData(data) {
      var canvasData = this.canvasData;
      var aspectRatio = canvasData.aspectRatio;
      if (this.ready && !this.disabled && isPlainObject(data)) {
        if (isNumber(data.left)) {
          canvasData.left = data.left;
        }
        if (isNumber(data.top)) {
          canvasData.top = data.top;
        }
        if (isNumber(data.width)) {
          canvasData.width = data.width;
          canvasData.height = data.width / aspectRatio;
        } else if (isNumber(data.height)) {
          canvasData.height = data.height;
          canvasData.width = data.height * aspectRatio;
        }
        this.renderCanvas(true);
      }
      return this;
    },
    /**
     * Get the crop box position and size data.
     * @returns {Object} The result crop box data.
     */
    getCropBoxData: function getCropBoxData() {
      var cropBoxData = this.cropBoxData;
      var data;
      if (this.ready && this.cropped) {
        data = {
          left: cropBoxData.left,
          top: cropBoxData.top,
          width: cropBoxData.width,
          height: cropBoxData.height
        };
      }
      return data || {};
    },
    /**
     * Set the crop box position and size with new data.
     * @param {Object} data - The new crop box data.
     * @returns {Cropper} this
     */
    setCropBoxData: function setCropBoxData(data) {
      var cropBoxData = this.cropBoxData;
      var aspectRatio = this.options.aspectRatio;
      var widthChanged;
      var heightChanged;
      if (this.ready && this.cropped && !this.disabled && isPlainObject(data)) {
        if (isNumber(data.left)) {
          cropBoxData.left = data.left;
        }
        if (isNumber(data.top)) {
          cropBoxData.top = data.top;
        }
        if (isNumber(data.width) && data.width !== cropBoxData.width) {
          widthChanged = true;
          cropBoxData.width = data.width;
        }
        if (isNumber(data.height) && data.height !== cropBoxData.height) {
          heightChanged = true;
          cropBoxData.height = data.height;
        }
        if (aspectRatio) {
          if (widthChanged) {
            cropBoxData.height = cropBoxData.width / aspectRatio;
          } else if (heightChanged) {
            cropBoxData.width = cropBoxData.height * aspectRatio;
          }
        }
        this.renderCropBox();
      }
      return this;
    },
    /**
     * Get a canvas drawn the cropped image.
     * @param {Object} [options={}] - The config options.
     * @returns {HTMLCanvasElement} - The result canvas.
     */
    getCroppedCanvas: function getCroppedCanvas() {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      if (!this.ready || !window.HTMLCanvasElement) {
        return null;
      }
      var canvasData = this.canvasData;
      var source = getSourceCanvas(this.image, this.imageData, canvasData, options);

      // Returns the source canvas if it is not cropped.
      if (!this.cropped) {
        return source;
      }
      var _this$getData = this.getData(options.rounded),
        initialX = _this$getData.x,
        initialY = _this$getData.y,
        initialWidth = _this$getData.width,
        initialHeight = _this$getData.height;
      var ratio = source.width / Math.floor(canvasData.naturalWidth);
      if (ratio !== 1) {
        initialX *= ratio;
        initialY *= ratio;
        initialWidth *= ratio;
        initialHeight *= ratio;
      }
      var aspectRatio = initialWidth / initialHeight;
      var maxSizes = getAdjustedSizes({
        aspectRatio: aspectRatio,
        width: options.maxWidth || Infinity,
        height: options.maxHeight || Infinity
      });
      var minSizes = getAdjustedSizes({
        aspectRatio: aspectRatio,
        width: options.minWidth || 0,
        height: options.minHeight || 0
      }, 'cover');
      var _getAdjustedSizes = getAdjustedSizes({
          aspectRatio: aspectRatio,
          width: options.width || (ratio !== 1 ? source.width : initialWidth),
          height: options.height || (ratio !== 1 ? source.height : initialHeight)
        }),
        width = _getAdjustedSizes.width,
        height = _getAdjustedSizes.height;
      width = Math.min(maxSizes.width, Math.max(minSizes.width, width));
      height = Math.min(maxSizes.height, Math.max(minSizes.height, height));
      var canvas = document.createElement('canvas');
      var context = canvas.getContext('2d');
      canvas.width = normalizeDecimalNumber(width);
      canvas.height = normalizeDecimalNumber(height);
      context.fillStyle = options.fillColor || 'transparent';
      context.fillRect(0, 0, width, height);
      var _options$imageSmoothi = options.imageSmoothingEnabled,
        imageSmoothingEnabled = _options$imageSmoothi === void 0 ? true : _options$imageSmoothi,
        imageSmoothingQuality = options.imageSmoothingQuality;
      context.imageSmoothingEnabled = imageSmoothingEnabled;
      if (imageSmoothingQuality) {
        context.imageSmoothingQuality = imageSmoothingQuality;
      }

      // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D.drawImage
      var sourceWidth = source.width;
      var sourceHeight = source.height;

      // Source canvas parameters
      var srcX = initialX;
      var srcY = initialY;
      var srcWidth;
      var srcHeight;

      // Destination canvas parameters
      var dstX;
      var dstY;
      var dstWidth;
      var dstHeight;
      if (srcX <= -initialWidth || srcX > sourceWidth) {
        srcX = 0;
        srcWidth = 0;
        dstX = 0;
        dstWidth = 0;
      } else if (srcX <= 0) {
        dstX = -srcX;
        srcX = 0;
        srcWidth = Math.min(sourceWidth, initialWidth + srcX);
        dstWidth = srcWidth;
      } else if (srcX <= sourceWidth) {
        dstX = 0;
        srcWidth = Math.min(initialWidth, sourceWidth - srcX);
        dstWidth = srcWidth;
      }
      if (srcWidth <= 0 || srcY <= -initialHeight || srcY > sourceHeight) {
        srcY = 0;
        srcHeight = 0;
        dstY = 0;
        dstHeight = 0;
      } else if (srcY <= 0) {
        dstY = -srcY;
        srcY = 0;
        srcHeight = Math.min(sourceHeight, initialHeight + srcY);
        dstHeight = srcHeight;
      } else if (srcY <= sourceHeight) {
        dstY = 0;
        srcHeight = Math.min(initialHeight, sourceHeight - srcY);
        dstHeight = srcHeight;
      }
      var params = [srcX, srcY, srcWidth, srcHeight];

      // Avoid "IndexSizeError"
      if (dstWidth > 0 && dstHeight > 0) {
        var scale = width / initialWidth;
        params.push(dstX * scale, dstY * scale, dstWidth * scale, dstHeight * scale);
      }

      // All the numerical parameters should be integer for `drawImage`
      // https://github.com/fengyuanchen/cropper/issues/476
      context.drawImage.apply(context, [source].concat(_toConsumableArray(params.map(function (param) {
        return Math.floor(normalizeDecimalNumber(param));
      }))));
      return canvas;
    },
    /**
     * Change the aspect ratio of the crop box.
     * @param {number} aspectRatio - The new aspect ratio.
     * @returns {Cropper} this
     */
    setAspectRatio: function setAspectRatio(aspectRatio) {
      var options = this.options;
      if (!this.disabled && !isUndefined(aspectRatio)) {
        // 0 -> NaN
        options.aspectRatio = Math.max(0, aspectRatio) || NaN;
        if (this.ready) {
          this.initCropBox();
          if (this.cropped) {
            this.renderCropBox();
          }
        }
      }
      return this;
    },
    /**
     * Change the drag mode.
     * @param {string} mode - The new drag mode.
     * @returns {Cropper} this
     */
    setDragMode: function setDragMode(mode) {
      var options = this.options,
        dragBox = this.dragBox,
        face = this.face;
      if (this.ready && !this.disabled) {
        var croppable = mode === DRAG_MODE_CROP;
        var movable = options.movable && mode === DRAG_MODE_MOVE;
        mode = croppable || movable ? mode : DRAG_MODE_NONE;
        options.dragMode = mode;
        setData(dragBox, DATA_ACTION, mode);
        toggleClass(dragBox, CLASS_CROP, croppable);
        toggleClass(dragBox, CLASS_MOVE, movable);
        if (!options.cropBoxMovable) {
          // Sync drag mode to crop box when it is not movable
          setData(face, DATA_ACTION, mode);
          toggleClass(face, CLASS_CROP, croppable);
          toggleClass(face, CLASS_MOVE, movable);
        }
      }
      return this;
    }
  };

  var AnotherCropper = WINDOW.Cropper;
  var Cropper = /*#__PURE__*/function () {
    /**
     * Create a new Cropper.
     * @param {Element} element - The target element for cropping.
     * @param {Object} [options={}] - The configuration options.
     */
    function Cropper(element) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      _classCallCheck(this, Cropper);
      if (!element || !REGEXP_TAG_NAME.test(element.tagName)) {
        throw new Error('The first argument is required and must be an <img> or <canvas> element.');
      }
      this.element = element;
      this.options = assign({}, DEFAULTS, isPlainObject(options) && options);
      this.cropped = false;
      this.disabled = false;
      this.pointers = {};
      this.ready = false;
      this.reloading = false;
      this.replaced = false;
      this.sized = false;
      this.sizing = false;
      this.init();
    }
    return _createClass(Cropper, [{
      key: "init",
      value: function init() {
        var element = this.element;
        var tagName = element.tagName.toLowerCase();
        var url;
        if (element[NAMESPACE]) {
          return;
        }
        element[NAMESPACE] = this;
        if (tagName === 'img') {
          this.isImg = true;

          // e.g.: "img/picture.jpg"
          url = element.getAttribute('src') || '';
          this.originalUrl = url;

          // Stop when it's a blank image
          if (!url) {
            return;
          }

          // e.g.: "https://example.com/img/picture.jpg"
          url = element.src;
        } else if (tagName === 'canvas' && window.HTMLCanvasElement) {
          url = element.toDataURL();
        }
        this.load(url);
      }
    }, {
      key: "load",
      value: function load(url) {
        var _this = this;
        if (!url) {
          return;
        }
        this.url = url;
        this.imageData = {};
        var element = this.element,
          options = this.options;
        if (!options.rotatable && !options.scalable) {
          options.checkOrientation = false;
        }

        // Only IE10+ supports Typed Arrays
        if (!options.checkOrientation || !window.ArrayBuffer) {
          this.clone();
          return;
        }

        // Detect the mime type of the image directly if it is a Data URL
        if (REGEXP_DATA_URL.test(url)) {
          // Read ArrayBuffer from Data URL of JPEG images directly for better performance
          if (REGEXP_DATA_URL_JPEG.test(url)) {
            this.read(dataURLToArrayBuffer(url));
          } else {
            // Only a JPEG image may contains Exif Orientation information,
            // the rest types of Data URLs are not necessary to check orientation at all.
            this.clone();
          }
          return;
        }

        // 1. Detect the mime type of the image by a XMLHttpRequest.
        // 2. Load the image as ArrayBuffer for reading orientation if its a JPEG image.
        var xhr = new XMLHttpRequest();
        var clone = this.clone.bind(this);
        this.reloading = true;
        this.xhr = xhr;

        // 1. Cross origin requests are only supported for protocol schemes:
        // http, https, data, chrome, chrome-extension.
        // 2. Access to XMLHttpRequest from a Data URL will be blocked by CORS policy
        // in some browsers as IE11 and Safari.
        xhr.onabort = clone;
        xhr.onerror = clone;
        xhr.ontimeout = clone;
        xhr.onprogress = function () {
          // Abort the request directly if it not a JPEG image for better performance
          if (xhr.getResponseHeader('content-type') !== MIME_TYPE_JPEG) {
            xhr.abort();
          }
        };
        xhr.onload = function () {
          _this.read(xhr.response);
        };
        xhr.onloadend = function () {
          _this.reloading = false;
          _this.xhr = null;
        };

        // Bust cache when there is a "crossOrigin" property to avoid browser cache error
        if (options.checkCrossOrigin && isCrossOriginURL(url) && element.crossOrigin) {
          url = addTimestamp(url);
        }

        // The third parameter is required for avoiding side-effect (#682)
        xhr.open('GET', url, true);
        xhr.responseType = 'arraybuffer';
        xhr.withCredentials = element.crossOrigin === 'use-credentials';
        xhr.send();
      }
    }, {
      key: "read",
      value: function read(arrayBuffer) {
        var options = this.options,
          imageData = this.imageData;

        // Reset the orientation value to its default value 1
        // as some iOS browsers will render image with its orientation
        var orientation = resetAndGetOrientation(arrayBuffer);
        var rotate = 0;
        var scaleX = 1;
        var scaleY = 1;
        if (orientation > 1) {
          // Generate a new URL which has the default orientation value
          this.url = arrayBufferToDataURL(arrayBuffer, MIME_TYPE_JPEG);
          var _parseOrientation = parseOrientation(orientation);
          rotate = _parseOrientation.rotate;
          scaleX = _parseOrientation.scaleX;
          scaleY = _parseOrientation.scaleY;
        }
        if (options.rotatable) {
          imageData.rotate = rotate;
        }
        if (options.scalable) {
          imageData.scaleX = scaleX;
          imageData.scaleY = scaleY;
        }
        this.clone();
      }
    }, {
      key: "clone",
      value: function clone() {
        var element = this.element,
          url = this.url;
        var crossOrigin = element.crossOrigin;
        var crossOriginUrl = url;
        if (this.options.checkCrossOrigin && isCrossOriginURL(url)) {
          if (!crossOrigin) {
            crossOrigin = 'anonymous';
          }

          // Bust cache when there is not a "crossOrigin" property (#519)
          crossOriginUrl = addTimestamp(url);
        }
        this.crossOrigin = crossOrigin;
        this.crossOriginUrl = crossOriginUrl;
        var image = document.createElement('img');
        if (crossOrigin) {
          image.crossOrigin = crossOrigin;
        }
        image.src = crossOriginUrl || url;
        image.alt = element.alt || 'The image to crop';
        this.image = image;
        image.onload = this.start.bind(this);
        image.onerror = this.stop.bind(this);
        addClass(image, CLASS_HIDE);
        element.parentNode.insertBefore(image, element.nextSibling);
      }
    }, {
      key: "start",
      value: function start() {
        var _this2 = this;
        var image = this.image;
        image.onload = null;
        image.onerror = null;
        this.sizing = true;

        // Match all browsers that use WebKit as the layout engine in iOS devices,
        // such as Safari for iOS, Chrome for iOS, and in-app browsers.
        var isIOSWebKit = WINDOW.navigator && /(?:iPad|iPhone|iPod).*?AppleWebKit/i.test(WINDOW.navigator.userAgent);
        var done = function done(naturalWidth, naturalHeight) {
          assign(_this2.imageData, {
            naturalWidth: naturalWidth,
            naturalHeight: naturalHeight,
            aspectRatio: naturalWidth / naturalHeight
          });
          _this2.initialImageData = assign({}, _this2.imageData);
          _this2.sizing = false;
          _this2.sized = true;
          _this2.build();
        };

        // Most modern browsers (excepts iOS WebKit)
        if (image.naturalWidth && !isIOSWebKit) {
          done(image.naturalWidth, image.naturalHeight);
          return;
        }
        var sizingImage = document.createElement('img');
        var body = document.body || document.documentElement;
        this.sizingImage = sizingImage;
        sizingImage.onload = function () {
          done(sizingImage.width, sizingImage.height);
          if (!isIOSWebKit) {
            body.removeChild(sizingImage);
          }
        };
        sizingImage.src = image.src;

        // iOS WebKit will convert the image automatically
        // with its orientation once append it into DOM (#279)
        if (!isIOSWebKit) {
          sizingImage.style.cssText = 'left:0;' + 'max-height:none!important;' + 'max-width:none!important;' + 'min-height:0!important;' + 'min-width:0!important;' + 'opacity:0;' + 'position:absolute;' + 'top:0;' + 'z-index:-1;';
          body.appendChild(sizingImage);
        }
      }
    }, {
      key: "stop",
      value: function stop() {
        var image = this.image;
        image.onload = null;
        image.onerror = null;
        image.parentNode.removeChild(image);
        this.image = null;
      }
    }, {
      key: "build",
      value: function build() {
        if (!this.sized || this.ready) {
          return;
        }
        var element = this.element,
          options = this.options,
          image = this.image;

        // Create cropper elements
        var container = element.parentNode;
        var template = document.createElement('div');
        template.innerHTML = TEMPLATE;
        var cropper = template.querySelector(".".concat(NAMESPACE, "-container"));
        var canvas = cropper.querySelector(".".concat(NAMESPACE, "-canvas"));
        var dragBox = cropper.querySelector(".".concat(NAMESPACE, "-drag-box"));
        var cropBox = cropper.querySelector(".".concat(NAMESPACE, "-crop-box"));
        var face = cropBox.querySelector(".".concat(NAMESPACE, "-face"));
        this.container = container;
        this.cropper = cropper;
        this.canvas = canvas;
        this.dragBox = dragBox;
        this.cropBox = cropBox;
        this.viewBox = cropper.querySelector(".".concat(NAMESPACE, "-view-box"));
        this.face = face;
        canvas.appendChild(image);

        // Hide the original image
        addClass(element, CLASS_HIDDEN);

        // Inserts the cropper after to the current image
        container.insertBefore(cropper, element.nextSibling);

        // Show the hidden image
        removeClass(image, CLASS_HIDE);
        this.initPreview();
        this.bind();
        options.initialAspectRatio = Math.max(0, options.initialAspectRatio) || NaN;
        options.aspectRatio = Math.max(0, options.aspectRatio) || NaN;
        options.viewMode = Math.max(0, Math.min(3, Math.round(options.viewMode))) || 0;
        addClass(cropBox, CLASS_HIDDEN);
        if (!options.guides) {
          addClass(cropBox.getElementsByClassName("".concat(NAMESPACE, "-dashed")), CLASS_HIDDEN);
        }
        if (!options.center) {
          addClass(cropBox.getElementsByClassName("".concat(NAMESPACE, "-center")), CLASS_HIDDEN);
        }
        if (options.background) {
          addClass(cropper, "".concat(NAMESPACE, "-bg"));
        }
        if (!options.highlight) {
          addClass(face, CLASS_INVISIBLE);
        }
        if (options.cropBoxMovable) {
          addClass(face, CLASS_MOVE);
          setData(face, DATA_ACTION, ACTION_ALL);
        }
        if (!options.cropBoxResizable) {
          addClass(cropBox.getElementsByClassName("".concat(NAMESPACE, "-line")), CLASS_HIDDEN);
          addClass(cropBox.getElementsByClassName("".concat(NAMESPACE, "-point")), CLASS_HIDDEN);
        }
        this.render();
        this.ready = true;
        this.setDragMode(options.dragMode);
        if (options.autoCrop) {
          this.crop();
        }
        this.setData(options.data);
        if (isFunction(options.ready)) {
          addListener(element, EVENT_READY, options.ready, {
            once: true
          });
        }
        dispatchEvent(element, EVENT_READY);
      }
    }, {
      key: "unbuild",
      value: function unbuild() {
        if (!this.ready) {
          return;
        }
        this.ready = false;
        this.unbind();
        this.resetPreview();
        var parentNode = this.cropper.parentNode;
        if (parentNode) {
          parentNode.removeChild(this.cropper);
        }
        removeClass(this.element, CLASS_HIDDEN);
      }
    }, {
      key: "uncreate",
      value: function uncreate() {
        if (this.ready) {
          this.unbuild();
          this.ready = false;
          this.cropped = false;
        } else if (this.sizing) {
          this.sizingImage.onload = null;
          this.sizing = false;
          this.sized = false;
        } else if (this.reloading) {
          this.xhr.onabort = null;
          this.xhr.abort();
        } else if (this.image) {
          this.stop();
        }
      }

      /**
       * Get the no conflict cropper class.
       * @returns {Cropper} The cropper class.
       */
    }], [{
      key: "noConflict",
      value: function noConflict() {
        window.Cropper = AnotherCropper;
        return Cropper;
      }

      /**
       * Change the default options.
       * @param {Object} options - The new default options.
       */
    }, {
      key: "setDefaults",
      value: function setDefaults(options) {
        assign(DEFAULTS, isPlainObject(options) && options);
      }
    }]);
  }();
  assign(Cropper.prototype, render, preview, events, handlers, change, methods);

  return Cropper;

}));

/*!
 * Toastify js 1.12.0
 * https://github.com/apvarun/toastify-js
 * @license MIT licensed
 *
 * Copyright (C) 2018 Varun A P
 */
(function(root, factory) {
  if (typeof module === "object" && module.exports) {
    module.exports = factory();
  } else {
    root.Toastify = factory();
  }
})(this, function(global) {
  // Object initialization
  var Toastify = function(options) {
      // Returning a new init object
      return new Toastify.lib.init(options);
    },
    // Library version
    version = "1.12.0";

  // Set the default global options
  Toastify.defaults = {
    oldestFirst: true,
    text: "Toastify is awesome!",
    node: undefined,
    duration: 3000,
    selector: undefined,
    callback: function () {
    },
    destination: undefined,
    newWindow: false,
    close: false,
    gravity: "toastify-top",
    positionLeft: false,
    position: '',
    backgroundColor: '',
    avatar: "",
    className: "",
    stopOnFocus: true,
    onClick: function () {
    },
    offset: {x: 0, y: 0},
    escapeMarkup: true,
    ariaLive: 'polite',
    style: {background: ''}
  };

  // Defining the prototype of the object
  Toastify.lib = Toastify.prototype = {
    toastify: version,

    constructor: Toastify,

    // Initializing the object with required parameters
    init: function(options) {
      // Verifying and validating the input object
      if (!options) {
        options = {};
      }

      // Creating the options object
      this.options = {};

      this.toastElement = null;

      // Validating the options
      this.options.text = options.text || Toastify.defaults.text; // Display message
      this.options.node = options.node || Toastify.defaults.node;  // Display content as node
      this.options.duration = options.duration === 0 ? 0 : options.duration || Toastify.defaults.duration; // Display duration
      this.options.selector = options.selector || Toastify.defaults.selector; // Parent selector
      this.options.callback = options.callback || Toastify.defaults.callback; // Callback after display
      this.options.destination = options.destination || Toastify.defaults.destination; // On-click destination
      this.options.newWindow = options.newWindow || Toastify.defaults.newWindow; // Open destination in new window
      this.options.close = options.close || Toastify.defaults.close; // Show toast close icon
      this.options.gravity = options.gravity === "bottom" ? "toastify-bottom" : Toastify.defaults.gravity; // toast position - top or bottom
      this.options.positionLeft = options.positionLeft || Toastify.defaults.positionLeft; // toast position - left or right
      this.options.position = options.position || Toastify.defaults.position; // toast position - left or right
      this.options.backgroundColor = options.backgroundColor || Toastify.defaults.backgroundColor; // toast background color
      this.options.avatar = options.avatar || Toastify.defaults.avatar; // img element src - url or a path
      this.options.className = options.className || Toastify.defaults.className; // additional class names for the toast
      this.options.stopOnFocus = options.stopOnFocus === undefined ? Toastify.defaults.stopOnFocus : options.stopOnFocus; // stop timeout on focus
      this.options.onClick = options.onClick || Toastify.defaults.onClick; // Callback after click
      this.options.offset = options.offset || Toastify.defaults.offset; // toast offset
      this.options.escapeMarkup = options.escapeMarkup !== undefined ? options.escapeMarkup : Toastify.defaults.escapeMarkup;
      this.options.ariaLive = options.ariaLive || Toastify.defaults.ariaLive;
      this.options.style = options.style || Toastify.defaults.style;
      if(options.backgroundColor) {
        this.options.style.background = options.backgroundColor;
      }

      // Returning the current object for chaining functions
      return this;
    },

    // Building the DOM element
    buildToast: function() {
      // Validating if the options are defined
      if (!this.options) {
        throw "Toastify is not initialized";
      }

      // Creating the DOM object
      var divElement = document.createElement("div");
      divElement.className = "toastify on " + this.options.className;

      // Positioning toast to left or right or center
      if (!!this.options.position) {
        divElement.className += " toastify-" + this.options.position;
      } else {
        // To be depreciated in further versions
        if (this.options.positionLeft === true) {
          divElement.className += " toastify-left";
          console.warn('Property `positionLeft` will be depreciated in further versions. Please use `position` instead.')
        } else {
          // Default position
          divElement.className += " toastify-right";
        }
      }

      // Assigning gravity of element
      divElement.className += " " + this.options.gravity;

      if (this.options.backgroundColor) {
        // This is being deprecated in favor of using the style HTML DOM property
        console.warn('DEPRECATION NOTICE: "backgroundColor" is being deprecated. Please use the "style.background" property.');
      }

      // Loop through our style object and apply styles to divElement
      for (var property in this.options.style) {
        divElement.style[property] = this.options.style[property];
      }

      // Announce the toast to screen readers
      if (this.options.ariaLive) {
        divElement.setAttribute('aria-live', this.options.ariaLive)
      }

      // Adding the toast message/node
      if (this.options.node && this.options.node.nodeType === Node.ELEMENT_NODE) {
        // If we have a valid node, we insert it
        divElement.appendChild(this.options.node)
      } else {
        if (this.options.escapeMarkup) {
          divElement.innerText = this.options.text;
        } else {
          divElement.innerHTML = this.options.text;
        }

        if (this.options.avatar !== "") {
          var avatarElement = document.createElement("img");
          avatarElement.src = this.options.avatar;

          avatarElement.className = "toastify-avatar";

          if (this.options.position == "left" || this.options.positionLeft === true) {
            // Adding close icon on the left of content
            divElement.appendChild(avatarElement);
          } else {
            // Adding close icon on the right of content
            divElement.insertAdjacentElement("afterbegin", avatarElement);
          }
        }
      }

      // Adding a close icon to the toast
      if (this.options.close === true) {
        // Create a span for close element
        var closeElement = document.createElement("button");
        closeElement.type = "button";
        closeElement.setAttribute("aria-label", "Close");
        closeElement.className = "toast-close";
        closeElement.innerHTML = "&#10006;";

        // Triggering the removal of toast from DOM on close click
        closeElement.addEventListener(
          "click",
          function(event) {
            event.stopPropagation();
            this.removeElement(this.toastElement);
            window.clearTimeout(this.toastElement.timeOutValue);
          }.bind(this)
        );

        //Calculating screen width
        var width = window.innerWidth > 0 ? window.innerWidth : screen.width;

        // Adding the close icon to the toast element
        // Display on the right if screen width is less than or equal to 360px
        if ((this.options.position == "left" || this.options.positionLeft === true) && width > 360) {
          // Adding close icon on the left of content
          divElement.insertAdjacentElement("afterbegin", closeElement);
        } else {
          // Adding close icon on the right of content
          divElement.appendChild(closeElement);
        }
      }

      // Clear timeout while toast is focused
      if (this.options.stopOnFocus && this.options.duration > 0) {
        var self = this;
        // stop countdown
        divElement.addEventListener(
          "mouseover",
          function(event) {
            window.clearTimeout(divElement.timeOutValue);
          }
        )
        // add back the timeout
        divElement.addEventListener(
          "mouseleave",
          function() {
            divElement.timeOutValue = window.setTimeout(
              function() {
                // Remove the toast from DOM
                self.removeElement(divElement);
              },
              self.options.duration
            )
          }
        )
      }

      // Adding an on-click destination path
      if (typeof this.options.destination !== "undefined") {
        divElement.addEventListener(
          "click",
          function(event) {
            event.stopPropagation();
            if (this.options.newWindow === true) {
              window.open(this.options.destination, "_blank");
            } else {
              window.location = this.options.destination;
            }
          }.bind(this)
        );
      }

      if (typeof this.options.onClick === "function" && typeof this.options.destination === "undefined") {
        divElement.addEventListener(
          "click",
          function(event) {
            event.stopPropagation();
            this.options.onClick();
          }.bind(this)
        );
      }

      // Adding offset
      if(typeof this.options.offset === "object") {

        var x = getAxisOffsetAValue("x", this.options);
        var y = getAxisOffsetAValue("y", this.options);

        var xOffset = this.options.position == "left" ? x : "-" + x;
        var yOffset = this.options.gravity == "toastify-top" ? y : "-" + y;

        divElement.style.transform = "translate(" + xOffset + "," + yOffset + ")";

      }

      // Returning the generated element
      return divElement;
    },

    // Displaying the toast
    showToast: function() {
      // Creating the DOM object for the toast
      this.toastElement = this.buildToast();

      // Getting the root element to with the toast needs to be added
      var rootElement;
      if (typeof this.options.selector === "string") {
        rootElement = document.getElementById(this.options.selector);
      } else if (this.options.selector instanceof HTMLElement || (typeof ShadowRoot !== 'undefined' && this.options.selector instanceof ShadowRoot)) {
        rootElement = this.options.selector;
      } else {
        rootElement = document.body;
      }

      // Validating if root element is present in DOM
      if (!rootElement) {
        throw "Root element is not defined";
      }

      // Adding the DOM element
      var elementToInsert = Toastify.defaults.oldestFirst ? rootElement.firstChild : rootElement.lastChild;
      rootElement.insertBefore(this.toastElement, elementToInsert);

      // Repositioning the toasts in case multiple toasts are present
      Toastify.reposition();

      if (this.options.duration > 0) {
        this.toastElement.timeOutValue = window.setTimeout(
          function() {
            // Remove the toast from DOM
            this.removeElement(this.toastElement);
          }.bind(this),
          this.options.duration
        ); // Binding `this` for function invocation
      }

      // Supporting function chaining
      return this;
    },

    hideToast: function() {
      if (this.toastElement.timeOutValue) {
        clearTimeout(this.toastElement.timeOutValue);
      }
      this.removeElement(this.toastElement);
    },

    // Removing the element from the DOM
    removeElement: function(toastElement) {
      // Hiding the element
      // toastElement.classList.remove("on");
      toastElement.className = toastElement.className.replace(" on", "");

      // Removing the element from DOM after transition end
      window.setTimeout(
        function() {
          // remove options node if any
          if (this.options.node && this.options.node.parentNode) {
            this.options.node.parentNode.removeChild(this.options.node);
          }

          // Remove the element from the DOM, only when the parent node was not removed before.
          if (toastElement.parentNode) {
            toastElement.parentNode.removeChild(toastElement);
          }

          // Calling the callback function
          this.options.callback.call(toastElement);

          // Repositioning the toasts again
          Toastify.reposition();
        }.bind(this),
        400
      ); // Binding `this` for function invocation
    },
  };

  // Positioning the toasts on the DOM
  Toastify.reposition = function() {

    // Top margins with gravity
    var topLeftOffsetSize = {
      top: 15,
      bottom: 15,
    };
    var topRightOffsetSize = {
      top: 15,
      bottom: 15,
    };
    var offsetSize = {
      top: 15,
      bottom: 15,
    };

    // Get all toast messages on the DOM
    var allToasts = document.getElementsByClassName("toastify");

    var classUsed;

    // Modifying the position of each toast element
    for (var i = 0; i < allToasts.length; i++) {
      // Getting the applied gravity
      if (containsClass(allToasts[i], "toastify-top") === true) {
        classUsed = "toastify-top";
      } else {
        classUsed = "toastify-bottom";
      }

      var height = allToasts[i].offsetHeight;
      classUsed = classUsed.substr(9, classUsed.length-1)
      // Spacing between toasts
      var offset = 15;

      var width = window.innerWidth > 0 ? window.innerWidth : screen.width;

      // Show toast in center if screen with less than or equal to 360px
      if (width <= 360) {
        // Setting the position
        allToasts[i].style[classUsed] = offsetSize[classUsed] + "px";

        offsetSize[classUsed] += height + offset;
      } else {
        if (containsClass(allToasts[i], "toastify-left") === true) {
          // Setting the position
          allToasts[i].style[classUsed] = topLeftOffsetSize[classUsed] + "px";

          topLeftOffsetSize[classUsed] += height + offset;
        } else {
          // Setting the position
          allToasts[i].style[classUsed] = topRightOffsetSize[classUsed] + "px";

          topRightOffsetSize[classUsed] += height + offset;
        }
      }
    }

    // Supporting function chaining
    return this;
  };

  // Helper function to get offset.
  function getAxisOffsetAValue(axis, options) {

    if(options.offset[axis]) {
      if(isNaN(options.offset[axis])) {
        return options.offset[axis];
      }
      else {
        return options.offset[axis] + 'px';
      }
    }

    return '0px';

  }

  function containsClass(elem, yourClass) {
    if (!elem || typeof yourClass !== "string") {
      return false;
    } else if (
      elem.className &&
      elem.className
        .trim()
        .split(/\s+/gi)
        .indexOf(yourClass) > -1
    ) {
      return true;
    } else {
      return false;
    }
  }

  // Setting up the prototype for the init object
  Toastify.lib.init.prototype = Toastify.lib;

  // Returning the Toastify function to be assigned to the window object/module
  return Toastify;
});

/**
 * # Styling
 * 
 * `<ink-ripple>` provides the following custom properties and mixins
 * for styling:
 * 
 * 
 * Custom property | Description | Default
 * ----------------|-------------|----------
 * `--ink-ripple-accent-color` | black
 * `--ink-ripple-opacity` | Opacity of the ripple | 0.15
 * `--ink-ripple-duration` | Animation duration of ripple | 0.3s
 * 
 */
class InkRipple extends HTMLElement {

  constructor() {
    super();

    this.attachShadow({
      mode: 'open'
    });
    this.shadowRoot.appendChild(InkRipple.template.cloneNode(true));

    this.cssAdded = false;

    let root = this.shadowRoot;

    this.styles = root.querySelector('style');
  }

  static get template() {
    if(this.fragment) return this.fragment;

    const fragment = document.createDocumentFragment();
    let styles = document.createElement('style');
    styles.innerHTML = `
      :host {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        overflow: hidden;

        --ink-ripple-accent-color: black;
        --ink-ripple-opacity: 0.15;
        --ink-ripple-duration: 0.3s;
      }
      :host::before {
        position: absolute;
        display: block;
        content: '';

        border-radius: 50%;
        background: var(--ink-ripple-accent-color);
        opacity: 0;
        transform: scale(0);
        
      }

      :host([animatable])::before {
        transition: opacity .1s linear, transform var(--ink-ripple-duration) linear;
      }

      :host([mouseup][animatable])::before {
        transition: opacity .4s linear, transform .2s linear;
      }

      :host([mousedown])::before {
        opacity: var(--ink-ripple-opacity);
        transform: scale(1);
      }

      :host([mouseup]:not([mousedown]))::before {
        opacity: 0;
        transform: scale(1);
      }
    `;

    fragment.appendChild(styles);

    this.fragment = fragment;

    return fragment;
  }

  set animatable(val) {
    if (val) {
      this.setAttribute('animatable', '');
    } else {
      this.removeAttribute('animatable');
    }
  }

  set mousedown(val) {
    if (val) {
      this.setAttribute('mousedown', '');
    } else {
      this.removeAttribute('mousedown');
    }
  }

  set mouseup(val) {
    if (val) {
      this.setAttribute('mouseup', '');
    } else {
      this.removeAttribute('mouseup');
    }
  }

  set disabled(val) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  get mouseup() {
    return this.hasAttribute('mouseup');
  }

  get mousedown() {
    return this.hasAttribute('mousedown');
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  connectedCallback() {
    // event listeners
    this.addEventListener('mousedown', (e) => {
      if (this.disabled) {
        return;
      }
      this._triggerRippleIn(e.offsetX, e.offsetY);
    });

    document.documentElement.addEventListener('mouseup', e => {
      if (this.disabled) {
        return;
      }
      this._triggerRippleOut();
    });
  }

  /**
   * Simulates a ripple effect at the given parameter coordinates.
   * 
   * If missing one or both parameters, ripple originates from center
   * 
   * @param {number} x The X coordinate of the ripple origin
   * @param {number} y The Y coordinate of the ripple origin
   */
  simulateRipple(x = false, y = false) {

    if (!x || !y) {
      const rect = this.getBoundingClientRect();
      x = rect.width / 2;
      y = rect.height / 2;
    }

    this._triggerRippleIn(x, y);

    this.addEventListener('transitionend', (e) => {
      if (e.propertyName === 'transform' ) {
        this._triggerRippleOut();
      }
    });

  }

  _fadeOut() {
      this.mousedown = false;

      // not strictly needed, just cleans up the element
      requestAnimationFrame(() => {
        this.addEventListener('transitionend', this._transitionOutEnd);
      });
  }

  _removeCSS() {
    this.styles.sheet.deleteRule(0);
    this.cssAdded = false;
  }

  _reset() {
    this.animatable = false;
    this.mousedown = false;
    this.mouseup = false;

    this.removeEventListener('transitionend', this._transitionOutEnd);
    this.removeEventListener('transitionend', this._transitionInEnd);

    if (this.cssAdded) {
      this._removeCSS();
    }

    this.transitionInOver = false;
    this.transitionOutOver = false;
  }

  _transitionInEnd(evt) {
    if (evt.pseudoElement) {
     if (evt.propertyName === 'transform' && !this.transitionInOver) {

        this.removeEventListener('transitionend', this._transitionInEnd);

        this.transitionInOver = true;

        if (this.mouseup) {
          this._fadeOut();
        }
     }
    }
  }

  _transitionOutEnd(evt) {
    if (evt.pseudoElement) {
      if (evt.propertyName === 'opacity' ) {

        this.transitionOutOver = true;
        
        this._reset();
      }
    }
  }

  _positionPseduoElement(x, y) {
    var rect = this.getBoundingClientRect();
    var height = rect.height;
    var width = rect.width;

    var largest = Math.max(height, width);

    width = largest * 2 + (largest / 2);
    height = largest * 2 + (largest / 2);

    var xPos = x - (width / 2);
    var yPos = y - (height / 2);

    var selector = ':host:before';
    var rule = 'left: ' + xPos + 'px; top: ' + yPos + 'px; width: ' + width + 'px; height: ' + height + 'px;';
    
    // document.styleSheets[0].addRule(selector, rule);
    this.styles.sheet.insertRule(selector + ' { ' + rule + ' } ', 0);

    this.cssAdded = true;
  }

  _triggerRippleIn(offsetX, offsetY) {

    this._reset();

    this._positionPseduoElement(offsetX, offsetY);

    requestAnimationFrame(() => {
      this.addEventListener('transitionend', this._transitionInEnd);
      this.animatable = true;
      this.mousedown = true;
    });
  }

  _triggerRippleOut() {
    if (this.transitionOutOver || !this.mousedown) {
      return;
    }

    this.mouseup = true;

    if (this.transitionInOver) {
      this._fadeOut();
    }
  }

}

customElements.define('ink-ripple', InkRipple);
;(function(undefined) {

/**
 * ES6 Promise object.
 * @external Promise
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise|Promise API}
 * @see {@link http://www.ecma-international.org/ecma-262/6.0/#sec-promise-objects|Promise spec}
 */

/**
 * ISO 8601 date format.
 * @external ISO 8601
 * @see {@link https://en.wikipedia.org/wiki/ISO_8601| ISO 8601 spec}
 */

/**
 * This is the root namespace for the Flybits SDK.  If included in a browser environment it will be accessible from the `window` object.
 * @namespace
 */
var Flybits = {
  /**
   * Promise that resolves when the SDK has completed initialization.
   * @memberof Flybits
   * @member {external:Promise}
   */
  ready: null,
  /**
   * SDK state related utilities.
   * @namespace
   */
  store: {
    Property: {},
    Record: {},
    _storageEngines: {}
  },
  /**
   * Reporting manager to facilitate the reporting of application events.
   * @namespace
   * @memberof Flybits
   */
  analytics: {},
  /**
   * Utility classes and reporting manager for collecting and reporting context about a user.
   * @namespace
   * @memberof Flybits
   */
  context: {},
  /**
   * Flybits API wrappers for core models
   * @namespace
   */
  api: {
    /**
     * Object that represents the paging status of a API query.
     * @typedef Paging
     * @memberof Flybits.api
     * @type Object
     * @property {number} offset Offset from first element of first page.
     * @property {number} limit Number of results per page.
     * @property {number} total Total number of objects that is available to page over.
     */
    /**
      * Result Object that is returned from a successful API query if the query is for multiple models.
      * @typedef Result
      * @memberof Flybits.api
      * @type Object
      * @property {BaseModel[]} result Resultant list of models from API request.
      * @property {function} [nextPageFn] If the resultant list of models is only a subset of total records, that is to say the paged result has additional pages, this function can be invoked to request the next page of results based on the initial query parameters.  If this is `undefined` there are no more pages to view.
      * @property {Flybits.api.Paging} paging Paging details of request.
      */
  },
  initFile: {},
  /**
   * Utilities for various aspects of the SDK
   * @namespace
   */
  util: {},
  /**
   * Interfaces to ensure SDK model integrity.
   * @namespace
   */
  interface: {},
  /**
   * Identity providers available for authentication.
   * @namespace
   */
  idp: {},
  /**
   * Event bus primarily for subscribing to SDK events.
   * @namespace
   */
  evt: {
    /**
     * Event payload for a {@link Flybits.evt.type.CTXREPORT | CTXREPORT} event.
     * @typedef CtxReportEvent
     * @memberof Flybits.evt
     * @type Object
     * @property {string} status "success" or "failed" status of Context Data report attempt.
     * @property {Flybits.context.Manager.CtxReport[]} data Context Data objects that the attempt report was made for.
     * @property {Flybits.Validation} [validation] Error objects should the report have failed.
     */
    /**
     * @member {Object} type A mapping of SDK event types.
     * @memberof Flybits.evt
     * @constant
     * @property {string} SESSIONSTART=sessionStart This event occurs whenever the SDK is connected to a Flybits tenant (initialized with a Flybits access token).
     * @property {string} SESSIONEND=sessionEnd This event occurs whenever the SDK is disconnected from a Flybits tenant (flushed of its Flybits access token).
     * @property {string} CTXREPORT=ctxreported This event occurs whenever the {@link Flybits.context.Manager | Context Manager} attempts to report locally batched Context Data. {@link Flybits.evt.CtxReportEvent | Event payload spec}.
     */
    type: {
      SESSIONSTART: 'sessionStart',
      SESSIONEND: 'sessionEnd',
      CTXREPORT: 'ctxreported'
    },
    handlerMap: {}
  },
};

//defaults
Flybits.cfg = {
  HOST: 'http://api.flybits.com',
  CTXREPORTDELAY: 60000,
  analytics: {
    REPORTDELAY: 3600000,
    REPORTDELAYUNIT: 'milliseconds',
    MAXSTORESIZE: 100
  },
  store: {
    SDKPROPS: 'flb.sdk.properties',
    RESOURCEPATH: './res',
    DEVICEID: 'flb_device',
    USERTOKEN: 'flb_usertoken',
    USER: 'flb_user',
    USERTOKENEXP: 'flb_usertoken_expiry',
    PROJECTID: 'flb_projectid',
    IDPTYPE: 'flb_idptype',
    ANALYTICSLASTREPORTED: 'flb_analytics_lastreported',
    ANALYTICSLASTREPORTATTEMPTED: 'flb_analytics_lastreportattempted',
    CONTEXTLASTREPORTED: 'flb_context_lastreported',
    CONTEXTLASTREPORTATTEMPTED: 'flb_context_lastreportattempted',
  },
  res: {
    CONTEXT: '/context/ctxdata',
    UNAUTHENTICATE: '/sso/auth/logout',
    AUTHENTICATE: '/sso/auth/authenticate',
    ANONYMOUSAUTH: '/sso/auth/anonymous',
    OAUTH: '/sso/auth/oauth',
    OIDC: '/sso/auth/oidc',
    SIGNEDLOGIN: '/sso/auth/signedLogin',
    JWTSIGNEDLOGIN: '/sso/auth/jwtlogin/sessions',
    REFRESHAUTH: '/sso/auth/refreshToken',
    AUTHUSER: '/sso/auth/me',
    AUTHPROJECT: '/sso/auth/project',
    REQRESETEMAIL: '/sso/auth/sendResetPasswordEmail',
    REQCONFIRMEMAIL: '/sso/auth/sendConfirmEmail',
    CONTENTS: '/kernel/content/instances',
    RELEVANTCONTENTS: '/kernel/experiences/contents',
    USERSETTINGS: '/kernel/userpref',
    ZONESCONFIGURATION: '/kernel/experiences/tenantzoneconfigurations',
    ZONESRELEVANTCONTENTS: '/kernel/experiences/zoneconfigurations/contents',
    PUSHTOKEN: '/push/token'
  },
};

/**
 * @memberof Flybits
 * @member {string} VERSION SDK version string
 */
Flybits.VERSION = '2.18.0:HEAD:5a323ce';

/**
 * Subscribes to SDK events.
 * @param {string} evtName The event key name to subscribe to. Supported events include: `sessionStart`,`sessionEnd`. Event names also support namespacing. For example, you can subscribe with an event key name `sessionStart.carousel`. All function handlers subscribed to `sessionStart` and `sessionStart.carousel` will receive callbacks upon the SDK session initialization. However this becomes useful when unsubscribing as it allows for unsubscription without providing the original `handler` function and without affecting other subscribers.
 * @param {function} handler Handler function to be invoked upon subscribed event occurrence.
 * @returns {Flybits.evt} Flybits.evt namespace allowing for chaining subscriptions and unsubscriptions.
 * ```
 * Flybits.evt.on('sessionStart', function(){
 *   // do stuff
 * }).on('sessionEnd', function(){
 *   // do stuff
 * })
 * ```
 * @throws {Flybits.Validation} {@link Flybits.Validation.type| MISSINGARG} When no `evtName` is provided.
 * @throws {Flybits.Validation} {@link Flybits.Validation.type| MISSINGARG} When no `handler` function is provided.
 * @throws {Flybits.Validation} {@link Flybits.Validation.type| INVALIDARG} When an `evtName` is provided that is not part of the internal supported {@link Flybits.evt.type|event types}
 */
Flybits.evt.on = function(evtName, handler){
  const availableEvtNames = Object.keys(Flybits.evt.type).map(function(evtKey){
    return Flybits.evt.type[evtKey];
  });
  const mainEvtName = evtName ? evtName.split('.')[0]: null;

  if(!mainEvtName){
    throw new Flybits.Validation().addError('Missing argument','Must provide event name to subscribe to.',{
      code: Flybits.Validation.type.MISSINGARG,
      context: 'evtName'
    });
  } else if(availableEvtNames.indexOf(mainEvtName) < 0){
    throw new Flybits.Validation().addError('Event name not supported','',{
      code: Flybits.Validation.type.INVALIDARG,
      context: mainEvtName
    });
  }

  if(!handler){
    throw new Flybits.Validation().addError('Missing argument', 'Must provide event handler function.',{
      code: Flybits.Validation.type.MISSINGARG,
      context: 'handler'
    });
  }

  if(!Flybits.evt.handlerMap[evtName]){
    Flybits.evt.handlerMap[evtName] = [];
  }

  if(Flybits.evt.handlerMap[evtName].indexOf(handler) < 0){
    Flybits.evt.handlerMap[evtName].push(handler);
  } else{
    console.warn('> handler already registered', evtName, handler);
  }
  return Flybits.evt;
}

/**
 * Unsubscribes to SDK events.
 * @param {string} evtName The event key name to unsubscribe to. Supported events include: `sessionStart`,`sessionEnd`.  Event names support namespacing.  For example if you subscribed with a namespaced event key, `sessionStart.carousel`, then you can unsubscribe via `sessionStart.carousel` without affecting other subscribers to the `sessionStart` event.
 * @param {function} [handler] Handler function that was used for subscription.  If provided, the exact handler function will be unsubscribed to specified `evtName`. If *not provided*, **all handler functions** subscribed to the specified `evtName` will be removed.
 * <br>**Note**: if you unsubscribe via a namespaced event key for example, `sessionStart.carousel` without a specified `handler`, all registered handlers will be removed for said namespaced event key. However if you unsubscribe via an non-namespaced event key and without a `handler`, then all registered handlers for all namespaces under the event key will be removed.
 * <br> Examples:
 * ```
 * const callbackFn = function(){
 *   // do stuff
 * };
 * 
 * // unsubscribes `callbackFn` from `sessionStart.carousel`
 * Flybits.evt.off('sessionStart.carousel', callbackFn);
 * // unsubscribes `callbackFn` from specifically `sessionStart`
 * Flybits.evt.off('sessionStart', callbackFn);
 * 
 * // unsubscribes all handler functions from `sessionStart.carousel`
 * Flybits.evt.off('sessionStart.carousel');
 * // unsubscribes all handler functions from events that contain the key name
 * // `sessionStart` including all namespaces such as `sessionStart.carousel`.
 * Flybits.evt.off('sessionStart');
 * ```
 * @returns {Flybits.evt} Flybits.evt namespace allowing for chaining subscriptions and unsubscriptions.
 * @throws {Flybits.Validation} {@link Flybits.Validation.type| MISSINGARG} When no `evtName` is provided.
 * @throws {Flybits.Validation} {@link Flybits.Validation.type| INVALIDARG} When an `evtName` is provided that is not part of the internal supported {@link Flybits.evt.type|event types}
 */
Flybits.evt.off = function(evtName, handler){
  const availableEvtNames = Object.keys(Flybits.evt.type).map(function(evtKey){
    return Flybits.evt.type[evtKey];
  });
  const mainEvtName = evtName ? evtName.split('.')[0]: null;
  const evtModifier = evtName ? evtName.split('.')[1]: null;

  if(!mainEvtName){
    throw new Flybits.Validation().addError('Missing argument', 'Must provide event name to unsubscribe to.',{
      code: Flybits.Validation.type.MISSINGARG,
      context: 'evtName'
    });
  } else if(availableEvtNames.indexOf(mainEvtName) < 0){
    throw new Flybits.Validation().addError('Event name not supported','',{
      code: Flybits.Validation.type.INVALIDARG,
      context: mainEvtName
    });
  }

  if(mainEvtName && evtModifier){
    if(Flybits.evt.handlerMap[evtName]){
      if(handler){
        Flybits.util.Obj.removeObject(Flybits.evt.handlerMap[evtName], handler);
      } else{
        Flybits.evt.handlerMap[evtName] = [];
      }
    } else{
      console.warn('> Event name not found', evtName);
    }
  } else if(mainEvtName && !evtModifier){
    const evtKeys = Object.keys(Flybits.evt.handlerMap).filter(function(key){
      return key && key.split('.')[0] === mainEvtName;
    });

    if(evtKeys.length){
      if(handler){
        evtKeys.forEach(function(key){
          Flybits.util.Obj.removeObject(Flybits.evt.handlerMap[key], handler);
        })
      } else{
        evtKeys.forEach(function(key){
          Flybits.evt.handlerMap[key] = [];
        })
      }
    } else{
      console.warn('> Event name not found', mainEvtName);
    }
  }

  return Flybits.evt;
}

Flybits.evt.emit = function(evtName, payload){
  const availableEvtNames = Object.keys(Flybits.evt.type).map(function(evtKey){
    return Flybits.evt.type[evtKey];
  });
  const mainEvtName = evtName ? evtName.split('.')[0]: null;
  const evtModifier = evtName ? evtName.split('.')[1]: null;

  if(!mainEvtName){
    throw new Flybits.Validation().addError('Missing argument', 'Must provide event name to emit event.',{
      code: Flybits.Validation.type.MISSINGARG,
      context: 'evtName'
    });
  } else if(availableEvtNames.indexOf(mainEvtName) < 0){
    throw new Flybits.Validation().addError('Event name not supported','',{
      code: Flybits.Validation.type.INVALIDARG,
      context: mainEvtName
    });
  }

  const registeredKeys = Object.keys(Flybits.evt.handlerMap);
  registeredKeys.forEach(function(key){
    if((evtModifier && key === evtName) || 
       (!evtModifier && key.indexOf(mainEvtName+'.') > -1) ||
       (!evtModifier && key === mainEvtName)){
      Flybits.evt.handlerMap[key].forEach(function(callbackFn){
        callbackFn(payload);
      })
    }
  })
}

var initBrowserFileConfig = function(url){
  var def = new Flybits.Deferred();

  fetch(url).then(function(resp){
    if(resp.status !== 200){
      throw new Flybits.Validation().addError('Configuration file not found','Reverting to default configuration. No configuration found at:'+url,{
        code: Flybits.Validation.type.INVALIDARG,
        context: 'url'
      });
    }
    return resp.json();
  }).then(function(json){
    Flybits.cfg = Flybits.util.Obj.extend(Flybits.cfg,json);
    def.resolve(Flybits.cfg);
  }).catch(function(ex){
    if(ex instanceof Flybits.Validation){
      def.reject(ex);
    } else{
      def.reject(new Flybits.Validation().addError('Failed to read configuration file.','Reverting to default configuration. Configuration format incorrect at:'+url,{
        code: Flybits.Validation.type.MALFORMED,
        context: 'url'
      }));
    }
  });

  return def.promise;
};

var initServerFileConfig = function(filePath){
  if(!filePath){
    console.log('> config file path not provided: using defaults');
    return false;
  }

  try{
    /*global fs*/ //only invoked in nodejs env
    var data = fs.readFileSync(filePath);
  } catch(e){
    console.error(e);
    throw new Error('Config file read failed: '+filePath);
  }
  try{
    Flybits.cfg = Flybits.util.Obj.extend(Flybits.cfg,JSON.parse(data));
  } catch(e){
    console.error(e);
    throw new Error('Malformed Config file: '+filePath);
  }
};

var restoreSessionDetails = function(){
  var def = new Flybits.Deferred();
  
  Flybits.store.Property.ready.then(function(){
    return Flybits.Session._restoreState();
  }).then(function(){
    if(!Flybits.Session._deviceID){
      Flybits.Session._deviceID = resolveDeviceID();
      return Flybits.Session._persistState();
    }
    return Promise.resolve();
  }).then(function(){
    def.resolve();
  }).catch(function(e){
    def.reject(e);
  });

  return def.promise;
};

var resolveDeviceID = function(){
  if(typeof window === 'object'){
    var agent = navigator.userAgent;
    var browserName = Flybits.util.Browser.getName(agent);
    return browserName || Flybits.util.Obj.guid();
  } else{
    return Flybits.util.Obj.guid();
  }
};

var setUserAgent = function(){
  var retObj = {
    physicalDeviceId: Flybits.Session._deviceID,
    sdkVersion: Flybits.VERSION
  };
  if(typeof window === 'object'){
    retObj.make = navigator.userAgent;
    retObj.deviceType = 'browser';
  } else{
    retObj.osVersion = process.version;
    retObj.deviceType = 'node';
  }

  Flybits.Session.userAgent = retObj;

  return retObj;
};

var setStaticDefaults = function(){
  Flybits.context.Manager.reportDelay = Flybits.cfg.CTXREPORTDELAY;
  Flybits.analytics.Manager.reportDelay = Flybits.cfg.analytics.REPORTDELAY;
  Flybits.analytics.Manager.reportDelayUnit = Flybits.cfg.analytics.REPORTDELAYUNIT;
  Flybits.analytics.Manager.maxStoreSize = Flybits.cfg.analytics.MAXSTORESIZE;
};

/*global deferredReady*/
Flybits.initFile.server = function(configFileURL){
  initServerFileConfig(configFileURL);
  restoreSessionDetails().then(function(){
    setUserAgent();
    setStaticDefaults();
    deferredReady.resolve(Flybits.cfg);
  }).catch(function(e){
    deferredReady.reject(e);
  });

  return Flybits.ready;
};

/**
 * Initializes the Flybits SDK via URI.
 * @param {string} configFileURL URI of the configuration file that contains the `Object` to override SDK defaults.
 * @function initFile
 * @memberof Flybits
 * @return {external:Promise<Object,Flybits.Validation>} Promise that resolves when the SDK has completed initialization.  The same Promise can be accessed at {@link Flybits.ready}.
 */
Flybits.initFile.browser = function(configFileURL){
  initBrowserFileConfig(configFileURL).then(function(){
    return restoreSessionDetails();
  }).then(function(){
    setUserAgent();
    setStaticDefaults();
    deferredReady.resolve(Flybits.cfg);
  }).catch(function(e){
    deferredReady.reject(e);
  });

  return Flybits.ready;
};

/**
 * Configuration `Object` used to override default SDK settings.
 * @typedef ConfigObject
 * @memberof Flybits
 * @type Object
 * @property {string} HOST Flybits core host URL.
 * @property {number} CTXREPORTDELAY The period length between {@link Flybits.context.Manager} reporting in milliseconds.  After said period, the {@link Flybits.context.Manager|Context Manager} will gather all locally stored context values from all registered {@link Flybits.context.ContextPlugin|context plugins} and report them to the core.
 */

/**
 * Initializes the Flybits SDK.
 * @param {Flybits.ConfigObject=} configObj Configuration `Object` used to override SDK defaults.
 * @function
 * @return {external:Promise<Object,Flybits.Validation>} Promise that resolves when the SDK has completed initialization.  The same Promise can be accessed at {@link Flybits.ready}.
 */
Flybits.init = function(configObj){
  configObj = configObj || {};
  Flybits.cfg = Flybits.util.Obj.extend(Flybits.cfg,configObj);
  restoreSessionDetails().then(function(){
    setUserAgent();
    setStaticDefaults();
    deferredReady.resolve(Flybits.cfg);
  }).catch(function(e){
    deferredReady.reject(e);
  });

  return Flybits.ready;
};

/*global Flybits*/
/**
 * @classdesc A helper class for {@link external:Promise|ES6 Promises} which allows for deferred asynchronous task management.  Not all asynchronous operations can be wrapped in a promise callback.  Sometimes the resolution of a promise needs to be deferred for another entity to resolve or reject, hence the paradigm of the deferred `Object`.
 * @class
 * @memberof Flybits
 */
Flybits.Deferred = (function(){
  var Deferred = function(){
    var def = this;
    /**
     * @instance
     * @memberof Flybits.Deferred
     * @member {external:Promise} promise Instance of an ES6 Promise to be fulfilled.
     */
    this.promise = new Promise(function(resolve,reject){
      /**
       * @instance
       * @memberof Flybits.Deferred
       * @member {function} resolve Callback to be invoked when the asychronous task that initiated the promise is successfully completed.
       */
      def.resolve = resolve;
      /**
       * @instance
       * @memberof Flybits.Deferred
       * @member {function} reject Callback to be invoked when the asychronous task that initiated the promise has failed to complete successfully.
       */
      def.reject = reject;
    });

    this.then = this.promise.then.bind(this.promise);
    this.catch = this.promise.catch.bind(this.promise);
  };

  Deferred.settlePromises = Deferred.prototype.settlePromises = function(promisesArr){
    var reflectedArr = promisesArr.map(function(promise){
      return promise.then(function(successResult){
        return {
          result: successResult,
          status: 'resolved'
        };
      },function(errorResult){
        return {
          result: errorResult,
          status: 'rejected'
        };
      });
    });
    return Promise.all(reflectedArr);  
  };

  return Deferred;
})();

/*global Flybits*/
/**
 * This is a utility class, do not use constructor.
 * @class Obj
 * @classdesc Basic utilities for dealing with Objects.
 * @hideconstructor
 * @memberof Flybits.util
 */
Flybits.util.Obj = (function(){
  var s4 = function(){
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  };

  var obj = {
    createGUID: function(tuples){
      var str = '';
      if(tuples && tuples > 0){
        for(var i = 0; i < tuples; i++){
          str += str !== ''?'-'+s4():s4();
        }
        return str.toUpperCase();
      }
      return (s4() + s4() + '-' + s4() + '-' + s4() + '-' +
           s4() + '-' + s4() + s4() + s4()).toUpperCase();
    },
    guid:function(tuples){
      return 'JS' + this.createGUID(tuples)
    },
    removeObject:function(arr,obj,findCallback){
      var index = arr.indexOf(obj);

      if(findCallback){
        var objs = arr.filter(findCallback);
        if(objs.length > 0){
          index = arr.indexOf(objs[0]);
        }
      }

      if(index >= 0){
        return arr.splice(index,1);
      }
      return arr;
    },
    debounce: function(func, wait, immediate) {
      var timeout;
      return function(...args) {
        var context = this;
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(function() {
          timeout = null;
          if (!immediate) {
            func.apply(context, args);
          }
        }, wait);
        if (callNow) {
          func.apply(context, args);
        }
      };
    },
    functionName: function(func){
      var result = /^function\s+([\w$]+)\s*\(/.exec( func.toString() );
    
      return  result?result[ 1 ]:'';
    },
    isNull: function(obj){
      return obj === null || obj === undefined || obj === '';
    },
    /**
     * Transforms localized object structure into a locale mapping centered around a particular property
     * @memberof Flybits.util.Obj
     * @param {Object} root Root localization structure
     * @param {string} propKey Property key for value within provided `root` localization structure.
     * @function toLocaleValueMap
     * @return Value map of locales and values for specified `propKey`.
     * @example
     * // Example input Root localization structure
     * {
     *   localizations: {
     *     en: {
     *       x: 'abc',
     *       y: 'asdf'
     *     },
     *     fr: {
     *       x: 'efg',
     *       y: 'jkl;'  
     *     }
     *   }
     * }
     * // Example output if `propKey` is equal to `x`
     * {
     *   en: 'abc',
     *   fr: 'efg'
     * }
     */
    toLocaleValueMap: function(root, propKey) {
      var valueObj = {};
      var localeObj = root.locales || root.localizations;
      var localeKeys = localeObj?Object.keys(localeObj):[];
      for (var keyIndex = 0; keyIndex < localeKeys.length; keyIndex++) {
        var localeKey = localeKeys[keyIndex];
        var value = localeObj[localeKey][propKey];
        if (value) {
          valueObj[localeKey] = value;
        }
      }
      return valueObj;
    }
  };

  obj.extend = (function(){
    function isMergeableObject(val) {
      var nonNullObject = val && typeof val === 'object';

      return nonNullObject && Object.prototype.toString.call(val) !== '[object RegExp]' && Object.prototype.toString.call(val) !== '[object Date]';
    }

    function emptyTarget(val) {
      return Array.isArray(val) ? [] : {};
    }

    function cloneIfNecessary(value, optionsArgument) {
      var clone = optionsArgument && optionsArgument.clone === true;
      return (clone && isMergeableObject(value)) ? deepmerge(emptyTarget(value), value, optionsArgument) : value;
    }

    function defaultArrayMerge(target, source, optionsArgument) {
      var destination = target.slice();
      source.forEach(function (e, i) {
        if (typeof destination[i] === 'undefined') {
          destination[i] = cloneIfNecessary(e, optionsArgument);
        } else if (isMergeableObject(e)) {
          destination[i] = deepmerge(target[i], e, optionsArgument);
        } else if (target.indexOf(e) === -1) {
          destination.push(cloneIfNecessary(e, optionsArgument));
        }
      });
      return destination;
    }

    function mergeObject(target, source, optionsArgument) {
      var destination = optionsArgument && optionsArgument.mutate? target:{};
      if (isMergeableObject(target)) {
        Object.keys(target).forEach(function (key) {
          destination[key] = cloneIfNecessary(target[key], optionsArgument);
        });
      }
      Object.keys(source).forEach(function (key) {
        if (!isMergeableObject(source[key]) || !target[key]) {
          destination[key] = cloneIfNecessary(source[key], optionsArgument);
        } else {
          destination[key] = deepmerge(target[key], source[key], optionsArgument);
        }
      });
      return destination;
    }

    function deepmerge(target, source, optionsArgument) {
      var array = Array.isArray(source);
      var options = optionsArgument || { arrayMerge: defaultArrayMerge };
      var arrayMerge = options.arrayMerge || defaultArrayMerge;

      if (array) {
        return Array.isArray(target) ? arrayMerge(target, source, optionsArgument) : cloneIfNecessary(source, optionsArgument);
      } else {
        return mergeObject(target, source, optionsArgument);
      }
    }

    deepmerge.all = function deepmergeAll(array, optionsArgument) {
      if (!Array.isArray(array) || array.length < 2) {
        throw new Error('first argument should be an array with at least two elements');
      }

      // we are sure there are at least 2 values, so it is safe to have no initial value
      return array.reduce(function (prev, next) {
        return deepmerge(prev, next, optionsArgument);
      });
    };

    return deepmerge;
  })();

  return obj;
})();

/*global Flybits*/
Flybits.util.Api = (function(){
  var Deferred = Flybits.Deferred;
  var ObjUtil = Flybits.util.Obj;

  var defaultAjaxOpts = {
    method: 'GET',
    credentials: 'omit',
  };

  var api = {
    flbFetch: function(url,opts){
      opts = opts || {};
      return this.fetch(url,ObjUtil.extend({
        headers: {
          'Content-Type': 'application/json',
          'x-user-agent': JSON.stringify(Flybits.Session.userAgent),
          'x-authorization': Flybits.Session.userToken
        },
        credentials: 'omit',
        respType: 'json'
      },opts));
    },
    fetch: function(url,opts){
      opts = opts || {};
      var def = new Deferred();
      var ajaxOpts = ObjUtil.extend({},defaultAjaxOpts);
      var isJSON = opts.respType === 'json';
      delete opts.respType;
      ajaxOpts = ObjUtil.extend(ajaxOpts,opts);

      fetch(url,ajaxOpts).then(this.checkResult).then(this.getResultBody)
        .then(function(respObj){
          if(isJSON){
            try{
              respObj.body = api.parseResponse(respObj.body);
              def.resolve(respObj);
            } catch(e){
              console.error(e);
              def.reject(new Flybits.Validation().addError('Request Failed','Unexpected server response.',{
                code: Flybits.Validation.type.MALFORMED,
              }));
            }
          } else{
            def.resolve(respObj);
          }
        }).catch(function(resp){
          api.getResultBody(resp).then(function(resultObj){
            var validation = new Flybits.Validation();
            var parsedError = api.parseErrorMsg(resultObj.body);
            var errObj = {
              serverCode: resp.status,
              context: parsedError.raw?parsedError.raw:undefined
            };
            validation.addError('Request error',parsedError.msg,errObj);

            def.reject(validation);
          });
        });

      return def.promise;
    },
    checkResult: function(resp){
      if(resp.status >= 200 && resp.status < 300){
        return resp;
      }
      throw resp;
    },
    getResultBody: function(resp){
      var def = new Deferred();
      if(resp && resp.text){
        var respHeaders = {};
        resp.headers.forEach(function(val,key){
          respHeaders[key] = val;
        });
        resp.text().then(function(str){
          def.resolve({
            body: str,
            headers: respHeaders
          });
        }).catch(function(e){
          def.reject(e);
        });
      } else{
        def.resolve({
          body: '',
          headers: {}
        });
      }

      return def.promise;
    },
    toURLParams: function(obj){
      var keys = Object.keys(obj);
      var keyLength = keys.length;
      var str = '';
      while(keyLength--){
        var key = keys[keyLength];
        if(str !== ''){
          str += '&';
        }
        str += key + '=' + encodeURIComponent(obj[key]);
      }

      return str;
    },
    htmlEncode:function(value){
      return encodeURIComponent(value);
    },
    htmlDecode:function(str){
      return str.replace(/&#?(\w+);/g, function(match, dec) {
        if(isNaN(dec)) {
          var chars = {quot: 34, amp: 38, lt: 60, gt: 62, nbsp: 160, copy: 169, reg: 174, deg: 176, frasl: 47, trade: 8482, euro: 8364, Agrave: 192, Aacute: 193, Acirc: 194, Atilde: 195, Auml: 196, Aring: 197, AElig: 198, Ccedil: 199, Egrave: 200, Eacute: 201, Ecirc: 202, Euml: 203, Igrave: 204, Iacute: 205, Icirc: 206, Iuml: 207, ETH: 208, Ntilde: 209, Ograve: 210, Oacute: 211, Ocirc: 212, Otilde: 213, Ouml: 214, times: 215, Oslash: 216, Ugrave: 217, Uacute: 218, Ucirc: 219, Uuml: 220, Yacute: 221, THORN: 222, szlig: 223, agrave: 224, aacute: 225, acirc: 226, atilde: 227, auml: 228, aring: 229, aelig: 230, ccedil: 231, egrave: 232, eacute: 233, ecirc: 234, euml: 235, igrave: 236, iacute: 237, icirc: 238, iuml: 239, eth: 240, ntilde: 241, ograve: 242, oacute: 243, ocirc: 244, otilde: 245, ouml: 246, divide: 247, oslash: 248, ugrave: 249, uacute: 250, ucirc: 251, uuml: 252, yacute: 253, thorn: 254, yuml: 255, lsquo: 8216, rsquo: 8217, sbquo: 8218, ldquo: 8220, rdquo: 8221, bdquo: 8222, dagger: 8224, Dagger: 8225, permil: 8240, lsaquo: 8249, rsaquo: 8250, spades: 9824, clubs: 9827, hearts: 9829, diams: 9830, oline: 8254, larr: 8592, uarr: 8593, rarr: 8594, darr: 8595, hellip: 133, ndash: 150, mdash: 151, iexcl: 161, cent: 162, pound: 163, curren: 164, yen: 165, brvbar: 166, brkbar: 166, sect: 167, uml: 168, die: 168, ordf: 170, laquo: 171, not: 172, shy: 173, macr: 175, hibar: 175, plusmn: 177, sup2: 178, sup3: 179, acute: 180, micro: 181, para: 182, middot: 183, cedil: 184, sup1: 185, ordm: 186, raquo: 187, frac14: 188, frac12: 189, frac34: 190, iquest: 191, Alpha: 913, alpha: 945, Beta: 914, beta: 946, Gamma: 915, gamma: 947, Delta: 916, delta: 948, Epsilon: 917, epsilon: 949, Zeta: 918, zeta: 950, Eta: 919, eta: 951, Theta: 920, theta: 952, Iota: 921, iota: 953, Kappa: 922, kappa: 954, Lambda: 923, lambda: 955, Mu: 924, mu: 956, Nu: 925, nu: 957, Xi: 926, xi: 958, Omicron: 927, omicron: 959, Pi: 928, pi: 960, Rho: 929, rho: 961, Sigma: 931, sigma: 963, Tau: 932, tau: 964, Upsilon: 933, upsilon: 965, Phi: 934, phi: 966, Chi: 935, chi: 967, Psi: 936, psi: 968, Omega: 937, omega: 969}
          if (chars[dec] !== undefined){
            dec = chars[dec];
          }
        }
        return String.fromCharCode(dec);
      });
    },
    base64Decode: function(str){
      return decodeURIComponent(Array.prototype.map.call(atob(str), function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
    },
    base64Encode: function(str){
      return btoa(str);
    },
    parseResponse: function(rawResponse){
      return JSON.parse(rawResponse,function(key,val){
        if(typeof val === 'string'){
          return api.htmlDecode(val);
        }
        return val;
      });
    },
    parseErrorMsg: function(rawResponse){
      var retObj = {};
      var GENERIC = 'Unexpected error has occurred';
      
      try{
        var resp = this.parseResponse(rawResponse);
        resp = resp.error || resp.errors || resp;
  
        if(resp instanceof Array){
          resp = resp[0];
        }
  
        if(resp){
          retObj.msg =  resp.exceptionMessage || resp.message || resp.messageJSON || resp.detail || GENERIC;
          if(retObj.msg === GENERIC){
            retObj.raw = resp;
          }
        }
      } catch(e){
        console.error(e);
        retObj.msg = GENERIC;
        retObj.raw = rawResponse;
      }

      return retObj;
    },
    parsePaging: function(jsonResp, paginationKey){
      paginationKey = paginationKey || 'pagination';
      var pagingObj = jsonResp[paginationKey];
      return {
        offset: pagingObj?jsonResp[paginationKey].offset:0,
        limit: pagingObj?jsonResp[paginationKey].limit:20,
        total: pagingObj?jsonResp[paginationKey].totalRecords:1
      };
    },
    createNextPageCall: function(requestFunction,reqParams,paging){
      if(paging.offset + paging.limit >= paging.total){
        return null;
      }
      reqParams = reqParams?reqParams:{};
      return function(){
        reqParams.paging = {
          limit: paging.limit,
          offset: paging.offset + paging.limit
        };
        return requestFunction(reqParams);
      };
    }
  };

  return api;
})();

/*global Flybits*/
Flybits.util.Browser = (function(){
  var browser = {
    getCookie: function(key) {
      var value = '; ' + document.cookie;
      var parts = value.split('; ' + key + '=');
      if (parts.length == 2) {
        return parts.pop().split(';').shift();
      } else{
        return null;
      }
    },
    setCookie: function(key,value,expiryDateObj){
      var expires = '';
      if (expiryDateObj) {
        expires = '; expires=' + expiryDateObj.toGMTString();
      }
      document.cookie = key + '=' + value + expires + '; path=/';
    },
    getName: function(userAgentString){
      userAgentString = userAgentString?userAgentString.toLowerCase():'';
      var hasChrome = userAgentString.indexOf('chrome') > -1;
      var hasChromium = userAgentString.indexOf('chromium') > -1;
      var hasFirefox = userAgentString.indexOf('firefox') > -1;
      var hasSafari = (userAgentString.indexOf('applewebkit') > -1 || userAgentString.indexOf('safari') > -1) && !hasChrome && !hasChromium;
      var hasIE = userAgentString.indexOf('msie') > -1 || userAgentString.indexOf('.net') > -1;
      var hasOpera = userAgentString.indexOf('opr') > -1 || userAgentString.indexOf('opera') > -1;
      
      var isMobile = userAgentString.indexOf('mobi') > -1;

      var name;
      if(hasChrome){
        name = 'chrome';
      } else if(hasChromium){
        name = 'chromium';
      } else if(hasFirefox){
        name = 'firefox';
      } else if(hasSafari){
        name = 'safari';
      } else if(hasIE){
        name = 'ie';
      } else if(hasOpera){
        name = 'opera';
      }

      if(name && isMobile){
        name += '_mobile';
      }

      return name;
    }
  };

  return browser;
})();

/*global Flybits*/
/**
 * This is a utility class, do not use constructor.
 * @class Content
 * @classdesc Utilities for manipulating and working with Flybits Content.
 * @hideconstructor
 * @memberof Flybits.util
 */
Flybits.util.Content = (function(){
  return {
    /**
     * Transforms localized Flybits Content data payload objects into plain objects with flattened localization objects whereby localized strings will be resolved based on preferred locale.  Logic contains failover support if preferred locale is not found for an attribute.  **Precedence for localized attribute lookup is as follows: preferred locale, english locale, and then first locale key available.**
     * @memberof Flybits.util.Content
     * @function flattenLocales
     * @param {Object} obj Flybits Content inner data payload objects containing `localizations` and/or `locales` nested objects with localized strings.
     * @param {string} [preferredLocale=en] Preferred locale key by which to resolve localized strings.  By default, English(en) will be used.
     * @returns {Object} Object with localized values flattened into parent objects.
     * @example
     * // Example input root structure
     * {
     *   title: 'titlestr',
     *   locales: {
     *     en: {
     *       description: 'english description string'
     *     },
     *     fr: {
     *       description: 'french description string'
     *     }
     *   }
     * }
     * // Example output if preferred locale is `fr`
     * {
     *   title: 'titlestr',
     *   description: 'french description string'
     * }
     */
    flattenLocales: function (obj, preferredLocale) {
      var result = {};
      var keys = obj ? Object.keys(obj) : null;
      keys?.forEach(function (key) {
        if (key === 'localizations' || key === 'locales') {
          const allLocales = obj[key];
          if(allLocales){
            var attrList = new Set();
            Object.keys(allLocales).forEach(function(localeKey){
              const localeObj = allLocales[localeKey];
              Object.keys(localeObj).forEach(item => attrList.add(item));
            })
            attrList = Array.from(attrList);

            var preferredLocaleObj = obj[key][preferredLocale];
            var enLocaleObj = obj[key].en;
            var firstLocaleObj = obj[key][Object.keys(obj[key])[0]];
            var primaryLocaleObj = preferredLocaleObj || enLocaleObj || firstLocaleObj;
            
            attrList.forEach(function(attrKey){
              if(primaryLocaleObj?.[attrKey]){
                result[attrKey] = primaryLocaleObj[attrKey];
              } else if(primaryLocaleObj !== enLocaleObj && enLocaleObj?.[attrKey]){
                result[attrKey] = enLocaleObj[attrKey];
              } else if(firstLocaleObj !== primaryLocaleObj && firstLocaleObj !== enLocaleObj && firstLocaleObj?.[attrKey]){
                result[attrKey] = firstLocaleObj[attrKey];
              }
            });
          }
        } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
          result[key] = Flybits.util.Content.flattenLocales(obj[key], preferredLocale);
        } else if (Array.isArray(obj[key]) && typeof obj[key][0] === 'object') {
          result[key] = obj[key].map(function (o) {
            return Flybits.util.Content.flattenLocales(o, preferredLocale);
          });
        } else {
          result[key] = obj[key];
        }
      });
  
      return result;
    }
  }
})();
/*global Flybits*/

Flybits.util.Ctx = (function(){
  var CTX_PLUGIN_ID_PATTERN = /^ctx\.[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+$/;
  var CTX_VALUE_KEY_PATTERN = /^[a-zA-Z0-9 -]+(?:\.[a-zA-Z0-9 -]+)*$/;

  return {
    CTX_PLUGIN_ID_PATTERN: CTX_PLUGIN_ID_PATTERN,
    CTX_VALUE_KEY_PATTERN: CTX_VALUE_KEY_PATTERN,

    validatePluginID: function(pluginID){
      const validation = new Flybits.Validation();
      if(!pluginID){
        validation.addError('Missing argument', 'Plugin ID is required', {
          code: Flybits.Validation.type.MISSINGARG,
          context: 'pluginID',
        });
      } else if(typeof pluginID !== 'string'){
        validation.addError('Invalid argument', 'Plugin ID must be a string', {
          code: Flybits.Validation.type.INVALIDARG,
          context: 'pluginID',
        });
      } else if(!CTX_PLUGIN_ID_PATTERN.test(pluginID)){
        validation.addError('Malformed argument', 'Plugin ID must be in the format of "ctx.<providerName>.<pluginName>"', {
          code: Flybits.Validation.type.MALFORMED,
          context: 'pluginID',
        });
      }
      
      return validation;
    },
    validateCtxValueKey: function(key){
      const validation = new Flybits.Validation();
      if(!key){
        validation.addError('Missing argument', 'Context value key is required', {
          code: Flybits.Validation.type.MISSINGARG,
          context: 'key',
        });
      } else if(typeof key !== 'string'){
        validation.addError('Invalid argument', 'Context value key must be a string', {
          code: Flybits.Validation.type.INVALIDARG,
          context: key,
        });
      } else if(!CTX_VALUE_KEY_PATTERN.test(key)){
        validation.addError('Malformed argument', 'Context value key must be in the format of "<attributeName>.[parameter1].[parameter2]...[parameter5]"', {
          code: Flybits.Validation.type.MALFORMED,
          context: key, 
        });
      }
      return validation;
    },
    validateCtxValue: function(value){
      const validation = new Flybits.Validation();
      if(value === undefined || value === null){
        validation.addError('Missing argument', 'Context value is required', {
          code: Flybits.Validation.type.MISSINGARG,
          context: 'value',
        });
      }
      return validation;
    },
    validateCtxValueObj: function(contextValue){
      const validation = new Flybits.Validation();
      if(!contextValue){
        validation.addError('Missing argument', 'Context value is required', {
          code: Flybits.Validation.type.MISSINGARG,
          context: 'contextValue',
        });
      } else if(typeof contextValue !== 'object' || Array.isArray(contextValue)){
        validation.addError('Invalid argument', 'Context value must be an object', {
          code: Flybits.Validation.type.INVALIDARG,
          context: 'contextValue',
        });
      } else if(Object.keys(contextValue).length === 0){
        validation.addError('Invalid argument', 'Context value must be an object with at least one key', {
          code: Flybits.Validation.type.INVALIDARG,
          context: 'contextValue',
        });
      } else{
        for(const key in contextValue){
          validation.merge(Flybits.util.Ctx.validateCtxValueKey(key));
          validation.merge(Flybits.util.Ctx.validateCtxValue(contextValue[key]));
        }
      }
      return validation;
    }
  };
})();
/*global Flybits*/
Flybits.util.Geo = (function(){
  var geo = {
    _toDeg : function(rad) {
      return rad * 180 / Math.PI;
    },
    _toRad : function(deg) {
      return deg * Math.PI / 180;
    },
    getBoundingBox: function(latLngArr){
      if(!latLngArr || latLngArr.length < 3){
        throw new Flybits.Validation().addError('Invalid Argument','Must provide an array of lat,lng coordinates greater than 2.',{
          code: Flybits.Validation.type.INVALIDARG
        });
      }
      var latMin = latLngArr[0].lat;
      var latMax = latLngArr[0].lat;
      var lngMin = latLngArr[0].lng;
      var lngMax = latLngArr[0].lng;

      for(var i = 1; i < latLngArr.length; i++){
        var pt = latLngArr[i];
        latMin = pt.lat < latMin? pt.lat:latMin;
        latMax = pt.lat > latMax? pt.lat:latMax;
        lngMin = pt.lng < lngMin? pt.lng:lngMin;
        lngMax = pt.lng > lngMax? pt.lng:lngMax;
      }

      return {
        min: {
          lat: latMin,
          lng: lngMin
        },
        max: {
          lat: latMax,
          lng: lngMax
        }
      };
    },
    getCenter: function(latLngArr){
      if(!latLngArr || latLngArr.length < 3){
        throw new Flybits.Validation().addError('Invalid Argument','Must provide an array of lat,lng coordinates greater than 2.',{
          code: Flybits.Validation.type.INVALIDARG
        });
      }
      var bounds = this.getBoundingBox(latLngArr);
      return {
        lat: (bounds.max.lat + bounds.min.lat) / 2,
        lng: (bounds.max.lng + bounds.min.lng) / 2
      };
    },
    getBearing: function(pt1,pt2){
      var dLng = (pt2.lng-pt1.lng);
      var y = Math.sin(dLng) * Math.cos(pt2.lat);
      var x = Math.cos(pt1.lat)*Math.sin(pt2.lat) - Math.sin(pt1.lat)*Math.cos(pt2.lat)*Math.cos(dLng);
      var brng = this._toDeg(Math.atan2(y, x));
      return 360 - ((brng + 360) % 360);
    }
  };

  return geo;
})();

/*global Flybits*/
/**
 * Interface for implementing context plugins.
 * @memberof Flybits.interface
 * @interface
 */
Flybits.interface.ContextPlugin = {
  /**
   * Checks for availability of this plugin on the current platform.
   * @function
   * @memberof Flybits.interface.ContextPlugin
   * @return {external:Promise<undefined,Flybits.Validation>} Promise that resolves without value if this context plugin is supported on the current platform.
   */
  isSupported: function(){},
  /**
   * Retrieves current value of this particular context plugin.
   * @function
   * @memberof Flybits.interface.ContextPlugin
   * @return {external:Promise<Object,Flybits.Validation>} Promise that resolves with context plugin specific data structure representing current value of context plugin.
   */
  getState: function(){},

  /**
   * Converts context value object into the server expected format.
   * @function
   * @memberof Flybits.interface.ContextPlugin
   * @param {Object} contextValue
   * @return {Object} Expected server format of context value.
   */
  _toServerFormat: function(contextValue){} // eslint-disable-line no-unused-vars

};

/*global Flybits*/
/**
 * Interface for implementing identity providers.
 * @memberof Flybits.interface
 * @interface
 */
Flybits.interface.IDP = {
  /**
   * Returns Flybits endpoint to report IDP payload.
   * @function getURL
   * @memberof Flybits.interface.IDP
   * @return {string} URL to authentication endpoint for this particular IDP
   */
  getURL: function(){},
  /**
   * Returns payload required for IDP to authenticate with Flybits.
   * @function getPayload
   * @memberof Flybits.interface.IDP
   * @return {Object} IDP specific payload used to authenticate with Flybits.
   * @throws {Flybits.Validation} If IDP parameters are incorrect an exception will be thrown in the form of a standard {@link Flybits.Validation|Validation} object.
   */
  getPayload: function(){},
};

/*global Flybits*/
/**
 * Interface for implementing data stores that operate on key/value pairs.
 * @memberof Flybits.interface
 * @interface
 */
Flybits.interface.KeyDataStore = {
  /**
   * Retrieves the amount of entries in the data store.
   * @function
   * @memberof Flybits.interface.KeyDataStore
   * @return {external:Promise<Object,Flybits.Validation>} Promise that resolves with the number of entries in the data store.
   */
  getSize: function(){},
  /**
   * Adds/Replaces/Removes an item in the data store.
   * @function
   * @memberof Flybits.interface.KeyDataStore
   * @param {string} id ID of item to be stored.
   * @param {Object} item Item to be stored in data store.  If `null` or `undefined` is supplied the related item that has provided `id` will be removed from the data store.
   * @return {external:Promise<Object,Flybits.Validation>} Promise that resolves without value if data is successfully set in data store.
   */
  set: function(id,item){}, // eslint-disable-line no-unused-vars

  /**
   * Retrieves an item from the data store.
   * @function
   * @memberof Flybits.interface.KeyDataStore
   * @param {string} id ID of item to be retrieved.
   * @return {external:Promise<Object,Flybits.Validation>} Promise that resolves with data store item based on `id` if it exists.
   */
  get: function(id){}, // eslint-disable-line no-unused-vars

  /**
   * Retrieves all item keys from the data store.
   * @function
   * @memberof Flybits.interface.KeyDataStore
   * @return {external:Promise<Object,Flybits.Validation>} Promise that resolves with all item keys in the data store.
   */
  getKeys: function(){},

  /**
   * Clears the entire data store of its entries.
   * @function
   * @memberof Flybits.interface.KeyDataStore
   * @return {external:Promise<undefined,Flybits.Validation>} Promise that resolves without value if data clear is successful.
   */
  clear: function(){}
};

/*global Flybits*/
/**
 * Interface for models which have localized properties.
 * @memberof Flybits.interface
 * @interface
 */
Flybits.interface.Localizable = {
  /**
   * Parses server localized properties for a single locale object. For instance if a model has localized properties {'en':{},'fr':{}}, each object mapped to each locale key would pass through this function.
   * @function
   * @instance
   * @memberof Flybits.interface.Localizable
   * @param {Object} serverObj server locale object containing localized properties.
   * @return {Object} Server localized properties of a locale key parsed to SDK equivalent objects.
   */
  _fromLocaleJSON: function(serverObj){}, // eslint-disable-line no-unused-vars

  /**
   * Maps SDK localized objects back to server equivalent objects.
   * @function
   * @instance
   * @memberof Flybits.interface.Localizable
   * @param {Object} appObj application locale object containing localized properties
   * @return {Object} SDK localized properties of a locale key parsed to server equivalent objects.
   */
  _toLocaleJSON: function(appObj){} // eslint-disable-line no-unused-vars

};

/*global Flybits*/
/**
 * Interface for implementing property stores (key-value pair storage).
 * @memberof Flybits.interface
 * @interface
 */
Flybits.interface.PropertyStore = {
  /**
   * Checks for availability of this property storage type.
   * @function
   * @memberof Flybits.interface.PropertyStore
   * @return {external:Promise<undefined,Flybits.Validation>} Promise that resolves without value if storage type is supported and rejects if it is not.
   */
  isSupported: function(){},
  /**
   * Retrieves property value based on key.
   * @function
   * @memberof Flybits.interface.PropertyStore
   * @instance
   * @param key Key by which to fetch the requested property.
   * @return {external:Promise<string,Flybits.Validation>} Promise that resolves with property store value unless a problem occurs.
   */
  getItem: function(key){}, // eslint-disable-line no-unused-vars

  /**
   * Converts context value object into the server expected format.
   * @function
   * @memberof Flybits.interface.PropertyStore
   * @instance
   * @param {string} key Key by which to store provided `value`
   * @param {string} value Value that is to be stored based on provided `key`
   * @return {external:Promise<undefined,Flybits.Validation>} Promise that resolves without value if value has been successfully stored based on specified key.
   */
  setItem: function(key, value){}, // eslint-disable-line no-unused-vars

  /**
   * Completely remove key and value from property store.
   * @function
   * @memberof Flybits.interface.PropertyStore
   * @instance
   * @param {string} key Key by which to store provided `value`
   * @return {external:Promise<undefined,Flybits.Validation>} Promise that resolves without value if key and value have been successfully removed based on specified key.
   */
  removeItem: function(key){}, // eslint-disable-line no-unused-vars

};

/*global Flybits*/
/**
 * Interface for SDK models that are abstracted from server models.
 * @memberof Flybits.interface
 * @interface
 */
Flybits.interface.Serializable = {
  /**
   * Parses raw server models into SDK model properties that implement this interface.
   * @function
   * @instance
   * @memberof Flybits.interface.Serializable
   * @param {Object} serverObj Raw server model.
   */
  fromJSON: function(serverObj){}, // eslint-disable-line no-unused-vars
  /**
   * Maps SDK model properties to abstracted server models.
   * @function
   * @instance
   * @memberof Flybits.interface.Serializable
   * @returns {Object} Raw server model.
   */
  toJSON: function(){}
};

/**
 * @classdesc Base class from which all core Flybits classes are extended.
 * @class
 */
var BaseObj = (function(){ // eslint-disable-line no-unused-vars
  function BaseObj(){}
  BaseObj.prototype = {
    implements: function(interfaceName){
      if(!this._interfaces){
        this._interfaces = [];
      }
      this._interfaces.push(interfaceName);
    }
  };

  return BaseObj;
})();

/*global Flybits*/
// eslint-disable-next-line no-unused-vars
var Path = (function(){
  function Path(){
    this.nodes = [];
  }

  Path.prototype.getNode = function(className){
    return this.nodes.filter(function(obj){
      return Flybits.util.Obj.functionName(obj.constructor) === className;
    })[0];
  };

  Path.prototype.append = function(obj){
    this.nodes.push(obj);
    return this;
  };

  Path.prototype.serialize = function(){
    var pathStr = '';
    for(var i = 0; i < this.nodes.length; i++){
      pathStr += '/' + this.nodes[i]._pathName;
      if(this.nodes[i].id){
        pathStr += '/' + this.nodes[i].id;
      }
    }
    
    return pathStr;
  };

  return Path;
})();

/*global BaseObj*/
/**
 * @classdesc Base class from which all core Flybits models are extended.
 * @class
 * @param {Object} serverObj Raw Flybits core model `Object` directly from API.
 */
var BaseModel = (function(){ // eslint-disable-line no-unused-vars
  var BaseModel = function(serverObj){
    BaseObj.call(this);
    /**
     * @instance
     * @memberof BaseModel
     * @member {string} id Parsed ID of the Flybits core model.
     */
    this.id;

    if(serverObj && serverObj.id){
      this.id = serverObj.id;
    }
  };

  BaseModel.prototype = Object.create(BaseObj.prototype);
  BaseModel.prototype.constructor = BaseModel;

  BaseModel.prototype.reqKeys = {
    id: 'id'
  };

  return BaseModel;
})();

/*global Flybits,BaseModel,Path*/
/**
 * @classdesc Flybits core Content model. A Content model instance represents the content created from a corresponding Content Template. That is to say it is the entity through which we can access/modify JSON data that fits the structure specified in the template.
 * @class
 * @memberof Flybits
 * @extends BaseModel
 * @implements {Flybits.interface.Serializable}
 * @implements {Flybits.interface.Localizable}
 * @param {Object} serverObj Raw Flybits core model `Object` directly from API.
 */
Flybits.Content = ( function(){
  var ApiUtil = Flybits.util.Api;

  /**
   * @typedef LocalizedObject
   * @memberof Flybits.Content
   * @type Object
   * @property {string} name Name of the Content instance.
   * @property {string} description Description of the Content instance.
   */

  function Content( serverObj ){
    BaseModel.call( this, serverObj );
    serverObj = serverObj || {};
    this.fromJSON( serverObj );
  }

  Content.prototype = Object.create( BaseModel.prototype );
  Content.prototype.constructor = Content;
  Content.prototype.implements( 'Serializable' );
  Content.prototype.implements( 'Localizable' );

  Content.prototype.types = Content.types = {
    DEFAULT: 'default',
    SURVEY: 'SurveyQuestions'
  };
  Content.prototype._pathName = Content._pathName = 'content/instances';

  Content.prototype.reqKeys = Content.reqKeys = {
    name: 'name',
    description: 'description',
    createdDate: 'createdAt',
    lastModifiedDate: 'modifiedAt',
    id: 'id'
  };

  /**
   * @typedef renderTypes
   * @memberof Flybits.Content
   * @type Object
   * @property {string} ALL ("all") Used to retrieve Content of any render type.
   * @property {string} HTML ("html") Used to retrieve only HTML based Content.
   * @property {string} DEFAULT ("default") Used to retrieve only non-HTML based Content.
   */
  Content.prototype.renderTypes = Content.renderTypes = {
    ALL: 'all',
    HTML: 'html',
    DEFAULT: 'default'
  }

  /***** Localizable function overloading *****/
  Content.prototype._fromLocaleJSON = function( serverObj ) {
    var retObj = {
      name: serverObj.name,
      description: serverObj.description
    };

    return retObj;
  };

  Content.prototype._toLocaleJSON = function ( appObj ) {
    var retObj = {
      name: appObj.name,
      description: appObj.description
    };

    return retObj;
  };

  Content.prototype.parseContents = function(serverObj){
    var paging = ApiUtil.parsePaging( serverObj );
    var contentObj = this;
    
    var allContentData = serverObj.data.map( function(obj){
      var routePath = new Path().append(contentObj);
      try{
        return new Flybits.ContentData( obj, routePath );
      } catch(e){
        console.error(e);
        throw new Flybits.Validation().addError('Parse Failed','Failed to parse Content data model.',{
          code: Flybits.Validation.type.MALFORMED,
          context: obj
        });
      }
    });

    return {
      result: allContentData,
      nextPageFn: ApiUtil.createNextPageCall( Flybits.api.ContentData.getAll, {
        contentID: contentObj.id
      }, paging )
    };
  };

  /**
   * Synchronously retrieve the data from the Content model instance.  This will only be possible if the instance was fetched with its inner data object via the [`data` request param](Flybits.api.Content.html#.RequestParams) set to `true` as it is by default.
   * @memberof Flybits.Content
   * @instance
   * @function getDataObj
   * @param {Object} [opts] Optional specification of output formatting (eg. localization flattening).
   * @param {string} [opts.locale=en] [Two character locale key](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) (eg. en, fr, es, etc) to flatten content attributes instead of returning entire embedded localization objects.  If "auto" is used, the locale key in {@link Flybits.Session.preferredLanguage} will be used.
   * @return {Object|null} Content Instance JSON payload object or `null` if Content Instance does not locally have one.
   */
  Content.prototype.getDataObj = function(opts){
    if(this.data && this.data.result && this.data.result.length){
      if(opts?.locale){
        if(opts.locale === 'auto'){
          return Flybits.util.Content.flattenLocales(this.data.result[0].payload, Flybits.Session.preferredLanguage);
        } else if(opts.locale.length === 2){
          return Flybits.util.Content.flattenLocales(this.data.result[0].payload, opts.locale);
        } else{
          throw new Flybits.Validation().addError('Invalid locale', 'Only two letter locale keys are supported [ISO 3166-1 alpha-2]',{
            code: Flybits.Validation.type.INVALIDARG,
            context: opts.locale
          })
        }
      }
      return this.data.result[0].payload;
    }

    return null;
  };

  /**
   * Asynchronously retrieve the data from the Content model instance.  This can be useful if the instance was originally fetched without its inner data object via the [`data` request param](Flybits.api.Content.html#.RequestParams) set to `false`.  If the instance does not locally have a data object a request to the server will be made to attempt to fetch one. 
   * @memberof Flybits.Content
   * @instance
   * @function getData
   * @return {external:Promise<Object,Flybits.Validation>} Promise that resolves with the Content instance's JSON payload object.
   */
  Content.prototype.getData = function(){
    var obj = this;
    if(this.data && this.data.result && this.data.result.length > 0){
      return Promise.resolve(this.data.result[0].payload);
    } else{
      var def = new Flybits.Deferred();
      Flybits.api.ContentData.getAll({
        contentID: this.id
      }).then(function(respObj){
        if(respObj && respObj.result && respObj.result.length > 0){
          obj.data = respObj;
          def.resolve(respObj.result[0].payload);
        } else{
          def.resolve();
        }
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    }
  };

  /***** Serializable function overloading *****/
  Content.prototype.fromJSON = function(serverObj){
    var obj = this;
    /**
     * @instance
     * @memberof Flybits.Content
     * @member {string} id - id value of the content instance.
     */
    obj.id = serverObj.id;
    /**
     * @instance
     * @memberof Flybits.Content
     * @member {string} tenantID - id value of the tenant(project) of instance
     */
    obj.tenantID = serverObj.tenantId;
    /**
     * @instance
     * @memberof Flybits.Content
     * @member {string} templateID - id value of the content template of the fetched content instance.
     */
    obj.templateID = serverObj.templateId;
    /**
     * @instance
     */
    obj.experienceIDs = serverObj.experienceIds || [];
    if(!serverObj.experienceIds && serverObj.experienceId){
      obj.experienceIDs.push(serverObj.experienceId);
    }

    /**
     * @instance
     * @memberof Flybits.Content
     * @member {string} type - content template type from which this instance was created from.
     */
    obj.type = serverObj.templateType || Content.types.DEFAULT;
    /**
     * @instance
     * @memberof Flybits.Content
     * @member {string} iconURL - URL of the icon image associated with this content instance.
     */
    obj.iconURL = serverObj.iconUrl;
    /**
     * @instance
     * @memberof Flybits.Content
     * @member {Date} createdDate - Date of model creation.
     */
    obj.createdDate = serverObj.createdAt? new Date(serverObj.createdAt*1000):null;
    /**
     * @instance
     * @memberof Flybits.Content
     * @member {Date} lastModifiedDate - Date of last modification of model.
     */
    obj.lastModifiedDate = serverObj.modifiedAt? new Date(serverObj.modifiedAt*1000):null;
    /**
     * @instance
     * @memberof Flybits.Content
     * @see Flybits.Content.LocalizedObject
     * @member {Object} locales - Map of available locale keys to {@link Flybits.Content.LocalizedObject} objects containing information.
     * @example
     * { 
     *   en: {
     *     name: 'Falafel',
     *     description: 'Falafel is good'
     *   },
     *   fr: {
     *     name: 'Falafel',
     *     description: 'Falafel est bon'
     *   }
     * }
     */
    obj.locales = {};

    var localeKeys = serverObj.localizations ? Object.keys(serverObj.localizations) : [];
    localeKeys.forEach( function( key ) {
      obj.locales[key] = obj._fromLocaleJSON( serverObj.localizations[key] );
    });

    obj.name = obj.locales?.en?.name;
    obj.description = obj.locales?.en?.description;

    // if( localeKeys.length > 0 && this.defaultLang ) {
    //   this.defaultLocaleObj = this.locales[ this.defaultLang ];
    // }
    /**
     * @instance
     * @memberof Flybits.Content
     * @member {string} 
     */
    obj.linkedFields = serverObj.linkedFields;

    /**
     * @instance
     * @memberof Flybits.Content
     * @member {string[]}
     */
    obj.labels = serverObj.labels || [];

    obj.data = serverObj.content? this.parseContents(serverObj.content):undefined;

    /**
     * @instance
     * @memberof Flybits.Content
     * @member {string} [renderType=""] - Indicates whether specific type of rendering is available.  Will be set to `html` if content has html/css UI defined.
     */
    obj.renderType = serverObj.renderType;

    /**
     * @instance
     * @memberof Flybits.Content
     * @member {string} [accessType=rule] Indicates whether or not this Content Instance is strictly governed by rules or accessible via ID regardless.  By default this SDK will create strictly governed instances.
     */
    obj.accessType = serverObj.accessType || 'rule';
    
    /**
     * @instance
     * @memberof Flybits.Content
     * @member {Object} defaultUI Rendering layout for the Content instance if it is of HTML {@link Flybits.Content.renderType}
     * @property {string} [layout=""] Out of the box layout type.  Flybits Concierge will come with referenceable Content UI components.  This attribute will be overriden if there is a defined {@link Flybits.Content.defaultUI.layoutHTML}
     * @property {string} [layoutHTML=""] Raw HTML for Content UI rendering.
     * @property {string} [styleCSS=""] Raw CSS for Content UI styling.
     * @property {string} [styleCSSURL=""] URL of CSS resource.  This attribute will be overriden by {@link Flybits.Content.defaultUI.styleCSSURL}
     */
    obj.defaultUI = serverObj.defaultUIConfig?serverObj.defaultUIConfig:{
      layout: '',
      layoutHTML: '',
      styleCSS: '',
      styleCSSURL: ''
    };

    /**
     * @instance
     * @memberof Flybits.Content
     * @member {Object} detailsUI Rendering layout for the Content instance details page if supported and is of HTML {@link Flybits.Content.renderType}.  This is optional as not all Content instances have a drilled down full page details view.
     * @property {string} [layout=""] Out of the box layout type.  Flybits Concierge will come with referenceable Content UI components.  This attribute will be overriden if there is a defined {@link Flybits.Content.defaultUI.layoutHTML}
     * @property {string} [layoutHTML=""] Raw HTML for Content UI rendering.
     * @property {string} [styleCSS=""] Raw CSS for Content UI styling.
     * @property {string} [styleCSSURL=""] URL of CSS resource.  This attribute will be overriden by {@link Flybits.Content.defaultUI.styleCSSURL}
     */
    obj.detailsUI = serverObj.detailsUIConfig?serverObj.detailsUIConfig:{
      layout: '',
      layoutHTML: '',
      styleCSS: '',
      styleCSSURL: ''
    };

    /**
     * @instance
     * @memberof Flybits.Content
     * @member {Array} usedByJourneys - Array of Journey IDs and names that are using this content instance.
     */
    obj.usedByJourneys = serverObj.usedBy || [];

    obj.metadata = serverObj.metadata || {};

    return this;
  };

  Content.prototype.toJSON = function(){
    var obj = this;
    
    var retObj = {
      iconUrl: obj.iconURL,
      localizations: {},
      labels: obj.labels,
      defaultUIConfig: this.defaultUI,
      detailsUIConfig: this.detailsUI,
      accessType: this.accessType
    };

    if((
      this.defaultUI && 
        (this.defaultUI.layout || this.defaultUI.layoutHTML)
    ) || 
      (
        this.detailsUI &&
        (this.detailsUI.layout || this.detailsUI.layoutHTML)
      )){
      retObj.renderType = 'html';
    } else {
      retObj.renderType = '';
    }

    if(obj.createdDate){
      retObj.createdAt = Math.round(obj.createdDate.getTime()/1000);
    }
    if(obj.lastModifiedDate){
      retObj.modifiedAt = Math.round(obj.lastModifiedDate.getTime()/1000);
    }

    if(obj.name || obj.description){
      obj.locales = {
        en: {
          name: obj.name,
          description: obj.description
        }
      }
    }

    if( Object.keys( obj.locales ).length > 0 ) {
      var localeKeys = Object.keys( obj.locales );
      localeKeys.forEach( function( key ) {
        retObj.localizations[key] = obj._toLocaleJSON( obj.locales[key] );
      });
    }

    /* id rendering: thrown in for just safety purpose
     * id validations(content instance, content template, tenant) moved to the content API
     * id is not passed in the body of JSON (PUT, POST) requests anymore (Part of Header and URL)
     */
    if( obj.id ) {
      retObj.id = obj.id;
    }

    if( obj.tenantID ) {
      retObj.tenantId = obj.tenantID;
    }

    if( obj.templateID ) {
      retObj.templateId = obj.templateID;
    }

    if( obj.experienceIDs ) {
      retObj.experienceIds = obj.experienceIDs;
    }

    if( obj.type ){
      retObj.templateType = obj.type;
    }

    if (Array.isArray(obj.usedByJourneys) && obj.usedByJourneys.length > 0) {
      retObj.usedBy = obj.usedByJourneys;
    }

    if (obj.metadata) {
      retObj.metadata = obj.metadata;
    } else {
      retObj.metadata = {};
    }

    if (obj.data) {
      retObj.content = {
        data: [obj.data.result?.[0]?.toJSON() || {}],
        pagination: {
          limit: 20,
          offset: 0,
          totalRecords: 1
        }
      }
    }

    return retObj;
  };

  Content.getInstance = function(serverObj){
    if(serverObj.templateType === Content.types.SURVEY && serverObj.surveyMetadata){
      return new Flybits.Survey(serverObj);
    } else{
      return new Content(serverObj);
    }
  };

  return Content;
})();

/*global Flybits,BaseModel,Path*/
/**
 * @classdesc Flybits core Content Root Data model. This is the root data of a content instance
 * @class
 * @memberof Flybits
 * @extends BaseModel
 * @implements {Flybits.interface.Serializable}
 * @param {Object} serverObj Raw Flybits core model `Object` directly from API.
 */
Flybits.ContentData = ( function(){
  function ContentData( serverObj, path ){
    BaseModel.call( this, serverObj );
    this.id = serverObj._id;
    this.path = path || new Path();
    this.path.append(this);

    if( serverObj ){
      this.fromJSON( serverObj );
    }
  }

  ContentData.prototype = Object.create( BaseModel.prototype );
  ContentData.prototype.constructor = ContentData;
  ContentData.prototype.implements('Serializable');

  ContentData.prototype._pathName = ContentData._pathName = 'data';

  var getRecursiveParseValue = function(value, path){
    if(value instanceof Object && !Array.isArray(value)){
      return recursiveObjParse(value, path);
    } else if(Array.isArray(value)){
      return value.map(function(obj){
        return getRecursiveParseValue(obj, path);
      });
    }
    return value;
  };

  var recursiveObjParse = function(parentObj, path){
    var retObj = {};
    var parentKeys = Object.keys(parentObj);
    parentKeys.forEach(function(key){
      var value = parentObj[key];
      if(key === '_id'){
        return;
      }
      if(key === 'localizations'){
        key = 'locales';
      }

      var pageKey = key+'.pagination';
      if(Object.prototype.hasOwnProperty.call(parentObj,pageKey)){
        retObj[key] = new Flybits.PagedData({
          data: value,
          paging: parentObj[pageKey],
          key: key,
          path: path
        });
      } else if(key.indexOf('.pagination') < 0){
        retObj[key] = getRecursiveParseValue(value, path);
      }
    });

    return retObj;
  };

  var getRecursiveSerializationValue = function(value){
    if(value instanceof Flybits.PagedData){
      return value.toJSON();
    } else if(value instanceof Object && !Array.isArray(value)){
      return recursiveObjSerialization(value);
    } else if(Array.isArray(value)){
      return value.map(function(obj){
        return getRecursiveSerializationValue(obj);
      });
    }
    
    return value;
  };

  var recursiveObjSerialization = function(parentObj){
    var serialized = {};
    var parentKeys = Object.keys(parentObj);
    parentKeys.forEach(function(key){
      var value = parentObj[key];
      key = key === 'locales'?'localizations':key;
      serialized[key] = getRecursiveSerializationValue(value);
    });

    return serialized;
  };

  /***** Serializable function overloading *****/
  ContentData.prototype.fromJSON = function( serverObj ){
    if(serverObj._id){
      this.id = serverObj._id;
    }
    this.payload = recursiveObjParse(serverObj, this.path);
  };

  ContentData.prototype.toJSON = function(){
    var serverPayload =  recursiveObjSerialization(this.payload);
    if(this.id){
      serverPayload._id = this.id;
    }
    return serverPayload;
  };

  return ContentData;
})();

/*global Flybits, BaseModel*/

/**
 * @classdesc Flybits core Module model. A Flybits Module contains a set of filtered {@link Flybits.Content} as well as associated layout UI.
 * @class
 * @memberof Flybits
 * @extends BaseModel
 * @param {Object} module Raw Flybits core model `Object` directly from API.
 * @param {Object} contentMap Object map of content instance IDs to {@link Flybits.Content}.
 */
Flybits.Module = (function () {
  function Module(module, contentMap) {
    BaseModel.call(this, module);
    this.content = [];

    if (module) {
      this.fromJSON(module, contentMap);
    }
  }

  Module.prototype = Object.create(BaseModel.prototype);
  Module.prototype.constructor = Module;

  Module.prototype.fromJSON = function (module, contentMap) {
    this.name = module.name;
    this.styleCSS = module.styleCSS;
    this.styleCSSURL = module.styleCSSURL;
    this.layout = module.layout;
    this.layoutHTML = module.layoutHTML;
    this.query = module.query;
    this.viewAll = module.viewAll;
    this.options = module.options;
    this.allowDeprioritization = !!module.allowDeprioritization;

    const model = this;
    if (module.contentIDs?.length && contentMap) {
      module.contentIDs.forEach(function (cID) {
        if (contentMap[cID]) {
          model.content.push(contentMap[cID]);
        }
      })
    }
  };

  /**
   * Produces module payload meant to be injected into the Concierge
   * @memberof Flybits.Module
   * @instance
   * @function toConciergeModuleContainer
   * @param {Object} [opts] Optional serialization specifications such as locale.
   * @param {string} [opts.locale=en] Preferred locale key
   * @returns {Object} Prepared Module payload object to be injected into a Concierge instance.
   */
  Module.prototype.toConciergeModuleContainer = function (opts) {
    var preferredLocale = 'en';
    if (opts?.locale) {
      let sanitizedLocale = opts.locale.toLowerCase();
      if (sanitizedLocale.indexOf('-') > -1) {
        sanitizedLocale = sanitizedLocale.split('-')[0];
      }
      if (sanitizedLocale?.length === 2) {
        preferredLocale = sanitizedLocale;
      }
    }

    return {
      id: this.id,
      name: this.name,
      style: this.styleCSSURL,
      styleStr: this.styleCSS,
      layout: this.layout,
      layoutStr: this.layoutHTML,
      query: this.query,
      allowDeprioritization: this.allowDeprioritization,
      content: this.content?.map(function (content) {
        const asJson = content.toJSON()
        return {
          contentInfo: {
            id: asJson.id,
            labels: asJson.labels,
            experienceIDs: content.experienceIDs,
            templateType: asJson.templateType,
          },
          id: asJson.id,
          uiTemplate: asJson.defaultUIConfig.layoutHTML,
          layout: asJson.defaultUIConfig.layout,
          style: asJson.defaultUIConfig.styleCSSURL,
          styleStr: asJson.defaultUIConfig.styleCSS,
          data: asJson.content.data?.length > 0 ? Flybits.util.Content.flattenLocales(asJson.content.data[0], preferredLocale) : []
        }
      })
    };
  }

  return Module;
})();

/*global Flybits,BaseModel*/
/**
 * @classdesc Helper representation of nested Content data arrays that are pageable.
 * @class
 * @memberof Flybits
 * @extends BaseModel
 * @implements {Flybits.interface.Serializable}
 * @param {Flybits.PagedData.InputObject} inputObj.
 */
Flybits.PagedData = ( function(){
  var ApiUtil = Flybits.util.Api;
  var Deferred = Flybits.Deferred;

  /**
   * @typedef InputObject
   * @memberof Flybits.PagedData
   * @type Object
   * @property {Flybits.Path} path Helper object that tracks depth of paged array in ancestor hierarchy for API paging purposes.
   * @property {string} key Key name that paged data array is mapped to in parent object.
   * @property {Object[]} data Array of paged data objects.
   * @property {Object} paging Pagination information of data array.
   */

  function PagedData( inputObj ){
    BaseModel.call( this, inputObj );
    /**
     * Array of nested Content data values.  As more pages are retrieved from the server this array will continue be updated.
     * @instance
     * @memberof Flybits.PagedData
     * @member {Array} data
     */
    this.data = [];
    if(inputObj){
      this.path = inputObj.path;
      this.key = inputObj.key;
      var paging = {
        total: inputObj.paging.totalRecords,
        limit: inputObj.paging.limit,
        offset: inputObj.paging.offset
      };

      /**
       * Function to request this nested Content data's next page. Do not invoke directly as it will not update the {@link Flybits.PagedData.data|data} array.  Instead invoke {@link Flybits.PagedData#getNext}
       * @instance
       * @memberof Flybits.PagedData
       * @member {function} nextPageFn
       */
      this.nextPageFn = ApiUtil.createNextPageCall(Flybits.api.ContentData.getPagedData,{
        path: this.path,
        key: this.key
      },paging);
    }

    if(inputObj && inputObj.data){
      this.fromJSON(inputObj.data);
    }
  }

  PagedData.prototype = Object.create( BaseModel.prototype );
  PagedData.prototype.constructor = PagedData;
  PagedData.prototype.implements( 'Serializable' );

  /**
   * Used to check the existence of a next page of data.
   * @function hasNext
   * @instance
   * @memberof Flybits.PagedData
   * @return Truthy value depending on the existence of a {@link Flybits.PagedData.nextPageFn|next page}.
   */
  PagedData.prototype.hasNext = function(){
    return this.nextPageFn;
  };

  /**
   * Retrieves the next page of nested Content data and also appends it to this instance's {@link Flybits.PagedData.data|data array}.
   * @function getNext
   * @instance
   * @memberof Flybits.PagedData
   * @return {external:Promise<Array,Flybits.Validation>} Promise that resolves with the next page of nested Content data.
   */
  PagedData.prototype.getNext = function(){
    if(!this.hasNext()){
      return Promise.resolve([]);
    }
    var def = new Deferred();
    var pagedData = this;
    this.nextPageFn().then(function(resp){
      pagedData.nextPageFn = resp.nextPageFn;
      pagedData.data.push.apply(pagedData.data,resp.result);
      def.resolve(resp.result);
    });

    return def.promise;
  };

  PagedData.prototype.fetchAll = function(){
    var def = new Flybits.Deferred();
    var pagedData = this;

    var recursiveFetch = function(){
      if(pagedData.hasNext()){
        pagedData.getNext().then(function(){
          recursiveFetch();
        }).catch(function(e){
          def.reject(e);
        });
      } else{
        def.resolve(pagedData.data);
      }
    };
    recursiveFetch();

    return def.promise;
  };

  /***** Serializable function overloading *****/
  PagedData.prototype.fromJSON = function(dataArr){
    var obj = this;

    for(var i = 0; i < dataArr.length; i++){
      var val = dataArr[i];
      if(val.constructor === Object){
        if(Object.prototype.hasOwnProperty.call(val,'localizations')){
          val.locales = val.localizations;
          delete val.localizations;
        }
      }
      obj.data.push(val);
    }
  };

  PagedData.prototype.toJSON = function(){
    var obj = this;
    var retObj = [];

    for(var i = 0; i < obj.data.length; i++){
      var val = obj.data[i];
      if(val.constructor === Object){
        if(Object.prototype.hasOwnProperty.call(val,'locales')){
          val.localizations = val.locales;
          delete val.locales;
        }
      }
      retObj.push(val);
    }

    return retObj;
  };

  return PagedData;
})();

/*global Flybits, BaseModel*/
/**
 * @classdesc Flybits core push token model
 * @class
 * @memberof Flybits
 * @extends BaseModel
 * @param {Object} serverObj Raw Flybits core model `Object` directly from API.
 */
Flybits.PushToken = (function () {
  var ObjUtil = Flybits.util.Obj;

  class PushToken extends BaseModel {
    /**
     * @typedef networkTypes
     * @memberof Flybits.PushToken
     * @type Object
     * @property {number} APNS (0) Apple Push Notification service.
     * @property {number} GCM (1) Google Cloud Messaging (Deprecated).
     * @property {number} HUAWEI (2) Huawei Push Kit.
     * @property {number} FCM (3) Firebase Cloud Messaging.
     */
    static networkTypes = {
      APNS: 0,
      GCM: 1,
      HUAWEI: 2,
      FCM: 3,
    };

    constructor(serverObj) {
      super(serverObj); // Replaces BaseModel.call(this, serverObj)
      
      if (serverObj) {
        this.fromJSON(serverObj);
      }
    }

    fromJSON(serverObj) {
      if (serverObj && serverObj.id) {
        this.id = serverObj.id;
      }

      /**
       * @instance
       * @memberof Flybits.PushToken
       * @member {string} deviceToken FCM or APNs device token string.
       */
      this.deviceToken = serverObj.deviceToken;

      if (typeof serverObj.network === 'string') {
        var netKey = serverObj.network.toUpperCase();
        if (PushToken.networkTypes[netKey] !== undefined) {
          this.network = PushToken.networkTypes[netKey];
        } else {
          this.network = serverObj.network; // Leave as-is; validation will catch it if invalid
        }
      } else {
        this.network = serverObj.network;
      }

      /**
       * @instance
       * @memberof Flybits.PushToken
       * @member {number} pushVersion The push version - Optional.
       */
      if (serverObj.pushVersion !== undefined) {
        this.pushVersion = serverObj.pushVersion;
      }

      /**
       * @instance
       * @memberof Flybits.PushToken
       * @member {Object} [data] Optional object linking the token to a user ID.
       */
      if (serverObj.data) {
        this.data = serverObj.data;
      }

      return this;
    }

    toJSON() {
      var retObj = {
        deviceToken: this.deviceToken,
        network: this.network,
      };

      if (this.pushVersion !== undefined) {
        retObj.pushVersion = this.pushVersion;
      }

      if (this.data) {
        retObj.data = this.data;
      }

      if (this.id) {
        retObj.id = this.id;
      }

      return retObj;
    }
  }

  /**
   * @memberof Flybits.PushToken
   * @constant {Object} reqKeys Map of model properties.
   */
  PushToken.prototype.reqKeys = PushToken.reqKeys = ObjUtil.extend(
    BaseModel.prototype.reqKeys,
    {
      deviceToken: 'deviceToken',
      network: 'network',
      pushVersion: 'pushVersion',
      data: 'data',
    }
  );

  return PushToken;
})();
/*global Flybits,SurveyAnswer*/
/**
 * @classdesc Flybits core Survey model. Contains questions and can facilitate survey submission.
 * @deprecated
 * @class
 * @extends Flybits.Content
 * @memberof Flybits
 * @param {Object} serverObj Raw Flybits core model `Object` directly from the API.
 */
Flybits.Survey = (function(){
  var Content = Flybits.Content;
  
  function Survey( serverObj ){
    console.warn('This class is deprecated');
    Content.call( this, serverObj );
    /**
     * @instance
     * @memberof Flybits.Survey
     * @member {Flybits.SurveyQuestion[]} questions Questions of the survey
     */
    this.questions = [];
    var surveyMeta = serverObj.surveyMetadata || {};
    this.resultID = surveyMeta.surveyResultInstanceID;
    this.answerID = surveyMeta.surveyAnswersInstanceID;

    if(this.data){
      this.parseQuestions(this.data);
    }
  }

  Survey.prototype = Object.create( Content.prototype );
  Survey.prototype.constructor = Survey;
  Survey.prototype.implements( 'Serializable' );

  Survey.prototype.parseQuestions = function(dataObj){
    var dataRoot = {};
    if(dataObj && dataObj.result && dataObj.result.length){
      dataRoot = dataObj.result[0].payload;
    }

    if(dataRoot.questions && dataRoot.questions.data){
      this.questions = dataRoot.questions.data.map(function(q){
        return new Flybits.SurveyQuestion(q);
      });
    }
  };

  /**
   * Retrieves questions of the survey.
   * @function getQuestions
   * @instance
   * @memberof Flybits.Survey
   * @return {external:Promise<Flybits.SurveyQuestion[],Flybits.Validation>} An array containing the questions of the survey or a `Validation` object if unsuccessful.
   */
  Survey.prototype.getQuestions = function(){
    var survey = this;
    var def = new Flybits.Deferred();
    var pagedData;
    this.getData().then(function(dataRoot){
      pagedData = dataRoot.questions;
      return pagedData.fetchAll();
    }).then(function(){
      survey.questions = pagedData.data.map(function(q){
        return new Flybits.SurveyQuestion(q);
      });
      def.resolve(survey.questions);
    }).catch(function(e){
      def.reject(e);
    });

    return def.promise;
  };

  var createNextPageWrapperFn = function(fn,survey){
    return function(){
      var def = new Flybits.Deferred();
      var nextPageFn;
      
      fn().then(function(resp){
        var fetchPromises = resp.result.map(function(obj){
          return Flybits.SurveyResponse.parseResponse(obj, survey);
        });
        nextPageFn = resp.nextPageFn;
        return Flybits.Deferred.settlePromises(fetchPromises);
      }).then(function(promises){
        def.resolve({
          result: promises.map(function(result){
            return result.result;
          }),
          contentID: survey.answerID,
          nextPageFn: createNextPageWrapperFn(nextPageFn,survey)
        });
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    };
  };

  /**
   * Retrieves previously submitted responses to the survey.  If you are an admin you will see all responses from all users, otherwise you will only see your own responses.
   * @function getResponses
   * @instance
   * @memberof Flybits.Survey
   * @returns {external:Promise<Flybits.api.Result,Flybits.Validation>} Promise that resolves with result object containing and array of {@link Flybits.SurveyResponse}.
   */
  Survey.prototype.getResponses = function(){
    var survey = this;

    return createNextPageWrapperFn(function(){
      return Flybits.api.ContentData.getAll({
        contentID: survey.answerID,
        paging: {
          limit: 2
        }
      });
    },survey)();
  };

  /**
   * Triggers the validation of all answers pertaining to all questions within the survey.
   * @function validate
   * @instance
   * @memberof Flybits.Survey
   * @return {Flybits.Validation} `Validation` instance pertaining to the result of validation checks on all question answers.
   */
  Survey.prototype.validate = function(){
    var validation = this.questions.reduce(function(accumalator, question){
      return accumalator.merge(question.validate());
    }, new Flybits.Validation());
    
    return validation;
  };

  /**
   * Submits a response to the survey which includes all answers to the associated questions.
   * @function submit
   * @instance
   * @memberof Flybits.Survey
   * @return {external:Promise<Flybits.api.Result,Flybits.Validation>} `Validation` instance if unsuccessful.
   */
  Survey.prototype.submit = function(){
    var def = new Flybits.Deferred();

    var answer = new SurveyAnswer({
      id: this.answerID,
      surveyID: this.id
    });
    for(var i = 0; i < this.questions.length; i++){
      answer.parseAnswer(this.questions[i]);
    }

    Flybits.api.ContentData.submitData(answer.data).then(function(resp){
      def.resolve(resp);
    }).catch(function(e){
      def.reject(e);
    });


    return def.promise;
  };

  return Survey;
})();
/*global Flybits,Path*/
// eslint-disable-next-line no-unused-vars
var SurveyAnswer = (function(){
  var Content = Flybits.Content;

  function SurveyAnswer(obj){
    console.warn('This class is deprecated');
    Content.call(this, obj);
    this.surveyID = obj.surveyID;
    this.qAnswers = [];
    this.path = new Path().append(this);
    this.data = new Flybits.ContentData({},this.path);
    this.data.payload = {
      surveyId: this.surveyID,
      answers: this.qAnswers
    };
  }

  SurveyAnswer.prototype = Object.create(Content.prototype);
  SurveyAnswer.prototype.constructor = SurveyAnswer;
  SurveyAnswer.prototype._pathName = SurveyAnswer._pathName = 'content/survey/instances';

  SurveyAnswer.prototype.parseAnswer = function(question){
    if(question.id && !Flybits.util.Obj.isNull(question.answer)){

      this.qAnswers.push({
        questionNumber: question.id,
        answer: question.answer instanceof Array?question.answer:[question.answer+'']
      });
    }

    return this;
  };

  return SurveyAnswer;
})();
/*global Flybits*/
/**
 * @classdesc A SurveyChoice model instance represents a possible answer to a {@link Flybits.SurveyQuestion| SurveyQuestion}.
 * @deprecated
 * @class
 * @memberof Flybits
 * @param {Object} serverObj Raw `Object` directly from the API from within the question model.
 */
Flybits.SurveyChoice = (function(){
  /**
   * A dictionary mapping of locale keys to localized string values.  
   * @typedef LocalizedObject
   * @memberof Flybits.SurveyChoice
   * @type Object
   * @example
   * { 
   *   en: 'falafel',
   *   fr: 'falafel'
   * }
   */

  function SurveyChoice(serverObj){
    console.warn('This class is deprecated');
    /**
     * @instance
     * @memberof Flybits.SurveyChoice
     * @member {string} key ID of the choice.
     */
    this.key = serverObj.key;
    /**
     * @instance
     * @memberof Flybits.SurveyChoice
     * @member {Flybits.SurveyChoice.LocalizedObject} title Localized display title for the answer choice.
     */
    this.title = Flybits.util.Obj.toLocaleValueMap(serverObj, 'value');
  }

  return SurveyChoice;
})();
/*global Flybits,SurveyValidation*/
/**
 * @classdesc Flybits core SurveyQuestion model. A SurveyQuestion model instance represents a question within a Survey instance.
 * @deprecated
 * @class
 * @memberof Flybits
 * @param {Object} serverObj Raw Flybits core model `Object` directly from the API.
 */
Flybits.SurveyQuestion = (function(){
  
  /**
   * A dictionary mapping of locale keys to localized string values.  
   * @typedef LocalizedObject
   * @memberof Flybits.SurveyQuestion
   * @type Object
   * @example
   * { 
   *   en: 'falafel',
   *   fr: 'falafel'
   * }
   */

  /**
   * UI Constraints based on question type.
   * @typedef UIConstraints
   * @memberof Flybits.SurveyQuestion
   * @type Object
   * @property {Object} number UI Constraints for the `NUMBER` type of question
   * @property {boolean} number.validation Flag indicating whether or not question has number constraints.
   * @property {number} number.min Minimum value for a number.
   * @property {number} number.max Maximum value for a number.
   * @property {number} number.increment Allowed answer step size. For instance if the `increment` was set to `2` and the `min` was set to `0`, the user should only be allowed to enter `0,2,4,6,...`
   */

  function SurveyQuestion( serverObj ){
    console.warn('This class is deprecated');
    /**
     * @instance
     * @memberof Flybits.SurveyQuestion
     * @member {boolean} isRequired Flag to indicate whether or not this survey question is mandatory.
     */
    this.isRequired = false;
    /**
     * @instance
     * @memberof Flybits.SurveyQuestion
     * @member {number} id Unique identifier of survey question.
     */
    this.id =  0;
    /**
     * @instance
     * @memberof Flybits.SurveyQuestion
     * @member {Flybits.SurveyChoice[]} choices Predefined answer options for questions that only allow selection from a subset of responses. For instance questions that contain a dropdown or radio buttons/checkboxes.
     */
    this.choices = [];
    /**
     * @instance
     * @memberof Flybits.SurveyQuestion
     * @member {Flybits.SurveyQuestion.LocalizedObject} title The localized string label of the question.
     */
    this.title = {};
    /**
     * @instance
     * @memberof Flybits.SurveyQuestion
     * @member {string} type Question type that can be found in class {@link Flybits.SurveyQuestion.types|constants.}
     */
    this.type = 'shortAnswer';
    /**
     * @instance
     * @memberof Flybits.SurveyQuestion
     * @member {Flybits.SurveyQuestion.UIConstraints} meta Metadata for the question type.  This may include UI constraints based on question type.
     */
    this.meta = {
      number: {}
    };
    this.answer = undefined;
    this.validation = undefined;

    if(serverObj){
      this.fromJSON(serverObj);
    }
  }
  
  /**
   * @memberof Flybits.SurveyQuestion
   * @member {Object} types Mapping of question types
   * @constant
   * @property {string} SHORTANSWER Short answer that should be displayed as a single line input box.
   * @property {string} LONGANSWER Long answer that should be displayed as a text area input box.
   * @property {string} NUMBER Answer that only accepts numbers.
   * @property {string} MULTIPLECHOICE Type that allows the user to select only one answer out of multiple predefined {@link Flybits.SurveyQuestion#choices|choice options}.
   * @property {string} CHECKBOXES Type that allows the user to select multiple answers out of multiple predefined {@link Flybits.SurveyQuestion#choices|choice options}.
   * @property {string} DROPDOWN Type that allows the user to select only one answer out of multiple predefined {@link Flybits.SurveyQuestion#choices|choice options}.  This is different from {@link Flybits.SurveyQuestion.types|MULTIPLECHOICE} simply by its UI component.  The question choices should be displayed in a dropdown picker.
   * @property {string} DATE Simple type that should provide the user with a date picker.  Dates should be stored in the form of an {@link external:ISO 8601|ISO 8601} string.
   * @property {string} TIME Simple type that should provide the user with a time picker. Dates should be stored in the form of an {@link external:ISO 8601|ISO 8601} string.
   */
  SurveyQuestion.prototype.types = SurveyQuestion.types = {
    SHORTANSWER: 'shortAnswer',
    LONGANSWER: 'longAnswer',
    NUMBER: 'number',
    MULTIPLECHOICE: 'multipleChoice',
    CHECKBOXES: 'checkboxes',
    DROPDOWN: 'dropdown',
    DATE: 'date',
    TIME: 'time',
    LOCATION: 'location',
    FILEUPLOAD: 'fileUpload'
  };

  SurveyQuestion.prototype.fromJSON = function(serverObj){
    if (serverObj.metadata) {
      this.meta = JSON.parse(serverObj.metadata);
    }
    this.isRequired = this.meta.isRequired;
    this.id = serverObj.questionNumber;
    this.type = this.meta.uiType;
    this.title = Flybits.util.Obj.toLocaleValueMap(serverObj, 'question');

    if(serverObj.choices && serverObj.choices.length){
      this.choices = serverObj.choices.map(function(choice){
        return new Flybits.SurveyChoice(choice);
      });
    }
  };

  /**
   * Sets the answer of the survey question.
   * @function setAnswer
   * @instance
   * @memberof Flybits.SurveyQuestion
   * @param {string} answer Answer can be a singular string value or an array of string answers depending on question type.  Only CHECKBOXES type questions can have multiple answers.
   * @return {Flybits.SurveyQuestion} The `SurveyQuestion` instance the method has been invoked upon to allow for method chaining.
   */
  SurveyQuestion.prototype.setAnswer = function(answer){
    if(this.type !== SurveyQuestion.types.CHECKBOXES && answer instanceof Array){
      throw new Flybits.Validation().addError('Invalid answer','Only CHECKBOXES type questions can contain multiple answers.',{
        context: answer,
        code: Flybits.Validation.type.INVALIDARG
      });
    }

    this.answer = answer;

    return this;
  };

  /**
   * Triggers the validation of the question answer.
   * @function validate
   * @instance
   * @memberof Flybits.SurveyQuestion
   * @return {Flybits.Validation} `Validation` instance pertaining to the result of validation checks on this question's answer.
   */
  SurveyQuestion.prototype.validate = function(){
    var validator = SurveyValidation.getValidator(this);
    this.validation = validator.validate();
    return this.validation;
  };

  return SurveyQuestion;

})();
/*global Flybits*/
/**
 * @classdesc A SurveyResponse model instance represents a user submitted response to a {@link Flybits.Survey|Survey} instance.
 * @deprecated
 * @class
 * @memberof Flybits
 * @param {Object} serverObj Raw `Object` directly from the API.
 */
Flybits.SurveyResponse = (function(){
  /**
   * A map of a {@link Flybits.SurveyQuestion|SurveyQuestion} to its corresponding answer.
   * @typedef QuestionAnswerMap
   * @memberof Flybits.SurveyResponse
   * @type Object
   * @property {Flybits.SurveyQuestion} question Full survey question model.
   * @property {string|Flybits.SurveyChoice[]} answer Corresponding answer to question.  If question {@link Flybits.SurveyQuestion#type|type} is one of which that contains bucketed {@link Flybits.SurveyChoice|choice options}, then the `answer` will be an array of user selected {@link Flybits.SurveyChoice|choice options}.  Otherwise, primitive answers will simply be a `string`.
   */

  function SurveyResponse(serverObj){
    console.warn('This class is deprecated');
    /**
     * @instance
     * @memberof Flybits.SurveyResponse
     * @member {string} id ID of reponse.  Equivalent to a {@link Flybits.ContentData|ContentData} ID.
     */
    this.id;
    /**
     * @instance
     * @memberof Flybits.SurveyResponse
     * @member {Flybits.SurveyResponse.QuestionAnswerMap[]} answers 
     */
    this.answers = [];
    if(serverObj){
      this.fromJSON(serverObj);
    }
  }

  SurveyResponse.prototype.fromJSON = function(serverObj){
    this.id = serverObj.id;
  };

  /**
   * Factory method that creates an instance and conveniently maps raw response IDs to {@link Flybits.SurveyQuestion|SurveyQuestion} entities. 
   * @memberof Flybits.SurveyResponse
   * @function
   * @param {object} serverObj 
   * @param {Flybits.Survey} survey 
   */
  SurveyResponse.parseResponse = function(serverObj,survey){
    var def = new Flybits.Deferred();
    var instance = new Flybits.SurveyResponse(serverObj);
    var pagedAnswers = serverObj.payload.answers;

    pagedAnswers.fetchAll().then(function(allAnswers){
      instance.answers = allAnswers.map(function(answerObj){
        var question = survey.questions.filter(function(question){
          return question.id === answerObj.questionNumber;
        })[0];
        var answer = answerObj.answer[0];
        if(question.choices.length){
          answer = answerObj.answer.map(function(id){
            return question.choices.filter(function(choice){
              return choice.key === id;
            })[0];
          });
        }
        return {
          answer: answer,
          question: question
        };
      });
      def.resolve(instance);
    }).catch(function(e){
      def.reject(e);
    });

    return def.promise;
  };

  return SurveyResponse;
})();
/*global Flybits*/
// eslint-disable-next-line no-unused-vars
var SurveyValidation = (function(){
  
  /**
   * Base Validator class
   */
  var BaseValidator = (function(){
    function BaseValidator(question){
      console.warn('This class is deprecated');
      this.question = question;
    }
  
    BaseValidator.prototype.validate = function(){
      var validation = new Flybits.Validation();
      if(this.question.isRequired){
        if(this.question.answer instanceof Array){
          var emptyAnswers = this.question.answer.filter(function(a){
            return Flybits.util.Obj.isNull(a);
          });
          if(!this.question.answer.length || emptyAnswers.length){
            validation.addError('Missing answer','This is a required question.',{
              code: Flybits.Validation.type.MISSINGARG,
              context: this.question
            });
          }
        } else if(Flybits.util.Obj.isNull(this.question.answer)){
          validation.addError('Missing answer','This is a required question.',{
            code: Flybits.Validation.type.MISSINGARG,
            context: this.question
          });
        }
      }
  
      return validation;
    };

    return BaseValidator;
  })();

  /**
   * Number Validator
   */
  var NumberValidator = (function(){
    function NumberValidator(question){
      BaseValidator.call(this, question);
    }

    NumberValidator.prototype = Object.create(BaseValidator.prototype);
    NumberValidator.prototype.constructor = NumberValidator;

    NumberValidator.prototype.validate = function(){
      var validation = BaseValidator.prototype.validate.call(this);
      if(isNaN(this.question.answer)){
        validation.addError('Invalid answer type','Answer must be a number.',{
          code: Flybits.Validation.type.INVALIDARG,
          context: this.question
        });
      }

      var questionMeta = this.question.meta;
      if(questionMeta && questionMeta.number){
        var numMeta = questionMeta.number;
        if(Object.prototype.hasOwnProperty.call(numMeta,'min') && this.question.answer < numMeta.min){
          validation.addError('Invalid range','Answer must be greater than or equal to '+numMeta.min,{
            code: Flybits.Validation.type.INVALIDARG,
            context: this.question
          });
        }
        if(Object.prototype.hasOwnProperty.call(numMeta,'max') && this.question.answer > numMeta.max){
          validation.addError('Invalid range','Answer must be less than or equal to '+numMeta.max,{
            code: Flybits.Validation.type.INVALIDARG,
            context: this.question
          });
        }
        if(Object.prototype.hasOwnProperty.call(numMeta,'increment') && (this.question.answer % (((numMeta.min || 0) + numMeta.increment) !== 0) || this.question.answer === (numMeta.min || 0))){
          validation.addError('Invalid range','Answer must be a multiple of '+((numMeta.min || 0)+numMeta.increment),{
            code: Flybits.Validation.type.INVALIDARG,
            context: this.question
          });
        }
      }

      return validation;
    }
  })();

  var MultipleChoiceValidator = (function(){
    function MultipleChoiceValidator(question){
      BaseValidator.call(this, question);
    }

    MultipleChoiceValidator.prototype = Object.create(BaseValidator.prototype);
    MultipleChoiceValidator.prototype.constructor = MultipleChoiceValidator;

    MultipleChoiceValidator.prototype.validate = function(){
      var validation = BaseValidator.prototype.validate.call(this);

      var nullAnswers = this.question.answer.filter(function(q){
        return Flybits.util.Obj.isEmpty(q);
      });
      
      if(nullAnswers.length){
        validation.addError('Invalid values','Cannot have null values',{
          code: Flybits.Validation.type.INVALIDARG,
          context: this.question
        });
      }

      return validation;
    };
  })();

  var SurveyValidation = {
    validatorMap: {
      shortAnswer: BaseValidator,
      longAnswer: BaseValidator,
      number: NumberValidator,  
      multipleChoice: BaseValidator,
      checkboxes: MultipleChoiceValidator,
      dropdown: BaseValidator
    },
    getValidator: function(surveyQuestion){
      var ValidatorClass = this.validatorMap[surveyQuestion.type] || BaseValidator;

      return new ValidatorClass(surveyQuestion);
    }
  };

  return SurveyValidation;
})();
/*global Flybits,BaseModel*/
/**
 * @classdesc Flybits core user model
 * @class
 * @memberof Flybits
 * @extends BaseModel
 * @implements {Flybits.interface.Serializable}
 * @param {Object} serverObj Raw Flybits core model `Object` directly from API.
 */
Flybits.User = (function(){
  var ObjUtil = Flybits.util.Obj;

  function User(serverObj){
    BaseModel.call(this,serverObj);
    if(serverObj){
      this.fromJSON(serverObj);
    }
  }
  User.prototype = Object.create(BaseModel.prototype);
  User.prototype.constructor = User;
  User.prototype.implements('Serializable');

  /**
   * @memberof Flybits.User
   * @constant {Object} reqKeys Map of model properties that can be used to order by and search for this model.  Currently comprising of, `id`, `email`, `firstName`, and `lastName`
   */
  User.prototype.reqKeys = User.reqKeys = ObjUtil.extend(BaseModel.prototype.reqKeys,{
    email: 'email',
    firstName: 'firstName',
    lastName: 'lastName'
  });

  User.prototype.fromJSON = function(serverObj){
    if(serverObj && serverObj.id){
      this.id = serverObj.id;
    }
    
    /**
     * @instance
     * @memberof Flybits.User
     * @member {string} email Registered email of the user.
     */
    this.email = serverObj.email;
    /**
     * @instance
     * @memberof Flybits.User
     * @member {string} firstName First name of the user.
     */
    this.firstName = serverObj.firstName;
    /**
     * @instance
     * @memberof Flybits.User
     * @member {string} lastName Last name of the user.
     */
    this.lastName = serverObj.lastName;

    /**
     * @instance
     * @memberof Flybits.User
     * @member {boolean} isConfirmed Flag indicating whether or not this user has successfully verified their account.
     */
    this.isConfirmed = serverObj.isConfirmed;

    /**
     * @instance
     * @memberof Flybits.User
     * @member {number} permissionLevel Permission level of the signed in user.
     */
    this.permissionLevel = serverObj.level;

    return this;
  };

  User.prototype.toJSON = function(){
    var retObj = {
      email: this.email,
      firstName: this.firstName,
      lastName: this.lastName,
      isConfirmed: this.isConfirmed,
      level: this.permissionLevel
    };

    if(this.id){
      retObj.id = this.id;
    }

    return retObj;
  };

  return User;
})();

/*global Flybits*/
/**
 * @classdesc Standard class used across the SDK to indicate the state of an asynchronous validation event or error.  It is comprised of a `state` which indicates the result of an operation and also an `errors` array should the `state` be `false`.  This class is often used as the error object returned from API operations and also as the result of model based validation which can incur multiple errors at once.
 * @class
 * @memberof Flybits
 */
Flybits.Validation = (function(){
  /**
   * @typedef ValidationError
   * @memberof Flybits.Validation
   * @type Object
   * @property {string} header Generally a short and broad error message
   * @property {string} message A more in depth explanation of the error.
   * @property {string} context This is populated if an error occurs that relates to one of the input properties of an operation and will be the property's key.
   * @property {number} code An internal error code indicating error type. This property is only populated when errors that can be discerned by the SDK occur. Errors that occur server side and cannot be discerned by the SDK will populate an HTTP status code in the `serverCode` property.  For instance, if you forget to supply required property the `code` property would be populated with `Flybits.Validation.type.MISSINGARG`.  On the other hand if there's a server outage, the `serverCode` would be populated with a 404 or 500.
   * @property {number} serverCode This is populated with an HTTP status code when a server side error occurs that cannot be discerned by the SDK.
   */

  function Validation(){
    /**
     * @instance
     * @memberof Flybits.Validation
     * @member {boolean} state Indicates the resultant state of an asynchronous task.
     */
    this.state = true;
    /**
     * @instance
     * @memberof Flybits.Validation
     * @member {Flybits.Validation.ValidationError[]} errors An array of errors that have accumulated because of an asynchronous task.
     */
    this.errors = [];

    /**
     * @instance
     * @memberof Flybits.Validation
     * @member {string} stacktrace Stacktrace of where the Validation model was created.  Ideally it should mark where the error has occurred.  This can be explicitly marked by calling {@link Flybits.Validation#setStackTrace}
     */
    this.stacktrace = '';

    this.setStackTrace();
  }

  Validation.prototype = {
    /**
     * Used to add error objects to the `Validation` instance.
     * @function
     * @instance
     * @memberof Flybits.Validation
     * @param {string} header Generally a short and broad error message
     * @param {string} message A more in depth explanation of the error.
     * @param {Object} detailsObj Optional extra details about the error.
     * @param {string} detailsObj.context This is populated if an error occurs that relates to one of the input properties of an operation and will be the property's key.
     * @param {number} detailsObj.code An internal error code indicating error type. This property is only populated when errors that can be discerned by the SDK occur. Errors that occur server side and cannot be discerned by the SDK will populate an HTTP status code in the `serverCode` property.  For instance, if you forget to supply required property the `code` property would be populated with `Flybits.Validation.type.MISSINGARG`.  On the other hand if there's a server outage, the `serverCode` would be populated with a 404 or 500.
     * @param {number} detailsObj.serverCode This is populated with an HTTP status code when a server side error occurs that cannot be discerned by the SDK.
     * @return {Flybits.Validation} The `Validation` instance the method has been invoked upon to allow for method chaining.
     */
    addError: function(header,message,detailsObj){
      this.state = false;
      var retObj = {
        header: header,
        message: message
      };
      if(detailsObj){
        retObj.context = detailsObj.context;
        retObj.code = detailsObj.code;
        retObj.serverCode = detailsObj.serverCode;
      }
      this.errors.push(retObj);

      return this;
    },
    /**
     * Used to merge two validation objects together.
     * @function
     * @instance
     * @memberof Flybits.Validation
     * @param {Flybits.Validation} validation The `Validation` instance that is to be merged into this instance.
     * @return {Flybits.Validation} The `Validation` instance the method has been invoked upon to allow for method chaining.
     */
    merge: function(validation){
      Array.prototype.push.apply(this.errors,validation.errors);
      this.state = this.errors.length < 1;

      return this;
    },
    /**
     * Used to retrieve the first available error if available.
     * @function
     * @instance
     * @memberof Flybits.Validation
     * @return {Flybits.Validation.ValidationError} First available error if validation state is `false` and errors have been found.
     * @return {null} `null` if no errors are available.
     */
    firstError: function(){
      if(this.errors.length > 0){
        return this.errors[0];
      }
      return null;
    },
    /**
     * Sets the stack trace origin point.  This is useful for when the `Validation`
     * instance is created early in a code block but only sent in the promise 
     * rejection later in a code block.
     * @function
     * @instance
     * @memberof Flybits.Validation
     * @return {Flybits.Validation} The `Validation` instance the method has been invoked upon to allow for method chaining.
     */
    setStackTrace: function(){
      var flyError = new Error();
      flyError.name = 'FlybitsError';
      this.stacktrace = flyError.stack;
      if(this.stacktrace){
        this.stacktrace = this.stacktrace.split('\n').filter(function(str){
          return str.indexOf('Validation') < 0;
        }).join('\n');
      }
      return this;
    },

    toJSON: function(){
      return {
        state: this.state,
        errors: this.errors,
        stacktrace: this.stacktrace
      };
    }
  };

  /**
   * Static method parses a JSON string constructing a `Flybits.Validation` instance.
   * @memberof Flybits.Validation
   * @static
   * @function parse
   * @param {string} str JSON string to be parsed.
   * @returns {Flybits.Validation} Validation instance based on string input.
   * @throws {TypeError} Invalid JSON structures that do not represent a `Flybits.Validation` instance will throw this error.
   * @throws {SyntaxError} Invalid JSON strings will throw this error.
   */
  Validation.prototype.parse = Validation.parse = function(str){
    var obj = JSON.parse(str);
    return Validation.parseObj(obj);
  };

  /**
   * Static method parses an Object constructing a `Flybits.Validation` instance.
   * @memberof Flybits.Validation
   * @static
   * @function parseObj
   * @param {Object} obj Object to be parsed.
   * @returns {Flybits.Validation} Validation instance based on string input.
   * @throws {TypeError} Invalid JSON structures that do not represent a `Flybits.Validation` instance will throw this error.
   */
  Validation.prototype.parseObj = Validation.parseObj = function(obj){
    var validation = new Validation();
    if(!Object.prototype.hasOwnProperty.call(obj, 'state')){
      throw new TypeError('Missing \'state\' attribute');
    } else if(typeof obj.state !== 'boolean'){
      throw new TypeError('Unexpected type \'state\'. Expected boolean attribute');
    }
    if(!Object.prototype.hasOwnProperty.call(obj,'errors')){
      throw new TypeError('Missing \'errors\' array attribute');
    }
    if(obj.stacktrace){
      validation.stack = obj.stacktrace;
    }
    validation.state = obj.state;
    validation.errors = obj.errors.map(function(errObj){
      if(!Object.prototype.hasOwnProperty.call(errObj,'header')){
        throw new TypeError('Missing \'errors[].header\' attribute');
      }
      if(!Object.prototype.hasOwnProperty.call(errObj,'message')){
        throw new TypeError('Missing \'errors[].message\' attribute');
      }
      if(!Object.prototype.hasOwnProperty.call(errObj,'code') && !Object.prototype.hasOwnProperty.call(errObj,'serverCode')){
        throw new TypeError('Missing \'errors[].code\' and/or \'errors[].serverCode\' attribute');
      }

      return errObj;
    });

    return validation;
  };

  /**
   * @member {Object} type A mapping of SDK error codes.
   * @memberof Flybits.Validation
   * @constant
   * @property {number} MALFORMED=1000 This error is usually thrown when an input property supplied to an library operation is incorrectly formatted, or sometimes a server response is not recognized by the library.
   * @property {number} INVALIDARG=1001 This error is thrown when an input property supplied to an library operation is semantically incorrect.
   * @property {number} MISSINGARG=1002 This error is thrown when a required property is not supplied to an library operation.
   * @property {number} NOTFOUND=1003 Usually thrown when model retrieval has yielded no results with provided input parameters.
   * @property {number} CONNECTIONERROR=1004 Error thrown when the library loses connection to particular resources.
   * @property {number} UNAUTHENTICATED=1005 Error is thrown when library operation requires authentication and current session is not found or expired.
   * @property {number} RETRIEVALERROR=1006 This error is thrown when any retrieval library operation fails to complete.
   * @property {number} NOTSUPPORTED=1007 Error is thrown when an operation or entity is not supported by the library.
   * @property {number} UNEXPECTED=1008 Error is thrown when an operation failed due to unexpected behavior.
   * @property {number} NOOP=1009 Error is thrown when an operation ceases execution and requires no follow up action.  This could be an intentional valid scenario.
   */
  Validation.prototype.type = Validation.type = {};
  Validation.prototype.type.MALFORMED = Validation.type.MALFORMED = 1000;
  Validation.prototype.type.INVALIDARG = Validation.type.INVALIDARG = 1001;
  Validation.prototype.type.MISSINGARG = Validation.type.MISSINGARG = 1002;
  Validation.prototype.type.NOTFOUND = Validation.type.NOTFOUND = 1003;
  Validation.prototype.type.CONNECTIONERROR = Validation.type.CONNECTIONERROR = 1004;
  Validation.prototype.type.UNAUTHENTICATED = Validation.type.UNAUTHENTICATED = 1005;
  Validation.prototype.type.RETRIEVALERROR = Validation.type.RETRIEVALERROR = 1006;
  Validation.prototype.type.NOTSUPPORTED = Validation.type.NOTSUPPORTED = 1007;
  Validation.prototype.type.UNEXPECTED = Validation.type.UNEXPECTED = 1008;
  Validation.prototype.type.NOOP = Validation.type.NOOP = 1009;

  return Validation;
})();

/*global Flybits, BaseModel*/

/**
 * @classdesc Flybits core Zone class. A Zone represents an embeddable container meant to be injected into the digital channel.
 * @class
 * @memberof Flybits
 * @extends BaseModel
 * @param {Object} zone Raw Flybits core model `Object` directly from API.
 * @param {Object} modulesMap Object map of module IDs to {@link Flybits.Module}
 */
Flybits.Zone = (function () {
  function Zone(zone, modulesMap) {
    BaseModel.call(this, zone);
    this.modules = [];
    
    if (zone) {
      this.fromJSON(zone, modulesMap);
    }
  }

  Zone.prototype = Object.create(BaseModel.prototype);
  Zone.prototype.constructor = Zone;

  Zone.prototype.fromJSON = function (zone, modulesMap) {
    this.labels = zone.labels;
    this.height = zone.height;
    this.name = zone.name;
    this.referenceID = zone.referenceID;

    const model = this;
    if(zone.modulesIDs?.length && modulesMap){
      zone.modulesIDs.forEach(function(mID){
        if(modulesMap[mID]){
          model.modules.push(modulesMap[mID]);
        }
      });
    }
  };

  /**
   * @memberof Flybits.Zone
   * @instance
   * @function toConciergeContainer
   * @param {Object} [opts] Optional serialization specifications such as locale and text direction.
   * @param {string} [opts.textDirection=ltr] Text direction based on language type. Supports: 'ltr','rtl','auto'.
   * @param {string} [opts.locale=en] [Two character locale key](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) (eg. en, fr, es, etc) that specifies the preferred locale by which to render content.
   * @returns {Object} Prepared Zone payload object to be injected into a Concierge instance. 
   */
  Zone.prototype.toConciergeContainer = function (opts) {
    var textDir = 'ltr';

    switch(opts?.textDirection) {
    case 'rtl':
    case 'auto':
      textDir = opts?.textDirection
      break;
    }
    
    return {
      name: this.name,
      textDirection: textDir,
      containers: this.modules?.map(function (module) {
        return module.toConciergeModuleContainer(opts); 
      }),
    };
  };

  return Zone;
})();

/*global Flybits*/
/**
 * @classdesc Flybits core ZonesMetadata class. ZoneMetadata contains a list of non-sensitive metadata (eg. height) for all available Zones in a Flybits project.
 * @class
 * @memberof Flybits
 * @param {Object} serverObj Raw Flybits core model `Object` directly from API.
 */
Flybits.ZonesMetadata = (function () {
  var Zone = Flybits.Zone;

  function ZonesMetadata(serverObj) {
    if (serverObj) {
      this.fromJSON(serverObj);
    }
  }

  ZonesMetadata.prototype.fromJSON = function (serverObj) {
    this.zones = serverObj.zones
      .filter(
        (e) =>
          e.height !== undefined &&
          e.height > 0 &&
          e.referenceID !== undefined &&
          e.referenceID.length > 0
      )
      .map((e) => new Zone(e));
  };

  /**
   * @typedef ZoneMetadata
   * @memberof Flybits.ZonesMetadata
   * @type Flybits.Zone
   * @property {string} id Zone ID.
   * @property {string} name Zone name.
   * @property {string} referenceID Zone Reference ID.
   * @property {string[]} labels List of labels categorizing the Zone.
   * @property {number} height Pixel height of entire Zone container.
   */

  /**
   * Returns a list of Zone metadata for specified Zone Reference IDs.
   * @memberof Flybits.ZonesMetadata
   * @instance
   * @function getZones
   * @property {string[]} referenceIDs Array of Zone Reference IDs.
   * @return {Flybits.ZonesMetadata.ZoneMetadata} List of Zone metadata for specified zones.
   * >**Note:** Results may appear of type {@link Flybits.Zone} however only metadata attributes will be populated as defined in {@link Flybits.ZonesMetadata.ZoneMetadata}
   */
  ZonesMetadata.prototype.getZones = function (referenceIDs) {
    return this.zones.filter((e) => referenceIDs.includes(e.referenceID));
  };

  return ZonesMetadata;
})();

/*global Flybits,KeychainStore,ForageStore,IDBStore,LocalStorageStore,FileStore,MemoryStore*/
Flybits.store.Property = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;
  var _ready = new Deferred();

  var resolveStorageEngine = function(){
    var def = new Deferred();
    var availableStorage = [KeychainStore, ForageStore,IDBStore,LocalStorageStore,CookieStore,FileStore,MemoryStore];
    var checkStorageEngine = function(){
      if(availableStorage.length < 1){
        def.reject(new Validation().addError('No supported property storage engines'))
      }
      var CurEngine = availableStorage.shift();
      CurEngine.isSupported().then(function(){
        def.resolve(new CurEngine(Flybits.cfg.store.SDKPROPS));
      }).catch(function(){
        checkStorageEngine();
      });
    };
    checkStorageEngine();

    return def.promise;
  };

  var Property = {
    ready: _ready.promise,
    init: function(){
      var property = this;
      var engineInit = resolveStorageEngine();
      return engineInit.then(function(engine) {
        property.storageEngine = engine;
        _ready.resolve();
      });
    },
    remove: function(key){
      return this.storageEngine.removeItem(key);
    },
    set: function(key, value){
      if(!value){
        return this.remove(key);
      }
      return this.storageEngine.setItem(key, value);
    },
    get: function(key){
      return this.storageEngine.getItem(key);
    }
  };

  return Property;
})();

/*global Flybits,BaseObj,ForageStore,IDBStore,FileStore,MemoryStore*/
Flybits.store.Record = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;

  var resolveStorageEngine = function(storeName){
    var def = new Deferred();
    var availableStorage = [ForageStore,IDBStore,FileStore,MemoryStore];
    var checkStorageEngine = function(){
      if(availableStorage.length < 1){
        def.reject(new Validation().addError('No supported property storage engines'))
      }
      var CurEngine = availableStorage.shift();
      CurEngine.isSupported().then(function(){
        def.resolve(new CurEngine(storeName));
      }).catch(function(){
        checkStorageEngine();
      });
    };
    checkStorageEngine();

    return def.promise;
  };

  function RecordStore(storeName){
    BaseObj.call(this);
    var store = this;
    this.storeName = storeName || 'flb.records.'+Date.now();
    var def = new Deferred();
    this.ready = def.promise;

    var engineInit = resolveStorageEngine(this.storeName);
    engineInit.then(function(engine){
      store.storageEngine = engine;
      def.resolve();
    });
  }

  RecordStore.prototype = Object.create(BaseObj.prototype);
  RecordStore.prototype.constructor = RecordStore;

  RecordStore.prototype.remove = function(key){
    return this.storageEngine.removeItem(key);
  };
  RecordStore.prototype.set = function(key, value){
    if(!value){
      return this.remove(key);
    }
    return this.storageEngine.setItem(key, value);
  };
  RecordStore.prototype.get = function(key){
    return this.storageEngine.getItem(key);
  };
  RecordStore.prototype.keys = function(){
    return this.storageEngine.getKeys();
  };
  RecordStore.prototype.clear = function(){
    return this.storageEngine.clearAll();
  };

  return RecordStore;
})();
/*global Flybits,BaseObj*/
// eslint-disable-next-line no-unused-vars
var CookieStore = Flybits.store._storageEngines.CookieStore = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;
  var BrowserUtil = Flybits.util.Browser;

  function CookieStore(){
    BaseObj.call(this);
  }

  CookieStore.prototype = Object.create(BaseObj.prototype);
  CookieStore.prototype.constructor = CookieStore;
  CookieStore.prototype.implements('PropertyStore');

  CookieStore.prototype.isSupported = CookieStore.isSupported = function(){
    var def = new Deferred();
    var validation = new Validation();
    var support = typeof document === 'object' && 'cookie' in document;
    if(support){
      try{
        BrowserUtil.setCookie('flbstoresupport','true');
        BrowserUtil.setCookie('flbstoresupport','true',new Date(0));
        def.resolve();
      } catch(e){
        def.reject(validation.addError('Storage not supported','Access error:' + e,{
          context: 'cookie'
        }));
      }
    } else{
      def.reject(validation.addError('Storage not supported','Missing reference in namespace.',{
        context: 'cookie'
      }));
    }

    return def.promise;
  };

  CookieStore.prototype.getItem = function(key){
    return Promise.resolve(BrowserUtil.getCookie(key));
  };
  CookieStore.prototype.setItem = function(key, value){
    BrowserUtil.setCookie(key, value);
    return Promise.resolve();
  };
  CookieStore.prototype.removeItem = function(key){
    BrowserUtil.setCookie(key, '', new Date(0));
    return Promise.resolve();
  };

  return CookieStore;

})();
/*global Flybits,BaseObj,Persistence*/
// eslint-disable-next-line no-unused-vars
var FileStore = Flybits.store._storageEngines.FileStore = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;

  function FileStore(storeName){
    BaseObj.call(this);
    storeName = storeName || 'flb.general';
    this.cache = {};
    this.promiseQueue = [];
    this.storage = Persistence.create({
      dir: Flybits.cfg.store.RESOURCEPATH + '/'+storeName,
      writeQueue: true,
    });
    this.storage.initSync();
  }

  FileStore.prototype = Object.create(BaseObj.prototype);
  FileStore.prototype.constructor = FileStore;
  FileStore.prototype.implements('PropertyStore');

  FileStore.prototype.isSupported = FileStore.isSupported = function(){
    var def = new Deferred();
    var validation = new Validation();
    var support = typeof fs === 'object' && typeof Persistence === 'object' && typeof __dirname === 'string';
    if(support){
      def.resolve();
    } else{
      def.reject(validation.addError('Storage not supported','Missing reference in namespace.',{
        context: 'fs'
      }));
    }

    return def.promise;
  };

  FileStore.prototype.getItem = function(key){
    return this.cache[key] ? 
      Promise.resolve(JSON.parse(JSON.stringify(this.cache[key]))):
      this.storage.getItem(key);
  };
  FileStore.prototype.setItem = function(key, value){
    let def = new Deferred();
    if(!value){
      return this.removeItem(key);
    } else{
      this.cache[key] = JSON.parse(JSON.stringify(value));
      const promise = this.storage.setItem(key,value);
      const queueObj = {
        key: key, 
        promise: promise
      };
      this.promiseQueue.push(queueObj);
      promise.catch(() => {}).then(() => {
        Flybits.util.Obj.removeObject(this.promiseQueue, queueObj);
        def.resolve();
      });
    }

    return def.promise;
  };
  FileStore.prototype.removeItem = function(key){
    let def = new Deferred();
    delete this.cache[key];
    let foundPromise = Promise.resolve();
    for(let i = this.promiseQueue.length-1; i >= 0; i--){
      let queueObj = this.promiseQueue[i];
      if(queueObj.key === key){
        foundPromise = queueObj.promise;
        break;
      }
    }

    foundPromise.catch(() => {} ).then(async () => {
      await this.storage.removeItem(key);
      def.resolve();
    })

    return def.promise;
  };
  FileStore.prototype.getKeys = function(){
    const cachedKeys = Object.keys(this.cache);
    return cachedKeys.length ? 
      cachedKeys :
      this.storage.keys();
  };
  FileStore.prototype.clearAll = function(){
    this.cache = {};
    return this.storage.clear();
  };

  return FileStore;

})();
/*global Flybits,BaseObj,localforage*/
// eslint-disable-next-line no-unused-vars
var ForageStore = Flybits.store._storageEngines.ForageStore = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;

  function ForageStore(storeName){
    BaseObj.call(this);
    this.store = localforage.createInstance({
      name: storeName
    });
  }

  ForageStore.prototype = Object.create(BaseObj.prototype);
  ForageStore.prototype.constructor = ForageStore;
  ForageStore.prototype.implements('PropertyStore');

  ForageStore.prototype.isSupported = ForageStore.isSupported = function(){
    var def = new Deferred();
    var validation = new Validation();
    var support = typeof window === 'object' && Object.prototype.hasOwnProperty.call(window,'localforage');
    if(support){
      localforage.setItem('flbstoresupport',true).then(function(){
        return localforage.removeItem('flbstoresupport');
      }).then(function(){
        def.resolve();
      }).catch(function(e){
        def.reject(validation.addError('Storage not supported','Access error:' + e,{
          context: 'localforage'
        }));
      });
    } else{
      def.reject(validation.addError('Storage not supported','No library detected',{
        context: 'localforage'
      }));
    }

    return def.promise;
  };

  ForageStore.prototype.getItem = function(key){
    return this.store.getItem(key);
  };
  ForageStore.prototype.setItem = function(key, value){
    return this.store.setItem(key, value);
  };
  ForageStore.prototype.removeItem = function(key){
    return this.store.removeItem(key);
  };
  ForageStore.prototype.getKeys = function(){
    return this.store.keys();
  };
  ForageStore.prototype.clearAll = function(){
    return this.store.clear();
  };

  return ForageStore;

})();
var IDB = function() {
  function toArray(arr) {
    return Array.prototype.slice.call(arr);
  }

  function promisifyRequest(request) {
    return new Promise(function(resolve, reject) {
      request.onsuccess = function() {
        resolve(request.result);
      };

      request.onerror = function() {
        reject(request.error);
      };
    });
  }

  function promisifyRequestCall(obj, method, args) {
    var request;
    var p = new Promise(function(resolve, reject) {
      request = obj[method].apply(obj, args);
      promisifyRequest(request).then(resolve, reject);
    });

    p.request = request;
    return p;
  }

  function promisifyCursorRequestCall(obj, method, args) {
    var p = promisifyRequestCall(obj, method, args);
    return p.then(function(value) {
      if (!value) return;
      return new Cursor(value, p.request);
    });
  }

  function proxyProperties(ProxyClass, targetProp, properties) {
    properties.forEach(function(prop) {
      Object.defineProperty(ProxyClass.prototype, prop, {
        get: function() {
          return this[targetProp][prop];
        },
        set: function(val) {
          this[targetProp][prop] = val;
        }
      });
    });
  }

  function proxyRequestMethods(ProxyClass, targetProp, Constructor, properties) {
    properties.forEach(function(prop) {
      if (!(prop in Constructor.prototype)) return;
      ProxyClass.prototype[prop] = function() {
        return promisifyRequestCall(this[targetProp], prop, arguments);
      };
    });
  }

  function proxyMethods(ProxyClass, targetProp, Constructor, properties) {
    properties.forEach(function(prop) {
      if (!(prop in Constructor.prototype)) return;
      ProxyClass.prototype[prop] = function() {
        return this[targetProp][prop].apply(this[targetProp], arguments);
      };
    });
  }

  function proxyCursorRequestMethods(ProxyClass, targetProp, Constructor, properties) {
    properties.forEach(function(prop) {
      if (!(prop in Constructor.prototype)) return;
      ProxyClass.prototype[prop] = function() {
        return promisifyCursorRequestCall(this[targetProp], prop, arguments);
      };
    });
  }

  function Index(index) {
    this._index = index;
  }

  proxyProperties(Index, '_index', [
    'name',
    'keyPath',
    'multiEntry',
    'unique'
  ]);

  proxyRequestMethods(Index, '_index', IDBIndex, [
    'get',
    'getKey',
    'getAll',
    'getAllKeys',
    'count'
  ]);

  proxyCursorRequestMethods(Index, '_index', IDBIndex, [
    'openCursor',
    'openKeyCursor'
  ]);

  function Cursor(cursor, request) {
    this._cursor = cursor;
    this._request = request;
  }

  proxyProperties(Cursor, '_cursor', [
    'direction',
    'key',
    'primaryKey',
    'value'
  ]);

  proxyRequestMethods(Cursor, '_cursor', IDBCursor, [
    'update',
    'delete'
  ]);

  // proxy 'next' methods
  ['advance', 'continue', 'continuePrimaryKey'].forEach(function(methodName) {
    if (!(methodName in IDBCursor.prototype)) return;
    Cursor.prototype[methodName] = function() {
      var cursor = this;
      var args = arguments;
      return Promise.resolve().then(function() {
        cursor._cursor[methodName].apply(cursor._cursor, args);
        return promisifyRequest(cursor._request).then(function(value) {
          if (!value) return;
          return new Cursor(value, cursor._request);
        });
      });
    };
  });

  function ObjectStore(store) {
    this._store = store;
  }

  ObjectStore.prototype.createIndex = function() {
    return new Index(this._store.createIndex.apply(this._store, arguments));
  };

  ObjectStore.prototype.index = function() {
    return new Index(this._store.index.apply(this._store, arguments));
  };

  proxyProperties(ObjectStore, '_store', [
    'name',
    'keyPath',
    'indexNames',
    'autoIncrement'
  ]);

  proxyRequestMethods(ObjectStore, '_store', IDBObjectStore, [
    'put',
    'add',
    'delete',
    'clear',
    'get',
    'getAll',
    'getKey',
    'getAllKeys',
    'count'
  ]);

  proxyCursorRequestMethods(ObjectStore, '_store', IDBObjectStore, [
    'openCursor',
    'openKeyCursor'
  ]);

  proxyMethods(ObjectStore, '_store', IDBObjectStore, [
    'deleteIndex'
  ]);

  function Transaction(idbTransaction) {
    this._tx = idbTransaction;
    this.complete = new Promise(function(resolve, reject) {
      idbTransaction.oncomplete = function() {
        resolve();
      };
      idbTransaction.onerror = function() {
        reject(idbTransaction.error);
      };
      idbTransaction.onabort = function() {
        reject(idbTransaction.error);
      };
    });
  }

  Transaction.prototype.objectStore = function() {
    return new ObjectStore(this._tx.objectStore.apply(this._tx, arguments));
  };

  proxyProperties(Transaction, '_tx', [
    'objectStoreNames',
    'mode'
  ]);

  proxyMethods(Transaction, '_tx', IDBTransaction, [
    'abort'
  ]);

  function UpgradeDB(db, oldVersion, transaction) {
    this._db = db;
    this.oldVersion = oldVersion;
    this.transaction = new Transaction(transaction);
  }

  UpgradeDB.prototype.createObjectStore = function() {
    return new ObjectStore(this._db.createObjectStore.apply(this._db, arguments));
  };

  proxyProperties(UpgradeDB, '_db', [
    'name',
    'version',
    'objectStoreNames'
  ]);

  proxyMethods(UpgradeDB, '_db', IDBDatabase, [
    'deleteObjectStore',
    'close'
  ]);

  function DB(db) {
    this._db = db;
  }

  DB.prototype.transaction = function() {
    return new Transaction(this._db.transaction.apply(this._db, arguments));
  };

  proxyProperties(DB, '_db', [
    'name',
    'version',
    'objectStoreNames'
  ]);

  proxyMethods(DB, '_db', IDBDatabase, [
    'close'
  ]);

  // Add cursor iterators
  // TODO: remove this once browsers do the right thing with promises
  ['openCursor', 'openKeyCursor'].forEach(function(funcName) {
    [ObjectStore, Index].forEach(function(Constructor) {
      Constructor.prototype[funcName.replace('open', 'iterate')] = function() {
        var args = toArray(arguments);
        var callback = args[args.length - 1];
        var nativeObject = this._store || this._index;
        var request = nativeObject[funcName].apply(nativeObject, args.slice(0, -1));
        request.onsuccess = function() {
          callback(request.result);
        };
      };
    });
  });

  // polyfill getAll
  [Index, ObjectStore].forEach(function(Constructor) {
    if (Constructor.prototype.getAll) return;
    Constructor.prototype.getAll = function(query, count) {
      var instance = this;
      var items = [];

      return new Promise(function(resolve) {
        instance.iterateCursor(query, function(cursor) {
          if (!cursor) {
            resolve(items);
            return;
          }
          items.push(cursor.value);

          if (count !== undefined && items.length == count) {
            resolve(items);
            return;
          }
          cursor.continue();
        });
      });
    };
  });

  var exp = {
    open: function(name, version, upgradeCallback) {
      var p = promisifyRequestCall(indexedDB, 'open', [name, version]);
      var request = p.request;

      request.onupgradeneeded = function(event) {
        if (upgradeCallback) {
          upgradeCallback(new UpgradeDB(request.result, event.oldVersion, request.transaction));
        }
      };

      return p.then(function(db) {
        return new DB(db);
      });
    },
    delete: function(name) {
      return promisifyRequestCall(indexedDB, 'deleteDatabase', [name]);
    }
  };

  return exp;
};

/*global Flybits,BaseObj*/
// eslint-disable-next-line no-unused-vars
var IDBStore = Flybits.store._storageEngines.IDBStore = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;

  var INTERNALSTORENAME = 'keyvaluepairs';

  function IDBStore(storeName, internalStoreName){
    BaseObj.call(this);
    storeName = storeName || 'flb.general';
    this.storeName = storeName;
    this.internalStoreName = internalStoreName || INTERNALSTORENAME;
    this._ready = IDB().open(storeName, undefined,(db) => {
      db.createObjectStore(this.internalStoreName);
    });
  }

  IDBStore.prototype = Object.create(BaseObj.prototype);
  IDBStore.prototype.constructor = IDBStore;
  IDBStore.prototype.implements('PropertyStore');

  IDBStore.prototype.isSupported = IDBStore.isSupported = function(){
    var def = new Deferred();
    var validation = new Validation();
    var support = typeof window === 'object' && 
                  Object.prototype.hasOwnProperty.call(window,'indexedDB') && 
                  typeof navigator === 'object' &&
                  (!(/^Apple/.test(navigator.vendor)) ||
                  (/^Apple/.test(navigator.vendor) && /AppleWebKit[/]60.*Version[/][89][.]/.test(navigator.appVersion)));
    if(support){
      def.resolve();
    } else{
      def.reject(validation.addError('Storage not supported','No compatible interface detected',{
        context: 'indexedDB'
      }));
    }

    return def.promise;
  };

  IDBStore.prototype.getItem = function(key){
    return this._ready.then((db) => {
      return db.transaction(this.internalStoreName).objectStore(this.internalStoreName).get(key);
    });
  };
  IDBStore.prototype.setItem = function(key, value){
    return this._ready.then((db) => {
      var tx = db.transaction(this.internalStoreName,'readwrite');
      tx.objectStore(this.internalStoreName).put(value,key);
      return tx.complete;
    });
  };
  IDBStore.prototype.removeItem = function(key){
    return this._ready.then((db) => {
      var tx = db.transaction(this.internalStoreName,'readwrite');
      tx.objectStore(this.internalStoreName).delete(key);
      return tx.complete;
    });
  };
  IDBStore.prototype.getKeys = function(){
    return this._ready.then((db) => {
      var tx = db.transaction(this.internalStoreName);
      var keys = [];
      var store = tx.objectStore(this.internalStoreName);

      (store.iterateKeyCursor || store.iterateCursor).call(store, function(cursor){
        if (!cursor) return;
        keys.push(cursor.key);
        cursor.continue();
      });

      return tx.complete.then(function(){
        return keys;
      });
    });
  };
  IDBStore.prototype.clearAll = function(){
    return this._ready.then((db) => {
      var tx = db.transaction(this.internalStoreName, 'readwrite');
      tx.objectStore(this.internalStoreName).clear();
      return tx.complete;
    });
  };

  return IDBStore;
})();
/*global Flybits,BaseObj,RNKeychain*/
// eslint-disable-next-line no-unused-vars
var KeychainStore = (Flybits.store._storageEngines.KeychainStore =
  (function () {
    var KEYCHAIN_SERVICE_PREFIX = 'Flybits.SDKPROPS.';

    function KeychainStore() {
      BaseObj.call(this);
    }

    KeychainStore.prototype = Object.create(BaseObj.prototype);
    KeychainStore.prototype.constructor = KeychainStore;
    KeychainStore.prototype.implements('PropertyStore');

    KeychainStore.prototype.isSupported = KeychainStore.isSupported =
      function () {
        var def = new Flybits.Deferred();
        if (
          typeof RNKeychain !== 'undefined' &&
          RNKeychain.setGenericPassword &&
          RNKeychain.getGenericPassword &&
          RNKeychain.resetGenericPassword
        ) {
          def.resolve();
        } else {
          def.reject();
        }
        return def.promise;
      };

    KeychainStore.prototype.getItem = function (key) {
      var service = KEYCHAIN_SERVICE_PREFIX + key;
      return RNKeychain.getGenericPassword({ service: service }).then(
        function (credentials) {
          if (credentials && credentials.password != null) {
            return credentials.password;
          }
          return null;
        },
      );
    };

    KeychainStore.prototype.setItem = function (key, value) {
      var service = KEYCHAIN_SERVICE_PREFIX + key;

      // NEW: Safely convert any non-string values (objects, booleans, etc.) into strings
      var stringValue =
        typeof value === 'string' ? value : JSON.stringify(value);

      return RNKeychain.setGenericPassword('v', stringValue, {
        service: service,
      });
    };

    KeychainStore.prototype.removeItem = function (key) {
      var service = KEYCHAIN_SERVICE_PREFIX + key;
      return RNKeychain.resetGenericPassword({ service: service });
    };

    return KeychainStore;
  })());

/*global Flybits,BaseObj*/
// eslint-disable-next-line no-unused-vars
var LocalStorageStore = Flybits.store._storageEngines.LocalStorageStore = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;

  function LocalStorageStore(){
    BaseObj.call(this);
    this.store = localStorage;
  }

  LocalStorageStore.prototype = Object.create(BaseObj.prototype);
  LocalStorageStore.prototype.constructor = LocalStorageStore;
  LocalStorageStore.prototype.implements('PropertyStore');

  LocalStorageStore.prototype.isSupported = LocalStorageStore.isSupported = function(){
    var def = new Deferred();
    var validation = new Validation();
    var support = typeof window === 'object' && Object.prototype.hasOwnProperty.call(window,'localStorage');
    if(support){
      try {
        localStorage.setItem('flbstoresupport', true);
        localStorage.removeItem('flbstoresupport');
        def.resolve()
      } catch (e) {
        def.reject(validation.addError('Storage not supported','Access error:' + e,{
          context: 'localStorage'
        }));
      }
    } else{
      def.reject(validation.addError('Storage not supported','Missing reference in namespace.',{
        context: 'localStorage'
      }));
    }

    return def.promise;
  };

  LocalStorageStore.prototype.getItem = function(key){
    return Promise.resolve(this.store.getItem(key));
  };
  LocalStorageStore.prototype.setItem = function(key, value){
    this.store.setItem(key, value);
    return Promise.resolve();
  };
  LocalStorageStore.prototype.removeItem = function(key){
    this.store.removeItem(key);
    return Promise.resolve();
  };

  return LocalStorageStore;

})();
/*global Flybits,BaseObj*/
// eslint-disable-next-line no-unused-vars
var MemoryStore = Flybits.store._storageEngines.MemoryStore = (function(){
  function MemoryStore(){
    BaseObj.call(this);
    this.store = {};
  }

  MemoryStore.prototype = Object.create(BaseObj.prototype);
  MemoryStore.prototype.constructor = MemoryStore;
  MemoryStore.prototype.implements('PropertyStore');

  MemoryStore.prototype.isSupported = MemoryStore.isSupported = function(){
    return Promise.resolve();
  };

  MemoryStore.prototype.getItem = function(key){
    return Promise.resolve(this.store[key]);
  };
  MemoryStore.prototype.setItem = function(key, value){
    this.store[key] = value;
    return Promise.resolve();
  };
  MemoryStore.prototype.removeItem = function(key){
    delete this.store[key];
    return Promise.resolve();
  };
  MemoryStore.prototype.getKeys = function(){
    return Promise.resolve(Object.keys(this.store));
  };
  MemoryStore.prototype.clearAll = function(){
    this.store = {};
    return Promise.resolve(this.store);
  };

  return MemoryStore;

})();
/*global Flybits,BaseObj*/
/**
 * @classdesc Abstract base class from which all identity providers are extended.
 * @memberof Flybits.idp
 * @implements {Flybits.interface.IDP}
 * @abstract
 * @class IDP
 * @param {Object} opts Configuration object to override default configuration
 * @param -
 */
Flybits.idp.IDP = (function(){

  function IDP(opts){
    if(this.constructor === Object){
      throw new Error('Abstract classes cannot be instantiated');
    }
    BaseObj.call(this);
    this.providerName = opts.providerName;
    this.projectID = opts.projectID;
  }

  IDP.prototype = Object.create(BaseObj.prototype);
  IDP.prototype.constructor = IDP;
  IDP.prototype.implements('IDP');

  /**
   * Returns any required headers, depending on IDP, for the facilitation of authentication with Flybits.
   * @instance
   * @memberof Flybits.idp.IDP
   * @function getHeaders
   * @return {Object} IDP specific headers to authenticate to Flybits in the form of a key-value map.
   */
  IDP.prototype.getHeaders = function(){
    return {};
  };

  /**
   * @abstract
   * @instance
   * @memberof Flybits.idp.IDP
   * @function getURL
   * @see Flybits.interface.IDP.getURL
   */

  /**
   * @abstract
   * @instance
   * @memberof Flybits.idp.IDP
   * @function getPayload
   * @see Flybits.interface.IDP.getPayload
   */

  return IDP;
})();

/*global Flybits*/
/**
 * @classdesc 
 * @memberof Flybits.idp
 * @extends Flybits.idp.IDP
 * @class OAuthIDP
 * @param {Object} opts Configuration object to override default configuration.
 * @param {string} opts.providerName name of OAuth provider.  Currently supported providers are listed as {@link Flybits.idp.OAuthIDP.provider|constants} of this IDP.
 * @param {string} opts.token OAuth token returned from third party IDP.
 * @param {string} [opts.projectID] ID of the specific Flybits project for which to obtain authorization.
 */
Flybits.idp.OAuthIDP = (function(){
  var Validation = Flybits.Validation;

  function OAuthIDP(opts){
    Flybits.idp.IDP.call(this,opts);
    this.token = opts.token;
  }

  OAuthIDP.prototype = Object.create(Flybits.idp.IDP.prototype);
  OAuthIDP.prototype.constructor = OAuthIDP;

  /**
   * @memberof Flybits.idp.OAuthIDP
   * @member {Object} provider A mapping of available identity provider (IDP) names.
   * @constant
   * @property {string} FACEBOOK Internal string identifier for the Facebook IDP.
   * @property {string} GOOGLE Internal string identifier for the Google IDP.
   */
  OAuthIDP.prototype.provider = OAuthIDP.provider = {};
  OAuthIDP.prototype.provider.FACEBOOK = OAuthIDP.provider.FACEBOOK = 'facebook';
  OAuthIDP.prototype.provider.GOOGLE = OAuthIDP.provider.GOOGLE = 'gplus';

  /**
   * @memberof Flybits.idp.OAuthIDP
   * @instance 
   * @function getURL
   */
  OAuthIDP.prototype.getURL = function(){
    return this.providerName === 'oidc' ?
      Flybits.cfg.HOST + Flybits.cfg.res.OIDC :
      Flybits.cfg.HOST + Flybits.cfg.res.OAUTH;
  };

  /**
   * @memberof Flybits.idp.OAuthIDP
   * @instance 
   * @function getPayload
   */
  OAuthIDP.prototype.getPayload = function(){
    var validation = new Validation();
    var retObj = {
      provider: this.providerName,
    };

    if(!this.providerName){
      validation.addError('Missing provider name','An OAuth provider name must be supplied',{
        code: Validation.type.MISSINGARG,
        context: 'providerName'
      });
    }
    if(!this.token){
      validation.addError('Missing token','No OAuth access token was supplied',{
        code: Validation.type.MISSINGARG,
        context: 'token'
      });
    }

    if(this.projectID){
      retObj.projectId = this.projectID;
    }

    if(this.providerName === 'oidc'){
      retObj.idToken = this.token;
    } else{
      retObj.accessToken = this.token;
    }

    if(!validation.state){
      throw validation;
    }

    return retObj;
  };  

  return OAuthIDP;
})();

/*global Flybits*/
/**
 * @classdesc IDP class used to establish a session anonymously.
 * @memberof Flybits.idp
 * @extends Flybits.idp.IDP
 * @class AnonymousIDP
 * @param {Object} opts Configuration object to override default configuration.
 * @param {string} opts.projectID ID of the specific Flybits project for which to obtain authorization.
 */
Flybits.idp.AnonymousIDP = (function(){
  var Validation = Flybits.Validation;

  function AnonymousIDP(opts){
    opts = opts || {};
    Flybits.idp.IDP.call(this,opts);
    this.providerName = 'Anonymous';
  }

  AnonymousIDP.prototype = Object.create(Flybits.idp.IDP.prototype);
  AnonymousIDP.prototype.constructor = AnonymousIDP;

  /**
   * @memberof Flybits.idp.AnonymousIDP
   * @instance 
   * @function getURL
   */
  AnonymousIDP.prototype.getURL = function(){
    return Flybits.cfg.HOST + Flybits.cfg.res.ANONYMOUSAUTH;
  };

  /**
   * @memberof Flybits.idp.AnonymousIDP
   * @instance 
   * @function getPayload
   */
  AnonymousIDP.prototype.getPayload = function(){
    var validation = new Validation();
    var retObj = {
      projectId: this.projectID
    };

    if(!this.projectID){
      validation.addError('Missing project ID','Anonymous authentication can only be approved for a specific project ID',{
        code: Validation.type.MISSINGARG,
        context: 'projectID'
      });
    }

    if(!validation.state){
      throw validation;
    }

    return retObj;
  };  

  return AnonymousIDP;
})();

/*global Flybits*/
/**
 * @classdesc
 * @memberof Flybits.idp
 * @extends Flybits.idp.OAuthIDP
 * @class ApiKeyIDP
 * @param {Object} opts Configuration object to override default configuration.
 * @param {string} opts.email Identifier of the user.
 * @param {string} opts.apiKey Flybits generated API key. Must be in the form of a GUID.
 * @param {string} opts.projectID ID of the specific Flybits project for which to obtain authorization.
 * @param {string} [opts.firstName] First name of the user. (Can only be set upon new user creation; ie. first use of `email`.)
 * @param {string} [opts.lastName] Last name of the user. (Can only be set upon new user creation; ie. first use of `email`.)
 */
Flybits.idp.ApiKeyIDP = (function(){
  function ApiKeyIDP(opts){
    opts = opts || {};
    Flybits.idp.OAuthIDP.call(this, opts);
    this.providerName = 'apikey';
    var validation = new Flybits.Validation();

    if(!opts.projectID){
      validation.addError('Missing project ID','',{
        code: Flybits.Validation.type.MISSINGARG,
        context: 'projectID'
      });
    }

    if(!opts.email){
      validation.addError('Missing user email','',{
        code: Flybits.Validation.type.MISSINGARG,
        context: 'email'
      });
    } else{
      this.email = opts.email;
    }
    if(!opts.apiKey){
      validation.addError('Missing api key','',{
        code: Flybits.Validation.type.MISSINGARG,
        context: 'apiKey'
      });
    } else{
      this.apiKey = opts.apiKey;
    }

    if(opts.firstName){
      this.firstName = opts.firstName;
    }

    if(opts.lastName){
      this.lastName = opts.lastName;
    }

    if(!validation.state){
      throw validation;
    }

    this.token = this.createToken();
  }

  ApiKeyIDP.prototype = Object.create(Flybits.idp.OAuthIDP.prototype);
  ApiKeyIDP.prototype.constructor = ApiKeyIDP;

  ApiKeyIDP.prototype.provider = ApiKeyIDP.provider = {};
  ApiKeyIDP.prototype.provider.APIKEY = ApiKeyIDP.provider.APIKEY = 'apikey';

  ApiKeyIDP.prototype.createToken = function(){
    var tokenPayload = {
      email: this.email,
      apikey: this.apiKey
    };

    if(this.firstName){
      tokenPayload['firstName'] = this.firstName;
    }

    if(this.firstName){
      tokenPayload['lastName'] = this.lastName;
    }

    return Flybits.util.Api.base64Encode(JSON.stringify(tokenPayload));
  };

  return ApiKeyIDP;
})();
/*global Flybits*/
/**
 * @deprecated This IdP type is no longer supported by the Flybits platform.
 * @classdesc IDP class used to establish a session with a Flybits user.
 * @memberof Flybits.idp
 * @extends Flybits.idp.IDP
 * @class FlybitsIDP
 * @param {Object} opts Configuration object to override default configuration
 * @param {string} opts.email Email of user.
 * @param {string} opts.password Flybits user password.
 * @param {string} [opts.firstName] First name of user for Flybits user profile.  This field is only used when registering for a new Flybits user and will not update existing user profiles.  If either `firstName` or `lastName` is specified, both fields will become mandatory.
 * @param {string} [opts.lastName] Last name of user for Flybits user profile. This field is only used when registering for a new Flybits user and will not update existing user profiles.  If either `firstName` or `lastName` is specified, both fields will become mandatory.
 * @param {string} [opts.projectID] ID of the specific Flybits project for which to obtain authorization.
 */
Flybits.idp.FlybitsIDP = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;
  var ApiUtil = Flybits.util.Api;

  function FlybitsIDP(opts){
    console.warn('This class is deprecated');
    Flybits.idp.IDP.call(this,opts);
    this.providerName = 'Flybits';
    this.email = opts.email;
    this.password = opts.password;
    this.firstName = opts.firstName;    
    this.lastName = opts.lastName;
  }

  FlybitsIDP.prototype = Object.create(Flybits.idp.IDP.prototype);
  FlybitsIDP.prototype.constructor = FlybitsIDP;

  /**
   * @memberof Flybits.idp.FlybitsIDP
   * @instance 
   * @function getURL
   */
  FlybitsIDP.prototype.getURL = function(){
    return Flybits.cfg.HOST + Flybits.cfg.res.AUTHENTICATE;
  };

  /**
   * @memberof Flybits.idp.FlybitsIDP
   * @instance 
   * @function getPayload
   */
  FlybitsIDP.prototype.getPayload = function(){
    var validation = new Validation();
    var retObj = {
      email: this.email,
      password: this.password
    };

    if(!this.email){
      validation.addError('Missing email','No email address provided',{
        code: Validation.type.MISSINGARG,
        context: 'email'
      });
    }
    if(!this.password){
      validation.addError('Missing password','No email address provided',{
        code: Validation.type.MISSINGARG,
        context: 'password'
      });
    }
    if(this.firstName && !this.lastName){
      validation.addError('Missing last name','First name was supplied without an associated last name.',{
        code: Validation.type.MISSINGARG,
        context: 'lastName'
      });
    } else if(!this.firstName && this.lastName){
      validation.addError('Missing last name','First name was supplied without an associated last name.',{
        code: Validation.type.MISSINGARG,
        context: 'lastName'
      });
    } else if(this.firstName && this.lastName){
      retObj.firstName = this.firstName;
      retObj.lastName = this.lastName;
    }

    if(this.projectID){
      retObj.projectId = this.projectID;
    }

    if(!validation.state){
      throw validation;
    }

    return retObj;
  };

  /**
   * Requests Flybits to send a password reset email to the email address associated with the currently authenticated user.  This is only available to users who have been created by the {@link Flybits.idp.FlybitsIDP}.
   * @memberof Flybits.idp.FlybitsIDP
   * @function requestCurrentPasswordReset
   * @returns {external:Promise<undefined,Flybits.Validation>} Promise that resolves without value if a password reset email is sent.  Promise rejects with {@link Flybits.Validation|Validation} object if an error has occurred.  Possible errors can include invalid current session or if the current user session was not created using the {@link Flybits.idp.FlybitsIDP}.
   */
  FlybitsIDP.prototype.requestCurrentPasswordReset = FlybitsIDP.requestCurrentPasswordReset = function(){
    var def = new Deferred();
    var validation = new Validation();

    if(!Flybits.Session.user || !Flybits.Session.userToken){
      validation.addError('No valid session','',{
        code: Validation.type.UNAUTHENTICATED
      });
    }
    if(!Flybits.Session._idpType || Flybits.Session._idpType !== 'FlybitsIDP'){
      validation.addError('Invalid IDP type','Current session user was not created using the FlybitsIDP',{
        code: Validation.type.INVALIDARG
      });
    }

    if(!validation.state){
      def.reject(validation);
      return def.promise;
    }

    var email = Flybits.Session.user.email;
    return this.requestPasswordReset(email);
  };

  /**
   * Requests Flybits to send a password reset email to the specified email address.
   * @memberof Flybits.idp.FlybitsIDP
   * @function requestPasswordReset
   * @param {string} email Email address for Flybits user that would like to reset their password.
   * @returns {external:Promise<undefined,Flybits.Validation>} Promise that resolves without value if a password reset email is sent.  Promise rejects with {@link Flybits.Validation|Validation} object if an error has occurred.
   */
  FlybitsIDP.prototype.requestPasswordReset = FlybitsIDP.requestPasswordReset = function(email){
    var def = new Deferred();
    var url = Flybits.cfg.HOST + Flybits.cfg.res.REQRESETEMAIL;
    
    if(!email){
      def.reject(new Validation().addError('Missing email','',{
        code: Validation.type.MISSINGARG
      }));
      return def.promise;
    }

    ApiUtil.flbFetch(url,{
      method: 'POST',
      body: JSON.stringify({
        email: email
      }),
      respType: 'empty'
    }).then(function(){
      def.resolve();
    }).catch(function(e){
      def.reject(e);
    });

    return def.promise;
  };

  FlybitsIDP.prototype.requestConfirmationEmail = FlybitsIDP.requestConfirmationEmail = function(noProject){
    var def = new Deferred();
    var url = Flybits.cfg.HOST + Flybits.cfg.res.REQCONFIRMEMAIL;

    if(!Flybits.Session.userToken){
      return Promise.reject(new Validation().addError('Session not found.','Must have a valid session to request a confirmation email.',{
        code: Validation.type.UNAUTHENTICATED
      }));
    }

    var opts = {
      method: 'POST',
      respType: 'empty'
    };
    if(!noProject){
      opts.body = JSON.stringify({
        projectID: Flybits.Session._projectID
      });
    }

    ApiUtil.flbFetch(url,opts).then(function(){
      def.resolve();
    }).catch(function(e){
      def.reject(e);
    })

    return def.promise;
  };

  return FlybitsIDP;
})();

/*global Flybits*/
/**
 * @classdesc IDP class used to establish a Flybits session using a secure JSON Web Token (JWT) as an authentication claim from an independent IDP.
 * @memberof Flybits.idp
 * @extends Flybits.idp.IDP
 * @class JWTSignedIDP
 * @param {Object} opts Configuration object to override default configuration.
 * @param {string} opts.jwt JSON Web Token (JWT) String
 */
Flybits.idp.JWTSignedIDP = (function(){
  function JWTSignedIDP(opts){
    opts = opts || {};
    Flybits.idp.IDP.call(this, opts);
    this.providerName = 'JWTSigned';
    this.jwt = opts.jwt;
  }

  JWTSignedIDP.prototype = Object.create(Flybits.idp.IDP.prototype);
  JWTSignedIDP.prototype.constructor = JWTSignedIDP;

  /**
   * @memberof Flybits.idp.JWTSignedIDP
   * @instance 
   * @function getHeaders
   */
  JWTSignedIDP.prototype.getHeaders = function(){
    return {
      Authorization: 'Bearer ' + this.jwt
    };
  };

  /**
   * @memberof Flybits.idp.JWTSignedIDP
   * @instance 
   * @function getURL
   */
  JWTSignedIDP.prototype.getURL = function(){
    return Flybits.cfg.HOST + Flybits.cfg.res.JWTSIGNEDLOGIN;
  };

  /**
   * @memberof Flybits.idp.JWTSignedIDP
   * @instance 
   * @function getPayload
   */
  JWTSignedIDP.prototype.getPayload = function(){
    return;
  };

  return JWTSignedIDP;
})();
/*global Flybits*/
/**
 * @classdesc IDP class used to establish a session using a secure signed token.
 * @memberof Flybits.idp
 * @extends Flybits.idp.IDP
 * @class SignedIDP
 * @param {Object} opts Configuration object to override default configuration.
 * @param {string} opts.accessToken Signed token used for identification.
 * @param {string} opts.signature Signature of token used for verification.
 */
Flybits.idp.SignedIDP = (function(){
  var Validation = Flybits.Validation;

  function SignedIDP(opts){
    opts = opts || {};
    Flybits.idp.IDP.call(this,opts);
    this.providerName = 'Signed';
    this.accessToken = opts.accessToken;
    this.signature = opts.signature;
  }

  SignedIDP.prototype = Object.create(Flybits.idp.IDP.prototype);
  SignedIDP.prototype.constructor = SignedIDP;

  /**
   * @memberof Flybits.idp.SignedIDP
   * @instance 
   * @function getURL
   */
  SignedIDP.prototype.getURL = function(){
    return Flybits.cfg.HOST + Flybits.cfg.res.SIGNEDLOGIN;
  };

  /**
   * @memberof Flybits.idp.SignedIDP
   * @instance 
   * @function getPayload
   */
  SignedIDP.prototype.getPayload = function(){
    var validation = new Validation();
    var retObj = {
      accessToken: this.accessToken,
      signature: this.signature
    };

    if(!this.accessToken){
      validation.addError('Missing access token','',{
        code: Validation.type.MISSINGARG,
        context: 'accessToken'
      });
    }

    if(!this.signature){
      validation.addError('Missing signature','',{
        code: Validation.type.MISSINGARG,
        context: 'signature'
      });
    }

    if(!validation.state){
      throw validation;
    }

    return retObj;
  };  

  return SignedIDP;
})();

/*global Flybits*/
/**
 * This is a utility class, do not use constructor.
 * @class
 * @classdesc Main utility class to establish and manage authenticated sessions with Flybits.
 * @memberof Flybits
 */
Flybits.Session = (function(){
  var Validation = Flybits.Validation;
  var Deferred = Flybits.Deferred;
  var User = Flybits.User;

  var deferredReady = new Flybits.Deferred();

  var Session = {
    /**
     * Promise that resolves when the SDK has a valid session. If disconnect is invoked
     * a new deferred promise will be set.
     * @memberof Flybits.Session
     * @member {external:Promise}
     */
    ready: deferredReady.promise,
    /**
     * @memberof Flybits.Session
     * @member {string} AUTHHEADER Header key that is used by the core to hold authorization tokens in HTTP requests and responses.
     * @constant
     */
    AUTHHEADER: 'x-authorization',
    /**
     * @instance
     * @memberof Flybits.Session
     * @member {Flybits.User} user {@link Flybits.User|User} currently authenticated to Flybits core.  Set to `null` if session is explicitly cleared such as after logout.
     */
    user: null,
    /**
     * @instance
     * @memberof Flybits.Session
     * @member {string} userToken Current session authorization token.  Set to `null` if session is explicitly cleared such as after logout.
     */
    userToken: null,
    userAgent: null,
    /**
     * @instance
     * @memberof Flybits.Session
     * @member {string} [preferredLanguage=en] Resolved preferred Content language.  This attribute will not persist across SDK initializations.  Initialization will attempt to detect browser locale using `navigator.language`.  If your application manages user scoped application preferences you can set this attribute using {@link Flybits.Session.setLocale}
     */
    preferredLanguage: 'en',
    _idpType: undefined,
    /**
     * @instance
     * @memberof Flybits.Session
     * @member {string} _projectID Current session project ID.  Set to `null` if session is explicitly cleared such as after logout.
     */
    _projectID: undefined,
    /**
     * @instance
     * @memberof Flybits.Session
     * @member {string} _deviceID Current session user device ID.  Set to `null` if session is explicitly cleared such as after logout.
     */
    _deviceID: null,
    _userTokenRetrievedAt: null,
    _userTokenExpiry: null,
    /**
     * Establishes authenticated session with Flybits using an {@link Flybits.idp.IDP|IDP}
     * @memberof Flybits.Session
     * @function connect
     * @param {Flybits.idp.IDP} idp Instance of an identity provider that is an implementation of the abstract class {@link Flybits.idp.IDP}
     * @returns {external:Promise<Flybits.User,Flybits.Validation>} Promise that resolves with the {@link Flybits.User|User} model of authenticated User if session exists.  Promise rejects with {@link Flybits.Validation|Validation} object if an error has occurred.  Possible errors can include invalid/insufficient values to the {@link Flybits.idp.IDP|IDP} instance.
     */
    connect: function(idp){
      var session = this;
      var def = new Deferred();
      if(!(idp instanceof Flybits.idp.IDP)){
        def.reject(new Validation().addError('Invalid IDP','The provided parameter was not an instance of Flybits.idp.IDP',{
          code: Validation.type.INVALIDARG
        }));
        return def.promise;
      }
      try{
        var authPayload = idp.getPayload();
        var authHeaders = idp.getHeaders();
        var requestOptions = {
          method: 'POST'
        };
        if(authPayload){
          requestOptions.body = JSON.stringify(authPayload);
        }
        if(authHeaders){
          requestOptions.headers = authHeaders;
        }

        Flybits.util.Api.flbFetch(idp.getURL(), requestOptions).then(function(respObj){
          if(!respObj.headers[session.AUTHHEADER]){
            throw new Validation().addError('Session could not be refreshed','Missing/Unreadable token',{
              context: 'x-authorization',
              code: Validation.type.MALFORMED
            });
          }
          var user = new User(respObj.body);
          session.user = user;
          session.setUserToken(respObj.headers[session.AUTHHEADER]);
          session._idpType = resolveIDPTypeName(idp);
          session._projectID = idp.projectID;
          
          def.resolve(user);
        }).catch(function(e){
          def.reject(e);
        });
      } catch(e){
        def.reject(e);
      }
      
      return def.promise;
    },
    /**
     * Destroys any existing session with Flybits
     * @memberof Flybits.Session
     * @function disconnect
     * @returns {external:Promise<undefined,undefined>} Promise that resolves without value.
     */
    disconnect: function(){
      var session = this;
      var def = new Deferred();

      if (!this.userToken) {
        def.resolve();
        return def.promise;
      }
      
      var url = Flybits.cfg.HOST + Flybits.cfg.res.UNAUTHENTICATE;
      var reqHeaders = {};
      reqHeaders[this.AUTHHEADER] = this.userToken;
      
      // 1. Define the core logout logic
      var performLogout = function() {
        Flybits.util.Api.flbFetch(url,{
          method: 'POST',
          headers: reqHeaders,
          respType: 'empty'
        }).catch(function(){}).then(function(){
          session.clearSession();
          def.resolve();
        });
      }

      // 2. Attempt to delete the Push Token BEFORE killing the session
      // We check if Flybits.api.Push exists just in case it's a modular build without Push
      if (Flybits.api && Flybits.api.Push && typeof Flybits.api.Push.deleteToken === 'function') {
        Flybits.api.Push.deleteToken()
          .catch(function() {
            // We intentionally swallow this error. 
            // If the token didn't exist or network failed, we still want to log the user out!
          })
          .then(function() {
            performLogout();
          });
      } else {
        // Fallback if Push module is missing
        performLogout();
      }

      return def.promise;
    },
    notifyReady: function(){
      Flybits.evt.emit(Flybits.evt.type.SESSIONSTART);
      deferredReady.resolve();
    },
    /**
     * Resets session ready promise.  Meant to be invoked before calling {@link Flybits.Session.bindSession|bindSession} or {@link Flybits.Session.refreshSession|refreshSession} if you wish to be notified when a valid session is ready in a modular way.
     * @memberof Flybits.Session
     * @function resetReady
     */
    resetReady: function(){
      deferredReady.reject();
      deferredReady = new Deferred();
      this.ready = deferredReady.promise;
    },
    /**
     * Verifies user session existence and viability.
     * @memberof Flybits.Session
     * @function resolveSession
     * @param {boolean} [doNotCheckServer=false] Flag to specify whether or not session resolution should make an AJAX call to verify session or simply draw from cache. `true` to bypass server and resolve session from cache, `false` if otherwise.  Note: `false` to check the server is the most accurate and also the default.
     * @returns {external:Promise<string,Flybits.Validation>} Promise that resolves with the current user's session token if session exists.
     */
    resolveSession: function(doNotCheckServer){
      var session = this;
      var def = new Deferred();

      if(this.userToken && doNotCheckServer){
        session.notifyReady();
        def.resolve(this.userToken);
      } else if(this.userToken && !doNotCheckServer){
        return this.refreshSession();
      } else{
        def.reject(new Validation().addError('Session not found.','',{
          code: Validation.type.UNAUTHENTICATED
        }));
      }

      return def.promise;
    },
    /**
     * Shifts the sliding window of the user session by refreshing the user's session token.
     * @memberof Flybits.Session
     * @function refreshSession
     * @returns {external:Promise<string,Flybits.Validation>} Promise that resolves with the current user's new  session token.
     */
    refreshSession: function(){
      var session = this;
      var def = new Deferred();
      var url = Flybits.cfg.HOST + Flybits.cfg.res.REFRESHAUTH;

      Flybits.util.Api.flbFetch(url,{
        respType: 'empty'
      }).then(function(respObj){
        if(!respObj.headers[session.AUTHHEADER]){
          throw new Validation().addError('Session could not be refreshed','Missing/Unreadable token',{
            context: 'x-authorization',
            code: Validation.type.MALFORMED
          });
        } else{
          session.setUserToken(respObj.headers[session.AUTHHEADER]);
          def.resolve(session.userToken);
        }
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    },
    /**
     * Binds existing session to a new project.  In the Flybits platform sessions are project specific.  Also there exists non-project specific sessions for developer portal management.  Thus this method is used to bind an authenticated user to a new project.
     * @memberof Flybits.Session
     * @function bindSession
     * @param {string} projectID ID of project for which to bind new session to.
     * @returns {external:Promise<string,Flybits.Validation>} Promise that resolves with the current user's new  session token.
     * @deprecated
     */
    bindSession: function(projectID){
      console.warn('@deprecated Flybits.Session.bindSession');
      var session = this;
      var def = new Deferred();
      var url = Flybits.cfg.HOST + Flybits.cfg.res.AUTHPROJECT + '?projectId='+projectID;
      var validation = new Validation();

      if(!this.user){
        validation.addError('No session','An existing session is required to bind to a project.',{
          code: Validation.type.UNAUTHENTICATED
        });
      }
      if(!projectID){
        validation.addError('Missing argument','A project ID is required to bind to a project.',{
          code: Validation.type.MISSINGARG,
          context: 'projectID'
        });
      }

      if(!validation.state){
        def.reject(validation);
        return def.promise;
      }

      Flybits.util.Api.flbFetch(url).then(function(respObj){
        session._projectID = projectID;
        if(!respObj.headers[session.AUTHHEADER]){
          throw new Validation().addError('Session could not be set','Missing/Unreadable token',{
            context: 'x-authorization',
            code: Validation.type.MALFORMED
          });
        }
        session.setUserToken(respObj.headers[session.AUTHHEADER]);
        def.resolve(session.userToken);
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    },
    /**
     * Inject pre-existing or independently retrieved Flybits session token into SDK for subsequent use.  This will locally store and persist the specified token making all subsequent API requests utilize this session.
     * @memberof Flybits.Session
     * @function setUserToken
     * @param {string} token Flybits issued session token.
     */
    setUserToken: function(token){
      this.userToken = token;
      
      if(token){
        this.notifyReady();
        var jwtData = Flybits.util.Api.base64Decode(token.split('.')[1]);
        var jwt = JSON.parse(jwtData);
        this._userTokenExpiry = jwt.exp?jwt.exp*1000:0;
        this._userTokenRetrievedAt = new Date().getTime();
        this._projectID = jwt.tenantID;
        this._persistState();
      } else{
        this._userTokenExpiry = null;
        this._userTokenRetrievedAt = null;
        this._projectID = null;
        this._persistState();
      }
    },
    /**
     * Set user preferred language for use when localizing Content.  Set language will not persist across SDK initializations so be sure to set this upon SDK initialization.
     * @memberof Flybits.Session
     * @function setLocale
     * @param {string} localeKey String language code as defined in [RFC 5646: Tags for Identifying Languages](https://datatracker.ietf.org/doc/html/rfc5646). Examples of valid language codes include "en", "en-US", "fr", "fr-FR", "es-ES", etc.  Default preferred language is English defined by language code "en".  This can be retrieved at {@link Flybits.Session.preferredLanguage}.
     */
    setLocale: function(localeKey){
      const cleansed = this._translateLocale(localeKey);
      if(!cleansed){
        throw new Flybits.Validation().addError('Invalid locale','locale key not recognized', {
          code: Flybits.Validation.type.MALFORMED,
          context: localeKey
        });
      }

      this.preferredLanguage = cleansed;
    },
    /**
     * Clear locally stored and persisted Flybits session token.
     * @memberof Flybits.Session
     * @function clearSession
     */
    clearSession: function(){
      this.user = null;
      this.setUserToken(null);
      this.resetReady();
      Flybits.evt.emit(Flybits.evt.type.SESSIONEND);
    },
    _translateLocale: function(localeKey){
      if(!localeKey){
        return null;
      }
      localeKey = localeKey.toLowerCase().split('-')[0];

      return localeKey;
    },
    _persistState: function(){
      var store = Flybits.store.Property;
      store.set(Flybits.cfg.store.USERTOKEN,this.userToken);
      store.set(Flybits.cfg.store.USERTOKENEXP,this._userTokenExpiry);
      store.set(Flybits.cfg.store.PROJECTID,this._projectID);
      store.set(Flybits.cfg.store.IDPTYPE,this._idpType);
      store.set(Flybits.cfg.store.DEVICEID,this._deviceID);
      store.set(Flybits.cfg.store.USER,this.user?this.user.toJSON():undefined);
    },
    _restoreState: function(){
      var def = new Deferred();
      var session = this;
      var store = Flybits.store.Property;
      var promises = [];
      promises.push(store.get(Flybits.cfg.store.USERTOKEN));
      promises.push(store.get(Flybits.cfg.store.USERTOKENEXP));
      promises.push(store.get(Flybits.cfg.store.PROJECTID));
      promises.push(store.get(Flybits.cfg.store.DEVICEID));
      promises.push(store.get(Flybits.cfg.store.IDPTYPE));
      promises.push(store.get(Flybits.cfg.store.USER));
      Flybits.Deferred.settlePromises(promises).then(function(resultArr){
        if(resultArr[0].status){
          session.userToken = resultArr[0].result;
        }
        if(resultArr[1].status){
          session._userTokenExpiry = resultArr[1].result;
        }
        if(resultArr[2].status){
          session._projectID = resultArr[2].result;
        }
        if(resultArr[3].status){
          session._deviceID = resultArr[3].result;
        }
        if(resultArr[4].status){
          session._idpType = resultArr[4].result;
        }
        if(resultArr[5].status){
          session.user = new Flybits.User(resultArr[5].result);
        }

        if(typeof navigator !== 'undefined' && navigator.language){
          session.setLocale(navigator.language);
        }
        if(session.userToken){
          session.notifyReady();
        }
        def.resolve();
      });

      return def.promise;
    }
  };

  var resolveIDPTypeName = function(idp){
    var availableIDPs = Object.keys(Flybits.idp);

    for(var i = 1; i < availableIDPs.length; i++){
      if(idp instanceof Flybits.idp[availableIDPs[i]]){
        return availableIDPs[i];
      }
    }

    return '';
  };

  return Session;
})();

/*global Flybits*/
/**
 * This is a utility class, do not use constructor.
 * @class Manager
 * @classdesc Main manager to handle scheduled context retrieval from context sources and reporting to Flybits core.
 * @memberof Flybits.context
 */
Flybits.context.Manager = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;
  var ObjUtil = Flybits.util.Obj;
  var ApiUtil = Flybits.util.Api;

  const CTXPLUGINREGEX = /ctx\.([\w]+)\.([\w]+)/;
  const paramCtxRegex = {
    ATTRKEY: /for attribute (.*?) of plugin/g,
    NUMBERS: /expect (\d+) but got (\d+)/g
  }

  var debouncedReportingRestart;

  var _ready = new Deferred();

  var restoreTimestamps = function(){
    var lastReportedPromise = Flybits.store.Property.get(Flybits.cfg.store.CONTEXTLASTREPORTED).then(function(epoch){
      if(epoch){
        contextManager.lastReported = +epoch;
      }
    });
    var lastReportAttemptedPromise = Flybits.store.Property.get(Flybits.cfg.store.CONTEXTLASTREPORTATTEMPTED).then(function(epoch){
      if(epoch){
        contextManager.lastReportAttempted = +epoch;
      }
    });

    return Flybits.Deferred.settlePromises([lastReportedPromise,lastReportAttemptedPromise]);
  };

  /**
   * Context Data report object.
   * @typedef CtxReport
   * @memberof Flybits.context.Manager
   * @type Object
   * @property {string} dataTypeID Context Plugin ID (eg. ctx.provider.pluginName).
   * @property {number} timestamp Epoch time (seconds) for which the Context Data applies.
   * @property {Object} value Context plugin specific key/value map of plugin attribute data.
   */

  var contextManager = {
    /**
     * Promise that resolves when the Context Manager has completed initialization.
     * @memberof Flybits.context.Manager
     * @member {external:Promise}
     */
    ready: _ready.promise,
    /**
     * Array of registered context plugins.
     * @memberof Flybits.context.Manager
     * @member {Array<Flybits.context.ContextPlugin>} services
     */
    services: [],
    _defaultServices:{
      contentAnalytics: null,
      pushAnalytics: null
    },
    /**
     * @memberof Flybits.context.Manager
     * @member {number} reportDelay=60000 Delay in milliseconds before the next interval of context reporting begins.  Note that the timer starts after the previous interval's context reporting has completed.
     */
    reportDelay: 60000,
    _reportTimeout: null,
    /**
     * @memberof Flybits.context.Manager
     * @member {boolean} isReporting Flag indicating whether scheduled context reporting is enabled.
     */
    isReporting: false,
    /**
     * @memberof Flybits.context.Manager
     * @member {boolean} isReportSending Flag indicating if the manager is currently sending a context value report.
     */
    isReportSending: false,
    isRestarting: false,

    /**
     * @memberof Flybits.context.Manager
     * @member {number} lastReported Datetime of last successful report.
     */
    lastReported: null,
    /**
     * @memberof Flybits.context.Manager
     * @member {number} lastReportAttempted 
     */
    lastReportAttempted: null,

    /**
     * Initialization function to setup internal properties and will start reporting interval.
     * @memberof Flybits.context.Manager
     * @function
     * @return {external:Promise<undefined,Flybits.Validation>} Promise that resolves with no value when initialization has completed.
     */
    initialize: function(){
      var def = new Deferred();
      var manager = this;

      debouncedReportingRestart = Flybits.util.Obj.debounce(function(){
        manager.startReporting();
      },manager.reportDelay,true);

      restoreTimestamps().then(function(){
        return manager._registerDefaults();
      }).then(function(){
        return manager.startReporting();
      }).then(function(){
        _ready.resolve();
        def.resolve();
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    },
    /**
     * Used to register a context plugin with the manager to begin scheduled retrieval of context values.
     * @memberof Flybits.context.Manager
     * @function
     * @param {Flybits.context.ContextPlugin} contextPlugin Instance of a context plugin to be registered for scheduled retrieval and reporting.
     * @return {external:Promise<Flybits.context.ContextPlugin,Flybits.Validation>} Promise that resolves with the successfully registered context plugin if supported. Context plugin will now begin scheduled retrieval.
     */
    register: function(contextPlugin){
      var manager = this;
      var def = new Deferred();
      if(!(contextPlugin instanceof Flybits.context.ContextPlugin)){
        def.reject(new Validation().addError('Invalid Context Plugin','Provided parameter was not a valid Flybits.context.ContextPlugin instance.',{
          code: Validation.type.NOTSUPPORTED,
          context: 'contextPlugin'
        }));
        return def.promise;
      }

      contextPlugin.isSupported().then(function(){
        if(contextPlugin.refreshDelay === contextPlugin.refreshInterval.ONETIME){
          contextPlugin.collectState().then(function(){
            manager.services.push(contextPlugin);
            def.resolve(contextPlugin);
          }).catch(function(e){
            def.reject(e);
          });
        } else{
          contextPlugin.startService();
          manager.services.push(contextPlugin);
          def.resolve(contextPlugin);
        }
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    },
    /**
     * Used to unregister a context plugin with the manager to and stop it's scheduled retrieval of context values.
     * @memberof Flybits.context.Manager
     * @function
     * @param {Flybits.context.ContextPlugin} contextPlugin Instance of a context plugin to be unregistered and have its scheduled retrieval and reporting stopped.
     * @return {Flybits.context.ContextPlugin} Context plugin instance that was unregistered.
     */
    unregister: function(contextPlugin){
      if(contextPlugin instanceof Flybits.context.ContextPlugin){
        ObjUtil.removeObject(this.services,contextPlugin);
        contextPlugin.stopService();
      }
      return contextPlugin;
    },
    /**
     * Used to unregister a context plugin with the manager to stop it's scheduled retrieval of context values based on specified type ID.
     * @memberof Flybits.context.Manager
     * @function
     * @param {string} typeID Context Plugin type ID string found in the class definition `TYPEID`.  For most Context Plugins this type ID is the type string for the Context Plugin found in the Flybits Project Settings identifying the Context Plugin schema.
     * @returns {Flybits.context.ContextPlugin|null} Context plugin instance that was unregisterd or `null` if Context plugin instance was not found.
     */
    unregisterTypeID: function(typeID){
      const plugin = this.services.filter(function(pluginService){
        return pluginService.TYPEID === typeID;
      })[0];
      
      if(plugin){
        return this.unregister(plugin);
      }
      return null;
    },
    /**
     * Unregisters all context plugins from the context manager.
     * @memberof Flybits.context.Manager
     * @function
     * @return {Flybits.context.Manager} Reference to the context manager to allow for method chaining.
     */
    unregisterAll: function(){
      this.stopAllServices();
      this.services = [];
      return this;
    },
    /**
     * Stops all scheduled retrieval services of registered context plugins.
     * @memberof Flybits.context.Manager
     * @function
     * @return {Flybits.context.Manager} Reference to the context manager to allow for method chaining.
     */
    stopAllServices: function(){
      var services = this.services;
      for (var i = 0; i < services.length; i++){
        services[i].stopService();
      }
      return this;
    },
    /**
     * Starts all scheduled retrieval services of registered context plugins.
     * @memberof Flybits.context.Manager
     * @function
     * @return {Flybits.context.Manager} Reference to the context manager to allow for method chaining.
     */
    startAllServices: function(){
      var services = this.services;
      for (var i = 0; i < services.length; i++){
        services[i].startService();
      }
      return this;
    },
    /**
     * Starts the scheduled service that continuously batch reports collected context data of registered context plugins.
     * @memberof Flybits.context.Manager
     * @function startReporting
     * @return {external:Promise<undefined,Flybits.Validation>} Promise that resolves without a return value and rejects with a common Flybits Validation model instance.
     */
    startReporting: function(){
      var def = new Deferred();
      var manager = this;
      if(this.isRestarting){
        throw new Validation().addError('Already restarting service','',{
          code: Validation.type.UNEXPECTED,
          context: 'context.Manager'
        });
      }
      this.isRestarting = true;
      manager.stopReporting();

      var sessionPromise = Flybits.Session.userToken?Promise.resolve(Flybits.Session.userToken):Flybits.Session.resolveSession();
      sessionPromise.then(function(){
        var interval;
        interval = function(){
          manager.report().catch(function(){}).then(function(){
            if(manager.isReporting){
              manager._reportTimeout = setTimeout(function(){
                interval();
              },manager.reportDelay);
            }
          });

          manager.isReporting = true;
        };
        interval();
        def.resolve();
      }).catch(function(e){
        def.reject(e);
      }).then(function(){
        manager.isRestarting = false;
      });

      return def.promise;
    },
    /**
     * Stops the scheduled service that continuously batch reports collected context data of registered context plugins.
     * @memberof Flybits.context.Manager
     * @function stopReporting
     * @return {Flybits.context.Manager} Reference to this context manager to allow for method chaining.
     */
    stopReporting: function(){
      this.isReporting = false;
      clearTimeout(this._reportTimeout);
      return this;
    },
    /**
     * By default all reported Context Data is locally batched before a report attempt.  This function will clear all locally cached data.
     * @memberof Flybits.context.Manager
     * @function flushCollected
     * @returns {external:Promise<undefined,string[]>} Resolves once all registered Context Plugin services have cleared locally cached Context Data.
     */
    flushCollected: function(){
      let def = new Deferred();
      let promiseArr = this.services.map((s) => {
        return s._flushCollected();
      });

      Promise.allSettled(promiseArr).then((results) => {
        const errors = results.filter((r) => {
          return r.status === 'rejected'
        });
        if(errors.length){
          def.reject(errors.map((e) => {
            return e.reason;
          }))
        } else{
          def.resolve();
        }
      })

      return def.promise;
    },
    _registerDefaults: function(){
      Flybits.context.Manager.unregisterTypeID(Flybits.context.ContentAnalytics.TYPEID);
      Flybits.context.Manager.unregisterTypeID(Flybits.context.PushAnalytics.TYPEID);
      Flybits.context.Manager.unregisterTypeID(Flybits.context.GenericPlugin.TYPEID);
      var contentAnalytics = this._defaultServices.contentAnalytics = new Flybits.context.ContentAnalytics({
        refreshDelay: 3600000
      });
      var pushAnalytics = this._defaultServices.pushAnalytics = new Flybits.context.PushAnalytics({
        refreshDelay: 3600000
      });
      var genericPlugin = this._defaultServices.genericPlugin = new Flybits.context.GenericPlugin({
        refreshDelay: 3600000
      });

      return Flybits.Deferred.settlePromises([
        Flybits.context.Manager.register(contentAnalytics),
        Flybits.context.Manager.register(pushAnalytics),
        Flybits.context.Manager.register(genericPlugin)
      ]);
    },
    _gatherAllData: function(){
      var def = new Deferred();
      var services = this.services;
      var data = [];
      var serviceDeletions = [];
      var promises = [];

      for(var i = 0; i < services.length; i++){
        (function(service){
          var retrievalPromise = service._fetchCollected();
          promises.push(retrievalPromise);
          retrievalPromise.then(function(values){
            Array.prototype.push.apply(data,values.data);
            serviceDeletions.push({
              serviceRef: service,
              keys: values.keys
            });
          });
        })(services[i]);
      }

      Flybits.Deferred.settlePromises(promises).then(function(){
        def.resolve({
          data: data,
          keys: serviceDeletions
        });
      });

      return def.promise;
    },
    _sendReport: function(data){
      var def = new Deferred();
      var url = Flybits.cfg.HOST + Flybits.cfg.res.CONTEXT;

      ApiUtil.flbFetch(url,{
        method: 'POST',
        body: JSON.stringify(data),
        respType: 'empty'
      }).then(function(){
        def.resolve();
      }).catch(function(resp){
        if(resp instanceof Flybits.Validation && resp.firstError()){
          let err = resp.firstError();
          let errMsg = err?.message;
          if(errMsg.includes('can\'t find plugin for dataTypeID')){
            err.code = Flybits.Validation.type.NOTFOUND;
            let pluginIDMatch = errMsg.match(CTXPLUGINREGEX);
            if(pluginIDMatch){
              err.context = pluginIDMatch[0];
            }
          } else if(errMsg.includes('could not get value for attribute ')){
            let attrStr = errMsg.split('could not get value for attribute ')[1];
            let pluginID = '';
            if(Array.isArray(data)){
              const dataTypeReport = data.find((dtReport) => {
                const valueObj = dtReport.value;
                if(valueObj){
                  const attrKeys = Object.keys(valueObj);
                  return attrKeys.includes(attrStr);
                }
                return false;
              })
              if(dataTypeReport){
                pluginID = dataTypeReport.dataTypeID;
              }
            }
            err.code = Flybits.Validation.type.INVALIDARG;
            err.context = {
              pluginID: pluginID,
              attribute: attrStr
            };
          } else if(errMsg.includes('incorrect number of parameters')){
            let pluginID = errMsg.match(CTXPLUGINREGEX)?.[0];
            let attribute = Array.from(errMsg.matchAll(paramCtxRegex.ATTRKEY))?.[0]?.[1];
            let numbers = Array.from(errMsg.matchAll(paramCtxRegex.NUMBERS))?.[0];

            err.code = Flybits.Validation.type.INVALIDARG;
            err.context = {
              pluginID: pluginID,
              attribute: attribute,
              paramCounts: {
                expected: numbers?.[1] ? +(numbers?.[1]): undefined,
                actual: numbers?.[2] ? +(numbers?.[2]): undefined
              } 
            }
          }
        }

        def.reject(resp);
      });

      return def.promise;
    },
    _cleanupServices: function(serviceKeyArr){
      var def = new Deferred();
      var promises = [];

      for(var i = 0; i < serviceKeyArr.length; i++){
        (function(serviceKeyMap){
          promises.push(serviceKeyMap.serviceRef._deleteCollected(serviceKeyMap.keys));
        })(serviceKeyArr[i]);
      }

      Flybits.Deferred.settlePromises(promises).then(function(){
        def.resolve();
      });

      return def.promise;
    },
    _cleanInvalidServiceData: function(serviceKeyArr, context){
      var def = new Deferred();

      if(typeof context === 'string'){
        let pluginServiceObjs = serviceKeyArr.filter((serviceKeyObj) => {
          return serviceKeyObj.serviceRef.TYPEID === context || serviceKeyObj.serviceRef.TYPEID === 'ctx.sdk.generic';
        });
        if(pluginServiceObjs.length){
          let promiseArr = [];
          pluginServiceObjs.forEach((pso) => {
            promiseArr.push(pso.serviceRef._cleanInvalidData(context));
          });

          Promise.allSettled(promiseArr).then(() => {
            def.resolve();
          })
        } else{
          def.resolve();
        }
      } else if(typeof context === 'object' && !context.paramCounts){
        let pluginServiceObjs = serviceKeyArr.filter((serviceKeyObj) => {
          return serviceKeyObj.serviceRef.TYPEID === context.pluginID || serviceKeyObj.serviceRef.TYPEID === 'ctx.sdk.generic';
        });
        if(pluginServiceObjs.length){
          let promiseArr = [];
          pluginServiceObjs.forEach((pso) => {
            promiseArr.push(pso.serviceRef._cleanInvalidData(context.pluginID, context.attribute));
          })

          Promise.allSettled(promiseArr).then(() => {
            def.resolve();
          })
        } else{
          def.resolve();
        }
      } else if(typeof context === 'object' && context.paramCounts){
        let pluginServiceObjs = serviceKeyArr.filter((serviceKeyObj) => {
          return serviceKeyObj.serviceRef.TYPEID === context.pluginID || serviceKeyObj.serviceRef.TYPEID === 'ctx.sdk.generic';
        });
        if(pluginServiceObjs.length){
          let promiseArr = [];
          pluginServiceObjs.forEach((pso) => {
            promiseArr.push(pso.serviceRef._cleanInvalidData(context.pluginID, context.attribute, context.paramCounts));
          })

          Promise.allSettled(promiseArr).then(() => {
            def.resolve();
          })
        } else{
          def.resolve();
        }
      } else if(context === undefined){
        if(serviceKeyArr.length){
          let promiseArr = [];
          serviceKeyArr.forEach((sso) => {
            promiseArr.push(sso.serviceRef._cleanAllInvalidData());
          });

          Promise.allSettled(promiseArr).then(() => {
            def.resolve();
          })
        } else{
          def.resolve();
        }
      }

      return def.promise;
    },
    /**
     * Batch reports collected context data of registered context plugins.
     * @memberof Flybits.context.Manager
     * @function report
     * @return {external:Promise<undefined,Flybits.Validation>} Promise that resolves without a return value and rejects with a common Flybits Validation model instance.
     */
    report: function(){
      var manager = this;
      var def = new Deferred();
      var serviceCleanup = [];
      var sentData = [];
      this._gatherAllData().then(function(result){
        if(!result.data || !result.data.length){
          throw new Validation().addError('No context to upload.','',{
            code: Validation.type.NOOP
          });
        }
        serviceCleanup = result.keys;
        sentData = result.data;
        manager.isReportSending = true;
        return manager._sendReport(sentData);
      }).then(function(){
        manager.lastReported = Date.now();
        Flybits.store.Property.set(Flybits.cfg.store.CONTEXTLASTREPORTED,manager.lastReported);
        return manager._cleanupServices(serviceCleanup);
      }).then(function(){
        Flybits.evt.emit(Flybits.evt.type.CTXREPORT, {
          status: 'success',
          data: sentData
        });
        def.resolve();
      }).catch(async function(e){
        if(e instanceof Validation){
          const err = e.firstError();
          if(err?.code === Flybits.Validation.type.NOTFOUND && err?.context){
            await manager._cleanInvalidServiceData(serviceCleanup, err.context)
          } else if(err?.code === Flybits.Validation.type.INVALIDARG && err?.context){
            await manager._cleanInvalidServiceData(serviceCleanup, err.context);
          } else if(err.code !== Flybits.Validation.type.NOOP){
            await manager._cleanInvalidServiceData(serviceCleanup, undefined);
          }
          let genericErr = new Validation().addError('Context report failed.',err.message,{
            code: Validation.UNEXPECTED
          });
          if(err.code !== Flybits.Validation.type.NOOP){
            Flybits.evt.emit(Flybits.evt.type.CTXREPORT, {
              status: 'failed',
              validation: e,
              data: sentData
            });
          }
          def.reject(genericErr);
        } else {
          let genericErr = new Validation().addError('Context report failed.',e,{
            code: Validation.UNEXPECTED
          });
          Flybits.evt.emit(Flybits.evt.type.CTXREPORT, {
            status: 'failed',
            validation: genericErr,
            data: sentData
          });
          console.error('> invalid ctxdata report', genericErr);
          def.reject(genericErr);
        }
      }).then(function(){
        manager.lastReportAttempted = Date.now();
        Flybits.store.Property.set(Flybits.cfg.store.CONTEXTLASTREPORTATTEMPTED,manager.lastReportAttempted);
        manager.isReportSending = false;
      });

      return def.promise;
    },

    warmReporting: function(){
      var diff = this.lastReported? Date.now() - this.lastReported:this.reportDelay + 1;

      if(this.isReporting && !this.isReportSending && diff > this.reportDelay){
        debouncedReportingRestart.apply(this);
      }
    },

    /**
     * Explicit report of context data. A simple way to report via known Context Plugin IDs without the need to implement and register custom Context Plugin instances. Value will be batched locally before reporting.
     * @memberof Flybits.context.Manager
     * @function reportCtx
     * @param {string} pluginID Context plugin ID string.
     * @param {Object} contextValue Context plugin specific key/value map of plugin attribute data.
     * @return {external:Promise<undefined,Flybits.Validation>} Promise that resolves without a return value and rejects with a common Flybits Validation model instance.
     * @throws {Error} 
     */
    reportCtx: function(pluginID, contextValue){
      if(!this._defaultServices.genericPlugin){
        throw Error('Context Manager not initialized. Missing required plugin service');
      }

      return this._defaultServices.genericPlugin.setGenericState(pluginID, contextValue);
    }
  };
  

  return contextManager;
})();

/*global Flybits,BaseObj*/
/**
 * @classdesc Abstract base class from which all context plugins are extended.
 * @memberof Flybits.context
 * @implements {Flybits.interface.ContextPlugin}
 * @abstract
 * @class ContextPlugin
 * @param {Object} opts Configuration object to override default configuration
 * @param {number} opts.refreshDelay {@link Flybits.context.ContextPlugin#refreshDelay}
 */
Flybits.context.ContextPlugin = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;

  function ContextPlugin(opts){
    if(this.constructor === Object){
      throw new Error('Abstract classes cannot be instantiated');
    }
    BaseObj.call(this);

    this._refreshTimeout = null;

    /**
     * @instance
     * @memberof Flybits.context.ContextPlugin
     * @member {number} [refreshDelay=60000] Delay in milliseconds before the next interval of context refreshing begins for this particular plugin.  Note that the timer starts after the previous interval's context refresh has completed.
     */
    this.refreshDelay = opts && opts.refreshDelay?opts.refreshDelay:this.refreshInterval.ONEMINUTE;

    /**
     * @instance
     * @memberof Flybits.context.ContextPlugin
     * @member {number} [maxStoreEntries=80] Maximum amount of entries this context type can store locally before old entries are flushed from the local store.
     */
    this.maxStoreSize = opts && opts.maxStoreSize?opts.maxStoreSize:80;

    /**
     * @instance
     * @memberof Flybits.context.ContextPlugin
     * @member {number} [maxStoreAge=86400000] Maximum age of an entry of this context type before it is flushed from the local store.  Default maximum age is 1 day old.
     */
    this.maxStoreAge = opts && opts.maxStoreAge?opts.maxStoreAge:86400000;

    /**
     * @instance
     * @memberof Flybits.context.ContextPlugin
     * @member {boolean} isServiceRunning flag indicating whether or not a context retrieval service is running for this context plugin.
     */
    this.isServiceRunning = false;

    this.collectHistorical = opts && opts.collectHistorical;

    /**
     * @instance
     * @memberof Flybits.context.ContextPlugin
     * @member {number} lastCollected Timestamp of the last context state retrieval and local storage;
     */
    this.lastCollected = -1;

    this.lastLocalValue = null;
    this.lastLocalKey = null;

    this._store = new Flybits.store.Record(this.TYPEID);
  }

  ContextPlugin.prototype = Object.create(BaseObj.prototype);
  ContextPlugin.prototype.constructor = ContextPlugin;
  ContextPlugin.prototype.implements('ContextPlugin');

  /**
   * @memberof Flybits.context.ContextPlugin
   * @member {Object} refreshInterval Common context refresh delays.
   * @constant
   * @property {number} ONETIME Indicates the value of the context should be fetched only a single time and should not be refreshed on a continuous interval.
   * @property {number} THIRTYSECONDS Common refresh delay of 30 seconds.
   * @property {number} ONEMINUTE Common refresh delay of 1 minute.
   * @property {number} ONEHOUR Common refresh delay of 1 hour.
   */
  ContextPlugin.prototype.refreshInterval = ContextPlugin.refreshInterval = {};
  ContextPlugin.prototype.refreshInterval.ONETIME = ContextPlugin.refreshInterval.ONETIME = -42;
  ContextPlugin.prototype.refreshInterval.THIRTYSECONDS = ContextPlugin.refreshInterval.THIRTYSECONDS = 1000*30;
  ContextPlugin.prototype.refreshInterval.ONEMINUTE = ContextPlugin.refreshInterval.ONEMINUTE = 1000*60*1;
  ContextPlugin.prototype.refreshInterval.ONEHOUR = ContextPlugin.refreshInterval.ONEHOUR = 1000*60*60;
  ContextPlugin.prototype.refreshInterval.ONEDAY = ContextPlugin.refreshInterval.ONEDAY = 1000*60*60*24;

  /**
   * @memberof Flybits.context.ContextPlugin
   * @member {string} TYPEID String descriptor to uniquely identify context data type on the Flybits context gateway.
   */
  ContextPlugin.prototype.TYPEID = ContextPlugin.TYPEID = 'ctx.sdk.generic';

  /**
   * Starts a scheduled service that continuously retrieves context data for this plugin.
   * @instance
   * @memberof Flybits.context.ContextPlugin
   * @function startService
   * @return {Flybits.context.ContextPlugin} Reference to this context plugin to allow for method chaining.
   */
  ContextPlugin.prototype.startService = function () {
    this.stopService();

    var interval;
    interval = function (contextPlugin) {
      contextPlugin.collectState().catch(function () { }).then(function () {
        if (contextPlugin.isServiceRunning) {
          contextPlugin._refreshTimeout = setTimeout(function () {
            interval(contextPlugin);
          }, contextPlugin.refreshDelay);
        }
      });

      contextPlugin.isServiceRunning = true;
    };
    interval(this);

    return this;
  };
    
  /**
   * Stops the scheduled service that continuously retrieves context data for this plugin.
   * @instance
   * @memberof Flybits.context.ContextPlugin
   * @function stopService
   * @return {Flybits.context.ContextPlugin} Reference to this context plugin to allow for method chaining.
   */
  ContextPlugin.prototype.stopService = function(){
    this.isServiceRunning = false;
    clearTimeout(this._refreshTimeout);
    return this;
  };

  /**
   * @abstract
   * @memberof Flybits.context.ContextPlugin
   * @function getState
   * @see Flybits.interface.ContextPlugin.getState
   */
  /**
   * @abstract
   * @memberof Flybits.context.ContextPlugin
   * @function isSupported
   * @see Flybits.interface.ContextPlugin.isSupported
   */

  /**
   * Force the immediate retrieval of context state from this `ContextPlugin` once and place store it into the local storage for later reporting.
   * @instance
   * @memberof Flybits.context.ContextPlugin
   * @function collectState
   * @return {external:Promise<undefined,undefined>} Promise that resolves when this `ContextPlugin` has retrieved and stored its current state.
   */
  ContextPlugin.prototype.collectState = function(){
    var def = new Deferred();
    var plugin = this;
    var store = this._store;
    var storeLength = 0;

    store.ready.then(function(){
      return store.keys();
    }).then(function(keys){
      storeLength = keys.length;
      return plugin.getState();
    }).then(function(e){
      if(!e){
        throw new Validation().addError('Empty context state collected','',{
          code: Validation.type.NOTFOUND
        });
      }
      return plugin._saveState(e);
    }).then(function(){
      plugin.lastCollected = new Date().getTime();
      if(storeLength >= plugin.maxStoreSize){
        plugin._validateStoreState();
      }
      def.resolve();
    }).catch(function(e){
      def.reject(e);
    });

    return def.promise;
  };

  /**
   * Allows for explicit injection of context values irrespective of automated collection.
   * @instance 
   * @memberof Flybits.context.ContextPlugin
   * @function setState
   * @param {Object} contextValue Context plugin specific data structure representing new value of context plugin.
   * @return {external:Promise<undefined,undefined>} Promise that resolves when this `ContextPlugin` stored provided state.
   */
  ContextPlugin.prototype.setState = function(contextValue){
    var def = new Deferred();
    var plugin = this;
    var store = this._store;
    var storeLength = 0;

    store.ready.then(function(){
      return store.keys();
    }).then(function(keys){
      storeLength = keys.length;
      return plugin._saveState(contextValue);
    }).then(function(){
      plugin.lastCollected = new Date().getTime();
      if(storeLength >= plugin.maxStoreSize){
        plugin._validateStoreState();
      }
      def.resolve();
    }).catch(function(e){
      console.error('>',plugin,e);
      def.reject();
    });

    return def.promise;
  };

  ContextPlugin.prototype._validateStoreState = function(){
    var plugin = this;
    var def = new Deferred();
    var promises = [];
    var store = this._store;
    store.keys().then(function(result){
      var keys = result;
      var now = new Date().getTime();
      var accessCount = keys.length - plugin.maxStoreSize;

      keys.sort(function(a,b){
        return (+(a.split('-')[1]))-(+(b.split('-')[1]));
      });

      if(accessCount > 0){
        for(var i = 0; i < accessCount; i++){
          promises.push(store.remove(keys.shift()));
        }
      }

      while(keys.length > 0 && (now - keys[0]) >= plugin.refreshInterval.ONEDAY){
        promises.push(store.remove(keys.shift()));
      }

      Flybits.Deferred.settlePromises(promises).then(function(){
        def.resolve();
      });
    }).catch(function(){
      def.reject();
    });

    return def.promise;
  };
  ContextPlugin.prototype._fetchCollected = function(){
    var plugin = this;
    var def = new Deferred();
    var data = [];
    var keysToDelete = [];
    var store = this._store;
    
    store.ready.then(function(){
      return store.keys();
    }).then(function(keys){
      var assembleData;
      assembleData = function(){
        if(keys.length <= 0){
          def.resolve({
            data: data,
            keys: keysToDelete
          });
          return;
        }
        var curKey = keys.pop();
        keysToDelete.push(curKey);

        store.get(curKey).then(function(item){
          var timestamp = +(curKey.split('-')[1]);
          data.push({
            timestamp: Math.round(timestamp/1000),
            dataTypeID: plugin.TYPEID,
            value: plugin._toServerFormat(item)
          });
        }).catch(function(){}).then(function(){
          assembleData();
        });
      };
      assembleData();
    }).catch(function(e){
      def.reject(e);
    });

    return def.promise;
  };
  /**
   * 
   * @param {string} pluginID 
   * @param {string} attrName 
   * @param {Object} paramCounts 
   * @param {number} paramCounts.expected
   * @param {number} paramCounts.actual
   * @returns {external:Promise<undefined,undefined>}
   */
  ContextPlugin.prototype._cleanInvalidData = function(pluginID, attrName, paramCounts){
    var def = new Deferred();
    const store = this._store;
    store.ready.then(() => {
      return store.keys();
    }).then((keys) => {
      const promiseArr = keys.map((key) => {
        return new Promise((resolve) => {
          (async function(curKey){
            if(pluginID && !attrName){
              await store.remove(curKey);
            } else if(pluginID && attrName && !paramCounts){
              let val = await store.get(curKey);
              let localAttrName = attrName.replace('query.','');
              if(val && typeof val === 'object' && Object.keys(val).includes(localAttrName)){
                delete val[localAttrName];
                if(Object.keys(val).length){
                  await store.set(curKey, val);
                } else{
                  await store.remove(curKey);
                }
              }
            } else if(pluginID && attrName && paramCounts){
              let val = await store.get(curKey);
              let localAttrKey = Object.keys(val).find((key) => {
                let attrCompArr = key.split('.');
                return attrCompArr[0] === attrName && (attrCompArr.length -1) === paramCounts.actual
              });

              if(localAttrKey){
                delete val[localAttrKey];
                if(Object.keys(val).length){
                  await store.set(curKey, val);
                } else{
                  await store.remove(curKey);
                }
              }
            }
            resolve();
          })(key)
        })
      })
      Promise.allSettled(promiseArr).then(() => {
        def.resolve();
      })
    }).catch((e) => {
      def.reject(e);
    })

    return def.promise;
  }; 

  ContextPlugin.prototype._cleanAllInvalidData = async function(){
    const store = this._store;
    await store.ready;
    const keys = await store.keys();
    const promiseArr = [];
    for(const key of keys){
      const val = await store.get(key);
      if(
        Object.prototype.hasOwnProperty.call(val, Flybits.context.GenericPlugin.STORERECORD_PLUGINKEY) &&
        !Flybits.util.Ctx.validatePluginID(val[Flybits.context.GenericPlugin.STORERECORD_PLUGINKEY]).state
      ){
        promiseArr.push(store.remove(key));
      } else{
        const resolvedVal = Flybits.util.Obj.extend({},val);
        delete resolvedVal[Flybits.context.GenericPlugin.STORERECORD_PLUGINKEY];
        if(!Flybits.util.Ctx.validateCtxValueObj(resolvedVal).state){
          promiseArr.push(store.remove(key));
        }
      }
    }

    await Promise.allSettled(promiseArr);
  };
  
  ContextPlugin.prototype._deleteCollected = function(keys){
    var store = this._store;
    var def = new Deferred();
    var deleteData;
    deleteData = function(){
      if(keys.length <= 0){
        def.resolve();
        return;
      }
      var curKey = keys.pop();
      store.remove(curKey).catch(function(){}).then(function(){
        deleteData();
      });
    };
    deleteData();

    return def.promise;
  };
  ContextPlugin.prototype._flushCollected = function(){
    return this._store.clear();
  };
  ContextPlugin.prototype._generateKey = ContextPlugin._generateKey = function(){
    var time = new Date().getTime();
    var guid = Flybits.util.Obj.guid(1);
    return guid+'-'+time;
  };
  ContextPlugin.prototype._cleanHistorical = function(value){
    if(this.lastLocalKey && JSON.stringify(this.lastLocalValue) === JSON.stringify(value)){
      this._store.remove(this.lastLocalKey);
    }
  };
  ContextPlugin.prototype._saveState = function(value){
    var plugin = this;
    var storeKey = this._generateKey();
    
    if(!this.collectHistorical){
      this._cleanHistorical(value);
    }

    var storeSetPromise = this._store.set(storeKey,value);
    storeSetPromise.then(function(){
      plugin.lastLocalKey = storeKey;
      plugin.lastLocalValue = value;
      Flybits.context.Manager.warmReporting();
    });
    return storeSetPromise;
  };
  ContextPlugin.prototype._toServerFormat = function(item){
    if(item){
      item = Flybits.util.Obj.extend({},item);
      var keys = Object.keys(item);
      for(var i = 0; i < keys.length; i++){
        var key = keys[i];
        if(key.indexOf('.') > -1){
          item['query.'+key] = item[key];
          delete item[key];
        }
      }
    }
    return item;
  };

  return ContextPlugin;
})();

/*global Flybits*/
/**
 * @class Connectivity
 * @classdesc Utility class to retrieve state of browser connectivity.
 * @deprecated
 * @extends Flybits.context.ContextPlugin
 * @memberof Flybits.context
 * @param {Flybits.context.Connectivity.Options} opts Configuration object to override default configuration
 */
Flybits.context.Connectivity = (function(){
  /**
   * @typedef ConnectionState
   * @memberof Flybits.context.Connectivity
   * @type Object
   * @property {number} state Numerical state of connectivity. 0 for disconnected and 1 for connected.
   */
  /**
    * @typedef Options
    * @memberof Flybits.context.Connectivity
    * @type Object
    * @property {boolean} hardCheck=false Flag to indicate whether or not a HTTP network request is to be used to determine network connectivity as opposed to using browser's `navigator.onLine` property. If the browser does not support `navigator.onLine` a network request based connectivity check will be performed regardless of the state of this flag.
    * @property {number} refreshDelay {@link Flybits.context.ContextPlugin#refreshDelay}
    */
  var Deferred = Flybits.Deferred;
  var ObjUtil = Flybits.util.Obj;

  function Connectivity(opts){
    Flybits.context.ContextPlugin.call(this,opts);
    if(opts){
      this.opts = ObjUtil.extend({},this.opts,opts);
    }
  }

  Connectivity.prototype = Object.create(Flybits.context.ContextPlugin.prototype);
  Connectivity.prototype.constructor = Connectivity;

  /**
   * @memberof Flybits.context.Connectivity
   * @member {string} TYPEID String descriptor to uniquely identify context data type on the Flybits context gateway.
   */
  Connectivity.prototype.TYPEID = Connectivity.TYPEID = 'ctx.sdk.network';

  /**
   * Check to see if Connectivity retrieval is available on the current browser.
   * @memberof Flybits.context.Connectivity
   * @function isSupported
   * @override
   * @returns {external:Promise<undefined,Flybits.Validation>} Promise that resolves without value.  Promise rejects with a {@link Flybits.Validation} object with errors.
   */
  Connectivity.isSupported = Connectivity.prototype.isSupported = function(){
    var def = new Deferred();
    def.resolve();

    return def.promise;
  };

  /**
   * Retrieve browser's current connectivity status.
   * @memberof Flybits.context.Connectivity
   * @function getState
   * @override
   * @returns {external:Promise<Flybits.context.Connectivity.ConnectionState,Flybits.Validation>} Promise that resolves with {@link Flybits.context.Connectivity.ConnectionState|ConnectionState} object.  Promise rejects with a {@link Flybits.Validation} object with errors.
   */
  Connectivity.getState = Connectivity.prototype.getState = function(){
    var def = new Deferred();
    var plugin = this;
    if(typeof navigator === 'object' && 'onLine' in navigator && !this.opts.hardCheck){
      def.resolve({
        state: navigator.onLine?plugin.state.CONNECTED:plugin.state.DISCONNECTED
      });
    } else{
      fetch(Flybits.cfg.HOST+'/ping').then(function(){
        def.resolve({
          state: plugin.state.CONNECTED
        });
      }).catch(function(){
        def.resolve({
          state: plugin.state.DISCONNECTED
        });
      });
    }

    return def.promise;
  };

  /**
   * Converts context value object into the server expected format.
   * @function
   * @memberof Flybits.context.Connectivity
   * @function _toServerFormat
   * @param {Object} contextValue
   * @return {Object} Expected server format of context value.
   */
  Connectivity._toServerFormat = Connectivity.prototype._toServerFormat = function(contextValue){
    return {
      connectionType: contextValue.state
    };
  };

  /**
   * @memberof Flybits.context.Connectivity
   * @member {Object} state Connection state constants.
   * @constant
   * @property {number} DISCONNECTED Indicates the state of being disconnected.
   * @property {number} CONNECTED Indicates the state of being connected.
   */
  Connectivity.state = Connectivity.prototype.state = {};
  Connectivity.state.DISCONNECTED = Connectivity.prototype.state.DISCONNECTED = -1;
  Connectivity.state.CONNECTED = Connectivity.prototype.state.CONNECTED = -99;

  /**
   * @memberof Flybits.context.Connectivity
   * @member opts
   * @type {Flybits.context.Connectivity.Options}
   */
  Connectivity.opts = Connectivity.prototype.opts = {
    hardCheck: false
  };

  return Connectivity;
})();

/*global Flybits*/
/**
 * @class ContentAnalytics
 * @classdesc
 * @extends Flybits.context.ContextPlugin
 * @memberof Flybits.context
 */
Flybits.context.ContentAnalytics = (function(){
  function ContentAnalytics(opts){
    Flybits.context.ContextPlugin.call(this, opts);
  }

  ContentAnalytics.prototype = Object.create(Flybits.context.ContextPlugin.prototype);
  ContentAnalytics.prototype.constructor = ContentAnalytics;

  /**
   * @memberof Flybits.context.ContentAnalytics
   * @member {string} TYPEID String descriptor to uniquely identify context data type on the Flybits context gateway.
   */
  ContentAnalytics.prototype.TYPEID = ContentAnalytics.TYPEID = 'ctx.flybits.contentAnalytics';

  /**
   * This support check will always succeed as analytics plugins are Flybits reserved plugins and available in every Flybits tenant
   * @memberof Flybits.context.ContentAnalytics
   * @function isSupported
   * @override
   * @returns {external:Promise<undefined,Flybits.Validation>} Promise that resolves without value.  Promise rejects with a {@link Flybits.Validation} object with errors.
   */
  ContentAnalytics.isSupported = ContentAnalytics.prototype.isSupported = function(){
    return Promise.resolve();
  };

  /**
   * Will not return value as this plugin requires explicit proactive context reporting and not passive collection.
   * @memberof Flybits.context.ContentAnalytics
   * @function getState
   * @override
   * @returns {external:Promise<null,null>} Promise rejects without response object.
   */
  ContentAnalytics.getState = ContentAnalytics.prototype.getState = function(){
    return Promise.reject();
  };

  /**
   * @memberof Flybits.context.ContentAnalytics
   * @member {Object} attributes
   * @type {Flybits.context.ContentAnalytics.attributes}
   */
  ContentAnalytics.attributes = ContentAnalytics.prototype.attributes = {
    VIEWED: 'viewed', // .<content instance ID>
    ENGAGED: 'engaged', // .<content instance ID>
    COMPONENTENGAGED: 'componentEngaged', // .<content instance ID>.<component ID>.<component name>.<component type>
    COMPONENTFULFILLED: 'componentFulfilled' // .<content instance ID>.<component ID>.<component name>.<component type>
  };

  return ContentAnalytics;
})();
/*global Flybits*/
Flybits.context.GenericPlugin = (function(){
  const PLUGINKEY = '_dataTypeID';

  function GenericPlugin(opts){
    Flybits.context.ContextPlugin.call(this, opts);
  }

  GenericPlugin.prototype = Object.create(Flybits.context.ContextPlugin.prototype);
  GenericPlugin.TYPEID = Flybits.context.ContextPlugin.TYPEID;
  GenericPlugin.STORERECORD_PLUGINKEY = PLUGINKEY;
  GenericPlugin.prototype.constructor = GenericPlugin;

  GenericPlugin.isSupported = GenericPlugin.prototype.isSupported = function(){
    return Promise.resolve();
  };

  GenericPlugin.getState = GenericPlugin.prototype.getState = function(){
    return Promise.reject();
  };

  GenericPlugin.prototype.setGenericState = function(pluginID, contextValue){
    const validation = new Flybits.Validation();
    validation.merge(Flybits.util.Ctx.validatePluginID(pluginID));
    validation.merge(Flybits.util.Ctx.validateCtxValueObj(contextValue));

    if(!validation.state){
      return Promise.reject(validation);
    }
    
    contextValue[PLUGINKEY] = pluginID;
    return this.setState(contextValue);
  };
  
  GenericPlugin.prototype._cleanInvalidData = function(pluginID, attrName, paramCounts){
    var def = new Flybits.Deferred();
    const store = this._store;
    store.ready.then(async () => {
      return store.keys();
    }).then((keys) => {
      const promiseArr = keys.map((key) => {
        return new Promise((resolve) => {
          (async function(curKey){
            let val = await store.get(curKey);
            if(pluginID && !attrName){
              if(val && typeof val === 'object' && val._dataTypeID === pluginID){
                await store.remove(curKey);
              }
            } else if(pluginID && attrName && !paramCounts){
              let localAttrName = attrName.replace('query.','');
              if(val && typeof val === 'object' && val._dataTypeID === pluginID && Object.keys(val).includes(localAttrName)){
                delete val[localAttrName];
                if(Object.keys(val).length){
                  await store.set(curKey, val);
                } else{
                  await store.remove(curKey);
                }
              }
            } else if(pluginID && attrName && paramCounts){
              if(val && typeof val === 'object' && val._dataTypeID   === pluginID){
                let localAttrKey = Object.keys(val).find((key) => {
                  let attrCompArr = key.split('.');
                  return attrCompArr[0] === attrName && (attrCompArr.length -1) === paramCounts.actual
                });
                if(localAttrKey){
                  delete val[localAttrKey];
                  if(Object.keys(val).length > 1){
                    await store.set(curKey, val);
                  } else{
                    await store.remove(curKey);
                  }
                }
              }
            }
            resolve();
          })(key);
        })
      })
      Promise.allSettled(promiseArr).then(() => {
        def.resolve();
      })
    }).catch((e) => {
      def.reject(e);
    })

    return def.promise;
  }; 

  GenericPlugin.prototype._fetchCollected = function(){
    var plugin = this;
    var def = new Flybits.Deferred();
    var data = [];
    var keysToDelete = [];
    var store = this._store;
    
    store.ready.then(function(){
      return store.keys();
    }).then(function(keys){
      var assembleData;
      assembleData = function(){
        if(keys.length <= 0){
          def.resolve({
            data: data,
            keys: keysToDelete
          });
          return;
        }
        var curKey = keys.pop();
        keysToDelete.push(curKey);

        store.get(curKey).then(function(item){
          var timestamp = +(curKey.split('-')[1]);
          var dataTypeID = item[PLUGINKEY];
          delete item[PLUGINKEY];
          data.push({
            timestamp: Math.round(timestamp/1000),
            dataTypeID: dataTypeID,
            value: plugin._toServerFormat(item)
          });
        }).catch(function(){}).then(function(){
          assembleData();
        });
      };
      assembleData();
    }).catch(function(e){
      def.reject(e);
    });

    return def.promise;
  };

  return GenericPlugin;
})()

/*global Flybits*/
/**
 * @class Location
 * @classdesc Utility class to retrieve browser location.
 * @extends Flybits.context.ContextPlugin
 * @memberof Flybits.context
 * @param {Flybits.context.Location.Options} opts Configuration object to override default configuration
 */
Flybits.context.Location = (function(){
  /**
   * @typedef Geoposition
   * @memberof Flybits.context.Location
   * @type Object
   * @property {Object} coords
   * @property {number} coords.latitude
   * @property {number} coords.longitude
   * @property {number} timestamp
   */
  /**
    * @typedef Options
    * @memberof Flybits.context.Location
    * @type Object
    * @property {number} maximumAge=1000*60*20
    * @property {number} refreshDelay {@link Flybits.context.ContextPlugin#refreshDelay}
    */
  var Deferred = Flybits.Deferred;
  var ObjUtil = Flybits.util.Obj;

  function Location(opts){
    Flybits.context.ContextPlugin.call(this,opts);
    if(opts){
      this.opts = ObjUtil.extend({},this.opts,opts);
    }
  }

  Location.prototype = Object.create(Flybits.context.ContextPlugin.prototype);
  Location.prototype.constructor = Location;

  /**
   * @memberof Flybits.context.Location
   * @member {string} TYPEID String descriptor to uniquely identify context data type on the Flybits context gateway.
   */
  Location.prototype.TYPEID = Location.TYPEID = 'ctx.sdk.location';

  /**
   * Check to see if Location retrieval is available on the current browser.
   * @memberof Flybits.context.Location
   * @function isSupported
   * @override
   * @returns {external:Promise<undefined,Flybits.Validation>} Promise that resolves without value.  Promise rejects with a {@link Flybits.Validation} object with errors.  Possible errors can include the lack of support on webview/browser or the User explicitly denying Location retrieval.
   */
  Location.isSupported = Location.prototype.isSupported = function(){
    var def = new Deferred();

    if(typeof navigator === 'object' && navigator.geolocation){
      navigator.geolocation.getCurrentPosition(function(){
        def.resolve();
      },function(err){
        console.error('>',err);
        def.reject(new Flybits.Validation().addError('Location Sensing Not Supported',err.message?err.message:'User denied'));
      });
    } else{
      def.reject(new Flybits.Validation().addError('Location Sensing Not Supported','Device GeoLocation API not found.'));
    }

    return def.promise;
  };

  /**
   * Retrieve browser's current location.
   * @memberof Flybits.context.Location
   * @function getState
   * @override
   * @returns {external:Promise<Flybits.context.Location.Geoposition,Flybits.Validation>} Promise that resolves with browser's {@link Flybits.context.Location.Geoposition|Geoposition} object.  Promise rejects with a {@link Flybits.Validation} object with errors.  Possible errors can include the lack of support on webview/browser or the User explicitly denying Location retrieval.
   */
  Location.getState = Location.prototype.getState = function(){
    var def = new Deferred();

    navigator.geolocation.getCurrentPosition(function(pos){
      def.resolve({
        coords:{
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy
        },
        timestamp: pos.timestamp
      });
    },function(err){
      console.error('>',err);
      def.reject(new Flybits.Validation().addError('Location could not be resolved'));
    },this.opts);

    return def.promise;
  };

  /**
   * Converts context value object into the server expected format.
   * @function
   * @memberof Flybits.context.Location
   * @function _toServerFormat
   * @param {Object} contextValue
   * @return {Object} Expected server format of context value.
   */
  Location._toServerFormat = Location.prototype._toServerFormat = function(contextValue){
    return {
      lat: contextValue.coords.latitude,
      lng: contextValue.coords.longitude
    };
  };

  /**
   * @memberof Flybits.context.Location
   * @member opts
   * @type {Flybits.context.Location.Options}
   */
  Location.opts = Location.prototype.opts = {
    maximumAge: 1000 * 60 * 20
  };

  return Location;
})();

/*global Flybits*/
/**
 * @class PushAnalytics
 * @classdesc
 * @extends Flybits.context.ContextPlugin
 * @memberof Flybits.context
 */
Flybits.context.PushAnalytics = (function(){
  function PushAnalytics(opts){
    Flybits.context.ContextPlugin.call(this, opts);
  }

  PushAnalytics.prototype = Object.create(Flybits.context.ContextPlugin.prototype);
  PushAnalytics.prototype.constructor = PushAnalytics;

  /**
   * @memberof Flybits.context.PushAnalytics
   * @member {string} TYPEID String descriptor to uniquely identify context data type on the Flybits context gateway.
   */
  PushAnalytics.prototype.TYPEID = PushAnalytics.TYPEID = 'ctx.flybits.pushAnalytics';

  /**
   * This support check will always succeed as analytics plugins are Flybits reserved plugins and available in every Flybits tenant
   * @memberof Flybits.context.PushAnalytics
   * @function isSupported
   * @override
   * @returns {external:Promise<undefined,Flybits.Validation>} Promise that resolves without value.  Promise rejects with a {@link Flybits.Validation} object with errors.
   */
  PushAnalytics.isSupported = PushAnalytics.prototype.isSupported = function(){
    return Promise.resolve();
  };

  /**
   * Will not return value as this plugin requires explicit proactive context reporting and not passive collection.
   * @memberof Flybits.context.PushAnalytics
   * @function getState
   * @override
   * @returns {external:Promise<null,null>} Promise rejects without response object.
   */
  PushAnalytics.getState = PushAnalytics.prototype.getState = function(){
    return Promise.reject();
  };

  /**
   * @memberof Flybits.context.PushAnalytics
   * @member attributes
   * @type {Flybits.context.PushAnalytics.Attributes}
   */
  PushAnalytics.attributes = PushAnalytics.prototype.attributes = {
    VIEWED: 'viewed', // .<content instance ID>
    ENGAGED: 'engaged', // .<content instance ID>
    COMPONENTENGAGED: 'componentEngaged' // .<content instance ID>.<component ID>.<component name>.<component type>
  };

  return PushAnalytics;
})();
/*global Flybits,BaseModel*/
Flybits.analytics.Event = (function(){
  var ObjUtil = Flybits.util.Obj;

  var Event = function(serverObj){
    console.warn('This class is deprecated');
    BaseModel.call(this,serverObj);
    this._tmpID = ObjUtil.guid(1) + '-' + Date.now();
    this.type = this.types.DISCRETE;
    this.name = '';
    this.loggedAt = new Date();
    this.properties = {};
    this._internal = {};
    this._isInternal = false;

    if(serverObj){
      this.fromJSON(serverObj);
    }
  };
  Event.prototype = Object.create(BaseModel.prototype);
  Event.prototype.constructor = Event;
  Event.prototype.implements('Serializable');

  Event.prototype.types = Event.types = {};
  Event.prototype.types.DISCRETE = Event.types.DISCRETE = 'event_discrete';
  Event.prototype.types.TIMEDSTART = Event.types.TIMEDSTART = 'event_timestart';
  Event.prototype.types.TIMEDEND = Event.types.TIMEDEND = 'event_timeend';

  Event.prototype.TIMEDREFID = Event.TIMEDREFID = 'timedRef';
  Event.prototype.OSTYPE = Event.OSTYPE = 'osType';
  Event.prototype.OSVERSION = Event.OSVERSION = 'osVersion';
  Event.prototype.UID = Event.UID = 'uid';

  Event.prototype._setProp = function(obj, key, value){
    if(typeof value === 'undefined' || value === null){
      delete obj[key];
      return this;
    }

    obj[key] = value;
    return this;
  };

  Event.prototype.setProperty = function(key, value){
    return this._setProp(this.properties, key, value);
  };

  Event.prototype._setInternalProperty = function(key, value){
    return this._setProp(this._internal, key, value);
  };

  Event.prototype.fromJSON = function(serverObj){
    this.type = serverObj.type || this.types.DISCRETE;
    this.name = serverObj.name;
    this.loggedAt = serverObj.loggedAt?new Date(serverObj.loggedAt):new Date();
    this.properties = serverObj.properties || {};

    this._internal = serverObj.flbProperties || {};
    this._isInternal = serverObj.isFlybits || false;
  };

  Event.prototype.toJSON = function(){
    var retObj = {
      type: this.type,
      name: this.name,
      loggedAt: this.loggedAt.getTime(),
      properties: this.properties,
      flbProperties: this._internal,
      isFlybits: this._isInternal
    };

    return retObj;
  };

  return Event;
})();

/*global Flybits*/
/**
 * This is a utility class, do not use constructor.
 * @class Manager
 * @classdesc Main manager to collect and report events for later analysis.
 * @memberof Flybits.analytics
 */
Flybits.analytics.Manager = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;
  var Session = Flybits.Session;
  var Event = Flybits.analytics.Event;

  var restoreTimestamps = function(){
    var lastReportedPromise = Flybits.store.Property.get(Flybits.cfg.store.ANALYTICSLASTREPORTED).then(function(epoch){
      if(epoch){
        analyticsManager.lastReported = +epoch;
      }
    });
    var lastReportAttemptedPromise = Flybits.store.Property.get(Flybits.cfg.store.ANALYTICSLASTREPORTATTEMPTED).then(function(epoch){
      if(epoch){
        analyticsManager.lastReportAttempted = +epoch;
      }
    });

    return Flybits.Deferred.settlePromises([lastReportedPromise,lastReportAttemptedPromise]);
  };

  var applyDefaultSysProps = function(event){
    if(typeof window === 'object'){
      event._setInternalProperty(Event.OSTYPE,'browser');
      if(Object.prototype.hasOwnProperty.call(window,'navigator')){
        event._setInternalProperty(Event.OSVERSION,navigator.userAgent);
      }
    } else{
      event._setInternalProperty(Event.OSTYPE,'node');
      if(typeof process !== 'undefined'){
        event._setInternalProperty(Event.OSVERSION,process.version);
      }
    }

    if(Session.user){
      event._setInternalProperty(Event.UID, Session.user.id);
    }
    return event;
  };

  var timeUnitMultiplier = {
    milliseconds: 1,
    seconds: 1000,
    minutes: 60*1000,
    hours: 60*60*1000,
    days: 24*60*60*1000
  };
  var resolveMilliseconds = function(value,units){
    var multiplier = timeUnitMultiplier[units] || 1;
    return value * multiplier;
  };

  var deferredReady = new Deferred();
  var analyticsManager = {
    ready: deferredReady.promise,
    maxStoreSize: 100,
    reportDelay: 3600000,
    reportDelayUnit: 'milliseconds',
    lastReported: null,
    lastReportAttempted: null,
    _reportTimeout: null,
    _analyticsStore: null,
    _uploadChannel: null,
    _timedEventCache: {},
    isReporting: false,
    initialize: function(){
      console.warn('This function is deprecated');
      var def = new Deferred();
      var manager = this;

      this._analyticsStore = new Flybits.analytics.RecordsStore({
        maxStoreSize: Flybits.cfg.analytics.MAXSTORESIZE
      });
      this._uploadChannel = new Flybits.analytics.DefaultChannel();
      
      this._analyticsStore._store.ready.then(function(){
        return restoreTimestamps();
      }).then(function(){
        return manager.startReporting();
      }).then(function(){
        deferredReady.resolve();
        def.resolve();
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    },
    stopReporting: function(){
      console.warn('This function is deprecated');
      this.isReporting = false;
      clearTimeout(this._reportTimeout);
      return this;
    },
    startReporting: function(){
      console.warn('This function is deprecated');
      var def = new Deferred();
      var manager = this;
      manager.stopReporting();
      
      var sessionPromise = Flybits.Session.userToken?Promise.resolve(Flybits.Session.userToken):Flybits.Session.resolveSession();
      sessionPromise.then(function(){
        var interval;
        interval = function(){
          manager.report().catch(function(){}).then(function(){
            if(manager.isReporting){
              manager._reportTimeout = setTimeout(function(){
                interval();
              },resolveMilliseconds(manager.reportDelay,manager.reportDelayUnit));
            }
          });

          manager.isReporting = true;
        };
        interval();
        def.resolve();
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    },
    report: function(){
      console.warn('This function is deprecated');
      var def = new Deferred();
      var manager = this;
      var eventTmpIDs;
      this._analyticsStore.getAllEvents().then(function(events){
        eventTmpIDs = events.map(function(evt){
          return evt._tmpID;
        });
        if(eventTmpIDs.length < 1){
          throw new Validation().addError('No analytics to upload.','',{
            code: Validation.type.NOTFOUND
          });
        }
        return manager._uploadChannel.uploadEvents(events);
      }).then(function(){
        manager.lastReported = Date.now();
        Flybits.store.Property.set(Flybits.cfg.store.ANALYTICSLASTREPORTED,manager.lastReported);
        return manager._analyticsStore.clearEvents(eventTmpIDs);
      }).then(function(){
        def.resolve();
      }).catch(function(e){
        if(e instanceof Validation && e.firstError().code !== Validation.type.NOTFOUND){
          console.error('> analytics report error',e);
        }
        def.reject(e);
      }).then(function(){
        manager.lastReportAttempted = Date.now();
        Flybits.store.Property.set(Flybits.cfg.store.ANALYTICSLASTREPORTATTEMPTED,manager.lastReportAttempted);
      });
      
      return def.promise;
    },
    logEvent: function(eventName, properties){
      console.warn('This function is deprecated');
      var evt = new Event({
        name: eventName,
        type: Event.types.DISCRETE,
        properties: properties
      });

      applyDefaultSysProps(evt);

      return this._analyticsStore.addEvent(evt);
    },
    _logInt: function(eventName, properties, internalProperties){
      console.warn('This function is deprecated');
      var evt = new Event({
        name: eventName,
        type: Event.types.DISCRETE,
        properties: properties,
        isFlybits: true,
        flbProperties: internalProperties
      });

      applyDefaultSysProps(evt);

      return this._analyticsStore.addEvent(evt);
    },
    startTimedEvent: function(eventName, properties){
      console.warn('This function is deprecated');
      var manager = this;
      var def = new Deferred();
      var evt = new Event({
        name: eventName,
        type: Event.types.TIMEDSTART,
        properties: properties
      });
      applyDefaultSysProps(evt);
      evt._setInternalProperty(Event.TIMEDREFID, evt._tmpID);

      this._analyticsStore.addEvent(evt).then(function(){
        manager._timedEventCache[evt._tmpID] = evt;
        def.resolve(evt._tmpID);
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    },
    _logStartInt: function(eventName, properties, internalProperties){
      console.warn('This function is deprecated');
      var manager = this;
      var def = new Deferred();
      var evt = new Event({
        name: eventName,
        type: Event.types.TIMEDSTART,
        properties: properties,
        isFlybits: true,
        flbProperties: internalProperties
      });
      applyDefaultSysProps(evt);
      evt._setInternalProperty(Event.TIMEDREFID, evt._tmpID);

      this._analyticsStore.addEvent(evt).then(function(){
        manager._timedEventCache[evt._tmpID] = evt;
        def.resolve(evt._tmpID);
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    },
    endTimedEvent: function(refID){
      console.warn('This function is deprecated');
      var manager = this;
      var def = new Deferred();
      var startedEvt = manager._timedEventCache[refID];
      if(!refID || !startedEvt){
        def.reject(new Validation().addError('No Timed Event Found','No corresponding start event was found for provided reference.',{
          code: Validation.type.NOTFOUND,
          context: refID
        }));
        return def.promise;
      }

      var endEvt = new Event({
        name: startedEvt.name,
        type: Event.types.TIMEDEND,
        properties: startedEvt.properties
      });
      endEvt._internal = startedEvt._internal;
      endEvt._isInternal = startedEvt._isInternal;

      this._analyticsStore.addEvent(endEvt).then(function(){
        delete manager._timedEventCache[refID];
        def.resolve();
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    },
    _logEndInt: function(refID){
      console.warn('This function is deprecated');
      return this.endTimedEvent(refID);
    },
    /**
     * @memberof Flybits.analytics.Manager
     * @member {Object} actions Common analytics metrics or actions performed on Flybits entities.
     * @constant
     * @property {String} VIEWED=viewed Action of viewing a Flybits entity.
     * @property {String} ENGAGED=engaged Action of engaging (clicking) with a Flybits entity.
     * @property {String} COMPONENTENGAGED=componentEngaged Action of engaging (clicking) with a specific part of a Flybits entity.  For instance, this is useful for capturing specific button or UI component clicks that relate to a Content instance.
     */
    actions: {
      VIEWED: 'viewed',
      ENGAGED: 'engaged',
      COMPONENTENGAGED: 'componentEngaged'
    },

    /**
     * Component details object used for reporting component engagement.
     * @typedef ComponentDetails
     * @memberof Flybits.analytics.Manager
     * @type Object
     * @property {String} name Name or label of the Component for human readable correlation.
     * @property {String} compID An identifier used for non human readable correlation.  Do not confuse this with the Content Instance ID.
     * @property {String} type A string to indicate what type of component it is (eg. Button, Image,...).
     */

    /**
     * Used to report content related analytics metrics.
     * @memberof Flybits.analytics.Manager
     * @function reportContent
     * @param {Flybits.Content} content Instance of a Flybits Content.
     * @param {String} [action=engaged] Analytics metric action string (refer to {@link Flybits.analytics.Manager.actions}). By default the action will be "engaged".
     * @param {Flybits.analytics.Manager.ComponentDetails} [details] Required component details when reporting {@link Flybits.analytics.Manager.actions.COMPONENTENGAGED} analytics metric.
     * @return {external:Promise<undefined,Flybits.Validation>} Promise that resolves without a return value and rejects with a common Flybits Validation model instance.
     */
    reportContent: function(content, action, details){
      var validation = new Flybits.Validation();

      if(!content){
        validation.addError('Missing Content', '',{
          code: Flybits.Validation.type.MISSINGARG,
          context: 'content'
        });
      } else if(!content.id){
        validation.addError('Invalid Content', 'No ID',{
          code: Flybits.Validation.type.INVALIDARG,
          context: 'content.id'
        });
      }
      
      if(!validation.state){
        return Promise.reject(validation);
      }

      return this.reportContentID(content.id, action, details);
    },
    /**
     * Used to report content related analytics metrics by Flybits Content ID.
     * @memberof Flybits.analytics.Manager
     * @function reportContentID
     * @param {String} contentID ID string of a Flybits Content instance.
     * @param {String} [action=engaged] Analytics metric action string (refer to {@link Flybits.analytics.Manager.actions}). By default the action will be "engaged".
     * @param {Flybits.analytics.Manager.ComponentDetails} [details] Required component details when reporting {@link Flybits.analytics.Manager.actions.COMPONENTENGAGED} analytics metric.
     * @return {external:Promise<undefined,Flybits.Validation>} Promise that resolves without a return value and rejects with a common Flybits Validation model instance.
     */
    reportContentID: function(contentID, action, details){
      action = action || this.actions.ENGAGED;
      var validation = new Flybits.Validation();

      if(!contentID){
        validation.addError('Missing Content', '',{
          code: Flybits.Validation.type.MISSINGARG,
          context: 'content'
        });
      }
      if(action === this.actions.COMPONENTENGAGED && !details){
        validation.addError('Missing Component Details', '',{
          code: Flybits.Validation.type.MISSINGARG,
          context: 'details'
        });
      } else if(action === this.actions.COMPONENTENGAGED && (!details.name || !details.compID || !details.type)){
        validation.addError('Invalid Component Details', '',{
          code: Flybits.Validation.type.INVALIDARG,
          context: 'name,compID,type'
        });
      }
      if(!Flybits.context.Manager._defaultServices.contentAnalytics){
        validation.addError('Context Manager not initialized', '',{
          code: Flybits.Validation.type.NOTFOUND,
          context: 'context.Manager'
        });
      }

      if(!validation.state){
        return Promise.reject(validation);
      }

      var report = {};
      var keyStr = action+'.'+contentID;
      if(action === this.actions.COMPONENTENGAGED && details){
        keyStr += '.'+ details.compID+'.' + details.name + '.' + details.type
      }

      report[keyStr] = true;

      return Flybits.context.Manager._defaultServices.contentAnalytics.setState(report);
    }
  };

  return analyticsManager;

})();
/*global Flybits*/
Flybits.analytics.AnalyticsStore = (function(){
  var AnalyticsStore = function(opts){
    console.warn('This class is deprecated');
    if(this.constructor === Object){
      throw new Error('Abstract classes cannot be instantiated');
    }

    this.maxStoreSize = opts && opts.maxStoreSize?opts.maxStoreSize:100;
  };
  AnalyticsStore.prototype = {
    implements: function(interfaceName){
      if(!this._interfaces){
        this._interfaces = [];
      }
      this._interfaces.push(interfaceName);
    },
  };

  return AnalyticsStore;
})();

/*global Flybits*/
Flybits.analytics.RecordsStore = (function(){
  var Deferred = Flybits.Deferred;
  var Validation = Flybits.Validation;
  var ObjUtil = Flybits.util.Obj;
  var Event = Flybits.analytics.Event;

  function RecordsStore(opts){
    console.warn('This class is deprecated');
    Flybits.analytics.AnalyticsStore.call(this,opts);
    if(opts){
      this.opts = ObjUtil.extend({},opts);
    }

    this._store = new Flybits.store.Record(this.STOREID);
  }

  RecordsStore.prototype = Object.create(Flybits.analytics.AnalyticsStore.prototype);
  RecordsStore.prototype.constructor = RecordsStore;

  RecordsStore.prototype.STOREID = RecordsStore.STOREID = 'flb.analytics';

  RecordsStore.prototype.addEvent = function(event){
    var def = new Deferred();
    var bStore = this;
    var store = this._store;
    var storeLength = 0;

    if(!(event instanceof Event)){
      def.reject(new Validation().addError('Invalid Argument','Must be an instance of an analytics Event',{
        code: Validation.type.INVALIDARG
      }));
      return def.promise;
    }

    store.keys().then(function(keys){
      storeLength = keys.length;
      return bStore._saveState(event);
    }).then(function(){
      if (storeLength >= bStore.maxStoreSize){
        bStore._validateStoreState().then(function(){
          def.resolve();
        });
      } else{
        def.resolve();
      }
    }).catch(function(e){
      def.reject(e);
    });

    return def.promise;
  };

  RecordsStore.prototype.getEvent = function(tmpID){
    var def = new Deferred();
    this._store.get(tmpID).then(function(res){
      if(res){
        var rehydratedEvt = new Event(res);
        rehydratedEvt._tmpID = tmpID;
        def.resolve(rehydratedEvt);
      } else{
        def.resolve();
      }
    }).catch(function(e){
      def.reject(e);
    });

    return def.promise;
  };

  RecordsStore.prototype.clearEvents = function(tmpIDs){
    var store = this._store;
    var def = new Deferred();
    var deleteData;
    deleteData = function(){
      if(tmpIDs.length <= 0){
        def.resolve();
        return;
      }
      var curKey = tmpIDs.pop();
      store.remove(curKey).catch(function(){}).then(function(){
        deleteData();
      });
    };
    deleteData();

    return def.promise;
  };

  RecordsStore.prototype.clearAllEvents = function(){
    return this._store.clear();
  };

  RecordsStore.prototype.getAllEvents = function(){
    var def = new Deferred();
    var data = [];
    var store = this._store;

    store.keys().then(function(keys){
      var mapData = function(){
        if(!keys.length){
          def.resolve(data);
          return;
        }
        var curKey = keys.pop();
        store.get(curKey).then(function(item){
          var rehydratedEvt = new Event(item);
          rehydratedEvt._tmpID = curKey;
          data.push(rehydratedEvt);
        }).catch(function(){}).then(function(){
          mapData();
        });
      };
      mapData();
    });

    return def.promise;
  };

  RecordsStore.prototype._saveState = function(event){
    return this._store.set(event._tmpID,event.toJSON());
  };

  RecordsStore.prototype._validateStoreState = function(){
    var bStore = this;
    var def = new Deferred();
    var promises = [];
    var store = this._store;
    store.keys().then(function(result){
      var keys = result;
      var accessCount = keys.length - bStore.maxStoreSize;

      keys.sort(function(a,b){
        return (+(a.split('-')[1]))-(+(b.split('-')[1]));
      });

      if(accessCount > 0){
        for(var i = 0; i < accessCount; i++){
          promises.push(store.remove(keys.shift()));
        }
      }

      Flybits.Deferred.settlePromises(promises).then(function(){
        def.resolve();
      });
    }).catch(function(){
      def.reject();
    });

    return def.promise;
  };


  return RecordsStore;
})();

/*global Flybits*/
Flybits.analytics.UploadChannel = (function(){
  var UploadChannel = function(){
    console.warn('This abstract class is deprecated');
    if(this.constructor === Object){
      throw new Error('Abstract classes cannot be instantiated');
    }
  };
  UploadChannel.prototype = {
    implements: function(interfaceName){
      if(!this._interfaces){
        this._interfaces = [];
      }
      this._interfaces.push(interfaceName);
    },
  };

  return UploadChannel;
})();

/*global Flybits*/
Flybits.analytics.DefaultChannel = (function(){
  var Deferred = Flybits.Deferred;
  var ApiUtil = Flybits.util.Api;
  var Session = Flybits.Session;

  var DefaultChannel = function(opts){
    console.warn('This class is deprecated');
    Flybits.analytics.UploadChannel.call(this,opts);

    this.sessionKey = null;
    this.HOST = Flybits.cfg.analytics.CHANNELHOST;
    this.channelKey = Flybits.cfg.analytics.CHANNELKEY;
    this.appID = Flybits.cfg.analytics.APPID;
  };

  DefaultChannel.prototype = Object.create(Flybits.analytics.UploadChannel.prototype);
  DefaultChannel.prototype.constructor = DefaultChannel;

  DefaultChannel.prototype.initSession = function(){
    var def = new Deferred();
    var channel = this;
    var url = this.HOST + '/session?key=' + this.channelKey;
    ApiUtil.fetch(url,{
      method: 'GET',
      respType: 'json'
    }).then(function(resp){
      channel.sessionKey = resp.body.key;
      def.resolve();
    }).catch(function(resp){
      def.reject(resp);
    });

    return def.promise;
  };

  DefaultChannel.prototype._preparePayload = function(events){
    return {
      deviceID: Session.deviceID,
      data: events.map(function(obj){
        var rawObj = obj.toJSON();
        rawObj.properties['_uid'] = Session.user.id;
        return rawObj;
      })
    };
  };

  DefaultChannel.prototype._upload = function(payload){
    var def = new Deferred();
    var url = this.HOST + '/events';
    ApiUtil.fetch(url,{
      method: 'POST',
      headers: {
        key: this.sessionKey,
        appid: this.appID,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      respType: 'empty'
    }).then(function(){
      def.resolve();
    }).catch(function(resp){
      def.reject(resp);
    });

    return def.promise;
  };

  DefaultChannel.prototype.uploadEvents = function(events){
    var channel = this;
    var payload = this._preparePayload(events);
    var sessionPromise = Promise.resolve();
    if(!this.sessionKey){
      sessionPromise = this.initSession();
    }

    return sessionPromise.then(function(){
      return channel._upload(payload);
    });
  };

  return DefaultChannel;
})();

/*global Flybits*/
/**
 * This is a utility class, do not use constructor.
 * @class Content
 * @classdesc API wrapper class for the retrieval of {@link Flybits.Content} models from the Flybits core.
 * @memberof Flybits.api
 */
Flybits.api.Content = (function(){
  /**
   * Available request parameters to filter {@link Flybits.Content} in the Flybits core.
   * @typedef RequestParams
   * @memberof Flybits.api.Content
   * @type Object
   * @property {string} [id] ID of a {@link Flybits.Content}.
   * @property {string[]} [ids] List of {@link Flybits.Content} IDs.
   * @property {Flybits.api.Paging} [paging] Details to dictate which page, or subset, of results should be returned from the total records that match the query parameters.
   * @property {Object} [sortBy] Specification of server sorting of models requested.
   * @property {string} sortBy.key String pertaining to a sorting parameter by which to order server model response by.  Current implementation allows sorting by `createdAt,modifiedAt,evaluatedAt`.  If `managed=true` than only the follow sorting keys are supported `name,createdAt,modifiedAt`.
   * @property {string} [sortBy.direction="descending"] Direction of ordering based on specified `key`.  Possible values are `descending` or `ascending`
   * @property {string} [templateType] Flybits Content Template type string to filter by.
   * @property {string} [templateID] ID of Flybits Content Template to filter by.  Note it is recommended to use `templateType` as `templateID` is different per Flybits tenant.
   * @property {string[]} labels An array of string labels to filter Content by.  Note that these labels are joined with a logical OR.  Eg: `['event', 'park']`
   * @property {string} [labelsFormula] An advanced label formula string that supports a boolean statement of label specification with brackets, logical ORs, and logical ANDs.  If a `labelsFormula` is provided, it will take precedence over a `labels` array. @see {@link Flybits.api.Content.LabelsFormula}
   * @property {boolean} [managed] Flag to indicate the return of relevant `Content` instances or `Content` instances that are manageable by the requesting user based on permission levels.  This is reserved for Experience Studio admins.
   * @property {boolean} [disableContextEmbed] Flag to indicate the return of `Content` instances with context embedding placeholders still in present. This flag is available only to management admin purposes.
   * @property {boolean} [richText] Flag to indicate the return of rich text.
   * @property {boolean} [getJourneyUsages] Flag to indicate whether to include journey usage information in the returned Content instances.
   * @property {Object} [location]  Specification of location attributes required for querying Content instances.
   * @property {string} location.key Key name of the location object within the Content data.
   * @property {number} location.lat Latitiude by which to perform the query.
   * @property {number} location.lng Longitude by which to perform the query.
   * @property {number} [location.radius=10000] Radius of location search surrounding provided latitude and longitude.
   * @property {boolean} [data=true] Flag indicating whether or not to include Content Instance JSON data objects embedded within the Content Instance in the server response.  By default this is set to `true`.
   * @property {string} [search] Search string to filter Content by name.  Note: this is only supported when `managed=true`
   * @property {string} [name] Name string to filter Content by exact name.  Note: this is only supported when `managed=true`
   * @property {string} [renderType=default] Indicates the class of content to retrieve.  By default only non-html based content is returned. View all available {@link Flybits.Content.renderTypes | render types here}.
   */

  /**
   * @typedef LabelsFormula
   * @memberof Flybits.api.Content
   * @example 
   * Supported Operators:
   * NOT - !
   * AND - ,
   * OR - ;
   * 
    (english)
    ((english))
    (!(english))
    (english;french)
    (english,french)
    (english,!french)
    (english;french;spanish)
    (english,french,spanish)
    (english,!french,!spanish)
    (english,(french;spanish),german)
    ((english,french);(spanish,german))
   */
  var ObjUtil = Flybits.util.Obj;
  var ApiUtil = Flybits.util.Api;
  var Validation = Flybits.Validation;
  var Deferred = Flybits.Deferred;
  var Content = Flybits.Content;

  var lastPaging = null;

  const MANAGEDSORTKEYS = ['name', 'createdAt', 'modifiedAt'];
  const RELAVANTSORTKEYS = ['createdAt','modifiedAt','evaluatedAt'];

  function parseParams( reqParams ){
    var validation = new Validation();
    var req = {};
    if( !reqParams ){
      return req;
    }

    if(reqParams.id || reqParams.ids){
      var retArr = reqParams.ids?reqParams.ids:[];

      if(reqParams.id && retArr.indexOf(reqParams.id) < 0){
        retArr.push(reqParams.id)
      }

      if(retArr.length){
        req.contentIds = retArr.join(',');
      }
    }

    if(reqParams.managed){
      req.managementMode = true;
    }

    if( reqParams.paging ){
      if( !reqParams.paging.limit && !reqParams.paging.offset ){
        validation.addError('Missing Argument','paging request parameter object must contain at least a limit or offset property.',{
          code: Validation.type.MISSINGARG,
          context: 'paging.limit, paging.offset'
        });
      } else{
        if( reqParams.paging.limit > 0 ){
          req.limit = reqParams.paging.limit;
        }
        if( reqParams.paging.offset >= 0 ){
          req.offset = reqParams.paging.offset;
        }
      }
    }

    if( reqParams.sortBy ){
      var key = reqParams.sortBy.key;
      var order = reqParams.sortBy.order;

      if( !key ){
        validation.addError('Missing Argument','sortBy requires a key to sort by.',{
          code: Validation.type.MISSINGARG,
          context: 'sortBy.key'
        });
      }

      if( order && !(order === 'asc' || order === 'ascending' || order === 'desc' || order === 'descending') ){
        validation.addError('Invalid Argument','Content sort direction invalid.  Supported values are: asc or ascending or desc or descending',{
          code: Validation.type.INVALIDARG,
          context: 'sortBy.order'
        });
      } else if(key && !order){
        order = 'desc'
      }

      if(order === 'ascending'){
        order = 'asc';
      } else if(order === 'descending'){
        order = 'desc';
      }
      
      if(reqParams.managed){
        if(!MANAGEDSORTKEYS.includes(key)){
          validation.addError('Invalid Argument','Content cannot be sorted by this property:'+key,{
            code: Validation.type.INVALIDARG,
            context: 'sortBy.key'
          });
        } else{
          req.sortby = key;
          req.sortorder = order;
        }
      } else{
        if(!RELAVANTSORTKEYS.includes(key) ){
          validation.addError('Invalid Argument','Content cannot be sorted by this property:'+key,{
            code: Validation.type.INVALIDARG,
            context: 'sortBy.key'
          });
        } else{
          req.orderby = key;
          req.sortorder = order;
        }
      }
    }

    if(reqParams.templateType){
      req.templateType = reqParams.templateType
    }

    if( reqParams.templateID ){
      req.templateId = reqParams.templateID;
    }

    if(reqParams.labelsFormula){
      req.labelsFormula = reqParams.labelsFormula;
    } else if(reqParams.labels){
      if(reqParams.labels.constructor !== Array){
        validation.addError('Invalid Argument','Labels must be an array of strings',{
          code: Validation.type.INVALIDARG,
          context: 'labels'
        });
      } else if(reqParams.labels.length > 0){
        req.labelsFormula = '('+reqParams.labels.join(';')+')';
      }
    }

    /** Boolean params **/
    if(Object.prototype.hasOwnProperty.call(reqParams,'data')){
      req.data = reqParams.data;
    }

    if(Object.prototype.hasOwnProperty.call(reqParams,'disableContextEmbed')){
      req.disableContextEmbed = reqParams.disableContextEmbed;
    }

    if(Object.prototype.hasOwnProperty.call(reqParams,'richText')){
      req.returnRichText = reqParams.richText;
    }

    if(Object.prototype.hasOwnProperty.call(reqParams,'getJourneyUsages')){
      req.getJourneyUsages = reqParams.getJourneyUsages;
    }
    /** End of boolean params **/

    if(reqParams.location){
      if(!reqParams.location.key){
        validation.addError('Missing Argument','location query requires a key to sort by.',{
          code: Validation.type.MISSINGARG,
          context: 'location.key'
        });
      } else{
        req.loc = reqParams.location.key;
      }
      if(!reqParams.location.lat){
        validation.addError('Missing Argument','location query requires a latitude.',{
          code: Validation.type.MISSINGARG,
          context: 'location.lat'
        });
      } else{
        req.lat = reqParams.location.lat;
      }
      if(!reqParams.location.lng){
        validation.addError('Missing Argument','location query requires a longitude.',{
          code: Validation.type.MISSINGARG,
          context: 'location.lng'
        });
      } else{
        req.lng = reqParams.location.lng;
      }
      if(reqParams.location.radius){
        req.radius = reqParams.location.radius;
      } else{
        req.radius = 1000;
      }
    }

    if(reqParams.managed){
      if (reqParams.name && typeof reqParams.name === 'string'){
        req.name = reqParams.name
      } else if (reqParams.search && typeof reqParams.search === 'string'){
        req.search = reqParams.search.toLowerCase();
      }
    } else {
      if(reqParams.name || reqParams.search){
        validation.addError('Invalid Argument', 'Name/Search is only supported for admins and if `managed` flag is true.',{
          code: Validation.type.INVALIDARG,
          context: reqParams.name ? 'name': 'search'
        });
      }
    }

    if(reqParams.renderType){
      switch(reqParams.renderType){
      case Flybits.Content.renderTypes.DEFAULT:
      case Flybits.Content.renderTypes.ALL:
      case Flybits.Content.renderTypes.HTML:
        req.renderType = reqParams.renderType;
        break;
      default:
        validation.addError('Invalid Argument', 'Render type can only be `html`, `all`, or `default`.',{
          code: Validation.type.INVALIDARG,
          context: 'renderType'
        });
      }
    }

    if(!validation.state){
      throw validation;
    }

    return req;
  }

  var content = {

    /**
     * Helper to retrieve cached paging property.  After every API request for {@link Flybits.Content} models, the static paging cache is replaced by the paging properties of the latest request.
     * @memberof Flybits.api.Content
     * @function getPaging
     * @returns {Flybits.api.Paging} The last pagination object received from retrieving {@link Flybits.Content|Contents}.
     */
    getPaging: function(){
      return lastPaging;
    },
    
    /**
     * Retrieves {@link Flybits.Content} model with specified ID.
     * @memberof Flybits.api.Content
     * @function get
     * @param {string} id - ID of the {@link Flybits.Content} model.
     * @returns {external:Promise<Flybits.Content,Flybits.Validation>} Promise that resolves with {@link Flybits.Content} with specified ID.  Promise rejects with a {@link Flybits.Validation} object containing {@link Flybits.Validation.ValidationError|error} objects if request has failed or if a {@link Flybits.Content} was not found with supplied ID.
     */
    get: function( id, opts ){
      var def = new Deferred();
      var url = Flybits.cfg.HOST + Flybits.cfg.res.CONTENTS;
      var data = {
        data: true,
      };
      var localization = opts && opts.localization?opts.localization : 'all';

      if( !id || id === '' ){
        throw new Validation().addError('Missing Argument','No Content ID specified',{
          code: Validation.type.MISSINGARG,
          context: 'id'
        });
      }

      if(opts && Object.prototype.hasOwnProperty.call(opts, 'getJourneyUsages')){
        data.getJourneyUsages = opts.getJourneyUsages;
      }
 
      url = url + '/' + id + '?' + ApiUtil.toURLParams(data);

      ApiUtil.flbFetch( url, {
        method: 'GET',
        headers: {
          'accept-language': localization
        }
      }).then( function( resp ){
        if( resp && resp.body ){
          var content; 
          try{
            content = Content.getInstance( resp.body );
          } catch(e){
            console.error(e);
            def.reject( new Validation().addError('Server model Parsing Failed','Failed to parse single content server model.',{
              code: Validation.type.MALFORMED,
              context: resp
            }));
          }

          def.resolve({
            result: content
          });
        } else {
          def.reject( new Validation().addError('Server Request Failed','Failed to retrieve single content from server',{
            code: Validation.type.MALFORMED
          }));
        }
      }).catch( function( resp ){
        def.reject( resp );
      });

      return def.promise;
    },

    /**
     * Retrieves {@link Flybits.Content} models by specified request parameters.
     * @memberof Flybits.api.Content
     * @function getAll
     * @param {Flybits.api.Content.RequestParams} requestParams Request parameter object to filter {@link Flybits.Content} models in the core.
     * @returns {external:Promise<Flybits.api.Result,Flybits.Validation>} Promise that resolves with a {@link Flybits.api.Result} object with a list of {@link Flybits.Content} models that meet the request parameters.  Promise rejects with a {@link Flybits.Validation} object containing {@link Flybits.Validation.ValidationError|error} objects if request has failed.
     */
    getAll: function( requestParams ){
      var def = new Deferred();
      var url = Flybits.cfg.HOST;
      var data = {
        data: true
      };
      var reqParamCopy = requestParams? ObjUtil.extend({}, requestParams): undefined;
      
      if(requestParams && requestParams.managed){
        url += Flybits.cfg.res.CONTENTS;
      } else if(requestParams && requestParams.location){
        url += Flybits.cfg.res.CONTENTS;
      } else{
        url += Flybits.cfg.res.RELEVANTCONTENTS;
      }
      var localization = 'all';
      
      if(requestParams){
        localization = requestParams.localization?requestParams.localization:'all';
        delete requestParams.localization;
        requestParams = ObjUtil.extend(data,requestParams);
        data = parseParams(requestParams);
      }
      
      data = ApiUtil.toURLParams( data );
      if( data !== '' ){
        url += '?' + data;
      }

      ApiUtil.flbFetch( url, {
        method: 'GET',
        headers: {
          'accept-language': localization
        }
      }).then( function( resp ){
        if( resp && resp.body && resp.body.data && resp.body.data.length >= 0 ){
          var paging = ApiUtil.parsePaging( resp.body );
          lastPaging = paging;

          var contents = resp.body.data.map( function(obj){
            try{
              var contentInstance = Content.getInstance( obj );
              return contentInstance;
            } catch(e){
              console.error(e);
              def.reject( new Validation().addError('Request Failed','Failed to parse server model.',{
                code: Validation.type.MALFORMED,
                context: obj
              }));
            }
          });

          def.resolve({
            result: contents,
            nextPageFn: ApiUtil.createNextPageCall( Flybits.api.Content.getAll, reqParamCopy, paging ),
            paging: paging
          });
        } else {
          def.reject( new Validation().addError('Server Request Failed','Failed to retrieve contents from server',{
            code: Validation.type.MALFORMED
          }));
        }
      }).catch( function( resp ){
        def.reject( resp );
      });

      return def.promise;
    }
  };

  return content;
})();
/*global Flybits,Path*/
/**
 * This is a utility class, do not use constructor.
 * @class ContentData
 * @classdesc API wrapper class for the retrieval of {@link Flybits.ContentData} and {@link Flybits.PagedData} models from Flybits core.
 * @memberof Flybits.api
 */
Flybits.api.ContentData = (function(){
  var ObjUtil = Flybits.util.Obj;
  var ApiUtil = Flybits.util.Api;
  var Validation = Flybits.Validation;
  var Deferred = Flybits.Deferred;
  var Content = Flybits.Content;
  var ContentData = Flybits.ContentData;
  var PagedData = Flybits.PagedData;

  var lastPaging = null;

  function parseParams( reqParams ){
    var req = {};
    if( !reqParams ){
      return req;
    }

    if( reqParams.paging ){
      var pagingFieldKeys = Object.keys( reqParams.paging );
      pagingFieldKeys.forEach( function( key ){
        var pagingKeyValue = reqParams.paging[key];
        if( pagingKeyValue.constructor === Object ){
          var pagingLimit = pagingKeyValue.limit;
          var pagingOffset = pagingKeyValue.offset;

          if( !pagingLimit && !pagingOffset ){
            throw new Validation().addError('Missing Argument','paging request parameter object must contain at least a limit or offset property.',{
              code: Validation.type.MISSINGARG,
              context: 'pagingLimit, pagingOffset'
            });
          }

          if( pagingLimit > 0 ){
            var pagingLimitKey = key+'.limit';
            req[pagingLimitKey] = pagingLimit;
          }
          if( reqParams.paging[key].offset >= 0 ){
            var pagingOffsetKey = key+'.offset';
            req[pagingOffsetKey] = pagingOffset;
          }
        } else {
          if( key === 'limit' && pagingKeyValue > 0 ){
            req.limit = pagingKeyValue;
          }
          if( key === 'offset' && pagingKeyValue >= 0 ){
            req.offset = pagingKeyValue;
          }
        }
      });
    }

    if( reqParams.sortBy ){
      var sortingFieldKeys = Object.keys( reqParams.sortBy );
      sortingFieldKeys.forEach( function( key ){
        var sortKeyValue = reqParams.sortBy[key];
        
        if( sortKeyValue.constructor === Object ){
          var sortKey = sortKeyValue.key;
          var sortOrder = sortKeyValue.order;

          if( !sortKey ){
            throw new Validation().addError('Missing Argument','orderBy requires a ContentData model property key to order by.',{
              code: Validation.type.MISSINGARG,
              context: 'orderBy.key'
            });
          }
          // @TODO: IMPLEMENT VALIDATION TO CHECK IF SORTING CAN BE DONE BY KEY SPECIFIED BY USER
          var sortFilterKey = key+'.sortBy';
          req[sortFilterKey] = sortKey;

          if( sortOrder && !(sortOrder === 'asc' || sortOrder === 'desc') ){
            throw new Validation().addError('Invalid Argument','Content cannot be ordered by in such way(MUST BE asc or desc)',{
              code: Validation.type.INVALIDARG,
              context: 'orderBy.order'
            });
          }
          var sortFilterOrder = key+'.order';
          req[sortFilterOrder] = sortOrder;
        } else {
          if( key === 'key' ){
            req.key = sortKeyValue;
          }
          if( key === 'order' ){
            req.key = sortKeyValue;
          }
        }
      });

      if(Object.prototype.hasOwnProperty.call(reqParams,'data')){
        req.data = reqParams.data;
      }
    }

    return req;
  }

  var content = {

    /**
     * Helper to retrieve cached paging property.  After every API request for {@link Flybits.ContentData} models, the static paging cache is replaced by the paging properties of the latest request.
     * @memberof Flybits.api.ContentData
     * @function getPaging
     * @returns {Flybits.api.Paging} The last pagination object received from retrieving {@link Flybits.ContentData}.
     */
    getPaging: function(){
      return lastPaging;
    },
    
    /**
     * Retrieves {@link Flybits.ContentData} model with specified ID.
     * @memberof Flybits.api.ContentData
     * @function get
     * @param {Object} requestParams Request parameters.
     * @param {string} requestParams.contentID Content ID to which the requested {@link Flybits.ContentData|ContentData} belongs to.
     * @param {string} requestParams.contentDataID Specific ID of the requested {@link Flybits.ContentData|ContentData}.
     * @param {Flybits.api.Paging} [requestParams.paging] Details to dictate which page, or subset, of results should be returned from the total records that match the query parameters.
     * @returns {external:Promise<Flybits.api.Result,Flybits.Validation>} Promise that resolves with result object containing specific {@link Flybits.ContentData}.
     */
    get: function( requestParams ){
      var def = new Deferred();
      var url = Flybits.cfg.HOST + Flybits.cfg.res.CONTENTS;
      var contentID = requestParams?requestParams.contentID:undefined;
      var contentDataID = requestParams?requestParams.contentDataID:undefined;
      var localization = requestParams && requestParams.localization?requestParams.localization:'all';

      if( !contentID || contentID === '' ){
        throw new Validation().addError('Missing Argument','No Content ID specified to find Data',{
          code: Validation.type.MISSINGARG,
          context: contentID
        });
      }

      if( !contentDataID || contentDataID === '' ){
        throw new Validation().addError('Missing Argument','No Content Data ID specified to find Data',{
          code: Validation.type.MISSINGARG,
          context: 'contentDataID'
        });
      }

      if(requestParams){
        delete requestParams.contentID;
        delete requestParams.localization;
        delete requestParams.contentDataID;
        requestParams = parseParams( requestParams );
      }

      url = url + '/' + contentID + '/data/' + contentDataID;
      requestParams = ApiUtil.toURLParams( requestParams );
      if( requestParams !== '' ){
        url += '?' + requestParams;
      }

      ApiUtil.flbFetch( url, {
        method: 'GET',
        headers: {
          'accept-language': localization
        }
      }).then( function( resp ){
        if( resp && resp.body ){
          var contentData; 
          var routePath = new Path();
          routePath.append(new Content({id:contentID}));
          try{
            contentData = new ContentData( resp.body, routePath );
          } catch(e){
            console.error(e);
            def.reject( new Validation().addError('Server model Parsing Failed','Failed to parse single content server model.',{
              code: Validation.type.MALFORMED,
              context: resp
            }));
          }

          def.resolve({
            result: contentData
          });
        } else {
          def.reject( new Validation().addError('Server Request Failed','Failed to retrieve single content from server',{
            code: Validation.type.MALFORMED
          }));
        }
      }).catch( function( resp ){
        def.reject( resp );
      });

      return def.promise;
    },

    /**
     * Retrieves all {@link Flybits.ContentData} models from a {@link Flybits.Content}.
     * @memberof Flybits.api.ContentData
     * @function getAll
     * @param {Object} requestParams Request parameters.
     * @param {string} requestParams.contentID Content ID to which the requested {@link Flybits.ContentData|ContentData} models belongs to.
     * @param {Flybits.api.Paging} [requestParams.paging] Details to dictate which page, or subset, of results should be returned from the total records that match the query parameters.
     * @returns {external:Promise<Flybits.api.Result,Flybits.Validation>} Promise that resolves with result object containing array of {@link Flybits.ContentData}.
     */
    getAll: function( requestParams ){
      var def = new Deferred();
      var url = Flybits.cfg.HOST + Flybits.cfg.res.CONTENTS;
      var contentID = requestParams?requestParams.contentID:undefined;
      var localization = requestParams && requestParams.localization?requestParams.localization:'all';

      if( !contentID || contentID === '' ){
        throw new Validation().addError('Missing Argument','No Content ID specified to find Data',{
          code: Validation.type.MISSINGARG,
          context: 'contentID'
        });
      }

      if(requestParams){
        var reqParamCopy = ObjUtil.extend({},requestParams);
        delete requestParams.contentID;
        delete requestParams.localization;
        var data = parseParams( requestParams );
      }

      url = url + '/' + contentID + '/data';

      data = ApiUtil.toURLParams( data );
      if( data !== '' ){
        url += '?' + data;
      }

      ApiUtil.flbFetch( url, {
        method: 'GET',
        headers: {
          'accept-language': localization
        }
      }).then( function( resp ){
        
        if( resp && resp.body && resp.body.data && resp.body.data.length >= 0 ){
          var paging = ApiUtil.parsePaging( resp.body );
          lastPaging = paging;

          var allContentData = resp.body.data.map( function(obj){
            var routePath = new Path();
            routePath.append(new Content({id:contentID}));
            try{
              return new ContentData( obj, routePath );
            } catch(e){
              console.error(e);
              def.reject( new Validation().addError('Request Failed','Failed to parse server model.',{
                code: Validation.type.MALFORMED,
                context: obj
              }));
            }
          });

          def.resolve({
            result: allContentData,
            contentID: contentID,
            nextPageFn: ApiUtil.createNextPageCall( Flybits.api.ContentData.getAll, reqParamCopy, paging )
          });
        } else {
          def.reject( new Validation().addError('Server Request Failed','Failed to retrieve contents from server',{
            code: Validation.type.MALFORMED
          }));
        }
      }).catch( function( resp ){
        def.reject( resp );
      });

      return def.promise;
    },
    /**
     * The actual JSON payload that lies within {@link Flybits.ContentData}, that is specified in the Experience Studio, can contain pageable arrays in the form of {@link Flybits.PagedData}.  Thus this function is used to retrieve further pages within.
     * @memberof Flybits.api.ContentData
     * @function getPagedData
     * @param {Object} requestParams Request parameters.
     * @param {string} requestParams.key JSON key of the {@link Flybits.PagedData}.
     * @param {Flybits.Path} requestParams.path Path object that represents the nested depth of the pageable data.
     * @param {Flybits.api.Paging} [requestParams.paging] Details to dictate which page, or subset, of results should be returned from the total records that match the query parameters.
     * @returns {external:Promise<Flybits.api.Result,Flybits.Validation>} Promise that resolves with result object containing raw JSON objects of the requests nested page.
     */
    getPagedData: function(requestParams){
      var def = new Deferred();
      var url = Flybits.cfg.HOST + '/kernel';
      var localization = requestParams && requestParams.localization?requestParams.localization:'all';
      var validation = new Validation();
      
      if(!requestParams || !requestParams.path){
        validation.addError('Missing Argument','No Path provided for nested data',{
          code: Validation.type.MISSINGARG,
          context: 'path'
        });
      }

      if(!requestParams || !requestParams.key){
        validation.addError('Missing Argument','No field key provided for nested data array.',{
          code: Validation.type.MISSINGARG,
          context: 'key'
        });
      }

      if(!validation.state){
        throw validation.setStackTrace();
      }

      url += requestParams.path.serialize() + '?field=' + requestParams.key;
      var reqParamCopy = ObjUtil.extend({},requestParams);
      delete requestParams.localization;
      delete requestParams.path;
      delete requestParams.key;
      if(requestParams.paging){
        var reqParam = requestParams.paging;
        requestParams.paging = {};
        requestParams.paging[reqParamCopy.key] = reqParam;
      }


      var dataParams = parseParams(requestParams);
      dataParams = ApiUtil.toURLParams( dataParams );
      if( dataParams !== '' ){
        url += '&' + dataParams;
      }

      ApiUtil.flbFetch( url, {
        method: 'GET',
        headers: {
          'accept-language': localization
        }
      }).then( function(resp){
        var list = resp.body[reqParamCopy.key];
        var listPaging = ApiUtil.parsePaging( resp.body, reqParamCopy.key+'.pagination');
        var tmpPagedData = new PagedData();
        tmpPagedData.fromJSON(list);

        def.resolve({
          result: tmpPagedData.data,
          nextPageFn: ApiUtil.createNextPageCall(Flybits.api.ContentData.getPagedData,reqParamCopy,listPaging)
        });
      }).catch(function(resp){
        def.reject(resp);
      });

      return def.promise;
    },
    /**
     * Submits JSON data to a content instance.
     * @memberof Flybits.api.ContentData
     * @function submitData
     * @param {Object} conatentData JSON body of data.  This body must match the template structure of the Content template 
     * @returns {external:Promise<Flybits.api.Result,Flybits.Validation>} Promise that resolves if successful and rejects with a `Validation` instance if it is not successful.
     */
    submitData: function(contentData){
      var def = new Deferred();
      var url = Flybits.cfg.HOST + '/kernel';
      url += contentData.path.serialize();
      var method = contentData.id? 'PUT' : 'POST';

      if(!(contentData instanceof Flybits.ContentData)){
        def.reject(new Validation().addError('Invalid argument','Can only submit ContentData types.',{
          code: Validation.type.INVALIDARG,
          context: contentData
        }));
      } else{
        ApiUtil.flbFetch(url, {
          method: method,
          body: JSON.stringify(contentData.toJSON())
        }).then(function(resp){
          def.resolve(resp);
        }).catch(function(e){
          def.reject(e);
        });
      }


      return def.promise;
    }
  };

  return content;
})();
/*global Flybits*/
/**
 * This is a utility class, do not use constructor.
 * @class Push
 * @classdesc API wrapper class for the registration of {@link Flybits.PushToken} models from Flybits core.
 * @memberof Flybits.api
 */
Flybits.api.Push = (function () {
  var ApiUtil = Flybits.util.Api;
  var Validation = Flybits.Validation;
  var Deferred = Flybits.Deferred;
  var PushToken = Flybits.PushToken;

  var push = {
    /**
     * Registers a device token to receive push notifications.
     * @memberof Flybits.api.Push
     * @function registerToken
     * @param {Object} params Request parameters.
     * @param {string} params.deviceToken The FCM or APNs token string from the device.
     * @param {number} params.network Integer representing the network (e.g., 0 for APNs, 1 for FCM).
     * @param {Object} [params.data] Object linking the token to a user ID.
     * @returns {external:Promise<Object,Flybits.Validation>}
     */
    registerToken: function (params) {
      var def = new Deferred();
      var validation = new Validation();
      var url = Flybits.cfg.HOST + Flybits.cfg.res.PUSHTOKEN;

      var pushTokenModel =
        params instanceof PushToken ? params : new PushToken(params);

      // 1. Validate Input
      if (!pushTokenModel.deviceToken) {
        validation.addError('Missing Argument', 'Device token is required.', {
          code: Validation.type.MISSINGARG,
          context: 'deviceToken',
        });
      }

      var isValidNetwork = Object.values(PushToken.networkTypes).includes(pushTokenModel.network);

      if (!isValidNetwork) {
        validation.addError(
          'Invalid Argument',
          'Network type must be a recognized integer corresponding to its respective network (e.g. APNs, FCM,...).',
          {
            code: Validation.type.INVALIDARG,
            context: 'network',
          },
        );
      }

      if (
        pushTokenModel.pushVersion !== undefined &&
        typeof pushTokenModel.pushVersion !== 'number'
      ) {
        validation.addError(
          'Missing Argument',
          'Push version must be an integer.',
          {
            code: Validation.type.INVALIDARG,
            context: 'pushVersion',
          },
        );
      }

      if (!validation.state) {
        def.reject(validation);
        return def.promise;
      }

      // 2. Prepare Payload
      var payload = pushTokenModel.toJSON();

      // 3. Make the API Call
      // flbFetch automatically adds X-Authorization, Content-Type, and X-User-Agent
      ApiUtil.flbFetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
        respType: 'empty',
      })
        .then(function (resp) {
          def.resolve(resp.body || resp);
        })
        .catch(function (err) {
          def.reject(err);
        });

      return def.promise;
    },

    /**
     * Deletes a registered device token from the Flybits server.
     * Matches the endpoint: DELETE {{host}}/push/token
     * @memberof Flybits.api.Push
     * @function deleteToken
     * @returns {external:Promise<string,Flybits.Validation>}
     */
    deleteToken: function () {
      var def = new Deferred();
      var url = Flybits.cfg.HOST + Flybits.cfg.res.PUSHTOKEN;

      // Make the API Call with NO body
      ApiUtil.flbFetch(url, {
        method: 'DELETE',
        respType: 'text',
      })
        .then(function (resp) {
          // Success: resp will be "Deleted the token."
          def.resolve(resp.body || resp);
        })
        .catch(function (err) {
          def.reject(err);
        });

      return def.promise;
    },
  };

  return push;
})();

/*global Flybits*/
/**
 * This is a utility class, do not use constructor.
 * @class User
 * @classdesc API wrapper class for the retrieval of {@link Flybits.User} models from Flybits core.
 * @memberof Flybits.api
 */
Flybits.api.User = (function(){
  var User = Flybits.User;
  var Session = Flybits.Session;

  var user = {
    /**
     * Fetches from the Flybits core the currently authenticated {@link Flybits.User}.
     * @memberof Flybits.api.User
     * @function getSignedInUser
     * @returns {external:Promise<Flybits.User,Flybits.Validation>} Promise that resolves with the {@link Flybits.User} model of the currently authenticated user. Promise rejects with a {@link Flybits.Validation} model if request fails to complete successfully.  This may occur if the user session has expired.
     */
    getSignedInUser: function(){
      var def = new Flybits.Deferred();
      var url = Flybits.cfg.HOST + Flybits.cfg.res.AUTHUSER;
      var authToken = Session.userToken;
      var headerObj = {};
      headerObj[Session.AUTHHEADER] = authToken;

      Flybits.util.Api.flbFetch(url,{
        method: 'GET',
        headers: headerObj,
      }).then(function(respObj){
        var signedInUser = new User(respObj.body);
        Session.user = signedInUser;
        def.resolve(signedInUser);
      }).catch(function(resp){
        def.reject(resp);
      });

      return def.promise;
    },
    /**
     * User scoped settings utilities
     * @memberof Flybits.api.User
     * @namespace settings
     */
    settings: {
      _cache: null,
      _create: function(dataObj, namespace){
        var api = this;
        dataObj = dataObj || {};
        var def = new Flybits.Deferred();
        var url = Flybits.cfg.HOST + Flybits.cfg.res.USERSETTINGS;

        if(namespace){
          url += '/' + namespace;
        }

        Flybits.util.Api.flbFetch(url, {
          method: 'POST',
          body: JSON.stringify(dataObj)
        }).then(function(resp){
          api._cache = resp.body.data;
          def.resolve(resp.body.data);
        }).catch(function(e){
          def.reject(e);
        });

        return def.promise;
      },
      _createBase: function(dataObj){
        dataObj = dataObj || {};
        dataObj = {
          data: dataObj
        };

        return this._create(dataObj);
      },
      /**
       * Fetches user scoped settings.
       * @memberof Flybits.api.User.settings
       * @function get
       * @param {string} [namespace] Namespace of user settings group to retrieve.  If no namespace is provided, the root user settings `Object` will be returned
       * @returns {external:Promise<Object,Flybits.Validation>} Promise that resolves with the user scoped settings `Object`.  If a `namespace` is specified then the corresponding user scoped settings group `Object` will be returned.  Promise rejects with a {@link Flybits.Validation} model if request fails to complete successfully.
       */
      get: function(namespace){
        var api = this;
        var def = new Flybits.Deferred();
        var url = Flybits.cfg.HOST + Flybits.cfg.res.USERSETTINGS;
        if(namespace){
          url += '/' + namespace;
        }

        Flybits.util.Api.flbFetch(url).then(function(resp){
          if(namespace){
            def.resolve(resp.body);
          } else{
            def.resolve(resp.body.data);
          }
        }).catch(function(e){
          if(e.firstError().serverCode === 404){
            if(namespace){
              api._create({}, namespace).then(function(){
                def.resolve({});
              }).catch(function(e){
                if(e.firstError().serverCode === 404){
                  var baseObj = {};
                  baseObj[namespace] = {};
                  api._createBase(baseObj).then(function(){
                    def.resolve({});
                  }).catch(function(e){
                    def.reject(e);
                  });
                } else{
                  def.reject(e);
                }
              });
            } else{
              api._createBase().then(function(){
                def.resolve({})
              }).catch(function(e){
                def.reject(e);
              });
            }
          } else{
            def.reject(e);
          }
        });

        return def.promise;
      },
      /**
       * Sets user scoped settings. 
       * <br/><br/><b>Note:</b> Setting a root settings `Object` will overwrite previously specified namespaced user settings as namespaces are placed as children of the root settings `Object`.
       * @memberof Flybits.api.User.settings
       * @function set
       * @param {Object} dataObj Settings `Object` to set.  If a `namespace` is specified then the settings `Object` will be set using the `namespace` as a settings group.  If no `namespace` is provided, the `dataObj` will be set on the root.
       * @param {string} [namespace] Namespace of user settings group to set `dataObj`.  If no namespace is provided, the root will be used.
       * @returns {external:Promise<Object,Flybits.Validation>} Promise that resolves with the user scoped settings `Object`.  Promise rejects with a {@link Flybits.Validation} model if request fails to complete successfully.
       */
      set: function(dataObj, namespace){
        dataObj = dataObj || {};
        var api = this;
        var def = new Flybits.Deferred();
        var url = Flybits.cfg.HOST + Flybits.cfg.res.USERSETTINGS;
        if(namespace){
          url += '/' + namespace;
        } else{
          dataObj = {
            data: dataObj
          };
        }

        Flybits.util.Api.flbFetch(url,{
          method: 'PUT',
          body: JSON.stringify(dataObj)
        }).then(function(resp){
          api._cache = resp.body.data;
          if(namespace){
            def.resolve(dataObj);
          } else{
            def.resolve(api._cache);
          }
        }).catch(function(e){
          if(e.firstError().serverCode === 404){
            api._create(dataObj,namespace).then(function(resp){
              api._cache = resp;
              if(namespace){
                def.resolve(dataObj);
              } else{
                def.resolve(api._cache);
              }
            }).catch(function(e){
              if(e.firstError().serverCode === 404){
                var baseObj = {};
                baseObj[namespace] = dataObj;
                api._createBase(baseObj).then(function(resp){
                  api._cache = resp;
                  def.resolve(dataObj);
                }).catch(function(e){
                  def.reject(e);
                });
              } else{
                def.reject(e);
              }
            });
          } else{
            def.reject(e);
          }
        });

        return def.promise;
      }
    },
  };

  return user;
})();

/*global Flybits*/
/**
 * This is a utility class, do not use constructor.
 * @class Zones
 * @hideconstructor
 * @classdesc API wrapper class for the retrieval of {@link Flybits.ZonesMetadata} and {@link Flybits.Zone} data.
 * @memberof Flybits.api
 */
Flybits.api.Zones = (function () {
  var ZonesMetadata = Flybits.ZonesMetadata;
  var Zone = Flybits.Zone
  var Module = Flybits.Module
  var Content = Flybits.Content
  var ApiUtil = Flybits.util.Api;
  var Validation = Flybits.Validation;
  var Deferred = Flybits.Deferred;

  var fetchZoneContent = function(url) {
    var def = new Deferred()

    ApiUtil.flbFetch(url, {
      method: 'GET',
      headers: {
        'accept-language': 'all'
      }
    }).then(function (resp) {
      const body = resp.body
      const modulesMap = {};
      const contentMap = {};
      
      if(body.data && Array.isArray(body.data)){
        body.data.forEach(function(c){
          contentMap[c.id] = new Content(c);
        });
      }

      if(body.modules && Array.isArray(body.modules)){
        body.modules.forEach(function(m){
          modulesMap[m.id] = new Module(m, contentMap);
        });
      }

      const zones = body.zones != null ? body.zones?.map((e) => new Zone(e, modulesMap)) : []
      def.resolve({
        zones: zones,
        scriptURLs: body?.scriptURLs || []
      });
    }).catch(function (e) {
      var serverCode = null;
      if(e instanceof Validation){
        serverCode = e.firstError()?.serverCode;
      }
    
      def.reject(
        new Validation().addError('Failed to fetch relevant content', '', {
          code: Validation.type.NOTFOUND,
          serverCode: serverCode,
          context: new URL(url).searchParams,})
      );
    });

    return def.promise
  }

  var zones = {
    /**
     * Retrieves Zones metadata for a specified Flybits project.  Zone metadata will contain simple non-sensitive data such as height and reference IDs of available {@link Flybits.Zone} instances.
     * 
     * >**Note:** This is an unauthenticated API that can assist in early application lifecycle rendering.  It can be invoked at any time while authenticated or not.
     * @memberof Flybits.api.Zones
     * @function getMetadata
     * @param {string} projectId Flybits Project ID specific to each Flybits tenant.
     * @returns {external:Promise<Flybits.ZonesMetadata,Flybits.Validation>} a Promise that fulfills with a {@link Flybits.ZonesMetadata}.
     */
    getMetadata: function (projectId) {
      var def = new Deferred();

      // Make sure a valid projectId has been provided
      if (!projectId) {
        const newValidation = new Validation();
        newValidation.addError(
          'Missing Parameter',
          'Your projectId undefined, length 0, or not a string which are needed to fetch zone metadata',
          {
            code: Validation.type.MISSINGARG,
          }
        );

        return Promise.reject(newValidation);
      }

      var url =
        Flybits.cfg.HOST +
        Flybits.cfg.res.ZONESCONFIGURATION +
        '?tenantId=' +
        projectId;

      ApiUtil.flbFetch(url, {
        method: 'GET',
      })
        .then(function (resp) {
          def.resolve(new ZonesMetadata(resp.body));
        })
        .catch(function (e) {
          var serverCode = null;
          if(e instanceof Validation){
            serverCode = e.firstError()?.serverCode;
          }
          
          def.reject(
            new Validation().addError('No Zones Configuration found', '', {
              code: Validation.type.NOTFOUND,
              serverCode: serverCode,
              context: projectId,
            })
          );
        });

      return def.promise;
    },
    /**
     * Retrieves a single Zone's metadata for a specified Flybits project and Zone Reference ID.
     * 
     * >**Note:** This is an unauthenticated API that can assist in early application lifecycle rendering.  It can be invoked at any time while authenticated or not.
     * @memberof Flybits.api.Zones
     * @function getZoneMetadata
     * @param {string} projectID Flybits Project ID specific to each Flybits tenant.
     * @param {string} zoneReferenceID Zone Reference ID specific to each Zone.
     * @returns {external:Promise<Flybits.ZonesMetadata.ZoneMetadata,Flybits.Validation>} a Promise that fulfills with a {@link Flybits.ZonesMetadata.ZoneMetadata}.
     */
    getZoneMetadata: function(projectID, zoneReferenceID){
      const def = new Deferred();

      this.getMetadata(projectID).then(function(zonesMetadata){
        if(zonesMetadata && zonesMetadata.getZones([zoneReferenceID])?.length){
          def.resolve(zonesMetadata.getZones([zoneReferenceID])[0]);
        } else{
          def.reject(new Validation().addError('Not found', 'No metadata found for specified Project and Zone',{
            code: Validation.type.NOTFOUND,
            context: {
              projectID: projectID,
              zoneReferenceID: zoneReferenceID
            }
          }));
        }
      }).catch(function(e){
        def.reject(e);
      });

      return def.promise;
    },

    /**
     * @typedef ZonesConfig
     * @memberof Flybits.api.Zones
     * @type Object
     * @property {string[]} scriptURLs List of URLs for JavaScript resources specific to the Flybits project.  These can be used to expand upon available web components and even specific tracking and support libraries.
     * @property {Flybits.Zone[]} zones List of {@link Flybits.Zone} objects
     */
    /**
     * Retrieves Flybits Zones containing their respective Modules and relevant curated Content based on specified Zone lables.
     * @memberof Flybits.api.Zones
     * @function getAllByLabels
     * @param {string[]} labels An array of Zone labels that will be used to filter available Zone in the Flybits Project.  The labels are treated as a logical AND thus each Zone matching the specified filter must have all specified labels. 
     * @returns {Flybits.api.Zones.ZonesConfig} An object that contains a list of Zones matching the specified labels and additional resources.
     */
    getAllByLabels: function(labels) {
      if (!labels) {
        const newValidation = new Validation();
        newValidation.addError(
          'Missing Parameter',
          'Your labels undefined',
          {
            code: Validation.type.MISSINGARG,
          }
        );

        return Promise.reject(newValidation);
      }

      var url =
      Flybits.cfg.HOST +
      Flybits.cfg.res.ZONESRELEVANTCONTENTS +
      '?labelsFormula=(' + labels +')';

      return fetchZoneContent(url)

    },

    /**
     * @typedef ZoneConfig
     * @memberof Flybits.api.Zones
     * @type Object
     * @property {string[]} scriptURLs List of URLs for JavaScript resources specific to the Flybits project.  These can be used to expand upon available web components and even specific tracking and support libraries.
     * @property {Flybits.Zone} zone A single {@link Flybits.Zone} object.
     */
    /**
     * Retrieves a Flybits Zone containing its respective Modules and relevant curated Content based on specified Zone Reference ID.
     * @memberof Flybits.api.Zones
     * @function getByReferenceId
     * @param {string} referenceId 
     * @returns {Flybits.api.Zones.ZoneConfig} An object that contains the Zone matching the specified Zone Reference ID and additional resources.
     */
    getByReferenceId: function(referenceId) {
      const def = new Deferred();

      if(!referenceId) {
        const newValidation = new Validation();
        newValidation.addError(
          'Missing Parameter',
          'Your referenceId undefined',
          {
            code: Validation.type.MISSINGARG,
          }
        );

        return Promise.reject(newValidation);
      }

      var url =
      Flybits.cfg.HOST +
      Flybits.cfg.res.ZONESRELEVANTCONTENTS +
      '?zoneReferenceID=' + referenceId;

      fetchZoneContent(url).then(function(resultObj){
        def.resolve({
          scriptURLs: resultObj.scriptURLs,
          zone: resultObj.zones? resultObj.zones[0]: null
        })
      }).catch(function(e){
        def.reject(e);
      })

      return def.promise;
    }
  };

  return zones;
})();

/***** Export ******/
/*global Flybits,define*/

if(typeof window === 'object'){
  window.Flybits = Flybits;
  Flybits.initFile = Flybits.initFile.browser;
} else if(typeof exports === 'object' && typeof module !== 'undefined'){
  // eslint-disable-next-line no-unused-vars
  var fs = require('fs');
  // eslint-disable-next-line no-unused-vars
  var Persistence = require('node-persist');
  global.atob = function(str){
    return Buffer.from(str, 'base64').toString();
  };
  global.btoa = function(str){
    return Buffer.from(str).toString('base64');
  };
  Flybits.initFile = Flybits.initFile.server;
  module.exports = Flybits;
} else if(typeof define === 'function' && define.amd){
  define(function(){
    Flybits.init = Flybits.init.server;
    return Flybits;
  });
} else{
  Flybits.init = Flybits.init.server;
  global.Flybits = Flybits;
}

if(typeof __dirname === 'string'){
  Flybits.cfg.store.RESOURCEPATH = __dirname+"/res";
}
Flybits.store.Property.init();
var deferredReady = new Flybits.Deferred();
Flybits.ready = deferredReady.promise;

})();

// @author Justin Lam
// @version 2.18.0:HEAD:5a323ce

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _typeof(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

(function (global, factory) {
  (typeof exports === "undefined" ? "undefined" : _typeof(exports)) === 'object' && typeof module !== 'undefined' ? module.exports = factory() : typeof define === 'function' && define.amd ? define(factory) : (global = global || self, global.LazyLoad = factory());
})(this, function () {
  'use strict';

  var runningOnBrowser = typeof window !== "undefined";
  var isBot = runningOnBrowser && !("onscroll" in window) || typeof navigator !== "undefined" && /(gle|ing|ro)bot|crawl|spider/i.test(navigator.userAgent);
  var supportsIntersectionObserver = runningOnBrowser && "IntersectionObserver" in window;
  var supportsClassList = runningOnBrowser && "classList" in document.createElement("p");
  var defaultSettings = {
    elements_selector: "img",
    container: isBot || runningOnBrowser ? document : null,
    threshold: 300,
    thresholds: null,
    data_src: "src",
    data_srcset: "srcset",
    data_sizes: "sizes",
    data_bg: "bg",
    data_poster: "poster",
    class_loading: "loading",
    class_loaded: "loaded",
    class_error: "error",
    load_delay: 0,
    auto_unobserve: true,
    callback_enter: null,
    callback_exit: null,
    callback_reveal: null,
    callback_loaded: null,
    callback_error: null,
    callback_finish: null,
    use_native: false
  };

  var getInstanceSettings = function getInstanceSettings(customSettings) {
    return _extends({}, defaultSettings, customSettings);
  };
  /* Creates instance and notifies it through the window element */


  var createInstance = function createInstance(classObj, options) {
    var event;
    var eventString = "LazyLoad::Initialized";
    var instance = new classObj(options);

    try {
      // Works in modern browsers
      event = new CustomEvent(eventString, {
        detail: {
          instance: instance
        }
      });
    } catch (err) {
      // Works in Internet Explorer (all versions)
      event = document.createEvent("CustomEvent");
      event.initCustomEvent(eventString, false, false, {
        instance: instance
      });
    }

    window.dispatchEvent(event);
  };
  /* Auto initialization of one or more instances of lazyload, depending on the 
      options passed in (plain object or an array) */


  function autoInitialize(classObj, options) {
    if (!options) {
      return;
    }

    if (!options.length) {
      // Plain object
      createInstance(classObj, options);
    } else {
      // Array of objects
      for (var i = 0, optionsItem; optionsItem = options[i]; i += 1) {
        createInstance(classObj, optionsItem);
      }
    }
  }

  var dataPrefix = "data-";
  var processedDataName = "was-processed";
  var timeoutDataName = "ll-timeout";
  var trueString = "true";

  var getData = function getData(element, attribute) {
    return element.getAttribute(dataPrefix + attribute);
  };

  var setData = function setData(element, attribute, value) {
    var attrName = dataPrefix + attribute;

    if (value === null) {
      element.removeAttribute(attrName);
      return;
    }

    element.setAttribute(attrName, value);
  };

  var resetWasProcessedData = function resetWasProcessedData(element) {
    return setData(element, processedDataName, null);
  };

  var setWasProcessedData = function setWasProcessedData(element) {
    return setData(element, processedDataName, trueString);
  };

  var getWasProcessedData = function getWasProcessedData(element) {
    return getData(element, processedDataName) === trueString;
  };

  var setTimeoutData = function setTimeoutData(element, value) {
    return setData(element, timeoutDataName, value);
  };

  var getTimeoutData = function getTimeoutData(element) {
    return getData(element, timeoutDataName);
  };

  var purgeProcessedElements = function purgeProcessedElements(elements) {
    return elements.filter(function (element) {
      return !getWasProcessedData(element);
    });
  };

  var purgeOneElement = function purgeOneElement(elements, elementToPurge) {
    return elements.filter(function (element) {
      return element !== elementToPurge;
    });
  };

  var safeCallback = function safeCallback(callback, arg1, arg2, arg3) {
    if (!callback) {
      return;
    }

    if (arg3 !== undefined) {
      callback(arg1, arg2, arg3);
      return;
    }

    if (arg2 !== undefined) {
      callback(arg1, arg2);
      return;
    }

    callback(arg1);
  };

  var updateLoadingCount = function updateLoadingCount(instance, plusMinus) {
    instance.loadingCount += plusMinus;

    if (instance._elements.length === 0 && instance.loadingCount === 0) {
      safeCallback(instance._settings.callback_finish, instance);
    }
  };

  var getSourceTags = function getSourceTags(parentTag) {
    var sourceTags = [];

    for (var i = 0, childTag; childTag = parentTag.children[i]; i += 1) {
      if (childTag.tagName === "SOURCE") {
        sourceTags.push(childTag);
      }
    }

    return sourceTags;
  };

  var setAttributeIfValue = function setAttributeIfValue(element, attrName, value) {
    if (!value) {
      return;
    }

    element.setAttribute(attrName, value);
  };

  var setImageAttributes = function setImageAttributes(element, settings) {
    setAttributeIfValue(element, "sizes", getData(element, settings.data_sizes));
    setAttributeIfValue(element, "srcset", getData(element, settings.data_srcset));
    setAttributeIfValue(element, "src", getData(element, settings.data_src));
  };

  var setSourcesImg = function setSourcesImg(element, settings) {
    var parent = element.parentNode;

    if (parent && parent.tagName === "PICTURE") {
      var sourceTags = getSourceTags(parent);
      sourceTags.forEach(function (sourceTag) {
        setImageAttributes(sourceTag, settings);
      });
    }

    setImageAttributes(element, settings);
  };

  var setSourcesIframe = function setSourcesIframe(element, settings) {
    setAttributeIfValue(element, "src", getData(element, settings.data_src));
  };

  var setSourcesVideo = function setSourcesVideo(element, settings) {
    var sourceTags = getSourceTags(element);
    sourceTags.forEach(function (sourceTag) {
      setAttributeIfValue(sourceTag, "src", getData(sourceTag, settings.data_src));
    });
    setAttributeIfValue(element, "poster", getData(element, settings.data_poster));
    setAttributeIfValue(element, "src", getData(element, settings.data_src));
    element.load();
  };

  var setSourcesBgImage = function setSourcesBgImage(element, settings) {
    var srcDataValue = getData(element, settings.data_src);
    var bgDataValue = getData(element, settings.data_bg);

    if (srcDataValue) {
      element.style.backgroundImage = "url(\"".concat(srcDataValue, "\")");
    }

    if (bgDataValue) {
      element.style.backgroundImage = bgDataValue;
    }
  };

  var setSourcesFunctions = {
    IMG: setSourcesImg,
    IFRAME: setSourcesIframe,
    VIDEO: setSourcesVideo
  };

  var setSources = function setSources(element, instance) {
    var settings = instance._settings;
    var tagName = element.tagName;
    var setSourcesFunction = setSourcesFunctions[tagName];

    if (setSourcesFunction) {
      setSourcesFunction(element, settings);
      updateLoadingCount(instance, 1);
      instance._elements = purgeOneElement(instance._elements, element);
      return;
    }

    setSourcesBgImage(element, settings);
  };

  var addClass = function addClass(element, className) {
    if (supportsClassList) {
      element.classList.add(className);
      return;
    }

    element.className += (element.className ? " " : "") + className;
  };

  var removeClass = function removeClass(element, className) {
    if (supportsClassList) {
      element.classList.remove(className);
      return;
    }

    element.className = element.className.replace(new RegExp("(^|\\s+)" + className + "(\\s+|$)"), " ").replace(/^\s+/, "").replace(/\s+$/, "");
  };

  var genericLoadEventName = "load";
  var mediaLoadEventName = "loadeddata";
  var errorEventName = "error";

  var addEventListener = function addEventListener(element, eventName, handler) {
    element.addEventListener(eventName, handler);
  };

  var removeEventListener = function removeEventListener(element, eventName, handler) {
    element.removeEventListener(eventName, handler);
  };

  var addEventListeners = function addEventListeners(element, loadHandler, errorHandler) {
    addEventListener(element, genericLoadEventName, loadHandler);
    addEventListener(element, mediaLoadEventName, loadHandler);
    addEventListener(element, errorEventName, errorHandler);
  };

  var removeEventListeners = function removeEventListeners(element, loadHandler, errorHandler) {
    removeEventListener(element, genericLoadEventName, loadHandler);
    removeEventListener(element, mediaLoadEventName, loadHandler);
    removeEventListener(element, errorEventName, errorHandler);
  };

  var eventHandler = function eventHandler(event, success, instance) {
    var settings = instance._settings;
    var className = success ? settings.class_loaded : settings.class_error;
    var callback = success ? settings.callback_loaded : settings.callback_error;
    var element = event.target;
    removeClass(element, settings.class_loading);
    addClass(element, className);
    safeCallback(callback, element, instance);
    updateLoadingCount(instance, -1);
  };

  var addOneShotEventListeners = function addOneShotEventListeners(element, instance) {
    var loadHandler = function loadHandler(event) {
      eventHandler(event, true, instance);
      removeEventListeners(element, loadHandler, errorHandler);
    };

    var errorHandler = function errorHandler(event) {
      eventHandler(event, false, instance);
      removeEventListeners(element, loadHandler, errorHandler);
    };

    addEventListeners(element, loadHandler, errorHandler);
  };

  var managedTags = ["IMG", "IFRAME", "VIDEO"];

  var onEnter = function onEnter(element, entry, instance) {
    var settings = instance._settings;
    safeCallback(settings.callback_enter, element, entry, instance);

    if (!settings.load_delay) {
      revealAndUnobserve(element, instance);
      return;
    }

    delayLoad(element, instance);
  };

  var revealAndUnobserve = function revealAndUnobserve(element, instance) {
    var observer = instance._observer;
    revealElement(element, instance);

    if (observer && instance._settings.auto_unobserve) {
      observer.unobserve(element);
    }
  };

  var onExit = function onExit(element, entry, instance) {
    var settings = instance._settings;
    safeCallback(settings.callback_exit, element, entry, instance);

    if (!settings.load_delay) {
      return;
    }

    cancelDelayLoad(element);
  };

  var cancelDelayLoad = function cancelDelayLoad(element) {
    var timeoutId = getTimeoutData(element);

    if (!timeoutId) {
      return; // do nothing if timeout doesn't exist
    }

    clearTimeout(timeoutId);
    setTimeoutData(element, null);
  };

  var delayLoad = function delayLoad(element, instance) {
    var loadDelay = instance._settings.load_delay;
    var timeoutId = getTimeoutData(element);

    if (timeoutId) {
      return; // do nothing if timeout already set
    }

    timeoutId = setTimeout(function () {
      revealAndUnobserve(element, instance);
      cancelDelayLoad(element);
    }, loadDelay);
    setTimeoutData(element, timeoutId);
  };

  var revealElement = function revealElement(element, instance, force) {
    var settings = instance._settings;

    if (!force && getWasProcessedData(element)) {
      return; // element has already been processed and force wasn't true
    }

    if (managedTags.indexOf(element.tagName) > -1) {
      addOneShotEventListeners(element, instance);
      addClass(element, settings.class_loading);
    }

    setSources(element, instance);
    setWasProcessedData(element);
    safeCallback(settings.callback_reveal, element, instance);
    safeCallback(settings.callback_set, element, instance);
  };

  var isIntersecting = function isIntersecting(entry) {
    return entry.isIntersecting || entry.intersectionRatio > 0;
  };

  var getObserverSettings = function getObserverSettings(settings) {
    return {
      root: settings.container === document ? null : settings.container,
      rootMargin: settings.thresholds || settings.threshold + "px"
    };
  };

  var setObserver = function setObserver(instance) {
    if (!supportsIntersectionObserver) {
      return false;
    }

    instance._observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        return isIntersecting(entry) ? onEnter(entry.target, entry, instance) : onExit(entry.target, entry, instance);
      });
    }, getObserverSettings(instance._settings));
    return true;
  };

  var nativeLazyTags = ["IMG", "IFRAME"];

  var shouldUseNative = function shouldUseNative(settings) {
    return settings.use_native && "loading" in HTMLImageElement.prototype;
  };

  var loadAllNative = function loadAllNative(instance) {
    instance._elements.forEach(function (element) {
      if (nativeLazyTags.indexOf(element.tagName) === -1) {
        return;
      }

      element.setAttribute("loading", "lazy");
      revealElement(element, instance);
    });
  };

  var nodeSetToArray = function nodeSetToArray(nodeSet) {
    return Array.prototype.slice.call(nodeSet);
  };

  var queryElements = function queryElements(settings) {
    return settings.container.querySelectorAll(settings.elements_selector);
  };

  var getElements = function getElements(elements, settings) {
    return purgeProcessedElements(nodeSetToArray(elements || queryElements(settings)));
  };

  var retryLazyLoad = function retryLazyLoad(instance) {
    var settings = instance._settings;
    var errorElements = settings.container.querySelectorAll("." + settings.class_error);
    errorElements.forEach(function (element) {
      removeClass(element, settings.class_error);
      resetWasProcessedData(element);
    });
    instance.update();
  };

  var setOnlineCheck = function setOnlineCheck(instance) {
    if (!runningOnBrowser) {
      return;
    }

    window.addEventListener("online", function (event) {
      retryLazyLoad(instance);
    });
  };

  var LazyLoad = function LazyLoad(customSettings, elements) {
    this._settings = getInstanceSettings(customSettings);
    this.loadingCount = 0;
    setObserver(this);
    this.update(elements);
    setOnlineCheck(this);
  };

  LazyLoad.prototype = {
    update: function update(elements) {
      var _this = this;

      var settings = this._settings;
      this._elements = getElements(elements, settings);

      if (isBot || !this._observer) {
        this.loadAll();
        return;
      }

      if (shouldUseNative(settings)) {
        loadAllNative(this);
        this._elements = getElements(elements, settings);
      }

      this._elements.forEach(function (element) {
        _this._observer.observe(element);
      });
    },
    destroy: function destroy() {
      var _this2 = this;

      if (this._observer) {
        this._elements.forEach(function (element) {
          _this2._observer.unobserve(element);
        });

        this._observer = null;
      }

      this._elements = null;
      this._settings = null;
    },
    load: function load(element, force) {
      revealElement(element, this, force);
    },
    loadAll: function loadAll() {
      var _this3 = this;

      this._elements.forEach(function (element) {
        revealAndUnobserve(element, _this3);
      });
    }
  };
  /* Automatic instances creation if required (useful for async script loading) */

  if (runningOnBrowser) {
    autoInitialize(LazyLoad, window.lazyLoadOptions);
  }

  return LazyLoad;
});
/**
 * @fileoverview
 * - Using the 'QRCode for Javascript library'
 * - Fixed dataset of 'QRCode for Javascript library' for support full-spec.
 * - this library has no dependencies.
 * 
 * @author davidshimjs
 * @see <a href="http://www.d-project.com/" target="_blank">http://www.d-project.com/</a>
 * @see <a href="http://jeromeetienne.github.com/jquery-qrcode/" target="_blank">http://jeromeetienne.github.com/jquery-qrcode/</a>
 */
var QRCode;

(function () {
	//---------------------------------------------------------------------
	// QRCode for JavaScript
	//
	// Copyright (c) 2009 Kazuhiko Arase
	//
	// URL: http://www.d-project.com/
	//
	// Licensed under the MIT license:
	//   http://www.opensource.org/licenses/mit-license.php
	//
	// The word "QR Code" is registered trademark of 
	// DENSO WAVE INCORPORATED
	//   http://www.denso-wave.com/qrcode/faqpatent-e.html
	//
	//---------------------------------------------------------------------
	function QR8bitByte(data) {
		this.mode = QRMode.MODE_8BIT_BYTE;
		this.data = data;
		this.parsedData = [];

		// Added to support UTF-8 Characters
		for (var i = 0, l = this.data.length; i < l; i++) {
			var byteArray = [];
			var code = this.data.charCodeAt(i);

			if (code > 0x10000) {
				byteArray[0] = 0xF0 | ((code & 0x1C0000) >>> 18);
				byteArray[1] = 0x80 | ((code & 0x3F000) >>> 12);
				byteArray[2] = 0x80 | ((code & 0xFC0) >>> 6);
				byteArray[3] = 0x80 | (code & 0x3F);
			} else if (code > 0x800) {
				byteArray[0] = 0xE0 | ((code & 0xF000) >>> 12);
				byteArray[1] = 0x80 | ((code & 0xFC0) >>> 6);
				byteArray[2] = 0x80 | (code & 0x3F);
			} else if (code > 0x80) {
				byteArray[0] = 0xC0 | ((code & 0x7C0) >>> 6);
				byteArray[1] = 0x80 | (code & 0x3F);
			} else {
				byteArray[0] = code;
			}

			this.parsedData.push(byteArray);
		}

		this.parsedData = Array.prototype.concat.apply([], this.parsedData);

		if (this.parsedData.length != this.data.length) {
			this.parsedData.unshift(191);
			this.parsedData.unshift(187);
			this.parsedData.unshift(239);
		}
	}

	QR8bitByte.prototype = {
		getLength: function (buffer) {
			return this.parsedData.length;
		},
		write: function (buffer) {
			for (var i = 0, l = this.parsedData.length; i < l; i++) {
				buffer.put(this.parsedData[i], 8);
			}
		}
	};

	function QRCodeModel(typeNumber, errorCorrectLevel) {
		this.typeNumber = typeNumber;
		this.errorCorrectLevel = errorCorrectLevel;
		this.modules = null;
		this.moduleCount = 0;
		this.dataCache = null;
		this.dataList = [];
	}

	QRCodeModel.prototype={addData:function(data){var newData=new QR8bitByte(data);this.dataList.push(newData);this.dataCache=null;},isDark:function(row,col){if(row<0||this.moduleCount<=row||col<0||this.moduleCount<=col){throw new Error(row+","+col);}
	return this.modules[row][col];},getModuleCount:function(){return this.moduleCount;},make:function(){this.makeImpl(false,this.getBestMaskPattern());},makeImpl:function(test,maskPattern){this.moduleCount=this.typeNumber*4+17;this.modules=new Array(this.moduleCount);for(var row=0;row<this.moduleCount;row++){this.modules[row]=new Array(this.moduleCount);for(var col=0;col<this.moduleCount;col++){this.modules[row][col]=null;}}
	this.setupPositionProbePattern(0,0);this.setupPositionProbePattern(this.moduleCount-7,0);this.setupPositionProbePattern(0,this.moduleCount-7);this.setupPositionAdjustPattern();this.setupTimingPattern();this.setupTypeInfo(test,maskPattern);if(this.typeNumber>=7){this.setupTypeNumber(test);}
	if(this.dataCache==null){this.dataCache=QRCodeModel.createData(this.typeNumber,this.errorCorrectLevel,this.dataList);}
	this.mapData(this.dataCache,maskPattern);},setupPositionProbePattern:function(row,col){for(var r=-1;r<=7;r++){if(row+r<=-1||this.moduleCount<=row+r)continue;for(var c=-1;c<=7;c++){if(col+c<=-1||this.moduleCount<=col+c)continue;if((0<=r&&r<=6&&(c==0||c==6))||(0<=c&&c<=6&&(r==0||r==6))||(2<=r&&r<=4&&2<=c&&c<=4)){this.modules[row+r][col+c]=true;}else{this.modules[row+r][col+c]=false;}}}},getBestMaskPattern:function(){var minLostPoint=0;var pattern=0;for(var i=0;i<8;i++){this.makeImpl(true,i);var lostPoint=QRUtil.getLostPoint(this);if(i==0||minLostPoint>lostPoint){minLostPoint=lostPoint;pattern=i;}}
	return pattern;},createMovieClip:function(target_mc,instance_name,depth){var qr_mc=target_mc.createEmptyMovieClip(instance_name,depth);var cs=1;this.make();for(var row=0;row<this.modules.length;row++){var y=row*cs;for(var col=0;col<this.modules[row].length;col++){var x=col*cs;var dark=this.modules[row][col];if(dark){qr_mc.beginFill(0,100);qr_mc.moveTo(x,y);qr_mc.lineTo(x+cs,y);qr_mc.lineTo(x+cs,y+cs);qr_mc.lineTo(x,y+cs);qr_mc.endFill();}}}
	return qr_mc;},setupTimingPattern:function(){for(var r=8;r<this.moduleCount-8;r++){if(this.modules[r][6]!=null){continue;}
	this.modules[r][6]=(r%2==0);}
	for(var c=8;c<this.moduleCount-8;c++){if(this.modules[6][c]!=null){continue;}
	this.modules[6][c]=(c%2==0);}},setupPositionAdjustPattern:function(){var pos=QRUtil.getPatternPosition(this.typeNumber);for(var i=0;i<pos.length;i++){for(var j=0;j<pos.length;j++){var row=pos[i];var col=pos[j];if(this.modules[row][col]!=null){continue;}
	for(var r=-2;r<=2;r++){for(var c=-2;c<=2;c++){if(r==-2||r==2||c==-2||c==2||(r==0&&c==0)){this.modules[row+r][col+c]=true;}else{this.modules[row+r][col+c]=false;}}}}}},setupTypeNumber:function(test){var bits=QRUtil.getBCHTypeNumber(this.typeNumber);for(var i=0;i<18;i++){var mod=(!test&&((bits>>i)&1)==1);this.modules[Math.floor(i/3)][i%3+this.moduleCount-8-3]=mod;}
	for(var i=0;i<18;i++){var mod=(!test&&((bits>>i)&1)==1);this.modules[i%3+this.moduleCount-8-3][Math.floor(i/3)]=mod;}},setupTypeInfo:function(test,maskPattern){var data=(this.errorCorrectLevel<<3)|maskPattern;var bits=QRUtil.getBCHTypeInfo(data);for(var i=0;i<15;i++){var mod=(!test&&((bits>>i)&1)==1);if(i<6){this.modules[i][8]=mod;}else if(i<8){this.modules[i+1][8]=mod;}else{this.modules[this.moduleCount-15+i][8]=mod;}}
	for(var i=0;i<15;i++){var mod=(!test&&((bits>>i)&1)==1);if(i<8){this.modules[8][this.moduleCount-i-1]=mod;}else if(i<9){this.modules[8][15-i-1+1]=mod;}else{this.modules[8][15-i-1]=mod;}}
	this.modules[this.moduleCount-8][8]=(!test);},mapData:function(data,maskPattern){var inc=-1;var row=this.moduleCount-1;var bitIndex=7;var byteIndex=0;for(var col=this.moduleCount-1;col>0;col-=2){if(col==6)col--;while(true){for(var c=0;c<2;c++){if(this.modules[row][col-c]==null){var dark=false;if(byteIndex<data.length){dark=(((data[byteIndex]>>>bitIndex)&1)==1);}
	var mask=QRUtil.getMask(maskPattern,row,col-c);if(mask){dark=!dark;}
	this.modules[row][col-c]=dark;bitIndex--;if(bitIndex==-1){byteIndex++;bitIndex=7;}}}
	row+=inc;if(row<0||this.moduleCount<=row){row-=inc;inc=-inc;break;}}}}};QRCodeModel.PAD0=0xEC;QRCodeModel.PAD1=0x11;QRCodeModel.createData=function(typeNumber,errorCorrectLevel,dataList){var rsBlocks=QRRSBlock.getRSBlocks(typeNumber,errorCorrectLevel);var buffer=new QRBitBuffer();for(var i=0;i<dataList.length;i++){var data=dataList[i];buffer.put(data.mode,4);buffer.put(data.getLength(),QRUtil.getLengthInBits(data.mode,typeNumber));data.write(buffer);}
	var totalDataCount=0;for(var i=0;i<rsBlocks.length;i++){totalDataCount+=rsBlocks[i].dataCount;}
	if(buffer.getLengthInBits()>totalDataCount*8){throw new Error("code length overflow. ("
	+buffer.getLengthInBits()
	+">"
	+totalDataCount*8
	+")");}
	if(buffer.getLengthInBits()+4<=totalDataCount*8){buffer.put(0,4);}
	while(buffer.getLengthInBits()%8!=0){buffer.putBit(false);}
	while(true){if(buffer.getLengthInBits()>=totalDataCount*8){break;}
	buffer.put(QRCodeModel.PAD0,8);if(buffer.getLengthInBits()>=totalDataCount*8){break;}
	buffer.put(QRCodeModel.PAD1,8);}
	return QRCodeModel.createBytes(buffer,rsBlocks);};QRCodeModel.createBytes=function(buffer,rsBlocks){var offset=0;var maxDcCount=0;var maxEcCount=0;var dcdata=new Array(rsBlocks.length);var ecdata=new Array(rsBlocks.length);for(var r=0;r<rsBlocks.length;r++){var dcCount=rsBlocks[r].dataCount;var ecCount=rsBlocks[r].totalCount-dcCount;maxDcCount=Math.max(maxDcCount,dcCount);maxEcCount=Math.max(maxEcCount,ecCount);dcdata[r]=new Array(dcCount);for(var i=0;i<dcdata[r].length;i++){dcdata[r][i]=0xff&buffer.buffer[i+offset];}
	offset+=dcCount;var rsPoly=QRUtil.getErrorCorrectPolynomial(ecCount);var rawPoly=new QRPolynomial(dcdata[r],rsPoly.getLength()-1);var modPoly=rawPoly.mod(rsPoly);ecdata[r]=new Array(rsPoly.getLength()-1);for(var i=0;i<ecdata[r].length;i++){var modIndex=i+modPoly.getLength()-ecdata[r].length;ecdata[r][i]=(modIndex>=0)?modPoly.get(modIndex):0;}}
	var totalCodeCount=0;for(var i=0;i<rsBlocks.length;i++){totalCodeCount+=rsBlocks[i].totalCount;}
	var data=new Array(totalCodeCount);var index=0;for(var i=0;i<maxDcCount;i++){for(var r=0;r<rsBlocks.length;r++){if(i<dcdata[r].length){data[index++]=dcdata[r][i];}}}
	for(var i=0;i<maxEcCount;i++){for(var r=0;r<rsBlocks.length;r++){if(i<ecdata[r].length){data[index++]=ecdata[r][i];}}}
	return data;};var QRMode={MODE_NUMBER:1<<0,MODE_ALPHA_NUM:1<<1,MODE_8BIT_BYTE:1<<2,MODE_KANJI:1<<3};var QRErrorCorrectLevel={L:1,M:0,Q:3,H:2};var QRMaskPattern={PATTERN000:0,PATTERN001:1,PATTERN010:2,PATTERN011:3,PATTERN100:4,PATTERN101:5,PATTERN110:6,PATTERN111:7};var QRUtil={PATTERN_POSITION_TABLE:[[],[6,18],[6,22],[6,26],[6,30],[6,34],[6,22,38],[6,24,42],[6,26,46],[6,28,50],[6,30,54],[6,32,58],[6,34,62],[6,26,46,66],[6,26,48,70],[6,26,50,74],[6,30,54,78],[6,30,56,82],[6,30,58,86],[6,34,62,90],[6,28,50,72,94],[6,26,50,74,98],[6,30,54,78,102],[6,28,54,80,106],[6,32,58,84,110],[6,30,58,86,114],[6,34,62,90,118],[6,26,50,74,98,122],[6,30,54,78,102,126],[6,26,52,78,104,130],[6,30,56,82,108,134],[6,34,60,86,112,138],[6,30,58,86,114,142],[6,34,62,90,118,146],[6,30,54,78,102,126,150],[6,24,50,76,102,128,154],[6,28,54,80,106,132,158],[6,32,58,84,110,136,162],[6,26,54,82,110,138,166],[6,30,58,86,114,142,170]],G15:(1<<10)|(1<<8)|(1<<5)|(1<<4)|(1<<2)|(1<<1)|(1<<0),G18:(1<<12)|(1<<11)|(1<<10)|(1<<9)|(1<<8)|(1<<5)|(1<<2)|(1<<0),G15_MASK:(1<<14)|(1<<12)|(1<<10)|(1<<4)|(1<<1),getBCHTypeInfo:function(data){var d=data<<10;while(QRUtil.getBCHDigit(d)-QRUtil.getBCHDigit(QRUtil.G15)>=0){d^=(QRUtil.G15<<(QRUtil.getBCHDigit(d)-QRUtil.getBCHDigit(QRUtil.G15)));}
	return((data<<10)|d)^QRUtil.G15_MASK;},getBCHTypeNumber:function(data){var d=data<<12;while(QRUtil.getBCHDigit(d)-QRUtil.getBCHDigit(QRUtil.G18)>=0){d^=(QRUtil.G18<<(QRUtil.getBCHDigit(d)-QRUtil.getBCHDigit(QRUtil.G18)));}
	return(data<<12)|d;},getBCHDigit:function(data){var digit=0;while(data!=0){digit++;data>>>=1;}
	return digit;},getPatternPosition:function(typeNumber){return QRUtil.PATTERN_POSITION_TABLE[typeNumber-1];},getMask:function(maskPattern,i,j){switch(maskPattern){case QRMaskPattern.PATTERN000:return(i+j)%2==0;case QRMaskPattern.PATTERN001:return i%2==0;case QRMaskPattern.PATTERN010:return j%3==0;case QRMaskPattern.PATTERN011:return(i+j)%3==0;case QRMaskPattern.PATTERN100:return(Math.floor(i/2)+Math.floor(j/3))%2==0;case QRMaskPattern.PATTERN101:return(i*j)%2+(i*j)%3==0;case QRMaskPattern.PATTERN110:return((i*j)%2+(i*j)%3)%2==0;case QRMaskPattern.PATTERN111:return((i*j)%3+(i+j)%2)%2==0;default:throw new Error("bad maskPattern:"+maskPattern);}},getErrorCorrectPolynomial:function(errorCorrectLength){var a=new QRPolynomial([1],0);for(var i=0;i<errorCorrectLength;i++){a=a.multiply(new QRPolynomial([1,QRMath.gexp(i)],0));}
	return a;},getLengthInBits:function(mode,type){if(1<=type&&type<10){switch(mode){case QRMode.MODE_NUMBER:return 10;case QRMode.MODE_ALPHA_NUM:return 9;case QRMode.MODE_8BIT_BYTE:return 8;case QRMode.MODE_KANJI:return 8;default:throw new Error("mode:"+mode);}}else if(type<27){switch(mode){case QRMode.MODE_NUMBER:return 12;case QRMode.MODE_ALPHA_NUM:return 11;case QRMode.MODE_8BIT_BYTE:return 16;case QRMode.MODE_KANJI:return 10;default:throw new Error("mode:"+mode);}}else if(type<41){switch(mode){case QRMode.MODE_NUMBER:return 14;case QRMode.MODE_ALPHA_NUM:return 13;case QRMode.MODE_8BIT_BYTE:return 16;case QRMode.MODE_KANJI:return 12;default:throw new Error("mode:"+mode);}}else{throw new Error("type:"+type);}},getLostPoint:function(qrCode){var moduleCount=qrCode.getModuleCount();var lostPoint=0;for(var row=0;row<moduleCount;row++){for(var col=0;col<moduleCount;col++){var sameCount=0;var dark=qrCode.isDark(row,col);for(var r=-1;r<=1;r++){if(row+r<0||moduleCount<=row+r){continue;}
	for(var c=-1;c<=1;c++){if(col+c<0||moduleCount<=col+c){continue;}
	if(r==0&&c==0){continue;}
	if(dark==qrCode.isDark(row+r,col+c)){sameCount++;}}}
	if(sameCount>5){lostPoint+=(3+sameCount-5);}}}
	for(var row=0;row<moduleCount-1;row++){for(var col=0;col<moduleCount-1;col++){var count=0;if(qrCode.isDark(row,col))count++;if(qrCode.isDark(row+1,col))count++;if(qrCode.isDark(row,col+1))count++;if(qrCode.isDark(row+1,col+1))count++;if(count==0||count==4){lostPoint+=3;}}}
	for(var row=0;row<moduleCount;row++){for(var col=0;col<moduleCount-6;col++){if(qrCode.isDark(row,col)&&!qrCode.isDark(row,col+1)&&qrCode.isDark(row,col+2)&&qrCode.isDark(row,col+3)&&qrCode.isDark(row,col+4)&&!qrCode.isDark(row,col+5)&&qrCode.isDark(row,col+6)){lostPoint+=40;}}}
	for(var col=0;col<moduleCount;col++){for(var row=0;row<moduleCount-6;row++){if(qrCode.isDark(row,col)&&!qrCode.isDark(row+1,col)&&qrCode.isDark(row+2,col)&&qrCode.isDark(row+3,col)&&qrCode.isDark(row+4,col)&&!qrCode.isDark(row+5,col)&&qrCode.isDark(row+6,col)){lostPoint+=40;}}}
	var darkCount=0;for(var col=0;col<moduleCount;col++){for(var row=0;row<moduleCount;row++){if(qrCode.isDark(row,col)){darkCount++;}}}
	var ratio=Math.abs(100*darkCount/moduleCount/moduleCount-50)/5;lostPoint+=ratio*10;return lostPoint;}};var QRMath={glog:function(n){if(n<1){throw new Error("glog("+n+")");}
	return QRMath.LOG_TABLE[n];},gexp:function(n){while(n<0){n+=255;}
	while(n>=256){n-=255;}
	return QRMath.EXP_TABLE[n];},EXP_TABLE:new Array(256),LOG_TABLE:new Array(256)};for(var i=0;i<8;i++){QRMath.EXP_TABLE[i]=1<<i;}
	for(var i=8;i<256;i++){QRMath.EXP_TABLE[i]=QRMath.EXP_TABLE[i-4]^QRMath.EXP_TABLE[i-5]^QRMath.EXP_TABLE[i-6]^QRMath.EXP_TABLE[i-8];}
	for(var i=0;i<255;i++){QRMath.LOG_TABLE[QRMath.EXP_TABLE[i]]=i;}
	function QRPolynomial(num,shift){if(num.length==undefined){throw new Error(num.length+"/"+shift);}
	var offset=0;while(offset<num.length&&num[offset]==0){offset++;}
	this.num=new Array(num.length-offset+shift);for(var i=0;i<num.length-offset;i++){this.num[i]=num[i+offset];}}
	QRPolynomial.prototype={get:function(index){return this.num[index];},getLength:function(){return this.num.length;},multiply:function(e){var num=new Array(this.getLength()+e.getLength()-1);for(var i=0;i<this.getLength();i++){for(var j=0;j<e.getLength();j++){num[i+j]^=QRMath.gexp(QRMath.glog(this.get(i))+QRMath.glog(e.get(j)));}}
	return new QRPolynomial(num,0);},mod:function(e){if(this.getLength()-e.getLength()<0){return this;}
	var ratio=QRMath.glog(this.get(0))-QRMath.glog(e.get(0));var num=new Array(this.getLength());for(var i=0;i<this.getLength();i++){num[i]=this.get(i);}
	for(var i=0;i<e.getLength();i++){num[i]^=QRMath.gexp(QRMath.glog(e.get(i))+ratio);}
	return new QRPolynomial(num,0).mod(e);}};function QRRSBlock(totalCount,dataCount){this.totalCount=totalCount;this.dataCount=dataCount;}
	QRRSBlock.RS_BLOCK_TABLE=[[1,26,19],[1,26,16],[1,26,13],[1,26,9],[1,44,34],[1,44,28],[1,44,22],[1,44,16],[1,70,55],[1,70,44],[2,35,17],[2,35,13],[1,100,80],[2,50,32],[2,50,24],[4,25,9],[1,134,108],[2,67,43],[2,33,15,2,34,16],[2,33,11,2,34,12],[2,86,68],[4,43,27],[4,43,19],[4,43,15],[2,98,78],[4,49,31],[2,32,14,4,33,15],[4,39,13,1,40,14],[2,121,97],[2,60,38,2,61,39],[4,40,18,2,41,19],[4,40,14,2,41,15],[2,146,116],[3,58,36,2,59,37],[4,36,16,4,37,17],[4,36,12,4,37,13],[2,86,68,2,87,69],[4,69,43,1,70,44],[6,43,19,2,44,20],[6,43,15,2,44,16],[4,101,81],[1,80,50,4,81,51],[4,50,22,4,51,23],[3,36,12,8,37,13],[2,116,92,2,117,93],[6,58,36,2,59,37],[4,46,20,6,47,21],[7,42,14,4,43,15],[4,133,107],[8,59,37,1,60,38],[8,44,20,4,45,21],[12,33,11,4,34,12],[3,145,115,1,146,116],[4,64,40,5,65,41],[11,36,16,5,37,17],[11,36,12,5,37,13],[5,109,87,1,110,88],[5,65,41,5,66,42],[5,54,24,7,55,25],[11,36,12],[5,122,98,1,123,99],[7,73,45,3,74,46],[15,43,19,2,44,20],[3,45,15,13,46,16],[1,135,107,5,136,108],[10,74,46,1,75,47],[1,50,22,15,51,23],[2,42,14,17,43,15],[5,150,120,1,151,121],[9,69,43,4,70,44],[17,50,22,1,51,23],[2,42,14,19,43,15],[3,141,113,4,142,114],[3,70,44,11,71,45],[17,47,21,4,48,22],[9,39,13,16,40,14],[3,135,107,5,136,108],[3,67,41,13,68,42],[15,54,24,5,55,25],[15,43,15,10,44,16],[4,144,116,4,145,117],[17,68,42],[17,50,22,6,51,23],[19,46,16,6,47,17],[2,139,111,7,140,112],[17,74,46],[7,54,24,16,55,25],[34,37,13],[4,151,121,5,152,122],[4,75,47,14,76,48],[11,54,24,14,55,25],[16,45,15,14,46,16],[6,147,117,4,148,118],[6,73,45,14,74,46],[11,54,24,16,55,25],[30,46,16,2,47,17],[8,132,106,4,133,107],[8,75,47,13,76,48],[7,54,24,22,55,25],[22,45,15,13,46,16],[10,142,114,2,143,115],[19,74,46,4,75,47],[28,50,22,6,51,23],[33,46,16,4,47,17],[8,152,122,4,153,123],[22,73,45,3,74,46],[8,53,23,26,54,24],[12,45,15,28,46,16],[3,147,117,10,148,118],[3,73,45,23,74,46],[4,54,24,31,55,25],[11,45,15,31,46,16],[7,146,116,7,147,117],[21,73,45,7,74,46],[1,53,23,37,54,24],[19,45,15,26,46,16],[5,145,115,10,146,116],[19,75,47,10,76,48],[15,54,24,25,55,25],[23,45,15,25,46,16],[13,145,115,3,146,116],[2,74,46,29,75,47],[42,54,24,1,55,25],[23,45,15,28,46,16],[17,145,115],[10,74,46,23,75,47],[10,54,24,35,55,25],[19,45,15,35,46,16],[17,145,115,1,146,116],[14,74,46,21,75,47],[29,54,24,19,55,25],[11,45,15,46,46,16],[13,145,115,6,146,116],[14,74,46,23,75,47],[44,54,24,7,55,25],[59,46,16,1,47,17],[12,151,121,7,152,122],[12,75,47,26,76,48],[39,54,24,14,55,25],[22,45,15,41,46,16],[6,151,121,14,152,122],[6,75,47,34,76,48],[46,54,24,10,55,25],[2,45,15,64,46,16],[17,152,122,4,153,123],[29,74,46,14,75,47],[49,54,24,10,55,25],[24,45,15,46,46,16],[4,152,122,18,153,123],[13,74,46,32,75,47],[48,54,24,14,55,25],[42,45,15,32,46,16],[20,147,117,4,148,118],[40,75,47,7,76,48],[43,54,24,22,55,25],[10,45,15,67,46,16],[19,148,118,6,149,119],[18,75,47,31,76,48],[34,54,24,34,55,25],[20,45,15,61,46,16]];QRRSBlock.getRSBlocks=function(typeNumber,errorCorrectLevel){var rsBlock=QRRSBlock.getRsBlockTable(typeNumber,errorCorrectLevel);if(rsBlock==undefined){throw new Error("bad rs block @ typeNumber:"+typeNumber+"/errorCorrectLevel:"+errorCorrectLevel);}
	var length=rsBlock.length/3;var list=[];for(var i=0;i<length;i++){var count=rsBlock[i*3+0];var totalCount=rsBlock[i*3+1];var dataCount=rsBlock[i*3+2];for(var j=0;j<count;j++){list.push(new QRRSBlock(totalCount,dataCount));}}
	return list;};QRRSBlock.getRsBlockTable=function(typeNumber,errorCorrectLevel){switch(errorCorrectLevel){case QRErrorCorrectLevel.L:return QRRSBlock.RS_BLOCK_TABLE[(typeNumber-1)*4+0];case QRErrorCorrectLevel.M:return QRRSBlock.RS_BLOCK_TABLE[(typeNumber-1)*4+1];case QRErrorCorrectLevel.Q:return QRRSBlock.RS_BLOCK_TABLE[(typeNumber-1)*4+2];case QRErrorCorrectLevel.H:return QRRSBlock.RS_BLOCK_TABLE[(typeNumber-1)*4+3];default:return undefined;}};function QRBitBuffer(){this.buffer=[];this.length=0;}
	QRBitBuffer.prototype={get:function(index){var bufIndex=Math.floor(index/8);return((this.buffer[bufIndex]>>>(7-index%8))&1)==1;},put:function(num,length){for(var i=0;i<length;i++){this.putBit(((num>>>(length-i-1))&1)==1);}},getLengthInBits:function(){return this.length;},putBit:function(bit){var bufIndex=Math.floor(this.length/8);if(this.buffer.length<=bufIndex){this.buffer.push(0);}
	if(bit){this.buffer[bufIndex]|=(0x80>>>(this.length%8));}
	this.length++;}};var QRCodeLimitLength=[[17,14,11,7],[32,26,20,14],[53,42,32,24],[78,62,46,34],[106,84,60,44],[134,106,74,58],[154,122,86,64],[192,152,108,84],[230,180,130,98],[271,213,151,119],[321,251,177,137],[367,287,203,155],[425,331,241,177],[458,362,258,194],[520,412,292,220],[586,450,322,250],[644,504,364,280],[718,560,394,310],[792,624,442,338],[858,666,482,382],[929,711,509,403],[1003,779,565,439],[1091,857,611,461],[1171,911,661,511],[1273,997,715,535],[1367,1059,751,593],[1465,1125,805,625],[1528,1190,868,658],[1628,1264,908,698],[1732,1370,982,742],[1840,1452,1030,790],[1952,1538,1112,842],[2068,1628,1168,898],[2188,1722,1228,958],[2303,1809,1283,983],[2431,1911,1351,1051],[2563,1989,1423,1093],[2699,2099,1499,1139],[2809,2213,1579,1219],[2953,2331,1663,1273]];
	
	function _isSupportCanvas() {
		return typeof CanvasRenderingContext2D != "undefined";
	}
	
	// android 2.x doesn't support Data-URI spec
	function _getAndroid() {
		var android = false;
		var sAgent = navigator.userAgent;
		
		if (/android/i.test(sAgent)) { // android
			android = true;
			var aMat = sAgent.toString().match(/android ([0-9]\.[0-9])/i);
			
			if (aMat && aMat[1]) {
				android = parseFloat(aMat[1]);
			}
		}
		
		return android;
	}
	
	var svgDrawer = (function() {

		var Drawing = function (el, htOption) {
			this._el = el;
			this._htOption = htOption;
		};

		Drawing.prototype.draw = function (oQRCode) {
			var _htOption = this._htOption;
			var _el = this._el;
			var nCount = oQRCode.getModuleCount();
			var nWidth = Math.floor(_htOption.width / nCount);
			var nHeight = Math.floor(_htOption.height / nCount);

			this.clear();

			function makeSVG(tag, attrs) {
				var el = document.createElementNS('http://www.w3.org/2000/svg', tag);
				for (var k in attrs)
					if (attrs.hasOwnProperty(k)) el.setAttribute(k, attrs[k]);
				return el;
			}

			var svg = makeSVG("svg" , {'viewBox': '0 0 ' + String(nCount) + " " + String(nCount), 'width': '100%', 'height': '100%', 'fill': _htOption.colorLight});
			svg.setAttributeNS("http://www.w3.org/2000/xmlns/", "xmlns:xlink", "http://www.w3.org/1999/xlink");
			_el.appendChild(svg);

			svg.appendChild(makeSVG("rect", {"fill": _htOption.colorLight, "width": "100%", "height": "100%"}));
			svg.appendChild(makeSVG("rect", {"fill": _htOption.colorDark, "width": "1", "height": "1", "id": "template"}));

			for (var row = 0; row < nCount; row++) {
				for (var col = 0; col < nCount; col++) {
					if (oQRCode.isDark(row, col)) {
						var child = makeSVG("use", {"x": String(col), "y": String(row)});
						child.setAttributeNS("http://www.w3.org/1999/xlink", "href", "#template")
						svg.appendChild(child);
					}
				}
			}
		};
		Drawing.prototype.clear = function () {
			while (this._el.hasChildNodes())
				this._el.removeChild(this._el.lastChild);
		};
		return Drawing;
	})();

	var useSVG = document.documentElement.tagName.toLowerCase() === "svg";

	// Drawing in DOM by using Table tag
	var Drawing = useSVG ? svgDrawer : !_isSupportCanvas() ? (function () {
		var Drawing = function (el, htOption) {
			this._el = el;
			this._htOption = htOption;
		};
			
		/**
		 * Draw the QRCode
		 * 
		 * @param {QRCode} oQRCode
		 */
		Drawing.prototype.draw = function (oQRCode) {
            var _htOption = this._htOption;
            var _el = this._el;
			var nCount = oQRCode.getModuleCount();
			var nWidth = Math.floor(_htOption.width / nCount);
			var nHeight = Math.floor(_htOption.height / nCount);
			var aHTML = ['<table style="border:0;border-collapse:collapse;">'];
			
			for (var row = 0; row < nCount; row++) {
				aHTML.push('<tr>');
				
				for (var col = 0; col < nCount; col++) {
					aHTML.push('<td style="border:0;border-collapse:collapse;padding:0;margin:0;width:' + nWidth + 'px;height:' + nHeight + 'px;background-color:' + (oQRCode.isDark(row, col) ? _htOption.colorDark : _htOption.colorLight) + ';"></td>');
				}
				
				aHTML.push('</tr>');
			}
			
			aHTML.push('</table>');
			_el.innerHTML = aHTML.join('');
			
			// Fix the margin values as real size.
			var elTable = _el.childNodes[0];
			var nLeftMarginTable = (_htOption.width - elTable.offsetWidth) / 2;
			var nTopMarginTable = (_htOption.height - elTable.offsetHeight) / 2;
			
			if (nLeftMarginTable > 0 && nTopMarginTable > 0) {
				elTable.style.margin = nTopMarginTable + "px " + nLeftMarginTable + "px";	
			}
		};
		
		/**
		 * Clear the QRCode
		 */
		Drawing.prototype.clear = function () {
			this._el.innerHTML = '';
		};
		
		return Drawing;
	})() : (function () { // Drawing in Canvas
		function _onMakeImage() {
			this._elImage.src = this._elCanvas.toDataURL("image/png");
			this._elImage.style.display = "block";
			this._elCanvas.style.display = "none";			
		}
		
		// Android 2.1 bug workaround
		// http://code.google.com/p/android/issues/detail?id=5141
		if (this._android && this._android <= 2.1) {
	    	var factor = 1 / window.devicePixelRatio;
	        var drawImage = CanvasRenderingContext2D.prototype.drawImage; 
	    	CanvasRenderingContext2D.prototype.drawImage = function (image, sx, sy, sw, sh, dx, dy, dw, dh) {
	    		if (("nodeName" in image) && /img/i.test(image.nodeName)) {
		        	for (var i = arguments.length - 1; i >= 1; i--) {
		            	arguments[i] = arguments[i] * factor;
		        	}
	    		} else if (typeof dw == "undefined") {
	    			arguments[1] *= factor;
	    			arguments[2] *= factor;
	    			arguments[3] *= factor;
	    			arguments[4] *= factor;
	    		}
	    		
	        	drawImage.apply(this, arguments); 
	    	};
		}
		
		/**
		 * Check whether the user's browser supports Data URI or not
		 * 
		 * @private
		 * @param {Function} fSuccess Occurs if it supports Data URI
		 * @param {Function} fFail Occurs if it doesn't support Data URI
		 */
		function _safeSetDataURI(fSuccess, fFail) {
            var self = this;
            self._fFail = fFail;
            self._fSuccess = fSuccess;

            // Check it just once
            if (self._bSupportDataURI === null) {
                var el = document.createElement("img");
                var fOnError = function() {
                    self._bSupportDataURI = false;

                    if (self._fFail) {
                        self._fFail.call(self);
                    }
                };
                var fOnSuccess = function() {
                    self._bSupportDataURI = true;

                    if (self._fSuccess) {
                        self._fSuccess.call(self);
                    }
                };

                el.onabort = fOnError;
                el.onerror = fOnError;
                el.onload = fOnSuccess;
                el.src = "data:image/gif;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFCAYAAACNbyblAAAAHElEQVQI12P4//8/w38GIAXDIBKE0DHxgljNBAAO9TXL0Y4OHwAAAABJRU5ErkJggg=="; // the Image contains 1px data.
                return;
            } else if (self._bSupportDataURI === true && self._fSuccess) {
                self._fSuccess.call(self);
            } else if (self._bSupportDataURI === false && self._fFail) {
                self._fFail.call(self);
            }
		};
		
		/**
		 * Drawing QRCode by using canvas
		 * 
		 * @constructor
		 * @param {HTMLElement} el
		 * @param {Object} htOption QRCode Options 
		 */
		var Drawing = function (el, htOption) {
    		this._bIsPainted = false;
    		this._android = _getAndroid();
		
			this._htOption = htOption;
			this._elCanvas = document.createElement("canvas");
			this._elCanvas.width = htOption.width;
			this._elCanvas.height = htOption.height;
			el.appendChild(this._elCanvas);
			this._el = el;
			this._oContext = this._elCanvas.getContext("2d");
			this._bIsPainted = false;
			this._elImage = document.createElement("img");
			this._elImage.alt = "Scan me!";
			this._elImage.style.display = "none";
			this._el.appendChild(this._elImage);
			this._bSupportDataURI = null;
		};
			
		/**
		 * Draw the QRCode
		 * 
		 * @param {QRCode} oQRCode 
		 */
		Drawing.prototype.draw = function (oQRCode) {
            var _elImage = this._elImage;
            var _oContext = this._oContext;
            var _htOption = this._htOption;
            
			var nCount = oQRCode.getModuleCount();
			var nWidth = _htOption.width / nCount;
			var nHeight = _htOption.height / nCount;
			var nRoundedWidth = Math.round(nWidth);
			var nRoundedHeight = Math.round(nHeight);

			_elImage.style.display = "none";
			this.clear();
			
			for (var row = 0; row < nCount; row++) {
				for (var col = 0; col < nCount; col++) {
					var bIsDark = oQRCode.isDark(row, col);
					var nLeft = col * nWidth;
					var nTop = row * nHeight;
					_oContext.strokeStyle = bIsDark ? _htOption.colorDark : _htOption.colorLight;
					_oContext.lineWidth = 1;
					_oContext.fillStyle = bIsDark ? _htOption.colorDark : _htOption.colorLight;					
					_oContext.fillRect(nLeft, nTop, nWidth, nHeight);
					
					// 안티 앨리어싱 방지 처리
					_oContext.strokeRect(
						Math.floor(nLeft) + 0.5,
						Math.floor(nTop) + 0.5,
						nRoundedWidth,
						nRoundedHeight
					);
					
					_oContext.strokeRect(
						Math.ceil(nLeft) - 0.5,
						Math.ceil(nTop) - 0.5,
						nRoundedWidth,
						nRoundedHeight
					);
				}
			}
			
			this._bIsPainted = true;
		};
			
		/**
		 * Make the image from Canvas if the browser supports Data URI.
		 */
		Drawing.prototype.makeImage = function () {
			if (this._bIsPainted) {
				_safeSetDataURI.call(this, _onMakeImage);
			}
		};
			
		/**
		 * Return whether the QRCode is painted or not
		 * 
		 * @return {Boolean}
		 */
		Drawing.prototype.isPainted = function () {
			return this._bIsPainted;
		};
		
		/**
		 * Clear the QRCode
		 */
		Drawing.prototype.clear = function () {
			this._oContext.clearRect(0, 0, this._elCanvas.width, this._elCanvas.height);
			this._bIsPainted = false;
		};
		
		/**
		 * @private
		 * @param {Number} nNumber
		 */
		Drawing.prototype.round = function (nNumber) {
			if (!nNumber) {
				return nNumber;
			}
			
			return Math.floor(nNumber * 1000) / 1000;
		};
		
		return Drawing;
	})();
	
	/**
	 * Get the type by string length
	 * 
	 * @private
	 * @param {String} sText
	 * @param {Number} nCorrectLevel
	 * @return {Number} type
	 */
	function _getTypeNumber(sText, nCorrectLevel) {			
		var nType = 1;
		var length = _getUTF8Length(sText);
		
		for (var i = 0, len = QRCodeLimitLength.length; i <= len; i++) {
			var nLimit = 0;
			
			switch (nCorrectLevel) {
				case QRErrorCorrectLevel.L :
					nLimit = QRCodeLimitLength[i][0];
					break;
				case QRErrorCorrectLevel.M :
					nLimit = QRCodeLimitLength[i][1];
					break;
				case QRErrorCorrectLevel.Q :
					nLimit = QRCodeLimitLength[i][2];
					break;
				case QRErrorCorrectLevel.H :
					nLimit = QRCodeLimitLength[i][3];
					break;
			}
			
			if (length <= nLimit) {
				break;
			} else {
				nType++;
			}
		}
		
		if (nType > QRCodeLimitLength.length) {
			throw new Error("Too long data");
		}
		
		return nType;
	}

	function _getUTF8Length(sText) {
		var replacedText = encodeURI(sText).toString().replace(/\%[0-9a-fA-F]{2}/g, 'a');
		return replacedText.length + (replacedText.length != sText ? 3 : 0);
	}
	
	/**
	 * @class QRCode
	 * @constructor
	 * @example 
	 * new QRCode(document.getElementById("test"), "http://jindo.dev.naver.com/collie");
	 *
	 * @example
	 * var oQRCode = new QRCode("test", {
	 *    text : "http://naver.com",
	 *    width : 128,
	 *    height : 128
	 * });
	 * 
	 * oQRCode.clear(); // Clear the QRCode.
	 * oQRCode.makeCode("http://map.naver.com"); // Re-create the QRCode.
	 *
	 * @param {HTMLElement|String} el target element or 'id' attribute of element.
	 * @param {Object|String} vOption
	 * @param {String} vOption.text QRCode link data
	 * @param {Number} [vOption.width=256]
	 * @param {Number} [vOption.height=256]
	 * @param {String} [vOption.colorDark="#000000"]
	 * @param {String} [vOption.colorLight="#ffffff"]
	 * @param {QRCode.CorrectLevel} [vOption.correctLevel=QRCode.CorrectLevel.H] [L|M|Q|H] 
	 */
	QRCode = function (el, vOption) {
		this._htOption = {
			width : 256, 
			height : 256,
			typeNumber : 4,
			colorDark : "#000000",
			colorLight : "#ffffff",
			correctLevel : QRErrorCorrectLevel.H
		};
		
		if (typeof vOption === 'string') {
			vOption	= {
				text : vOption
			};
		}
		
		// Overwrites options
		if (vOption) {
			for (var i in vOption) {
				this._htOption[i] = vOption[i];
			}
		}
		
		if (typeof el == "string") {
			el = document.getElementById(el);
		}

		if (this._htOption.useSVG) {
			Drawing = svgDrawer;
		}
		
		this._android = _getAndroid();
		this._el = el;
		this._oQRCode = null;
		this._oDrawing = new Drawing(this._el, this._htOption);
		
		if (this._htOption.text) {
			this.makeCode(this._htOption.text);	
		}
	};
	
	/**
	 * Make the QRCode
	 * 
	 * @param {String} sText link data
	 */
	QRCode.prototype.makeCode = function (sText) {
		this._oQRCode = new QRCodeModel(_getTypeNumber(sText, this._htOption.correctLevel), this._htOption.correctLevel);
		this._oQRCode.addData(sText);
		this._oQRCode.make();
		this._el.title = sText;
		this._oDrawing.draw(this._oQRCode);			
		this.makeImage();
	};
	
	/**
	 * Make the Image from Canvas element
	 * - It occurs automatically
	 * - Android below 3 doesn't support Data-URI spec.
	 * 
	 * @private
	 */
	QRCode.prototype.makeImage = function () {
		if (typeof this._oDrawing.makeImage == "function" && (!this._android || this._android >= 3)) {
			this._oDrawing.makeImage();
		}
	};
	
	/**
	 * Clear the QRCode
	 */
	QRCode.prototype.clear = function () {
		this._oDrawing.clear();
	};
	
	/**
	 * @name QRCode.CorrectLevel
	 */
	QRCode.CorrectLevel = QRErrorCorrectLevel;
})();

// @version 1.0.1
// @git main:ddfcd56
;(function(window, undefined){
  /**
   * ES6 Promise object.
   * @external Promise
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise|Promise API}
   * @see {@link http://www.ecma-international.org/ecma-262/6.0/#sec-promise-objects|Promise spec}
   */


  var dec2hex = function(dec){
    return ('0' + dec.toString(16)).substr(-2);
  };

  /**
   * This is the root namespace for the IAMFlybits library.  If included in a browser environment it will be accessible from the `window` object.
   * @namespace IAMFlybits
   */
  var IAMFlybits = {
    /**
     * 
     * @memberof IAMFlybits
     * @returns {string} Cryptographically random `string`
     */
    getRandomStr: function(){
      var array = new Uint32Array(56/2);
      window.crypto.getRandomValues(array);
      return Array.from(array, dec2hex).join('');
    },
    /**
     * 
     * @memberof IAMFlybits
     * @param {string} str Plaintext string to be hashed
     * @returns {external:Promise<string, Error>} Promise that will resolve with
     * an ArrayBuffer.
     */
    sha256: function(str){
      var encoder = new TextEncoder();
      var data = encoder.encode(str);
      return window.crypto.subtle.digest('SHA-256', data);
    },
    htmlEncode:function(value){
      /*global encodeURIComponent*/
      return encodeURIComponent(value);
    },
    base64urlencode: function(inputStr){
      var str = "";
      var bytes = new Uint8Array(inputStr);
      var len = bytes.byteLength;
      for (var i = 0; i < len; i++) {
        str += String.fromCharCode(bytes[i]);
      }
      return btoa(str)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
    },
    getURLParam:function(name) {
      name = name.replace(/[\[]/, "\\\[").replace(/[\]]/, "\\\]");
      var regexS = "[\\?&]" + name + "=([^&#]*)";
      var regex = new RegExp(regexS);
      var results = regex.exec(window.location.href);
      if (results === null)
        return "";
      else
        return results[1];
    },
    toURLParams: function(obj){
      var keys = Object.keys(obj);
      var keyLength = keys.length;
      var str = "";
      while(keyLength--){
        var key = keys[keyLength];
        if(str !== ""){
          str += "&";
        }
        str += key + "=" + encodeURIComponent(obj[key]);
      }

      return str;
    },
    /**
     * This namespace contains libraries and clients for OAuth2.0 with PKCE workflows.
     * @memberof IAMFlybits
     * @namespace oauth2PKCE
     */
    oauth2PKCE: {
      CODESTOREKEY: 'flbcdverify',
      generateCodeVerifier: function(){
        return IAMFlybits.getRandomStr();
      },
      generateCodeChallenge: function(codeVerifier){
        return new Promise(function(resolve, reject){
          IAMFlybits.sha256(codeVerifier).then(function(hash){
            resolve(IAMFlybits.base64urlencode(hash));
          }).catch(function(e){
            reject(e);
          });
        });
      },
      store: {
        getCodeVerifier: function(){
          return window.sessionStorage.getItem(IAMFlybits.oauth2PKCE.CODESTOREKEY);
        },
        clearCodeVerifier: function(){
          window.sessionStorage.removeItem(IAMFlybits.oauth2PKCE.CODESTOREKEY);
        },
        setCodeVerifier: function(code){
          window.sessionStorage.setItem(IAMFlybits.oauth2PKCE.CODESTOREKEY, code);
        }
      },

    }
  };

  /**
   * @classdesc OAuth2.0 with PKCE client to be used to abstract request generation and SP access token retrieval.
   * @class Client
   * @memberof IAMFlybits.oauth2PKCE
   * @param {IAMFlybits.oauth2PKCE.Client.InitializationObject} opts Initialization configuration.
   */
  IAMFlybits.oauth2PKCE.Client = (function(){
    /**
     * @typedef InitializationObject
     * @memberof IAMFlybits.oauth2PKCE.Client
     * @property {string} projectID Flybits tenant ID.
     * @property {string} clientID Flybits fully qualified tenant name (eg. awesome.demo.flybits.com)
     * @property {string} appCallbackURL Redirection endpoint of your application that will be receiving the redirection result from the Auth Broker that originated from the IdP.
     * @property {string} [idpHost=https://api.controltower.flybits.com] Host URL of the IdP (Identity Provider)
     * @property {string} [spHost=https://api.demo.flybits.com] Host URL for the SP (Service Provider). In our cases this will be the Flybits core deployment API host.
     * @property {string} [spAuth=/sso/auth/oidc] SP access token request route.
     * @property {string} [authzReqURL=/auth/request] IdP authorization request route.
     * @property {string} [authzTokenURL=/auth/token] IdP id_token retrieval based on authorization request code
     * @property {string} [authBrokerURL=https://appauth.solutions.flybits.com] Authorization broker host URL.
     * @property {string} [authBrokerCallback=/auth-callback] Authorization broker route for redirection callback.
     * @property {string} [authBrokerValidate=/api/validate/app] Authorization broker route for app validation checks (not used).
     * @property {string} [idTokenProvider=flybits] ID token provider name used as one factor of token validation at the Flybits core SP.
     */

    /**
     * @typedef AccessResponse
     * @memberof IAMFlybits.oauth2PKCE.Client
     * @property {string} projectID Flybits tenant ID.
     * @property {string} deviceID Flybits device ID of currently authorized user.
     * @property {string} email Email of authorized user
     * @property {string} accessToken Flybits tenant specific access token (JWT).
     * @property {string} level Flybits tenant authorized permission level.
     */

    function OAuthClient(opts){
      opts = opts || {};

      this.IDPHOST = opts.idpHost || 'https://api.controltower.flybits.com';
      this.SPHOST = opts.spHost || 'https://api.demo.flybits.com';
      this.SPAUTH = opts.spAuth || '/sso/auth/oidc';
      this.AUTHZREQ = opts.authzReqURL || '/auth/request';
      this.AUTHZTOKEN = opts.authzTokenURL || '/auth/token';
      this.AUTHBROKER = opts.authBrokerURL || 'https://appauth.solutions-us.flybits.com'
      this.AUTHBROKERCALLBACK = opts.authBrokerCallback || '/auth-callback';
      this.AUTHBROKERVALIDATE = opts.authBrokerValidate || '/api/validate/app';
      this.IDTOKENPROVIDER = opts.idTokenProvider || 'flybits';

      // for now must have a `fetch` method
      this.RequestClient = opts.RequestClient || Flybits.util.Api;

      this.appCallbackURL = opts.appCallbackURL;
      this.clientID = opts.clientID;
      this.projectID = opts.projectID;

      if(this.projectID){
        this.redirectURL = this.AUTHBROKER + this.AUTHBROKERCALLBACK + '/' + this.projectID;
      }
    };

    OAuthClient.prototype = {
      /**
       * Generates and invokes a Flybits tenant authorization request against the Control Tower IdP. Request will cause a browser redirect.
       * @memberof IAMFlybits.oauth2PKCE.Client
       * @function
       * @instance
       * @returns {external:Promise<undefined, Error>} Promise that will resolve to indicate completion. However your application may not be able to continue execution as this function will perform a browser redirection.
       */
      request: function(){
        var client = this;
        IAMFlybits.oauth2PKCE.store.clearCodeVerifier();
        var codeVerifier = IAMFlybits.oauth2PKCE.generateCodeVerifier();
        IAMFlybits.oauth2PKCE.store.setCodeVerifier(codeVerifier);
        var reqParams = {
          scope: 'openid',
          response_type: 'code',
          client_id: this.clientID,
          code_challenge_method: 'S256',
          code_challenge: '',
          redirect_uri: this.redirectURL,
          state: JSON.stringify({
            origin: this.appCallbackURL
          })
        };

        return new Promise(function(resolve, reject){
          IAMFlybits.oauth2PKCE.generateCodeChallenge(codeVerifier).then(function(challenge){
            reqParams.code_challenge = challenge;
            var idpAuthzRequest = client.IDPHOST + client.AUTHZREQ + '?' +
                                  IAMFlybits.toURLParams(reqParams);

            window.location.replace(idpAuthzRequest);
            resolve();
          }).catch(function(e){
            reject(e);
          });
        });
      },
      /**
       * Handle IdP callback by ingesting response parameters and perform necessary steps to retrieve a Flybits tenant specific access token (JWT).
       * @memberof IAMFlybits.oauth2PKCE.Client
       * @function
       * @instance
       * @returns {external:Promise<IAMFlybits.oauth2PKCE.Client.AccessResponse, Error>} Promise that will resolve with Flybits tenant specific access details.
       */
      requestSPAuthz: function(){
        var client = this;
        return this.authzReqCallback().then(function(res){
          return client.authzFlybitsProject(res.idToken);
        });
      },
      authzReqCallback: function(){
        var client = this;
        var tokenExchangeURL = this.IDPHOST + this.AUTHZTOKEN;
        var authCode = IAMFlybits.getURLParam('code');
        var error = IAMFlybits.getURLParam('error');
        var state = IAMFlybits.getURLParam('state');

        return new Promise(function(resolve, reject){
          if(error){
            reject(error);
          } else{
            client.RequestClient.fetch(tokenExchangeURL, {
              method: 'POST',
              headers: {
                'content-type': 'application/x-www-form-urlencoded'
              },
              body: new URLSearchParams({
                grant_type: 'authorization_code',
                client_id: client.clientID,
                redirect_uri: client.redirectURL,
                code: authCode,
                code_verifier: IAMFlybits.oauth2PKCE.store.getCodeVerifier(),
              }),
              respType: 'json'
            }).then(function(res){
              resolve({
                idToken: res.body.id_token,
                accessToken: res.body.access_token,
                expiry: res.body.expires_in
              });
            }).catch(function(e){
              reject(e);
            });
          }
        });
      },
      authzFlybitsProject: function(idToken){
        var client = this;
        var url = this.SPHOST + this.SPAUTH;

        return new Promise(function(resolve, reject){
          if(!idToken){
            reject();
          } else {
            client.RequestClient.fetch(url, {
              method: 'POST',
              headers: {
                'content-type': 'application/json'
              },
              body: JSON.stringify({
                idToken: idToken,
                projectId: client.projectID,
                provider: client.IDTOKENPROVIDER
              }),
              respType: 'json'
            }).then(function(res){
              resolve({
                deviceID: res.body.currentDeviceId,
                email: res.body.email,
                level: res.body.level,
                projectID: res.body.projectId,
                accessToken: res.headers['x-authorization'] 
              });
            }).catch(function(e){
              reject(e);
            });
          }
        })
      }
    };

    return OAuthClient;
  })();

  if(typeof window === 'object'){
    window.IAMFlybits = IAMFlybits;
  } else if(typeof exports === 'object' && typeof module !== 'undefined'){
    module.exports = IAMFlybits;
  } else if(typeof define === 'function' && define.amd){
    define(function(){
      return IAMFlybits;
    });
  } else{
    global.IAMFlybits = IAMFlybits;
  }
})(window);
Vue.config.productionTip = false;